import { isValidFullDomain } from '@thecompaniesapi/url-helpers';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import vine from '@vinejs/vine';
import { z } from 'zod';

const apps = [
  {
    name: "Amazon",
    slug: "amazon"
  },
  {
    name: "Android",
    slug: "android"
  },
  {
    name: "Chrome",
    slug: "chrome"
  },
  {
    name: "iOS",
    slug: "ios"
  },
  {
    name: "Mac",
    slug: "mac"
  },
  {
    name: "Meta",
    slug: "meta"
  },
  {
    name: "Microsoft",
    slug: "microsoft"
  },
  {
    name: "Playstation",
    slug: "playstation"
  },
  {
    name: "Xbox",
    slug: "xbox"
  }
];

const businessTypes = [
  {
    name: {
      en: "Educational Institutions",
      es: "Instituciones Educativas",
      fr: "\xC9tablissements \xE9ducatifs"
    },
    slug: "educational-institution"
  },
  {
    name: {
      en: "Government Agencies",
      es: "Agencias Gubernamentales",
      fr: "Organismes gouvernementaux"
    },
    slug: "government-agency"
  },
  {
    name: {
      en: "Nonprofits",
      es: "Organizaciones sin \xE1nimo de lucro",
      fr: "Associations \xE0 but non lucratif"
    },
    slug: "nonprofit"
  },
  {
    name: {
      en: "Partnerships",
      es: "Sociedades de responsabilidad limitada",
      fr: "Partenariats"
    },
    slug: "partnership"
  },
  {
    name: {
      en: "Privately Held",
      es: "Empresas privadas",
      fr: "Entreprises priv\xE9es"
    },
    slug: "privately-held"
  },
  {
    name: {
      en: "Public Companies",
      es: "Empresas p\xFAblicas",
      fr: "Entreprises publiques"
    },
    slug: "public-company"
  },
  {
    name: {
      en: "Self-Employed",
      es: "Aut\xF3nomos",
      fr: "Travailleurs ind\xE9pendants"
    },
    slug: "self-employed"
  },
  {
    name: {
      en: "Sole Proprietorships",
      es: "Sociedades individuales",
      fr: "Entreprises individuelles"
    },
    slug: "sole-proprietorship"
  }
];

const contacts = [
  {
    name: "URL",
    slug: "contactUrl"
  },
  {
    name: "Email",
    slug: "emails"
  },
  {
    name: "Line",
    slug: "lines"
  },
  {
    name: "Phone",
    slug: "phones"
  },
  {
    name: "WhatsApp",
    slug: "whatsapps"
  }
];

const continents = [
  {
    code: "af",
    color: "yellow",
    icon: "globe-africa",
    latitude: "11.5024338",
    linkedinHeadline: "Africa",
    linkedinId: "103537801",
    longitude: "17.7578122",
    name: {
      en: "Africa",
      es: "\xC1frica",
      fr: "Afrique"
    }
  },
  {
    code: "an",
    color: "gray",
    icon: "globe-americas",
    latitude: "-79.4063075",
    linkedinHeadline: "Antarctica",
    linkedinId: "100428639",
    longitude: "0.3149312",
    name: {
      en: "Antarctica",
      es: "Ant\xE1rtida",
      fr: "Antarctique"
    }
  },
  {
    code: "as",
    color: "red",
    icon: "globe-asia",
    latitude: "51.2086975",
    linkedinHeadline: "Asia",
    linkedinId: "102393603",
    longitude: "89.2343748",
    name: {
      en: "Asia",
      es: "Asia",
      fr: "Asie"
    }
  },
  {
    code: "eu",
    color: "blue",
    icon: "globe-europe",
    latitude: "51",
    linkedinHeadline: "Europe",
    linkedinId: "100506914",
    longitude: "10",
    name: {
      en: "Europe",
      es: "Europa",
      fr: "Europe"
    }
  },
  {
    code: "na",
    color: "purple",
    icon: "globe-americas",
    latitude: "51.0000002",
    linkedinHeadline: "North America",
    linkedinId: "102221843",
    longitude: "-109",
    name: {
      en: "North America",
      es: "Am\xE9rica del Norte",
      fr: "Am\xE9rique du Nord"
    }
  },
  {
    code: "oc",
    color: "pink",
    icon: "globe-asia",
    latitude: "-18.3128",
    linkedinHeadline: "Oceania",
    linkedinId: "91000010",
    longitude: "138.5156",
    name: {
      en: "Oceania",
      es: "Ocean\xEDa",
      fr: "Oc\xE9anie"
    }
  },
  {
    code: "sa",
    color: "green",
    icon: "globe-americas",
    latitude: "-21.0002179",
    linkedinHeadline: "South America",
    linkedinId: "104514572",
    longitude: "-61.0006565",
    name: {
      en: "South America",
      es: "Sudamerica",
      fr: "Am\xE9rique du Sud"
    }
  }
];

extendZodWithOpenApi(z);
function hasTrim(zodSchema) {
  return zodSchema._def?.typeName === z.ZodFirstPartyTypeKind.ZodString && zodSchema._def.checks?.some((check) => check.kind === "trim");
}
function unwrapInnerType(schema) {
  if (schema._def?.innerType) {
    return unwrapInnerType(schema._def.innerType);
  }
  return schema;
}
function zodToVine(schema, overwrites) {
  const result = {};
  const unwrapValue = (value) => {
    let zodType = unwrapInnerType(value);
    let resultValue;
    let maxLength;
    let minLength;
    let min;
    let max;
    const isOptional = value.isOptional() ?? void 0;
    const isNullable = value.isNullable() ?? void 0;
    let isTrimmed;
    let isArray;
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodArray) {
      const element = unwrapInnerType(zodType.element);
      isArray = true;
      zodType = element;
      value = zodType;
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodObject) {
      resultValue = zodToVine(zodType);
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodEnum) {
      const options = zodType.options;
      resultValue = vine.enum(options);
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodAny) {
      resultValue = vine.any();
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodString) {
      maxLength = value.maxLength ?? void 0;
      minLength = value.minLength ?? void 0;
      isTrimmed = hasTrim(value) ?? void 0;
      resultValue = vine.string();
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodNumber) {
      min = value.minValue ?? void 0;
      max = value.maxValue ?? void 0;
      resultValue = vine.number();
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodBoolean) {
      resultValue = vine.boolean();
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodDate) {
      resultValue = vine.date();
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodUnion) {
      const branches = zodType.options;
      resultValue = vine.unionOfTypes(branches.map((branch) => zodToVine(branch)));
    }
    if (zodType._def?.typeName === z.ZodFirstPartyTypeKind.ZodRecord) {
      const valueType = zodType._def?.valueType ? zodToVine(zodType._def.valueType) : vine.any();
      resultValue = vine.record(valueType);
    }
    if (resultValue && isArray) {
      resultValue = vine.array(resultValue);
    }
    if (resultValue && maxLength) {
      resultValue = resultValue.maxLength(maxLength);
    }
    if (resultValue && minLength) {
      resultValue = resultValue.minLength(minLength);
    }
    if (resultValue && isOptional) {
      resultValue = resultValue.optional();
    }
    if (resultValue && isNullable) {
      resultValue = resultValue.nullable();
    }
    if (resultValue && isTrimmed) {
      resultValue = resultValue.trim();
    }
    if (resultValue && min) {
      resultValue = resultValue.min(min);
    }
    if (resultValue && max) {
      resultValue = resultValue.max(max);
    }
    return resultValue;
  };
  if (schema._def?.typeName === z.ZodFirstPartyTypeKind.ZodObject) {
    for (const [key, value] of Object.entries(schema.shape)) {
      if (overwrites && overwrites?.[key]) {
        result[key] = overwrites[key];
        continue;
      }
      const resultValue = unwrapValue(value);
      if (resultValue) {
        result[key] = resultValue;
      }
    }
  } else {
    return unwrapValue(schema);
  }
  return vine.object(result);
}
const zodHelpers = { zodToVine };

const industries = {
  "abrasives-and-nonmetallic-minerals-manufacturing": {
    description: "This industry includes entities that manufacture nonmetallic mineral products (except clay products, refractory products, glass products, cement and concrete products, lime, and gypsum products, which are in separate industries).",
    linkedinId: 799,
    naics: {
      codes: [
        "3279",
        "32791",
        "327910",
        "32799",
        "327991",
        "327992",
        "327993",
        "327999"
      ],
      slugs: [
        "abrasive-product-manufacturing",
        "all-other-miscellaneous-nonmetallic-mineral-product-manufacturing",
        "all-other-nonmetallic-mineral-product-manufacturing",
        "cut-stone-and-stone-product-manufacturing",
        "ground-or-treated-mineral-and-earth-manufacturing",
        "mineral-wool-manufacturing",
        "other-nonmetallic-mineral-product-manufacturing"
      ]
    },
    name: "Abrasives and Nonmetallic Minerals Manufacturing",
    parents: ["glass-ceramics-and-concrete-manufacturing", "manufacturing"],
    sic: {
      codes: ["3290"],
      slugs: ["abrasive-asbestos-misc-nonmetallic-mineral-prods"]
    }
  },
  "accessible-architecture-and-design": {
    description: "This industry includes entities that plan and design residential, institutional, leisure, commercial, and industrial buildings and structures that can be accessed, understood and used by all people regardless of their ability, disability or impairment.",
    linkedinId: 3246,
    naics: {
      codes: ["541310"],
      slugs: ["architectural-services"]
    },
    name: "Accessible Architecture and Design",
    parents: ["architecture-and-planning", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "accessible-hardware-manufacturing": {
    description: "This industry includes entities that manufacture or assemble computer accessibility hardware, such as electronic pointing devices, touch screens and adaptive keyboards.",
    linkedinId: 3245,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Accessible Hardware Manufacturing",
    parents: [
      "computer-hardware-manufacturing",
      "computers-and-electronics-manufacturing",
      "manufacturing"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "accommodation-services": {
    description: "This industry includes entities that provide short-term lodging in facilities, such as hotels, motels, and bed-and-breakfast inns. In addition to lodging, they may provide a range of other services for their guests.",
    isParent: true,
    linkedinId: 2190,
    naics: {
      codes: [
        "72",
        "721",
        "7212",
        "72121",
        "721211",
        "721214",
        "7213",
        "72131",
        "721310"
      ],
      slugs: [
        "accommodation",
        "accommodation-and-food-services",
        "recreational-and-vacation-camps-except-campgrounds",
        "rooming-and-boarding-houses-dormitories-and-workers-camps",
        "rooming-and-boarding-houses-dormitories-and-workers-camps-t",
        "rv-recreational-vehicle-parks-and-campgrounds",
        "rv-recreational-vehicle-parks-and-recreational-camps"
      ]
    },
    name: "Accommodation Services",
    parents: [],
    sic: {
      codes: ["7000", "7011"],
      slugs: ["hotels-motels", "hotels-rooming-houses-camps-other-lodging-places"]
    }
  },
  "accounting": {
    description: "This industry includes entities that make accounting record audits, design accounting systems, prepare financial statements, develop budgets, prepare tax returns, process payroll, and perform bookkeeping and billing.",
    linkedinId: 47,
    naics: {
      codes: [
        "5412",
        "54121",
        "541211",
        "541213",
        "541214",
        "541219"
      ],
      slugs: [
        "accounting-tax-preparation-bookkeeping-and-payroll-services",
        "offices-of-certified-public-accountants",
        "other-accounting-services",
        "payroll-services",
        "tax-preparation-services"
      ]
    },
    name: "Accounting",
    parents: ["professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "administration-of-justice": {
    description: "This industry includes government entities that administer justice, including courts, correctional institutions, and other offices.",
    linkedinId: 73,
    naics: {
      codes: ["92211"],
      slugs: ["courts"]
    },
    name: "Administration of Justice",
    parents: ["government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "administrative-and-support-services": {
    description: "This industry includes entities that perform routine support activities for the day-to-day operations of other organizations, including office administration, hiring and placing of personnel, document preparation and similar clerical services, solicitation, collection, security and surveillance services, cleaning, and waste disposal services.",
    isParent: true,
    linkedinId: 1912,
    naics: {
      codes: [
        "56",
        "561",
        "5614",
        "56143",
        "561431",
        "561439",
        "56145",
        "561450",
        "56149",
        "561491",
        "561492",
        "5617",
        "56171",
        "561710",
        "56173",
        "56174",
        "561740",
        "56179",
        "561790",
        "5619",
        "56191",
        "561910",
        "56199",
        "561990",
        "562",
        "5629",
        "56291",
        "562910",
        "56292",
        "562920",
        "56299",
        "562991",
        "562998"
      ],
      slugs: [
        "administrative-and-support-and-waste-management-and-remediation-services",
        "administrative-and-support-services",
        "all-other-miscellaneous-waste-management-services",
        "all-other-support-services",
        "all-other-waste-management-services",
        "business-service-centers",
        "business-support-services",
        "carpet-and-upholstery-cleaning-services",
        "court-reporting-and-stenotype-services",
        "credit-bureaus",
        "exterminating-and-pest-control-services",
        "landscaping-services",
        "materials-recovery-facilities",
        "other-business-service-centers-including-copy-shops",
        "other-business-support-services",
        "other-services-to-buildings-and-dwellings",
        "other-support-services",
        "packaging-and-labeling-services",
        "private-mail-centers",
        "remediation-and-other-waste-management-services",
        "remediation-services",
        "repossession-services",
        "septic-tank-and-related-services",
        "services-to-buildings-and-dwellings",
        "waste-management-and-remediation-services"
      ]
    },
    name: "Administrative and Support Services",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "advertising-services": {
    description: "This industry includes entities that provide advertising, public relations, and related services, such as the purchase of media, independent media representation, outdoor advertisement, direct mail advertisement, and advertisement material distribution services.",
    linkedinId: 80,
    naics: {
      codes: ["541613"],
      slugs: ["marketing-consulting-services"]
    },
    name: "Advertising Services",
    parents: ["professional-services"],
    sic: {
      codes: [
        "7310",
        "7311",
        "7331"
      ],
      slugs: [
        "services-advertising",
        "services-advertising-agencies",
        "services-direct-mail-advertising-services"
      ]
    }
  },
  "agricultural-chemical-manufacturing": {
    description: "This industry includes entities that manufacture: nitrogenous or phosphatic fertilizer materials with or without mixing with other ingredients into fertilizers; Mixing ingredients made elsewhere into fertilizers; and Fertilizers from sewage or animal waste. Included are entities that formulate and prepare pesticides and other agricultural chemicals.",
    linkedinId: 709,
    naics: {
      codes: [
        "3253",
        "32531",
        "325311",
        "325312",
        "325314",
        "325315",
        "32532",
        "325320"
      ],
      slugs: [
        "compost-manufacturing",
        "fertilizer-manufacturing",
        "fertilizer-mixing-only-manufacturing",
        "nitrogenous-fertilizer-manufacturing",
        "pesticide-and-other-agricultural-chemical-manufacturing",
        "pesticide-fertilizer-and-other-agricultural-chemical-manufacturing",
        "phosphatic-fertilizer-manufacturing"
      ]
    },
    name: "Agricultural Chemical Manufacturing",
    parents: ["chemical-manufacturing", "manufacturing"],
    sic: {
      codes: ["2870"],
      slugs: ["agricultural-chemicals"]
    }
  },
  "agriculture-construction-mining-machinery-manufacturing": {
    description: "This industry includes entities that manufacture one or more of the following: farm machinery and equipment, power mowing equipment, and other powered home lawn and garden equipment; construction machinery, surface mining machinery, and logging equipment; and oil and gas field and underground mining machinery and equipment.",
    linkedinId: 901,
    naics: {
      codes: [
        "3331",
        "33311",
        "333111",
        "333112",
        "33312",
        "333120",
        "33313",
        "333131",
        "333132"
      ],
      slugs: [
        "agricultural-implement-manufacturing",
        "agriculture-construction-and-mining-machinery-manufacturing",
        "construction-machinery-manufacturing",
        "farm-machinery-and-equipment-manufacturing",
        "lawn-and-garden-tractor-and-home-lawn-and-garden-equipment-manufacturing",
        "mining-and-oil-and-gas-field-machinery-manufacturing",
        "mining-machinery-and-equipment-manufacturing",
        "oil-and-gas-field-machinery-and-equipment-manufacturing"
      ]
    },
    name: "Agriculture, Construction, Mining Machinery Manufacturing",
    parents: ["machinery-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "3523",
        "3524",
        "3530",
        "3531",
        "3532",
        "3533"
      ],
      slugs: [
        "construction-machinery-equip",
        "construction-mining-materials-handling-machinery-equip",
        "farm-machinery-equipment",
        "lawn-garden-tractors-home-lawn-gardens-equip",
        "mining-machinery-equip-no-oil-gas-field-mach-equip",
        "oil-gas-field-machinery-equipment"
      ]
    }
  },
  "air-water-and-waste-program-management": {
    description: "This industry includes government entities that focus on administration, regulation, or enforcement of programs related to one or more of the following: Air and water resources; Solid waste management; Water and air pollution control and prevention; Flood control; Drainage development and water resource consumption; Toxic waste removal and cleanup; and/or coordination of these activities at intergovernmental levels.",
    linkedinId: 2366,
    naics: {
      codes: ["92411", "924110"],
      slugs: ["administration-of-air-and-water-resource-and-solid-waste-management-programs"]
    },
    name: "Air, Water, and Waste Program Management",
    parents: ["environmental-quality-programs", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "airlines-and-aviation": {
    description: "This industry includes entities that provide air transportation of passengers and/or cargo using aircraft, such as airplanes and helicopters. Included are scheduled air transportation and nonscheduled, such as chartered air transportation of passengers, cargo, or specialty flying services.",
    linkedinId: 94,
    naics: {
      codes: [
        "481",
        "4811",
        "48111",
        "481111",
        "481112",
        "4812",
        "48121",
        "481211",
        "481212",
        "481219"
      ],
      slugs: [
        "air-transportation",
        "nonscheduled-air-transportation",
        "nonscheduled-chartered-freight-air-transportation",
        "nonscheduled-chartered-passenger-air-transportation",
        "other-nonscheduled-air-transportation",
        "scheduled-air-transportation",
        "scheduled-freight-air-transportation",
        "scheduled-passenger-air-transportation"
      ]
    },
    name: "Airlines and Aviation",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [
        "4512",
        "4522",
        "4581"
      ],
      slugs: [
        "air-transportation-nonscheduled",
        "air-transportation-scheduled",
        "airports-flying-fields-airport-terminal-services"
      ]
    }
  },
  "alternative-dispute-resolution": {
    description: "This industry includes entities (except offices of lawyers, attorneys, and paralegals, which are in separate industries) that provide arbitration and conciliation services.",
    linkedinId: 120,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Alternative Dispute Resolution",
    parents: ["legal-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "alternative-fuel-vehicle-manufacturing": {
    description: "This industry includes entities that manufacture motor vehicles powered by fuels other than gasoline or diesel, such as electricity, natural gas, propane, hydrogen, and biofuels. Electric and hybrid vehicles are included in this industry.",
    linkedinId: 3253,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Alternative Fuel Vehicle Manufacturing",
    parents: [
      "manufacturing",
      "motor-vehicle-manufacturing",
      "transportation-equipment-manufacturing"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "alternative-medicine": {
    description: "This industry includes independent health practitioners including acupuncturists , hypnotherapists, homeopaths, and naturopaths. These practitioners operate private or group practices in their own offices or in the facilities of others (e.g., hospitals).",
    linkedinId: 125,
    naics: {
      codes: ["621399"],
      slugs: ["offices-of-all-other-miscellaneous-health-practitioners"]
    },
    name: "Alternative Medicine",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "ambulance-services": {
    description: "This industry includes entities that provide transportation of patients by ground or air, along with medical care.",
    linkedinId: 2077,
    naics: {
      codes: ["62191", "621910"],
      slugs: ["ambulance-services"]
    },
    name: "Ambulance Services",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "amusement-parks-and-arcades": {
    description: "This industry includes entities that operate amusement parks and amusement arcades and parlors.",
    linkedinId: 2167,
    naics: {
      codes: [
        "7131",
        "71311",
        "713110",
        "71312",
        "713120"
      ],
      slugs: [
        "amusement-and-theme-parks",
        "amusement-arcades",
        "amusement-parks-and-arcades"
      ]
    },
    name: "Amusement Parks and Arcades",
    parents: ["entertainment-providers", "recreational-facilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "animal-feed-manufacturing": {
    description: "This industry includes entities that manufacture food and feed for animals from ingredients, such as grains, oilseed mill products, and meat products.",
    linkedinId: 481,
    naics: {
      codes: [
        "3111",
        "31111",
        "311111",
        "311119"
      ],
      slugs: [
        "animal-food-manufacturing",
        "dog-and-cat-food-manufacturing",
        "other-animal-food-manufacturing"
      ]
    },
    name: "Animal Feed Manufacturing",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "animation-and-post-production": {
    description: "This industry includes entities that provide specialized motion picture or video postproduction services, such as editing, film/tape transfers, subtitling, credits, closed captioning, and animation and special effects.",
    linkedinId: 127,
    naics: {
      codes: [
        "51219",
        "512191",
        "512199"
      ],
      slugs: [
        "other-motion-picture-and-video-industries",
        "postproduction-services-and-other-motion-picture-and-video-industries",
        "teleproduction-and-other-postproduction-services"
      ]
    },
    name: "Animation and Post-production",
    parents: [
      "media-and-telecommunications",
      "movies-videos-and-sound",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "apparel-manufacturing": {
    description: "This industry includes entities that purchase fabric and cut and sew to make a garment, and those that first knit fabric and then cut and sew to make a garment. This includes apparel contractors that cut or sew on materials owned by others, jobbers who perform entrepreneurial functions in apparel manufacturing, and tailors who manufacture custom garments for individual clients.",
    linkedinId: 598,
    naics: {
      codes: [
        "315",
        "3151",
        "31512",
        "315120",
        "3152",
        "31521",
        "315210",
        "31525",
        "315250"
      ],
      slugs: [
        "apparel-knitting-mills",
        "apparel-manufacturing",
        "cut-and-sew-apparel-contractors",
        "cut-and-sew-apparel-manufacturing",
        "cut-and-sew-apparel-manufacturing-except-contractors"
      ]
    },
    name: "Apparel Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "2253",
        "2300",
        "2320",
        "2330",
        "2340"
      ],
      slugs: [
        "apparel-other-finishd-prods-of-fabrics-similar-matl",
        "knit-outerwear-mills",
        "men-s-boys-furnishgs-work-clothing-allied-garments",
        "women-s-misses-and-juniors-outerwear",
        "women-s-misses-children-s-infants-undergarments"
      ]
    }
  },
  "appliances-electrical-and-electronics-manufacturing": {
    description: "This industry includes entities that manufacture products that generate, distribute, and use electrical power, including small and major electrical appliances and parts; electric motors; devices for storing electrical power (e.g., batteries); for transmitting electricity (e.g., insulated wire); and wiring devices (e.g., electrical outlets, fuse boxes, and light switches).",
    linkedinId: 112,
    naics: {
      codes: [
        "335",
        "3359",
        "33591",
        "335910",
        "33592",
        "335921",
        "335929",
        "33593",
        "335931",
        "335932",
        "33599",
        "335991",
        "335999"
      ],
      slugs: [
        "all-other-electrical-equipment-and-component-manufacturing",
        "all-other-miscellaneous-electrical-equipment-and-component-manufacturing",
        "battery-manufacturing",
        "carbon-and-graphite-product-manufacturing",
        "communication-and-energy-wire-and-cable-manufacturing",
        "current-carrying-wiring-device-manufacturing",
        "electrical-equipment-appliance-and-component-manufacturing",
        "fiber-optic-cable-manufacturing",
        "noncurrent-carrying-wiring-device-manufacturing",
        "other-communication-and-energy-wire-manufacturing",
        "other-electrical-equipment-and-component-manufacturing",
        "wiring-device-manufacturing"
      ]
    },
    name: "Appliances, Electrical, and Electronics Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "architectural-and-structural-metal-manufacturing": {
    description: "This industry includes entities that manufacture one or more of the following: Prefabricated metal buildings, panels and sections; Structural metal products; Metal plate work products; Metal framed windows (such as, typically using purchased glass) and metal doors; Sheet metal work; and Ornamental and architectural metal products.",
    linkedinId: 852,
    naics: {
      codes: [
        "3323",
        "33231",
        "332311",
        "332312",
        "332313",
        "33232",
        "332321",
        "332322",
        "332323"
      ],
      slugs: [
        "architectural-and-structural-metals-manufacturing",
        "fabricated-structural-metal-manufacturing",
        "metal-window-and-door-manufacturing",
        "ornamental-and-architectural-metal-products-manufacturing",
        "ornamental-and-architectural-metal-work-manufacturing",
        "plate-work-and-fabricated-structural-product-manufacturing",
        "plate-work-manufacturing",
        "prefabricated-metal-building-and-component-manufacturing",
        "sheet-metal-work-manufacturing"
      ]
    },
    name: "Architectural and Structural Metal Manufacturing",
    parents: ["fabricated-metal-products", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "architecture-and-planning": {
    description: "This industry includes entities that plan and design residential, institutional, leisure, commercial, and industrial buildings and structures by applying knowledge of design, construction procedures, zoning regulations, building codes, and building materials.",
    linkedinId: 50,
    naics: {
      codes: ["54131", "541310"],
      slugs: ["architectural-services"]
    },
    name: "Architecture and Planning",
    parents: ["professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "armed-forces": {
    description: "This industry includes government entities engaged in national security and related activities.",
    linkedinId: 71,
    naics: {
      codes: ["92811", "928110"],
      slugs: ["national-security"]
    },
    name: "Armed Forces",
    parents: ["government-administration", "military-and-international-affairs"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "artificial-rubber-and-synthetic-fiber-manufacturing": {
    description: "This industry includes entities that manufacture synthetic resins, plastics materials, and non-vulcanizable elastomers and mixing and blending resins; synthetic rubber; and cellulosic (e.g., rayon, acetate) and non-cellulosic (e.g., nylon, polyolefin, polyester) fibers, including in the form of monofilament, filament yarn, staple, or tow.",
    linkedinId: 703,
    naics: {
      codes: [
        "3252",
        "32521",
        "325211",
        "325212",
        "32522",
        "325220"
      ],
      slugs: [
        "artificial-and-synthetic-fibers-and-filaments-manufacturing",
        "plastics-material-and-resin-manufacturing",
        "resin-and-synthetic-rubber-manufacturing",
        "resin-synthetic-rubber-and-artificial-and-synthetic-fibers-and-filaments-manufacturing",
        "synthetic-rubber-manufacturing"
      ]
    },
    name: "Artificial Rubber and Synthetic Fiber Manufacturing",
    parents: ["chemical-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "artists-and-writers": {
    description: "This industry includes independent (such as, freelance) individuals that perform in artistic productions, create artistic and cultural works, or provide technical expertise necessary for these productions.",
    linkedinId: 38,
    naics: {
      codes: [
        "7115",
        "71151",
        "711510"
      ],
      slugs: ["independent-artists-writers-and-performers"]
    },
    name: "Artists and Writers",
    parents: ["entertainment-providers"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "audio-and-video-equipment-manufacturing": {
    description: "This industry includes entities that manufacture electronic audio and video equipment for home entertainment, motor vehicles, and public address and musical instrument amplification.",
    linkedinId: 973,
    naics: {
      codes: [
        "3343",
        "33431",
        "334310"
      ],
      slugs: ["audio-and-video-equipment-manufacturing"]
    },
    name: "Audio and Video Equipment Manufacturing",
    parents: ["computers-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: ["3651", "3652"],
      slugs: ["household-audio-video-equipment", "phonograph-records-prerecorded-audio-tapes-disks"]
    }
  },
  "automation-machinery-manufacturing": {
    description: "This industry includes entities that use control systems for machinery, processes in factories, and human-machine interfaces with minimal or reduced human intervention. Control systems can be discrete, sequential, or computerized, and range in complexity from household temperature control to large industrial systems with multi-variable high-level algorithms. Artificial neural networks, motion control, and robotics are included.",
    linkedinId: 147,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Automation Machinery Manufacturing",
    parents: ["machinery-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "aviation-and-aerospace-component-manufacturing": {
    description: "This industry includes entities that: manufacture complete aircraft, missiles, or space vehicles; manufacture aerospace engines, propulsion units, auxiliary equipment or parts; develop and make prototypes of aerospace products; make major modifications to aircraft systems; and overhaul and rebuild complete aircraft or propulsion systems.",
    linkedinId: 52,
    naics: {
      codes: [
        "3364",
        "33641",
        "336411",
        "336412",
        "336413",
        "336415",
        "336419"
      ],
      slugs: [
        "aerospace-product-and-parts-manufacturing",
        "aircraft-engine-and-engine-parts-manufacturing",
        "aircraft-manufacturing",
        "guided-missile-and-space-vehicle-propulsion-unit-and-propulsion-unit-parts-manufacturing",
        "other-aircraft-parts-and-auxiliary-equipment-manufacturing",
        "other-guided-missile-and-space-vehicle-parts-and-auxiliary-equipment-manufacturing"
      ]
    },
    name: "Aviation and Aerospace Component Manufacturing",
    parents: ["manufacturing", "transportation-equipment-manufacturing"],
    sic: {
      codes: [
        "3720",
        "3721",
        "3724",
        "3728"
      ],
      slugs: [
        "aircraft",
        "aircraft-engines-engine-parts",
        "aircraft-parts",
        "aircraft-parts-auxiliary-equipment-nec"
      ]
    }
  },
  "baked-goods-manufacturing": {
    description: "This industry includes entities that: manufacture fresh and frozen bread and other bakery products; retail bread and other bakery products not for immediate consumption made on the premises from flour, not from prepared dough; or manufacture cookies, crackers, dry pasta, tortillas, or prepared flour mixes or dough from flour ground elsewhere.",
    linkedinId: 529,
    naics: {
      codes: [
        "3118",
        "31181",
        "311811",
        "311812",
        "311813",
        "31182",
        "311821",
        "311824",
        "31183",
        "311830"
      ],
      slugs: [
        "bakeries-and-tortilla-manufacturing",
        "bread-and-bakery-product-manufacturing",
        "commercial-bakeries",
        "cookie-and-cracker-manufacturing",
        "cookie-cracker-and-pasta-manufacturing",
        "dry-pasta-dough-and-flour-mixes-manufacturing-from-purchased-flour",
        "frozen-cakes-pies-and-other-pastries-manufacturing",
        "retail-bakeries",
        "tortilla-manufacturing"
      ]
    },
    name: "Baked Goods Manufacturing",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: ["2050", "2052"],
      slugs: ["bakery-products", "cookies-crackers"]
    }
  },
  "banking": {
    description: "This industry includes entities that accept demand and other deposits and make commercial, industrial, and consumer loans. Commercial banks and branches of foreign banks are included.",
    linkedinId: 41,
    naics: {
      codes: ["52211", "522110"],
      slugs: ["commercial-banking"]
    },
    name: "Banking",
    parents: ["credit-intermediation", "financial-services"],
    sic: {
      codes: [
        "6021",
        "6022",
        "6029",
        "6099"
      ],
      slugs: [
        "commercial-banks-nec",
        "functions-related-to-depository-banking-nec",
        "national-commercial-banks",
        "state-commercial-banks"
      ]
    }
  },
  "bars-taverns-and-nightclubs": {
    description: "This industry includes entities known as bars, taverns, nightclubs, or drinking places preparing and serving alcoholic beverages for immediate consumption.",
    linkedinId: 2217,
    naics: {
      codes: [
        "7224",
        "72241",
        "722410"
      ],
      slugs: ["drinking-places-alcoholic-beverages"]
    },
    name: "Bars, Taverns, and Nightclubs",
    parents: ["accommodation-services", "food-and-beverage-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "bed-and-breakfasts-hostels-homestays": {
    description: "This industry includes entities that provide short-term lodging and food (except hotels, motels, and casino hotels).",
    linkedinId: 2197,
    naics: {
      codes: [
        "72119",
        "721191",
        "721199"
      ],
      slugs: [
        "all-other-traveler-accommodation",
        "bed-and-breakfast-inns",
        "other-traveler-accommodation"
      ]
    },
    name: "Bed-and-Breakfasts, Hostels, Homestays",
    parents: ["accommodation-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "beverage-manufacturing": {
    description: "This industry includes entities that manufacture soft drinks and ice; purify and bottle water; and manufacture brewery, winery, and distillery products.",
    linkedinId: 142,
    naics: {
      codes: [
        "3121",
        "31211",
        "312111",
        "312112",
        "312113"
      ],
      slugs: [
        "beverage-manufacturing",
        "bottled-water-manufacturing",
        "ice-manufacturing",
        "soft-drink-and-ice-manufacturing",
        "soft-drink-manufacturing"
      ]
    },
    name: "Beverage Manufacturing",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: ["2080", "2086"],
      slugs: ["beverages", "bottled-canned-soft-drinks-carbonated-waters"]
    }
  },
  "biomass-electric-power-generation": {
    description: "This industry includes entities that operate biomass electric power generation facilities. These facilities use biomass (wood, waste, alcohol fuels) to produce electric energy provided to transmission or electric power distribution systems.",
    linkedinId: 390,
    naics: {
      codes: ["221117"],
      slugs: ["biomass-electric-power-generation"]
    },
    name: "Biomass Electric Power Generation",
    parents: [
      "electric-power-generation",
      "renewable-energy-power-generation",
      "utilities"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "biotechnology-research": {
    description: "This industry includes entities that conduct biotechnology (except nanobiotechnology) research and experimental development.",
    linkedinId: 12,
    naics: {
      codes: ["541714"],
      slugs: ["research-and-development-in-biotechnology-except-nanobiotechnology"]
    },
    name: "Biotechnology Research",
    parents: ["professional-services", "research-services"],
    sic: {
      codes: ["8731"],
      slugs: ["services-commercial-physical-biological-research"]
    }
  },
  "blockchain-services": {
    description: "This industry includes entities that provide infrastructure for management of decentralized digital assets, such as cryptocurrency and non-fungible tokens (NFTs). Cryptocurrency trading platforms and blockchain hosting platforms are included in this industry.",
    linkedinId: 3134,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Blockchain Services",
    parents: [
      "data-infrastructure-and-analytics",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "blogs": {
    description: "This industry includes entities that produce content for the internet: textual or video blogs.",
    linkedinId: 3125,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Blogs",
    parents: [
      "information-services",
      "internet-publishing",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "boilers-tanks-and-shipping-container-manufacturing": {
    description: "This industry includes entities that: Manufacture power boilers and heat exchangers; Cut, form, and join heavy gauge metal to manufacture tanks, vessels, and other containers; or Form light gauge metal containers.",
    linkedinId: 861,
    naics: {
      codes: [
        "3324",
        "33241",
        "332410",
        "33242",
        "332420",
        "33243",
        "332431",
        "332439"
      ],
      slugs: [
        "boiler-tank-and-shipping-container-manufacturing",
        "metal-can-box-and-other-metal-container-light-gauge-manufacturing",
        "metal-can-manufacturing",
        "metal-tank-heavy-gauge-manufacturing",
        "other-metal-container-manufacturing",
        "power-boiler-and-heat-exchanger-manufacturing"
      ]
    },
    name: "Boilers, Tanks, and Shipping Container Manufacturing",
    parents: ["fabricated-metal-products", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "book-and-periodical-publishing": {
    description: "This industry includes entities that publish newspapers, magazines, other periodicals, and books, as well as directories and mailing lists.",
    linkedinId: 82,
    naics: {
      codes: [
        "513",
        "5131",
        "51314",
        "513140",
        "51319",
        "513191",
        "513199"
      ],
      slugs: [
        "all-other-publishers",
        "directory-and-mailing-list-publishers",
        "greeting-card-publishers",
        "newspaper-periodical-book-and-directory-publishers",
        "other-publishers",
        "publishing-industries"
      ]
    },
    name: "Book and Periodical Publishing",
    parents: ["media-and-telecommunications", "technology-information-and-media"],
    sic: {
      codes: ["2732", "2741"],
      slugs: ["book-printing", "miscellaneous-publishing"]
    }
  },
  "book-publishing": {
    description: "This industry includes entities known as book publishers that carry out design, editing, and marketing activities necessary for producing and distributing books, in print, electronic, or audio form.",
    linkedinId: 1602,
    naics: {
      codes: ["51313", "513130"],
      slugs: ["book-publishers"]
    },
    name: "Book Publishing",
    parents: [
      "book-and-periodical-publishing",
      "media-and-telecommunications",
      "technology-information-and-media"
    ],
    sic: {
      codes: ["2731"],
      slugs: ["books-publishing-or-publishing-printing"]
    }
  },
  "breweries": {
    description: "This industry includes entities that brew beer, ale, lager, malt liquors, and nonalcoholic beer.",
    linkedinId: 562,
    naics: {
      codes: ["31212", "312120"],
      slugs: ["breweries"]
    },
    name: "Breweries",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: ["2082"],
      slugs: ["malt-beverages"]
    }
  },
  "broadcast-media-production-and-distribution": {
    description: "This industry includes entities that create or license radio or television content as well as distribute or broadcast it.",
    linkedinId: 36,
    naics: {
      codes: [
        "516",
        "5161",
        "51611"
      ],
      slugs: [
        "broadcasting-and-content-providers",
        "radio-and-television-broadcasting-stations",
        "radio-broadcasting-stations"
      ]
    },
    name: "Broadcast Media Production and Distribution",
    parents: ["media-and-telecommunications", "technology-information-and-media"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "building-construction": {
    description: "This industry includes entities that construct buildings, including temporary buildings and precut, panelized, and prefabricated buildings assembled on-site. New work, additions, alterations, or maintenance and repairs are also included.",
    linkedinId: 406,
    naics: {
      codes: ["236"],
      slugs: ["construction-of-buildings"]
    },
    name: "Building Construction",
    parents: ["construction"],
    sic: {
      codes: ["1531"],
      slugs: ["operative-builders"]
    }
  },
  "building-equipment-contractors": {
    description: "This industry includes entities that install or service equipment that forms part of a building mechanical system (e.g., electricity, water, heating, and cooling). Contractors installing specialized building equipment, such as elevators, escalators, service station equipment, and central vacuum cleaning systems are also included.",
    linkedinId: 453,
    naics: {
      codes: [
        "2382",
        "23821",
        "238210",
        "23822",
        "238220",
        "23829",
        "238290"
      ],
      slugs: [
        "building-equipment-contractors",
        "electrical-contractors-and-other-wiring-installation-contractors",
        "other-building-equipment-contractors",
        "plumbing-heating-and-air-conditioning-contractors"
      ]
    },
    name: "Building Equipment Contractors",
    parents: ["construction", "specialty-trade-contractors"],
    sic: {
      codes: ["1731"],
      slugs: ["electrical-work"]
    }
  },
  "building-finishing-contractors": {
    description: "This industry includes entities that provide specialty trades needed to finish buildings. This includes new work, additions, alterations, maintenance, and repairs.",
    linkedinId: 460,
    naics: {
      codes: [
        "2383",
        "23831",
        "238310",
        "23832",
        "238320",
        "23833",
        "238330",
        "23834",
        "238340",
        "23835",
        "238350",
        "23839",
        "238390"
      ],
      slugs: [
        "building-finishing-contractors",
        "drywall-and-insulation-contractors",
        "finish-carpentry-contractors",
        "flooring-contractors",
        "other-building-finishing-contractors",
        "painting-and-wall-covering-contractors",
        "tile-and-terrazzo-contractors"
      ]
    },
    name: "Building Finishing Contractors",
    parents: ["construction", "specialty-trade-contractors"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "building-structure-and-exterior-contractors": {
    description: "This industry includes entities that provide specialty trades needed to complete the basic structure (such as, foundation, frame, and shell) of buildings. This includes new work, additions, alterations, maintenance, and repairs.",
    linkedinId: 436,
    naics: {
      codes: [
        "2381",
        "23811",
        "238110",
        "23812",
        "238120",
        "23813",
        "238130",
        "23814",
        "238140",
        "23815",
        "238150",
        "23816",
        "238160",
        "23817",
        "238170",
        "23819",
        "238190"
      ],
      slugs: [
        "foundation-structure-and-building-exterior-contractors",
        "framing-contractors",
        "glass-and-glazing-contractors",
        "masonry-contractors",
        "other-foundation-structure-and-building-exterior-contractors",
        "poured-concrete-foundation-and-structure-contractors",
        "roofing-contractors",
        "siding-contractors",
        "structural-steel-and-precast-concrete-contractors"
      ]
    },
    name: "Building Structure and Exterior Contractors",
    parents: ["construction", "specialty-trade-contractors"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "business-consulting-and-services": {
    description: "This industry includes entities that provide advice and assistance to businesses and other organizations on administrative and operational issues, such as strategic and organizational planning; financial planning and budgeting; marketing objectives and policies; human resource policies, practices, and planning; production scheduling, control planning, and IT Consulting.",
    linkedinId: 11,
    naics: {
      codes: [
        "5416",
        "54161",
        "541618",
        "54169",
        "541690"
      ],
      slugs: [
        "management-consulting-services",
        "management-scientific-and-technical-consulting-services",
        "other-management-consulting-services",
        "other-scientific-and-technical-consulting-services"
      ]
    },
    name: "Business Consulting and Services",
    parents: ["professional-services"],
    sic: {
      codes: ["7380", "7389"],
      slugs: ["services-business-services-nec", "services-miscellaneous-business-services"]
    }
  },
  "business-content": {
    description: "This industry includes entities that provide news and content about markets and business activities.",
    linkedinId: 3129,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Business Content",
    parents: [
      "information-services",
      "internet-publishing",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "business-intelligence-platforms": {
    description: "This industry includes entities that provide content, data, services, and/or computer hardware for gathering or distributing information specifically about markets and business activities.",
    linkedinId: 3128,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Business Intelligence Platforms",
    parents: [
      "data-infrastructure-and-analytics",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "cable-and-satellite-programming": {
    description: "This industry includes entities that operate studios and facilities for the broadcasting of programs on a subscription or fee basis which are usually delivered to a third party, such as cable systems or direct-to-home satellite systems, for transmission to viewers.",
    linkedinId: 1641,
    naics: {
      codes: [
        "5162",
        "51621",
        "516210"
      ],
      slugs: ["media-streaming-distribution-services-social-networks-and-other-media-networks-and-content-providers"]
    },
    name: "Cable and Satellite Programming",
    parents: [
      "broadcast-media-production-and-distribution",
      "media-and-telecommunications",
      "technology-information-and-media"
    ],
    sic: {
      codes: ["4841"],
      slugs: ["cable-other-pay-television-services"]
    }
  },
  "capital-markets": {
    description: "This industry includes entities that: Underwrite securities issues and/or make markets for securities and commodities; Act as brokers between buyers and sellers of securities and commodities; Provide securities and commodity exchange services; and Manage portfolios of assets, provide investment advice, and provide and trust, fiduciary, and custody services.",
    linkedinId: 129,
    naics: {
      codes: [
        "523",
        "5231",
        "52316",
        "523160"
      ],
      slugs: [
        "commodity-contracts-intermediation",
        "securities-and-commodity-contracts-intermediation-and-brokerage",
        "securities-commodity-contracts-and-other-financial-investments-and-related-activities"
      ]
    },
    name: "Capital Markets",
    parents: ["financial-services"],
    sic: {
      codes: [
        "6189",
        "6200",
        "6211",
        "6221",
        "6770",
        "6799",
        "8880"
      ],
      slugs: [
        "american-depositary-receipts",
        "asset-backed-securities",
        "blank-checks",
        "commodity-contracts-brokers-dealers",
        "investors-nec",
        "security-brokers-dealers-flotation-companies",
        "security-commodity-brokers-dealers-exchanges-services"
      ]
    }
  },
  "caterers": {
    description: "This industry includes entities that provide single event-based food services and generally have equipment and vehicles to transport meals and snacks to events and/or prepare food at an off-premises site.",
    linkedinId: 2212,
    naics: {
      codes: ["72232", "722320"],
      slugs: ["caterers"]
    },
    name: "Caterers",
    parents: ["accommodation-services", "food-and-beverage-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "chemical-manufacturing": {
    description: "This industry includes entities that transform organic and inorganic raw materials by a chemical process and formulate products. It includes the production of basic chemicals as well as intermediate and end products produced by further processing basic chemicals.",
    linkedinId: 54,
    naics: {
      codes: [
        "325",
        "3256",
        "3259",
        "32591",
        "325910",
        "32592",
        "325920",
        "32599",
        "325991",
        "325992",
        "325998"
      ],
      slugs: [
        "all-other-chemical-product-and-preparation-manufacturing",
        "all-other-miscellaneous-chemical-product-and-preparation-manufacturing",
        "chemical-manufacturing",
        "custom-compounding-of-purchased-resins",
        "explosives-manufacturing",
        "other-chemical-product-and-preparation-manufacturing",
        "photographic-film-paper-plate-and-chemical-manufacturing",
        "printing-ink-manufacturing",
        "soap-cleaning-compound-and-toilet-preparation-manufacturing"
      ]
    },
    name: "Chemical Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "2800",
        "2810",
        "2860",
        "2890"
      ],
      slugs: [
        "chemicals-allied-products",
        "industrial-inorganic-chemicals",
        "industrial-organic-chemicals",
        "miscellaneous-chemical-products"
      ]
    }
  },
  "chemical-raw-materials-manufacturing": {
    description: "This industry includes entities that manufacture chemicals using basic processes, such as thermal cracking and distillation. These chemicals are usually separate chemical elements or separate chemically-defined compounds.",
    linkedinId: 690,
    naics: {
      codes: [
        "3251",
        "32511",
        "325110",
        "32512",
        "325120",
        "32513",
        "325130",
        "32518",
        "325180",
        "32519",
        "325193",
        "325194",
        "325199"
      ],
      slugs: [
        "all-other-basic-organic-chemical-manufacturing",
        "basic-chemical-manufacturing",
        "cyclic-crude-intermediate-and-gum-and-wood-chemical-manufacturing",
        "ethyl-alcohol-manufacturing",
        "industrial-gas-manufacturing",
        "other-basic-inorganic-chemical-manufacturing",
        "other-basic-organic-chemical-manufacturing",
        "petrochemical-manufacturing",
        "synthetic-dye-and-pigment-manufacturing"
      ]
    },
    name: "Chemical Raw Materials Manufacturing",
    parents: ["chemical-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "child-day-care-services": {
    description: "This industry includes entities that provide day care of infants or children who are not in school and may also offer pre-kindergarten and/or kindergarten educational programs.",
    linkedinId: 2128,
    naics: {
      codes: [
        "6244",
        "62441",
        "624410"
      ],
      slugs: ["child-day-care-services"]
    },
    name: "Child Day Care Services",
    parents: ["hospitals-and-health-care", "individual-and-family-services"],
    sic: {
      codes: ["8351"],
      slugs: ["services-child-day-care-services"]
    }
  },
  "chiropractors": {
    description: "This industry includes health practitioners having the degree of D.C. (Doctor of Chiropractic) practicing chiropractic therapy.",
    linkedinId: 2048,
    naics: {
      codes: ["62131", "621310"],
      slugs: ["offices-of-chiropractors"]
    },
    name: "Chiropractors",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "circuses-and-magic-shows": {
    description: "This industry includes companies or groups (except theater companies, dance companies, and musical groups and artists, which are in separate industries) producing live theatrical presentations.",
    linkedinId: 2139,
    naics: {
      codes: ["71119", "711190"],
      slugs: ["other-performing-arts-companies"]
    },
    name: "Circuses and Magic Shows",
    parents: ["entertainment-providers", "performing-arts-and-spectator-sports"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "civic-and-social-organizations": {
    description: "This industry includes entities that organize religious activities; promote causes and beliefs for the public good; support charitable and other causes through grantmaking; advocate social and political causes; and promote and defend the interests of their members.",
    linkedinId: 90,
    naics: {
      codes: [
        "8134",
        "81341",
        "813410"
      ],
      slugs: ["civic-and-social-organizations"]
    },
    name: "Civic and Social Organizations",
    parents: ["consumer-services"],
    sic: {
      codes: ["8300"],
      slugs: ["services-social-services"]
    }
  },
  "civil-engineering": {
    description: "This industry includes entities that conduct engineering projects such as the construction of highways, dams, and utility systems.",
    linkedinId: 51,
    naics: {
      codes: [
        "237",
        "2379",
        "23799",
        "237990"
      ],
      slugs: ["heavy-and-civil-engineering-construction", "other-heavy-and-civil-engineering-construction"]
    },
    name: "Civil Engineering",
    parents: ["construction"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "claims-adjusting-actuarial-services": {
    description: "This industry includes entities that primarily provide services related to insurance (except insurance agencies and brokerages).",
    linkedinId: 1738,
    naics: {
      codes: [
        "52429",
        "524291",
        "524292",
        "524298"
      ],
      slugs: [
        "all-other-insurance-related-activities",
        "claims-adjusting",
        "other-insurance-related-activities",
        "pharmacy-benefit-management-and-other-third-party-administration-of-insurance-and-pension-funds"
      ]
    },
    name: "Claims Adjusting, Actuarial Services",
    parents: ["financial-services", "insurance"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "clay-and-refractory-products-manufacturing": {
    description: "This industry includes entities that shape, mold, glaze, and fire pottery, ceramics, and plumbing fixtures, and electrical supplies made entirely or partly of clay or other ceramic materials or shape, mold, bake, burn, or harden clay refractories, nonclay refractories, ceramic tile, structural clay tile, brick, and other structural clay building materials.",
    linkedinId: 773,
    naics: {
      codes: [
        "3271",
        "32711",
        "327110",
        "32712",
        "327120"
      ],
      slugs: [
        "clay-building-material-and-refractories-manufacturing",
        "clay-product-and-refractory-manufacturing",
        "pottery-ceramics-and-plumbing-fixture-manufacturing"
      ]
    },
    name: "Clay and Refractory Products Manufacturing",
    parents: ["glass-ceramics-and-concrete-manufacturing", "manufacturing"],
    sic: {
      codes: ["3250", "3260"],
      slugs: ["pottery-related-products", "structural-clay-products"]
    }
  },
  "climate-data-and-analytics": {
    description: "This industry includes entities that collect, analyze, and interpret data related to the Earth\u2019s climate. It includes data from sources such as satellites, weather stations, and ocean buoys.",
    linkedinId: 3252,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Climate Data and Analytics",
    parents: [
      "data-infrastructure-and-analytics",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "climate-technology-product-manufacturing": {
    description: "This industry includes entities that research, develop, and produce technologies for the generation of renewable energy, alternative fuel cells, and sequestering carbon and other greenhouse gases from the atmosphere.",
    linkedinId: 3251,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Climate Technology Product Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "coal-mining": {
    description: "This industry includes entities that: mine bituminous coal, anthracite, and lignite by underground mining, auger mining, strip mining, culm bank mining, and other surface mining; develop coal mine sites; and beneficiate coal (e.g., clean, wash, screen, and size coal).",
    linkedinId: 341,
    naics: {
      codes: [
        "2121",
        "21211",
        "212114",
        "212115"
      ],
      slugs: [
        "coal-mining",
        "surface-coal-mining",
        "underground-coal-mining"
      ]
    },
    name: "Coal Mining",
    parents: ["mining", "oil-gas-and-mining"],
    sic: {
      codes: ["1220", "1221"],
      slugs: ["bituminous-coal-lignite-mining", "bituminous-coal-lignite-surface-mining"]
    }
  },
  "collection-agencies": {
    description: "This industry includes entities that collect payments for claims and remitting payments collected to their clients.",
    linkedinId: 1938,
    naics: {
      codes: ["56144", "561440"],
      slugs: ["collection-agencies"]
    },
    name: "Collection Agencies",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: ["7320"],
      slugs: ["services-consumer-credit-reporting-collection-agencies"]
    }
  },
  "commercial-and-industrial-equipment-rental": {
    description: "This industry includes entities that rent or lease commercial-type and industrial-type machinery and equipment -- capital or investment-type equipment that clients use in their business operations.",
    linkedinId: 1798,
    naics: {
      codes: [
        "5324",
        "53241",
        "532411",
        "532412",
        "53242",
        "532420",
        "53249",
        "532490"
      ],
      slugs: [
        "commercial-air-rail-and-water-transportation-equipment-rental-and-leasing",
        "commercial-and-industrial-machinery-and-equipment-rental-and-leasing",
        "construction-mining-and-forestry-machinery-and-equipment-rental-and-leasing",
        "construction-transportation-mining-and-forestry-machinery-and-equipment-rental-and-leasing",
        "office-machinery-and-equipment-rental-and-leasing",
        "other-commercial-and-industrial-machinery-and-equipment-rental-and-leasing"
      ]
    },
    name: "Commercial and Industrial Equipment Rental",
    parents: ["equipment-rental-services", "real-estate-and-equipment-rental-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "commercial-and-industrial-machinery-maintenance": {
    description: "This industry includes entities that repair and maintain commercial and industrial machinery and equipment, including either sharpening/installing commercial and industrial machinery blades and saws or provide welding (e.g., automotive, general) repair services; or repairing agricultural and other heavy and industrial machinery and equipment (e.g., forklifts and other material handling equipment, machine tools, commercial refrigeration equipment, construction equipment, and mining machinery).",
    linkedinId: 2247,
    naics: {
      codes: [
        "8113",
        "81131",
        "811310"
      ],
      slugs: ["commercial-and-industrial-machinery-and-equipment-except-automotive-and-electronic-repair-and-maintenance"]
    },
    name: "Commercial and Industrial Machinery Maintenance",
    parents: ["consumer-services", "repair-and-maintenance"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "commercial-and-service-industry-machinery-manufacturing": {
    description: "This industry includes entities that manufacture commercial and service industry machinery, such as optical instruments, photographic and photocopying equipment, automatic vending machinery, commercial laundry and drycleaning machinery, office machinery, automotive maintenance equipment (except mechanics\u2019 handtools, which are in separate industries), and commercial-type cooking equipment.",
    linkedinId: 918,
    naics: {
      codes: [
        "3333",
        "33331",
        "333310"
      ],
      slugs: ["commercial-and-service-industry-machinery-manufacturing"]
    },
    name: "Commercial and Service Industry Machinery Manufacturing",
    parents: ["machinery-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "communications-equipment-manufacturing": {
    description: "This industry includes entities that manufacture communications equipment such as wire telephone and data communications equipment, and radio or television broadcast and wireless communications equipment.",
    linkedinId: 964,
    naics: {
      codes: [
        "3342",
        "33421",
        "334210",
        "33422",
        "334220",
        "33429",
        "334290"
      ],
      slugs: [
        "communications-equipment-manufacturing",
        "other-communications-equipment-manufacturing",
        "radio-and-television-broadcasting-and-wireless-communications-equipment-manufacturing",
        "telephone-apparatus-manufacturing"
      ]
    },
    name: "Communications Equipment Manufacturing",
    parents: ["computers-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "3661",
        "3663",
        "3669"
      ],
      slugs: [
        "communications-equipment-nec",
        "radio-tv-broadcasting-communications-equipment",
        "telephone-telegraph-apparatus"
      ]
    }
  },
  "community-development-and-urban-planning": {
    description: "This industry includes government entities that focus on the administration and planning of the development of urban and rural areas, including government zoning boards and commissions.",
    linkedinId: 2374,
    naics: {
      codes: ["92512", "925120"],
      slugs: ["administration-of-urban-planning-and-community-and-rural-development"]
    },
    name: "Community Development and Urban Planning",
    parents: ["government-administration", "housing-and-community-development"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "community-services": {
    description: "This industry includes entities offer short-term emergency shelter, temporary residential shelter, transitional housing, volunteer construction or repair of low-cost housing; or food, shelter, clothing, medical relief, resettlement, and counseling to the needy, or to victims of domestic or international disasters or conflicts.",
    linkedinId: 2115,
    naics: {
      codes: [
        "6242",
        "62421",
        "624210",
        "62422",
        "624221",
        "624229"
      ],
      slugs: [
        "community-food-and-housing-and-emergency-and-other-relief-services",
        "community-food-services",
        "community-housing-services",
        "other-community-housing-services",
        "temporary-shelters"
      ]
    },
    name: "Community Services",
    parents: ["hospitals-and-health-care"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "computer-and-network-security": {
    description: "This industry includes entities that provide services and technologies to protect computer systems from unauthorized access, such as theft or damage to hardware, software, or electronic data, including the disruption or misdirection of the services they provide.",
    linkedinId: 118,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Computer and Network Security",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "computer-games": {
    description: "This industry includes entities that produce user-facing software for gaming on different platforms.",
    linkedinId: 109,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Computer Games",
    parents: [
      "software-development",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "computer-hardware-manufacturing": {
    description: "This industry includes entities that manufacture or assemble electronic computers, such as mainframes, personal computers, workstations, laptops, and computer servers; and computer peripheral equipment, such as storage devices, printers, monitors, and input/output devices and terminals.",
    linkedinId: 3,
    naics: {
      codes: [
        "3341",
        "33411",
        "334111",
        "334112",
        "334118"
      ],
      slugs: [
        "computer-and-peripheral-equipment-manufacturing",
        "computer-storage-device-manufacturing",
        "computer-terminal-and-other-computer-peripheral-equipment-manufacturing",
        "electronic-computer-manufacturing"
      ]
    },
    name: "Computer Hardware Manufacturing",
    parents: ["computers-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "computer-networking-products": {
    description: "This industry includes entities that produce software that manages or runs on collections of connected computers.",
    linkedinId: 5,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Computer Networking Products",
    parents: [
      "software-development",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "computers-and-electronics-manufacturing": {
    description: "This industry includes entities that manufacture computers, computer peripherals, communications equipment, and similar electronic products, and entities that manufacture components for such products.",
    linkedinId: 24,
    naics: {
      codes: [
        "334",
        "334412",
        "334413",
        "334416",
        "334417",
        "334418",
        "334419"
      ],
      slugs: [
        "bare-printed-circuit-board-manufacturing",
        "capacitor-resistor-coil-transformer-and-other-inductor-manufacturing",
        "computer-and-electronic-product-manufacturing",
        "electronic-connector-manufacturing",
        "other-electronic-component-manufacturing",
        "printed-circuit-assembly-electronic-assembly-manufacturing",
        "semiconductor-and-related-device-manufacturing"
      ]
    },
    name: "Computers and Electronics Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "3570",
        "3571",
        "3572",
        "3575",
        "3576",
        "3577",
        "3578",
        "3579"
      ],
      slugs: [
        "calculating-accounting-machines-no-electronic-computers",
        "computer-communications-equipment",
        "computer-office-equipment",
        "computer-peripheral-equipment-nec",
        "computer-storage-devices",
        "computer-terminals",
        "electronic-computers",
        "office-machines-nec"
      ]
    }
  },
  "conservation-programs": {
    description: "This industry includes government entities that focus on the administration, regulation, supervision, and control of land use, including recreational areas; conservation and preservation of natural resources; erosion control; geological survey program administration; weather forecasting program administration; and the administration and protection of publicly and privately owned forest lands, including game, fish, and wildlife populations, including wildlife management areas and field stations.",
    linkedinId: 2368,
    naics: {
      codes: ["92412", "924120"],
      slugs: ["administration-of-conservation-programs"]
    },
    name: "Conservation Programs",
    parents: ["environmental-quality-programs", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "construction": {
    description: "This industry includes entities that construct buildings or engineer projects (e.g., highways and utility systems) and perform specific activities (e.g., painting and plumbing).",
    isParent: true,
    linkedinId: 48,
    naics: {
      codes: ["23"],
      slugs: ["construction"]
    },
    name: "Construction",
    parents: [],
    sic: {
      codes: ["1600"],
      slugs: ["heavy-construction-other-than-bldg-const-contractors"]
    }
  },
  "construction-hardware-manufacturing": {
    description: "This industry includes entities that manufacture metal hardware, such as metal hinges, metal handles, keys, and locks (except coin-operated or time locks, which are in separate industries).",
    linkedinId: 871,
    naics: {
      codes: [
        "3325",
        "33251",
        "332510"
      ],
      slugs: ["hardware-manufacturing"]
    },
    name: "Construction Hardware Manufacturing",
    parents: ["fabricated-metal-products", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "consumer-goods-rental": {
    description: "This industry includes entities that rent personal and household-type goods, generally short-term rental although in some instances, the goods may be leased for longer periods of time.",
    linkedinId: 1786,
    naics: {
      codes: [
        "5322",
        "53221",
        "532210",
        "53228",
        "532281",
        "532282",
        "532283",
        "532284",
        "532289"
      ],
      slugs: [
        "all-other-consumer-goods-rental",
        "consumer-electronics-and-appliances-rental",
        "consumer-goods-rental",
        "formal-wear-and-costume-rental",
        "home-health-equipment-rental",
        "other-consumer-goods-rental",
        "recreational-goods-rental",
        "video-tape-and-disc-rental"
      ]
    },
    name: "Consumer Goods Rental",
    parents: ["equipment-rental-services", "real-estate-and-equipment-rental-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "consumer-services": {
    description: "This industry includes entities that repair cars, machinery, electronics, furniture, footwear, and leather goods, and those that provide personal services such as laundry, pet care, and household services.",
    isParent: true,
    linkedinId: 91,
    naics: {
      codes: [
        "81",
        "813",
        "8133",
        "81331",
        "813311",
        "813312",
        "813319"
      ],
      slugs: [
        "environment-conservation-and-wildlife-organizations",
        "human-rights-organizations",
        "other-services-except-public-administration",
        "other-social-advocacy-organizations",
        "religious-grantmaking-civic-professional-and-similar-organizations",
        "social-advocacy-organizations"
      ]
    },
    name: "Consumer Services",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "correctional-institutions": {
    description: "This industry includes government entities that manage and operate correctional institutions designed for the confinement, correction, and rehabilitation of adult and/or juvenile offenders sentenced by a court.",
    linkedinId: 3068,
    naics: {
      codes: ["922140"],
      slugs: ["correctional-institutions"]
    },
    name: "Correctional Institutions",
    parents: ["administration-of-justice", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "cosmetology-and-barber-schools": {
    description: "This industry includes entities that offer training in barbering, hair styling, or the cosmetic arts, such as makeup or skin care.",
    linkedinId: 2019,
    naics: {
      codes: ["611511"],
      slugs: ["cosmetology-and-barber-schools"]
    },
    name: "Cosmetology and Barber Schools",
    parents: ["education", "technical-and-vocational-training"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "courts-of-law": {
    description: "This industry includes civilian courts of law (except American Indian and Alaska Native tribal courts, which are in a separate industry).",
    linkedinId: 3065,
    naics: {
      codes: ["922110"],
      slugs: ["courts"]
    },
    name: "Courts of Law",
    parents: ["administration-of-justice", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "credit-intermediation": {
    description: "This industry includes entities that primarily lend funds raised from depositors or from credit market borrowing; or facilitate the lending of funds or issuance of credit by engaging in such activities as mortgage and loan brokerage, clearinghouse and reserve services, and check cashing services.",
    linkedinId: 1673,
    naics: {
      codes: [
        "522",
        "5221",
        "52213",
        "522130",
        "5222",
        "52221",
        "522210",
        "52222",
        "522220",
        "52229",
        "522291",
        "522292",
        "5223",
        "52232",
        "522320",
        "52239",
        "522390"
      ],
      slugs: [
        "activities-related-to-credit-intermediation",
        "consumer-lending",
        "credit-card-issuing",
        "credit-intermediation-and-related-activities",
        "credit-unions",
        "depository-credit-intermediation",
        "financial-transactions-processing-reserve-and-clearinghouse-activities",
        "nondepository-credit-intermediation",
        "other-activities-related-to-credit-intermediation",
        "other-nondepository-credit-intermediation",
        "real-estate-credit",
        "sales-financing"
      ]
    },
    name: "Credit Intermediation",
    parents: ["financial-services"],
    sic: {
      codes: [
        "6111",
        "6141",
        "6153",
        "6159",
        "6162",
        "6172"
      ],
      slugs: [
        "federal-federally-sponsored-credit-agencies",
        "finance-lessors",
        "miscellaneous-business-credit-institution",
        "mortgage-bankers-loan-correspondents",
        "personal-credit-institutions",
        "short-term-business-credit-institutions"
      ]
    }
  },
  "cutlery-and-handtool-manufacturing": {
    description: "This industry includes entities that manufacture one or more of the following: Metal kitchen cookware (except by casting (e.g., cast iron skillets) or stamped without further fabrication), utensils, and/or nonprecious and precious plated metal cutlery and flatware; Saw blades, all types (including those for power sawing machines); and Nonpowered handtools and edge tools.",
    linkedinId: 849,
    naics: {
      codes: [
        "3322",
        "33221",
        "332215",
        "332216"
      ],
      slugs: [
        "cutlery-and-handtool-manufacturing",
        "metal-kitchen-cookware-utensil-cutlery-and-flatware-except-precious-manufacturing",
        "saw-blade-and-handtool-manufacturing"
      ]
    },
    name: "Cutlery and Handtool Manufacturing",
    parents: ["fabricated-metal-products", "manufacturing"],
    sic: {
      codes: ["3420"],
      slugs: ["cutlery-handtools-general-hardware"]
    }
  },
  "dairy-product-manufacturing": {
    description: "This industry includes entities that manufacture dairy products from raw milk, processed milk, and dairy substitutes.",
    linkedinId: 65,
    naics: {
      codes: [
        "3115",
        "31151",
        "311511",
        "311512",
        "311513",
        "311514",
        "31152",
        "311520"
      ],
      slugs: [
        "cheese-manufacturing",
        "creamery-butter-manufacturing",
        "dairy-product-except-frozen-manufacturing",
        "dairy-product-manufacturing",
        "dry-condensed-and-evaporated-dairy-product-manufacturing",
        "fluid-milk-manufacturing",
        "ice-cream-and-frozen-dessert-manufacturing"
      ]
    },
    name: "Dairy Product Manufacturing",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: ["2020", "2024"],
      slugs: ["dairy-products", "ice-cream-frozen-desserts"]
    }
  },
  "dance-companies": {
    description: "This industry includes companies, groups, or theaters producing all types of live theatrical dance (e.g., ballet, contemporary dance, folk dance) presentations.",
    linkedinId: 2135,
    naics: {
      codes: ["71112", "711120"],
      slugs: ["dance-companies"]
    },
    name: "Dance Companies",
    parents: ["entertainment-providers", "performing-arts-and-spectator-sports"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "data-infrastructure-and-analytics": {
    description: "This industry includes entities that provide infrastructure for hosting including web or application hosting, streaming services, and general time-share mainframe facilities to clients, or they provide data processing, data entry, or data analysis services.",
    linkedinId: 2458,
    naics: {
      codes: [
        "518",
        "5182",
        "51821",
        "518210"
      ],
      slugs: ["computing-infrastructure-providers-data-processing-web-hosting-and-related-services"]
    },
    name: "Data Infrastructure and Analytics",
    parents: ["technology-information-and-internet", "technology-information-and-media"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "data-security-software-products": {
    description: "This industry includes entities that produce software to ensure security of data and computing systems.",
    linkedinId: 3130,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Data Security Software Products",
    parents: [
      "software-development",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "defense-and-space-manufacturing": {
    description: "This industry includes entities that manufacture complete guided missiles and space vehicles and/or develop prototypes.",
    linkedinId: 1,
    naics: {
      codes: ["336414"],
      slugs: ["guided-missile-and-space-vehicle-manufacturing"]
    },
    name: "Defense and Space Manufacturing",
    parents: ["manufacturing", "transportation-equipment-manufacturing"],
    sic: {
      codes: [
        "3480",
        "3760",
        "3812"
      ],
      slugs: [
        "guided-missiles-space-vehicles-parts",
        "ordnance-accessories-no-vehicles-guided-missiles",
        "search-detection-navagation-guidance-aeronautical-sys"
      ]
    }
  },
  "dentists": {
    description: "This industry includes health practitioners having the degree of D.M.D. (Doctor of Dental Medicine), D.D.S. (Doctor of Dental Surgery), or D.D.Sc. (Doctor of Dental Science) practicing general or specialized dentistry or dental surgery.",
    linkedinId: 2045,
    naics: {
      codes: [
        "6212",
        "62121",
        "621210"
      ],
      slugs: ["offices-of-dentists"]
    },
    name: "Dentists",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "design-services": {
    description: "This industry includes entities that provide specialized design services (except architectural, engineering, and computer systems design, which are in separate industries).",
    linkedinId: 99,
    naics: {
      codes: [
        "5414",
        "54142",
        "541420",
        "54149",
        "541490"
      ],
      slugs: [
        "industrial-design-services",
        "other-specialized-design-services",
        "specialized-design-services"
      ]
    },
    name: "Design Services",
    parents: ["professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "desktop-computing-software-products": {
    description: "This industry includes entities that produce software which runs on desktop or laptop computers.",
    linkedinId: 3101,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Desktop Computing Software Products",
    parents: [
      "software-development",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "digital-accessibility-services": {
    description: "This industry includes entities that provide services and technologies that support digital accessibility, such as digital accessibility testing, auditing and consulting services.",
    linkedinId: 3244,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Digital Accessibility Services",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "distilleries": {
    description: "This industry includes entities that distill potable liquors (except brandies, which are in a separate industry), distill and blend liquors, and/or blend and mix liquors and other ingredients.",
    linkedinId: 564,
    naics: {
      codes: ["31214", "312140"],
      slugs: ["distilleries"]
    },
    name: "Distilleries",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "e-learning-providers": {
    description: "This industry includes entities that offer instruction using electronic technologies, such as through web-based trainings, online learning platforms, and live or recorded lectures. Training may be tuition-based or open to the public, accredited or non-accredited.",
    linkedinId: 132,
    naics: {
      codes: [],
      slugs: []
    },
    name: "E-Learning Providers",
    parents: ["education"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "economic-programs": {
    description: "This industry includes government entities that administer economic programs.",
    linkedinId: 2375,
    naics: {
      codes: [
        "926",
        "9261",
        "92611",
        "926110",
        "92612",
        "92613",
        "92614",
        "926140",
        "92615",
        "926150"
      ],
      slugs: [
        "administration-of-economic-programs",
        "administration-of-general-economic-programs",
        "regulation-and-administration-of-communications-electric-gas-and-other-utilities",
        "regulation-and-administration-of-transportation-programs",
        "regulation-licensing-and-inspection-of-miscellaneous-commercial-sectors",
        "regulation-of-agricultural-marketing-and-commodities"
      ]
    },
    name: "Economic Programs",
    parents: ["government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "education": {
    description: "This industry includes entities that provide instruction or training in a wide variety of subjects from specialized entities, such as schools, colleges, universities, and training centers.",
    isParent: true,
    linkedinId: 1999,
    naics: {
      codes: [
        "61",
        "611",
        "6112",
        "61121",
        "611210",
        "6116",
        "61169",
        "611691",
        "611692",
        "611699",
        "6117",
        "61171",
        "611710"
      ],
      slugs: [
        "all-other-miscellaneous-schools-and-instruction",
        "all-other-schools-and-instruction",
        "automobile-driving-schools",
        "educational-services",
        "educational-support-services",
        "exam-preparation-and-tutoring",
        "junior-colleges",
        "other-schools-and-instruction"
      ]
    },
    name: "Education",
    parents: [],
    sic: {
      codes: ["8200"],
      slugs: ["services-educational-services"]
    }
  },
  "education-administration-programs": {
    description: "This industry includes government entities that coordinate, plan, supervise, and administrate funds, policies, activities, statistical reports, data collection, and centralized programs for educational administration. Government scholarship programs are included.",
    linkedinId: 69,
    naics: {
      codes: ["92311", "923110"],
      slugs: ["administration-of-education-programs"]
    },
    name: "Education Administration Programs",
    parents: ["government-administration", "health-and-human-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "electric-lighting-equipment-manufacturing": {
    description: "This industry includes entities that manufacture electric light bulbs and tubes, and parts and components (except glass blanks for electric light bulbs, which are in separate industries) or electric lighting fixtures (except vehicular, which are in a separate industry), nonelectric lighting equipment, lamp shades (except glass and plastics, which are in separate industries), and lighting fixture components (except current-carrying wiring devices, which are in a separate industry).",
    linkedinId: 998,
    naics: {
      codes: [
        "3351",
        "335121",
        "33513",
        "335132",
        "335139"
      ],
      slugs: [
        "commercial-industrial-and-institutional-electric-lighting-fixture-manufacturing",
        "electric-lamp-bulb-and-other-lighting-equipment-manufacturing",
        "electric-lighting-equipment-manufacturing",
        "residential-electric-lighting-fixture-manufacturing"
      ]
    },
    name: "Electric Lighting Equipment Manufacturing",
    parents: ["appliances-electrical-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: ["3640"],
      slugs: ["electric-lighting-wiring-equipment"]
    }
  },
  "electric-power-generation": {
    description: "This industry includes entities that operate electric power generation facilities. These facilities convert other forms of energy, such as waterpower (such as, hydroelectric), fossil fuels, nuclear power, and solar power, into electrical energy provided to transmission or electric power distribution systems.",
    linkedinId: 383,
    naics: {
      codes: ["22111", "221118"],
      slugs: ["electric-power-generation", "other-electric-power-generation"]
    },
    name: "Electric Power Generation",
    parents: ["utilities"],
    sic: {
      codes: ["4991"],
      slugs: ["cogeneration-services-small-power-producers"]
    }
  },
  "electric-power-transmission-control-and-distribution": {
    description: "This industry includes entities that operate electric power transmission systems; control (such as, regulate voltages) the transmission of electricity; and/or distribute electricity to distribution centers, other electric utilities, or final consumers.",
    linkedinId: 382,
    naics: {
      codes: [
        "22112",
        "221121",
        "221122"
      ],
      slugs: [
        "electric-bulk-power-transmission-and-control",
        "electric-power-distribution",
        "electric-power-transmission-control-and-distribution"
      ]
    },
    name: "Electric Power Transmission, Control, and Distribution",
    parents: ["utilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "electrical-equipment-manufacturing": {
    description: "This industry includes entities that manufacture power, distribution, and specialty transformers; electric motors, generators, and motor generator sets; switchgear and switchboard apparatus; relays; and industrial controls.",
    linkedinId: 2468,
    naics: {
      codes: [
        "3353",
        "33531",
        "335311",
        "335312",
        "335313",
        "335314"
      ],
      slugs: [
        "electrical-equipment-manufacturing",
        "motor-and-generator-manufacturing",
        "power-distribution-and-specialty-transformer-manufacturing",
        "relay-and-industrial-control-manufacturing",
        "switchgear-and-switchboard-apparatus-manufacturing"
      ]
    },
    name: "Electrical Equipment Manufacturing",
    parents: ["appliances-electrical-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "3600",
        "3612",
        "3613",
        "3620",
        "3621",
        "3690"
      ],
      slugs: [
        "electrical-industrial-apparatus",
        "electronic-other-electrical-equipment-no-computer-equip",
        "miscellaneous-electrical-machinery-equipment-supplies",
        "motors-generators",
        "power-distribution-specialty-transformers",
        "switchgear-switchboard-apparatus"
      ]
    }
  },
  "electronic-and-precision-equipment-maintenance": {
    description: "This industry includes entities that repair and maintain one or more of the following: Consumer electronic equipment; Computers; Office machines; Communication equipment; and Other electronic and precision equipment and instruments, without retailing these products as new.",
    linkedinId: 2240,
    naics: {
      codes: [
        "8112",
        "81121",
        "811210"
      ],
      slugs: ["electronic-and-precision-equipment-repair-and-maintenance"]
    },
    name: "Electronic and Precision Equipment Maintenance",
    parents: ["consumer-services", "repair-and-maintenance"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "embedded-software-products": {
    description: 'This industry includes entities that produce software (or "firmware") for machines or devices that are not usually thought of as computers, ex: routers, refrigerators, display systems, washing machines, etc. such as, software for the internet of things but do not produce these devices.',
    linkedinId: 3099,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Embedded Software Products",
    parents: [
      "software-development",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "emergency-and-relief-services": {
    description: "This industry includes entities that provide food, shelter, clothing, medical relief, resettlement, and counseling to victims of domestic or international disasters or conflicts (e.g., wars).",
    linkedinId: 2122,
    naics: {
      codes: ["62423", "624230"],
      slugs: ["emergency-and-other-relief-services"]
    },
    name: "Emergency and Relief Services",
    parents: ["hospitals-and-health-care", "individual-and-family-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "engineering-services": {
    description: "This industry includes entities that apply physical laws and principles of engineering in the design, development, and utilization of machines, materials, instruments, structures, processes, and systems. Services include the provision of advice, preparation of feasibility studies, preparation of preliminary and final plans and designs, provision of technical services, inspection, testing and evaluation of engineering projects, and other related services.",
    linkedinId: 3242,
    naics: {
      codes: ["54133"],
      slugs: ["engineering-services"]
    },
    name: "Engineering Services",
    parents: ["professional-services"],
    sic: {
      codes: ["8700", "8711"],
      slugs: ["services-engineering-accounting-research-management", "services-engineering-services"]
    }
  },
  "engines-and-power-transmission-equipment-manufacturing": {
    description: "This industry includes entities that manufacture turbines, power transmission equipment, and internal combustion engines (except automotive, gasoline and aircraft, which are in separate industries).",
    linkedinId: 935,
    naics: {
      codes: [
        "3336",
        "33361",
        "333611",
        "333612",
        "333613",
        "333618"
      ],
      slugs: [
        "engine-turbine-and-power-transmission-equipment-manufacturing",
        "mechanical-power-transmission-equipment-manufacturing",
        "other-engine-equipment-manufacturing",
        "speed-changer-industrial-high-speed-drive-and-gear-manufacturing",
        "turbine-and-turbine-generator-set-units-manufacturing"
      ]
    },
    name: "Engines and Power Transmission Equipment Manufacturing",
    parents: ["machinery-manufacturing", "manufacturing"],
    sic: {
      codes: ["3510"],
      slugs: ["engines-turbines"]
    }
  },
  "entertainment-providers": {
    description: "This industry includes entities that: Produce, promote, or participate in live performances, events, or exhibits intended for the public; Preserve and exhibit objects and sites of historical, cultural, or educational interest; and Operate facilities or provide services that enable patrons to participate in recreational activities or pursue amusement, hobby, and leisure-time interests.",
    isParent: true,
    linkedinId: 28,
    naics: {
      codes: ["71"],
      slugs: ["arts-entertainment-and-recreation"]
    },
    name: "Entertainment Providers",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "environmental-quality-programs": {
    description: "This industry includes government entities that administer environmental quality programs.",
    linkedinId: 388,
    naics: {
      codes: ["924", "9241"],
      slugs: ["administration-of-environmental-quality-programs"]
    },
    name: "Environmental Quality Programs",
    parents: ["government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "environmental-services": {
    description: "This industry includes entities that provide advice and assistance to businesses and organizations on environmental issues, such as control of environmental contamination from pollutants, toxic substances, and hazardous materials. They employ experts in fields such as air and water quality, asbestos contamination, remediation, ecological restoration, and environmental law. Sanitation or site remediation consulting services are included.",
    linkedinId: 86,
    naics: {
      codes: ["54162", "541620"],
      slugs: ["environmental-consulting-services"]
    },
    name: "Environmental Services",
    parents: ["business-consulting-and-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "equipment-rental-services": {
    description: "This industry includes entities that provide a wide array of tangible goods, such as automobiles, computers, consumer goods, and industrial machinery and equipment, to customers in return for a periodic rental or lease payment.",
    linkedinId: 1779,
    naics: {
      codes: [
        "532",
        "5321",
        "53211",
        "532111",
        "532112",
        "53212",
        "532120",
        "5323",
        "53231",
        "532310"
      ],
      slugs: [
        "automotive-equipment-rental-and-leasing",
        "general-rental-centers",
        "passenger-car-leasing",
        "passenger-car-rental",
        "passenger-car-rental-and-leasing",
        "rental-and-leasing-services",
        "truck-utility-trailer-and-rv-recreational-vehicle-rental-and-leasing"
      ]
    },
    name: "Equipment Rental Services",
    parents: ["real-estate-and-equipment-rental-services"],
    sic: {
      codes: ["7350", "7359"],
      slugs: ["services-equipment-rental-leasing-nec", "services-miscellaneous-equipment-rental-leasing"]
    }
  },
  "events-services": {
    description: "This industry includes entities that organize, promote, and/or manage events, such as business and trade shows, conventions, conferences, and meetings, held in facilities they manage and operate or in facilities that are managed and operated by others.",
    linkedinId: 110,
    naics: {
      codes: ["56192", "561920"],
      slugs: ["convention-and-trade-show-organizers"]
    },
    name: "Events Services",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "executive-offices": {
    description: "This industry includes government entities that serve as offices of chief executives and their advisory committees and commissions.",
    linkedinId: 76,
    naics: {
      codes: ["92111", "921110"],
      slugs: ["executive-offices"]
    },
    name: "Executive Offices",
    parents: ["government-administration", "public-policy-offices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "executive-search-services": {
    description: "This industry includes entities that provide executive search, recruitment, and placement services for clients with specific executive and senior management position requirements, including developing a search strategy and position specification based on the culture and needs of the client; researching, identifying, screening, and interviewing candidates; verifying candidate qualifications; and assisting in final offer negotiations and assimilation of the selected candidate.",
    linkedinId: 1923,
    naics: {
      codes: ["561312"],
      slugs: ["executive-search-services"]
    },
    name: "Executive Search Services",
    parents: ["administrative-and-support-services", "staffing-and-recruiting"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "fabricated-metal-products": {
    description: "This industry includes entities that transform metal into intermediate or end products, or treat metals and metal formed products fabricated elsewhere. This industry does not include machinery, computers and electronics, or metal furniture. This industry includes firearms manufacturing.",
    linkedinId: 840,
    naics: {
      codes: [
        "332",
        "3321",
        "33211",
        "332111",
        "332112",
        "332114",
        "332117",
        "332119"
      ],
      slugs: [
        "custom-roll-forming",
        "fabricated-metal-product-manufacturing",
        "forging-and-stamping",
        "iron-and-steel-forging",
        "metal-crown-closure-and-other-metal-stamping-except-automotive",
        "nonferrous-forging",
        "powder-metallurgy-part-manufacturing"
      ]
    },
    name: "Fabricated Metal Products",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "3411",
        "3412",
        "3430",
        "3433",
        "3440",
        "3442",
        "3443",
        "3444",
        "3448",
        "3490"
      ],
      slugs: [
        "fabricated-plate-work-boiler-shops",
        "fabricated-structural-metal-products",
        "hearing-equip-except-elec-warm-air-plumbing-fixtures",
        "heating-equipment-except-electric-warm-air-furnaces",
        "metal-cans",
        "metal-doors-sash-frames-moldings-trim",
        "metal-shipping-barrels-drums-kegs-pails",
        "miscellaneous-fabricated-metal-products",
        "prefabricated-metal-buildings-components",
        "sheet-metal-work"
      ]
    }
  },
  "facilities-services": {
    description: "This industry includes entities that provide operating staff who perform a combination of services within a client's facilities (excluding computer and/or data processing facilities, which are in separate industries), such as janitorial, maintenance, trash disposal, guard and security, mail routing, reception, and laundry. Entities that provide private jail services or operate correctional facilities on a contract or fee basis are included.",
    linkedinId: 122,
    naics: {
      codes: [
        "5612",
        "56121",
        "561210"
      ],
      slugs: ["facilities-support-services"]
    },
    name: "Facilities Services",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: ["7340", "8744"],
      slugs: ["services-facilities-support-management-services", "services-to-dwellings-other-buildings"]
    }
  },
  "family-planning-centers": {
    description: "This industry includes entities with medical staff that provide a range of family planning services on an outpatient basis, such as contraceptive services, genetic and prenatal counseling, voluntary sterilization, and therapeutic and medically induced termination of pregnancy.",
    linkedinId: 2060,
    naics: {
      codes: ["62141", "621410"],
      slugs: ["family-planning-centers"]
    },
    name: "Family Planning Centers",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "farming": {
    description: "This industry includes entities that grow crops mainly for food and fiber, such as farms, orchards, groves, greenhouses, and nurseries.",
    linkedinId: 63,
    naics: {
      codes: [
        "111",
        "1111",
        "11111",
        "111110",
        "11112",
        "111120",
        "11113",
        "111130",
        "11114",
        "111140",
        "11115",
        "111150",
        "11116",
        "111160",
        "11119",
        "111191",
        "111199",
        "1112",
        "11121",
        "111211",
        "111219",
        "1113",
        "11131",
        "111310",
        "11132",
        "111320",
        "11133",
        "111331",
        "111332",
        "111333",
        "111334",
        "111335",
        "111336",
        "111339",
        "1114",
        "11141",
        "111411",
        "111419",
        "11142",
        "111421",
        "111422",
        "1119",
        "11191",
        "111910",
        "11192",
        "111920",
        "11193",
        "111930",
        "11194",
        "111940",
        "11199",
        "111991",
        "111992",
        "111998"
      ],
      slugs: [
        "all-other-crop-farming",
        "all-other-grain-farming",
        "all-other-miscellaneous-crop-farming",
        "apple-orchards",
        "berry-except-strawberry-farming",
        "citrus-except-orange-groves",
        "corn-farming",
        "cotton-farming",
        "crop-production",
        "dry-pea-and-bean-farming",
        "floriculture-production",
        "food-crops-grown-under-cover",
        "fruit-and-tree-nut-combination-farming",
        "fruit-and-tree-nut-farming",
        "grape-vineyards",
        "greenhouse-nursery-and-floriculture-production",
        "hay-farming",
        "mushroom-production",
        "noncitrus-fruit-and-tree-nut-farming",
        "nursery-and-floriculture-production",
        "nursery-and-tree-production",
        "oilseed-and-grain-combination-farming",
        "oilseed-and-grain-farming",
        "oilseed-except-soybean-farming",
        "orange-groves",
        "other-crop-farming",
        "other-food-crops-grown-under-cover",
        "other-grain-farming",
        "other-noncitrus-fruit-farming",
        "other-vegetable-except-potato-and-melon-farming",
        "peanut-farming",
        "potato-farming",
        "rice-farming",
        "soybean-farming",
        "strawberry-farming",
        "sugar-beet-farming",
        "sugarcane-farming",
        "tobacco-farming",
        "tree-nut-farming",
        "vegetable-and-melon-farming",
        "wheat-farming"
      ]
    },
    name: "Farming",
    parents: ["farming-ranching-forestry"],
    sic: {
      codes: ["100", "200"],
      slugs: ["agricultural-prod-livestock-animal-specialties", "agricultural-production-crops"]
    }
  },
  "farming-ranching-forestry": {
    description: "This industry includes entities that grow crops, raise animals, harvest timber, and harvest fish and other animals from a farm, ranch, or their natural habitats.",
    isParent: true,
    linkedinId: 201,
    naics: {
      codes: [
        "11",
        "114",
        "1142",
        "11421",
        "114210",
        "115",
        "1151",
        "11511",
        "115111",
        "115112",
        "115113",
        "115114",
        "115115",
        "115116",
        "1152",
        "11521",
        "115210",
        "1153",
        "11531",
        "115310"
      ],
      slugs: [
        "agriculture-forestry-fishing-and-hunting",
        "cotton-ginning",
        "crop-harvesting-primarily-by-machine",
        "farm-labor-contractors-and-crew-leaders",
        "farm-management-services",
        "fishing-hunting-and-trapping",
        "hunting-and-trapping",
        "postharvest-crop-activities-except-cotton-ginning",
        "soil-preparation-planting-and-cultivating",
        "support-activities-for-agriculture-and-forestry",
        "support-activities-for-animal-production",
        "support-activities-for-crop-production",
        "support-activities-for-forestry"
      ]
    },
    name: "Farming, Ranching, Forestry",
    parents: [],
    sic: {
      codes: ["700"],
      slugs: ["agricultural-services"]
    }
  },
  "fashion-accessories-manufacturing": {
    description: "This industry includes entities that manufacture apparel and accessories (except apparel knitting mills and cut and sew apparel, which are in separate industries), including jobbers who perform entrepreneurial functions in apparel accessories manufacture. Example products include belts, caps, gloves (except medical, sporting, safety), hats, and neckties.",
    linkedinId: 615,
    naics: {
      codes: [
        "3159",
        "31599",
        "315990"
      ],
      slugs: ["apparel-accessories-and-other-apparel-manufacturing"]
    },
    name: "Fashion Accessories Manufacturing",
    parents: ["apparel-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "financial-services": {
    description: "This industry includes entities that make financial transactions (creation, liquidation, or change in ownership of financial assets) and/or that facilitate financial transactions.",
    isParent: true,
    linkedinId: 43,
    naics: {
      codes: [
        "52",
        "521",
        "5211",
        "52111",
        "521110"
      ],
      slugs: ["finance-and-insurance", "monetary-authorities-central-bank"]
    },
    name: "Financial Services",
    parents: [],
    sic: {
      codes: ["6199"],
      slugs: ["finance-services"]
    }
  },
  "fine-arts-schools": {
    description: "This industry includes entities that offer instruction in the arts, including dance, art, drama, and music.",
    linkedinId: 2025,
    naics: {
      codes: ["61161", "611610"],
      slugs: ["fine-arts-schools"]
    },
    name: "Fine Arts Schools",
    parents: ["education", "technical-and-vocational-training"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "fire-protection": {
    description: "This industry includes government entities that offer firefighting and other related fire protection activities.",
    linkedinId: 3070,
    naics: {
      codes: ["922160"],
      slugs: ["fire-protection"]
    },
    name: "Fire Protection",
    parents: ["administration-of-justice", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "fisheries": {
    description: "This industry includes entities that commercially catch finfish, shellfish, or miscellaneous marine products from a natural habitat, such as bluefish, eels, salmon, tuna, clams, crabs, lobsters, mussels, oysters, shrimp, frogs, sea urchins, and turtles.",
    linkedinId: 66,
    naics: {
      codes: [
        "1141",
        "11411",
        "114111",
        "114112",
        "114119"
      ],
      slugs: [
        "finfish-fishing",
        "fishing",
        "other-marine-fishing",
        "shellfish-fishing"
      ]
    },
    name: "Fisheries",
    parents: ["farming-ranching-forestry", "ranching-and-fisheries"],
    sic: {
      codes: ["900"],
      slugs: ["fishing-hunting-and-trapping"]
    }
  },
  "flight-training": {
    description: "This industry includes entities that offer aviation and flight training.",
    linkedinId: 2020,
    naics: {
      codes: ["611512"],
      slugs: ["flight-training"]
    },
    name: "Flight Training",
    parents: ["education", "technical-and-vocational-training"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "food-and-beverage-manufacturing": {
    description: "This industry includes entities that transform livestock and agricultural products into products for intermediate or final consumption, typically sold to wholesalers or retailers for distribution to consumers; retailers of bakery and candy products made on the premises not for immediate consumption are included.",
    linkedinId: 23,
    naics: {
      codes: [
        "311",
        "3112",
        "31121",
        "311211",
        "311212",
        "311213",
        "31122",
        "311221",
        "311224",
        "311225",
        "31123",
        "311230",
        "3119",
        "31191",
        "311911",
        "311919",
        "31192",
        "311920",
        "31193",
        "311930",
        "31194",
        "311941",
        "311942",
        "31199",
        "311991",
        "311999"
      ],
      slugs: [
        "all-other-food-manufacturing",
        "all-other-miscellaneous-food-manufacturing",
        "breakfast-cereal-manufacturing",
        "coffee-and-tea-manufacturing",
        "fats-and-oils-refining-and-blending",
        "flavoring-syrup-and-concentrate-manufacturing",
        "flour-milling",
        "flour-milling-and-malt-manufacturing",
        "food-manufacturing",
        "grain-and-oilseed-milling",
        "malt-manufacturing",
        "mayonnaise-dressing-and-other-prepared-sauce-manufacturing",
        "other-food-manufacturing",
        "other-snack-food-manufacturing",
        "perishable-prepared-food-manufacturing",
        "rice-milling",
        "roasted-nuts-and-peanut-butter-manufacturing",
        "seasoning-and-dressing-manufacturing",
        "snack-food-manufacturing",
        "soybean-and-other-oilseed-processing",
        "spice-and-extract-manufacturing",
        "starch-and-vegetable-fats-and-oils-manufacturing",
        "wet-corn-milling"
      ]
    },
    name: "Food and Beverage Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "2000",
        "2070",
        "2090"
      ],
      slugs: [
        "fats-oils",
        "food-and-kindred-products",
        "miscellaneous-food-preparations-kindred-products"
      ]
    }
  },
  "food-and-beverage-retail": {
    description: "This industry includes entities that primarily retail food and/or beverage merchandise from fixed point-of-sale locations and guarantee the proper storage and sanitary conditions required by regulatory authority.",
    linkedinId: 1339,
    naics: {
      codes: [
        "445",
        "4451",
        "44513",
        "445131",
        "4452",
        "44523",
        "445230",
        "44524",
        "445240",
        "44525",
        "445250",
        "44529",
        "445291",
        "445292",
        "445298",
        "4453",
        "44532",
        "445320"
      ],
      slugs: [
        "all-other-specialty-food-retailers",
        "baked-goods-retailers",
        "beer-wine-and-liquor-retailers",
        "confectionery-and-nut-retailers",
        "convenience-retailers",
        "convenience-retailers-and-vending-machine-operators",
        "fish-and-seafood-retailers",
        "food-and-beverage-retailers",
        "fruit-and-vegetable-retailers",
        "grocery-and-convenience-retailers",
        "meat-retailers",
        "other-specialty-food-retailers",
        "specialty-food-retailers"
      ]
    },
    name: "Food and Beverage Retail",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "food-and-beverage-services": {
    description: "This industry includes entities that prepare meals, snacks, and beverages to customer order, for immediate on-premises and off-premises consumption.",
    linkedinId: 34,
    naics: {
      codes: [
        "722",
        "7223",
        "72231",
        "722310"
      ],
      slugs: [
        "food-service-contractors",
        "food-services-and-drinking-places",
        "special-food-services"
      ]
    },
    name: "Food and Beverage Services",
    parents: ["accommodation-services"],
    sic: {
      codes: ["5810", "5812"],
      slugs: ["retail-eating-drinking-places", "retail-eating-places"]
    }
  },
  "footwear-and-leather-goods-repair": {
    description: "This industry includes entities that repair footwear and/or other leather or leather-like goods without retailing new footwear and leather or leather-like goods, such as handbags and briefcases.",
    linkedinId: 2255,
    naics: {
      codes: ["81143", "811430"],
      slugs: ["footwear-and-leather-goods-repair"]
    },
    name: "Footwear and Leather Goods Repair",
    parents: ["consumer-services", "repair-and-maintenance"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "footwear-manufacturing": {
    description: "This industry includes entities that manufacture footwear (except orthopedic extension footwear, which is in a separate industry).",
    linkedinId: 622,
    naics: {
      codes: [
        "3162",
        "31621",
        "316210"
      ],
      slugs: ["footwear-manufacturing"]
    },
    name: "Footwear Manufacturing",
    parents: ["leather-product-manufacturing", "manufacturing"],
    sic: {
      codes: ["3021", "3140"],
      slugs: ["footwear-no-rubber", "rubber-plastics-footwear"]
    }
  },
  "forestry-and-logging": {
    description: "This industry includes entities that grow and harvest timber on a long production cycle (such as, of 10 years or more).",
    linkedinId: 298,
    naics: {
      codes: [
        "113",
        "1131",
        "11311",
        "113110",
        "1132",
        "11321",
        "113210",
        "1133",
        "11331",
        "113310"
      ],
      slugs: [
        "forest-nurseries-and-gathering-of-forest-products",
        "forestry-and-logging",
        "logging",
        "timber-tract-operations"
      ]
    },
    name: "Forestry and Logging",
    parents: ["farming-ranching-forestry"],
    sic: {
      codes: ["800"],
      slugs: ["forestry"]
    }
  },
  "fossil-fuel-electric-power-generation": {
    description: "This industry includes entities that operate fossil-fuel-powered electric power generation facilities. These facilities use fossil fuels (coal, oil, or gas) in internal combustion or combustion-turbine conventional steam processes to produce electric energy provided to transmission or electric power distribution systems.",
    linkedinId: 385,
    naics: {
      codes: ["221112"],
      slugs: ["fossil-fuel-electric-power-generation"]
    },
    name: "Fossil Fuel Electric Power Generation",
    parents: ["electric-power-generation", "utilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "freight-and-package-transportation": {
    description: "This industry includes entities that primarily provide a variety of transportation services for freight, packages, and mail.",
    linkedinId: 87,
    naics: {
      codes: [
        "492",
        "4921",
        "49211",
        "492110",
        "4922",
        "49221",
        "492210"
      ],
      slugs: [
        "couriers-and-express-delivery-services",
        "couriers-and-messengers",
        "local-messengers-and-local-delivery"
      ]
    },
    name: "Freight and Package Transportation",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: ["4513"],
      slugs: ["air-courier-services"]
    }
  },
  "fruit-and-vegetable-preserves-manufacturing": {
    description: "This industry includes entities that freeze food and use preservation processes such as pickling, canning, and dehydrating. Production processes begin with inputs of vegetable or animal origin.",
    linkedinId: 504,
    naics: {
      codes: [
        "3114",
        "31141",
        "311411",
        "311412",
        "31142",
        "311421",
        "311422",
        "311423"
      ],
      slugs: [
        "dried-and-dehydrated-food-manufacturing",
        "frozen-food-manufacturing",
        "frozen-fruit-juice-and-vegetable-manufacturing",
        "frozen-specialty-food-manufacturing",
        "fruit-and-vegetable-canning",
        "fruit-and-vegetable-canning-pickling-and-drying",
        "fruit-and-vegetable-preserving-and-specialty-food-manufacturing",
        "specialty-canning"
      ]
    },
    name: "Fruit and Vegetable Preserves Manufacturing",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: ["2030", "2033"],
      slugs: ["canned-frozen-preservd-fruit-veg-food-specialties", "canned-fruits-veg-preserves-jams-jellies"]
    }
  },
  "fuel-cell-manufacturing": {
    description: "This industry includes entities that manufacture electrochemical conversion devices that convert chemical energy from fuel sources into electricity.",
    linkedinId: 3255,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Fuel Cell Manufacturing",
    parents: [
      "appliances-electrical-and-electronics-manufacturing",
      "electrical-equipment-manufacturing",
      "manufacturing"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "fundraising": {
    description: "This industry includes entities that provide business support services, in particular fundraising organization services on a contract or fee basis.",
    linkedinId: 101,
    naics: {
      codes: ["561499"],
      slugs: ["all-other-business-support-services"]
    },
    name: "Fundraising",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "funds-and-trusts": {
    description: "This industry includes legal entities (such as, funds, plans, and/or programs) organized to pool securities or other assets on behalf of shareholders or beneficiaries of employee benefit or other trust funds to earn interest, dividends, and other investment income.",
    linkedinId: 1742,
    naics: {
      codes: ["525"],
      slugs: ["funds-trusts-and-other-financial-vehicles"]
    },
    name: "Funds and Trusts",
    parents: ["financial-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "furniture-and-home-furnishings-manufacturing": {
    description: "This industry includes entities that make furniture and related articles, such as mattresses, window blinds, cabinets, and fixtures. Design may be performed by the entity\u2019s workforce or may be purchased from industrial designers.",
    linkedinId: 26,
    naics: {
      codes: [
        "337",
        "337212",
        "3379",
        "33791",
        "33792"
      ],
      slugs: [
        "blind-and-shade-manufacturing",
        "custom-architectural-woodwork-and-millwork-manufacturing",
        "furniture-and-related-product-manufacturing",
        "mattress-manufacturing",
        "other-furniture-related-product-manufacturing"
      ]
    },
    name: "Furniture and Home Furnishings Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "gambling-facilities-and-casinos": {
    description: "This industry includes entities that operate gambling facilities, such as casinos, bingo halls, and video gaming terminals, or that provision gambling services, such as lotteries and off-track betting.",
    linkedinId: 29,
    naics: {
      codes: [
        "7132",
        "71321",
        "713210",
        "71329",
        "713290"
      ],
      slugs: [
        "casinos-except-casino-hotels",
        "gambling-industries",
        "other-gambling-industries"
      ]
    },
    name: "Gambling Facilities and Casinos",
    parents: ["entertainment-providers", "recreational-facilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "geothermal-electric-power-generation": {
    description: "This industry includes entities that operate geothermal electric power generation facilities. These facilities use heat derived from the Earth to produce electric energy provided to transmission or electric power distribution systems.",
    linkedinId: 389,
    naics: {
      codes: ["221116"],
      slugs: ["geothermal-electric-power-generation"]
    },
    name: "Geothermal Electric Power Generation",
    parents: [
      "electric-power-generation",
      "renewable-energy-power-generation",
      "utilities"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "glass-ceramics-and-concrete-manufacturing": {
    description: "This industry includes entities that transform mined or quarried nonmetallic minerals, such as sand, gravel, stone, clay, and refractory materials, into products for intermediate or final consumption.",
    linkedinId: 145,
    naics: {
      codes: [
        "327",
        "3273",
        "32731",
        "327310",
        "32732",
        "327320",
        "32733",
        "327331",
        "327332",
        "32739",
        "327390"
      ],
      slugs: [
        "cement-and-concrete-product-manufacturing",
        "cement-manufacturing",
        "concrete-block-and-brick-manufacturing",
        "concrete-pipe-brick-and-block-manufacturing",
        "concrete-pipe-manufacturing",
        "nonmetallic-mineral-product-manufacturing",
        "other-concrete-product-manufacturing",
        "ready-mix-concrete-manufacturing"
      ]
    },
    name: "Glass, Ceramics and Concrete Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "3241",
        "3270",
        "3272",
        "3281"
      ],
      slugs: [
        "cement-hydraulic",
        "concrete-gypsum-plaster-products",
        "concrete-products-except-block-brick",
        "cut-stone-stone-products"
      ]
    }
  },
  "glass-product-manufacturing": {
    description: "This industry includes entities that manufacture glass and/or glass products by melting silica sand or cullet, or from purchased glass.",
    linkedinId: 779,
    naics: {
      codes: [
        "3272",
        "32721",
        "327211",
        "327212",
        "327213",
        "327215"
      ],
      slugs: [
        "flat-glass-manufacturing",
        "glass-and-glass-product-manufacturing",
        "glass-container-manufacturing",
        "glass-product-manufacturing-made-of-purchased-glass",
        "other-pressed-and-blown-glass-and-glassware-manufacturing"
      ]
    },
    name: "Glass Product Manufacturing",
    parents: ["glass-ceramics-and-concrete-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "3211",
        "3220",
        "3221",
        "3231"
      ],
      slugs: [
        "flat-glass",
        "glass-containers",
        "glass-glassware-pressed-or-blown",
        "glass-products-made-of-purchased-glass"
      ]
    }
  },
  "golf-courses-and-country-clubs": {
    description: "This industry includes entities that operate golf courses (except miniature) and operate golf courses along with dining facilities and other recreational facilities that are known as country clubs.",
    linkedinId: 2179,
    naics: {
      codes: ["71391", "713910"],
      slugs: ["golf-courses-and-country-clubs"]
    },
    name: "Golf Courses and Country Clubs",
    parents: ["entertainment-providers", "recreational-facilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "government-administration": {
    description: "This industry includes entities of federal, state, and local government agencies that administer, oversee, and manage public programs; organize and finance public goods and services; and have executive, legislative, or judicial authority over other institutions within a given area. These agencies set policy, create laws, adjudicate civil and criminal legal cases, and provide for public safety and national defense.",
    isParent: true,
    linkedinId: 75,
    naics: {
      codes: [
        "92",
        "922",
        "9221",
        "92213",
        "922130",
        "92214",
        "92215",
        "922150",
        "92216",
        "927"
      ],
      slugs: [
        "correctional-institutions",
        "fire-protection",
        "justice-public-order-and-safety-activities",
        "legal-counsel-and-prosecution",
        "parole-offices-and-probation-offices",
        "public-administration",
        "space-research-and-technology"
      ]
    },
    name: "Government Administration",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "government-relations-services": {
    description: "This industry includes entities aimed at impacting public policy at all levels of governance (local, regional, national and international) in order to promote the interests of a particular organization or group of organizations.",
    linkedinId: 148,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Government Relations Services",
    parents: ["advertising-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "graphic-design": {
    description: "This industry includes entities that plan, design, and manage the production of visual communication to convey specific messages or concepts, clarify complex information, or project visual identities, such as in the design of printed materials, packaging, advertising, signage systems, and logos. Commercial artists engaged exclusively in generating drawings and illustrations requiring technical accuracy or interpretative skills are included.",
    linkedinId: 140,
    naics: {
      codes: ["54143", "541430"],
      slugs: ["graphic-design-services"]
    },
    name: "Graphic Design",
    parents: ["design-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "ground-passenger-transportation": {
    description: "This industry includes entities that primarily provide a variety of ground passenger transportation services, such as urban transit systems; chartered bus, school bus, and interurban bus transportation; and taxis.",
    linkedinId: 1495,
    naics: {
      codes: [
        "485",
        "4855",
        "48551",
        "485510"
      ],
      slugs: ["charter-bus-industry", "transit-and-ground-passenger-transportation"]
    },
    name: "Ground Passenger Transportation",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "health-and-human-services": {
    description: "This industry includes government entities that administer human resource programs.",
    linkedinId: 2353,
    naics: {
      codes: [
        "923",
        "9231",
        "92314",
        "923140"
      ],
      slugs: ["administration-of-human-resource-programs", "administration-of-veterans-affairs"]
    },
    name: "Health and Human Services",
    parents: ["government-administration"],
    sic: {
      codes: ["8000", "8090"],
      slugs: ["services-health-services", "services-misc-health-allied-services-nec"]
    }
  },
  "higher-education": {
    description: "This industry includes entities that furnish academic courses and grant degrees at baccalaureate or graduate levels, where the requirement for admission is at least a high school diploma or equivalent general academic training.",
    linkedinId: 68,
    naics: {
      codes: [
        "6113",
        "61131",
        "611310"
      ],
      slugs: ["colleges-universities-and-professional-schools"]
    },
    name: "Higher Education",
    parents: ["education"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "highway-street-and-bridge-construction": {
    description: "This industry includes entities that construct highways (including elevated), streets, roads, airport runways, public sidewalks, or bridges. The work performed may include new work, reconstruction, rehabilitation, and repairs.",
    linkedinId: 431,
    naics: {
      codes: [
        "2373",
        "23731",
        "237310"
      ],
      slugs: ["highway-street-and-bridge-construction"]
    },
    name: "Highway, Street, and Bridge Construction",
    parents: ["civil-engineering", "construction"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "historical-sites": {
    description: "This industry includes entities that preserve and exhibit sites, buildings, forts, or communities that describe events or persons of particular historical interest, such as archeological sites, battlefields, historical ships, and pioneer villages.",
    linkedinId: 2161,
    naics: {
      codes: ["71212", "712120"],
      slugs: ["historical-sites"]
    },
    name: "Historical Sites",
    parents: ["entertainment-providers", "museums-historical-sites-and-zoos"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "holding-companies": {
    description: "This industry includes entities that hold equity interests in companies and enterprises for the purpose of owning a controlling interest or influencing management decisions. Entities in this industry don't have any of their own operations, but rather only have investments in other firms. Parent companies that have their own independent operations and also have subsidiaries are not included in this industry.",
    isParent: true,
    linkedinId: 1905,
    naics: {
      codes: [
        "55",
        "551",
        "5511",
        "55111",
        "551111",
        "551112",
        "551114"
      ],
      slugs: [
        "corporate-subsidiary-and-regional-managing-offices",
        "management-of-companies-and-enterprises",
        "offices-of-bank-holding-companies",
        "offices-of-other-holding-companies"
      ]
    },
    name: "Holding Companies",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "home-health-care-services": {
    description: "This industry includes entities that provide skilled nursing services in the home, along with a range of the following: personal care services; homemaker and companion services; physical therapy; medical social services; medication; support for medical equipment and supplies; counseling; 24-hour home care; occupation and vocational therapy; dietary and nutritional services; speech therapy; audiology; and high-tech care, such as intravenous therapy.",
    linkedinId: 2074,
    naics: {
      codes: [
        "6216",
        "62161",
        "621610"
      ],
      slugs: ["home-health-care-services"]
    },
    name: "Home Health Care Services",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: ["8082"],
      slugs: ["services-home-health-care-services"]
    }
  },
  "horticulture": {
    description: "This industry includes entities that cultivate garden plants, such as fruits, vegetables, flowers, and ornamentals.",
    linkedinId: 150,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Horticulture",
    parents: ["farming", "farming-ranching-forestry"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "hospitality": {
    description: "This industry includes entities that provide customers with lodging and/or prepared meals, snacks, and beverages for immediate consumption.",
    linkedinId: 31,
    naics: {
      codes: [
        "7211",
        "72112",
        "721120"
      ],
      slugs: ["casino-hotels", "traveler-accommodation"]
    },
    name: "Hospitality",
    parents: ["accommodation-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "hospitals": {
    description: "This industry includes entities that provide varied medical, diagnostic, and treatment services that include physician, nursing, and other health services to inpatients.",
    linkedinId: 2081,
    naics: {
      codes: [
        "622",
        "6221",
        "62211",
        "622110",
        "6222",
        "62221",
        "622210",
        "6223",
        "62231",
        "622310"
      ],
      slugs: [
        "general-medical-and-surgical-hospitals",
        "hospitals",
        "psychiatric-and-substance-abuse-hospitals",
        "specialty-except-psychiatric-and-substance-abuse-hospitals"
      ]
    },
    name: "Hospitals",
    parents: ["hospitals-and-health-care"],
    sic: {
      codes: ["8060", "8062"],
      slugs: ["services-general-medical-surgical-hospitals-nec", "services-hospitals"]
    }
  },
  "hospitals-and-health-care": {
    description: "This industry includes entities that provide health care and health-related social assistance for individuals. It includes entities that provide medical care exclusively, health care and social assistance, and only social assistance. These entities deliver services by trained professional health practitioners or social workers.",
    isParent: true,
    linkedinId: 14,
    naics: {
      codes: ["62", "624"],
      slugs: ["health-care-and-social-assistance", "social-assistance"]
    },
    name: "Hospitals and Health Care",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "hotels-and-motels": {
    description: "This industry includes entities that provide short-term lodging specifically in facilities known as hotels, motor hotels, resort hotels, and motels.",
    linkedinId: 2194,
    naics: {
      codes: ["72111", "721110"],
      slugs: ["hotels-except-casino-hotels-and-motels"]
    },
    name: "Hotels and Motels",
    parents: ["accommodation-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "household-and-institutional-furniture-manufacturing": {
    description: "This industry includes entities that manufacture household-type furniture, such as living room, kitchen and bedroom furniture and institutional (such as, public building) furniture, such as furniture for schools, theaters, and churches.",
    linkedinId: 1080,
    naics: {
      codes: [
        "3371",
        "33711",
        "337110",
        "33712",
        "337121",
        "337122",
        "337126",
        "337127"
      ],
      slugs: [
        "household-and-institutional-furniture-and-kitchen-cabinet-manufacturing",
        "household-and-institutional-furniture-manufacturing",
        "household-furniture-except-wood-and-upholstered-manufacturing",
        "institutional-furniture-manufacturing",
        "nonupholstered-wood-household-furniture-manufacturing",
        "upholstered-household-furniture-manufacturing",
        "wood-kitchen-cabinet-and-countertop-manufacturing"
      ]
    },
    name: "Household and Institutional Furniture Manufacturing",
    parents: ["furniture-and-home-furnishings-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "2510",
        "2511",
        "2531",
        "2590"
      ],
      slugs: [
        "household-furniture",
        "miscellaneous-furniture-fixtures",
        "public-bldg-related-furniture",
        "wood-household-furniture-no-upholstered"
      ]
    }
  },
  "household-appliance-manufacturing": {
    description: "This industry includes entities that manufacture small electric appliances, electric housewares, and major household appliances.",
    linkedinId: 1005,
    naics: {
      codes: [
        "3352",
        "33521",
        "335210",
        "33522",
        "335220"
      ],
      slugs: [
        "household-appliance-manufacturing",
        "major-household-appliance-manufacturing",
        "small-electrical-appliance-manufacturing"
      ]
    },
    name: "Household Appliance Manufacturing",
    parents: ["appliances-electrical-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: ["3630", "3634"],
      slugs: ["electric-housewares-fans", "household-appliances"]
    }
  },
  "household-services": {
    description: "This industry includes private households that employ workers on or off the premises in activities primarily concerned with the operation of the household, including cooks, maids, butlers, and outside workers, such as gardeners, caretakers, and other maintenance workers. This industry includes Private Service Professionals who primarily support the staff of individuals and families.",
    linkedinId: 2318,
    naics: {
      codes: [
        "814",
        "8141",
        "81411",
        "814110"
      ],
      slugs: ["private-households"]
    },
    name: "Household Services",
    parents: ["consumer-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "housing-and-community-development": {
    description: "This industry includes government entities that administer programs for housing, urban planning, and community development.",
    linkedinId: 2369,
    naics: {
      codes: [
        "925",
        "9251",
        "92511"
      ],
      slugs: ["administration-of-housing-programs", "administration-of-housing-programs-urban-planning-and-community-development"]
    },
    name: "Housing and Community Development",
    parents: ["government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "housing-programs": {
    description: "This industry includes government entities that plan and administer housing programs.",
    linkedinId: 3081,
    naics: {
      codes: ["925110"],
      slugs: ["administration-of-housing-programs"]
    },
    name: "Housing Programs",
    parents: ["government-administration", "housing-and-community-development"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "human-resources-services": {
    description: "This industry includes entities that provide advice and assistance to businesses and organizations in: human resource and personnel policies, practices, and procedures; employee benefits planning, communication, and administration; compensation systems planning; and/or wage and salary administration. Outsourcing firms that provide human resources services to their clients are not included in this industry.",
    linkedinId: 137,
    naics: {
      codes: ["541612"],
      slugs: ["human-resources-consulting-services"]
    },
    name: "Human Resources Services",
    parents: ["business-consulting-and-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "hvac-and-refrigeration-equipment-manufacturing": {
    description: "This industry includes entities that manufacture ventilating, heating, air-conditioning, and commercial or industrial refrigeration and freezer equipment.",
    linkedinId: 923,
    naics: {
      codes: [
        "3334",
        "33341",
        "333413",
        "333414",
        "333415"
      ],
      slugs: [
        "air-conditioning-and-warm-air-heating-equipment-and-commercial-and-industrial-refrigeration-equipment-manufacturing",
        "heating-equipment-except-warm-air-furnaces-manufacturing",
        "industrial-and-commercial-fan-and-blower-and-air-purification-equipment-manufacturing",
        "ventilation-heating-air-conditioning-and-commercial-refrigeration-equipment-manufacturing"
      ]
    },
    name: "HVAC and Refrigeration Equipment Manufacturing",
    parents: ["machinery-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "hydroelectric-power-generation": {
    description: "This industry includes entities that operate hydroelectric power generation facilities. These facilities use waterpower to drive a turbine and produce electric energy provided to transmission or electric power distribution systems.",
    linkedinId: 384,
    naics: {
      codes: ["221111"],
      slugs: ["hydroelectric-power-generation"]
    },
    name: "Hydroelectric Power Generation",
    parents: [
      "electric-power-generation",
      "renewable-energy-power-generation",
      "utilities"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "individual-and-family-services": {
    description: "This industry includes entities that provide nonresidential social assistance to children and youth, the elderly, persons with disabilities, and all other individuals and families.",
    linkedinId: 88,
    naics: {
      codes: [
        "6241",
        "62411",
        "624110",
        "62419",
        "624190"
      ],
      slugs: [
        "child-and-youth-services",
        "individual-and-family-services",
        "other-individual-and-family-services"
      ]
    },
    name: "Individual and Family Services",
    parents: ["hospitals-and-health-care"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "industrial-machinery-manufacturing": {
    description: "This industry includes entities that manufacture industrial machinery, such as food and beverage manufacturing machinery, semiconductor manufacturing machinery, sawmill and woodworking machinery (except handheld), machinery for making paper and paper products, printing and binding machinery and equipment, textile making machinery, and machinery for making plastics and rubber products.",
    linkedinId: 135,
    naics: {
      codes: [
        "3332",
        "33324",
        "333241",
        "333242",
        "333243",
        "333248"
      ],
      slugs: [
        "all-other-industrial-machinery-manufacturing",
        "food-product-machinery-manufacturing",
        "industrial-machinery-manufacturing",
        "sawmill-woodworking-and-paper-machinery-manufacturing",
        "semiconductor-machinery-manufacturing"
      ]
    },
    name: "Industrial Machinery Manufacturing",
    parents: ["machinery-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "3537",
        "3560",
        "3561",
        "3564",
        "3567",
        "3569",
        "3580",
        "3585",
        "3590",
        "3822"
      ],
      slugs: [
        "air-cond-warm-air-heatg-equip-comm-indl-refrig-equip",
        "auto-controls-for-regulating-residential-comml-environments",
        "general-industrial-machinery-equipment",
        "general-industrial-machinery-equipment-nec",
        "industrial-commercial-fans-blowers-air-purifying-equip",
        "industrial-process-furnaces-ovens",
        "industrial-trucks-tractors-trailors-stackers",
        "misc-industrial-commercial-machinery-equipment",
        "pumps-pumping-equipment",
        "refrigeration-service-industry-machinery"
      ]
    }
  },
  "industry-associations": {
    description: "This industry includes entities that promote the business interests of their member companies and may conduct research on new products and services; develop market statistics; sponsor quality and certification standards; lobby public officials; or publish newsletters, books, or periodicals for distribution to their members.",
    linkedinId: 1909,
    naics: {
      codes: ["81391", "813910"],
      slugs: ["business-associations"]
    },
    name: "Industry Associations",
    parents: ["civic-and-social-organizations", "consumer-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "information-services": {
    description: "This industry includes entities that supply information, store and provide access to information, search and retrieve information, or operate Web sites that use search engines to allow for Internet search. Examples are libraries, archives, and Web search portals.",
    linkedinId: 84,
    naics: {
      codes: [
        "519",
        "5192",
        "51929",
        "519290"
      ],
      slugs: ["web-search-portals-and-all-other-information-services", "web-search-portals-libraries-archives-and-other-information-services"]
    },
    name: "Information Services",
    parents: ["technology-information-and-internet", "technology-information-and-media"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "insurance": {
    description: "This industry includes entities that underwrite annuities and insurance policies or sell insurance policies and provide other insurance and employee-benefit-related services.",
    linkedinId: 42,
    naics: {
      codes: ["524", "5242"],
      slugs: ["agencies-brokerages-and-other-insurance-related-activities", "insurance-carriers-and-related-activities"]
    },
    name: "Insurance",
    parents: ["financial-services"],
    sic: {
      codes: [
        "6311",
        "6321",
        "6324",
        "6331",
        "6351",
        "6361",
        "6399"
      ],
      slugs: [
        "accident-health-insurance",
        "fire-marine-casualty-insurance",
        "hospital-medical-service-plans",
        "insurance-carriers-nec",
        "life-insurance",
        "surety-insurance",
        "title-insurance"
      ]
    }
  },
  "insurance-agencies-and-brokerages": {
    description: "This industry includes entities that primarily act as agents (such as, brokers) in selling annuities and insurance policies.",
    linkedinId: 1737,
    naics: {
      codes: ["52421", "524210"],
      slugs: ["insurance-agencies-and-brokerages"]
    },
    name: "Insurance Agencies and Brokerages",
    parents: ["financial-services", "insurance"],
    sic: {
      codes: ["6411"],
      slugs: ["insurance-agents-brokers-service"]
    }
  },
  "insurance-and-employee-benefit-funds": {
    description: "This industry includes legal entities (such as, funds, plans, and/or programs) organized to provide insurance and employee benefits exclusively for the sponsor, firm, or its employees or members.",
    linkedinId: 1743,
    naics: {
      codes: [
        "5251",
        "52512",
        "525120",
        "52519",
        "525190"
      ],
      slugs: [
        "health-and-welfare-funds",
        "insurance-and-employee-benefit-funds",
        "other-insurance-funds"
      ]
    },
    name: "Insurance and Employee Benefit Funds",
    parents: ["financial-services", "funds-and-trusts"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "insurance-carriers": {
    description: "This industry includes entities that primarily provide underwriting (assuming the risk, assigning premiums, and so forth) annuities and insurance policies and investing premiums to build up a portfolio of financial assets to be used against future claims.",
    linkedinId: 1725,
    naics: {
      codes: [
        "5241",
        "52411",
        "524113",
        "524114",
        "52412",
        "524126",
        "524127",
        "524128",
        "52413",
        "524130"
      ],
      slugs: [
        "direct-health-and-medical-insurance-carriers",
        "direct-insurance-except-life-health-and-medical-carriers",
        "direct-life-health-and-medical-insurance-carriers",
        "direct-life-insurance-carriers",
        "direct-property-and-casualty-insurance-carriers",
        "direct-title-insurance-carriers",
        "insurance-carriers",
        "other-direct-insurance-except-life-health-and-medical-carriers",
        "reinsurance-carriers"
      ]
    },
    name: "Insurance Carriers",
    parents: ["financial-services", "insurance"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "interior-design": {
    description: "This industry includes entities that plan, design, and administer projects in interior spaces to meet the physical and aesthetic needs of people using them, taking into consideration building codes, health and safety regulations, traffic patterns and floor planning, mechanical and electrical needs, and interior fittings and furniture.",
    linkedinId: 3126,
    naics: {
      codes: ["54141", "541410"],
      slugs: ["interior-design-services"]
    },
    name: "Interior Design",
    parents: ["design-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "international-affairs": {
    description: "This industry includes entities of local and foreign governments that focus on international affairs and programs relating to other nations and peoples.",
    linkedinId: 74,
    naics: {
      codes: ["92812", "928120"],
      slugs: ["international-affairs"]
    },
    name: "International Affairs",
    parents: ["government-administration", "military-and-international-affairs"],
    sic: {
      codes: ["8888", "9721"],
      slugs: ["foreign-governments", "international-affairs"]
    }
  },
  "international-trade-and-development": {
    description: "This industry includes entities that provide working capital funds to exporters; lend funds to foreign buyers of goods; or lend funds to domestic buyers of imported goods.",
    linkedinId: 141,
    naics: {
      codes: ["522299"],
      slugs: ["international-secondary-market-and-all-other-nondepository-credit-intermediation"]
    },
    name: "International Trade and Development",
    parents: ["credit-intermediation", "financial-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "internet-marketplace-platforms": {
    description: "This industry includes entities that provide a web-based platform for the buying and selling of goods owned by others, or as a marketplace that creates a trading opportunity between two entities or parties (e.g., matching platforms), generally on a fee or commission basis.",
    linkedinId: 1285,
    naics: {
      codes: [
        "425",
        "4251",
        "42512",
        "425120"
      ],
      slugs: ["wholesale-trade-agents-and-brokers"]
    },
    name: "Internet Marketplace Platforms",
    parents: ["technology-information-and-internet", "technology-information-and-media"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "internet-news": {
    description: "This industry includes entities that publish and/or broadcast news content on the Internet exclusively or operate Web sites that use a search engine to generate and maintain extensive databases of news in an easily searchable format, and do not provide traditional (non-Internet) versions of the content that they publish or broadcast.",
    linkedinId: 3124,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Internet News",
    parents: [
      "information-services",
      "internet-publishing",
      "online-audio-and-video-media",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "internet-publishing": {
    description: "This industry includes entities that publish and/or broadcast content on the Internet exclusively. They do not provide traditional (non-Internet) versions of the content they publish or broadcast.",
    linkedinId: 3132,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Internet Publishing",
    parents: [
      "information-services",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "interurban-and-rural-bus-services": {
    description: "This industry includes entities that primarily provide bus passenger transportation over regular routes and on regular schedules, principally outside a single metropolitan area.",
    linkedinId: 1504,
    naics: {
      codes: [
        "4852",
        "48521",
        "485210"
      ],
      slugs: ["interurban-and-rural-bus-transportation"]
    },
    name: "Interurban and Rural Bus Services",
    parents: ["ground-passenger-transportation", "transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "investment-advice": {
    description: "This industry includes entities that provide customized investment advice to clients on a fee basis, but do not have the authority to execute trades.",
    linkedinId: 1720,
    naics: {
      codes: ["52394", "523940"],
      slugs: ["portfolio-management-and-investment-advice"]
    },
    name: "Investment Advice",
    parents: ["capital-markets", "financial-services"],
    sic: {
      codes: ["6282"],
      slugs: ["investment-advice"]
    }
  },
  "investment-banking": {
    description: "This industry includes entities that underwrite, originate, and/or maintain markets for issues of securities, and entities that act as principals in buying or selling securities on a spread basis, such as securities dealers or stock option dealers.",
    linkedinId: 45,
    naics: {
      codes: ["52315", "523150"],
      slugs: ["investment-banking-and-securities-intermediation"]
    },
    name: "Investment Banking",
    parents: ["capital-markets", "financial-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "investment-management": {
    description: "This industry includes entities that act as principals or brokers in buying or selling financial contracts (except investment bankers and securities and commodity contracts dealers and brokerages, which are in separate industries). Also included are investment services (except securities and commodity exchanges, which are in separate industries) such as portfolio management, investment advice, and trust, fiduciary, and custody services.",
    linkedinId: 46,
    naics: {
      codes: [
        "5239",
        "52399",
        "523991",
        "523999"
      ],
      slugs: [
        "all-other-financial-investment-activities",
        "miscellaneous-financial-investment-activities",
        "other-financial-investment-activities",
        "trust-fiduciary-and-custody-activities"
      ]
    },
    name: "Investment Management",
    parents: ["capital-markets", "financial-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "it-services-and-it-consulting": {
    description: "This industry includes entities that: write, modify, test, and support software to meet the needs of a customer; plan and design computer systems that integrate computer hardware, software, and communication technologies; manage and operate clients' computer systems and/or data processing facilities on-site; and/or provide other professional and technical computer-related advice and services.",
    linkedinId: 96,
    naics: {
      codes: [
        "5415",
        "54151",
        "541519"
      ],
      slugs: ["computer-systems-design-and-related-services", "other-computer-related-services"]
    },
    name: "IT Services and IT Consulting",
    parents: ["professional-services"],
    sic: {
      codes: ["7370", "7371"],
      slugs: ["services-computer-programming-data-processing-etc", "services-computer-programming-services"]
    }
  },
  "it-system-custom-software-development": {
    description: "This industry includes entities that focus on creating, modifying, testing, and supporting software to meet the needs of a particular customer.",
    linkedinId: 3102,
    naics: {
      codes: ["541511"],
      slugs: ["custom-computer-programming-services"]
    },
    name: "IT System Custom Software Development",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "it-system-data-services": {
    description: "This industry includes entities that provide human expertise for data collection, data annotation, data review, data normalization, and data entry and other manual or semi-automated data services.",
    linkedinId: 3106,
    naics: {
      codes: [],
      slugs: []
    },
    name: "IT System Data Services",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: ["7374"],
      slugs: ["services-computer-processing-data-preparation"]
    }
  },
  "it-system-design-services": {
    description: "This industry includes entities that plan and design computer systems that integrate computer hardware, software, and communication technologies.",
    linkedinId: 1855,
    naics: {
      codes: ["541512"],
      slugs: ["computer-systems-design-services"]
    },
    name: "IT System Design Services",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: ["7373"],
      slugs: ["services-computer-integrated-systems-design"]
    }
  },
  "it-system-installation-and-disposal": {
    description: "This industry includes entities that provide IT services related to installing and deploying new IT systems, disaster recovery services, and/or uninstalling, and disposing of no-longer-needed IT systems or components.",
    linkedinId: 3104,
    naics: {
      codes: [],
      slugs: []
    },
    name: "IT System Installation and Disposal",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: ["7377"],
      slugs: ["services-computer-rental-leasing"]
    }
  },
  "it-system-operations-and-maintenance": {
    description: "This industry includes entities that manage and/or operate clients' computer systems and/or data processing facilities (on-site or remote).",
    linkedinId: 3103,
    naics: {
      codes: ["541513"],
      slugs: ["computer-facilities-management-services"]
    },
    name: "IT System Operations and Maintenance",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "it-system-testing-and-evaluation": {
    description: "This industry includes entities that provide training for and evaluation of IT systems and components.",
    linkedinId: 3107,
    naics: {
      codes: [],
      slugs: []
    },
    name: "IT System Testing and Evaluation",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "it-system-training-and-support": {
    description: "This industry includes entities that provide training and technical support for users and maintainers of IT systems.",
    linkedinId: 3105,
    naics: {
      codes: [],
      slugs: []
    },
    name: "IT System Training and Support",
    parents: ["it-services-and-it-consulting", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "janitorial-services": {
    description: "This industry includes entities that clean building interiors, interiors of transportation equipment (e.g., aircraft, rail cars, ships), and/or windows.",
    linkedinId: 1965,
    naics: {
      codes: ["56172", "561720"],
      slugs: ["janitorial-services"]
    },
    name: "Janitorial Services",
    parents: ["administrative-and-support-services", "facilities-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "landscaping-services": {
    description: "This industry includes entities that: provide landscape care and maintenance services and/or installing trees, shrubs, plants, lawns, or gardens ; provide these services along with the design of landscape plans and/or the installation of walkways, retaining walls, decks, fences, ponds, and similar structures.",
    linkedinId: 2934,
    naics: {
      codes: ["561730"],
      slugs: ["landscaping-services"]
    },
    name: "Landscaping Services",
    parents: ["administrative-and-support-services", "facilities-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "language-schools": {
    description: "This industry includes entities that offer foreign language instruction (including sign language).",
    linkedinId: 2029,
    naics: {
      codes: ["61163", "611630"],
      slugs: ["language-schools"]
    },
    name: "Language Schools",
    parents: ["education", "technical-and-vocational-training"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "laundry-and-drycleaning-services": {
    description: "This industry includes entities that operate coin-operated or similar self-service laundries and drycleaners; that provide drycleaning and laundry services; and supply, on a rental or contract basis, laundered items (e.g., uniforms, gowns, shop towels, etc.).",
    linkedinId: 2272,
    naics: {
      codes: [
        "8123",
        "81231",
        "812310",
        "81232",
        "812320",
        "81233",
        "812331",
        "812332"
      ],
      slugs: [
        "coin-operated-laundries-and-drycleaners",
        "drycleaning-and-laundry-services",
        "drycleaning-and-laundry-services-except-coin-operated",
        "industrial-launderers",
        "linen-and-uniform-supply",
        "linen-supply"
      ]
    },
    name: "Laundry and Drycleaning Services",
    parents: ["consumer-services", "personal-and-laundry-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "law-enforcement": {
    description: "This industry includes government entities that enforce criminal and civil law, provide police activity, traffic safety, and other activities related to the enforcement of the law and preservation of order. Combined police and fire departments are included.",
    linkedinId: 77,
    naics: {
      codes: ["92212", "922120"],
      slugs: ["police-protection"]
    },
    name: "Law Enforcement",
    parents: ["administration-of-justice", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "law-practice": {
    description: "This industry includes offices of lawyers or attorneys (such as, counselors-at-law) that provide expertise in a range or in specific areas of law, such as criminal law, corporate law, family and estate law, patent law, real estate law, or tax law.",
    linkedinId: 9,
    naics: {
      codes: ["54111", "541110"],
      slugs: ["offices-of-lawyers"]
    },
    name: "Law Practice",
    parents: ["legal-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "leasing-non-residential-real-estate": {
    description: "This industry includes entities that act as lessors of buildings (except mini warehouses and self-storage units, which are in separate industries) that are not used as residences or dwellings.",
    linkedinId: 128,
    naics: {
      codes: ["53112", "531120"],
      slugs: ["lessors-of-nonresidential-buildings-except-miniwarehouses"]
    },
    name: "Leasing Non-residential Real Estate",
    parents: ["real-estate", "real-estate-and-equipment-rental-services"],
    sic: {
      codes: [
        "6510",
        "6512",
        "6519"
      ],
      slugs: [
        "lessors-of-real-property-nec",
        "operators-of-nonresidential-buildings",
        "real-estate-operators-no-developers-lessors"
      ]
    }
  },
  "leasing-residential-real-estate": {
    description: "This industry includes entities that act as lessors of residential buildings and dwellings.",
    linkedinId: 1759,
    naics: {
      codes: [
        "531",
        "5311",
        "53111",
        "531110",
        "53113",
        "531130",
        "53119",
        "531190",
        "5313",
        "53131",
        "531311",
        "531312",
        "53132",
        "531320",
        "53139",
        "531390"
      ],
      slugs: [
        "activities-related-to-real-estate",
        "lessors-of-miniwarehouses-and-self-storage-units",
        "lessors-of-other-real-estate-property",
        "lessors-of-real-estate",
        "lessors-of-residential-buildings-and-dwellings",
        "nonresidential-property-managers",
        "offices-of-real-estate-appraisers",
        "other-activities-related-to-real-estate",
        "real-estate",
        "real-estate-property-managers",
        "residential-property-managers"
      ]
    },
    name: "Leasing Residential Real Estate",
    parents: ["real-estate", "real-estate-and-equipment-rental-services"],
    sic: {
      codes: ["6513"],
      slugs: ["operators-of-apartment-buildings"]
    }
  },
  "leather-product-manufacturing": {
    description: "This industry includes entities that transform hides into leather by tanning or curing and fabricating the leather into products (except apparel, which is in a separate industry). Included is the manufacture of similar products made from leather substitutes, such as rubber footwear, textile luggage, and plastic wallets.",
    linkedinId: 616,
    naics: {
      codes: [
        "316",
        "3161",
        "31611",
        "316110",
        "3169",
        "31699",
        "316990"
      ],
      slugs: [
        "leather-and-allied-product-manufacturing",
        "leather-and-hide-tanning-and-finishing",
        "other-leather-and-allied-product-manufacturing"
      ]
    },
    name: "Leather Product Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: ["3100"],
      slugs: ["leather-leather-products"]
    }
  },
  "legal-services": {
    description: "This industry includes entities that offer legal services, such as those offered by offices of lawyers, offices of notaries, title abstract and settlement offices, and paralegal services.",
    linkedinId: 10,
    naics: {
      codes: [
        "5411",
        "54112",
        "541120",
        "54119",
        "541191",
        "541199"
      ],
      slugs: [
        "all-other-legal-services",
        "legal-services",
        "offices-of-notaries",
        "other-legal-services",
        "title-abstract-and-settlement-offices"
      ]
    },
    name: "Legal Services",
    parents: ["professional-services"],
    sic: {
      codes: ["8111"],
      slugs: ["services-legal-services"]
    }
  },
  "legislative-offices": {
    description: "This industry includes government entities that serve as legislative bodies and their advisory committees and commissions.",
    linkedinId: 72,
    naics: {
      codes: ["92112", "921120"],
      slugs: ["legislative-bodies"]
    },
    name: "Legislative Offices",
    parents: ["government-administration", "public-policy-offices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "libraries": {
    description: "This industry includes entities that provide library or archive services. They maintain collections of documents (e.g., books, journals, newspapers, and music) and archival material of historical interest for informational, research, educational, or recreational use. All or portions may be accessible electronically.",
    linkedinId: 85,
    naics: {
      codes: ["51921", "519210"],
      slugs: ["libraries-and-archives"]
    },
    name: "Libraries",
    parents: [
      "information-services",
      "internet-publishing",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "lime-and-gypsum-products-manufacturing": {
    description: "This industry includes entities that manufacture lime from calcitic limestone, dolomitic limestone, or other calcareous materials or manufacturing gypsum products.",
    linkedinId: 794,
    naics: {
      codes: [
        "3274",
        "32741",
        "327410",
        "32742",
        "327420"
      ],
      slugs: [
        "gypsum-product-manufacturing",
        "lime-and-gypsum-product-manufacturing",
        "lime-manufacturing"
      ]
    },
    name: "Lime and Gypsum Products Manufacturing",
    parents: ["glass-ceramics-and-concrete-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "loan-brokers": {
    description: "This industry includes entities that primarily arrange loans by bringing borrowers and lenders together on a commission or fee basis.",
    linkedinId: 1696,
    naics: {
      codes: ["52231", "522310"],
      slugs: ["mortgage-and-nonmortgage-loan-brokers"]
    },
    name: "Loan Brokers",
    parents: ["credit-intermediation", "financial-services"],
    sic: {
      codes: ["6163"],
      slugs: ["loan-brokers"]
    }
  },
  "machinery-manufacturing": {
    description: "This industry includes entities that create end products that apply mechanical force, for example, the application of gears and levers, to perform work.",
    linkedinId: 55,
    naics: {
      codes: [
        "333",
        "3339",
        "33391",
        "333912",
        "333914",
        "33392",
        "333921",
        "333922",
        "333923",
        "333924",
        "33399",
        "333991",
        "333992",
        "333993",
        "333994",
        "333995",
        "333996",
        "333998"
      ],
      slugs: [
        "air-and-gas-compressor-manufacturing",
        "all-other-general-purpose-machinery-manufacturing",
        "all-other-miscellaneous-general-purpose-machinery-manufacturing",
        "conveyor-and-conveying-equipment-manufacturing",
        "elevator-and-moving-stairway-manufacturing",
        "fluid-power-cylinder-and-actuator-manufacturing",
        "fluid-power-pump-and-motor-manufacturing",
        "industrial-process-furnace-and-oven-manufacturing",
        "industrial-truck-tractor-trailer-and-stacker-machinery-manufacturing",
        "machinery-manufacturing",
        "material-handling-equipment-manufacturing",
        "measuring-dispensing-and-other-pumping-equipment-manufacturing",
        "other-general-purpose-machinery-manufacturing",
        "overhead-traveling-crane-hoist-and-monorail-system-manufacturing",
        "packaging-machinery-manufacturing",
        "power-driven-handtool-manufacturing",
        "pump-and-compressor-manufacturing",
        "welding-and-soldering-equipment-manufacturing"
      ]
    },
    name: "machinery-manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "magnetic-and-optical-media-manufacturing": {
    description: "This industry includes entities that manufacture optical and magnetic media, such as blank audio tapes, blank video tapes, and blank diskettes, and/or mass duplicate (such as, making copies) audio, video, software, and other data on magnetic, optical, and similar media.",
    linkedinId: 994,
    naics: {
      codes: [
        "3346",
        "33461",
        "334610"
      ],
      slugs: ["manufacturing-and-reproducing-magnetic-and-optical-media"]
    },
    name: "Magnetic and Optical Media Manufacturing",
    parents: ["computers-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: ["3695", "3827"],
      slugs: ["magnetic-optical-recording-media", "optical-instruments-lenses"]
    }
  },
  "manufacturing": {
    description: "This industry includes entities that use mechanical, physical, or chemical transformation of materials, substances, or components to create new products. Included are entities that assemble component parts of manufactured products.",
    isParent: true,
    linkedinId: 25,
    naics: {
      codes: [
        "31",
        "312",
        "3141",
        "31411",
        "314110",
        "31412",
        "314120",
        "3149",
        "31491",
        "314910",
        "31499",
        "314994",
        "314999",
        "339",
        "3399",
        "33991",
        "339910",
        "33993",
        "339930",
        "33994",
        "339940",
        "33995",
        "339950",
        "33999",
        "339991",
        "339992",
        "339993",
        "339994",
        "339995",
        "339999"
      ],
      slugs: [
        "all-other-miscellaneous-manufacturing",
        "all-other-miscellaneous-textile-product-mills",
        "all-other-textile-product-mills",
        "beverage-and-tobacco-product-manufacturing",
        "broom-brush-and-mop-manufacturing",
        "burial-casket-manufacturing",
        "carpet-and-rug-mills",
        "curtain-and-linen-mills",
        "doll-toy-and-game-manufacturing",
        "fastener-button-needle-and-pin-manufacturing",
        "gasket-packing-and-sealing-device-manufacturing",
        "jewelry-and-silverware-manufacturing",
        "manufacturing",
        "miscellaneous-manufacturing",
        "musical-instrument-manufacturing",
        "office-supplies-except-paper-manufacturing",
        "other-miscellaneous-manufacturing",
        "other-textile-product-mills",
        "rope-cordage-twine-tire-cord-and-tire-fabric-mills",
        "sign-manufacturing",
        "textile-bag-and-canvas-mills",
        "textile-furnishings-mills"
      ]
    },
    name: "Manufacturing",
    parents: [],
    sic: {
      codes: [
        "3942",
        "3944",
        "3990"
      ],
      slugs: [
        "dolls-stuffed-toys",
        "games-toys-children-s-vehicles-no-dolls-bicycles",
        "miscellaneous-manufacturing-industries"
      ]
    }
  },
  "maritime-transportation": {
    description: "This industry includes entities that transport passengers and cargo using watercraft, such as ships, barges, and boats. This includes deep sea, coastal, and inland water transportation.",
    linkedinId: 95,
    naics: {
      codes: [
        "483",
        "4831",
        "48311",
        "483111",
        "483112",
        "483113",
        "483114",
        "4832",
        "48321",
        "483211",
        "483212"
      ],
      slugs: [
        "coastal-and-great-lakes-freight-transportation",
        "coastal-and-great-lakes-passenger-transportation",
        "deep-sea-coastal-and-great-lakes-water-transportation",
        "deep-sea-freight-transportation",
        "deep-sea-passenger-transportation",
        "inland-water-freight-transportation",
        "inland-water-passenger-transportation",
        "inland-water-transportation",
        "water-transportation"
      ]
    },
    name: "Maritime Transportation",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: ["4400", "4412"],
      slugs: ["deep-sea-foreign-transportation-of-freight", "water-transportation"]
    }
  },
  "market-research": {
    description: "This industry includes entities that systematically gather, record, tabulate, and present marketing and public opinion data.",
    linkedinId: 97,
    naics: {
      codes: ["54191", "541910"],
      slugs: ["marketing-research-and-public-opinion-polling"]
    },
    name: "Market Research",
    parents: ["professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "marketing-services": {
    description: "This industry includes entities that provide operating advice and assistance to businesses and other organizations on marketing issues, such as developing marketing objectives and policies, sales forecasting, new product development and pricing, licensing and franchise planning, and marketing planning and strategy.",
    linkedinId: 1862,
    naics: {
      codes: [
        "5418",
        "54181",
        "541810",
        "54183",
        "541830",
        "54184",
        "541840",
        "54185",
        "541850",
        "54186",
        "541860",
        "54187",
        "541870",
        "54189",
        "541890"
      ],
      slugs: [
        "advertising-agencies",
        "advertising-material-distribution-services",
        "advertising-public-relations-and-related-services",
        "direct-mail-advertising",
        "indoor-and-outdoor-display-advertising",
        "media-buying-agencies",
        "media-representatives",
        "other-services-related-to-advertising"
      ]
    },
    name: "Marketing Services",
    parents: ["business-consulting-and-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "mattress-and-blinds-manufacturing": {
    description: "This industry includes entities that manufacture furniture related products, such as mattresses, blinds, and shades.",
    linkedinId: 1095,
    naics: {
      codes: ["337910", "337920"],
      slugs: ["blind-and-shade-manufacturing", "mattress-manufacturing"]
    },
    name: "Mattress and Blinds Manufacturing",
    parents: ["furniture-and-home-furnishings-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "measuring-and-control-instrument-manufacturing": {
    description: "This industry includes entities that manufacture navigational, measuring, electromedical, and control instruments. Examples of products made by these entities are aeronautical instruments, appliance regulators and controls (except switches, which are in a separate industry), laboratory analytical instruments, navigation and guidance systems, and physical properties testing equipment.",
    linkedinId: 983,
    naics: {
      codes: [
        "3345",
        "33451",
        "334510",
        "334511",
        "334512",
        "334513",
        "334514",
        "334515",
        "334516",
        "334517",
        "334519"
      ],
      slugs: [
        "analytical-laboratory-instrument-manufacturing",
        "automatic-environmental-control-manufacturing-for-residential-commercial-and-appliance-use",
        "electromedical-and-electrotherapeutic-apparatus-manufacturing",
        "instrument-manufacturing-for-measuring-and-testing-electricity-and-electrical-signals",
        "instruments-and-related-products-manufacturing-for-measuring-displaying-and-controlling-industrial-process-variables",
        "irradiation-apparatus-manufacturing",
        "navigational-measuring-electromedical-and-control-instruments-manufacturing",
        "other-measuring-and-controlling-device-manufacturing",
        "search-detection-navigation-guidance-aeronautical-and-nautical-system-and-instrument-manufacturing",
        "totalizing-fluid-meter-and-counting-device-manufacturing"
      ]
    },
    name: "Measuring and Control Instrument Manufacturing",
    parents: ["computers-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "3821",
        "3823",
        "3824",
        "3825",
        "3826",
        "3829"
      ],
      slugs: [
        "industrial-instruments-for-measurement-display-and-control",
        "instruments-for-meas-testing-of-electricity-elec-signals",
        "laboratory-analytical-instruments",
        "laboratory-apparatus-furniture",
        "measuring-controlling-devices-nec",
        "totalizing-fluid-meters-counting-devices"
      ]
    }
  },
  "meat-products-manufacturing": {
    description: "This industry includes entities that slaughter animals, prepare processed meats and meat byproducts, and render and/or refine animal fat, bones, and meat scraps. Entities that assembly cut and pack meats (such as, boxed meats) from purchased carcasses are included.",
    linkedinId: 521,
    naics: {
      codes: [
        "3116",
        "31161",
        "311611",
        "311612",
        "311613",
        "311615"
      ],
      slugs: [
        "animal-except-poultry-slaughtering",
        "animal-slaughtering-and-processing",
        "meat-processed-from-carcasses",
        "poultry-processing",
        "rendering-and-meat-byproduct-processing"
      ]
    },
    name: "Meat Products Manufacturing",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "2011",
        "2013",
        "2015"
      ],
      slugs: [
        "meat-packing-plants",
        "poultry-slaughtering-and-processing",
        "sausages-other-prepared-meat-products"
      ]
    }
  },
  "media-and-telecommunications": {
    description: "This industry includes entities that provide telecommunications and related services and media production and distribution, including broadcast media, movies, sound recordings, and animation.",
    linkedinId: 3133,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Media and Telecommunications",
    parents: ["technology-information-and-media"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "media-production": {
    description: "This industry includes entities that produce multimedia content. Entities in this industry do not distribute or broadcast this content themselves.",
    linkedinId: 126,
    naics: {
      codes: ["51211", "512110"],
      slugs: ["motion-picture-and-video-production"]
    },
    name: "Media Production",
    parents: [
      "media-and-telecommunications",
      "movies-videos-and-sound",
      "technology-information-and-media"
    ],
    sic: {
      codes: [
        "7812",
        "7819",
        "7822",
        "7829",
        "7830",
        "7841"
      ],
      slugs: [
        "services-allied-to-motion-picture-distribution",
        "services-allied-to-motion-picture-production",
        "services-motion-picture-theaters",
        "services-motion-picture-video-tape-distribution",
        "services-motion-picture-video-tape-production",
        "services-video-tape-rental"
      ]
    }
  },
  "medical-and-diagnostic-laboratories": {
    description: "This industry includes medical and diagnostic laboratories that provide analytic or diagnostic services, including body fluid analysis and diagnostic imaging, generally to the medical profession or to the patient on referral from a health practitioner.",
    linkedinId: 2069,
    naics: {
      codes: [
        "6215",
        "62151",
        "621511",
        "621512"
      ],
      slugs: [
        "diagnostic-imaging-centers",
        "medical-and-diagnostic-laboratories",
        "medical-laboratories"
      ]
    },
    name: "Medical and Diagnostic Laboratories",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: ["8071"],
      slugs: ["services-medical-laboratories"]
    }
  },
  "medical-equipment-manufacturing": {
    description: "This industry includes entities that manufacture medical equipment and supplies. Example products include surgical and medical instruments, surgical appliances and supplies, dental equipment and supplies, orthodontic goods, ophthalmic goods, dentures, and orthodontic appliances.",
    linkedinId: 17,
    naics: {
      codes: [
        "3391",
        "33911",
        "339112",
        "339113",
        "339114",
        "339115",
        "339116"
      ],
      slugs: [
        "dental-equipment-and-supplies-manufacturing",
        "dental-laboratories",
        "medical-equipment-and-supplies-manufacturing",
        "ophthalmic-goods-manufacturing",
        "surgical-and-medical-instrument-manufacturing",
        "surgical-appliance-and-supplies-manufacturing"
      ]
    },
    name: "Medical Equipment Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "3841",
        "3842",
        "3843",
        "3844",
        "3845",
        "3851",
        "5047"
      ],
      slugs: [
        "dental-equipment-supplies",
        "electromedical-electrotherapeutic-apparatus",
        "ophthalmic-goods",
        "orthopedic-prosthetic-surgical-appliances-supplies",
        "surgical-medical-instruments-apparatus",
        "wholesale-medical-dental-hospital-equipment-supplies",
        "x-ray-apparatus-tubes-related-irradiation-apparatus"
      ]
    }
  },
  "medical-practices": {
    description: "This industry includes individual medical practices that provide health care services directly or indirectly to ambulatory patients. This industry does not include hospitals, which are in a separate industry.",
    linkedinId: 13,
    naics: {
      codes: [
        "621",
        "6213",
        "62139",
        "621391",
        "6214",
        "62142",
        "621420",
        "6219",
        "62199",
        "621991",
        "621999"
      ],
      slugs: [
        "all-other-ambulatory-health-care-services",
        "all-other-miscellaneous-ambulatory-health-care-services",
        "ambulatory-health-care-services",
        "blood-and-organ-banks",
        "offices-of-all-other-health-practitioners",
        "offices-of-other-health-practitioners",
        "offices-of-podiatrists",
        "other-ambulatory-health-care-services",
        "outpatient-care-centers",
        "outpatient-mental-health-and-substance-abuse-centers"
      ]
    },
    name: "Medical Practices",
    parents: ["hospitals-and-health-care"],
    sic: {
      codes: ["8011"],
      slugs: ["services-offices-clinics-of-doctors-of-medicine"]
    }
  },
  "mental-health-care": {
    description: "This industry includes independent mental health practitioners (except physicians, which are in a separate industry) that diagnose and treat mental, emotional, and behavioral disorders and/or individual or group social dysfunction brought about by mental illness, alcohol and substance abuse, physical and emotional trauma, or stress. They operate private or group practices in their own offices or in the facilities of others (e.g., hospitals).",
    linkedinId: 139,
    naics: {
      codes: ["62133", "621330"],
      slugs: ["offices-of-mental-health-practitioners-except-physicians"]
    },
    name: "Mental Health Care",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "metal-ore-mining": {
    description: "This industry includes entities that develop mine sites or mine metallic minerals, and those that dress ore or beneficiate (e.g., crush, grind, wash, dry, sinter, concentrate, calcine, and leach).",
    linkedinId: 345,
    naics: {
      codes: [
        "2122",
        "21221",
        "212210",
        "21222",
        "212220",
        "21223",
        "212230",
        "21229",
        "212290"
      ],
      slugs: [
        "copper-nickel-lead-and-zinc-mining",
        "gold-ore-and-silver-ore-mining",
        "iron-ore-mining",
        "metal-ore-mining",
        "other-metal-ore-mining"
      ]
    },
    name: "Metal Ore Mining",
    parents: ["mining", "oil-gas-and-mining"],
    sic: {
      codes: [
        "1000",
        "1040",
        "1090"
      ],
      slugs: [
        "gold-and-silver-ores",
        "metal-mining",
        "miscellaneous-metal-ores"
      ]
    }
  },
  "metal-treatments": {
    description: "This industry includes entities that perform one or more of the following services for metals and metal products: Heat treating; Enameling, lacquering, and varnishing; Hot dip galvanizing; Engraving, chasing, or etching; Powder coating; Electroplating, plating, anodizing, coloring, and finishing; and Other metal surfacing services.",
    linkedinId: 883,
    naics: {
      codes: [
        "3328",
        "33281",
        "332811",
        "332812",
        "332813"
      ],
      slugs: [
        "coating-engraving-heat-treating-and-allied-activities",
        "electroplating-plating-polishing-anodizing-and-coloring",
        "metal-coating-engraving-except-jewelry-and-silverware-and-allied-services-to-manufacturers",
        "metal-heat-treating"
      ]
    },
    name: "Metal Treatments",
    parents: ["fabricated-metal-products", "manufacturing"],
    sic: {
      codes: ["3470"],
      slugs: ["coating-engraving-allied-services"]
    }
  },
  "metal-valve-ball-and-roller-manufacturing": {
    description: "This industry includes entities that manufacture fabricated metal products like valves, balls, and rollers (except forgings and stampings, cutlery and handtools, architectural and structural metals, boilers, tanks, shipping containers, hardware, spring and wire products, machine shop products, turned products, screws, and nuts and bolts, which are in separate industries).",
    linkedinId: 887,
    naics: {
      codes: [
        "3329",
        "33291",
        "332911",
        "332912",
        "332913",
        "332919",
        "33299",
        "332991",
        "332992",
        "332993",
        "332994",
        "332996",
        "332999"
      ],
      slugs: [
        "all-other-fabricated-metal-product-manufacturing",
        "all-other-miscellaneous-fabricated-metal-product-manufacturing",
        "ammunition-except-small-arms-manufacturing",
        "ball-and-roller-bearing-manufacturing",
        "fabricated-pipe-and-pipe-fitting-manufacturing",
        "fluid-power-valve-and-hose-fitting-manufacturing",
        "industrial-valve-manufacturing",
        "metal-valve-manufacturing",
        "other-fabricated-metal-product-manufacturing",
        "other-metal-valve-and-pipe-fitting-manufacturing",
        "plumbing-fixture-fitting-and-trim-manufacturing",
        "small-arms-ammunition-manufacturing",
        "small-arms-ordnance-and-ordnance-accessories-manufacturing"
      ]
    },
    name: "Metal Valve, Ball, and Roller Manufacturing",
    parents: ["fabricated-metal-products", "manufacturing"],
    sic: {
      codes: ["3562"],
      slugs: ["ball-roller-bearings"]
    }
  },
  "metalworking-machinery-manufacturing": {
    description: "This industry includes entities that manufacture metalworking machinery, such as metal cutting and metal forming machine tools; cutting tools; accessories for metalworking machinery; special dies, tools, jigs, and fixtures; industrial molds; rolling mill machinery; assembly machinery; coil handling, conversion, or straightening equipment; and wire drawing and fabricating machines.",
    linkedinId: 928,
    naics: {
      codes: [
        "3335",
        "33351",
        "333511",
        "333514",
        "333515",
        "333517",
        "333519"
      ],
      slugs: [
        "cutting-tool-and-machine-tool-accessory-manufacturing",
        "industrial-mold-manufacturing",
        "machine-tool-manufacturing",
        "metalworking-machinery-manufacturing",
        "rolling-mill-and-other-metalworking-machinery-manufacturing",
        "special-die-and-tool-die-set-jig-and-fixture-manufacturing"
      ]
    },
    name: "Metalworking Machinery Manufacturing",
    parents: ["machinery-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "3460",
        "3540",
        "3541"
      ],
      slugs: [
        "machine-tools-metal-cutting-types",
        "metal-forgings-stampings",
        "metalworking-machinery-equipment"
      ]
    }
  },
  "military-and-international-affairs": {
    description: "This industry includes government entities that administer programs of national security and international affairs.",
    linkedinId: 2391,
    naics: {
      codes: ["928", "9281"],
      slugs: ["national-security-and-international-affairs"]
    },
    name: "Military and International Affairs",
    parents: ["government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "mining": {
    description: "This industry includes entities that mine, develop mine sites, and prepare metallic minerals and nonmetallic minerals, including coal.",
    linkedinId: 56,
    naics: {
      codes: ["212"],
      slugs: ["mining-except-oil-and-gas"]
    },
    name: "Mining",
    parents: ["oil-gas-and-mining"],
    sic: {
      codes: ["6795"],
      slugs: ["mineral-royalty-traders"]
    }
  },
  "mobile-computing-software-products": {
    description: "This industry includes entities that produce software which runs on mobile computing devices such as smartphones and tablet computers.",
    linkedinId: 3100,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Mobile Computing Software Products",
    parents: [
      "software-development",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "mobile-food-services": {
    description: "This industry includes entities that prepare and serve meals and snacks for immediate consumption from motorized vehicles or nonmotorized carts. The entity is the central location from which the caterer route is serviced, not each vehicle or cart. Included in this industry are entities that provide food services from vehicles, such as hot dog carts and ice cream trucks.",
    linkedinId: 2214,
    naics: {
      codes: ["72233", "722330"],
      slugs: ["mobile-food-services"]
    },
    name: "Mobile Food Services",
    parents: ["accommodation-services", "food-and-beverage-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "mobile-gaming-apps": {
    description: "This industry includes entities that produce user-facing software for gaming on mobile platforms.",
    linkedinId: 3131,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Mobile Gaming Apps",
    parents: [
      "computer-games",
      "software-development",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "motor-vehicle-manufacturing": {
    description: "This industry includes entities that manufacture complete automobiles, light duty motor vehicles, and heavy duty trucks (such as, body and chassis or unibody) or manufacture motor vehicle chassis only.",
    linkedinId: 53,
    naics: {
      codes: [
        "3361",
        "33611",
        "336110",
        "33612",
        "336120"
      ],
      slugs: [
        "automobile-and-light-duty-motor-vehicle-manufacturing",
        "heavy-duty-truck-manufacturing",
        "motor-vehicle-manufacturing"
      ]
    },
    name: "Motor Vehicle Manufacturing",
    parents: ["manufacturing", "transportation-equipment-manufacturing"],
    sic: {
      codes: [
        "3711",
        "3713",
        "3716",
        "3751"
      ],
      slugs: [
        "motor-homes",
        "motor-vehicles-passenger-car-bodies",
        "motorcycles-bicycles-parts",
        "truck-bus-bodies"
      ]
    }
  },
  "motor-vehicle-parts-manufacturing": {
    description: "This industry includes entities that manufacture motor vehicle gasoline engines and engine parts, motor vehicle electrical and electronic equipment, motor vehicle steering and suspension components (except springs), motor vehicle brake systems, motor vehicle transmission and power train parts, motor vehicle seating and interior trim, motor vehicle metal stampings, and other motor vehicle parts and accessories.",
    linkedinId: 1042,
    naics: {
      codes: [
        "3363",
        "33631",
        "336310",
        "33632",
        "336320",
        "33633",
        "336330",
        "33634",
        "336340",
        "33635",
        "336350",
        "33636",
        "336360",
        "33637",
        "336370",
        "33639",
        "336390"
      ],
      slugs: [
        "motor-vehicle-brake-system-manufacturing",
        "motor-vehicle-electrical-and-electronic-equipment-manufacturing",
        "motor-vehicle-gasoline-engine-and-engine-parts-manufacturing",
        "motor-vehicle-metal-stamping",
        "motor-vehicle-parts-manufacturing",
        "motor-vehicle-seating-and-interior-trim-manufacturing",
        "motor-vehicle-steering-and-suspension-components-except-spring-manufacturing",
        "motor-vehicle-transmission-and-power-train-parts-manufacturing",
        "other-motor-vehicle-parts-manufacturing"
      ]
    },
    name: "Motor Vehicle Parts Manufacturing",
    parents: ["manufacturing", "transportation-equipment-manufacturing"],
    sic: {
      codes: ["3714", "3715"],
      slugs: ["motor-vehicle-parts-accessories", "truck-trailers"]
    }
  },
  "movies-and-sound-recording": {
    description: "This industry includes entities that primarily produce and distribute movies and sound recordings.",
    linkedinId: 1611,
    naics: {
      codes: [
        "5121",
        "51212",
        "512120",
        "51213",
        "512131",
        "512132"
      ],
      slugs: [
        "drive-in-motion-picture-theaters",
        "motion-picture-and-video-distribution",
        "motion-picture-and-video-exhibition",
        "motion-picture-and-video-industries",
        "motion-picture-theaters-except-drive-ins"
      ]
    },
    name: "Movies and Sound Recording",
    parents: [
      "media-and-telecommunications",
      "movies-videos-and-sound",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "movies-videos-and-sound": {
    description: "This industry includes entities that primarily produce and distribute movies, videos and sound recordings.",
    linkedinId: 35,
    naics: {
      codes: ["512"],
      slugs: ["motion-picture-and-sound-recording-industries"]
    },
    name: "Movies, Videos and Sound",
    parents: ["media-and-telecommunications", "technology-information-and-media"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "museums": {
    description: "This industry includes entities that preserve and exhibit objects of historical, cultural, and/or educational value.",
    linkedinId: 2159,
    naics: {
      codes: ["71211", "712110"],
      slugs: ["museums"]
    },
    name: "Museums",
    parents: ["entertainment-providers", "museums-historical-sites-and-zoos"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "museums-historical-sites-and-zoos": {
    description: "This industry includes entities that preserve and exhibit objects, sites, and natural wonders of historical, cultural, and/or educational value.",
    linkedinId: 37,
    naics: {
      codes: [
        "712",
        "7121",
        "71219",
        "712190"
      ],
      slugs: ["museums-historical-sites-and-similar-institutions", "nature-parks-and-other-similar-institutions"]
    },
    name: "Museums, Historical Sites, and Zoos",
    parents: ["entertainment-providers"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "musicians": {
    description: "This industry includes groups that produce live musical entertainment (except theatrical musical or opera productions, which are in separate industries) and independent (such as, freelance) artists that provide live musical entertainment. Musical groups and artists may perform in front of a live audience or in a studio; they may or may not operate their own facilities for staging shows.",
    linkedinId: 115,
    naics: {
      codes: ["71113", "711130"],
      slugs: ["musical-groups-and-artists"]
    },
    name: "Musicians",
    parents: ["entertainment-providers"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "nanotechnology-research": {
    description: "This industry includes entities that conduct nanotechnology research and experimental development. Research may result in new nanotechnology processes and/or products that may be reproduced, utilized, or implemented by various industries.",
    linkedinId: 114,
    naics: {
      codes: ["541713"],
      slugs: ["research-and-development-in-nanotechnology"]
    },
    name: "Nanotechnology Research",
    parents: ["professional-services", "research-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "natural-gas-distribution": {
    description: "This industry includes entities that: operate gas distribution systems (e.g., mains, meters); buy gas from the well and sell it to a distribution system (such as gas marketers); arrange the sale of gas over gas distribution systems operated by others (such as gas brokers or agents); and transmit and distribute gas to final consumers.",
    linkedinId: 397,
    naics: {
      codes: [
        "2212",
        "22121",
        "221210"
      ],
      slugs: ["natural-gas-distribution"]
    },
    name: "Natural Gas Distribution",
    parents: ["utilities"],
    sic: {
      codes: [
        "4922",
        "4923",
        "4924"
      ],
      slugs: [
        "natural-gas-distribution",
        "natural-gas-transmission",
        "natural-gas-transmission-distribution"
      ]
    }
  },
  "natural-gas-extraction": {
    description: "This industry includes entities that focus on the exploration, development, and/or the production of natural gas from wells in which the hydrocarbons will initially flow or can be produced using normal or enhanced drilling and extraction techniques or the recovery of liquid hydrocarbons from oil and gas field gases.",
    linkedinId: 3096,
    naics: {
      codes: ["21113", "211130"],
      slugs: ["natural-gas-extraction"]
    },
    name: "Natural Gas Extraction",
    parents: ["oil-and-gas", "oil-gas-and-mining"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "newspaper-publishing": {
    description: "This industry includes entities known as newspaper publishers. Entities in this industry produce and distribute newspapers (such as, gather news; write news columns, feature stories, and editorials; and sell and prepare advertisements). These entities may publish newspapers in print or electronic form.",
    linkedinId: 81,
    naics: {
      codes: ["51311", "513110"],
      slugs: ["newspaper-publishers"]
    },
    name: "Newspaper Publishing",
    parents: [
      "book-and-periodical-publishing",
      "media-and-telecommunications",
      "technology-information-and-media"
    ],
    sic: {
      codes: ["2711"],
      slugs: ["newspapers-publishing-or-publishing-printing"]
    }
  },
  "non-profit-organizations": {
    description: "This industry includes entities that provide public benefit on a nonprofit basis, including those that organize and promote religious activities, support social and political causes, and provide programs and facilities for emergency relief, education, and donations of goods and services.",
    linkedinId: 100,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Non-profit Organizations",
    parents: ["consumer-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "nonmetallic-mineral-mining": {
    description: "This industry includes entities that develop mine sites, or mine or quarry nonmetallic minerals (except fuels, which are in a separate industry). Included are certain well and brine operations, and preparation plants that beneficiate (e.g., crush, grind, wash, and concentrate) nonmetallic minerals.",
    linkedinId: 356,
    naics: {
      codes: [
        "2123",
        "21231",
        "212311",
        "212312",
        "212313",
        "212319",
        "21232",
        "212321",
        "212322",
        "212323",
        "21239",
        "212390"
      ],
      slugs: [
        "construction-sand-and-gravel-mining",
        "crushed-and-broken-granite-mining-and-quarrying",
        "crushed-and-broken-limestone-mining-and-quarrying",
        "dimension-stone-mining-and-quarrying",
        "industrial-sand-mining",
        "kaolin-clay-and-ceramic-and-refractory-minerals-mining",
        "nonmetallic-mineral-mining-and-quarrying",
        "other-crushed-and-broken-stone-mining-and-quarrying",
        "other-nonmetallic-mineral-mining-and-quarrying",
        "sand-gravel-clay-and-ceramic-and-refractory-minerals-mining-and-quarrying",
        "stone-mining-and-quarrying"
      ]
    },
    name: "Nonmetallic Mineral Mining",
    parents: ["mining", "oil-gas-and-mining"],
    sic: {
      codes: ["1400"],
      slugs: ["mining-quarrying-of-nonmetallic-minerals-no-fuels"]
    }
  },
  "nonresidential-building-construction": {
    description: "This industry includes entities that construct nonresidential buildings (including new work, additions, alterations, maintenance, and repairs). This includes the work of nonresidential general contractors, nonresidential for-sale builders, nonresidential design-build firms, and nonresidential project construction management firms.",
    linkedinId: 413,
    naics: {
      codes: [
        "2362",
        "23621",
        "236210",
        "23622",
        "236220"
      ],
      slugs: [
        "commercial-and-institutional-building-construction",
        "industrial-building-construction",
        "nonresidential-building-construction"
      ]
    },
    name: "Nonresidential Building Construction",
    parents: ["building-construction", "construction"],
    sic: {
      codes: ["1540"],
      slugs: ["general-bldg-contractors-nonresidential-bldgs"]
    }
  },
  "nuclear-electric-power-generation": {
    description: "This industry includes entities that operate nuclear electric power generation facilities. These facilities use nuclear power to produce electric energy provided to transmission or electric power distribution systems.",
    linkedinId: 386,
    naics: {
      codes: ["221113"],
      slugs: ["nuclear-electric-power-generation"]
    },
    name: "Nuclear Electric Power Generation",
    parents: ["electric-power-generation", "utilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "nursing-homes-and-residential-care-facilities": {
    description: "This industry includes entities that provide residential care combined with either nursing, supervisory, or other types of care as required by the residents.",
    linkedinId: 2091,
    naics: {
      codes: [
        "623",
        "6231",
        "62311",
        "623110",
        "6232",
        "62321",
        "623210",
        "62322",
        "623220",
        "6233",
        "62331",
        "623311",
        "623312",
        "6239",
        "62399",
        "623990"
      ],
      slugs: [
        "assisted-living-facilities-for-the-elderly",
        "continuing-care-retirement-communities",
        "continuing-care-retirement-communities-and-assisted-living-facilities-for-the-elderly",
        "nursing-and-residential-care-facilities",
        "nursing-care-facilities-skilled-nursing-facilities",
        "other-residential-care-facilities",
        "residential-intellectual-and-developmental-disability-facilities",
        "residential-intellectual-and-developmental-disability-mental-health-and-substance-abuse-facilities",
        "residential-mental-health-and-substance-abuse-facilities"
      ]
    },
    name: "Nursing Homes and Residential Care Facilities",
    parents: ["hospitals-and-health-care"],
    sic: {
      codes: ["8050", "8051"],
      slugs: ["services-nursing-personal-care-facilities", "services-skilled-nursing-care-facilities"]
    }
  },
  "office-administration": {
    description: "This industry includes entities that provide a range of day-to-day office administrative services, such as financial planning; billing and recordkeeping; personnel; and physical distribution and logistics, for others on a contract or fee basis.",
    linkedinId: 1916,
    naics: {
      codes: [
        "5611",
        "56111",
        "561110"
      ],
      slugs: ["office-administrative-services"]
    },
    name: "Office Administration",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "office-furniture-and-fixtures-manufacturing": {
    description: "This industry includes entities that manufacture office furniture and/or office and store fixtures on a stock or custom basis that may be assembled or unassembled (such as, knockdown).",
    linkedinId: 1090,
    naics: {
      codes: [
        "3372",
        "33721",
        "337211",
        "337214",
        "337215"
      ],
      slugs: [
        "office-furniture-except-wood-manufacturing",
        "office-furniture-including-fixtures-manufacturing",
        "showcase-partition-shelving-and-locker-manufacturing",
        "wood-office-furniture-manufacturing"
      ]
    },
    name: "Office Furniture and Fixtures Manufacturing",
    parents: ["furniture-and-home-furnishings-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "2520",
        "2522",
        "2540"
      ],
      slugs: [
        "office-furniture",
        "office-furniture-no-wood",
        "partitions-shelvg-lockers-office-store-fixtures"
      ]
    }
  },
  "oil-and-coal-product-manufacturing": {
    description: "This industry includes entities that transform crude petroleum and coal into usable products, as well as entities that further process refined petroleum and coal products to produce products such as asphalt coatings and petroleum lubricating oils.",
    linkedinId: 679,
    naics: {
      codes: [
        "324",
        "3241",
        "32411",
        "324110",
        "32412",
        "324121",
        "324122",
        "32419",
        "324191",
        "324199"
      ],
      slugs: [
        "all-other-petroleum-and-coal-products-manufacturing",
        "asphalt-paving-mixture-and-block-manufacturing",
        "asphalt-paving-roofing-and-saturated-materials-manufacturing",
        "asphalt-shingle-and-coating-materials-manufacturing",
        "other-petroleum-and-coal-products-manufacturing",
        "petroleum-and-coal-products-manufacturing",
        "petroleum-lubricating-oil-and-grease-manufacturing",
        "petroleum-refineries"
      ]
    },
    name: "Oil and Coal Product Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "2911",
        "2950",
        "2990"
      ],
      slugs: [
        "asphalt-paving-roofing-materials",
        "miscellaneous-products-of-petroleum-coal",
        "petroleum-refining"
      ]
    }
  },
  "oil-and-gas": {
    description: "This industry includes entities that operate and/or develop oil and gas field properties. Crude petroleum production, extraction of oil from oil shale and oil sands, production of natural gas, sulfur recovery from natural gas, and recovery of hydrocarbon liquids are included.",
    linkedinId: 57,
    naics: {
      codes: ["211", "2111"],
      slugs: ["oil-and-gas-extraction"]
    },
    name: "Oil and Gas",
    parents: ["oil-gas-and-mining"],
    sic: {
      codes: [
        "1311",
        "1381",
        "1382",
        "1389",
        "6792"
      ],
      slugs: [
        "crude-petroleum-natural-gas",
        "drilling-oil-gas-wells",
        "oil-gas-field-exploration-services",
        "oil-gas-field-services-nec",
        "oil-royalty-traders"
      ]
    }
  },
  "oil-extraction": {
    description: "This industry includes entities that focus on the exploration, development, and/or the production of petroleum from wells in which the hydrocarbons will initially flow or can be produced using normal or enhanced drilling and extraction techniques or on the production of crude petroleum from surface shales or tar sands or from reservoirs in which the hydrocarbons are semisolids.",
    linkedinId: 3095,
    naics: {
      codes: ["21112", "211120"],
      slugs: ["crude-petroleum-extraction"]
    },
    name: "Oil Extraction",
    parents: ["oil-and-gas", "oil-gas-and-mining"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "oil-gas-and-mining": {
    description: "This industry includes entities that extract naturally occurring mineral solids, such as coal and ores; liquid minerals, such as crude petroleum; and gases, such as natural gas. Included are entities that provide quarrying, well operations, and other preparation customarily performed as a part of mining activity.",
    isParent: true,
    linkedinId: 332,
    naics: {
      codes: [
        "21",
        "213",
        "2131",
        "21311",
        "213111",
        "213112",
        "213113",
        "213114",
        "213115"
      ],
      slugs: [
        "drilling-oil-and-gas-wells",
        "mining-quarrying-and-oil-and-gas-extraction",
        "support-activities-for-coal-mining",
        "support-activities-for-metal-mining",
        "support-activities-for-mining",
        "support-activities-for-nonmetallic-minerals-except-fuels-mining",
        "support-activities-for-oil-and-gas-operations"
      ]
    },
    name: "Oil, Gas, and Mining",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "online-and-mail-order-retail": {
    description: "This industry includes entities that create and operate online-only and/or mail-order based marketplaces for the sale of goods. This industry includes entities that retail all types of merchandise using non-store means, such as catalogs, toll free telephone numbers, interactive television, or the Internet.",
    linkedinId: 1445,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Online and Mail Order Retail",
    parents: ["retail"],
    sic: {
      codes: ["5960", "5961"],
      slugs: ["retail-catalog-mail-order-houses", "retail-nonstore-retailers"]
    }
  },
  "online-audio-and-video-media": {
    description: "This industry includes entities that publish and/or broadcast content on the Internet exclusively. These entities publish in audio and video content primarily, and do not publish news, business content, blogs or vlogs.",
    linkedinId: 113,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Online Audio and Video Media",
    parents: [
      "information-services",
      "internet-publishing",
      "technology-information-and-internet",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "operations-consulting": {
    description: "This industry includes entities that provide operating advice and assistance to businesses and other organizations in: manufacturing operations improvement; productivity improvement; production planning and control; quality assurance and quality control; inventory management; distribution networks; warehouse use, operations, and utilization; transportation and shipment of goods and materials; and materials management and handling.",
    linkedinId: 2401,
    naics: {
      codes: ["541614"],
      slugs: ["process-physical-distribution-and-logistics-consulting-services"]
    },
    name: "Operations Consulting",
    parents: ["business-consulting-and-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "optometrists": {
    description: "This industry includes health practitioners having the degree of O.D. (Doctor of Optometry) practicing optometry.",
    linkedinId: 2050,
    naics: {
      codes: ["62132", "621320"],
      slugs: ["offices-of-optometrists"]
    },
    name: "Optometrists",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "outpatient-care-centers": {
    description: "This industry includes entities with medical staff that provide general or specialized outpatient care (except family planning centers and outpatient mental health and substance abuse centers, which are in separate industries).",
    linkedinId: 2063,
    naics: {
      codes: [
        "62149",
        "621491",
        "621492",
        "621493",
        "621498"
      ],
      slugs: [
        "all-other-outpatient-care-centers",
        "freestanding-ambulatory-surgical-and-emergency-centers",
        "hmo-medical-centers",
        "kidney-dialysis-centers",
        "other-outpatient-care-centers"
      ]
    },
    name: "Outpatient Care Centers",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: ["8093"],
      slugs: ["services-specialty-outpatient-facilities-nec"]
    }
  },
  "outsourcing-and-offshoring-consulting": {
    description: "This industry includes entities that provide operating advice on the practice of outsourcing and/or offshoring.",
    linkedinId: 123,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Outsourcing and Offshoring Consulting",
    parents: ["business-consulting-and-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "packaging-and-containers-manufacturing": {
    description: "This industry includes entities that convert plastics resins into plastics packaging (flexible) film and packaging sheet.",
    linkedinId: 146,
    naics: {
      codes: ["326112"],
      slugs: ["plastics-packaging-film-and-sheet-including-laminated-manufacturing"]
    },
    name: "Packaging and Containers Manufacturing",
    parents: ["manufacturing", "plastics-and-rubber-product-manufacturing"],
    sic: {
      codes: [
        "2650",
        "2670",
        "2673"
      ],
      slugs: [
        "converted-paper-paperboard-prods-no-containers-boxes",
        "paperboard-containers-boxes",
        "plastics-foil-coated-paper-bags"
      ]
    }
  },
  "paint-coating-and-adhesive-manufacturing": {
    description: "This industry includes entities that mix pigments, solvents, and binders into paints and other coatings, as well as those that manufacture allied paint products, adhesives, glues, and caulking compounds.",
    linkedinId: 722,
    naics: {
      codes: [
        "3255",
        "32551",
        "325510",
        "32552",
        "325520"
      ],
      slugs: [
        "adhesive-manufacturing",
        "paint-and-coating-manufacturing",
        "paint-coating-and-adhesive-manufacturing"
      ]
    },
    name: "Paint, Coating, and Adhesive Manufacturing",
    parents: ["chemical-manufacturing", "manufacturing"],
    sic: {
      codes: ["2891"],
      slugs: ["adhesives-sealants"]
    }
  },
  "paper-and-forest-product-manufacturing": {
    description: "This industry includes entities that make pulp, paper, or converted paper products.",
    linkedinId: 61,
    naics: {
      codes: [
        "322",
        "3221",
        "32211",
        "322110",
        "32212",
        "322120",
        "32213",
        "322130",
        "3222",
        "32221",
        "322211",
        "322212",
        "322219",
        "32222",
        "322220",
        "32223",
        "322230",
        "32229",
        "322291",
        "322299"
      ],
      slugs: [
        "all-other-converted-paper-product-manufacturing",
        "converted-paper-product-manufacturing",
        "corrugated-and-solid-fiber-box-manufacturing",
        "folding-paperboard-box-manufacturing",
        "other-converted-paper-product-manufacturing",
        "other-paperboard-container-manufacturing",
        "paper-bag-and-coated-and-treated-paper-manufacturing",
        "paper-manufacturing",
        "paper-mills",
        "paperboard-container-manufacturing",
        "paperboard-mills",
        "pulp-mills",
        "pulp-paper-and-paperboard-mills",
        "sanitary-paper-product-manufacturing",
        "stationery-product-manufacturing"
      ]
    },
    name: "Paper and Forest Product Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "2600",
        "2611",
        "2621",
        "2631"
      ],
      slugs: [
        "paper-mills",
        "paperboard-mills",
        "papers-allied-products",
        "pulp-mills"
      ]
    }
  },
  "pension-funds": {
    description: "This industry includes legal entities (such as, funds, plans, and/or programs) organized to provide retirement income benefits exclusively for the sponsor's employees or members.",
    linkedinId: 1745,
    naics: {
      codes: ["52511", "525110"],
      slugs: ["pension-funds"]
    },
    name: "Pension Funds",
    parents: ["financial-services", "funds-and-trusts"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "performing-arts": {
    description: "This industry includes entities that produce live presentations featuring performances of actors and actresses, singers, dancers, musicians and musical groups, and other performing artists.",
    linkedinId: 39,
    naics: {
      codes: ["7111"],
      slugs: ["performing-arts-companies"]
    },
    name: "Performing Arts",
    parents: ["entertainment-providers", "performing-arts-and-spectator-sports"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "performing-arts-and-spectator-sports": {
    description: "This industry includes entities that produce or organize and promote live presentations involving the performances of actors and actresses, singers, dancers, musical groups and artists, athletes, and other entertainers, including independent (such as, freelance) entertainers and the entities that manage their careers.",
    linkedinId: 2130,
    naics: {
      codes: [
        "711",
        "7113",
        "71131",
        "711310",
        "71132",
        "711320",
        "7114",
        "71141",
        "711410"
      ],
      slugs: [
        "agents-and-managers-for-artists-athletes-entertainers-and-other-public-figures",
        "performing-arts-spectator-sports-and-related-industries",
        "promoters-of-performing-arts-sports-and-similar-events",
        "promoters-of-performing-arts-sports-and-similar-events-with-facilities",
        "promoters-of-performing-arts-sports-and-similar-events-without-facilities"
      ]
    },
    name: "Performing Arts and Spectator Sports",
    parents: ["entertainment-providers"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "periodical-publishing": {
    description: "This industry includes entities known either as magazine or periodical publishers that carry out the operations necessary for producing and distributing magazines and other periodicals, such as gathering, writing, and editing articles, and selling and preparing advertisements and may publish magazines and other periodicals in print or electronic form.",
    linkedinId: 1600,
    naics: {
      codes: ["51312", "513120"],
      slugs: ["periodical-publishers"]
    },
    name: "Periodical Publishing",
    parents: [
      "book-and-periodical-publishing",
      "media-and-telecommunications",
      "technology-information-and-media"
    ],
    sic: {
      codes: ["2721"],
      slugs: ["periodicals-publishing-or-publishing-printing"]
    }
  },
  "personal-and-laundry-services": {
    description: "This industry includes entities that provide personal and laundry services to individuals, households, and businesses, including personal care services; death care services; laundry and drycleaning services; and a wide range of other personal services, such as pet care (except veterinary) services, photofinishing services, temporary parking services, and dating services.",
    linkedinId: 2258,
    naics: {
      codes: [
        "812",
        "8122",
        "81221",
        "812210",
        "81222",
        "812220",
        "8129",
        "81292",
        "812921",
        "812922",
        "81293",
        "812930",
        "81299",
        "812990"
      ],
      slugs: [
        "all-other-personal-services",
        "cemeteries-and-crematories",
        "death-care-services",
        "funeral-homes-and-funeral-services",
        "one-hour-photofinishing",
        "other-personal-services",
        "parking-lots-and-garages",
        "personal-and-laundry-services",
        "photofinishing",
        "photofinishing-laboratories-except-one-hour"
      ]
    },
    name: "Personal and Laundry Services",
    parents: ["consumer-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "personal-care-product-manufacturing": {
    description: "This industry includes entities that prepare, blend, compound, and package toiletry preparations, such as perfumes, shaving preparations, hair preparations, face creams, lotions (including sunscreens), and other cosmetic preparations.",
    linkedinId: 18,
    naics: {
      codes: ["32562", "325620"],
      slugs: ["toilet-preparation-manufacturing"]
    },
    name: "Personal Care Product Manufacturing",
    parents: ["chemical-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "personal-care-services": {
    description: "This industry includes entities, such as barber and beauty shops, that provide appearance care services to individual consumers.",
    linkedinId: 2259,
    naics: {
      codes: [
        "8121",
        "81211",
        "812111",
        "812112",
        "812113",
        "81219",
        "812191",
        "812199"
      ],
      slugs: [
        "barber-shops",
        "beauty-salons",
        "diet-and-weight-reducing-centers",
        "hair-nail-and-skin-care-services",
        "nail-salons",
        "other-personal-care-services",
        "personal-care-services"
      ]
    },
    name: "Personal Care Services",
    parents: ["consumer-services", "personal-and-laundry-services"],
    sic: {
      codes: ["7200"],
      slugs: ["services-personal-services"]
    }
  },
  "pet-services": {
    description: "This industry includes entities that provide pet care services (except veterinary, which is in a separate industry), such as boarding, grooming, sitting, and training pets.",
    linkedinId: 2282,
    naics: {
      codes: ["81291", "812910"],
      slugs: ["pet-care-except-veterinary-services"]
    },
    name: "Pet Services",
    parents: ["consumer-services", "personal-and-laundry-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "pharmaceutical-manufacturing": {
    description: "This industry includes entities that: Manufacture biological and medicinal products; Process botanical drugs and herbs; Isolate active medicinal principals from botanical drugs and herbs; and/or Manufacture pharmaceutical products intended for internal and external consumption in such forms as ampoules, tablets, capsules, vials, ointments, powders, solutions, and suspensions.",
    linkedinId: 15,
    naics: {
      codes: [
        "3254",
        "32541",
        "325411",
        "325412",
        "325413",
        "325414"
      ],
      slugs: [
        "biological-product-except-diagnostic-manufacturing",
        "in-vitro-diagnostic-substance-manufacturing",
        "medicinal-and-botanical-manufacturing",
        "pharmaceutical-and-medicine-manufacturing",
        "pharmaceutical-preparation-manufacturing"
      ]
    },
    name: "Pharmaceutical Manufacturing",
    parents: ["chemical-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "2833",
        "2834",
        "2835",
        "2836"
      ],
      slugs: [
        "biological-products-no-diagnostic-substances",
        "in-vitro-in-vivo-diagnostic-substances",
        "medicinal-chemicals-botanical-products",
        "pharmaceutical-preparations"
      ]
    }
  },
  "philanthropic-fundraising-services": {
    description: "This industry includes grantmaking foundations, charitable trusts, and entities that raise funds for a wide range of social welfare activities, such as health, educational, scientific, and cultural activities.",
    linkedinId: 131,
    naics: {
      codes: [
        "8132",
        "81321",
        "813211",
        "813212",
        "813219"
      ],
      slugs: [
        "grantmaking-and-giving-services",
        "grantmaking-foundations",
        "other-grantmaking-and-giving-services",
        "voluntary-health-organizations"
      ]
    },
    name: "Philanthropic Fundraising Services",
    parents: ["consumer-services", "philanthropic-fundraising-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "photography": {
    description: "This industry includes entities that provide still, video, or digital photography services. They may specialize in a particular field of photography, such as commercial and industrial photography, portrait photography, and special events photography. Commercial or portrait photography studios are included.",
    linkedinId: 136,
    naics: {
      codes: [
        "54192",
        "541921",
        "541922"
      ],
      slugs: [
        "commercial-photography",
        "photographic-services",
        "photography-studios-portrait"
      ]
    },
    name: "Photography",
    parents: ["professional-services"],
    sic: {
      codes: [
        "3861",
        "7330",
        "7384"
      ],
      slugs: [
        "photographic-equipment-supplies",
        "services-mailing-reproduction-commercial-art-photography",
        "services-photofinishing-laboratories"
      ]
    }
  },
  "physical-occupational-and-speech-therapists": {
    description: "This industry includes independent health practitioners who provide physical therapy services to patients who require prevention, wellness or fitness services; plan and administer educational, recreational, and social activities designed to help patients or individuals with disabilities regain physical or mental functioning or adapt to their disabilities; or diagnose and treat speech, language, or hearing problems.",
    linkedinId: 2054,
    naics: {
      codes: ["62134", "621340"],
      slugs: ["offices-of-physical-occupational-and-speech-therapists-and-audiologists"]
    },
    name: "Physical, Occupational and Speech Therapists",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "physicians": {
    description: "This industry includes health practitioners having the degree of M.D. (Doctor of Medicine) or D.O. (Doctor of Osteopathy) practicing general or specialized medicine (e.g., anesthesiology, oncology, ophthalmology, psychiatry) or surgery. This category does not include clinics or hospitals.",
    linkedinId: 2040,
    naics: {
      codes: [
        "6211",
        "62111",
        "621111",
        "621112"
      ],
      slugs: [
        "offices-of-physicians",
        "offices-of-physicians-except-mental-health-specialists",
        "offices-of-physicians-mental-health-specialists"
      ]
    },
    name: "Physicians",
    parents: ["hospitals-and-health-care", "medical-practices"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "pipeline-transportation": {
    description: "This industry includes entities that primarily use transmission pipelines to transport products, such as crude oil, natural gas, refined petroleum products, and slurry, which may include the storage of natural gas.",
    linkedinId: 1520,
    naics: {
      codes: [
        "486",
        "4861",
        "48611",
        "486110",
        "4862",
        "48621",
        "486210",
        "4869",
        "48691",
        "486910",
        "48699",
        "486990"
      ],
      slugs: [
        "all-other-pipeline-transportation",
        "other-pipeline-transportation",
        "pipeline-transportation",
        "pipeline-transportation-of-crude-oil",
        "pipeline-transportation-of-natural-gas",
        "pipeline-transportation-of-refined-petroleum-products"
      ]
    },
    name: "Pipeline Transportation",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: ["4610"],
      slugs: ["pipe-lines-no-natural-gas"]
    }
  },
  "plastics-and-rubber-product-manufacturing": {
    description: "This industry includes entities that make goods by processing plastics materials and raw rubber, generally restricted to products made solely of plastics or rubber.",
    linkedinId: 743,
    naics: {
      codes: ["326"],
      slugs: ["plastics-and-rubber-products-manufacturing"]
    },
    name: "Plastics and Rubber Product Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: ["2820", "2821"],
      slugs: ["plastic-material-synth-resin-rubber-cellulos-no-glass", "plastic-materials-synth-resins-nonvulcan-elastomers"]
    }
  },
  "plastics-manufacturing": {
    description: "This industry includes entities that process new or spent (such as, recycled) plastics resins into intermediate or final products, using such processes as compression molding, extrusion molding, injection molding, blow molding, and casting.",
    linkedinId: 117,
    naics: {
      codes: [
        "3261",
        "32611",
        "326111",
        "326113",
        "32612",
        "326121",
        "326122",
        "32613",
        "326130",
        "32614",
        "326140",
        "32615",
        "326150",
        "32616",
        "326160",
        "32619",
        "326191",
        "326199"
      ],
      slugs: [
        "all-other-plastics-product-manufacturing",
        "laminated-plastics-plate-sheet-except-packaging-and-shape-manufacturing",
        "other-plastics-product-manufacturing",
        "plastics-bag-and-pouch-manufacturing",
        "plastics-bottle-manufacturing",
        "plastics-packaging-materials-and-unlaminated-film-and-sheet-manufacturing",
        "plastics-pipe-and-pipe-fitting-manufacturing",
        "plastics-pipe-pipe-fitting-and-unlaminated-profile-shape-manufacturing",
        "plastics-plumbing-fixture-manufacturing",
        "plastics-product-manufacturing",
        "polystyrene-foam-product-manufacturing",
        "unlaminated-plastics-film-and-sheet-except-packaging-manufacturing",
        "unlaminated-plastics-profile-shape-manufacturing",
        "urethane-and-other-foam-product-except-polystyrene-manufacturing"
      ]
    },
    name: "Plastics Manufacturing",
    parents: ["manufacturing", "plastics-and-rubber-product-manufacturing"],
    sic: {
      codes: [
        "3080",
        "3081",
        "3086",
        "3089"
      ],
      slugs: [
        "miscellaneous-plastics-products",
        "plastics-foam-products",
        "plastics-products-nec",
        "unsupported-plastics-film-sheet"
      ]
    }
  },
  "political-organizations": {
    description: "This industry includes entities that promote the interests of national or local parties or candidates. Political groups organized to raise funds for a political party or individual candidate are included.",
    linkedinId: 107,
    naics: {
      codes: [
        "8139",
        "81393",
        "813930",
        "81394",
        "813940",
        "81399",
        "813990"
      ],
      slugs: [
        "business-professional-labor-political-and-similar-organizations",
        "labor-unions-and-similar-labor-organizations",
        "other-similar-organizations-except-business-professional-labor-and-political-organizations",
        "political-organizations"
      ]
    },
    name: "Political Organizations",
    parents: ["civic-and-social-organizations", "consumer-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "postal-services": {
    description: "This industry includes government entities that provide mail services under a universal service obligation, including the carriage of letters, printed matter, or mailable packages, including acceptance, collection, processing, and delivery.",
    linkedinId: 1573,
    naics: {
      codes: [
        "491",
        "4911",
        "49111",
        "491110"
      ],
      slugs: ["postal-service"]
    },
    name: "Postal Services",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "primary-and-secondary-education": {
    description: "This industry includes entities that furnish academic courses and associated coursework to provide basic preparatory education, ordinarily kindergarten through 12th grade. School boards and school districts are included.",
    linkedinId: 67,
    naics: {
      codes: [
        "6111",
        "61111",
        "611110"
      ],
      slugs: ["elementary-and-secondary-schools"]
    },
    name: "Primary and Secondary Education",
    parents: ["education"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "primary-metal-manufacturing": {
    description: "This industry includes entities that smelt and/or refine ferrous and nonferrous metals from ore, pig or scrap, using electrometallurgical and other process metallurgical techniques.",
    linkedinId: 807,
    naics: {
      codes: [
        "331",
        "3311",
        "33111",
        "331110",
        "3312",
        "33121",
        "331210",
        "33122",
        "331221",
        "331222",
        "3313",
        "33131",
        "331313",
        "331314",
        "331315",
        "331318",
        "3314",
        "33141",
        "331410",
        "33142",
        "331420",
        "33149",
        "331491",
        "331492",
        "3315",
        "33151",
        "331511",
        "331512",
        "331513",
        "33152",
        "331523",
        "331524",
        "331529"
      ],
      slugs: [
        "alumina-and-aluminum-production-and-processing",
        "alumina-refining-and-primary-aluminum-production",
        "aluminum-foundries-except-die-casting",
        "aluminum-sheet-plate-and-foil-manufacturing",
        "copper-rolling-drawing-extruding-and-alloying",
        "ferrous-metal-foundries",
        "foundries",
        "iron-and-steel-mills-and-ferroalloy-manufacturing",
        "iron-and-steel-pipe-and-tube-manufacturing-from-purchased-steel",
        "iron-foundries",
        "nonferrous-metal-die-casting-foundries",
        "nonferrous-metal-except-aluminum-production-and-processing",
        "nonferrous-metal-except-aluminum-smelting-and-refining",
        "nonferrous-metal-except-copper-and-aluminum-rolling-drawing-and-extruding",
        "nonferrous-metal-except-copper-and-aluminum-rolling-drawing-extruding-and-alloying",
        "nonferrous-metal-foundries",
        "other-aluminum-rolling-drawing-and-extruding",
        "other-nonferrous-metal-foundries-except-die-casting",
        "primary-metal-manufacturing",
        "rolled-steel-shape-manufacturing",
        "rolling-and-drawing-of-purchased-steel",
        "secondary-smelting-and-alloying-of-aluminum",
        "secondary-smelting-refining-and-alloying-of-nonferrous-metal-except-copper-and-aluminum",
        "steel-foundries-except-investment",
        "steel-investment-foundries",
        "steel-product-manufacturing-from-purchased-steel",
        "steel-wire-drawing"
      ]
    },
    name: "Primary Metal Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "3310",
        "3312",
        "3317",
        "3320",
        "3330",
        "3334",
        "3341",
        "3350",
        "3357",
        "3360",
        "3390"
      ],
      slugs: [
        "drawing-insulating-of-nonferrous-wire",
        "iron-steel-foundries",
        "miscellaneous-primary-metal-products",
        "nonferrous-foundries-castings",
        "primary-production-of-aluminum",
        "primary-smelting-refining-of-nonferrous-metals",
        "rolling-drawing-extruding-of-nonferrous-metals",
        "secondary-smelting-refining-of-nonferrous-metals",
        "steel-pipe-tubes",
        "steel-works-blast-furnaces-rolling-finishing-mills",
        "steel-works-blast-furnaces-rolling-mills-coke-ovens"
      ]
    }
  },
  "printing-services": {
    description: "This industry includes entities that print on apparel and textile products, paper, metal, glass, plastics, and other materials, except fabric (grey goods).",
    linkedinId: 83,
    naics: {
      codes: [
        "323",
        "3231",
        "32311",
        "323111",
        "323113",
        "323117",
        "32312",
        "323120"
      ],
      slugs: [
        "books-printing",
        "commercial-printing-except-screen-and-books",
        "commercial-screen-printing",
        "printing",
        "printing-and-related-support-activities",
        "support-activities-for-printing"
      ]
    },
    name: "Printing Services",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "2750",
        "2761",
        "2771",
        "2780",
        "2790",
        "3555"
      ],
      slugs: [
        "blankbooks-looseleaf-binders-bookbinding-related-work",
        "commercial-printing",
        "greeting-cards",
        "manifold-business-forms",
        "printing-trades-machinery-equipment",
        "service-industries-for-the-printing-trade"
      ]
    }
  },
  "professional-organizations": {
    description: "This industry includes entities that promote the professional interests of their individual members and their profession as a whole; and may conduct research; develop statistics; sponsor quality and certification standards; lobby public officials; or publish newsletters, books, or periodicals for distribution to their members.",
    linkedinId: 1911,
    naics: {
      codes: ["81392", "813920"],
      slugs: ["professional-organizations"]
    },
    name: "Professional Organizations",
    parents: ["civic-and-social-organizations", "consumer-services"],
    sic: {
      codes: ["8600"],
      slugs: ["services-membership-organizations"]
    }
  },
  "professional-services": {
    description: "This industry includes entities that perform professional, scientific, and technical activities for others, including legal advice and representation; accounting, bookkeeping, and payroll services; architectural, engineering, and specialized design services; computer services; consulting services; research services; advertising services; photographic services; translation and interpretation services; veterinary services; and other professional, scientific, and technical services.",
    isParent: true,
    linkedinId: 1810,
    naics: {
      codes: [
        "54",
        "541",
        "5413",
        "54132",
        "541320",
        "541330",
        "54134",
        "541340",
        "54135",
        "541350",
        "54138",
        "541380",
        "5419",
        "54199",
        "541990"
      ],
      slugs: [
        "all-other-professional-scientific-and-technical-services",
        "architectural-engineering-and-related-services",
        "building-inspection-services",
        "drafting-services",
        "engineering-services",
        "landscape-architectural-services",
        "other-professional-scientific-and-technical-services",
        "professional-scientific-and-technical-services",
        "testing-laboratories-and-services"
      ]
    },
    name: "Professional Services",
    parents: [],
    sic: {
      codes: ["8900"],
      slugs: ["services-services-nec"]
    }
  },
  "professional-training-and-coaching": {
    description: "This industry includes entities that offer training courses in office skills, such as how to use software applications. Also included in this industry are entities that offer short-term courses for professional training. Computer repair training is not included in this industry.",
    linkedinId: 105,
    naics: {
      codes: [
        "6114",
        "61142",
        "611420",
        "61143",
        "611430"
      ],
      slugs: [
        "business-schools-and-computer-and-management-training",
        "computer-training",
        "professional-and-management-development-training"
      ]
    },
    name: "Professional Training and Coaching",
    parents: ["education"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "public-assistance-programs": {
    description: "This industry includes government entities that plan, administer, and coordinate programs for public assistance, social work, and welfare activities, including Social Security, disability insurance, Medicare, unemployment insurance, and workers' compensation programs.",
    linkedinId: 2360,
    naics: {
      codes: ["92313", "923130"],
      slugs: ["administration-of-human-resource-programs-except-education-public-health-and-veterans-affairs-programs"]
    },
    name: "Public Assistance Programs",
    parents: ["government-administration", "health-and-human-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "public-health": {
    description: "This industry includes government entities that plan, administer, and coordinate public health programs and services, including environmental health activities, mental health, categorical health programs, health statistics, and immunization services.",
    linkedinId: 2358,
    naics: {
      codes: ["92312", "923120"],
      slugs: ["administration-of-public-health-programs"]
    },
    name: "Public Health",
    parents: ["government-administration", "health-and-human-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "public-policy-offices": {
    description: "This industry includes offices of government executives, legislative bodies, public finance, and general government support.",
    linkedinId: 79,
    naics: {
      codes: [
        "921",
        "9211",
        "92113",
        "921130",
        "92114",
        "921140",
        "92115",
        "921150",
        "92119",
        "921190"
      ],
      slugs: [
        "american-indian-and-alaska-native-tribal-governments",
        "executive-and-legislative-offices-combined",
        "executive-legislative-and-other-general-government-support",
        "other-general-government-support",
        "public-finance-activities"
      ]
    },
    name: "Public Policy Offices",
    parents: ["government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "public-relations-and-communications-services": {
    description: "This industry includes entities that design and implement public relations campaigns, such as, campaigns to promote the interests and image of their clients. This includes lobbying, political consulting, and public relations consulting.",
    linkedinId: 98,
    naics: {
      codes: ["54182", "541820"],
      slugs: ["public-relations-agencies"]
    },
    name: "Public Relations and Communications Services",
    parents: ["advertising-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "public-safety": {
    description: "This industry includes government entities that provide public order and safety (except courts, police protection, legal counsel and prosecution, correctional institutions, parole offices, probation offices, pardon boards, and fire protection). Government administration of public order, safety programs, and collection of statistics on public safety are included.",
    linkedinId: 78,
    naics: {
      codes: ["92219", "922190"],
      slugs: ["other-justice-public-order-and-safety-activities"]
    },
    name: "Public Safety",
    parents: ["administration-of-justice", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "racetracks": {
    description: "This industry includes entities that operate racetracks and may also present and/or promote the events, such as auto, dog, and horse races, held in these facilities.",
    linkedinId: 2143,
    naics: {
      codes: ["711212"],
      slugs: ["racetracks"]
    },
    name: "Racetracks",
    parents: [
      "entertainment-providers",
      "performing-arts-and-spectator-sports",
      "spectator-sports"
    ],
    sic: {
      codes: ["7948"],
      slugs: ["services-racing-including-track-operation"]
    }
  },
  "radio-and-television-broadcasting": {
    description: "This industry includes entities that operate broadcast studios and facilities for over-the-air or satellite delivery of radio and television programs and often generate revenues from the sale of air time to advertisers, from donations and subsidies, or from the sale of programs.",
    linkedinId: 1633,
    naics: {
      codes: [
        "516110",
        "51612",
        "516120"
      ],
      slugs: ["radio-broadcasting-stations", "television-broadcasting-stations"]
    },
    name: "Radio and Television Broadcasting",
    parents: [
      "broadcast-media-production-and-distribution",
      "media-and-telecommunications",
      "technology-information-and-media"
    ],
    sic: {
      codes: ["4832", "4833"],
      slugs: ["radio-broadcasting-stations", "television-broadcasting-stations"]
    }
  },
  "rail-transportation": {
    description: "This industry includes entities that primarily provide rail transportation of passengers and/or cargo using railroad rolling stock that operate on networks, with physical facilities, labor force, and equipment spread over an extensive geographic area, or operate over a short distance on a local rail line.",
    linkedinId: 1481,
    naics: {
      codes: [
        "482",
        "4821",
        "48211",
        "482111",
        "482112"
      ],
      slugs: [
        "line-haul-railroads",
        "rail-transportation",
        "short-line-railroads"
      ]
    },
    name: "Rail Transportation",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: ["4011", "4013"],
      slugs: ["railroad-switching-terminal-establishments", "railroads-line-haul-operating"]
    }
  },
  "railroad-equipment-manufacturing": {
    description: "This industry includes entities that: manufacture and/or rebuild locomotives, locomotive frames and parts; manufacture railroad, street, and rapid transit cars and car equipment for freight and passenger rail service; and manufacture rail layers, ballast distributors, rail tamping equipment and other railway track maintenance equipment.",
    linkedinId: 62,
    naics: {
      codes: [
        "3365",
        "33651",
        "336510"
      ],
      slugs: ["railroad-rolling-stock-manufacturing"]
    },
    name: "Railroad Equipment Manufacturing",
    parents: ["manufacturing", "transportation-equipment-manufacturing"],
    sic: {
      codes: ["3743"],
      slugs: ["railroad-equipment"]
    }
  },
  "ranching": {
    description: "This industry includes entities that raise cattle, milk dairy cattle, or feed cattle for fattening.",
    linkedinId: 64,
    naics: {
      codes: [
        "1121",
        "11211",
        "112111",
        "112112",
        "11212",
        "112120",
        "11213",
        "112130"
      ],
      slugs: [
        "beef-cattle-ranching-and-farming",
        "beef-cattle-ranching-and-farming-including-feedlots",
        "cattle-feedlots",
        "cattle-ranching-and-farming",
        "dairy-cattle-and-milk-production",
        "dual-purpose-cattle-ranching-and-farming"
      ]
    },
    name: "Ranching",
    parents: ["farming-ranching-forestry", "ranching-and-fisheries"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "ranching-and-fisheries": {
    description: "This industry includes entities that raise or fatten animals and/or raise aquatic plants and aquatic animals in controlled or selected aquatic environments. This includes ranches, farms, and feedlots that keep animals for the products they produce or for eventual sale.",
    linkedinId: 256,
    naics: {
      codes: [
        "112",
        "1122",
        "11221",
        "112210",
        "1123",
        "11231",
        "112310",
        "11232",
        "112320",
        "11233",
        "112330",
        "11234",
        "112340",
        "11239",
        "112390",
        "1124",
        "11241",
        "112410",
        "11242",
        "112420",
        "1125",
        "11251",
        "112511",
        "112512",
        "112519",
        "1129",
        "11291",
        "112910",
        "11292",
        "112920",
        "11293",
        "112930",
        "11299",
        "112990"
      ],
      slugs: [
        "all-other-animal-production",
        "animal-production-and-aquaculture",
        "apiculture",
        "aquaculture",
        "broilers-and-other-meat-type-chicken-production",
        "chicken-egg-production",
        "finfish-farming-and-fish-hatcheries",
        "fur-bearing-animal-and-rabbit-production",
        "goat-farming",
        "hog-and-pig-farming",
        "horses-and-other-equine-production",
        "other-animal-production",
        "other-aquaculture",
        "other-poultry-production",
        "poultry-and-egg-production",
        "poultry-hatcheries",
        "sheep-and-goat-farming",
        "sheep-farming",
        "shellfish-farming",
        "turkey-production"
      ]
    },
    name: "Ranching and Fisheries",
    parents: ["farming-ranching-forestry"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "real-estate": {
    description: "This industry includes entities that rent or lease real estate to others; manage, sell, buy, or rent real estate for others; and provide other services related to real estate, such as appraisals. Equity real estate investment trusts (REITs) that lease buildings, dwellings, or other real estate property to others are included. Property development organizations are included in this industry.",
    linkedinId: 44,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Real Estate",
    parents: ["real-estate-and-equipment-rental-services"],
    sic: {
      codes: [
        "6500",
        "6532",
        "6794",
        "6798"
      ],
      slugs: [
        "patent-owners-lessors",
        "real-estate",
        "real-estate-dealers-for-their-own-account",
        "real-estate-investment-trusts"
      ]
    }
  },
  "real-estate-agents-and-brokers": {
    description: "This industry includes entities that primarily act as agents and/or brokers in selling real estate for others; buying real estate for others; and/or renting real estate for others.",
    linkedinId: 1770,
    naics: {
      codes: [
        "5312",
        "53121",
        "531210"
      ],
      slugs: ["offices-of-real-estate-agents-and-brokers"]
    },
    name: "Real Estate Agents and Brokers",
    parents: ["real-estate", "real-estate-and-equipment-rental-services"],
    sic: {
      codes: ["6531"],
      slugs: ["real-estate-agents-managers-for-others"]
    }
  },
  "real-estate-and-equipment-rental-services": {
    description: "This industry includes entities that rent, lease, or otherwise allow the use of tangible or intangible assets, and entities providing related services such as managing real estate for others, selling, renting and/or buying real estate for others, and appraising real estate.",
    isParent: true,
    linkedinId: 1757,
    naics: {
      codes: [
        "53",
        "533",
        "5331",
        "53311",
        "533110"
      ],
      slugs: ["lessors-of-nonfinancial-intangible-assets-except-copyrighted-works", "real-estate-and-rental-and-leasing"]
    },
    name: "Real Estate and Equipment Rental Services",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "recreational-facilities": {
    description: "This industry includes entities that operate facilities where patrons can engage in sports, recreation, amusement, or gambling activities and/or provide other amusement and recreation, such as amusement devices in places of business operated by others; operation of sports teams, clubs, or leagues that play games for recreational purposes; and tours that do not use transportation equipment.",
    linkedinId: 40,
    naics: {
      codes: [
        "713",
        "7139",
        "71393",
        "713930",
        "71395",
        "713950",
        "71399",
        "713990"
      ],
      slugs: [
        "all-other-amusement-and-recreation-industries",
        "amusement-gambling-and-recreation-industries",
        "bowling-centers",
        "marinas",
        "other-amusement-and-recreation-industries"
      ]
    },
    name: "Recreational Facilities",
    parents: ["entertainment-providers"],
    sic: {
      codes: ["7900", "7990"],
      slugs: ["services-amusement-recreation-services", "services-miscellaneous-amusement-recreation"]
    }
  },
  "regenerative-design": {
    description: "This industry includes entities that apply specialized design approaches to design buildings, products, and systems that are ecologically restorative and inherently beneficial to the environment. Entities involved in this industry utilize techniques such as biomimicry, permaculture, and green building.",
    linkedinId: 3256,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Regenerative Design",
    parents: ["design-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "religious-institutions": {
    description: "This industry includes religious organizations, such as churches, religious temples, and monasteries, and/or entities that organize religion or promote religious activities.",
    linkedinId: 89,
    naics: {
      codes: [
        "8131",
        "81311",
        "813110"
      ],
      slugs: ["religious-organizations"]
    },
    name: "Religious Institutions",
    parents: ["consumer-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "renewable-energy-equipment-manufacturing": {
    description: "This industry includes entities that manufacture renewable energy machinery and equipment.",
    linkedinId: 3241,
    naics: {
      codes: ["3336"],
      slugs: ["engine-turbine-and-power-transmission-equipment-manufacturing"]
    },
    name: "Renewable Energy Equipment Manufacturing",
    parents: [
      "engines-and-power-transmission-equipment-manufacturing",
      "machinery-manufacturing",
      "manufacturing"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "renewable-energy-power-generation": {
    description: "This industry includes entities that operate electric power generation facilities that use renewable energy resources.",
    linkedinId: 3240,
    naics: {
      codes: ["22111"],
      slugs: ["electric-power-generation"]
    },
    name: "Renewable Energy Power Generation",
    parents: ["electric-power-generation", "utilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "renewable-energy-semiconductor-manufacturing": {
    description: "This industry includes entities that manufacture semiconductors and other components for use in photovoltaic cells and other renewable energy applications.",
    linkedinId: 144,
    naics: {
      codes: ["33441"],
      slugs: ["semiconductor-and-other-electronic-component-manufacturing"]
    },
    name: "Renewable Energy Semiconductor Manufacturing",
    parents: [
      "computers-and-electronics-manufacturing",
      "manufacturing",
      "semiconductor-manufacturing"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "repair-and-maintenance": {
    description: "This industry includes entities that restore machinery, equipment, and other products to working order and provide general or routine maintenance (such as, servicing) on such products to ensure they work efficiently and to prevent breakdown and unnecessary repairs.",
    linkedinId: 2225,
    naics: {
      codes: [
        "811",
        "8114",
        "81141",
        "811411",
        "811412",
        "81149",
        "811490"
      ],
      slugs: [
        "appliance-repair-and-maintenance",
        "home-and-garden-equipment-and-appliance-repair-and-maintenance",
        "home-and-garden-equipment-repair-and-maintenance",
        "other-personal-and-household-goods-repair-and-maintenance",
        "personal-and-household-goods-repair-and-maintenance",
        "repair-and-maintenance"
      ]
    },
    name: "Repair and Maintenance",
    parents: ["consumer-services"],
    sic: {
      codes: ["7600"],
      slugs: ["services-miscellaneous-repair-services"]
    }
  },
  "research-services": {
    description: "This industry includes entities that conduct original investigation undertaken on a systematic basis to gain new knowledge (research) and/or apply research findings or other scientific knowledge to create new or significantly improved products or processes (experimental development). Techniques may include modeling and simulation.",
    linkedinId: 70,
    naics: {
      codes: [
        "5417",
        "54171",
        "541715"
      ],
      slugs: [
        "research-and-development-in-the-physical-engineering-and-life-sciences",
        "research-and-development-in-the-physical-engineering-and-life-sciences-except-nanotechnology-and-biotechnology",
        "scientific-research-and-development-services"
      ]
    },
    name: "Research Services",
    parents: ["professional-services"],
    sic: {
      codes: ["8734"],
      slugs: ["services-testing-laboratories"]
    }
  },
  "residential-building-construction": {
    description: "This industry includes entities that construct or remodel and renovate single-family and multifamily residential buildings. Included are residential housing general contractors (such as, new construction, remodeling, or renovating existing residential structures), for-sale builders and remodelers of residential structures, residential project construction management firms, and residential design-build firms.",
    linkedinId: 408,
    naics: {
      codes: [
        "2361",
        "23611",
        "236115",
        "236116",
        "236117",
        "236118"
      ],
      slugs: [
        "new-housing-for-sale-builders",
        "new-multifamily-housing-construction-except-for-sale-builders",
        "new-single-family-housing-construction-except-for-sale-builders",
        "residential-building-construction",
        "residential-remodelers"
      ]
    },
    name: "Residential Building Construction",
    parents: ["building-construction", "construction"],
    sic: {
      codes: ["1520"],
      slugs: ["general-bldg-contractors-residential-bldgs"]
    }
  },
  "restaurants": {
    description: "This industry includes entities that provide food to patrons who are served while seated and pay after eating, or those who select items (e.g., at a counter, in a buffet line) and pay before eating. Entities that prepare and/or serve specialty snacks and/or nonalcoholic beverages for consumption on or near the premises are included.",
    linkedinId: 32,
    naics: {
      codes: [
        "7225",
        "72251",
        "722511",
        "722513",
        "722514",
        "722515"
      ],
      slugs: [
        "cafeterias-grill-buffets-and-buffets",
        "full-service-restaurants",
        "limited-service-restaurants",
        "restaurants-and-other-eating-places",
        "snack-and-nonalcoholic-beverage-bars"
      ]
    },
    name: "Restaurants",
    parents: ["accommodation-services", "food-and-beverage-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail": {
    description: "This industry includes entities that retail merchandise generally in small quantities to the general public and provide services incidental to the sale of the merchandise.",
    isParent: true,
    linkedinId: 27,
    naics: {
      codes: [
        "4234",
        "4239",
        "4249",
        "44-45",
        "441",
        "4412",
        "44121",
        "441210",
        "44122",
        "441222",
        "441227",
        "4413",
        "441330",
        "441340",
        "445132",
        "455",
        "4551",
        "45511",
        "455110",
        "4552",
        "45521",
        "455211",
        "455219",
        "4572",
        "45721",
        "457210",
        "459",
        "4591",
        "45911",
        "459110",
        "45912",
        "459120",
        "45913",
        "459130",
        "4599",
        "45991",
        "459910",
        "45993",
        "459930",
        "45999",
        "459991",
        "459999"
      ],
      slugs: [
        "all-other-general-merchandise-retailers",
        "all-other-miscellaneous-retailers",
        "automotive-parts-accessories-and-tire-retailers",
        "automotive-parts-and-accessories-retailers",
        "boat-dealers",
        "department-stores",
        "fuel-dealers",
        "general-merchandise-retailers",
        "hobby-toy-and-game-retailers",
        "manufactured-mobile-home-dealers",
        "miscellaneous-durable-goods-merchant-wholesalers",
        "miscellaneous-nondurable-goods-merchant-wholesalers",
        "motor-vehicle-and-parts-dealers",
        "motorcycle-atv-and-all-other-motor-vehicle-dealers",
        "motorcycle-boat-and-other-motor-vehicle-dealers",
        "other-miscellaneous-retailers",
        "other-motor-vehicle-dealers",
        "pet-and-pet-supplies-retailers",
        "professional-and-commercial-equipment-and-supplies-merchant-wholesalers",
        "recreational-vehicle-dealers",
        "retail-trade",
        "sewing-needlework-and-piece-goods-retailers",
        "sporting-goods-hobby-and-musical-instrument-retailers",
        "sporting-goods-hobby-musical-instrument-book-and-miscellaneous-retailers",
        "sporting-goods-retailers",
        "tire-dealers",
        "tobacco-electronic-cigarette-and-other-smoking-supplies-retailers",
        "vending-machine-operators",
        "warehouse-clubs-and-supercenters",
        "warehouse-clubs-supercenters-and-other-general-merchandise-retailers"
      ]
    },
    name: "Retail",
    parents: [],
    sic: {
      codes: [
        "5311",
        "5331",
        "5399",
        "5900",
        "5940",
        "5945",
        "5990"
      ],
      slugs: [
        "retail-department-stores",
        "retail-hobby-toy-game-shops",
        "retail-misc-general-merchandise-stores",
        "retail-miscellaneous-retail",
        "retail-miscellaneous-shopping-goods-stores",
        "retail-retail-stores-nec",
        "retail-variety-stores"
      ]
    }
  },
  "retail-apparel-and-fashion": {
    description: "This industry includes entities that retail new clothing and clothing accessories from fixed point-of-sale locations.",
    linkedinId: 19,
    naics: {
      codes: [
        "458",
        "4581",
        "45811",
        "458110",
        "4582",
        "45821",
        "458210"
      ],
      slugs: [
        "clothing-and-clothing-accessories-retailers",
        "clothing-clothing-accessories-shoe-and-jewelry-retailers",
        "shoe-retailers"
      ]
    },
    name: "Retail Apparel and Fashion",
    parents: ["retail"],
    sic: {
      codes: [
        "5600",
        "5621",
        "5651",
        "5661"
      ],
      slugs: [
        "retail-apparel-accessory-stores",
        "retail-family-clothing-stores",
        "retail-shoe-stores",
        "retail-women-s-clothing-stores"
      ]
    }
  },
  "retail-appliances-electrical-and-electronic-equipment": {
    description: "This industry includes entities that primarily retail new electronics and appliances, including heating and refrigeration appliances, from point-of-sale locations and may also offer maintenance and repair of the electronic equipment and appliances.",
    linkedinId: 1319,
    naics: {
      codes: [
        "449",
        "4492",
        "44921",
        "449210"
      ],
      slugs: ["electronics-and-appliance-retailers", "furniture-home-furnishings-electronics-and-appliance-retailers"]
    },
    name: "Retail Appliances, Electrical, and Electronic Equipment",
    parents: ["retail"],
    sic: {
      codes: [
        "3670",
        "3672",
        "3677",
        "3678",
        "3679",
        "5731",
        "5734"
      ],
      slugs: [
        "electronic-coils-transformers-other-inductors",
        "electronic-components-accessories",
        "electronic-components-nec",
        "electronic-connectors",
        "printed-circuit-boards",
        "retail-computer-computer-software-stores",
        "retail-radio-tv-consumer-electronics-stores"
      ]
    }
  },
  "retail-art-dealers": {
    description: "This industry includes entities that retail original and limited-edition art works. Included in this industry are entities engaged in displaying works of art for retail sale in art galleries.",
    linkedinId: 3186,
    naics: {
      codes: ["45992", "459920"],
      slugs: ["art-dealers"]
    },
    name: "Retail Art Dealers",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail-art-supplies": {
    description: "This industry includes entities that retail art materials and supplies.",
    linkedinId: 111,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Retail Art Supplies",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail-books-and-printed-news": {
    description: "This industry includes entities that primarily retail new books, newspapers, magazines, and other periodicals.",
    linkedinId: 1409,
    naics: {
      codes: [
        "4592",
        "45921",
        "459210"
      ],
      slugs: ["book-retailers-and-news-dealers"]
    },
    name: "Retail Books and Printed News",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail-building-materials-and-garden-equipment": {
    description: "This industry includes entities that primarily retail new building materials and garden equipment and supplies, including hardware and plumbing fixtures, from fixed point-of-sale locations.",
    linkedinId: 1324,
    naics: {
      codes: [
        "444",
        "4441",
        "44411",
        "444110",
        "44412",
        "444120",
        "44414",
        "444140",
        "44418",
        "444180",
        "4442",
        "44423",
        "444230",
        "44424",
        "444240"
      ],
      slugs: [
        "building-material-and-garden-equipment-and-supplies-dealers",
        "building-material-and-supplies-dealers",
        "hardware-retailers",
        "home-centers",
        "lawn-and-garden-equipment-and-supplies-retailers",
        "nursery-garden-center-and-farm-supply-retailers",
        "other-building-material-dealers",
        "outdoor-power-equipment-retailers",
        "paint-and-wallpaper-retailers"
      ]
    },
    name: "Retail Building Materials and Garden Equipment",
    parents: ["retail"],
    sic: {
      codes: ["5200", "5211"],
      slugs: ["retail-building-materials-hardware-garden-supply", "retail-lumber-other-building-materials-dealers"]
    }
  },
  "retail-florists": {
    description: "This industry includes entities that primarily retail cut flowers, floral arrangements, and potted plants purchased from others.",
    linkedinId: 1423,
    naics: {
      codes: [
        "4593",
        "45931",
        "459310"
      ],
      slugs: ["florists"]
    },
    name: "Retail Florists",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail-furniture-and-home-furnishings": {
    description: "This industry includes entities that primarily retail new furniture and home furnishings from fixed point-of-sale locations and many offer interior decorating services in addition to the sale of products.",
    linkedinId: 1309,
    naics: {
      codes: [
        "449",
        "4491",
        "44911",
        "449110",
        "44912",
        "449121",
        "449122",
        "449129"
      ],
      slugs: [
        "all-other-home-furnishings-retailers",
        "floor-covering-retailers",
        "furniture-and-home-furnishings-retailers",
        "furniture-home-furnishings-electronics-and-appliance-retailers",
        "furniture-retailers",
        "home-furnishings-retailers",
        "window-treatment-retailers"
      ]
    },
    name: "Retail Furniture and Home Furnishings",
    parents: ["retail"],
    sic: {
      codes: ["5700", "5712"],
      slugs: ["retail-furniture-stores", "retail-home-furniture-furnishings-equipment-stores"]
    }
  },
  "retail-gasoline": {
    description: "This industry includes entities that primarily retail automotive fuels (e.g., gasoline, diesel fuel, gasohol, alternative fuels) and automotive oils or retail these products in combination with convenience store items.",
    linkedinId: 1370,
    naics: {
      codes: [
        "457",
        "4571",
        "45711",
        "457110",
        "45712",
        "457120"
      ],
      slugs: [
        "gasoline-stations",
        "gasoline-stations-and-fuel-dealers",
        "gasoline-stations-with-convenience-stores",
        "other-gasoline-stations"
      ]
    },
    name: "Retail Gasoline",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail-groceries": {
    description: "This industry includes entities that retail a general line of food, such as canned and frozen foods; fresh fruits and vegetables; and fresh and prepared meats, fish, and poultry. Examples include grocery stores, supermarkets, and delicatessens.",
    linkedinId: 22,
    naics: {
      codes: ["44511", "445110"],
      slugs: ["supermarkets-and-other-grocery-retailers-except-convenience-retailers"]
    },
    name: "Retail Groceries",
    parents: ["food-and-beverage-retail", "retail"],
    sic: {
      codes: [
        "5400",
        "5411",
        "5412"
      ],
      slugs: [
        "retail-convenience-stores",
        "retail-food-stores",
        "retail-grocery-stores"
      ]
    }
  },
  "retail-health-and-personal-care-products": {
    description: "This industry includes entities that primarily retail health and personal care merchandise from fixed point-of-sale locations and may offer services related to retailing, advising customers, and/or fitting the product sold to the customer's needs.",
    linkedinId: 1359,
    naics: {
      codes: [
        "456",
        "4561",
        "45612",
        "456120",
        "45613",
        "456130",
        "45619",
        "456191",
        "456199"
      ],
      slugs: [
        "all-other-health-and-personal-care-retailers",
        "cosmetics-beauty-supplies-and-perfume-retailers",
        "food-health-supplement-retailers",
        "health-and-personal-care-retailers",
        "optical-goods-retailers",
        "other-health-and-personal-care-retailers"
      ]
    },
    name: "Retail Health and Personal Care Products",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail-luxury-goods-and-jewelry": {
    description: "This industry includes entities that retail one or more of the following items: new jewelry (except costume jewelry); new sterling and plated silverware; and new watches and clocks. Also included are entities retailing these new products in combination with lapidary work and/or repair services.",
    linkedinId: 143,
    naics: {
      codes: [
        "4583",
        "45831",
        "458310",
        "45832",
        "458320"
      ],
      slugs: [
        "jewelry-luggage-and-leather-goods-retailers",
        "jewelry-retailers",
        "luggage-and-leather-goods-retailers"
      ]
    },
    name: "Retail Luxury Goods and Jewelry",
    parents: ["retail"],
    sic: {
      codes: [
        "3873",
        "3910",
        "3911",
        "3960",
        "5944"
      ],
      slugs: [
        "costume-jewelry-novelties",
        "jewelry-precious-metal",
        "jewelry-silverware-plated-ware",
        "retail-jewelry-stores",
        "watches-clocks-clockwork-operated-devices-parts"
      ]
    }
  },
  "retail-motor-vehicles": {
    description: "This industry includes entities that primarily retail motor vehicles and parts from fixed point-of-sale locations and also provide repair and maintenance services for vehicles.",
    linkedinId: 1292,
    naics: {
      codes: [
        "4411",
        "44111",
        "441110",
        "44112",
        "441120",
        "44133",
        "44134"
      ],
      slugs: [
        "automobile-dealers",
        "automotive-parts-and-accessories-retailers",
        "new-car-dealers",
        "tire-dealers",
        "used-car-dealers"
      ]
    },
    name: "Retail Motor Vehicles",
    parents: ["retail"],
    sic: {
      codes: [
        "5271",
        "5500",
        "5531"
      ],
      slugs: [
        "retail-auto-dealers-gasoline-stations",
        "retail-auto-home-supply-stores",
        "retail-mobile-home-dealers"
      ]
    }
  },
  "retail-musical-instruments": {
    description: "This industry includes entities that primarily retail new musical instruments, sheet music, and related supplies; or retailing these new products in combination with services like musical instrument repair, rental, or music instruction.",
    linkedinId: 1407,
    naics: {
      codes: ["45914", "459140"],
      slugs: ["musical-instrument-and-supplies-retailers"]
    },
    name: "Retail Musical Instruments",
    parents: ["retail"],
    sic: {
      codes: ["3931", "5735"],
      slugs: ["musical-instruments", "retail-record-prerecorded-tape-stores"]
    }
  },
  "retail-office-equipment": {
    description: "This industry includes entities that retail office equipment, such as printers, copiers, and other general office machinery.",
    linkedinId: 138,
    naics: {
      codes: ["45941", "459410"],
      slugs: ["office-supplies-and-stationery-retailers"]
    },
    name: "Retail Office Equipment",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail-office-supplies-and-gifts": {
    description: "This industry includes entities that primarily retail new office supplies, stationery, gifts, novelty merchandise, and souvenirs.",
    linkedinId: 1424,
    naics: {
      codes: [
        "4594",
        "45942",
        "459420"
      ],
      slugs: ["gift-novelty-and-souvenir-retailers", "office-supplies-stationery-and-gift-retailers"]
    },
    name: "Retail Office Supplies and Gifts",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "retail-pharmacies": {
    description: "This industry includes entities that retail prescription or nonprescription drugs and medicines.",
    linkedinId: 3250,
    naics: {
      codes: ["45611", "456110"],
      slugs: ["pharmacies-and-drug-retailers"]
    },
    name: "Retail Pharmacies",
    parents: ["retail", "retail-health-and-personal-care-products"],
    sic: {
      codes: ["5912"],
      slugs: ["retail-drug-stores-and-proprietary-stores"]
    }
  },
  "retail-recyclable-materials-and-used-merchandise": {
    description: "This industry includes entities that primarily retail used merchandise, antiques, and secondhand goods (except motor vehicles, such as automobiles, RVs, motorcycles, and boats; motor vehicle parts; tires; and mobile homes, which are in separate industries).",
    linkedinId: 1431,
    naics: {
      codes: [
        "4595",
        "45951",
        "459510"
      ],
      slugs: ["used-merchandise-retailers"]
    },
    name: "Retail Recyclable Materials and Used Merchandise",
    parents: ["retail"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "reupholstery-and-furniture-repair": {
    description: "This industry includes entities that offer one or more of the following services: Reupholstering furniture; Refinishing furniture; Repairing furniture; and Repairing and restoring furniture.",
    linkedinId: 2253,
    naics: {
      codes: ["81142", "811420"],
      slugs: ["reupholstery-and-furniture-repair"]
    },
    name: "Reupholstery and Furniture Repair",
    parents: ["consumer-services", "repair-and-maintenance"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "robot-manufacturing": {
    description: "This industry includes entities that manufacture autonomous or semi-autonomous robots.",
    linkedinId: 3247,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Robot Manufacturing",
    parents: [
      "automation-machinery-manufacturing",
      "machinery-manufacturing",
      "manufacturing"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "robotics-engineering": {
    description: "This industry includes entities that design autonomous or semi-autonomous robots using knowledge from multiple engineering disciplines, such as computer engineering, mechanical engineering, electrical engineering, bioengineering, control engineering and software engineering.",
    linkedinId: 3248,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Robotics Engineering",
    parents: ["engineering-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "rubber-products-manufacturing": {
    description: "This industry includes entities that process natural, synthetic, or reclaimed rubber materials into intermediate or final products using processes, such as vulcanizing, cementing, molding, extruding, and lathe-cutting.",
    linkedinId: 763,
    naics: {
      codes: [
        "3262",
        "32621",
        "326211",
        "326212",
        "32622",
        "326220",
        "32629",
        "326291",
        "326299"
      ],
      slugs: [
        "all-other-rubber-product-manufacturing",
        "other-rubber-product-manufacturing",
        "rubber-and-plastics-hoses-and-belting-manufacturing",
        "rubber-product-manufacturing",
        "rubber-product-manufacturing-for-mechanical-use",
        "tire-manufacturing",
        "tire-manufacturing-except-retreading",
        "tire-retreading"
      ]
    },
    name: "Rubber Products Manufacturing",
    parents: ["manufacturing", "plastics-and-rubber-product-manufacturing"],
    sic: {
      codes: [
        "3011",
        "3050",
        "3060"
      ],
      slugs: [
        "fabricated-rubber-products-nec",
        "gaskets-packg-sealg-devices-rubber-plastics-hose",
        "tires-inner-tubes"
      ]
    }
  },
  "satellite-telecommunications": {
    description: "This industry includes entities that provide telecommunications services to other entities in the telecommunications and broadcasting industries by forwarding and receiving communications signals via a system of satellites or reselling satellite telecommunications.",
    linkedinId: 1649,
    naics: {
      codes: [
        "5174",
        "51741",
        "517410"
      ],
      slugs: ["satellite-telecommunications"]
    },
    name: "Satellite Telecommunications",
    parents: [
      "media-and-telecommunications",
      "technology-information-and-media",
      "telecommunications"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "savings-institutions": {
    description: "This industry includes entities that primarily accept time deposits, make mortgage and real estate loans, and invest in high-grade securities, including savings and loan associations and savings banks.",
    linkedinId: 1678,
    naics: {
      codes: ["52218", "522180"],
      slugs: ["savings-institutions-and-other-depository-credit-intermediation"]
    },
    name: "Savings Institutions",
    parents: ["credit-intermediation", "financial-services"],
    sic: {
      codes: ["6035", "6036"],
      slugs: ["savings-institution-federally-chartered", "savings-institutions-not-federally-chartered"]
    }
  },
  "school-and-employee-bus-services": {
    description: "This industry includes entities that primarily provide buses and other motor vehicles to transport pupils to and from school or employees to and from work.",
    linkedinId: 1512,
    naics: {
      codes: [
        "4854",
        "48541",
        "485410"
      ],
      slugs: ["school-and-employee-bus-transportation"]
    },
    name: "School and Employee Bus Services",
    parents: ["ground-passenger-transportation", "transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "seafood-product-manufacturing": {
    description: "This industry includes entities that: can seafood (including soup); smoke, salt, and dry seafood; eviscerate fresh fish by removing heads, fins, scales, bones, and entrails; shuck and pack fresh shellfish; process marine fats and oils; and freeze seafood. Factory ships that gather and process seafood into canned seafood products are included.",
    linkedinId: 528,
    naics: {
      codes: [
        "3117",
        "31171",
        "311710"
      ],
      slugs: ["seafood-product-preparation-and-packaging"]
    },
    name: "Seafood Product Manufacturing",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: ["2092"],
      slugs: ["prepared-fresh-or-frozen-fish-seafoods"]
    }
  },
  "secretarial-schools": {
    description: "This industry focuses on training for secretarial and administrative staff and includes entities that offer courses in secretarial and other basic office skills.",
    linkedinId: 2012,
    naics: {
      codes: ["61141", "611410"],
      slugs: ["business-and-secretarial-schools"]
    },
    name: "Secretarial Schools",
    parents: ["education", "technical-and-vocational-training"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "securities-and-commodity-exchanges": {
    description: "This industry includes entities that furnish physical or electronic marketplaces to facilitate the buying and selling of stocks, stock options, bonds, or commodity contracts.",
    linkedinId: 1713,
    naics: {
      codes: [
        "5232",
        "52321",
        "523210"
      ],
      slugs: ["securities-and-commodity-exchanges"]
    },
    name: "Securities and Commodity Exchanges",
    parents: ["capital-markets", "financial-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "security-and-investigations": {
    description: "This industry includes entities that: provide investigation, guard, and armored car services ; sell security systems, such as burglar and fire alarms and locking devices, along with installation, repair, or monitoring services ; provide remote monitoring of electronic security alarm systems.",
    linkedinId: 121,
    naics: {
      codes: [
        "5616",
        "56161",
        "561611",
        "561613"
      ],
      slugs: [
        "armored-car-services",
        "investigation-and-security-services",
        "investigation-guard-and-armored-car-services",
        "investigation-services"
      ]
    },
    name: "Security and Investigations",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "security-guards-and-patrol-services": {
    description: "This industry includes entities that provide staff for guard and patrol services, such as bodyguard, guard dog, and parking security services.",
    linkedinId: 1956,
    naics: {
      codes: ["561612"],
      slugs: ["security-guards-and-patrol-services"]
    },
    name: "Security Guards and Patrol Services",
    parents: ["administrative-and-support-services", "security-and-investigations"],
    sic: {
      codes: ["7381"],
      slugs: ["services-detective-guard-armored-car-services"]
    }
  },
  "security-systems-services": {
    description: "This industry includes entities that sell security systems hardware , such as burglar and fire alarms and locking devices, along with installation, repair, or remote monitoring of electronic security alarm systems.",
    linkedinId: 1958,
    naics: {
      codes: [
        "56162",
        "561621",
        "561622"
      ],
      slugs: [
        "locksmiths",
        "security-systems-services",
        "security-systems-services-except-locksmiths"
      ]
    },
    name: "Security Systems Services",
    parents: ["administrative-and-support-services", "security-and-investigations"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "semiconductor-manufacturing": {
    description: "This industry includes entities that manufacture semiconductors and other components for electronic applications.",
    linkedinId: 7,
    naics: {
      codes: ["3344"],
      slugs: ["semiconductor-and-other-electronic-component-manufacturing"]
    },
    name: "Semiconductor Manufacturing",
    parents: ["computers-and-electronics-manufacturing", "manufacturing"],
    sic: {
      codes: ["3674"],
      slugs: ["semiconductors-related-devices"]
    }
  },
  "services-for-renewable-energy": {
    description: "This industry includes entities that provide services for renewable energy solutions, such as solar panel installation and renewable energy consulting services.",
    linkedinId: 3243,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Services for Renewable Energy",
    parents: ["professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "services-for-the-elderly-and-disabled": {
    description: "This industry includes entities that provide services in support of the elderly and disabled.",
    linkedinId: 2112,
    naics: {
      codes: ["62412", "624120"],
      slugs: ["services-for-the-elderly-and-persons-with-disabilities"]
    },
    name: "Services for the Elderly and Disabled",
    parents: ["community-services", "hospitals-and-health-care"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "sheet-music-publishing": {
    description: "This industry includes entities that primarily acquire and register copyrights for musical compositions in accordance with law and promoting and authorizing the use of these compositions in recordings, radio, television, motion pictures, live performances, print, or other media.",
    linkedinId: 1625,
    naics: {
      codes: ["51223", "512230"],
      slugs: ["music-publishers"]
    },
    name: "Sheet Music Publishing",
    parents: [
      "media-and-telecommunications",
      "movies-videos-and-sound",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "shipbuilding": {
    description: "This industry includes entities that operate shipyards or boatyards (such as, ship or boat manufacturing facilities). Activities include the construction of ships, their repair, conversion and alteration, production of prefabricated ship and barge sections, and specialized services such as ship scaling.",
    linkedinId: 58,
    naics: {
      codes: [
        "3366",
        "33661",
        "336611",
        "336612"
      ],
      slugs: [
        "boat-building",
        "ship-and-boat-building",
        "ship-building-and-repairing"
      ]
    },
    name: "Shipbuilding",
    parents: ["manufacturing", "transportation-equipment-manufacturing"],
    sic: {
      codes: ["3730"],
      slugs: ["ship-boat-building-repairing"]
    }
  },
  "shuttles-and-special-needs-transportation-services": {
    description: "This industry includes entities that primarily provide other transit and ground passenger transportation (except urban transit systems, interurban and rural bus transportation, taxi services, school and employee bus transportation, charter bus services, and limousine services (except shuttle services), which are in separate industries), including shuttle services (except employee bus, which is in a separate industry) and special needs transportation services.",
    linkedinId: 1517,
    naics: {
      codes: [
        "4859",
        "48599",
        "485991",
        "485999"
      ],
      slugs: [
        "all-other-transit-and-ground-passenger-transportation",
        "other-transit-and-ground-passenger-transportation",
        "special-needs-transportation"
      ]
    },
    name: "Shuttles and Special Needs Transportation Services",
    parents: ["ground-passenger-transportation", "transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "sightseeing-transportation": {
    description: "This industry includes entities that primarily use transportation equipment to provide recreation and entertainment.",
    linkedinId: 1532,
    naics: {
      codes: [
        "487",
        "4871",
        "48711",
        "487110",
        "4872",
        "48721",
        "487210",
        "4879",
        "48799",
        "487990"
      ],
      slugs: [
        "scenic-and-sightseeing-transportation",
        "scenic-and-sightseeing-transportation-land",
        "scenic-and-sightseeing-transportation-other",
        "scenic-and-sightseeing-transportation-water"
      ]
    },
    name: "Sightseeing Transportation",
    parents: ["ground-passenger-transportation", "transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "skiing-facilities": {
    description: "This industry includes entities that operate downhill, cross country/region, or related skiing areas and/or operate related equipment, such as ski lifts and tows.",
    linkedinId: 2181,
    naics: {
      codes: ["71392", "713920"],
      slugs: ["skiing-facilities"]
    },
    name: "Skiing Facilities",
    parents: ["entertainment-providers", "recreational-facilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "smart-meter-manufacturing": {
    description: "This industry includes entities that manufacture electronic metering devices that measure and record utility usage in real-time and enable two way communication with utility providers.",
    linkedinId: 3254,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Smart Meter Manufacturing",
    parents: [
      "computers-and-electronics-manufacturing",
      "manufacturing",
      "measuring-and-control-instrument-manufacturing"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "soap-and-cleaning-product-manufacturing": {
    description: "This industry includes entities that manufacture and package soaps and other cleaning compounds, surface active agents, and textile and leather finishing agents used to reduce tension or speed the drying process.",
    linkedinId: 727,
    naics: {
      codes: [
        "32561",
        "325611",
        "325612",
        "325613"
      ],
      slugs: [
        "polish-and-other-sanitation-good-manufacturing",
        "soap-and-cleaning-compound-manufacturing",
        "soap-and-other-detergent-manufacturing",
        "surface-active-agent-manufacturing"
      ]
    },
    name: "Soap and Cleaning Product Manufacturing",
    parents: ["chemical-manufacturing", "manufacturing"],
    sic: {
      codes: [
        "2840",
        "2842",
        "2844"
      ],
      slugs: [
        "perfumes-cosmetics-other-toilet-preparations",
        "soap-detergents-cleang-preparations-perfumes-cosmetics",
        "specialty-cleaning-polishing-and-sanitation-preparations"
      ]
    }
  },
  "social-networking-platforms": {
    description: "This industry includes entities that provide services and infrastructure for social networking, primarily over the internet.",
    linkedinId: 3127,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Social Networking Platforms",
    parents: ["technology-information-and-internet", "technology-information-and-media"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "software-development": {
    description: "This industry includes entities that publish computer software and carry out operations necessary for production and distribution, such as design, documentation, installation, and support services. They may design, develop, and publish, or publish only; they may publish and distribute remotely through subscriptions and downloads.",
    linkedinId: 4,
    naics: {
      codes: [
        "5132",
        "51321",
        "513210"
      ],
      slugs: ["software-publishers"]
    },
    name: "Software Development",
    parents: ["technology-information-and-internet", "technology-information-and-media"],
    sic: {
      codes: ["7372"],
      slugs: ["services-prepackaged-software"]
    }
  },
  "solar-electric-power-generation": {
    description: "This industry includes entities that operate solar electric power generation facilities. These facilities use energy from the sun to produce electric energy.",
    linkedinId: 387,
    naics: {
      codes: ["221114"],
      slugs: ["solar-electric-power-generation"]
    },
    name: "Solar Electric Power Generation",
    parents: [
      "electric-power-generation",
      "renewable-energy-power-generation",
      "utilities"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "sound-recording": {
    description: "This industry includes entities that primarily produce and distribute musical recordings, publish music, or provide sound recording and related services.",
    linkedinId: 1623,
    naics: {
      codes: [
        "5122",
        "51224",
        "512240",
        "51225",
        "512250",
        "51229",
        "512290"
      ],
      slugs: [
        "other-sound-recording-industries",
        "record-production-and-distribution",
        "sound-recording-industries",
        "sound-recording-studios"
      ]
    },
    name: "Sound Recording",
    parents: [
      "media-and-telecommunications",
      "movies-and-sound-recording",
      "movies-videos-and-sound",
      "technology-information-and-media"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "space-research-and-technology": {
    description: "This industry includes government entities that administer and operate space flights, space research, space exploration, and space flight centers.",
    linkedinId: 3089,
    naics: {
      codes: [
        "9271",
        "92711",
        "927110"
      ],
      slugs: ["space-research-and-technology"]
    },
    name: "Space Research and Technology",
    parents: ["government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "specialty-trade-contractors": {
    description: "This industry includes entities that perform specific activities (e.g., pouring concrete, site preparation, plumbing, painting, and electrical work) involved in building construction or similar construction work, but are not responsible for the entire project. This includes new work, additions, alterations, maintenance, and repairs.",
    linkedinId: 435,
    naics: {
      codes: [
        "238",
        "2389",
        "23891",
        "238910",
        "23899",
        "238990"
      ],
      slugs: [
        "all-other-specialty-trade-contractors",
        "other-specialty-trade-contractors",
        "site-preparation-contractors",
        "specialty-trade-contractors"
      ]
    },
    name: "Specialty Trade Contractors",
    parents: ["construction"],
    sic: {
      codes: [
        "1700",
        "3550",
        "3559"
      ],
      slugs: [
        "construction-special-trade-contractors",
        "special-industry-machinery-nec",
        "special-industry-machinery-no-metalworking-machinery"
      ]
    }
  },
  "spectator-sports": {
    description: "This industry includes: Sports teams, clubs, or independent athletes that participate in live sports events before a paying audience; Entities that operate racetracks; Owners of racing participants that enter them in spectator sports events; and Entities such as trainers, that provide specialized services to support participants.",
    linkedinId: 33,
    naics: {
      codes: [
        "7112",
        "71121",
        "711219"
      ],
      slugs: ["other-spectator-sports", "spectator-sports"]
    },
    name: "Spectator Sports",
    parents: ["entertainment-providers", "performing-arts-and-spectator-sports"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "sporting-goods-manufacturing": {
    description: "This industry includes entities that manufacture sporting and athletic goods (except apparel and footwear).",
    linkedinId: 20,
    naics: {
      codes: ["33992", "339920"],
      slugs: ["sporting-and-athletic-goods-manufacturing"]
    },
    name: "Sporting Goods Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: ["3949"],
      slugs: ["sporting-athletic-goods-nec"]
    }
  },
  "sports-and-recreation-instruction": {
    description: "This industry includes entities, such as camps and schools, that offer instruction in athletic activities to groups of individuals.",
    linkedinId: 2027,
    naics: {
      codes: ["61162", "611620"],
      slugs: ["sports-and-recreation-instruction"]
    },
    name: "Sports and Recreation Instruction",
    parents: ["education", "technical-and-vocational-training"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "sports-teams-and-clubs": {
    description: "This industry includes professional or semiprofessional sports teams or clubs participating in live sporting events, such as baseball, basketball, football, hockey, soccer, and jai alai games, before a paying audience.",
    linkedinId: 2142,
    naics: {
      codes: ["711211"],
      slugs: ["sports-teams-and-clubs"]
    },
    name: "Sports Teams and Clubs",
    parents: [
      "entertainment-providers",
      "performing-arts-and-spectator-sports",
      "spectator-sports"
    ],
    sic: {
      codes: ["7997"],
      slugs: ["services-membership-sports-recreation-clubs"]
    }
  },
  "spring-and-wire-product-manufacturing": {
    description: "This industry includes entities that manufacture steel springs by forming, such as cutting, bending, and heat winding, metal rod or strip stock and/or wire springs and fabricated wire products from wire drawn elsewhere (except watch and clock springs, which are in separate industries).",
    linkedinId: 873,
    naics: {
      codes: [
        "3326",
        "33261",
        "332613",
        "332618"
      ],
      slugs: [
        "other-fabricated-wire-product-manufacturing",
        "spring-and-wire-product-manufacturing",
        "spring-manufacturing"
      ]
    },
    name: "Spring and Wire Product Manufacturing",
    parents: ["fabricated-metal-products", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "staffing-and-recruiting": {
    description: "This industry includes entities that: pist employment vacancies and place or refer applicants for employment ; provide executive search, recruitment, and placement services ; supply workers to clients' businesses for limited periods of time; or ; provide day-to-day human resources and human resources management services to client businesses and households. Human resources outsourcing firms are included in this industry. This industry does not include entities that provide consulting on human resources policies and procedures.",
    linkedinId: 104,
    naics: {
      codes: [
        "5613",
        "56131",
        "561311",
        "56133",
        "561330"
      ],
      slugs: [
        "employment-placement-agencies",
        "employment-placement-agencies-and-executive-search-services",
        "employment-services",
        "professional-employer-organizations"
      ]
    },
    name: "Staffing and Recruiting",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: ["7361", "7363"],
      slugs: ["services-employment-agencies", "services-help-supply-services"]
    }
  },
  "steam-and-air-conditioning-supply": {
    description: "This industry includes entities that provide steam, heated air, or cooled air. The steam distribution may be through mains.",
    linkedinId: 404,
    naics: {
      codes: ["22133", "221330"],
      slugs: ["steam-and-air-conditioning-supply"]
    },
    name: "Steam and Air-Conditioning Supply",
    parents: ["utilities", "water-waste-steam-and-air-conditioning-services"],
    sic: {
      codes: ["4961"],
      slugs: ["steam-air-conditioning-supply"]
    }
  },
  "strategic-management-services": {
    description: "This industry includes entities that advise on administrative and management issues, such as financial planning and asset management, records management, office planning, strategic and organizational planning, site selection, new business start-up, and business process improvement. This does not include IT or operations consulting, which are in separate industries.",
    linkedinId: 102,
    naics: {
      codes: ["541611"],
      slugs: ["administrative-management-and-general-management-consulting-services"]
    },
    name: "Strategic Management Services",
    parents: ["business-consulting-and-services", "professional-services"],
    sic: {
      codes: ["8741", "8742"],
      slugs: ["services-management-consulting-services", "services-management-services"]
    }
  },
  "subdivision-of-land": {
    description: "This industry includes entities that service land and subdivide property into lots for subsequent sale to builders. This may include excavation work for the installation of roads and utility lines. Entities that perform only the legal subdivision of land are not included.",
    linkedinId: 428,
    naics: {
      codes: [
        "2372",
        "23721",
        "237210"
      ],
      slugs: ["land-subdivision"]
    },
    name: "Subdivision of Land",
    parents: ["civil-engineering", "construction"],
    sic: {
      codes: ["6552"],
      slugs: ["land-subdividers-developers-no-cemeteries"]
    }
  },
  "sugar-and-confectionery-product-manufacturing": {
    description: "This industry includes entities that process agricultural inputs, such as sugarcane, beet, and cacao, to give rise to a new product (sugar or chocolate) and those that begin with sugar and chocolate and process these further.",
    linkedinId: 495,
    naics: {
      codes: [
        "3113",
        "31131",
        "311313",
        "311314",
        "31134",
        "311340",
        "31135",
        "311351",
        "311352"
      ],
      slugs: [
        "beet-sugar-manufacturing",
        "cane-sugar-manufacturing",
        "chocolate-and-confectionery-manufacturing",
        "chocolate-and-confectionery-manufacturing-from-cacao-beans",
        "confectionery-manufacturing-from-purchased-chocolate",
        "nonchocolate-confectionery-manufacturing",
        "sugar-and-confectionery-product-manufacturing",
        "sugar-manufacturing"
      ]
    },
    name: "Sugar and Confectionery Product Manufacturing",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: ["2060"],
      slugs: ["sugar-confectionery-products"]
    }
  },
  "surveying-and-mapping-services": {
    description: "This industry includes entities that provide surveying and mapping services, such as cartographic, topographic and geophysical surveying and mapping services. Included in this industry are entities involved in locating and measuring subsurface resources, such as oil, gas, and minerals, and conducting surveys for engineering purposes.",
    linkedinId: 3249,
    naics: {
      codes: [
        "54136",
        "541360",
        "54137",
        "541370"
      ],
      slugs: ["geophysical-surveying-and-mapping-services", "surveying-and-mapping-except-geophysical-services"]
    },
    name: "Surveying and Mapping Services",
    parents: ["engineering-services", "professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "taxi-and-limousine-services": {
    description: "This industry includes entities that primarily provide passenger transportation by automobile or van or providing an array of specialty and luxury passenger transportation services via limousine or luxury sedan generally on a reserved basis, and do not operate over regular routes and on regular schedules.",
    linkedinId: 1505,
    naics: {
      codes: [
        "4853",
        "48531",
        "485310",
        "48532",
        "485320"
      ],
      slugs: [
        "limousine-service",
        "taxi-and-limousine-service",
        "taxi-and-ridesharing-services"
      ]
    },
    name: "Taxi and Limousine Services",
    parents: ["ground-passenger-transportation", "transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "technical-and-vocational-training": {
    description: "This industry includes entities that offer vocational and technical training in a variety of technical subjects and trades, including flight schools and apprenticeships but excluding office skills, which is in a separate industry.",
    linkedinId: 2018,
    naics: {
      codes: [
        "6115",
        "61151",
        "611513",
        "611519"
      ],
      slugs: [
        "apprenticeship-training",
        "other-technical-and-trade-schools",
        "technical-and-trade-schools"
      ]
    },
    name: "Technical and Vocational Training",
    parents: ["education"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "technology-information-and-internet": {
    description: "This industry includes entities that publish computer software, provide data infrastructure and analytics, provide web-based platforms (including marketplace platforms and social media platforms, and media streaming services), and provide information services, including internet publishing and libraries.",
    linkedinId: 6,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Technology, Information and Internet",
    parents: ["technology-information-and-media"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "technology-information-and-media": {
    description: "This industry includes entities that produce technology products, such as software and data analytics, and provide the means to transmit or distribute these products. Also included are motion picture and sound recording; traditional broadcasting and broadcasting exclusively over the Internet; telecommunications; data processing; and Web search portals and information services.",
    isParent: true,
    linkedinId: 1594,
    naics: {
      codes: ["51"],
      slugs: ["information"]
    },
    name: "Technology, Information and Media",
    parents: [],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "telecommunications": {
    description: "This industry includes entities that provide telecommunications and related services (e.g., telephony, including Voice over Internet Protocol (VoIP); cable and satellite television distribution services; Internet access; and telecommunications reselling services). They operate and/or provide access to facilities for the transmission of voice, data, text, sound, and video.",
    linkedinId: 8,
    naics: {
      codes: [
        "517",
        "51712",
        "517121",
        "517122",
        "5178",
        "51781",
        "517810"
      ],
      slugs: [
        "agents-for-wireless-telecommunications-services",
        "all-other-telecommunications",
        "telecommunications",
        "telecommunications-resellers",
        "telecommunications-resellers-and-agents-for-wireless-telecommunication-services"
      ]
    },
    name: "Telecommunications",
    parents: ["media-and-telecommunications", "technology-information-and-media"],
    sic: {
      codes: [
        "4812",
        "4813",
        "4822",
        "4899",
        "7385"
      ],
      slugs: [
        "communications-services-nec",
        "radiotelephone-communications",
        "services-telephone-interconnect-systems",
        "telegraph-other-message-communications",
        "telephone-communications-no-radiotelephone"
      ]
    }
  },
  "telecommunications-carriers": {
    description: "This industry includes entities that operate, maintain, and/or provide access to switching and transmission facilities and infrastructure that they own and/or lease for the transmission of voice, data, text, sound, and video using wired and wireless telecommunications networks.",
    linkedinId: 1644,
    naics: {
      codes: [
        "5171",
        "51711",
        "517111"
      ],
      slugs: [
        "wired-and-wireless-telecommunications-carriers-except-satellite",
        "wired-and-wireless-telecommunications-except-satellite",
        "wired-telecommunications-carriers"
      ]
    },
    name: "Telecommunications Carriers",
    parents: [
      "media-and-telecommunications",
      "technology-information-and-media",
      "telecommunications"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "telephone-call-centers": {
    description: "This industry includes entities that: answer telephone calls and relay messages to clients or ; provide telemarketing services on a contract or fee basis for others, such as promoting clients' products or services by telephone; taking orders for clients by telephone; and soliciting contributions or providing information for clients by telephone.",
    linkedinId: 1931,
    naics: {
      codes: [
        "56142",
        "561421",
        "561422"
      ],
      slugs: [
        "telemarketing-bureaus-and-other-contact-centers",
        "telephone-answering-services",
        "telephone-call-centers"
      ]
    },
    name: "Telephone Call Centers",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "temporary-help-services": {
    description: "This industry includes entities that supply workers to clients' businesses for limited periods of time to supplement the working force of the client.",
    linkedinId: 1925,
    naics: {
      codes: ["56132", "561320"],
      slugs: ["temporary-help-services"]
    },
    name: "Temporary Help Services",
    parents: ["administrative-and-support-services", "staffing-and-recruiting"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "textile-manufacturing": {
    description: "This industry includes entities that transform a basic fiber (natural or synthetic) into a product, such as yarn or fabric that is further manufactured into usable items, such as sheets, towels, and textile bags for individual or industrial consumption.",
    linkedinId: 60,
    naics: {
      codes: [
        "313",
        "3131",
        "31311",
        "313110",
        "3132",
        "31321",
        "313210",
        "31322",
        "313220",
        "31323",
        "313230",
        "31324",
        "313240",
        "3133",
        "31331",
        "313310",
        "31332",
        "313320",
        "314"
      ],
      slugs: [
        "broadwoven-fabric-mills",
        "fabric-coating-mills",
        "fabric-mills",
        "fiber-yarn-and-thread-mills",
        "knit-fabric-mills",
        "narrow-fabric-mills-and-schiffli-machine-embroidery",
        "nonwoven-fabric-mills",
        "textile-and-fabric-finishing-and-fabric-coating-mills",
        "textile-and-fabric-finishing-mills",
        "textile-mills",
        "textile-product-mills"
      ]
    },
    name: "Textile Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "2200",
        "2211",
        "2221",
        "2250",
        "2273",
        "2390"
      ],
      slugs: [
        "broadwoven-fabric-mills-cotton",
        "broadwoven-fabric-mills-man-made-fiber-silk",
        "carpets-rugs",
        "knitting-mills",
        "miscellaneous-fabricated-textile-products",
        "textile-mill-products"
      ]
    }
  },
  "theater-companies": {
    description: "This industry includes companies, groups, or theaters that offer the following live theatrical presentations: musicals; operas; plays; and comedy, improvisational, mime, and puppet shows and entities, commonly known as dinner theaters, that produce live theatrical productions along with food and beverages for consumption on the premises.",
    linkedinId: 2133,
    naics: {
      codes: ["71111", "711110"],
      slugs: ["theater-companies-and-dinner-theaters"]
    },
    name: "Theater Companies",
    parents: ["entertainment-providers", "performing-arts-and-spectator-sports"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "think-tanks": {
    description: "This industry includes entities that conduct research and analyses in sociology, psychology, economics, and other fields in social sciences and humanities.",
    linkedinId: 130,
    naics: {
      codes: ["54172", "541720"],
      slugs: ["research-and-development-in-the-social-sciences-and-humanities"]
    },
    name: "Think Tanks",
    parents: ["professional-services", "research-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "tobacco-manufacturing": {
    description: "This industry includes entities that stem and redry tobacco and/or manufacture cigarettes and other tobacco products.",
    linkedinId: 21,
    naics: {
      codes: [
        "3122",
        "31223",
        "312230"
      ],
      slugs: ["tobacco-manufacturing"]
    },
    name: "Tobacco Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: ["2100", "2111"],
      slugs: ["cigarettes", "tobacco-products"]
    }
  },
  "translation-and-localization": {
    description: "This industry includes entities that translate written material and interpret speech from one language to another.",
    linkedinId: 108,
    naics: {
      codes: ["54193", "541930"],
      slugs: ["translation-and-interpretation-services"]
    },
    name: "Translation and Localization",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "transportation-equipment-manufacturing": {
    description: "This industry includes entities that manufacture equipment for transporting people and goods.",
    linkedinId: 1029,
    naics: {
      codes: [
        "336",
        "3362",
        "33621",
        "336211",
        "336212",
        "336213",
        "336214",
        "3369",
        "33699",
        "336991",
        "336992",
        "336999"
      ],
      slugs: [
        "all-other-transportation-equipment-manufacturing",
        "military-armored-vehicle-tank-and-tank-component-manufacturing",
        "motor-home-manufacturing",
        "motor-vehicle-body-and-trailer-manufacturing",
        "motor-vehicle-body-manufacturing",
        "motorcycle-bicycle-and-parts-manufacturing",
        "other-transportation-equipment-manufacturing",
        "transportation-equipment-manufacturing",
        "travel-trailer-and-camper-manufacturing",
        "truck-trailer-manufacturing"
      ]
    },
    name: "Transportation Equipment Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: ["3790"],
      slugs: ["miscellaneous-transportation-equipment"]
    }
  },
  "transportation-logistics-supply-chain-and-storage": {
    description: "This industry includes entities that store and warehouse goods, transport passengers and cargo, provide scenic and sightseeing transportation, and provide support activities related to modes of transportation.",
    isParent: true,
    linkedinId: 116,
    naics: {
      codes: [
        "48-49",
        "488",
        "4881",
        "48811",
        "488111",
        "488119",
        "48819",
        "488190",
        "4882",
        "48821",
        "488210",
        "4883",
        "48831",
        "488310",
        "48832",
        "488320",
        "48833",
        "488330",
        "48839",
        "488390",
        "4884",
        "48841",
        "488410",
        "48849",
        "488490",
        "4885",
        "48851",
        "488510",
        "4889",
        "48899",
        "488991",
        "488999"
      ],
      slugs: [
        "air-traffic-control",
        "airport-operations",
        "all-other-support-activities-for-transportation",
        "freight-transportation-arrangement",
        "marine-cargo-handling",
        "motor-vehicle-towing",
        "navigational-services-to-shipping",
        "other-airport-operations",
        "other-support-activities-for-air-transportation",
        "other-support-activities-for-road-transportation",
        "other-support-activities-for-transportation",
        "other-support-activities-for-water-transportation",
        "packing-and-crating",
        "port-and-harbor-operations",
        "support-activities-for-air-transportation",
        "support-activities-for-rail-transportation",
        "support-activities-for-road-transportation",
        "support-activities-for-transportation",
        "support-activities-for-water-transportation",
        "transportation-and-warehousing"
      ]
    },
    name: "Transportation, Logistics, Supply Chain and Storage",
    parents: [],
    sic: {
      codes: ["4700", "4731"],
      slugs: ["arrangement-of-transportation-of-freight-cargo", "transportation-services"]
    }
  },
  "transportation-programs": {
    description: "This industry includes government entities that focus on the administration, regulation, licensing, planning, inspection, and investigation of transportation services and facilities, including motor vehicle and operator licensing, the Coast Guard (except the Coast Guard Academy, which is in a separate industry), and parking authorities.",
    linkedinId: 3085,
    naics: {
      codes: ["926120"],
      slugs: ["regulation-and-administration-of-transportation-programs"]
    },
    name: "Transportation Programs",
    parents: ["economic-programs", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "travel-arrangements": {
    description: "This industry includes entities that provide travel agency services, arrange or assemble tours, or provide other travel arrangement and reservation services.",
    linkedinId: 30,
    naics: {
      codes: [
        "5615",
        "56151",
        "561510",
        "56152",
        "561520",
        "56159",
        "561591",
        "561599"
      ],
      slugs: [
        "all-other-travel-arrangement-and-reservation-services",
        "convention-and-visitors-bureaus",
        "other-travel-arrangement-and-reservation-services",
        "tour-operators",
        "travel-agencies",
        "travel-arrangement-and-reservation-services"
      ]
    },
    name: "Travel Arrangements",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "truck-transportation": {
    description: "This industry includes entities that provide over-the-road transportation of cargo using motor vehicles, such as trucks and tractor trailers. This includes general freight trucking (such as palletized commodities transported in a container or van trailer) and specialized freight trucking (such as, transportation of cargo that, because of size, weight, or shape, requires specialized transportation equipment).",
    linkedinId: 92,
    naics: {
      codes: [
        "484",
        "4841",
        "48411",
        "484110",
        "48412",
        "484121",
        "484122",
        "4842",
        "48421",
        "484210",
        "48422",
        "484220",
        "48423",
        "484230"
      ],
      slugs: [
        "general-freight-trucking",
        "general-freight-trucking-local",
        "general-freight-trucking-long-distance",
        "general-freight-trucking-long-distance-less-than-truckload",
        "general-freight-trucking-long-distance-truckload",
        "specialized-freight-except-used-goods-trucking-local",
        "specialized-freight-except-used-goods-trucking-long-distance",
        "specialized-freight-trucking",
        "truck-transportation",
        "used-household-and-office-goods-moving"
      ]
    },
    name: "Truck Transportation",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: [
        "4210",
        "4213",
        "4231"
      ],
      slugs: [
        "terminal-maintenance-facilities-for-motor-freight-transport",
        "trucking-courier-services-no-air",
        "trucking-no-local"
      ]
    }
  },
  "trusts-and-estates": {
    description: "This industry includes legal entities (such as, investment pools and/or funds) organized to pool securities or other assets (except insurance and employee benefit funds, which are in separate industries) on behalf of shareholders, unitholders, or beneficiaries.",
    linkedinId: 1750,
    naics: {
      codes: [
        "5259",
        "52591",
        "525910",
        "52592",
        "525920",
        "52599",
        "525990"
      ],
      slugs: [
        "open-end-investment-funds",
        "other-financial-vehicles",
        "other-investment-pools-and-funds",
        "trusts-estates-and-agency-accounts"
      ]
    },
    name: "Trusts and Estates",
    parents: ["financial-services", "funds-and-trusts"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "turned-products-and-fastener-manufacturing": {
    description: "This industry includes entities involved in one of the following: operating machine shops for machining metal and plastic parts and parts of other composite materials on a job or order basis; machining precision turned products; or manufacturing metal bolts, nuts, screws, rivets, and other industrial fasteners.",
    linkedinId: 876,
    naics: {
      codes: [
        "3327",
        "33271",
        "332710",
        "33272",
        "332721",
        "332722"
      ],
      slugs: [
        "bolt-nut-screw-rivet-and-washer-manufacturing",
        "machine-shops",
        "machine-shops-turned-product-and-screw-nut-and-bolt-manufacturing",
        "precision-turned-product-manufacturing",
        "turned-product-and-screw-nut-and-bolt-manufacturing"
      ]
    },
    name: "Turned Products and Fastener Manufacturing",
    parents: ["fabricated-metal-products", "manufacturing"],
    sic: {
      codes: ["3451", "3452"],
      slugs: ["bolts-nuts-screws-rivets-washers", "screw-machine-products"]
    }
  },
  "urban-transit-services": {
    description: "This industry includes entities that primarily operate local and suburban passenger transit systems over regular routes and on regular schedules within a metropolitan area.",
    linkedinId: 1497,
    naics: {
      codes: [
        "4851",
        "48511",
        "485111",
        "485112",
        "485113",
        "485119"
      ],
      slugs: [
        "bus-and-other-motor-vehicle-transit-systems",
        "commuter-rail-systems",
        "mixed-mode-transit-systems",
        "other-urban-transit-systems",
        "urban-transit-systems"
      ]
    },
    name: "Urban Transit Services",
    parents: ["ground-passenger-transportation", "transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: ["4100"],
      slugs: ["local-suburban-transit-interurban-hwy-passenger-trans"]
    }
  },
  "utilities": {
    description: "This industry includes entities that provide electric power, natural gas, steam supply, water supply, and sewage removal. Electric power includes generation, transmission, and distribution; natural gas includes distribution; steam supply includes provision and/or distribution; water supply includes treatment and distribution; and sewage removal includes collection, treatment, and disposal through sewer systems and treatment facilities.",
    isParent: true,
    linkedinId: 59,
    naics: {
      codes: [
        "22",
        "221",
        "2211"
      ],
      slugs: ["electric-power-generation-transmission-and-distribution", "utilities"]
    },
    name: "Utilities",
    parents: [],
    sic: {
      codes: [
        "4900",
        "4911",
        "4931",
        "4932"
      ],
      slugs: [
        "electric-gas-sanitary-services",
        "electric-other-services-combined",
        "electric-services",
        "gas-other-services-combined"
      ]
    }
  },
  "utilities-administration": {
    description: "This industry includes government entities that focus on administration, regulation, licensing, and inspection of utilities, such as communications, electric power (including fossil, nuclear, solar, water, and wind), gas and water supply, and sewerage.",
    linkedinId: 3086,
    naics: {
      codes: ["926130"],
      slugs: ["regulation-and-administration-of-communications-electric-gas-and-other-utilities"]
    },
    name: "Utilities Administration",
    parents: ["economic-programs", "government-administration"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "utility-system-construction": {
    description: "This industry includes entities that construct distribution lines and related buildings and structures for utilities (such as, water, sewer, petroleum, gas, power, and communication). All structures (including buildings) that are integral parts of utility systems (e.g., storage tanks, pumping stations, power plants, and refineries) are included.",
    linkedinId: 419,
    naics: {
      codes: [
        "2371",
        "23711",
        "237110",
        "23712",
        "237120",
        "23713",
        "237130"
      ],
      slugs: [
        "oil-and-gas-pipeline-and-related-structures-construction",
        "power-and-communication-line-and-related-structures-construction",
        "utility-system-construction",
        "water-and-sewer-line-and-related-structures-construction"
      ]
    },
    name: "Utility System Construction",
    parents: ["civil-engineering", "construction"],
    sic: {
      codes: ["1623"],
      slugs: ["water-sewer-pipeline-comm-power-line-construction"]
    }
  },
  "vehicle-repair-and-maintenance": {
    description: "This industry includes entities that provide repair and maintenance services for automotive vehicles, such as passenger cars, trucks, and vans, and all trailers.",
    linkedinId: 2226,
    naics: {
      codes: [
        "8111",
        "81111",
        "811111",
        "811114",
        "81112",
        "811121",
        "811122",
        "81119",
        "811191",
        "811192",
        "811198"
      ],
      slugs: [
        "all-other-automotive-repair-and-maintenance",
        "automotive-body-paint-and-interior-repair-and-maintenance",
        "automotive-body-paint-interior-and-glass-repair",
        "automotive-glass-replacement-shops",
        "automotive-mechanical-and-electrical-repair-and-maintenance",
        "automotive-oil-change-and-lubrication-shops",
        "automotive-repair-and-maintenance",
        "car-washes",
        "general-automotive-repair",
        "other-automotive-repair-and-maintenance",
        "specialized-automotive-repair"
      ]
    },
    name: "Vehicle Repair and Maintenance",
    parents: ["consumer-services", "repair-and-maintenance"],
    sic: {
      codes: ["7500", "7510"],
      slugs: ["services-auto-rental-leasing-no-drivers", "services-automotive-repair-services-parking"]
    }
  },
  "venture-capital-and-private-equity-principals": {
    description: "This industry includes entities that act as principals (except investment bankers, securities dealers, and commodity contracts dealers, which are in separate industries) in buying or selling financial contracts generally on a spread basis.",
    linkedinId: 106,
    naics: {
      codes: ["52391", "523910"],
      slugs: ["miscellaneous-intermediation"]
    },
    name: "Venture Capital and Private Equity Principals",
    parents: ["capital-markets", "financial-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "veterinary-services": {
    description: "This industry includes entities of licensed veterinary practitioners that practice veterinary medicine, dentistry, or surgery for animals. Entities that provide testing services for licensed veterinary practitioners are included.",
    linkedinId: 16,
    naics: {
      codes: ["54194", "541940"],
      slugs: ["veterinary-services"]
    },
    name: "Veterinary Services",
    parents: ["professional-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "vocational-rehabilitation-services": {
    description: "This industry includes entities that provide vocational rehabilitation or habilitation services, such as job counseling, job training, and work experience, to unemployed and underemployed persons, persons with disabilities, and persons who have a job market disadvantage because of lack of education, job skill, or experience and/or training and employment to persons with disabilities.",
    linkedinId: 2125,
    naics: {
      codes: [
        "6243",
        "62431",
        "624310"
      ],
      slugs: ["vocational-rehabilitation-services"]
    },
    name: "Vocational Rehabilitation Services",
    parents: ["hospitals-and-health-care", "individual-and-family-services"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "warehousing-and-storage": {
    description: "This industry includes entities that operate warehouse and storage facilities for general merchandise, refrigerated goods, and other warehouse products. They take responsibility for keeping goods secure and do not sell the goods they handle but may provide services related to the distribution of goods, such as labeling, inventory control, and light assembly.",
    linkedinId: 93,
    naics: {
      codes: [
        "493",
        "4931",
        "49311",
        "493110",
        "49312",
        "493120",
        "49313",
        "493130",
        "49319",
        "493190"
      ],
      slugs: [
        "farm-product-warehousing-and-storage",
        "general-warehousing-and-storage",
        "other-warehousing-and-storage",
        "refrigerated-warehousing-and-storage",
        "warehousing-and-storage"
      ]
    },
    name: "Warehousing and Storage",
    parents: ["transportation-logistics-supply-chain-and-storage"],
    sic: {
      codes: ["4220"],
      slugs: ["public-warehousing-storage"]
    }
  },
  "waste-collection": {
    description: "This industry includes entities that collect and/or haul hazardous waste, nonhazardous waste, and/or recyclable materials within a local area and/or operate hazardous or nonhazardous waste transfer stations and may be responsible for the identification, treatment, packaging, and labeling of waste for the purposes of transport.",
    linkedinId: 1981,
    naics: {
      codes: [
        "5621",
        "56211",
        "562111",
        "562112",
        "562119"
      ],
      slugs: [
        "hazardous-waste-collection",
        "other-waste-collection",
        "solid-waste-collection",
        "waste-collection"
      ]
    },
    name: "Waste Collection",
    parents: ["utilities", "water-waste-steam-and-air-conditioning-services"],
    sic: {
      codes: ["4953"],
      slugs: ["refuse-systems"]
    }
  },
  "waste-treatment-and-disposal": {
    description: "This industry includes entities that operate waste treatment or disposal facilities (except sewer systems or sewage treatment facilities, which are in a separate industry) or offer the combined activity of collecting and/or hauling of waste materials within a local area and operating waste treatment or disposal facilities. Waste combustors or incinerators, solid waste landfills, and compost dumps are included in this industry.",
    linkedinId: 1986,
    naics: {
      codes: [
        "5622",
        "56221",
        "562211",
        "562212",
        "562213",
        "562219"
      ],
      slugs: [
        "hazardous-waste-treatment-and-disposal",
        "other-nonhazardous-waste-treatment-and-disposal",
        "solid-waste-combustors-and-incinerators",
        "solid-waste-landfill",
        "waste-treatment-and-disposal"
      ]
    },
    name: "Waste Treatment and Disposal",
    parents: ["utilities", "water-waste-steam-and-air-conditioning-services"],
    sic: {
      codes: ["4950", "4955"],
      slugs: ["hazardous-waste-management", "sanitary-services"]
    }
  },
  "water-supply-and-irrigation-systems": {
    description: "This industry includes entities that operate treatment plants and/or water supply systems. This may include pumping stations, aqueducts, and/or distribution mains, for water used for drinking, irrigation, or other uses.",
    linkedinId: 400,
    naics: {
      codes: ["22131", "221310"],
      slugs: ["water-supply-and-irrigation-systems"]
    },
    name: "Water Supply and Irrigation Systems",
    parents: ["utilities", "water-waste-steam-and-air-conditioning-services"],
    sic: {
      codes: ["4941"],
      slugs: ["water-supply"]
    }
  },
  "water-waste-steam-and-air-conditioning-services": {
    description: "This industry includes entities that: operate water treatment plants and/or water supply systems; operate sewer systems or sewage treatment facilities; or provide steam, heated air, or cooled air.",
    linkedinId: 398,
    naics: {
      codes: [
        "2213",
        "22132",
        "221320"
      ],
      slugs: ["sewage-treatment-facilities", "water-sewage-and-other-systems"]
    },
    name: "Water, Waste, Steam, and Air Conditioning Services",
    parents: ["utilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wellness-and-fitness-services": {
    description: "This industry includes entities that: Operate fitness and recreational facilities for sports, such as swimming, skating, or racquet sports; and/or provide non-medical services to assist clients in attaining or maintaining a desired weight or healthfulness, such as individual or group counseling and menu and exercise planning.",
    linkedinId: 124,
    naics: {
      codes: ["71394", "713940"],
      slugs: ["fitness-and-recreational-sports-centers"]
    },
    name: "Wellness and Fitness Services",
    parents: ["entertainment-providers", "recreational-facilities"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wholesale": {
    description: "This industry includes entities that distribute wholesale merchandise, generally without transformation, and render services incidental to the sale of merchandise. Merchandise includes the outputs of agriculture, mining, manufacturing, and certain information industries, such as publishing.",
    isParent: true,
    linkedinId: 133,
    naics: {
      codes: [
        "42",
        "423",
        "423420",
        "423440",
        "423450",
        "423460",
        "42349",
        "423490",
        "423840",
        "423850",
        "423860",
        "42391",
        "423910",
        "42392",
        "423920",
        "42399",
        "423990",
        "424",
        "42492",
        "424920",
        "42493",
        "424930",
        "42494",
        "424940",
        "42499",
        "424990"
      ],
      slugs: [
        "book-periodical-and-newspaper-merchant-wholesalers",
        "flower-nursery-stock-and-florists-supplies-merchant-wholesalers",
        "industrial-supplies-merchant-wholesalers",
        "medical-dental-and-hospital-equipment-and-supplies-merchant-wholesalers",
        "merchant-wholesalers-durable-goods",
        "merchant-wholesalers-nondurable-goods",
        "office-equipment-merchant-wholesalers",
        "ophthalmic-goods-merchant-wholesalers",
        "other-commercial-equipment-merchant-wholesalers",
        "other-miscellaneous-durable-goods-merchant-wholesalers",
        "other-miscellaneous-nondurable-goods-merchant-wholesalers",
        "other-professional-equipment-and-supplies-merchant-wholesalers",
        "service-establishment-equipment-and-supplies-merchant-wholesalers",
        "sporting-and-recreational-goods-and-supplies-merchant-wholesalers",
        "tobacco-product-and-electronic-cigarette-merchant-wholesalers",
        "toy-and-hobby-goods-and-supplies-merchant-wholesalers",
        "transportation-equipment-and-supplies-except-motor-vehicle-merchant-wholesalers",
        "wholesale-trade"
      ]
    },
    name: "Wholesale",
    parents: [],
    sic: {
      codes: [
        "5000",
        "5040",
        "5090",
        "5099",
        "5190"
      ],
      slugs: [
        "wholesale-durable-goods",
        "wholesale-durable-goods-nec",
        "wholesale-misc-durable-goods",
        "wholesale-miscellaneous-nondurable-goods",
        "wholesale-professional-commercial-equipment-supplies"
      ]
    }
  },
  "wholesale-alcoholic-beverages": {
    description: "This industry includes entities that distribute wholesale beer, ale, wine, and/or distilled alcoholic beverages.",
    linkedinId: 1267,
    naics: {
      codes: [
        "4248",
        "42481",
        "424810",
        "42482",
        "424820"
      ],
      slugs: [
        "beer-and-ale-merchant-wholesalers",
        "beer-wine-and-distilled-alcoholic-beverage-merchant-wholesalers",
        "wine-and-distilled-alcoholic-beverage-merchant-wholesalers"
      ]
    },
    name: "Wholesale Alcoholic Beverages",
    parents: ["wholesale"],
    sic: {
      codes: ["5180"],
      slugs: ["wholesale-beer-wine-distilled-alcoholic-beverages"]
    }
  },
  "wholesale-apparel-and-sewing-supplies": {
    description: "This industry includes entities that distribute wholesale piece goods, notions, and other dry goods; men's and boys' clothing and furnishings; women's, children's, and infants' clothing and accessories; and footwear.",
    linkedinId: 1222,
    naics: {
      codes: [
        "4243",
        "42431",
        "424310",
        "42435",
        "424350"
      ],
      slugs: [
        "apparel-piece-goods-and-notions-merchant-wholesalers",
        "clothing-and-clothing-accessories-merchant-wholesalers",
        "piece-goods-notions-and-other-dry-goods-merchant-wholesalers"
      ]
    },
    name: "Wholesale Apparel and Sewing Supplies",
    parents: ["wholesale"],
    sic: {
      codes: ["5130"],
      slugs: ["wholesale-apparel-piece-goods-notions"]
    }
  },
  "wholesale-appliances-electrical-and-electronics": {
    description: "This industry includes entities that distribute wholesale electrical apparatus and equipment, wiring supplies, and related equipment; household appliances, electric housewares, and consumer electronics; and other electronic parts and equipment.",
    linkedinId: 1171,
    naics: {
      codes: [
        "4236",
        "42361",
        "423610",
        "42362",
        "423620",
        "42369",
        "423690"
      ],
      slugs: [
        "electrical-apparatus-and-equipment-wiring-supplies-and-related-equipment-merchant-wholesalers",
        "household-appliances-and-electrical-and-electronic-goods-merchant-wholesalers",
        "household-appliances-electric-housewares-and-consumer-electronics-merchant-wholesalers",
        "other-electronic-parts-and-equipment-merchant-wholesalers"
      ]
    },
    name: "Wholesale Appliances, Electrical, and Electronics",
    parents: ["wholesale"],
    sic: {
      codes: [
        "5063",
        "5064",
        "5065"
      ],
      slugs: [
        "wholesale-electrical-apparatus-equipment-wiring-supplies",
        "wholesale-electrical-appliances-tv-radio-sets",
        "wholesale-electronic-parts-equipment-nec"
      ]
    }
  },
  "wholesale-building-materials": {
    description: "This industry includes entities that distribute wholesale building materials, such as lumber, plywood, millwork, and wood panels; brick, stone, and related construction materials; roofing, siding, and insulation materials; and other construction materials, including manufactured homes (such as, mobile homes) and/or prefabricated buildings.",
    linkedinId: 49,
    naics: {
      codes: [
        "4233",
        "42331",
        "423310",
        "42332",
        "423320",
        "42333",
        "423330",
        "42339",
        "423390"
      ],
      slugs: [
        "brick-stone-and-related-construction-material-merchant-wholesalers",
        "lumber-and-other-construction-materials-merchant-wholesalers",
        "lumber-plywood-millwork-and-wood-panel-merchant-wholesalers",
        "other-construction-material-merchant-wholesalers",
        "roofing-siding-and-insulation-material-merchant-wholesalers"
      ]
    },
    name: "Wholesale Building Materials",
    parents: ["wholesale"],
    sic: {
      codes: ["5030", "5031"],
      slugs: ["wholesale-lumber-other-construction-materials", "wholesale-lumber-plywood-millwork-wood-panels"]
    }
  },
  "wholesale-chemical-and-allied-products": {
    description: "This industry includes entities that distribute wholesale chemicals, plastics materials and basic forms and shapes, and allied products.",
    linkedinId: 1257,
    naics: {
      codes: [
        "4246",
        "42461",
        "424610",
        "42469",
        "424690",
        "42495",
        "424950"
      ],
      slugs: [
        "chemical-and-allied-products-merchant-wholesalers",
        "other-chemical-and-allied-products-merchant-wholesalers",
        "paint-varnish-and-supplies-merchant-wholesalers",
        "plastics-materials-and-basic-forms-and-shapes-merchant-wholesalers"
      ]
    },
    name: "Wholesale Chemical and Allied Products",
    parents: ["wholesale"],
    sic: {
      codes: ["2851", "5160"],
      slugs: ["paints-varnishes-lacquers-enamels-allied-products", "wholesale-chemicals-allied-products"]
    }
  },
  "wholesale-computer-equipment": {
    description: "This industry includes entities that distribute wholesale computers and computer equipment.",
    linkedinId: 1157,
    naics: {
      codes: [
        "42342",
        "42343",
        "423430"
      ],
      slugs: ["computer-and-computer-peripheral-equipment-and-software-merchant-wholesalers", "office-equipment-merchant-wholesalers"]
    },
    name: "Wholesale Computer Equipment",
    parents: ["wholesale"],
    sic: {
      codes: ["5045"],
      slugs: ["wholesale-computers-peripheral-equipment-software"]
    }
  },
  "wholesale-drugs-and-sundries": {
    description: "This industry includes entities that distribute wholesale biological and medical products; botanical drugs and herbs; and pharmaceutical products intended for internal and/or external consumption in such forms as ampoules, tablets, capsules, vials, ointments, powders, solutions, and suspensions.",
    linkedinId: 1221,
    naics: {
      codes: [
        "42345",
        "42346",
        "4242",
        "42421",
        "424210"
      ],
      slugs: [
        "drugs-and-druggists-sundries-merchant-wholesalers",
        "medical-dental-and-hospital-equipment-and-supplies-merchant-wholesalers",
        "ophthalmic-goods-merchant-wholesalers"
      ]
    },
    name: "Wholesale Drugs and Sundries",
    parents: ["wholesale"],
    sic: {
      codes: ["5122"],
      slugs: ["wholesale-drugs-proprietaries-druggists-sundries"]
    }
  },
  "wholesale-food-and-beverage": {
    description: "This industry includes entities that distribute wholesale: general line of groceries; packaged frozen food; dairy products; poultry and poultry products; confectioneries; fish and seafood; meats and meat products; fresh fruits and vegetables; and other grocery and related products.",
    linkedinId: 1231,
    naics: {
      codes: [
        "4244",
        "42441",
        "424410",
        "42442",
        "424420",
        "42443",
        "424430",
        "42444",
        "424440",
        "42445",
        "424450",
        "42446",
        "424460",
        "42447",
        "424470",
        "42448",
        "424480",
        "42449",
        "424490"
      ],
      slugs: [
        "confectionery-merchant-wholesalers",
        "dairy-product-except-dried-or-canned-merchant-wholesalers",
        "fish-and-seafood-merchant-wholesalers",
        "fresh-fruit-and-vegetable-merchant-wholesalers",
        "general-line-grocery-merchant-wholesalers",
        "grocery-and-related-product-merchant-wholesalers",
        "meat-and-meat-product-merchant-wholesalers",
        "other-grocery-and-related-products-merchant-wholesalers",
        "packaged-frozen-food-merchant-wholesalers",
        "poultry-and-poultry-product-merchant-wholesalers"
      ]
    },
    name: "Wholesale Food and Beverage",
    parents: ["wholesale"],
    sic: {
      codes: ["5140", "5141"],
      slugs: ["wholesale-groceries-general-line", "wholesale-groceries-related-products"]
    }
  },
  "wholesale-footwear": {
    description: "This industry includes entities that distribute wholesale footwear (including athletic footwear) leather, rubber, and other materials.",
    linkedinId: 1230,
    naics: {
      codes: ["42434", "424340"],
      slugs: ["footwear-merchant-wholesalers"]
    },
    name: "Wholesale Footwear",
    parents: ["wholesale"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wholesale-furniture-and-home-furnishings": {
    description: "This industry includes entities that distribute wholesale furniture (except hospital beds, medical furniture, and drafting tables), home furnishings, and/or housewares.",
    linkedinId: 1137,
    naics: {
      codes: [
        "4232",
        "42321",
        "423210",
        "42322",
        "423220"
      ],
      slugs: [
        "furniture-and-home-furnishing-merchant-wholesalers",
        "furniture-merchant-wholesalers",
        "home-furnishing-merchant-wholesalers"
      ]
    },
    name: "Wholesale Furniture and Home Furnishings",
    parents: ["wholesale"],
    sic: {
      codes: ["5020"],
      slugs: ["wholesale-furniture-home-furnishings"]
    }
  },
  "wholesale-hardware-plumbing-heating-equipment": {
    description: "This industry includes entities that distribute wholesale hardware; plumbing and heating equipment and supplies (hydronics); warm air heating and air-conditioning equipment and supplies; and refrigeration equipment and supplies.",
    linkedinId: 1178,
    naics: {
      codes: [
        "4237",
        "42371",
        "423710",
        "42372",
        "423720",
        "42373",
        "423730",
        "42374",
        "423740"
      ],
      slugs: [
        "hardware-and-plumbing-and-heating-equipment-and-supplies-merchant-wholesalers",
        "hardware-merchant-wholesalers",
        "plumbing-and-heating-equipment-and-supplies-hydronics-merchant-wholesalers",
        "refrigeration-equipment-and-supplies-merchant-wholesalers",
        "warm-air-heating-and-air-conditioning-equipment-and-supplies-merchant-wholesalers"
      ]
    },
    name: "Wholesale Hardware, Plumbing, Heating Equipment",
    parents: ["wholesale"],
    sic: {
      codes: ["5070", "5072"],
      slugs: ["wholesale-hardware", "wholesale-hardware-plumbing-heating-equipment-supplies"]
    }
  },
  "wholesale-import-and-export": {
    description: "This industry includes entities that facilitate trade of goods and commodities between domestic and foreign companies. Entities may buy/sell goods on their own account (import/export merchants) or on a commission basis (import/export agents and brokers). Subject to trade agreements between jurisdictions, they may handle tariffs and customs declarations and procedures.",
    linkedinId: 134,
    naics: {
      codes: [],
      slugs: []
    },
    name: "Wholesale Import and Export",
    parents: ["wholesale"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wholesale-luxury-goods-and-jewelry": {
    description: "This industry includes entities that distribute wholesale jewelry, precious and semiprecious stones, precious metals and metal flatware, costume jewelry, watches, clocks, silverware, and/or jewelers' findings.",
    linkedinId: 1208,
    naics: {
      codes: ["42394", "423940"],
      slugs: ["jewelry-watch-precious-stone-and-precious-metal-merchant-wholesalers"]
    },
    name: "Wholesale Luxury Goods and Jewelry",
    parents: ["wholesale"],
    sic: {
      codes: ["5094"],
      slugs: ["wholesale-jewelry-watches-precious-stones-metals"]
    }
  },
  "wholesale-machinery": {
    description: "This industry includes entities that distribute wholesale construction, mining, farm, garden, industrial, service establishment, and transportation machinery, equipment, and supplies.",
    linkedinId: 1187,
    naics: {
      codes: [
        "42344",
        "4238",
        "42381",
        "423810",
        "42382",
        "423820",
        "42383",
        "423830",
        "42384",
        "42385",
        "42386"
      ],
      slugs: [
        "construction-and-mining-except-oil-well-machinery-and-equipment-merchant-wholesalers",
        "farm-and-garden-machinery-and-equipment-merchant-wholesalers",
        "industrial-machinery-and-equipment-merchant-wholesalers",
        "industrial-supplies-merchant-wholesalers",
        "machinery-equipment-and-supplies-merchant-wholesalers",
        "other-commercial-equipment-merchant-wholesalers",
        "service-establishment-equipment-and-supplies-merchant-wholesalers",
        "transportation-equipment-and-supplies-except-motor-vehicle-merchant-wholesalers"
      ]
    },
    name: "Wholesale Machinery",
    parents: ["wholesale"],
    sic: {
      codes: [
        "5080",
        "5082",
        "5084"
      ],
      slugs: [
        "wholesale-construction-mining-no-petro-machinery-equip",
        "wholesale-industrial-machinery-equipment",
        "wholesale-machinery-equipment-supplies"
      ]
    }
  },
  "wholesale-metals-and-minerals": {
    description: "This industry includes entities that engage in the merchant wholesale distribution of products of the primary metals industries (including metal service centers) and coal, coke, metal ores, and/or nonmetallic minerals (except precious and semiprecious stones and minerals used in construction, such as sand and gravel).",
    linkedinId: 1166,
    naics: {
      codes: [
        "4235",
        "42351",
        "423510",
        "42352",
        "423520"
      ],
      slugs: [
        "coal-and-other-mineral-and-ore-merchant-wholesalers",
        "metal-and-mineral-except-petroleum-merchant-wholesalers",
        "metal-service-centers-and-other-metal-merchant-wholesalers"
      ]
    },
    name: "Wholesale Metals and Minerals",
    parents: ["wholesale"],
    sic: {
      codes: ["5050", "5051"],
      slugs: ["wholesale-metals-minerals-no-petroleum", "wholesale-metals-service-centers-offices"]
    }
  },
  "wholesale-motor-vehicles-and-parts": {
    description: "This industry includes entities that distribute wholesale automobiles and other motor vehicles, motor vehicle supplies, tires, and new or used parts.",
    linkedinId: 1128,
    naics: {
      codes: [
        "4231",
        "42311",
        "423110",
        "42312",
        "423120",
        "42313",
        "423130",
        "42314",
        "423140"
      ],
      slugs: [
        "automobile-and-other-motor-vehicle-merchant-wholesalers",
        "motor-vehicle-and-motor-vehicle-parts-and-supplies-merchant-wholesalers",
        "motor-vehicle-parts-used-merchant-wholesalers",
        "motor-vehicle-supplies-and-new-parts-merchant-wholesalers",
        "tire-and-tube-merchant-wholesalers"
      ]
    },
    name: "Wholesale Motor Vehicles and Parts",
    parents: ["wholesale"],
    sic: {
      codes: ["5010", "5013"],
      slugs: ["wholesale-motor-vehicle-supplies-new-parts", "wholesale-motor-vehicles-motor-vehicle-parts-supplies"]
    }
  },
  "wholesale-paper-products": {
    description: "This industry includes entities that distribute wholesale bulk printing and writing paper; stationery and office supplies; and industrial and personal service paper.",
    linkedinId: 1212,
    naics: {
      codes: [
        "4241",
        "42411",
        "424110",
        "42412",
        "424120",
        "42413",
        "424130"
      ],
      slugs: [
        "industrial-and-personal-service-paper-merchant-wholesalers",
        "paper-and-paper-product-merchant-wholesalers",
        "printing-and-writing-paper-merchant-wholesalers",
        "stationery-and-office-supplies-merchant-wholesalers"
      ]
    },
    name: "Wholesale Paper Products",
    parents: ["wholesale"],
    sic: {
      codes: ["5110"],
      slugs: ["wholesale-paper-paper-products"]
    }
  },
  "wholesale-petroleum-and-petroleum-products": {
    description: "This industry includes entities that distribute wholesale petroleum and petroleum products, including liquefied petroleum gas.",
    linkedinId: 1262,
    naics: {
      codes: [
        "4247",
        "42471",
        "424710",
        "42472",
        "424720"
      ],
      slugs: [
        "petroleum-and-petroleum-products-merchant-wholesalers",
        "petroleum-and-petroleum-products-merchant-wholesalers-except-bulk-stations-and-terminals",
        "petroleum-bulk-stations-and-terminals"
      ]
    },
    name: "Wholesale Petroleum and Petroleum Products",
    parents: ["wholesale"],
    sic: {
      codes: ["5171", "5172"],
      slugs: ["wholesale-petroleum-bulk-stations-terminals", "wholesale-petroleum-petroleum-products-no-bulk-stations"]
    }
  },
  "wholesale-photography-equipment-and-supplies": {
    description: "This industry includes entities that distribute wholesale photographic equipment and supplies (except office equipment).",
    linkedinId: 1153,
    naics: {
      codes: ["42341", "423410"],
      slugs: ["photographic-equipment-and-supplies-merchant-wholesalers"]
    },
    name: "Wholesale Photography Equipment and Supplies",
    parents: ["wholesale"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wholesale-raw-farm-products": {
    description: "This industry includes entities that distribute wholesale agricultural products (except raw milk, live poultry, and fresh fruits and vegetables, which are in separate industries), such as grains, field beans, livestock, and other farm product raw materials (excluding seeds, which is in a separate industry).",
    linkedinId: 1250,
    naics: {
      codes: [
        "4245",
        "42451",
        "424510",
        "42452",
        "424520",
        "42459",
        "424590",
        "42491",
        "424910"
      ],
      slugs: [
        "farm-product-raw-material-merchant-wholesalers",
        "farm-supplies-merchant-wholesalers",
        "grain-and-field-bean-merchant-wholesalers",
        "livestock-merchant-wholesalers",
        "other-farm-product-raw-material-merchant-wholesalers"
      ]
    },
    name: "Wholesale Raw Farm Products",
    parents: ["wholesale"],
    sic: {
      codes: ["2040", "5150"],
      slugs: ["grain-mill-products", "wholesale-farm-product-raw-materials"]
    }
  },
  "wholesale-recyclable-materials": {
    description: "This industry includes entities that distribute wholesale automotive scrap, industrial scrap, and other recyclable materials. Included in this industry are auto wreckers that dismantle motor vehicles for the purpose of wholesaling scrap.",
    linkedinId: 1206,
    naics: {
      codes: ["42393", "423930"],
      slugs: ["recyclable-material-merchant-wholesalers"]
    },
    name: "Wholesale Recyclable Materials",
    parents: ["wholesale"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wind-electric-power-generation": {
    description: "This industry includes entities that operate wind electric power generation facilities to drive turbines and produce electric energy for electric power transmission systems or for electric power distribution systems.",
    linkedinId: 2489,
    naics: {
      codes: ["221115"],
      slugs: ["wind-electric-power-generation"]
    },
    name: "Wind Electric Power Generation",
    parents: [
      "electric-power-generation",
      "renewable-energy-power-generation",
      "utilities"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wineries": {
    description: "This industry includes entities that focus on one or more of the following: growing grapes and manufacturing wines and brandies; manufacturing wines and brandies from grapes and other fruits grown elsewhere; and blending wines and brandies.",
    linkedinId: 2500,
    naics: {
      codes: ["31213", "312130"],
      slugs: ["wineries"]
    },
    name: "Wineries",
    parents: ["food-and-beverage-manufacturing", "manufacturing"],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wireless-services": {
    description: "This industry includes entities that operate and maintain switching and transmission facilities to provide communications via the airwaves, such as cellular phone services, paging services, wireless Internet access, and wireless video services.",
    linkedinId: 119,
    naics: {
      codes: ["517112"],
      slugs: ["wireless-telecommunications-carriers-except-satellite"]
    },
    name: "Wireless Services",
    parents: [
      "media-and-telecommunications",
      "technology-information-and-media",
      "telecommunications"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "women-s-handbag-manufacturing": {
    description: "This industry includes entities that manufacture women's handbags and purses of any material (except precious metal, which is in a separate industry).",
    linkedinId: 625,
    naics: {
      codes: ["316990"],
      slugs: ["other-leather-and-allied-product-manufacturing"]
    },
    name: "Women's Handbag Manufacturing",
    parents: [
      "leather-product-manufacturing",
      "manufacturing",
      "women-s-handbag-manufacturing"
    ],
    sic: {
      codes: [],
      slugs: []
    }
  },
  "wood-product-manufacturing": {
    description: "This industry includes entities that manufacture wood products, such as lumber, plywood, veneers, wood containers, wood flooring, wood trusses, manufactured homes (such as, mobile homes), and prefabricated wood buildings.",
    linkedinId: 784,
    naics: {
      codes: [
        "321",
        "3211",
        "32111",
        "321113",
        "321114",
        "3212",
        "32121",
        "321211",
        "321212",
        "321215",
        "321219",
        "3219",
        "32191",
        "321911",
        "321912",
        "321918",
        "32192",
        "321920",
        "32199",
        "321991",
        "321992",
        "321999"
      ],
      slugs: [
        "all-other-miscellaneous-wood-product-manufacturing",
        "all-other-wood-product-manufacturing",
        "cut-stock-resawing-lumber-and-planing",
        "engineered-wood-member-manufacturing",
        "hardwood-veneer-and-plywood-manufacturing",
        "manufactured-home-mobile-home-manufacturing",
        "millwork",
        "other-millwork-including-flooring",
        "other-wood-product-manufacturing",
        "prefabricated-wood-building-manufacturing",
        "reconstituted-wood-product-manufacturing",
        "sawmills",
        "sawmills-and-wood-preservation",
        "softwood-veneer-and-plywood-manufacturing",
        "veneer-plywood-and-engineered-wood-product-manufacturing",
        "wood-container-and-pallet-manufacturing",
        "wood-preservation",
        "wood-product-manufacturing",
        "wood-window-and-door-manufacturing"
      ]
    },
    name: "Wood Product Manufacturing",
    parents: ["manufacturing"],
    sic: {
      codes: [
        "2400",
        "2421",
        "2430",
        "2451",
        "2452"
      ],
      slugs: [
        "lumber-wood-products-no-furniture",
        "millwood-veneer-plywood-structural-wood-members",
        "mobile-homes",
        "prefabricated-wood-bldgs-components",
        "sawmills-planting-mills-general"
      ]
    }
  },
  "writing-and-editing": {
    description: "This industry includes entities that create documents or resumes, edit or proofread documents, transcribe documents, and/or provide other writing and editing services.",
    linkedinId: 103,
    naics: {
      codes: ["56141", "561410"],
      slugs: ["document-preparation-services"]
    },
    name: "Writing and Editing",
    parents: ["administrative-and-support-services"],
    sic: {
      codes: ["3950"],
      slugs: ["pens-pencils-other-artists-materials"]
    }
  },
  "zoos-and-botanical-gardens": {
    description: "This industry includes entities that preserve and exhibit live plant and animal life displays.",
    linkedinId: 2163,
    naics: {
      codes: ["71213", "712130"],
      slugs: ["zoos-and-botanical-gardens"]
    },
    name: "Zoos and Botanical Gardens",
    parents: ["entertainment-providers", "museums-historical-sites-and-zoos"],
    sic: {
      codes: [],
      slugs: []
    }
  }
};

const industriesNaics = {
  "11": {
    description: "The Sector as a Whole The Agriculture, Forestry, Fishing and Hunting sector comprises establishments primarily engaged in growing crops, raising animals, harvesting timber, and harvesting fish and other animals from a farm, ranch, or their natural habitats. The establishments in this sector are often described as farms, ranches, dairies, greenhouses, nurseries, orchards, or hatcheries.  A farm may consist of a single tract of land or a number of separate tracts which may be held under different tenures.  For example, one tract may be owned by the farm operator and another rented.  It may be operated by the operator alone or with the assistance of members of the household or hired employees, or it may be operated by a partnership, corporation, or other type of organization. When a landowner has one or more tenants, renters, croppers, or managers, the land operated by each is considered a farm. The sector distinguishes two basic activities: agricultural production and agricultural support activities.  Agricultural production includes establishments performing the complete farm or ranch operation, such as farm owner-operators and tenant farm operators.  Agricultural support activities include establishments that perform one or more activities associated with farm operation, such as soil preparation, planting, harvesting, and management, on a contract or fee basis. Excluded from the Agriculture, Forestry, Fishing and Hunting sector are establishments primarily engaged in agricultural research (e.g., experimental farms) and government establishments primarily engaged in administering programs for regulating and conserving land, mineral, wildlife, and forest use.  These establishments are classified in Industry 54171, Research and Development in the Physical, Engineering, and Life Sciences; and Industry 92412, Administration of Conservation Programs, respectively.",
    industries: ["farming-ranching-forestry"],
    name: "Agriculture, Forestry, Fishing and Hunting",
    slug: "agriculture-forestry-fishing-and-hunting"
  },
  "21": {
    description: 'The Sector as a Whole The Mining, Quarrying, and Oil and Gas Extraction sector comprises establishments that extract naturally occurring mineral solids, such as coal and ores; liquid minerals, such as crude petroleum; and gases, such as natural gas.  The term "mining" is used in the broad sense to include quarrying, well operations, beneficiating (e.g., crushing, screening, washing, and flotation), and other preparation customarily performed at the mine site, or as a part of mining activity. The Mining, Quarrying, and Oil and Gas Extraction sector distinguishes two basic activities: mine operation and mining support activities.  Mine operation includes establishments operating mines, quarries, or oil and gas wells on their own account or for others on a contract or fee basis.  Mining support activities include establishments that perform exploration (except geophysical surveying and mapping) on a contract or fee basis and/or other mining services on a contract or fee basis (except mine site preparation, construction, and transportation activities). Establishments in the Mining, Quarrying, and Oil and Gas Extraction sector are grouped and classified according to the natural resource mined or to be mined.  Industries include establishments that develop and/or operate the mine site, extract the natural resources, beneficiate (i.e., prepare) the mineral mined, or provide mining support activities.  Beneficiation is the process whereby the extracted material is reduced to particles that can be separated into mineral and waste, the former suitable for further processing or direct use.  The operations that take place in beneficiation are primarily mechanical, such as grinding, washing, magnetic separation, and centrifugal separation.  In contrast, manufacturing operations primarily use chemical and electrochemical processes, such as electrolysis and distillation.  However, some treatments, such as heat treatments, take place in both the beneficiation and the manufacturing (i.e., smelting/refining) stages.  The range of preparation activities varies by mineral and the purity of any given ore deposit.  While some minerals, such as petroleum and natural gas, require little or no preparation, others are washed and screened, while yet others, such as gold and silver, can be transformed into bullion before leaving the mine site. Mining, beneficiating, and manufacturing activities often occur in a single location.  Separate receipts will be collected for these activities whenever possible.  When receipts cannot be broken out between mining and manufacturing, establishments that mine or quarry nonmetallic minerals, and then beneficiate the nonmetallic minerals into more finished manufactured products are classified based on the primary activity of the establishment.  A mine that manufactures a small amount of finished products will be classified in Sector 21, Mining, Quarrying, and Oil and Gas Extraction.  An establishment that mines whose primary output is a more finished manufactured product will be classified in Sector 31-33, Manufacturing.',
    industries: ["oil-gas-and-mining"],
    name: "Mining, Quarrying, and Oil and Gas Extraction",
    slug: "mining-quarrying-and-oil-and-gas-extraction"
  },
  "22": {
    description: "The Sector as a Whole The Utilities sector comprises establishments engaged in the provision of the following utility services: electric power, natural gas, steam supply, water supply, and sewage treatment and disposal.  Within this sector, the specific activities associated with the utility services provided vary by utility: electric power includes generation, transmission, and distribution; natural gas includes distribution; steam supply includes provision and/or distribution; water supply includes treatment and distribution; and sewage removal includes collection, treatment, and disposal of waste through sewer systems and sewage treatment facilities. Excluded from this sector are establishments primarily engaged in waste management services classified in Subsector 562, Waste Management and Remediation Services.  These establishments also collect, treat, and dispose of waste materials; however, they do not use sewer systems or sewage treatment facilities.",
    industries: ["utilities"],
    name: "Utilities",
    slug: "utilities"
  },
  "23": {
    description: 'The Sector as a Whole The Construction sector comprises establishments primarily engaged in the construction of buildings or engineering projects (e.g., highways and utility systems).  Establishments primarily engaged in the preparation of sites for new construction and establishments primarily engaged in subdividing land for sale as building sites also are included in this sector. Construction work done may include new work, additions, alterations, or maintenance and repairs.  Activities of these establishments generally are managed at a fixed place of business, but they usually perform construction activities at multiple project sites.  Production responsibilities for establishments in this sector are usually specified in (1) contracts with the owners of construction projects (prime contracts) or (2) contracts with other construction establishments (subcontracts). Establishments primarily engaged in contracts that include responsibility for all aspects of individual construction projects are commonly known as general contractors, but also may be known as design-builders, construction managers, turnkey contractors, or (in cases where two or more establishments jointly secure a general contract) joint-venture contractors.  Construction managers that provide oversight and scheduling only (i.e., agency) as well as construction managers that are responsible for the entire project (i.e., at risk) are included as general contractor type establishments.  Establishments of the "general contractor type" frequently arrange construction of separate parts of their projects through subcontracts with other construction establishments. Establishments primarily engaged in activities to produce a specific component (e.g., masonry, painting, and electrical work) of a construction project are commonly known as specialty trade contractors.  Activities of specialty trade contractors are usually subcontracted from other construction establishments, but especially in remodeling and repair construction, the work may be done directly for the owner of the property. Establishments primarily engaged in activities to construct buildings to be sold on sites that they own are known as for-sale builders, but also may be known as speculative builders or merchant builders.  For-sale builders produce buildings in a manner similar to general contractors, but their production processes also include site acquisition and securing of financial backing.  For-sale builders are most often associated with the construction of residential buildings.  Like general contractors, they may subcontract all or part of the actual construction work on their buildings. There are substantial differences in the types of equipment, work force skills, and other inputs required by establishments in this sector.  To highlight these differences and variations in the underlying production functions, this sector is divided into three subsectors. Subsector 236, Construction of Buildings, comprises establishments of the general contractor type and for-sale builders involved in the construction of buildings.  Subsector 237, Heavy and Civil Engineering Construction, comprises establishments involved in the construction of engineering projects.  Subsector 238, Specialty Trade Contractors, comprises establishments engaged in specialty trade activities generally needed in the construction of all types of buildings. Force account construction is construction work performed by an enterprise primarily engaged in some business other than construction for its own account, using employees of the enterprise.  This activity is not included in the construction sector unless the construction work performed is the primary activity of a separate establishment of the enterprise.  The installation and the ongoing repair and maintenance of telecommunications and utility networks is excluded from construction when the establishments performing the work are not independent contractors.  Although a growing proportion of this work is subcontracted to independent contractors in the Construction sector, the operating units of telecommunications and utility companies performing this work are included with the telecommunications or utility activities.',
    industries: ["construction"],
    name: "Construction",
    slug: "construction"
  },
  "31": {
    description: "The Sector as a Whole The Manufacturing sector comprises establishments engaged in the mechanical, physical, or chemical transformation of materials, substances, or components into new products.  The assembling of component parts of manufactured products is considered manufacturing, except in cases where the activity is appropriately classified in Sector 23, Construction. Establishments in the Manufacturing sector are often described as plants, factories, or mills and characteristically use power-driven machines and material handling equipment.  However, establishments that transform materials or substances into new products by hand or in the worker's home and those engaged in selling to the general public products made on the same premises from which they are sold, such as bakeries, candy stores, and custom tailors, may also be included in this sector.  Manufacturing establishments may process materials or may contract with other establishments to process their materials for them.  Both types of establishments are included in manufacturing.  Selected industries in the Manufacturing sector are comprised solely of establishments that process materials for other establishments on a contract or fee basis.  Beyond these dedicated contract manufacturing industries, establishments that process materials for other establishments are generally classified in the Manufacturing industry of the processed materials. The materials, substances, or components transformed by manufacturing establishments are raw materials that are products of agriculture, forestry, fishing, mining, or quarrying as well as products of other manufacturing establishments.  The materials used may be purchased directly from producers, obtained through customary trade channels, or secured without recourse to the market by transferring the product from one establishment to another, under the same ownership. The new product of a manufacturing establishment may be finished in the sense that it is ready for utilization or consumption, or it may be semi-finished to become an input for an establishment engaged in further manufacturing.  For example, the product of the alumina refinery is the input used in the primary production of aluminum; primary aluminum is the input to an aluminum wire drawing plant; and aluminum wire is the input for a fabricated wire product manufacturing establishment. The subsectors in the Manufacturing sector generally reflect distinct production processes related to material inputs, production equipment, and employee skills.  In the machinery area, where assembling is a key activity, parts and accessories for manufactured products are classified in the industry of the finished manufactured item when they are made for separate sale.  For example, an attachment for a piece of metalworking machinery would be classified with metalworking machinery.  However, component inputs from other manufacturing establishments are classified based on the production function of the component manufacturer.  For example, electronic components are classified in Subsector 334, Computer and Electronic Product Manufacturing, and stampings are classified in Subsector 332, Fabricated Metal Product Manufacturing. Manufacturing establishments often perform one or more activities that are classified outside the Manufacturing sector of NAICS.  For instance, almost all manufacturing has some captive research and development or administrative operations, such as accounting, payroll, or management.  These captive services are treated the same as captive manufacturing activities.  When the services are provided by separate establishments, they are classified in the NAICS sector where such services are primary, not in manufacturing.  The boundaries of manufacturing and the other sectors of the classification system can be somewhat blurry.  The establishments in the Manufacturing sector are engaged in the transformation of materials into new products.  Their output is a new product.  However, the definition of what constitutes a new product can be somewhat subjective.  As clarification, the following activities are considered manufacturing in NAICS:, <table width=100%><tr><td width=10%> </td><td><dl><dt style='padding-left: 10px; text-indent: -10px;'>Milk bottling and pasteurizing;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Water bottling and processing;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Fresh fish packaging (oyster<br/> shucking, fish filleting);</dt><dt style='padding-left: 10px; text-indent: -10px;'>Apparel jobbing (assigning<br/> materials to contract<br/> factories or shops for<br/>  fabrication or other contract<br/> operations) as well as<br/> contracting on materials<br/> owned by others;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Printing and related activities;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Ready-mix concrete production;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Leather converting;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Grinding lenses to<br/> prescription;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Wood preserving;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Lapidary work for the trade;</dt></dl></td><td width=10%> </td><td><dl><dt style='padding-left: 10px; text-indent: -10px;'>Electroplating, plating, metal<br/> heat treating, and<br/> polishing for the trade;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Fabricating signs and<br/> advertising displays;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Rebuilding or remanufacturing<br/> machinery (i.e., automotive<br/> parts);</dt><dt style='padding-left: 10px; text-indent: -10px;'>Making manufactured homes<br/> (i.e., mobile homes) or<br/> prefabricated buildings,<br/> whether or not assembling/<br/> erecting at the customers'<br/> site;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Ship repair and renovation;</dt><dt style='padding-left: 10px; text-indent: -10px;'>Machine shops; and</dt><dt style='padding-left: 10px; text-indent: -10px;'>Tire retreading.</dt></dl></td><td width=10%> </td></tr></table>, Conversely, there are activities that are sometimes considered manufacturing, but which for NAICS are classified in another sector (i.e., not classified as manufacturing). They include: 1. Logging, classified in Sector 11, Agriculture, Forestry, Fishing and Hunting, is considered a harvesting operation;, 2. Beneficiating ores and other minerals, classified in Sector 21, Mining, Quarrying, and Oil and Gas Extraction, is considered part of the activity of mining;, 3. Constructing structures, assembling prefabricated buildings, and fabricating at the construction site by contractors are classified in Sector 23, Construction;, 4. Breaking bulk and reselling in smaller lots, including packaging, repackaging, or bottling products, such as liquors or chemicals; assembling and selling computers on a custom basis; sorting and reselling scrap; mixing and selling paints to customer order; and cutting metals to customer order for resale are classified in Sector 42, Wholesale Trade, or Sector 44-45, Retail Trade; and, 5. Publishing and the combined activity of publishing and printing, classified in Sector 51, Information, transform information into a product for which the value to the consumer lies in the information content, not in the format in which it is distributed (i.e., the book or software compact disc).",
    industries: ["manufacturing"],
    name: "Manufacturing",
    slug: "manufacturing"
  },
  "42": {
    description: "The Sector as a Whole The Wholesale Trade sector comprises establishments engaged in wholesaling merchandise, generally without transformation, and rendering services incidental to the sale of merchandise.  The merchandise described in this sector includes the outputs of agriculture, mining, manufacturing, and certain information industries, such as publishing. The wholesaling process is an intermediate step in the distribution of merchandise.  Wholesalers are organized to sell or arrange the purchase or sale of (a) goods for resale (i.e., goods sold to other wholesalers or retailers), (b) capital or durable nonconsumer goods, and (c) raw and intermediate materials and supplies used in production. Wholesalers sell merchandise to other businesses and normally operate from a warehouse or office.  These warehouses and offices are characterized by having little or no display of merchandise.  In addition, neither the design nor the location of the premises is intended to solicit walk-in traffic.  Wholesalers do not normally use advertising directed to the general public.  Customers are generally reached initially via telephone, in-person marketing, or by specialized advertising that may include Internet and other electronic means.  Follow-up orders are either vendor-initiated or client-initiated, generally based on previous sales, and typically exhibit strong ties between sellers and buyers.  In fact, transactions are often conducted between wholesalers and clients that have long-standing business relationships. This sector comprises two main types of wholesalers: merchant wholesalers that sell goods on their own account and agents and brokers that arrange sales and purchases for others generally for a commission or fee. (1)   Establishments that sell goods on their own account are known as wholesale merchants, distributors, jobbers, drop shippers, and import/export merchants.  Also included as wholesale merchants are sales offices and sales branches (but not retail stores) maintained by manufacturing, refining, or mining enterprises apart from their plants or mines for the purpose of marketing their products, and group purchasing organizations primarily purchasing and selling goods on their own account.  Merchant wholesale establishments typically maintain their own warehouse, where they receive and handle goods for their customers.  Goods are generally sold without transformation, but may include integral functions, such as sorting, packaging, labeling, and other marketing services. (2)   Establishments arranging for the purchase or sale of goods owned by others or purchasing goods, generally on a commission basis are known as business-to-business electronic markets, agents and brokers, commission merchants, import/export agents and brokers, auction companies, group purchasing organizations (acting as agents), and manufacturers' representatives.  These establishments operate from offices and generally do not own or handle the goods they sell. Some wholesale establishments may be connected with a single manufacturer and promote and sell the particular manufacturer's products to a wide range of other wholesalers or retailers.  Other wholesalers may be connected to a retail chain, or limited number of retail chains, and only provide a variety of products needed by that particular retail operation(s).  These wholesalers may obtain the products from a wide range of manufacturers.  Still other wholesalers may not take title to the goods, but act as agents and brokers for a commission. Although, in general, wholesaling normally denotes sales in large volumes, durable nonconsumer goods may be sold in single units.  Sales of capital or durable nonconsumer goods used in the production of goods and services, such as farm machinery, medium- and heavy-duty trucks, and industrial machinery, are always included in wholesale trade.",
    industries: ["wholesale"],
    name: "Wholesale Trade",
    slug: "wholesale-trade"
  },
  "51": {
    description: "The Sector as a Whole The Information sector comprises establishments engaged in the following processes: (a) producing and distributing information and cultural products, (b) providing the means to transmit or distribute these products as well as data or communications, and (c) processing data. The main components of this sector are motion picture and sound recording industries; publishing industries, including software publishing; broadcasting and content providers; telecommunications industries; computing infrastructure providers, data processing, Web hosting, and related services; and Web search portals, libraries, archives, and other information services. The unique characteristics of information and cultural products, and of the processes involved in their production and distribution, distinguish the Information sector from the goods-producing and service-producing sectors.  Some of these characteristics are: 1. Unlike traditional goods, an ''information or cultural product,'' such as an online newspaper or a television program, does not necessarily have tangible qualities, nor is it necessarily associated with a particular form.  A movie can be viewed at a movie theater or through television broadcast, video-on-demand, or streaming services.  A sound recording can be aired on radio, embedded in multimedia products, streamed, or sold at a record store. 2. Unlike traditional services, the delivery of these products does not require direct contact between the supplier and the consumer. 3. The value of these products to the consumer lies in their informational, educational, cultural, or entertainment content, not in the format in which they are distributed.  Most of these products are protected from unlawful reproduction by copyright laws.  4. The intangible property aspect of information and cultural products makes the processes involved in their production and distribution very different from goods and services.  Only those possessing the rights to these works are authorized to reproduce, alter, improve, and distribute them.  Acquiring and using these rights often involves significant costs.  In addition, technology has revolutionized the distribution of these products.  It is possible to distribute them in a physical form, via broadcast, or online. 5. Distributors of information and cultural products can easily add value to the products they distribute.  For instance, broadcasters add advertising not contained in the original product.  This capacity means that unlike traditional goods distributors, they derive revenue not from sale of the distributed product to the final consumer, but from those who pay for the privilege of adding information to the original product.  Similarly, a directory and mailing list publisher can acquire the rights to thousands of previously published newspaper and periodical articles and add new value by providing search and software and organizing the information in a way that facilitates research and retrieval.  These products often command a much higher price than the original information. Excluded from this sector are establishments primarily engaged in custom design of software; mass reproducing software or other prerecorded audio and video material on magnetic or optical media; producing live artistic and cultural works or productions; and performing in or creating artistic and cultural works or productions as independent (i.e., freelance) individuals.",
    industries: ["technology-information-and-media"],
    name: "Information",
    slug: "information"
  },
  "52": {
    description: "The Sector as a Whole The Finance and Insurance sector comprises establishments primarily engaged in financial transactions (transactions involving the creation, liquidation, or change in ownership of financial assets) and/or in facilitating financial transactions.  Three principal types of activities are identified: 1.  Raising funds by taking deposits and/or issuing securities and, in the process, incurring liabilities.  Establishments engaged in this activity use raised funds to acquire financial assets by making loans and/or purchasing securities.  Putting themselves at risk, they channel funds from lenders to borrowers and transform or repackage the funds with respect to maturity, scale, and risk.  This activity is known as financial intermediation. 2.  Pooling of risk by underwriting insurance and annuities.  Establishments engaged in this activity collect fees, insurance premiums, or annuity considerations; build up reserves; invest those reserves; and make contractual payments.  Fees are based on the expected incidence of the insured risk and the expected return on investment. 3.  Providing specialized services facilitating or supporting financial intermediation, insurance, and employee benefit programs. In addition, monetary authorities charged with monetary control are included in this sector. The subsectors, industry groups, and industries within the Finance and Insurance sector are defined on the basis of their unique production processes.  As with all industries, the production processes are distinguished by their use of specialized human resources and specialized physical capital.  In addition, the way in which these establishments acquire and allocate financial capital, their source of funds, and the use of those funds provides a third basis for distinguishing characteristics of the production process.  For instance, the production process in raising funds through deposit-taking is different from the process of raising funds in bond or money markets.  The process of making loans to individuals also requires different production processes than does the creation of investment pools or the underwriting of securities. Most of the Finance and Insurance subsectors contain one or more industry groups of (1) intermediaries with similar patterns of raising and using funds and (2) establishments engaged in activities that facilitate, or are otherwise related to, that type of financial or insurance intermediation.  Industries within this sector are defined in terms of activities for which a production process can be specified, and many of these activities are not exclusive to a particular type of financial institution.  To deal with the varied activities taking place within existing financial institutions, the approach is to split these institutions into components performing specialized services.  This requires defining the units engaged in providing those services and developing procedures that allow for their delineation.  These units are the equivalents for finance and insurance of the establishments defined for other industries. The output of many financial services, as well as the inputs and the processes by which they are combined, cannot be observed at a single location and can only be defined at a higher level of the organizational structure of the enterprise.  Additionally, a number of independent activities that represent separate and distinct production processes may take place at a single location belonging to a multilocation financial firm.  Activities are more likely to be homogeneous with respect to production characteristics than are locations, at least in financial services.  The classification defines activities broadly enough that it can be used both by those classifying by location and by those employing a more top-down approach to the delineation of the establishment. Establishments engaged in activities that facilitate, or are otherwise related to, the various types of intermediation are included in multiple subsectors, rather than in a separate subsector dedicated to services alone, because these services are performed by intermediaries, as well as by specialist establishments, and the extent to which the activity of the intermediaries can be separately identified is not clear. Financial industries are extensive users of electronic means for facilitating the verification of financial balances, authorizing transactions, transferring funds to and from transactors' accounts, notifying banks (or credit card issuers) of the individual transactions, and providing daily summaries.  Since these transaction processing activities are integral to the production of finance and insurance services, establishments that principally provide a financial transaction processing service are classified in this sector, rather than in the data processing industry in the Information sector. Legal entities that hold portfolios of assets on behalf of others are significant and data on them are required for a variety of purposes.  Thus, for NAICS, these funds, trusts, and other financial vehicles are the fifth subsector of the Finance and Insurance sector.  These entities earn interest, dividends, and other property income, but have little or no employment and no revenue from the sale of services.  Separate establishments and employees devoted to the management of funds are classified in Industry Group 5239, Other Financial Investment Activities.",
    industries: ["financial-services"],
    name: "Finance and Insurance",
    slug: "finance-and-insurance"
  },
  "53": {
    description: "The Sector as a Whole The Real Estate and Rental and Leasing sector comprises establishments primarily engaged in renting, leasing, or otherwise allowing the use of tangible or intangible assets, and establishments providing related services.  The major portion of this sector comprises establishments that rent, lease, or otherwise allow the use of their own assets by others.  The assets may be tangible, as is the case of real estate and equipment, or intangible, as is the case with patents and trademarks. This sector also includes establishments primarily engaged in managing real estate for others, selling, renting, and/or buying real estate for others, and appraising real estate.  These activities are closely related to this sector's main activity, and from a production basis they are included here.  In addition, a substantial proportion of property management is self-performed by lessors. The main components of this sector are the real estate lessors industries (including equity real estate investment trusts (REITs)); equipment lessors industries (including motor vehicles, computers, and consumer goods); and lessors of nonfinancial intangible assets (except copyrighted works). Excluded from this sector are establishments primarily engaged in renting or leasing equipment with operators.  Establishments renting or leasing equipment with operators are classified in various subsectors of NAICS depending on the nature of the services provided (e.g., transportation, construction, agriculture).  These activities are excluded from this sector because the client is paying for the expertise and knowledge of the equipment operator, in addition to the rental of the equipment.  In many cases, such as the rental of heavy construction equipment, the operator is essential to operate the equipment.  Also excluded from this sector are mortgage REITs and establishments primarily engaged in managing the financial portfolio assets of REITs on a fee or commission basis.  These establishments are classified in Sector 52, Finance and Insurance.",
    industries: ["real-estate-and-equipment-rental-services"],
    name: "Real Estate and Rental and Leasing",
    slug: "real-estate-and-rental-and-leasing"
  },
  "54": {
    description: "The Sector as a Whole The Professional, Scientific, and Technical Services sector comprises establishments that specialize in performing professional, scientific, and technical activities for others.  These activities require a high degree of expertise and training.  The establishments in this sector specialize according to expertise and provide these services to clients in a variety of industries and, in some cases, to households.  Activities performed include: legal advice and representation; accounting, bookkeeping, and payroll services; architectural, engineering, and specialized design services; computer services; consulting services; research services; advertising services; photographic services; translation and interpretation services; veterinary services; and other professional, scientific, and technical services. This sector excludes establishments primarily engaged in providing a range of day-to-day office administrative services, such as financial planning, billing and recordkeeping, personnel supply, and physical distribution and logistics.  These establishments are classified in Sector 56, Administrative and Support and Waste Management and Remediation Services.",
    industries: ["professional-services"],
    name: "Professional, Scientific, and Technical Services",
    slug: "professional-scientific-and-technical-services"
  },
  "55": {
    description: "The Sector as a Whole The Management of Companies and Enterprises sector comprises (1) establishments that hold the securities of (or other equity interests in) companies and enterprises for the purpose of owning a controlling interest or influencing management decisions or (2) establishments (except government establishments) that administer, oversee, and manage establishments of the company or enterprise and that normally undertake the strategic or organizational planning and decision-making role of the company or enterprise.  Establishments that administer, oversee, and manage may hold the securities of the company or enterprise. Establishments in this sector perform essential activities that are often undertaken in-house by establishments in many sectors of the economy.  By consolidating the performance of these activities of the enterprise at one establishment, economies of scale are achieved. Government establishments primarily engaged in administering, overseeing, and managing governmental programs are classified in Sector 92, Public Administration.  Establishments primarily engaged in providing a range of day-to-day office administrative services for other companies or enterprises on a contract or fee basis, such as financial planning, billing and recordkeeping, personnel, and physical distribution and logistics, are classified in Industry 56111, Office Administrative Services.",
    industries: ["holding-companies"],
    name: "Management of Companies and Enterprises",
    slug: "management-of-companies-and-enterprises"
  },
  "56": {
    description: "The Sector as a Whole The Administrative and Support and Waste Management and Remediation Services sector comprises establishments performing routine support activities for the day-to-day operations of other organizations.  These essential activities are often undertaken in-house by establishments in many sectors of the economy.  The establishments in this sector specialize in one or more of these support activities and provide these services to clients in a variety of industries and, in some cases, to households.  Activities performed include: office administration, hiring and placing of personnel, document preparation and similar clerical services, solicitation, collection, security and surveillance services, cleaning, and waste disposal services. The administrative and management activities performed by establishments in this sector are typically on a contract or fee basis.  These activities may also be performed by establishments that are part of the company or enterprise.  However, establishments involved in administering, overseeing, and managing other establishments of the company or enterprise are classified in Sector 55, Management of Companies and Enterprises.  Establishments in Sector 55, Management of Companies and Enterprises, normally undertake the strategic and organizational planning and decision-making role of the company or enterprise.  Government establishments engaged in administering, overseeing, and managing governmental programs are classified in Sector 92, Public Administration.",
    industries: ["administrative-and-support-services"],
    name: "Administrative and Support and Waste Management and Remediation Services",
    slug: "administrative-and-support-and-waste-management-and-remediation-services"
  },
  "61": {
    description: "The Sector as a Whole The Educational Services sector comprises establishments that provide instruction and training in a wide variety of subjects.  This instruction and training is provided by specialized establishments, such as schools, colleges, universities, and training centers.  These establishments may be privately owned and operated for profit or not for profit, or they may be publicly owned and operated.  They may also offer food and/or accommodation services to their students. Educational services are usually delivered by teachers or instructors that explain, tell, demonstrate, supervise, and direct learning.  Instruction is imparted in diverse settings, such as educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods.  It can be adapted to the particular needs of the students, for example sign language can replace verbal language for teaching students with hearing impairments.  All industries in the sector share this commonality of process, namely, labor inputs of instructors with the requisite subject matter expertise and teaching ability.",
    industries: ["education"],
    name: "Educational Services",
    slug: "educational-services"
  },
  "62": {
    description: "The Sector as a Whole The Health Care and Social Assistance sector comprises establishments providing health care and social assistance for individuals.  The sector includes both health care and social assistance because it is sometimes difficult to distinguish between the boundaries of these two activities.  The industries in this sector are arranged on a continuum starting with establishments providing medical care exclusively, continuing with those providing health care and social assistance, and finally finishing with those providing only social assistance.  Establishments in this sector deliver services by trained professionals.  All industries in the sector share this commonality of process, namely, labor inputs of health practitioners or social workers with the requisite expertise.  Many of the industries in the sector are defined based on the educational degree held by the practitioners included in the industry. Excluded from this sector are yoga and aerobics instruction in Subsector 611, Educational Services, physical fitness facilities in Subsector 713, Amusement, Gambling, and Recreation Industries, and personal fitness training services and non-medical diet and weight reducing centers in Subsector 812, Personal and Laundry Services.  Although these can be viewed as health services, these services are not typically delivered by health practitioners.",
    industries: ["hospitals-and-health-care"],
    name: "Health Care and Social Assistance",
    slug: "health-care-and-social-assistance"
  },
  "71": {
    description: "The Sector as a Whole The Arts, Entertainment, and Recreation sector includes a wide range of establishments that operate facilities or provide services to meet varied cultural, entertainment, and recreational interests of their patrons.  This sector comprises (1) establishments that are involved in producing, promoting, or participating in live performances, events, or exhibits intended for public viewing; (2) establishments that preserve and exhibit objects and sites of historical, cultural, or educational interest; and (3) establishments that operate facilities or provide services that enable patrons to participate in recreational activities or pursue amusement, hobby, and leisure-time interests. Some establishments that provide cultural, entertainment, or recreational facilities and services are classified in other sectors.  Excluded from this sector are: (1) establishments that provide both accommodations and recreational facilities, such as hunting and fishing camps and resort and casino hotels, are classified in Subsector 721, Accommodation; (2) restaurants and night clubs that provide live entertainment in addition to the sale of food and beverages are classified in Subsector 722, Food Services and Drinking Places; (3) motion picture theaters, libraries and archives, and publishers of newspapers, magazines, books, periodicals, and computer software are classified in Sector 51, Information; and (4) establishments using transportation equipment to provide recreational and entertainment services, such as those operating sightseeing buses, dinner cruises, or helicopter rides, are classified in Subsector 487, Scenic and Sightseeing Transportation.",
    industries: ["entertainment-providers"],
    name: "Arts, Entertainment, and Recreation",
    slug: "arts-entertainment-and-recreation"
  },
  "72": {
    description: "The Sector as a Whole The Accommodation and Food Services sector comprises establishments providing customers with lodging and/or preparing meals, snacks, and beverages for immediate consumption.  The sector includes both accommodation and food services establishments because the two activities are often combined at the same establishment. Some establishments that provide food and beverage services are classified in other sectors.  Excluded from this sector are civic and social organizations.  These establishments are classified in Sector 81, Other Services (except Public Administration).  Amusement and recreation parks, dinner theaters, and other recreation or entertainment facilities are classified in Sector 71, Arts, Entertainment, and Recreation.  Motion picture theaters are classified in Sector 51, Information.",
    industries: ["accommodation-services"],
    name: "Accommodation and Food Services",
    slug: "accommodation-and-food-services"
  },
  "81": {
    description: "The Sector as a Whole The Other Services (except Public Administration) sector comprises establishments engaged in providing services not specifically provided for elsewhere in the classification system.  Establishments in this sector are primarily engaged in activities such as equipment and machinery repairing, promoting or administering religious activities, grantmaking, advocacy, and providing drycleaning and laundry services, personal care services, death care services, pet care (except veterinary) services, photofinishing services, temporary parking services, and dating services. Private households that engage in employing workers on or about the premises in activities primarily concerned with the operation of the household are included in this sector. Excluded from this sector are establishments primarily engaged in retailing new equipment and also performing repairs and general maintenance on equipment.  These establishments are classified in Sector 44-45, Retail Trade.",
    industries: ["consumer-services"],
    name: "Other Services (except Public Administration)",
    slug: "other-services-except-public-administration"
  },
  "92": {
    description: "The Sector as a Whole The Public Administration sector consists of establishments of federal, state, and local government agencies that administer, oversee, and manage public programs and have executive, legislative, or judicial authority over other institutions within a given area.  These agencies also set policy, create laws, adjudicate civil and criminal legal cases, and provide for public safety and for national defense.  In general, government establishments in the Public Administration sector oversee governmental programs and activities that are not performed by private establishments.  Establishments in this sector typically are engaged in the organization and financing of the production of public goods and services, most of which are provided for free or at prices that are not economically significant. Government establishments also engage in a wide range of productive activities covering not only public goods and services but also individual goods and services similar to those produced in sectors typically identified with private-sector establishments.  In general, ownership is not a criterion for classification in NAICS.  Therefore, government establishments engaged in the production of private-sector-like goods and services should be classified in the same industry as private-sector establishments engaged in similar activities. As a practical matter, it is difficult to identify separate establishment detail for many government agencies.  To the extent that separate establishment records are available, the administration of governmental programs is classified in Sector 92, Public Administration, while the operation of governmental programs is classified elsewhere in NAICS based on the activities performed.  For example, the governmental administrative authority for an airport is classified in Industry 92612, Regulation and Administration of Transportation Programs, while operating the airport is classified in Industry 48811, Airport Operations.  When separate records for multi-establishment companies are not available to distinguish between the administration of a governmental program and the operation of it, the establishment is classified in Sector 92, Public Administration. Examples of government-provided goods and services that are classified in sectors other than Public Administration include: schools, classified in Sector 61, Educational Services; health care facilities, classified in Sector 62, Health Care and Social Assistance; establishments operating transportation facilities, classified in Sector 48-49, Transportation and Warehousing; the operation of utilities, classified in Sector 22, Utilities; and the Government Printing Office, classified in Subsector 323, Printing and Related Support Activities.",
    industries: ["government-administration"],
    name: "Public Administration",
    slug: "public-administration"
  },
  "111": {
    description: `Industries in the Crop Production subsector grow crops mainly for food and fiber.  The subsector comprises establishments, such as farms, orchards, groves, greenhouses, and nurseries, primarily engaged in growing crops, plants, vines, or trees and their seeds. The industries in this subsector are grouped by similarity of production activity, including biological and physiological characteristics and economic requirements, the length of growing season, degree of crop rotation, extent of input specialization, labor requirements, and capital demands.  The production process is typically completed when the raw product or commodity grown reaches the "farm gate" for market, that is, at the point of first sale or price determination. Establishments are classified in the Crop Production subsector when crop production (i.e., value of crops for market) accounts for one-half or more of the establishment's total agricultural production.  Within the subsector, establishments are classified in a specific industry when a product or industry family of products (i.e., oilseed and grain farming, vegetable and melon farming, fruit and tree nut farming) account for one-half or more of the establishment's agricultural production.  Establishments with one-half or more crop production with no one product or family of products of an industry accounting for one-half of the establishment's agricultural production are treated as general combination crop farming and are classified in Industry 11199, All Other Crop Farming. Industries in the Crop Production subsector include establishments that own, operate, and manage and those that operate and manage.  Those that manage only are classified in Subsector 115, Support Activities for Agriculture and Forestry.  Establishments that raise aquatic plants in controlled or selected aquatic environments are classified in Subsector 112, Animal Production and Aquaculture.`,
    industries: ["farming"],
    name: "Crop Production",
    slug: "crop-production"
  },
  "112": {
    description: "Industries in the Animal Production and Aquaculture subsector raise or fatten animals for the sale of animals or animal products and/or raise aquatic plants and animals in controlled or selected aquatic environments for the sale of aquatic plants, animals, or their products.  The subsector includes establishments, such as ranches, farms, and feedlots, primarily engaged in keeping, grazing, breeding, or feeding animals.  These animals are kept for the products they produce or for eventual sale.  The animals are generally raised in various environments, from total confinement or captivity to feeding on an open range pasture. The industries in this subsector are grouped by important factors, such as suitable grazing or pasture land, specialized buildings, type of equipment, and the amount and types of labor required.  Establishments are classified in the Animal Production and Aquaculture subsector when animal production (i.e., value of animals for market) accounts for one-half or more of the establishment's total agricultural production.  Establishments with one-half or more animal production with no one animal product or family of animal products of an industry accounting for one-half of the establishment's agricultural production are treated as combination animal farming classified in Industry 11299, All Other Animal Production.",
    industries: ["ranching-and-fisheries"],
    name: "Animal Production and Aquaculture",
    slug: "animal-production-and-aquaculture"
  },
  "113": {
    description: "Industries in the Forestry and Logging subsector grow and harvest timber on a long production cycle (i.e., of 10 years or more).  Long production cycles use different production processes than short production cycles, which require more horticultural interventions prior to harvest, resulting in processes more similar to those found in the Crop Production subsector.  Consequently, Christmas tree production and other production involving production cycles of less than 10 years, are classified in the Crop Production subsector. Industries in this subsector specialize in different stages of the production cycle.  Reforestation requires production of seedlings in specialized nurseries.  Timber production requires natural forest or suitable areas of land that are available for a long duration.  The maturation time for timber depends upon the species of tree, the climatic conditions of the region, and the intended purpose of the timber.  The harvesting of timber (except when done on an extremely small scale) requires specialized machinery unique to the industry.  Establishments gathering forest products, such as gums, barks, balsam needles, rhizomes, fibers, Spanish moss, and ginseng and truffles, are also included in this subsector.",
    industries: ["forestry-and-logging"],
    name: "Forestry and Logging",
    slug: "forestry-and-logging"
  },
  "114": {
    description: "Industries in the Fishing, Hunting and Trapping subsector harvest fish and other wild animals from their natural habitats and are dependent upon a continued supply of the natural resource.  The harvesting of fish is the predominant economic activity of this subsector and it usually requires specialized vessels that, by the nature of their size, configuration and equipment, are not suitable for any other type of production, such as transportation.  Hunting and trapping activities utilize a wide variety of production processes and are classified in the same subsector as fishing because the availability of resources and the constraints imposed, such as conservation requirements and proper habitat maintenance, are similar.",
    industries: ["farming-ranching-forestry"],
    name: "Fishing, Hunting and Trapping",
    slug: "fishing-hunting-and-trapping"
  },
  "115": {
    description: "Industries in the Support Activities for Agriculture and Forestry subsector provide support services that are an essential part of agricultural and forestry production.  These support activities may be performed by the agriculture or forestry producing establishment or conducted independently as an alternative source of inputs required for the production process for a given crop, animal, or forestry industry.  Establishments that primarily perform these activities independent of the agriculture or forestry producing establishment are in this subsector.",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Agriculture and Forestry",
    slug: "support-activities-for-agriculture-and-forestry"
  },
  "211": {
    description: "Industries in the Oil and Gas Extraction subsector operate and/or develop oil and gas field properties.  Operation and development activities include exploration for crude petroleum and natural gas; drilling, completing, and equipping wells; operating separators, emulsion breakers, desilting equipment, and field gathering lines for crude petroleum and natural gas; and all other activities in the preparation of oil and gas up to the point of shipment from the producing property.  This subsector includes the production of crude petroleum, the mining and extraction of oil from oil shale and oil sands, the production of natural gas, sulfur recovery from natural gas, and recovery of hydrocarbon liquids. Establishments in this subsector include those that operate oil and gas wells on their own account or for others on a contract or fee basis.  Establishments primarily engaged in providing support services, on a contract or fee basis, required for the drilling or operation of oil and gas wells (except geophysical surveying and mapping, mine site preparation, construction of oil/gas pipelines, and transportation activities) are classified in Subsector 213, Support Activities for Mining.",
    industries: ["oil-and-gas"],
    name: "Oil and Gas Extraction",
    slug: "oil-and-gas-extraction"
  },
  "212": {
    description: 'Industries in the Mining (except Oil and Gas) subsector primarily engage in mining, mine site development, and beneficiating (i.e., preparing) metallic minerals and nonmetallic minerals, including coal.  The term "mining" is used in the broad sense to include ore extraction, quarrying, and beneficiating (e.g., crushing, screening, washing, sizing, concentrating, and flotation), customarily done at the mine site. Beneficiation is the process whereby the extracted material is reduced to particles which can be separated into mineral and waste, the former suitable for further processing or direct use.  The operations that take place in beneficiation are primarily mechanical, such as grinding, washing, magnetic separation, centrifugal separation, and so on. In contrast, manufacturing operations primarily use chemical and electrochemical processes, such as electrolysis, distillation, and so on.  However, some treatments, such as heat treatments, take place in both stages: the beneficiation and the manufacturing (i.e., smelting/refining) stages.  The range of preparation activities varies by mineral and the purity of any given ore deposit.  While some minerals, such as petroleum and natural gas, require little or no preparation, others are washed and screened, while yet others, such as gold and silver, can be transformed into bullion before leaving the mine site. Establishments in the Mining (except Oil and Gas) subsector include those that have complete responsibility for operating mines and quarries (except oil and gas wells) and those that operate mines and quarries (except oil and gas wells) for others on a contract or fee basis.  Establishments primarily engaged in providing support services, on a contract or fee basis (except geophysical surveying and mapping, mine site preparation, construction, and transportation activities), required for the mining and quarrying of minerals are classified in Subsector 213, Support Activities for Mining.',
    industries: ["mining"],
    name: "Mining (except Oil and Gas)",
    slug: "mining-except-oil-and-gas"
  },
  "213": {
    description: "Industries in the Support Activities for Mining subsector group establishments primarily providing support services, on a contract or fee basis (except geophysical surveying and mapping, mine site preparation, construction, and transportation activities), required for the mining and quarrying of minerals and for the extraction of oil and gas.  Establishments performing exploration for minerals, on a contract or fee basis, are included in this subsector.  Exploration services include traditional prospecting methods, such as taking core samples and making geological observations at prospective sites. The activities performed on a contract or fee basis by establishments in the Support Activities for Mining subsector are also often performed in-house by mining operators.  These activities include taking core samples, making geological observations at prospective sites, excavating slush pits and cellars, and such oil and gas operations as spudding in, drilling in, redrilling, directional drilling, and well surveying; running, cutting, and pulling casings, tubes, and rods; cementing and shooting wells; perforating well casings; acidizing and chemically treating wells; cleaning out, bailing, and swabbing wells; and operating oil and gas field gathering lines. Establishments primarily engaged in providing site preparation and related construction activities on a contract or fee basis are classified in Sector 23, Construction.  Establishments primarily engaged in providing transportation activities in support of mining, quarrying, or oil and gas extraction are classified in Sector 48-49, Transportation and Warehousing, based on the primary activity.",
    industries: ["oil-gas-and-mining"],
    name: "Support Activities for Mining",
    slug: "support-activities-for-mining"
  },
  "221": {
    description: "Industries in the Utilities subsector provide electric power, natural gas, steam supply, water supply, and sewage treatment and disposal through a permanent infrastructure of lines, mains, and pipes.  Establishments are grouped together based on the utility service provided and the particular system or facilities required to perform the service.",
    industries: ["utilities"],
    name: "Utilities",
    slug: "utilities"
  },
  "236": {
    description: "The Construction of Buildings subsector comprises establishments primarily responsible for the construction of buildings.  The work performed may include new work, additions, alterations, or maintenance and repairs.  The on-site assembly of precut, panelized, and prefabricated buildings and construction of temporary buildings are included in this subsector.  Part or all of the production work for which the establishments in this subsector have responsibility may be subcontracted to other construction establishments--usually specialty trade contractors. Establishments in this subsector are classified based on the types of buildings they construct.  This classification reflects variations in the requirements of the underlying production processes.",
    industries: ["building-construction"],
    name: "Construction of Buildings",
    slug: "construction-of-buildings"
  },
  "237": {
    description: "The Heavy and Civil Engineering Construction subsector comprises establishments whose primary activity is the construction of entire engineering projects (e.g., highways and dams), and specialty trade contractors, whose primary activity is the production of a specific component for such projects.  Specialty trade contractors in the Heavy and Civil Engineering Construction subsector generally are performing activities that are specific to heavy and civil engineering construction projects and are not normally performed on buildings.  The work performed may include new work, additions, alterations, or maintenance and repairs.  Specialty trade activities are classified in this subsector if the skills and equipment present are specific to heavy or civil engineering construction projects.  For example, specialized equipment is needed to paint lines on highways.  This equipment is not normally used in building applications so the activity is classified in this subsector.  Traffic signal installation, while specific to highways, uses much of the same skills and equipment that are needed for electrical work in building projects and is therefore classified in Subsector 238, Specialty Trade Contractors. Construction projects involving water resources (e.g., dredging and land drainage) and projects involving open space improvement (e.g., parks and trails) are included in this subsector.  Establishments whose primary activity is the subdivision of land into individual building lots usually perform various additional site-improvement activities (e.g., road building and utility line installation) and are included in this subsector.  Establishments in this subsector are classified based on the types of structures that they construct.  This classification reflects variations in the requirements of the underlying production processes.",
    industries: ["civil-engineering"],
    name: "Heavy and Civil Engineering Construction",
    slug: "heavy-and-civil-engineering-construction"
  },
  "238": {
    description: "The Specialty Trade Contractors subsector comprises establishments whose primary activity is performing specific activities (e.g., pouring concrete, site preparation, plumbing, painting, and electrical work) involved in building construction or other activities that are similar for all types of construction, but that are not responsible for the entire project.  The work performed may include new work, additions, alterations, maintenance, and repairs.  The production work performed by establishments in this subsector is usually subcontracted from establishments of the general contractor type or for-sale builders, but especially in remodeling and repair construction, work also may be done directly for the owner of the property.  Specialty trade contractors usually perform most of their work at the construction site, although they may have shops where they perform prefabrication and other work.  Establishments primarily engaged in preparing sites for new construction are also included in this subsector. There are substantial differences in types of equipment, work force skills, and other inputs required by specialty trade contractors.  Establishments in this subsector are classified based on the underlying production function for the specialty trade in which they specialize.  Throughout the Specialty Trade Contractors subsector, establishments commonly provide both the parts and labor required to complete work.  For example, electrical contractors supply the current-carrying and noncurrent-carrying wiring devices that are required to install a circuit.  Plumbing, heating, and air-conditioning contractors also supply the parts required to complete a contract.  Establishments that specialize in activities primarily related to heavy and civil engineering construction that are not normally performed on buildings, such as the painting of lines on highways, are classified in Subsector 237, Heavy and Civil Engineering Construction. Establishments that are primarily engaged in selling construction materials are classified in Sector 42, Wholesale Trade, or Sector 44-45, Retail Trade, based on the characteristics of the selling unit.",
    industries: ["specialty-trade-contractors"],
    name: "Specialty Trade Contractors",
    slug: "specialty-trade-contractors"
  },
  "311": {
    description: "Industries in the Food Manufacturing subsector transform livestock and agricultural products into products for intermediate or final consumption.  The industry groups are distinguished by the raw materials (generally of animal or vegetable origin) processed into food products. The food products manufactured in these establishments are typically sold to wholesalers or retailers for distribution to consumers, but establishments primarily engaged in retailing bakery and candy products made on the premises not for immediate consumption are included. Establishments primarily engaged in manufacturing beverages are classified in Subsector 312, Beverage and Tobacco Product Manufacturing.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Food Manufacturing",
    slug: "food-manufacturing"
  },
  "312": {
    description: "Industries in the Beverage and Tobacco Product Manufacturing subsector manufacture beverages and tobacco products.  The Beverage Manufacturing industry group includes three types of establishments: (1) those that manufacture nonalcoholic beverages; (2) those that manufacture alcoholic beverages through the fermentation process; and (3) those that produce distilled alcoholic beverages.  Ice manufacturing, while not a beverage, is included with nonalcoholic beverage manufacturing because it uses the same production process as water purification. In the case of activities related to the manufacture of beverages, the structure follows the defined production processes.  Brandy, a distilled beverage, is not placed under distillery product manufacturing, but rather under winery product manufacturing since the production process used in the manufacturing of alcoholic grape-based beverages produces both wines (fermented beverage) and brandies (distilled beverage). The Tobacco Manufacturing industry group includes two types of establishments: (1) those engaged in redrying and stemming tobacco and (2) those that manufacture tobacco products, such as cigarettes and cigars.",
    industries: ["manufacturing"],
    name: "Beverage and Tobacco Product Manufacturing",
    slug: "beverage-and-tobacco-product-manufacturing"
  },
  "313": {
    description: "Industries in the Textile Mills subsector group establishments that transform a basic fiber (natural or synthetic) into a product, such as yarn or fabric that is further manufactured into usable items, such as apparel, sheets, towels, and textile bags for individual or industrial consumption.  The further manufacturing may be performed in the same establishment and classified in this subsector, or it may be performed at a separate establishment and be classified elsewhere in the Manufacturing sector. The main processes in this subsector include preparation and spinning of fiber, knitting or weaving of fabric, and the finishing of the textile.  The NAICS structure follows and captures this process flow.  Major industries in this flow, such as preparation of fibers, weaving of fabric, knitting of fabric, and fiber and fabric finishing, are uniquely identified.  Texturizing, throwing, twisting, and winding of yarn contain aspects of both fiber preparation and fiber finishing and are classified with preparation of fibers rather than with finishing of fibers. NAICS separates the manufacturing of primary textiles and the manufacturing of textile products (except apparel) produced from purchased primary textiles, such as fabric.  The manufacturing of textile products (except apparel) from purchased fabric is classified in Subsector 314, Textile Product Mills, and apparel from purchased fabric is classified in Subsector 315, Apparel Manufacturing. Excluded from this subsector are establishments that weave or knit fabric and make garments.  These establishments are included in Subsector 315, Apparel Manufacturing.",
    industries: ["textile-manufacturing"],
    name: "Textile Mills",
    slug: "textile-mills"
  },
  "314": {
    description: "Industries in the Textile Product Mills subsector group establishments that make textile products (except apparel).  With a few exceptions, processes used by these establishments are generally cut and sew (i.e., purchasing fabric and cutting and sewing to make nonapparel textile products, such as sheets and towels).",
    industries: ["textile-manufacturing"],
    name: "Textile Product Mills",
    slug: "textile-product-mills"
  },
  "315": {
    description: "Industries in the Apparel Manufacturing subsector group establishments with two distinct manufacturing processes: (1) cut and sew (i.e., purchasing fabric and cutting and sewing to make a garment) and (2) the manufacture of garments in establishments that first knit fabric and then cut and sew the fabric into a garment.  The Apparel Manufacturing subsector includes a diverse range of establishments manufacturing full lines of ready-to-wear apparel and custom apparel: apparel contractors, performing cutting or sewing operations on materials owned by others; jobbers, performing entrepreneurial functions involved in apparel manufacturing; and tailors, manufacturing custom garments for individual clients.  Knitting fabric, when done alone, is classified in the Textile Mills subsector, but when knitting is combined with the production of complete garments, the activity is classified in the Apparel Manufacturing subsector.",
    industries: ["apparel-manufacturing"],
    name: "Apparel Manufacturing",
    slug: "apparel-manufacturing"
  },
  "316": {
    description: 'Establishments in the Leather and Allied Product Manufacturing subsector transform hides into leather by tanning or curing and fabricating the leather into products for final consumption.  This subsector also includes the manufacture of similar products from other materials, including products (except apparel) made from "leather substitutes," such as rubber, plastics, or textiles.  Rubber footwear, textile luggage, and plastics purses or wallets are examples of "leather substitute" products included in this subsector.  The products made from leather substitutes are included in this subsector because they are made in similar ways leather products are made (e.g., luggage).  They are made in the same establishments, so it is not practical to separate them. The inclusion of leather and hide tanning and finishing in this subsector is partly because it is a relatively small industry that has few close neighbors as a production process, partly because leather is an input to some of the other products classified in this subsector, and partly for historical reasons.',
    industries: ["leather-product-manufacturing"],
    name: "Leather and Allied Product Manufacturing",
    slug: "leather-and-allied-product-manufacturing"
  },
  "321": {
    description: "Establishments in the Wood Product Manufacturing subsector manufacture wood products, such as lumber, plywood, veneers, wood containers, wood flooring, wood trusses, manufactured homes (i.e., mobile homes), and prefabricated wood buildings.  The production processes of the Wood Product Manufacturing subsector include sawing, planing, shaping, laminating, and assembling wood products starting from logs that are cut into bolts, or lumber that then may be further cut, or shaped by lathes or other shaping tools.  The lumber or other transformed wood shapes may also be subsequently planed or smoothed, and assembled into finished products, such as wood containers.  The Wood Product Manufacturing subsector includes establishments that make wood products from logs and bolts that are sawed and shaped, and establishments that purchase sawed lumber and make wood products.  With the exception of sawmills and wood preservation establishments, the establishments are grouped into industries mainly based on the specific products manufactured.",
    industries: ["wood-product-manufacturing"],
    name: "Wood Product Manufacturing",
    slug: "wood-product-manufacturing"
  },
  "322": {
    description: "Industries in the Paper Manufacturing subsector make pulp, paper, or converted paper products.  The manufacturing of these products is grouped together because they constitute a series of vertically connected processes.  More than one is often carried out in a single establishment.  There are essentially three activities.  The manufacturing of pulp involves separating the cellulose fibers from other impurities in wood or used paper.  The manufacturing of paper involves matting these fibers into a sheet.  The manufacturing of converted paper products involves converting paper and other materials by various cutting and shaping techniques and includes coating and laminating activities. The Paper Manufacturing subsector is subdivided into two industry groups, the first for the manufacturing of pulp and paper and the second for the manufacturing of converted paper products.  Paper making is treated as the core activity of the subsector.  Therefore, any establishment that makes paper (including paperboard), either alone or in combination with pulp manufacturing or paper converting, is classified as a paper or paperboard mill.  Establishments that make pulp without making paper are classified as pulp mills.  Pulp mills, paper mills, and paperboard mills comprise the first industry group. Establishments that make products from purchased paper and other materials make up the second industry group, Converted Paper Product Manufacturing.  This general activity is then subdivided based, for the most part, on process distinctions.  Paperboard container manufacturing uses corrugating, cutting, and shaping machinery to form paperboard into containers.  Paper bag and coated and treated paper manufacturing establishments cut and coat paper and foil.  Stationery product manufacturing establishments make a variety of paper products used for writing, filing, and similar applications.  Other converted paper product manufacturing includes, in particular, the conversion of sanitary paper stock into such things as tissue paper and disposable diapers. An important process used in the Paper Bag and Coated and Treated Paper Manufacturing industry is lamination, often combined with coating.  Lamination and coating make a composite material with improved properties of strength, impermeability, and so on.  The laminated materials may be paper, metal foil, or plastics film.  While paper is often one of the components, it is not always.  Lamination of plastics film to plastics film is classified in Subsector 326, Plastics and Rubber Products Manufacturing, because establishments that do this often first make the film.  The same situation holds with respect to bags.  The manufacturing of bags from plastics only, whether or not laminated, is classified in Subsector 326, Plastics and Rubber Products Manufacturing. Excluded from this subsector are photosensitive papers.  These papers are chemically treated and are classified in Industry 32599, All Other Chemical Product and Preparation Manufacturing.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Paper Manufacturing",
    slug: "paper-manufacturing"
  },
  "323": {
    description: "Industries in the Printing and Related Support Activities subsector print products, such as newspapers, books, labels, business cards, stationery, business forms, and other materials, and perform support activities, such as data imaging, platemaking services, and bookbinding.  The support activities included here are an integral part of the printing industry, and a product (a printing plate, a bound book, or a computer disk or file) that is an integral part of the printing industry is almost always provided by these operations. Processes used in printing include a variety of methods used to transfer an image from a plate, screen, film, or computer file to some medium, such as paper, plastics, metal, textile articles, or wood.  The printing processes employed include, but are not limited to, lithographic, gravure, screen, flexographic, digital, and letterpress. In contrast to many other classification systems that locate publishing of printed materials in manufacturing, NAICS classifies the publishing of printed products in Subsector 513, Publishing Industries.  Though printing and publishing are often carried out by the same enterprise, it is less and less the case that these distinct activities are carried out in the same establishment.  When publishing and printing are done in the same establishment, the establishment is classified in Subsector 513, Publishing Industries, in the appropriate NAICS industry even if the receipts for printing exceed those for publishing. This subsector includes printing on clothing because the production process for that activity is printing, not clothing manufacturing.  For instance, the printing of T-shirts is included in this subsector.  In contrast, printing on fabric (or grey goods) is not included.  This activity is part of the process of finishing the fabric and is included in Industry 31331, Textile and Fabric Finishing Mills. Excluded from this subsector are establishment primarily engaged in manufacturing bare printed circuit boards.  These establishments print, perforate, plate, screen, etch, or photoprint interconnecting pathways for electric current on laminates and are classified in Industry 33441, Semiconductor and Other Electronic Component Manufacturing.  Establishments primarily providing printing brokerage services are classified in Industry 56199, All Other Support Services.",
    industries: ["printing-services"],
    name: "Printing and Related Support Activities",
    slug: "printing-and-related-support-activities"
  },
  "324": {
    description: "The Petroleum and Coal Products Manufacturing subsector is based on the transformation of crude petroleum and coal into usable products.  The dominant process is petroleum refining that involves the separation of crude petroleum into component products through such techniques as cracking and distillation. In addition, this subsector includes establishments that primarily further process refined petroleum and coal products and produce products, such as asphalt coatings and petroleum lubricating oils.  However, establishments that manufacture petrochemicals from refined petroleum are classified in Industry 32511, Petrochemical Manufacturing.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Petroleum and Coal Products Manufacturing",
    slug: "petroleum-and-coal-products-manufacturing"
  },
  "325": {
    description: "The Chemical Manufacturing subsector is based on the transformation of organic and inorganic raw materials by a chemical process and the formulation of products.  This subsector distinguishes the production of basic chemicals that comprise the first industry group from the production of intermediate and end products produced by further processing of basic chemicals that make up the remaining industry groups. This subsector does not include all industries transforming raw materials by a chemical process.  It is common for some chemical processing to occur during mining operations.  These beneficiating operations, such as copper concentrating, are classified in Sector 21, Mining, Quarrying, and Oil and Gas Extraction.  Furthermore, the refining of crude petroleum is included in Subsector 324, Petroleum and Coal Products Manufacturing.  In addition, the manufacturing of aluminum oxide is included in Subsector 331, Primary Metal Manufacturing; and beverage distilleries are classified in Subsector 312, Beverage and Tobacco Product Manufacturing.  As is the case of these two activities, the grouping of industries into subsectors may take into account the association of the activities performed with other activities in the subsector.",
    industries: ["chemical-manufacturing"],
    name: "Chemical Manufacturing",
    slug: "chemical-manufacturing"
  },
  "326": {
    description: "Industries in the Plastics and Rubber Products Manufacturing subsector make goods by processing plastics materials and raw rubber.  The core technology employed by establishments in this subsector is that of plastics or rubber product production.  Plastics and rubber are combined in the same subsector because plastics are increasingly being used as a substitute for rubber; however, the subsector is generally restricted to the production of products made of just one material, either solely plastics or rubber. Many manufacturing activities use plastics or rubber, for example the manufacture of footwear or furniture.  Typically, the production process of these products involves more than one material.  In these cases, technologies that allow disparate materials to be formed and combined are of central importance in describing the manufacturing activity.  In NAICS, such activities (footwear and furniture manufacturing) are not classified in the Plastics and Rubber Products Manufacturing subsector because the core technologies for these activities are diverse and involve multiple materials. Within the Plastics and Rubber Products Manufacturing subsector, a distinction is made between plastics and rubber products at the industry group level, although it is not a rigid distinction, as can be seen from the definition of Industry 32622, Rubber and Plastics Hoses and Belting Manufacturing.  In the case of hoses and belting, plastics are used as a substitute for rubber, and the distinction in materials is not useful as a basis for establishment classification. In keeping with the core technology focus of plastics, lamination of plastics film to plastics film as well as the production of bags from plastics only is classified in this subsector.  Lamination and bag production involving plastics and materials other than plastics are classified in Subsector 322, Paper Manufacturing.",
    industries: ["plastics-and-rubber-product-manufacturing"],
    name: "Plastics and Rubber Products Manufacturing",
    slug: "plastics-and-rubber-products-manufacturing"
  },
  "327": {
    description: "The Nonmetallic Mineral Product Manufacturing subsector is based on the transformation of mined or quarried nonmetallic minerals, such as sand, gravel, stone, clay, and refractory materials, into products for intermediate or final consumption.  Processes used include grinding, mixing, cutting, shaping, and honing.  Heat often is used in the process and chemicals are frequently mixed to change the composition, purity, and chemical properties for the intended product.  For example, glass is produced by heating silica sand to the melting point (sometimes combined with cullet or recycled glass) and then drawn, floated, or blow molded to the desired shape or thickness.  Refractory materials are heated and then formed into bricks or other shapes for use in industrial applications. The Nonmetallic Mineral Product Manufacturing subsector includes establishments that manufacture bricks, refractories, ceramic products, and glass and glass products, such as plate glass and containers.  Also included are cement and concrete products, lime, gypsum, and other nonmetallic mineral products including abrasive products, ceramic plumbing fixtures, statuary, cut stone products, and mineral wool.  The products are used in a wide range of activities from construction and heavy and light manufacturing to articles for personal use. Mining, beneficiating, and manufacturing activities often occur in a single location.  Separate receipts will be collected for these activities whenever possible.  When receipts cannot be broken out between mining and manufacturing, establishments that mine or quarry nonmetallic minerals, beneficiate the nonmetallic minerals, and further process the nonmetallic minerals into a more finished manufactured product are classified based on the primary activity of the establishment.  A mine that manufactures a small amount of finished products is classified in Sector 21, Mining, Quarrying, and Oil and Gas Extraction.  An establishment that mines whose primary output is a more finished manufactured product is classified in the Manufacturing sector. Excluded from the Nonmetallic Mineral Product Manufacturing subsector are establishments that primarily beneficiate mined nonmetallic minerals.  Beneficiation is the process whereby the extracted material is reduced to particles that can be separated into mineral and waste, the former suitable for further processing or direct use.  Beneficiation establishments are included in Sector 21, Mining, Quarrying, and Oil and Gas Extraction.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Nonmetallic Mineral Product Manufacturing",
    slug: "nonmetallic-mineral-product-manufacturing"
  },
  "331": {
    description: "Industries in the Primary Metal Manufacturing subsector smelt and/or refine ferrous and nonferrous metals from ore, pig, or scrap, using electrometallurgical and other process metallurgical techniques.  Establishments in this subsector also manufacture metal alloys and superalloys by introducing other chemical elements to pure metals.  The output of smelting and refining, usually in ingot form, is used in rolling, drawing, and extruding operations to make sheet, strip, bar, rod, or wire, and in molten form to make castings and other basic metal products. Primary manufacturing of ferrous and nonferrous metals begins with ore or concentrate as the primary input.  Establishments manufacturing primary metals from ore and/or concentrate remain classified in the primary smelting, primary refining, or iron and steel mill industries regardless of the form of their output.  Establishments primarily engaged in secondary smelting and/or secondary refining recover ferrous and nonferrous metals from scrap and/or dross.  The output of the secondary smelting and/or secondary refining industries is limited to shapes such as ingot or billet that will be further processed.  Recovery of metals from scrap often occurs in establishments that are primarily engaged in activities, such as rolling, drawing, extruding, or similar processes. Excluded from the Primary Metal Manufacturing subsector are establishments primarily engaged in manufacturing ferrous and nonferrous forgings (except ferrous forgings made in steel mills) and stampings.  Although forging, stamping, and casting are all methods used to make metal shapes, forging and stamping do not use molten metals and are included in Subsector 332, Fabricated Metal Product Manufacturing.  Establishments primarily engaged in operating coke ovens are classified in Industry 32419, Other Petroleum and Coal Products Manufacturing.",
    industries: ["primary-metal-manufacturing"],
    name: "Primary Metal Manufacturing",
    slug: "primary-metal-manufacturing"
  },
  "332": {
    description: "Industries in the Fabricated Metal Product Manufacturing subsector transform metal into intermediate or end products, other than machinery, computers and electronics, and metal furniture, or treat metals and metal formed products fabricated elsewhere.  Important fabricated metal processes are forging, stamping, bending, forming, and machining, used to shape individual pieces of metal; and other processes, such as welding and assembling, used to join separate parts together.  Establishments in this subsector may use one of these processes or a combination of these processes. The NAICS structure for this subsector distinguishes the forging and stamping processes in a single industry.  The remaining industries in the subsector group establishments based on similar combinations of processes used to make products. The manufacturing performed in the Fabricated Metal Product Manufacturing subsector begins with manufactured metal shapes.  The establishments in this subsector further fabricate the purchased metal shapes into a product.  For instance, the Spring and Wire Product Manufacturing industry starts with wire and fabricates such items. Within the Manufacturing sector there are other establishments that make the same products made by this subsector; only these establishments begin production further back in the production process.  These establishments have a more integrated operation.  For instance, one establishment may manufacture steel, draw it into wire, and make wire products in the same establishment.  Such operations are classified in the Primary Metal Manufacturing subsector.",
    industries: ["fabricated-metal-products"],
    name: "Fabricated Metal Product Manufacturing",
    slug: "fabricated-metal-product-manufacturing"
  },
  "333": {
    description: "Industries in the Machinery Manufacturing subsector create end products that apply mechanical force, for example, the application of gears and levers, to perform work.  Some important processes for the manufacture of machinery are forging, stamping, bending, forming, and machining that are used to shape individual pieces of metal.  Processes, such as welding and assembling are used to join separate parts together.  Although these processes are similar to those used in metal fabricating establishments, machinery manufacturing is different because it typically employs multiple metal forming processes in manufacturing the various parts of the machine.  Moreover, complex assembly operations are an inherent part of the production process. In general, design considerations are very important in machinery production.  Establishments specialize in making machinery designed for particular applications.  Thus, design is considered to be part of the production process for the purpose of implementing NAICS.  The NAICS structure reflects this by defining industries and industry groups that make machinery for different applications.  A broad distinction exists between machinery that is generally used in a variety of industrial applications (i.e., general purpose machinery) and machinery that is designed to be used in a particular industry (i.e., special purpose machinery).  Three industry groups consist of special purpose machinery--Agriculture, Construction, and Mining Machinery Manufacturing; Industrial Machinery Manufacturing; and Commercial and Service Industry Machinery Manufacturing.  The other industry groups make general purpose machinery: Ventilation, Heating, Air-Conditioning, and Commercial Refrigeration Equipment Manufacturing; Metalworking Machinery Manufacturing; Engine, Turbine, and Power Transmission Equipment Manufacturing; and Other General Purpose Machinery Manufacturing.",
    industries: ["machinery-manufacturing"],
    name: "Machinery Manufacturing",
    slug: "machinery-manufacturing"
  },
  "334": {
    description: "Industries in the Computer and Electronic Product Manufacturing subsector group establishments that manufacture computers, computer peripherals, communications equipment, and similar electronic products, and establishments that manufacture components for such products.  The Computer and Electronic Product Manufacturing industries are combined in the hierarchy of NAICS because of their economic significance to the economies of all three North American countries.  For industries in this subsector, the manufacturing processes are fundamentally different from the manufacturing processes of other machinery and equipment.  The design and use of integrated circuits and the application of highly specialized miniaturization technologies are common elements in the production technologies of the Computer and Electronic Product Manufacturing subsector.",
    industries: ["computers-and-electronics-manufacturing"],
    name: "Computer and Electronic Product Manufacturing",
    slug: "computer-and-electronic-product-manufacturing"
  },
  "335": {
    description: "Industries in the Electrical Equipment, Appliance, and Component Manufacturing subsector manufacture products that generate, distribute, and use electrical power.  Electric Lighting Equipment Manufacturing establishments produce lighting fixtures, electric light bulbs, and parts (except glass parts and light emitting diodes (LEDs)), and other lighting equipment.  Household Appliance Manufacturing establishments make both small and major electrical appliances and parts.  Electrical Equipment Manufacturing establishments make goods, such as electric motors, generators, transformers, and switchgear apparatus.  Other Electrical Equipment and Component Manufacturing establishments make devices for storing electrical power (e.g., batteries), for transmitting electricity (e.g., insulated wire), and wiring devices (e.g., electrical outlets, fuse boxes, and light switches).",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Electrical Equipment, Appliance, and Component Manufacturing",
    slug: "electrical-equipment-appliance-and-component-manufacturing"
  },
  "336": {
    description: "Industries in the Transportation Equipment Manufacturing subsector produce equipment for transporting people and goods.  Transportation equipment is a type of machinery.  An entire subsector is devoted to this activity because of the significance of its economic size in all three North American countries. Establishments in this subsector utilize production processes similar to those of other machinery manufacturing establishments\u2014bending, forming, welding, machining, and assembling metal or plastic parts into components and finished products.  However, the assembly of components and subassemblies and their further assembly into finished vehicles tends to be a more common production process in this subsector than in the Machinery Manufacturing subsector. NAICS has industry groups for the manufacture of equipment for each mode of transport\u2014road, rail, air, and water.  Parts for motor vehicles warrant a separate industry group because of their importance and because they require less assembly than complete vehicles. Land use motor vehicle equipment not designed for highway operation (e.g., agricultural equipment, construction equipment, and material handling equipment) is classified in the appropriate NAICS subsector based on the type and use of the equipment.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Transportation Equipment Manufacturing",
    slug: "transportation-equipment-manufacturing"
  },
  "337": {
    description: "Industries in the Furniture and Related Product Manufacturing subsector make furniture and related articles, such as mattresses, window blinds, cabinets, and fixtures.  The processes used in the manufacture of furniture include the cutting, bending, molding, laminating, and assembly of such materials as wood, metal, glass, plastics, and rattan.  However, the production process for furniture is not solely bending metal, cutting and shaping wood, or extruding and molding plastics.  Design and fashion trends play an important part in the production of furniture.  The integrated design of the article for both esthetic and functional qualities is also a major part of the process of manufacturing furniture.  Design services may be performed by the furniture establishment's work force or may be purchased from industrial designers. Furniture may be made of any material, but the most common ones used in North America are metal and wood.  Furniture manufacturing establishments may specialize in making articles primarily from one material.  Some of the equipment required to make a wooden table, for example, is different from that used to make a metal one.  However, furniture is usually made from several materials.  A wooden table might have metal brackets, and a wooden chair a fabric or plastics seat.  Therefore, in NAICS, furniture initially is classified based on the type of furniture (application for which it is designed) rather than the material used.  For example, an upholstered sofa is treated as household furniture, although it may also be used in hotels or offices. When classifying furniture according to the component material from which it is made, furniture made from more than one material is classified based on the material used in the frame, or if there is no frame, the predominant component material.  Upholstered household furniture (excluding kitchen and dining room chairs with upholstered seats) is classified without regard to the frame material.  Kitchen or dining room chairs with upholstered seats are classified according to the frame material. Furniture may be made on a stock or custom basis and may be shipped assembled or unassembled (i.e., knockdown).  The manufacture of furniture parts and frames is included in this subsector. Some of the processes used in furniture manufacturing are similar to processes that are used in other segments of manufacturing.  For example, cutting and assembly occurs in the production of wood trusses that are classified in Subsector 321, Wood Product Manufacturing.  However, the multiple processes that distinguish wood furniture manufacturing from wood product manufacturing warrant inclusion of wooden furniture manufacturing in the Furniture and Related Product Manufacturing subsector.  Metal furniture manufacturing uses techniques that are also employed in the manufacturing of roll formed products classified in Subsector 332, Fabricated Metal Product Manufacturing.  The molding process for plastics furniture is similar to the molding of other plastics products.  However, plastics furniture producing establishments tend to specialize in furniture. NAICS attempts to keep furniture manufacturing together, but there are notable exceptions: concrete, ceramic, or stone furniture; seating for transportation equipment; and specialized hospital furniture (e.g., hospital beds and operating tables).  These are classified in Subsector 327, Nonmetallic Mineral Product Manufacturing; Subsector 336, Transportation Equipment Manufacturing; and Subsector 339, Miscellaneous Manufacturing, respectively.",
    industries: ["furniture-and-home-furnishings-manufacturing"],
    name: "Furniture and Related Product Manufacturing",
    slug: "furniture-and-related-product-manufacturing"
  },
  "339": {
    description: "Industries in the Miscellaneous Manufacturing subsector make a wide range of products that cannot readily be classified in specific NAICS subsectors in manufacturing.  Processes used by these establishments vary significantly, both among and within industries.  For example, a variety of manufacturing processes are used in manufacturing sporting and athletic goods that include products such as tennis rackets and golf balls.  The processes for these products differ from each other, and the processes differ significantly from the fabrication processes used in making dolls or toys, the melting and shaping of precious metals to make jewelry, and the bending, forming, and assembly used in making medical products. The industries in this subsector are defined by what is made rather than how it is made.  Although individual establishments might be appropriately classified elsewhere in the NAICS structure, for historical continuity, these product-based industries were maintained.  In most cases, no one process or material predominates for an industry. Establishments in this subsector manufacture products as diverse as medical equipment and supplies, jewelry, sporting goods, toys, and office supplies.",
    industries: ["manufacturing"],
    name: "Miscellaneous Manufacturing",
    slug: "miscellaneous-manufacturing"
  },
  "423": {
    description: "Industries in the Merchant Wholesalers, Durable Goods subsector sell capital or durable goods to other businesses.  Merchant wholesalers generally take title to the goods that they sell; in other words, they buy and sell goods on their own account.  Durable goods are new or used items generally with a normal life expectancy of three years or more.  Durable goods merchant wholesale trade establishments are engaged in wholesaling products, such as motor vehicles, furniture, construction materials, machinery and equipment (including household-type appliances), metals and minerals (except petroleum), sporting goods, toys and hobby goods, recyclable materials, and parts. Agents and brokers primarily engaged in wholesaling durable goods, generally on a commission or fee basis, are classified in Subsector 425, Wholesale Trade Agents and Brokers.",
    industries: ["wholesale"],
    name: "Merchant Wholesalers, Durable Goods",
    slug: "merchant-wholesalers-durable-goods"
  },
  "424": {
    description: "Industries in the Merchant Wholesalers, Nondurable Goods subsector sell nondurable goods to other businesses.  Nondurable goods are items generally with a normal life expectancy of less than three years.  Nondurable goods merchant wholesale trade establishments are engaged in wholesaling products, such as paper and paper products, chemicals and chemical products, drugs, textiles and textile products, apparel, footwear, groceries, farm products, petroleum and petroleum products, alcoholic beverages, books, magazines, newspapers, flowers and nursery stock, and tobacco products. The detailed industries within the subsector are organized in the classification structure based on the products sold. Agents and brokers primarily engaged in wholesaling nondurable goods, generally on a commission or fee basis, are classified in Subsector 425, Wholesale Trade Agents and Brokers.",
    industries: ["wholesale"],
    name: "Merchant Wholesalers, Nondurable Goods",
    slug: "merchant-wholesalers-nondurable-goods"
  },
  "425": {
    description: "The Wholesale Trade Agents and Brokers subsector groups establishments that arrange for the sale of goods owned by others, generally on a fee or commission basis.  They act on behalf of the buyers and sellers of goods to facilitate wholesale trade.",
    industries: ["internet-marketplace-platforms"],
    name: "Wholesale Trade Agents and Brokers",
    slug: "wholesale-trade-agents-and-brokers"
  },
  "441": {
    description: "Industries in the Motor Vehicle and Parts Dealers subsector retail motor vehicles and parts.  Establishments in this subsector often operate from a showroom and/or an open lot where the vehicles are on display.  The display of vehicles and the related parts require little by way of display equipment.  The personnel generally include both the sales and sales support staff familiar with the requirements for registering and financing a vehicle as well as a staff of parts experts and mechanics trained to provide repair and maintenance services for the vehicles.  Specific industries included in this subsector identify the type of vehicle being retailed. Sales of capital or durable nonconsumer goods, such as medium- and heavy-duty trucks, are always included in wholesale trade.",
    industries: ["retail"],
    name: "Motor Vehicle and Parts Dealers",
    slug: "motor-vehicle-and-parts-dealers"
  },
  "444": {
    description: "Industries in the Building Material and Garden Equipment and Supplies Dealers subsector retail new building materials, hardware, paint, and garden and outdoor power equipment and supplies.  Establishments in this subsector with fixed point-of-sale locations, including home centers and retail lumber yards, may display merchandise either indoors or outdoors under covered areas.  The staff is usually knowledgeable in the use of the specific products being retailed in the construction, repair, and maintenance of the home and associated grounds.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Building Material and Garden Equipment and Supplies Dealers",
    slug: "building-material-and-garden-equipment-and-supplies-dealers"
  },
  "445": {
    description: "Industries in the Food and Beverage Retailers subsector primarily retail general or specialized lines of food and beverage products.  Establishments in this subsector with fixed point-of-sale locations have special equipment (e.g., freezers, refrigerated display cases, refrigerators) for displaying food and beverage products and have staff trained in the processing of food products to guarantee the proper storage and sanitary conditions required by regulatory authority.  Vending machine operators are also included in this subsector.",
    industries: ["food-and-beverage-retail"],
    name: "Food and Beverage Retailers",
    slug: "food-and-beverage-retailers"
  },
  "449": {
    description: "Industries in the Furniture, Home Furnishings, Electronics, and Appliance Retailers subsector retail new furniture, home furnishings, electronics, and appliances.  Establishments in this subsector with fixed point-of-sale locations may operate from showrooms or other locations that have substantial areas for the presentation or demonstration of their products.  Establishments in this subsector may provide incidental services, including interior decorating, product assembly, installation, or repair services.",
    name: "Furniture, Home Furnishings, Electronics, and Appliance Retailers",
    slug: "furniture-home-furnishings-electronics-and-appliance-retailers",
    industries: ["retail-appliances-electrical-and-electronic-equipment", "retail-furniture-and-home-furnishings"]
  },
  "455": {
    description: "Industries in the General Merchandise Retailers subsector retail new general merchandise.  This subsector includes new and used general merchandise auction retailers and establishments generally known as department stores, warehouse clubs, superstores, or supercenters.",
    industries: ["retail"],
    name: "General Merchandise Retailers",
    slug: "general-merchandise-retailers"
  },
  "456": {
    description: "Industries in the Health and Personal Care Retailers subsector retail health and personal care merchandise.  Establishments in this subsector are characterized principally by the products they retail, and some health and personal care retailers have specialized staff including pharmacists, opticians, and other professionals engaged in retailing, advising customers, and/or fitting the product sold to the customer's needs.",
    industries: ["retail-health-and-personal-care-products"],
    name: "Health and Personal Care Retailers",
    slug: "health-and-personal-care-retailers"
  },
  "457": {
    description: "Industries in the Gasoline Stations and Fuel Dealers subsector retail automotive fuels (e.g., gasoline, diesel fuel, gasohol, alternative fuels) and automotive oils, without or in combination with convenience store items; or retail heating oil, liquefied petroleum (LP) gas, and other fuels via direct selling (i.e., home delivery).  Gasoline stations have specialized equipment for storing and dispensing automotive fuels.",
    industries: ["retail-gasoline"],
    name: "Gasoline Stations and Fuel Dealers",
    slug: "gasoline-stations-and-fuel-dealers"
  },
  "458": {
    description: "Industries in the Clothing, Clothing Accessories, Shoe, and Jewelry Retailers subsector retail new clothing, clothing accessories, shoes, jewelry, luggage, and leather goods.",
    industries: ["retail-apparel-and-fashion"],
    name: "Clothing, Clothing Accessories, Shoe, and Jewelry Retailers",
    slug: "clothing-clothing-accessories-shoe-and-jewelry-retailers"
  },
  "459": {
    description: "Industries in the Sporting Goods, Hobby, Musical Instrument, Book, and Miscellaneous Retailers subsector retail new sporting goods; new toys, games, and hobby supplies; new sewing supplies and needlework accessories; new musical instruments; new books, newspapers, magazines, and other periodicals; and other specialized lines of merchandise, such as cut flowers and floral arrangements, new office supplies and stationery, new gifts, novelty merchandise, and souvenirs, used merchandise, pets and pet supplies, art, new or used manufactured (mobile) homes, and tobacco, electronic cigarettes, and other smoking supplies.",
    industries: ["retail"],
    name: "Sporting Goods, Hobby, Musical Instrument, Book, and Miscellaneous Retailers",
    slug: "sporting-goods-hobby-musical-instrument-book-and-miscellaneous-retailers"
  },
  "481": {
    description: "Industries in the Air Transportation subsector provide air transportation of passengers and/or cargo using aircraft, such as airplanes, helicopters, and spacecraft.  The subsector distinguishes scheduled from nonscheduled air transportation.  Scheduled air carriers fly regular routes on regular schedules and operate even if flights are only partially loaded.  Nonscheduled carriers often operate during nonpeak time slots at busy airports.  These establishments have more flexibility with respect to choice of airport, hours of operation, load factors, and similar operational characteristics.  Nonscheduled carriers provide chartered air transportation of passengers, cargo, or specialty flying services.  Specialty flying services establishments use general purpose aircraft to provide a variety of specialized flying services. Scenic and sightseeing air transportation and air courier services are not included in this subsector but are included in Subsector 487, Scenic and Sightseeing Transportation, and in Subsector 492, Couriers and Messengers, respectively.  Although these activities may use aircraft, they are different from the activities included in air transportation.  Air sightseeing does not usually involve place-to-place transportation; the passenger's flight (e.g., balloon ride, aerial sightseeing) typically starts and ends at the same location.  Courier services (individual package or cargo delivery) include more than air transportation; road transportation is usually required to deliver the cargo to the intended recipient.",
    industries: ["airlines-and-aviation"],
    name: "Air Transportation",
    slug: "air-transportation"
  },
  "482": {
    description: "Industries in the Rail Transportation subsector provide rail transportation of passengers and/or cargo using railroad rolling stock.  The railroads in this subsector primarily either operate on networks, with physical facilities, labor force, and equipment spread over an extensive geographic area, or operate over a short distance on a local rail line. Scenic and sightseeing rail transportation and street railroads, commuter rail, and rapid transit are not included in this subsector but are included in Subsector 487, Scenic and Sightseeing Transportation, and Subsector 485, Transit and Ground Passenger Transportation, respectively.  Although these activities use railroad rolling stock, they are different from the activities included in rail transportation.  Sightseeing and scenic railroads do not usually involve place-to-place transportation; the passenger's trip typically starts and ends at the same location.  Commuter railroads operate in a manner more consistent with local and urban transit and are often part of integrated transit systems.",
    industries: ["rail-transportation"],
    name: "Rail Transportation",
    slug: "rail-transportation"
  },
  "483": {
    description: "Industries in the Water Transportation subsector provide water transportation of passengers and cargo using watercraft, such as ships, barges, and boats. The subsector is composed of two industry groups: (1) one for deep sea, coastal, and Great Lakes water transportation; and (2) one for inland water transportation.  This split typically reflects the difference in equipment used. Scenic and sightseeing water transportation services are not included in this subsector but are included in Subsector 487, Scenic and Sightseeing Transportation.  Although these activities use watercraft, they are different from the activities included in water transportation.  Water sightseeing does not usually involve place-to-place transportation; the passenger's trip starts and ends at the same location.",
    industries: ["maritime-transportation"],
    name: "Water Transportation",
    slug: "water-transportation"
  },
  "484": {
    description: "Industries in the Truck Transportation subsector provide over-the-road transportation of cargo using motor vehicles, such as trucks and tractor trailers.  The subsector is subdivided into general freight trucking and specialized freight trucking.  This distinction reflects differences in equipment used, type of load carried, scheduling, terminal, and other networking services.  General freight transportation establishments handle a wide variety of general commodities, generally palletized, and transported in a container or van trailer.  Specialized freight transportation is the transportation of cargo that, because of size, weight, shape, or other inherent characteristics, requires specialized equipment for transportation. Each of these industry groups is further subdivided based on distance traveled.  Local trucking establishments primarily carry goods within a single metropolitan area and its adjacent nonurban areas.  Long-distance trucking establishments carry goods between metropolitan areas. The Specialized Freight Trucking industry group includes a separate industry for Used Household and Office Goods Moving. The household and office goods movers are separated because of the substantial network of establishments that has developed to deal with local and long-distance moving and the associated storage.  In this area, the same establishment provides both local and long-distance services, while other specialized freight establishments generally limit their services to either local or long-distance hauling.",
    industries: ["truck-transportation"],
    name: "Truck Transportation",
    slug: "truck-transportation"
  },
  "485": {
    description: "Industries in the Transit and Ground Passenger Transportation subsector include a variety of passenger transportation activities, such as urban transit systems; chartered bus, school bus, and interurban bus transportation; and taxis.  These activities are distinguished based primarily on such production process factors as vehicle types, routes, and schedules. In this subsector, the principal splits identify scheduled transportation as separate from nonscheduled transportation.  The scheduled transportation industry groups are Urban Transit Systems, Interurban and Rural Bus Transportation, and School and Employee Bus Transportation.  The nonscheduled industry groups are the Charter Bus Industry and Taxi and Limousine Service.  The Other Transit and Ground Passenger Transportation industry group includes both scheduled and nonscheduled transportation. Scenic and sightseeing ground transportation services are not included in this subsector but are included in Subsector 487, Scenic and Sightseeing Transportation.  Sightseeing does not usually involve place-to-place transportation; the passenger's trip starts and ends at the same location.",
    industries: ["ground-passenger-transportation"],
    name: "Transit and Ground Passenger Transportation",
    slug: "transit-and-ground-passenger-transportation"
  },
  "486": {
    description: "Industries in the Pipeline Transportation subsector use transmission pipelines to transport products, such as crude oil, natural gas, refined petroleum products, and slurry.  Industries are identified based on the products transported (i.e., pipeline transportation of crude oil, natural gas, refined petroleum products, and other products). The Pipeline Transportation of Natural Gas industry includes the storage of natural gas because the storage is usually done by the pipeline establishment and because a pipeline is inherently a network in which all the nodes are interdependent.",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation",
    slug: "pipeline-transportation"
  },
  "487": {
    description: "Industries in the Scenic and Sightseeing Transportation subsector utilize transportation equipment to provide recreation and entertainment.  These activities have a production process distinct from passenger transportation carried out for the purpose of other types of for-hire transportation.  This process does not emphasize efficient transportation; in fact, such activities often use obsolete vehicles, such as steam trains, to provide some extra ambience.  The activity is local in nature, usually involving a same-day return to the point of departure. The Scenic and Sightseeing Transportation subsector is separated into three industries based on the mode: land, water, and other. Activities that are recreational in nature and involve participation by the customer, such as white water rafting, are generally excluded from this subsector, unless they impose an impact on part of the transportation system.  Charter boat fishing, for example, is included in the Scenic and Sightseeing Transportation, Water industry.",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation",
    slug: "scenic-and-sightseeing-transportation"
  },
  "488": {
    description: "Industries in the Support Activities for Transportation subsector provide services which support transportation.  These services may be provided to transportation carrier establishments or to the general public.  This subsector includes a wide array of establishments, including air traffic control services, marine cargo handling, and motor vehicle towing. The Support Activities for Transportation subsector includes services to transportation, separated by type of mode serviced.  The Support Activities for Rail Transportation industry includes services to the rail industry (e.g., railroad switching and terminal establishments).  Ship repair and maintenance services not done in a shipyard are included in the Other Support Activities for Water Transportation industry.  An example would be floating drydock services in a harbor. Excluded from this subsector are establishments primarily engaged in providing factory conversion and overhaul of transportation equipment, which are classified in Subsector 336, Transportation Equipment Manufacturing.  Establishments primarily engaged in providing rental and leasing of transportation equipment without operator are classified in Subsector 532, Rental and Leasing Services. Also, establishments primarily engaged in providing travel arrangement and reservation services are classified in Industry Group 5615, Travel Arrangement and Reservation Services.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Support Activities for Transportation",
    slug: "support-activities-for-transportation"
  },
  "491": {
    description: "The Postal Service subsector includes the activities of the National Post Office and its subcontractors operating under a universal service obligation to provide mail services, and using the infrastructure required to fulfill that obligation.  These services include delivering letters and small parcels.  These articles can be described as those that can be handled by one person without using special equipment.  This allows the collection, pick-up, and delivery operations to be done with limited labor costs and minimal equipment.  Sorting and transportation activities, where necessary, are generally mechanized.  The restriction to small parcels distinguishes these establishments from those in the transportation industries.  These establishments may also provide express delivery services using the infrastructure established for provision of basic mail services. The traditional activity of the National Postal Service is described in this subsector.  Subcontractors include rural post offices on contract to the Postal Service. Bulk transportation of mail on contract to the Postal Service is not included here, because it is usually done by transportation establishments that carry other customers' cargo as well.  Establishments that provide courier and express delivery services without operating under a universal service obligation are classified in Subsector 492, Couriers and Messengers.",
    industries: ["postal-services"],
    name: "Postal Service",
    slug: "postal-service"
  },
  "492": {
    description: "Industries in the Couriers and Messengers subsector provide intercity, local, and/or international delivery of parcels and documents (including express delivery services) without operating under a universal service obligation.  These articles may originate in the U.S. but be delivered to another country and can be described as those that may be handled by one person without using special equipment.  This allows the collection, pick-up, and delivery operations to be done with limited labor costs and minimal equipment.  Sorting and transportation activities, where necessary, are generally mechanized.  The restriction to small parcels partly distinguishes these establishments from those in the transportation industries.  The complete network of courier services establishments also distinguishes these transportation services from local messenger and delivery establishments in this subsector.  This includes the establishments that perform intercity transportation as well as establishments that, under contract to them, perform local pick-up and delivery.  Messengers, which usually deliver within a metropolitan or single urban area, may use bicycle, foot, car, small truck, or van.",
    industries: ["freight-and-package-transportation"],
    name: "Couriers and Messengers",
    slug: "couriers-and-messengers"
  },
  "493": {
    description: "Industries in the Warehousing and Storage subsector are primarily engaged in operating warehousing and storage facilities for general merchandise, refrigerated goods, and other warehouse products.  These establishments provide facilities to store goods.  They do not sell the goods they handle.  These establishments take responsibility for storing the goods and keeping them secure.  They may also provide a range of services, often referred to as logistics services, related to the distribution of goods.  Logistics services can include labeling, breaking bulk, inventory control and management, light assembly, order entry and fulfillment, packaging, pick and pack, price marking and ticketing, and transportation arrangement.  However, establishments in this industry group always provide warehousing or storage services in addition to any logistic services.  Furthermore, the warehousing or storage of goods must be more than incidental to the performance of services, such as price marking. Bonded warehousing and storage services and warehouses located in free trade zones are included in the industries of this subsector.",
    industries: ["warehousing-and-storage"],
    name: "Warehousing and Storage",
    slug: "warehousing-and-storage"
  },
  "512": {
    description: "Industries in the Motion Picture and Sound Recording Industries subsector group establishments involved in the production and distribution of motion pictures and sound recordings.  While producers and distributors of motion pictures and sound recordings issue works for sale as traditional publishers do, the processes are sufficiently different to warrant placing establishments engaged in these activities in a separate subsector.  Production is typically a complex process that involves several distinct types of establishments that are engaged in activities, such as contracting with performers, creating the film or sound content, and providing technical postproduction services.  Film distribution is often to exhibitors, such as theaters and broadcasters, rather than through the wholesale and retail distribution chain.  When the product is in a mass-produced form, NAICS treats production and distribution as the major economic activity as it does in the Publishing Industries subsector, rather than as a subsidiary activity to the manufacture of such products. This subsector does not include establishments primarily engaged in the wholesale distribution of video and sound recordings, such as compact discs and audio tapes; these establishments are included in the Wholesale Trade sector.  Reproduction of video and sound recordings that is carried out separately from establishments engaged in production and distribution is treated in NAICS as a manufacturing activity.  Establishments that primarily acquire the rights to distribute video and sound recordings to the public via television or radio broadcast or streaming distribution services are classified in Subsector 516, Broadcasting and Content Providers.  Establishments using facilities and infrastructure that they operate to distribute cable and satellite television subscription programming are included in Subsector 517, Telecommunications.",
    industries: ["movies-videos-and-sound"],
    name: "Motion Picture and Sound Recording Industries",
    slug: "motion-picture-and-sound-recording-industries"
  },
  "513": {
    description: "Industries in the Publishing Industries subsector group establishments engaged in publishing newspapers, magazines, other periodicals, books, directories, and software.  In general, establishments known as publishers issue copies of works for which they usually possess copyright.  Works may be in one or more formats including print form, CD-ROM, proprietary electronic networks, or exclusively on the Internet.  Publishers may publish works originally created by others for which they have obtained the rights and/or works that they have created in-house.  Publishers may publish only and license rights to others to distribute their content, or they may publish and distribute content they create or own.  Software publishing is included here because the activity, creation of a copyrighted product and bringing it to market, is equivalent to the creation process for other types of intellectual products. In NAICS, publishing--the reporting, writing, editing, and other processes that are required to create an edition of a newspaper, for example--is treated as a major economic activity in its own right, rather than as a subsidiary activity to a manufacturing activity, printing.  Thus, publishing is classified in the Information sector; whereas, printing is in the Manufacturing sector.  The Publishing Industries subsector excludes printed products, such as manifold business forms and appointment books, for which information is not the essential component.  Establishments producing these items are included in Subsector 323, Printing and Related Support Activities. Reproduction of prepackaged software is treated in NAICS as a manufacturing activity, and custom design of software to client specifications is included in the Professional, Scientific, and Technical Services sector.  These distinctions arise because of the different ways that software is created, reproduced, and distributed. Music publishers and establishments primarily engaged in the production, or production and distribution, of motion pictures and sound recordings are included in Subsector 512, Motion Picture and Sound Recording Industries.  Establishments not engaged in publishing and exclusively obtaining rights from publishers to broadcast and distribute content are included in Subsector 516, Broadcasting and Content Providers.",
    industries: ["book-and-periodical-publishing"],
    name: "Publishing Industries",
    slug: "publishing-industries"
  },
  "516": {
    description: "Industries in the Broadcasting and Content Providers subsector include establishments that create content or acquire the right to distribute content and subsequently broadcast or distribute that content.  The industry groups (Radio and Television Broadcasting Stations and Media Streaming Distribution Services, Social Networks, and Other Media Networks and Content Providers) are based on differences in the methods of communication and the nature of services provided.  The Radio and Television Broadcasting Stations industry group includes establishments that operate radio or television broadcasting stations for the programming and transmission of programs to the public.  Programming may originate in their own studio, from an affiliated network, or from external sources.  The Media Streaming Distribution Services, Social Networks, and Other Media Networks and Content Providers industry group includes establishments providing media streaming distribution services, operating social network sites, operating media broadcasting and cable television networks, and supplying information, such as news reports, articles, pictures, and features, to the news media.  The establishments classified in this subsector are often engaged in the production and purchase of programs and other textual, audio, and/or video content, and they typically generate revenues from the sale of advertising space and air time, subscriptions, donations, subsidies, and/or the sale of programs. Establishments operating telecommunications facilities and infrastructure and distributing audio and video programming, including cable and satellite television subscription programming, are included in Subsector 517, Telecommunications.  Establishments primarily engaged as independent contractors in the installation and maintenance of broadcasting systems are classified in Sector 23, Construction.  Establishments primarily engaged in the production, or production and distribution, of motion pictures and sound recordings are included in Subsector 512, Motion Picture and Sound Recording Industries.",
    industries: ["broadcast-media-production-and-distribution"],
    name: "Broadcasting and Content Providers",
    slug: "broadcasting-and-content-providers"
  },
  "517": {
    description: "Industries in the Telecommunications subsector group establishments that provide telecommunications and the services related to that activity (e.g., telephony, including Voice over Internet Protocol (VoIP); cable and satellite television distribution services; Internet access; telecommunications reselling services).  The Telecommunications subsector is primarily engaged in operating and/or providing access to facilities for the transmission of voice, data, text, sound, and video.  Transmission facilities may be based on a single technology or a combination of technologies. Establishments primarily engaged as independent contractors in the installation and maintenance of telecommunications systems are classified in Sector 23, Construction.",
    industries: ["telecommunications"],
    name: "Telecommunications",
    slug: "telecommunications"
  },
  "518": {
    description: "The Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services subsector groups establishments that provide computing infrastructure, data processing services, Web hosting services (except software publishing), and related services, including streaming support services (except streaming distribution services).",
    industries: ["data-infrastructure-and-analytics"],
    name: "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
    slug: "computing-infrastructure-providers-data-processing-web-hosting-and-related-services"
  },
  "519": {
    description: "Industries in the Web Search Portals, Libraries, Archives, and Other Information Services subsector group establishments supplying information, storing and providing access to information, searching and retrieving information, and operating Web sites that use search engines to allow for searching information on the Internet.  The main components of the subsector are libraries, archives, and Web search portals.",
    industries: ["information-services"],
    name: "Web Search Portals, Libraries, Archives, and Other Information Services",
    slug: "web-search-portals-libraries-archives-and-other-information-services"
  },
  "521": {
    description: "The Monetary Authorities-Central Bank subsector groups establishments that engage in performing central banking functions, such as issuing currency, managing the Nation's money supply and international reserves, holding deposits that represent the reserves of other banks and other central banks, and acting as a fiscal agent for the central government.",
    industries: ["financial-services"],
    name: "Monetary Authorities-Central Bank",
    slug: "monetary-authorities-central-bank"
  },
  "522": {
    description: "Industries in the Credit Intermediation and Related Activities subsector group establishments that (1) lend funds raised from depositors; (2) lend funds raised from credit market borrowing; or (3) facilitate the lending of funds or issuance of credit by engaging in such activities as mortgage and loan brokerage, clearinghouse and reserve services, and check cashing services.",
    industries: ["credit-intermediation"],
    name: "Credit Intermediation and Related Activities",
    slug: "credit-intermediation-and-related-activities"
  },
  "523": {
    description: "Industries in the Securities, Commodity Contracts, and Other Financial Investments and Related Activities subsector group establishments that are primarily engaged in one of the following: (1) underwriting securities issues and/or making markets for securities and commodities; (2) acting as agents (i.e., brokers) between buyers and sellers of securities and commodities; (3) providing securities and commodity exchange services; and (4) providing other services, such as managing portfolios of assets; providing investment advice; and trust, fiduciary, and custody services.",
    industries: ["capital-markets"],
    name: "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
    slug: "securities-commodity-contracts-and-other-financial-investments-and-related-activities"
  },
  "524": {
    description: "Industries in the Insurance Carriers and Related Activities subsector group establishments that are primarily engaged in one of the following: (1) underwriting (assuming the risk, assigning premiums, and so forth) annuities and insurance policies or (2) facilitating such underwriting by selling insurance policies and by providing other insurance and employee benefit related services.",
    industries: ["insurance"],
    name: "Insurance Carriers and Related Activities",
    slug: "insurance-carriers-and-related-activities"
  },
  "525": {
    description: "Industries in the Funds, Trusts, and Other Financial Vehicles subsector group legal entities (i.e., funds, plans, and/or programs) organized to pool securities or other assets on behalf of shareholders or beneficiaries of employee benefit or other trust funds.  The portfolios are customized to achieve specific investment characteristics, such as diversification, risk, rate of return, and price volatility.  These entities earn interest, dividends, and other investment income, but have little or no employment and no revenue from the sale of services.  Establishments with employees devoted to the management of funds are classified in Industry Group 5239, Other Financial Investment Activities. Establishments primarily engaged in holding the securities of (or other equity interests in) other firms are classified in Sector 55, Management of Companies and Enterprises.  Equity real estate investment trusts (REITs) that are primarily engaged in leasing buildings, dwellings, or other real estate property to others are classified in Subsector 531, Real Estate.",
    industries: ["funds-and-trusts"],
    name: "Funds, Trusts, and Other Financial Vehicles",
    slug: "funds-trusts-and-other-financial-vehicles"
  },
  "531": {
    description: "Industries in the Real Estate subsector group establishments primarily engaged in renting or leasing real estate to others; managing real estate for others; selling, buying, or renting real estate for others; and providing other real estate related services, such as appraisal services. This subsector includes equity real estate investment trusts (REITs) primarily engaged in leasing buildings, dwellings, or other real estate property to others.  Mortgage REITs are classified in Subsector 525, Funds, Trusts, and Other Financial Vehicles. Establishments primarily engaged in subdividing and developing unimproved real estate and constructing buildings for sale are classified in Subsector 236, Construction of Buildings.  Establishments primarily engaged in subdividing and improving raw land for subsequent sale to builders are classified in Subsector 237, Heavy and Civil Engineering Construction.",
    industries: ["leasing-residential-real-estate"],
    name: "Real Estate",
    slug: "real-estate"
  },
  "532": {
    description: "Industries in the Rental and Leasing Services subsector include establishments that provide a wide array of tangible goods, such as automobiles, computers, consumer goods, and industrial machinery and equipment, to customers in return for a periodic rental or lease payment. The subsector includes two main types of establishments: (1) those that are engaged in renting consumer goods and equipment and (2) those that are engaged in leasing machinery and equipment often used for business operations.  The first type typically operates from a retail-like or storefront facility and maintains inventories of goods that are rented for short periods of time.  The latter type typically does not operate from retail-like locations or maintain inventories, and usually offers longer-term leases.  These establishments work directly with clients to enable them to acquire the use of equipment on a lease basis, or they work with equipment vendors or dealers to support the marketing of equipment to their customers under lease arrangements.  Equipment lessors generally structure lease contracts to meet the specialized needs of their clients and use their remarketing expertise to find other users for previously leased equipment.  Establishments that provide operating and capital (i.e., finance) leases are included in this subsector. Establishments primarily engaged in leasing in combination with providing loans are classified in Sector 52, Finance and Insurance.  Establishments primarily engaged in leasing real property are classified in Subsector 531, Real Estate.  Establishments primarily engaged in renting or leasing equipment with operators are classified in various subsectors of NAICS depending on the nature of the services provided (e.g., transportation, construction, agriculture).  These activities are excluded from this subsector since the client is paying for the expertise and knowledge of the equipment operator, in addition to the rental of the equipment.  In many cases, such as the rental of heavy construction equipment, the operator is essential to operate the equipment.  Likewise, since the provision of crop harvesting services includes both the equipment and operator, it is included in Subsector 115, Support Activities for Agriculture and Forestry.  The rental or leasing of copyrighted works is classified in Sector 51, Information, and the rental or leasing of nonfinancial intangible assets, such as patents, trademarks, and/or licensing agreements, is classified in Subsector 533, Lessors of Nonfinancial Intangible Assets (except Copyrighted Works).",
    industries: ["equipment-rental-services"],
    name: "Rental and Leasing Services",
    slug: "rental-and-leasing-services"
  },
  "533": {
    description: "Industries in the Lessors of Nonfinancial Intangible Assets (except Copyrighted Works) subsector include establishments primarily engaged in assigning rights to assets, such as patents, trademarks, brand names, and/or franchise agreements, for which a royalty payment or licensing fee is paid to the asset holder.  Establishments in this subsector own the patents, trademarks, and/or franchise agreements that they allow others to use or reproduce for a fee and may or may not have created those assets. Establishments that allow franchisees the use of the franchise name, contingent on the franchisee buying products or services from the franchisor, are classified elsewhere. Excluded from this subsector are establishments primarily engaged in leasing real property and establishments primarily engaged in leasing tangible assets, such as automobiles, computers, consumer goods, and industrial machinery and equipment.  These establishments are classified in Subsector 531, Real Estate, and Subsector 532, Rental and Leasing Services, respectively.",
    industries: ["real-estate-and-equipment-rental-services"],
    name: "Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)",
    slug: "lessors-of-nonfinancial-intangible-assets-except-copyrighted-works"
  },
  "541": {
    description: "Industries in the Professional, Scientific, and Technical Services subsector group establishments engaged in processes where human capital is the major input.  These establishments make available the knowledge and skills of their employees, often on an assignment basis, where an individual or team is responsible for the delivery of services to the client.  The individual industries of this subsector are defined on the basis of the particular expertise and training of the services provider. The distinguishing feature of the Professional, Scientific, and Technical Services subsector is the fact that most of the industries grouped in it have production processes that are almost wholly dependent on worker skills.  Thus, the establishments classified in this subsector sell expertise.  Much of the expertise requires degrees, though not in every case.",
    industries: ["professional-services"],
    name: "Professional, Scientific, and Technical Services",
    slug: "professional-scientific-and-technical-services"
  },
  "551": {
    description: "Industries in the Management of Companies and Enterprises subsector include three main types of establishments: (1) those that hold the securities of (or other equity interests in) companies and enterprises; (2) those (except government establishments) that administer, oversee, and manage other establishments of the company or enterprise but do not hold the securities of these establishments; and (3) those that both administer, oversee, and manage other establishments of the company or enterprise and hold the securities of (or other equity interests in) these establishments.  Those establishments that administer, oversee, and manage normally undertake the strategic or organizational planning and decision-making role of the company or enterprise.",
    industries: ["holding-companies"],
    name: "Management of Companies and Enterprises",
    slug: "management-of-companies-and-enterprises"
  },
  "561": {
    description: 'Industries in the Administrative and Support Services subsector group establishments engaged in activities that support the day-to-day operations of other organizations.  The processes employed in this sector (e.g., general management, personnel administration, clerical activities, cleaning activities) are often integral parts of the activities of establishments found in all sectors of the economy.  The establishments classified in this subsector have specialization in one or more of these activities and can, therefore, provide services to clients in a variety of industries and, in some cases, to households.  The individual industries of this subsector are defined on the basis of the particular process that they are engaged in and the particular services they provide. Many of the activities in this subsector are ongoing routine support functions that businesses and organizations perform in-house.  However, it is common to contract or purchase services from businesses that specialize in such activities and can, therefore, provide the services more efficiently. The industries in this subsector cannot be viewed as strictly "support."  The Travel Arrangement and Reservation Services industry group includes travel agents, tour operators, and providers of other travel arrangement services, such as hotel and restaurant reservations and arranging the purchase of tickets, serving many types of clients, including individual consumers.  This group was placed in this subsector because the services are often of the "support" nature (e.g., travel arrangement) to businesses and other organizations that purchase such services. The administrative and management activities performed by establishments in this sector are typically on a contract or fee basis.  These activities may also be performed by establishments that are part of the company or enterprise.  However, establishments involved in administering, overseeing, and managing other establishments of the company or enterprise are classified in Sector 55, Management of Companies and Enterprises.  Establishments in Sector 55, Management of Companies and Enterprises, normally undertake the strategic and organizational planning and decision-making role of the company or enterprise.  Government establishments engaged in administering, overseeing, and managing governmental programs are classified in Sector 92, Public Administration.',
    industries: ["administrative-and-support-services"],
    name: "Administrative and Support Services",
    slug: "administrative-and-support-services"
  },
  "562": {
    description: "Industries in the Waste Management and Remediation Services subsector group establishments engaged in the collection, treatment, and disposal of waste materials.  This includes establishments engaged in local hauling of waste materials; operating materials recovery facilities (i.e., those that sort recyclable materials from the trash stream); providing remediation services (i.e., those that provide for the cleanup of contaminated buildings, mine sites, soil, or ground water); and providing septic pumping and other miscellaneous waste management services.  There are three industry groups within the subsector that separate these activities into waste collection, waste treatment and disposal, and remediation and other waste management. Excluded from this subsector are establishments primarily engaged in collecting, treating, and disposing waste through sewer systems or sewage treatment facilities that are classified in Industry 22132, Sewage Treatment Facilities, and establishments primarily engaged in long-distance hauling of waste materials that are classified in Industry 48423, Specialized Freight (except Used Goods) Trucking, Long-Distance.  Also, there are some activities that appear to be related to waste management, but that are not included in this subsector.  For example, establishments primarily engaged in providing waste management consulting services are classified in Industry 54162, Environmental Consulting Services.",
    industries: ["administrative-and-support-services"],
    name: "Waste Management and Remediation Services",
    slug: "waste-management-and-remediation-services"
  },
  "611": {
    description: "Industries in the Educational Services subsector provide instruction and training in a wide variety of subjects.  The instruction and training is provided by specialized establishments, such as schools, colleges, universities, and training centers. The subsector is structured according to level and type of educational services.  Elementary and secondary schools, junior colleges, and colleges, universities, and professional schools correspond to a recognized series of formal levels of education designated by diplomas, associate degrees (including equivalent certificates), and degrees.  The remaining industry groups are based more on the type of instruction or training offered, and the levels are not always as formally defined.  The establishments are often highly specialized, many offering instruction in a very limited subject matter, for example ski lessons or one specific computer software application.  Within the subsector, the level and types of training that are required of the instructors and teachers vary depending on the industry. Establishments that manage schools and other educational establishments on a contractual basis are classified in this subsector if they both manage the operation and provide the operating staff.  Such establishments are classified in the Educational Services subsector based on the type of facility managed and operated. Excluded from this subsector are establishments primarily engaged in publishing educational software or other educational materials without providing instruction.  These establishments are classified in Subsector 513, Publishing Industries.  Establishments that manage schools and other educational establishments on a contract or fee basis without providing the operating staff are classified in Subsector 561, Administrative and Support Services.  Establishments primarily engaged in providing vocational rehabilitation services and establishments primarily engaged in providing child care services are classified in Subsector 624, Social Assistance.",
    industries: ["education"],
    name: "Educational Services",
    slug: "educational-services"
  },
  "621": {
    description: "Industries in the Ambulatory Health Care Services subsector provide health care services directly or indirectly to ambulatory patients and do not usually provide inpatient services.  Health practitioners in this subsector provide outpatient services, with the facilities and equipment not usually being the most significant part of the production process.",
    industries: ["medical-practices"],
    name: "Ambulatory Health Care Services",
    slug: "ambulatory-health-care-services"
  },
  "622": {
    description: "Industries in the Hospitals subsector provide medical, diagnostic, and treatment services that include physician, nursing, and other health services to inpatients and the specialized accommodation services required by inpatients.  Hospitals may also provide outpatient services as a secondary activity.  Establishments in the Hospitals subsector provide inpatient health services, many of which can only be provided using the specialized facilities and equipment that form a significant and integral part of the production process.",
    industries: ["hospitals"],
    name: "Hospitals",
    slug: "hospitals"
  },
  "623": {
    description: "Industries in the Nursing and Residential Care Facilities subsector provide residential care combined with either nursing, supervisory, or other types of care as required by the residents.  In this subsector, the facilities are a significant part of the production process, and the care provided is a mix of health and social services with the health services being largely some level of nursing services.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Nursing and Residential Care Facilities",
    slug: "nursing-and-residential-care-facilities"
  },
  "624": {
    description: "Industries in the Social Assistance subsector provide a wide variety of social assistance services directly to their clients.  These services do not include residential or accommodation services, except on a short-stay basis.",
    industries: ["hospitals-and-health-care"],
    name: "Social Assistance",
    slug: "social-assistance"
  },
  "711": {
    description: "Industries in the Performing Arts, Spectator Sports, and Related Industries subsector group establishments that produce or organize and promote live presentations involving the performances of actors and actresses, singers, dancers, musical groups and artists, athletes, and other entertainers, including independent (i.e., freelance) entertainers and the establishments that manage their careers.  The classification recognizes four basic processes: (1) producing (i.e., presenting) events; (2) organizing, managing, and/or promoting events; (3) managing and representing entertainers; and (4) providing the artistic, creative, and technical skills necessary to the production of these live events.  Also, this subsector contains four industries for performing arts companies.  Each is defined on the basis of the particular skills of the entertainers involved in the presentations. The industry structure for this subsector makes a clear distinction between performing arts companies and performing artists (i.e., independent or freelance).  Although not unique to arts and entertainment, freelancing is a particularly important phenomenon in this Performing Arts, Spectator Sports, and Related Industries subsector.  Distinguishing this activity from the production activity is a meaningful process differentiation.  This approach, however, is difficult to implement in the case of musical groups (i.e., companies) and artists.  These establishments tend to be more loosely organized and it can be difficult to distinguish companies from freelancers.  For this reason, NAICS includes one industry that covers both musical groups and musical artists. This subsector contains two industries for Industry Group 7113, Promoters of Performing Arts, Sports, and Similar Events, one for those that operate facilities and another for those that do not.  This is because there are significant differences in cost structures between those promoters that manage and provide the staff to operate facilities and those that do not.  In addition to promoters without facilities, other industries in this subsector include establishments that may operate without permanent facilities.  These types of establishments include performing arts companies; musical groups and artists; spectator sports; and independent (i.e., freelance) artists, writers, and performers. Excluded from this subsector are nightclubs.  Some nightclubs promote live entertainment on a regular basis and it can be argued that they could be classified in Industry Group 7113, Promoters of Performing Arts, Sports, and Similar Events.  However, since most of these establishments function as any other drinking place when they do not promote entertainment and because most of their revenue is derived from sale of food and beverages, they are classified in Subsector 722, Food Services and Drinking Places.",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Performing Arts, Spectator Sports, and Related Industries",
    slug: "performing-arts-spectator-sports-and-related-industries"
  },
  "712": {
    description: "Industries in the Museums, Historical Sites, and Similar Institutions subsector engage in the preservation and exhibition of objects, sites, and natural wonders of historical, cultural, and/or educational value.",
    industries: ["museums-historical-sites-and-zoos"],
    name: "Museums, Historical Sites, and Similar Institutions",
    slug: "museums-historical-sites-and-similar-institutions"
  },
  "713": {
    description: "Industries in the Amusement, Gambling, and Recreation Industries subsector (1) operate facilities where patrons can primarily engage in sports, recreation, amusement, or gambling activities and/or (2) provide other amusement and recreation services, such as supplying and servicing amusement devices in places of business operated by others; operating sports teams, clubs, or leagues engaged in playing games for recreational purposes; and guiding tours without using transportation equipment. This subsector does not cover all establishments providing recreational services.  Other sectors of NAICS also provide recreational services.  Providers of recreational services are often engaged in processes classified in other sectors of NAICS.  For example, operators of resorts and hunting and fishing camps provide both accommodation and recreational facilities and services.  These establishments are classified in Subsector 721, Accommodation, partly to reflect the significant costs associated with the provision of accommodation services and partly to ensure consistency with international standards.  Likewise, establishments using transportation equipment to provide recreational and entertainment services, such as those operating sightseeing buses, dinner cruises, or helicopter rides, are classified in Subsector 487, Scenic and Sightseeing Transportation. The industry groups in this subsector highlight particular types of activities: amusement parks and arcades, gambling industries, and other amusement and recreation industries.  The groups, however, are not all-inclusive of the activity.  The Gambling Industries industry group does not provide for full coverage of gambling activities.  For example, casino hotels are classified in Subsector 721, Accommodation; and horse and dog racing tracks without casinos are classified in Industry Group 7112, Spectator Sports.",
    industries: ["recreational-facilities"],
    name: "Amusement, Gambling, and Recreation Industries",
    slug: "amusement-gambling-and-recreation-industries"
  },
  "721": {
    description: "Industries in the Accommodation subsector provide lodging or short-term accommodations for travelers, vacationers, and others.  There is a wide range of establishments in these industries.  Some provide lodging only, while others provide meals, laundry services, and recreational facilities, as well as lodging.  Lodging establishments are classified in this subsector even if the provision of complementary services generates more revenue.  The types of complementary services provided vary from establishment to establishment. The subsector is organized into three groups: (1) traveler accommodation, (2) recreational accommodation, and (3) rooming and boarding houses, dormitories, and workers' camps.  The Traveler Accommodation industry group includes establishments that primarily provide traditional types of lodging services.  This group includes hotels, motels, and bed-and-breakfast inns.  In addition to lodging, these establishments may provide a range of other services to their guests.  The RV (Recreational Vehicle) Parks and Recreational Camps industry group includes establishments that operate lodging facilities primarily designed to accommodate outdoor enthusiasts.  Included are travel trailer campsites, recreational vehicle parks, and outdoor adventure retreats.  The Rooming and Boarding Houses, Dormitories, and Workers' Camps industry group includes establishments providing temporary or longer-term accommodations, that for the period of occupancy, may serve as a principal residence.  Board (i.e., meals) may be provided but is not essential. Establishments that manage short-stay accommodation establishments (e.g., hotels and motels) on a contractual basis are classified in this subsector if they both manage the operation and provide the operating staff.  Such establishments are classified based on the type of facility managed and operated.",
    industries: ["accommodation-services"],
    name: "Accommodation",
    slug: "accommodation"
  },
  "722": {
    description: "Industries in the Food Services and Drinking Places subsector prepare meals, snacks, and beverages to customer order for immediate on-premises and off-premises consumption.  There is a wide range of establishments in these industries.  Some provide food and drink only, while others provide various combinations of seating space, waiter/waitress services, and incidental amenities, such as limited entertainment.  The industries in the subsector are grouped based on the type and level of services provided.  The industry groups are Special Food Services, such as food service contractors, caterers, and mobile food services; Drinking Places (Alcoholic Beverages); and Restaurants and Other Eating Places. Food and beverage services at hotels and motels, amusement parks, theaters, casinos, country clubs, similar recreational facilities, and civic and social organizations are included in this subsector only if these services are provided by a separate establishment primarily engaged in providing food and beverage services. Excluded from this subsector are establishments operating dinner cruises.  These establishments are classified in Subsector 487, Scenic and Sightseeing Transportation, because they utilize transportation equipment to provide scenic recreational entertainment.",
    industries: ["food-and-beverage-services"],
    name: "Food Services and Drinking Places",
    slug: "food-services-and-drinking-places"
  },
  "811": {
    description: "Industries in the Repair and Maintenance subsector restore machinery, equipment, and other products to working order.  These establishments also typically provide general or routine maintenance (i.e., servicing) on such products to ensure they work efficiently and to prevent breakdown and unnecessary repairs. The NAICS structure for this subsector brings together most types of repair and maintenance establishments and categorizes them based on production processes (i.e., on the type of repair and maintenance activity performed, and the necessary skills, expertise, and processes that are found in different repair and maintenance establishments).  This NAICS classification does not delineate between repair services provided to businesses versus those that serve households.  Although some industries primarily serve either businesses or households, separation by class of customer is limited by the fact that many establishments serve both.  Establishments repairing computers and consumer electronics products are two examples of such overlap. The Repair and Maintenance subsector does not include all establishments that do repair and maintenance.  For example, a substantial amount of repair is done by establishments that also manufacture machinery, equipment, and other goods.  These establishments are included in the Manufacturing sector in NAICS.  In addition, repair of transportation equipment is often provided by or based at transportation facilities, such as airports and seaports, and these activities are included in the Transportation and Warehousing sector.  A particularly unique situation exists with repair of buildings.  Plumbing, electrical installation and repair, painting and decorating, and other construction-related establishments are often involved in performing installation or other work on new construction as well as providing repair services on existing structures.  While some specialize in repair, it is difficult to distinguish between the two types and all are included in the Construction sector. Excluded from this subsector are establishments primarily engaged in rebuilding or remanufacturing machinery and equipment.  These are classified in Sector 31-33, Manufacturing.  Also excluded are retail establishments that provide after-sale services and repair.  These are classified in Sector 44-45, Retail Trade.",
    industries: ["repair-and-maintenance"],
    name: "Repair and Maintenance",
    slug: "repair-and-maintenance"
  },
  "812": {
    description: "Industries in the Personal and Laundry Services subsector group establishments that provide personal and laundry services to individuals, households, and businesses.  Services performed include: personal care services; death care services; laundry and drycleaning services; and a wide range of other personal services, such as pet care (except veterinary) services, photofinishing services, temporary parking services, and dating services. The Personal and Laundry Services subsector is by no means all-inclusive of the services that could be termed personal services (i.e., those provided to individuals rather than businesses).  There are many other subsectors, as well as sectors, that provide services to persons.  Establishments providing legal, accounting, tax preparation, architectural, portrait photography, and similar professional services are classified in Sector 54, Professional, Scientific, and Technical Services; those providing job placement, travel arrangement, home security, interior and exterior house cleaning, exterminating, lawn and garden care, and similar support services are classified in Sector 56, Administrative and Support and Waste Management and Remediation Services; those providing health and social services are classified in Sector 62, Health Care and Social Assistance; those providing amusement and recreation services are classified in Sector 71, Arts, Entertainment, and Recreation; those providing educational instruction are classified in Sector 61, Educational Services; those providing repair services are classified in Subsector 811, Repair and Maintenance; and those providing spiritual, civic, and advocacy services are classified in Subsector 813, Religious, Grantmaking, Civic, Professional, and Similar Organizations.",
    industries: ["personal-and-laundry-services"],
    name: "Personal and Laundry Services",
    slug: "personal-and-laundry-services"
  },
  "813": {
    description: "Industries in the Religious, Grantmaking, Civic, Professional, and Similar Organizations subsector group establishments that organize and promote religious activities; support various causes through grantmaking; advocate various social and political causes; and promote and defend the interests of their members. The industry groups within the subsector are defined in terms of their activities, such as establishments that provide funding for specific causes or for a variety of charitable causes; establishments that advocate and actively promote causes and beliefs for the public good; and establishments that have an active membership structure to promote causes and represent the interests of their members.  Establishments in this subsector may publish newsletters, books, and periodicals for distribution to their members.",
    industries: ["consumer-services"],
    name: "Religious, Grantmaking, Civic, Professional, and Similar Organizations",
    slug: "religious-grantmaking-civic-professional-and-similar-organizations"
  },
  "814": {
    description: "The Private Households subsector includes private households that engage in employing workers on or about the premises in activities primarily concerned with the operation of the household.  These private households may employ individuals, such as cooks, maids, nannies, butlers, non-medical personal care aides, and outside workers, such as gardeners, caretakers, and other maintenance workers.",
    industries: ["household-services"],
    name: "Private Households",
    slug: "private-households"
  },
  "921": {
    description: "The Executive, Legislative, and Other General Government Support subsector groups offices of government executives, legislative bodies, public finance, and general government support.",
    industries: ["public-policy-offices"],
    name: "Executive, Legislative, and Other General Government Support",
    slug: "executive-legislative-and-other-general-government-support"
  },
  "922": {
    description: "The Justice, Public Order, and Safety Activities subsector groups government establishments engaged in the administration of justice, public order, and safety programs.",
    industries: ["government-administration"],
    name: "Justice, Public Order, and Safety Activities",
    slug: "justice-public-order-and-safety-activities"
  },
  "923": {
    description: "The Administration of Human Resource Programs subsector groups government establishments primarily engaged in the administration of human resource programs.",
    industries: ["health-and-human-services"],
    name: "Administration of Human Resource Programs",
    slug: "administration-of-human-resource-programs"
  },
  "924": {
    description: "The Administration of Environmental Quality Programs subsector groups government establishments primarily engaged in the administration of environmental quality.",
    industries: ["environmental-quality-programs"],
    name: "Administration of Environmental Quality Programs",
    slug: "administration-of-environmental-quality-programs"
  },
  "925": {
    description: "The Administration of Housing Programs, Urban Planning, and Community Development subsector groups government establishments primarily engaged in the administration of housing, urban planning, and community development.",
    industries: ["housing-and-community-development"],
    name: "Administration of Housing Programs, Urban Planning, and Community Development",
    slug: "administration-of-housing-programs-urban-planning-and-community-development"
  },
  "926": {
    description: "The Administration of Economic Programs subsector groups government establishments primarily engaged in the administration of economic programs.",
    industries: ["economic-programs"],
    name: "Administration of Economic Programs",
    slug: "administration-of-economic-programs"
  },
  "927": {
    description: "The Space Research and Technology subsector comprises government establishments primarily engaged in the administration and operations of space flights, space research, and space exploration.",
    industries: ["government-administration"],
    name: "Space Research and Technology",
    slug: "space-research-and-technology"
  },
  "928": {
    description: "The National Security and International Affairs subsector groups government establishments primarily engaged in national security and international affairs.",
    industries: ["military-and-international-affairs"],
    name: "National Security and International Affairs",
    slug: "national-security-and-international-affairs"
  },
  "1111": {
    description: "This industry group comprises establishments primarily engaged in (1) growing oilseed and/or grain crops and/or (2) producing oilseed and grain seeds.  These crops have an annual life cycle and are typically grown in open fields.",
    industries: ["farming"],
    name: "Oilseed and Grain Farming",
    slug: "oilseed-and-grain-farming"
  },
  "1112": {
    description: "",
    industries: ["farming"],
    name: "Vegetable and Melon Farming",
    slug: "vegetable-and-melon-farming"
  },
  "1113": {
    description: "This industry group comprises establishments primarily engaged in growing fruit and/or tree nut crops.  The crops included in this industry group are generally not grown from seeds and have a perennial life cycle.",
    industries: ["farming"],
    name: "Fruit and Tree Nut Farming",
    slug: "fruit-and-tree-nut-farming"
  },
  "1114": {
    description: 'This industry group comprises establishments primarily engaged in growing crops of any kind under cover and/or growing nursery stock and flowers.  "Under cover" is generally defined as greenhouses, cold frames, cloth houses, and lath houses.  The crops grown are removed at various stages of maturity and have annual and perennial life cycles.  The nursery stock includes short rotation woody crops that have growth cycles of 10 years or less.',
    industries: ["farming"],
    name: "Greenhouse, Nursery, and Floriculture Production",
    slug: "greenhouse-nursery-and-floriculture-production"
  },
  "1119": {
    description: "This industry group comprises establishments primarily engaged in (1) growing crops (except oilseed and/or grain; vegetable and/or melon; fruit and tree nut; and greenhouse, nursery, and/or floriculture products), such as tobacco, cotton, sugarcane, hay, sugar beets, peanuts, agave, herbs and spices, and hay and grass seeds, or (2) growing a combination of crops (except a combination of oilseed(s) and grain(s) and a combination of fruit(s) and tree nut(s)).",
    industries: ["farming"],
    name: "Other Crop Farming",
    slug: "other-crop-farming"
  },
  "1121": {
    description: "This industry group comprises establishments primarily engaged in raising cattle, milking dairy cattle, or feeding cattle for fattening.",
    industries: ["ranching"],
    name: "Cattle Ranching and Farming",
    slug: "cattle-ranching-and-farming"
  },
  "1122": {
    description: "",
    industries: ["ranching-and-fisheries"],
    name: "Hog and Pig Farming",
    slug: "hog-and-pig-farming"
  },
  "1123": {
    description: "This industry group comprises establishments primarily engaged in breeding, hatching, and raising poultry for meat or egg production.",
    industries: ["ranching-and-fisheries"],
    name: "Poultry and Egg Production",
    slug: "poultry-and-egg-production"
  },
  "1124": {
    description: "This industry group comprises establishments primarily engaged in raising sheep, lambs, and goats, or feeding lambs for fattening.",
    industries: ["ranching-and-fisheries"],
    name: "Sheep and Goat Farming",
    slug: "sheep-and-goat-farming"
  },
  "1125": {
    description: "",
    industries: ["ranching-and-fisheries"],
    name: "Aquaculture",
    slug: "aquaculture"
  },
  "1129": {
    description: "This industry group comprises establishments primarily engaged in raising animals and insects (except cattle, hogs and pigs, poultry, sheep and goats, and aquaculture) for sale or product production.  These establishments are primarily engaged in raising one of the following: bees, horses and other equines, rabbits and other fur-bearing animals, and so forth, and producing products, such as honey and other bee products.  Establishments primarily engaged in raising a combination of animals with no one animal or family of animals accounting for one-half of the establishment's agricultural production (i.e., value of animals for market) are included in this industry group.",
    industries: ["ranching-and-fisheries"],
    name: "Other Animal Production",
    slug: "other-animal-production"
  },
  "1131": {
    description: "",
    industries: ["forestry-and-logging"],
    name: "Timber Tract Operations",
    slug: "timber-tract-operations"
  },
  "1132": {
    description: "",
    industries: ["forestry-and-logging"],
    name: "Forest Nurseries and Gathering of Forest Products",
    slug: "forest-nurseries-and-gathering-of-forest-products"
  },
  "1133": {
    description: "",
    industries: ["forestry-and-logging"],
    name: "Logging",
    slug: "logging"
  },
  "1141": {
    description: "",
    industries: ["fisheries"],
    name: "Fishing",
    slug: "fishing"
  },
  "1142": {
    description: "",
    industries: ["farming-ranching-forestry"],
    name: "Hunting and Trapping",
    slug: "hunting-and-trapping"
  },
  "1151": {
    description: "",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Crop Production",
    slug: "support-activities-for-crop-production"
  },
  "1152": {
    description: "",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Animal Production",
    slug: "support-activities-for-animal-production"
  },
  "1153": {
    description: "",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Forestry",
    slug: "support-activities-for-forestry"
  },
  "2111": {
    description: "",
    industries: ["oil-and-gas"],
    name: "Oil and Gas Extraction",
    slug: "oil-and-gas-extraction"
  },
  "2121": {
    description: "",
    industries: ["coal-mining"],
    name: "Coal Mining",
    slug: "coal-mining"
  },
  "2122": {
    description: "This industry group comprises establishments primarily engaged in developing mine sites or mining metallic minerals, and establishments primarily engaged in ore dressing and beneficiating (i.e., preparing) operations, such as crushing, grinding, washing, drying, sintering, concentrating, calcining, and leaching.  Beneficiating may be performed at mills operated in conjunction with the mines served or at mills, such as custom mills, operated separately.",
    industries: ["metal-ore-mining"],
    name: "Metal Ore Mining",
    slug: "metal-ore-mining"
  },
  "2123": {
    description: "This industry group comprises establishments primarily engaged in developing mine sites, or in mining or quarrying nonmetallic minerals (except fuels).  Also included are certain well and brine operations, and preparation plants primarily engaged in beneficiating (e.g., crushing, grinding, washing, and concentrating) nonmetallic minerals. Beneficiation is the process whereby the extracted material is reduced to particles which can be separated into mineral and waste, the former suitable for further processing or direct use.  The operations that take place in beneficiation are primarily mechanical, such as grinding, washing, magnetic separation, and centrifugal separation.  In contrast, manufacturing operations primarily use chemical and electrochemical processes, such as electrolysis and distillation.  However, some treatments, such as heat treatments, take place in both the beneficiation and the manufacturing (i.e., smelting/refining) stages.  The range of preparation activities varies by mineral and the purity of any given ore deposit.  While some minerals, such as petroleum and natural gas, require little or no preparation, others are washed and screened, while yet others, such as gold and silver, can be transformed into bullion before leaving the mine site.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Nonmetallic Mineral Mining and Quarrying",
    slug: "nonmetallic-mineral-mining-and-quarrying"
  },
  "2131": {
    description: "",
    industries: ["oil-gas-and-mining"],
    name: "Support Activities for Mining",
    slug: "support-activities-for-mining"
  },
  "2211": {
    description: "This industry group comprises establishments primarily engaged in generating, transmitting, and/or distributing electric power.  Establishments in this industry group may perform one or more of the following activities: (1) operate generation facilities that produce electric energy; (2) operate transmission systems that convey the electricity from the generation facility to the distribution system; and (3) operate distribution systems that convey electric power received from the generation facility or the transmission system to the final consumer.",
    industries: ["utilities"],
    name: "Electric Power Generation, Transmission and Distribution",
    slug: "electric-power-generation-transmission-and-distribution"
  },
  "2212": {
    description: "",
    industries: ["natural-gas-distribution"],
    name: "Natural Gas Distribution",
    slug: "natural-gas-distribution"
  },
  "2213": {
    description: "This industry group comprises establishments primarily engaged in: (1) operating water treatment plants and/or water supply systems; (2) operating sewer systems or sewage treatment facilities; or (3) providing steam, heated air, or cooled air.",
    industries: ["water-waste-steam-and-air-conditioning-services"],
    name: "Water, Sewage and Other Systems",
    slug: "water-sewage-and-other-systems"
  },
  "2361": {
    description: "",
    industries: ["residential-building-construction"],
    name: "Residential Building Construction",
    slug: "residential-building-construction"
  },
  "2362": {
    description: "This industry group comprises establishments primarily responsible for the construction (including new work, additions, alterations, maintenance, and repairs) of nonresidential buildings.  This industry group includes nonresidential general contractors, nonresidential for-sale builders, nonresidential design-build firms, and nonresidential project construction management firms.",
    industries: ["nonresidential-building-construction"],
    name: "Nonresidential Building Construction",
    slug: "nonresidential-building-construction"
  },
  "2371": {
    description: "This industry group comprises establishments primarily engaged in the construction of distribution lines and related buildings and structures for utilities (i.e., water, sewer, petroleum, gas, power, and communication).  All structures (including buildings) that are integral parts of utility systems (e.g., storage tanks, pumping stations, power plants, and refineries) are included in this industry group.",
    industries: ["utility-system-construction"],
    name: "Utility System Construction",
    slug: "utility-system-construction"
  },
  "2372": {
    description: "",
    industries: ["subdivision-of-land"],
    name: "Land Subdivision",
    slug: "land-subdivision"
  },
  "2373": {
    description: "",
    industries: ["highway-street-and-bridge-construction"],
    name: "Highway, Street, and Bridge Construction",
    slug: "highway-street-and-bridge-construction"
  },
  "2379": {
    description: "",
    industries: ["civil-engineering"],
    name: "Other Heavy and Civil Engineering Construction",
    slug: "other-heavy-and-civil-engineering-construction"
  },
  "2381": {
    description: "This industry group comprises establishments primarily engaged in the specialty trades needed to complete the basic structure (i.e., foundation, frame, and shell) of buildings.  The work performed may include new work, additions, alterations, maintenance, and repairs.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Foundation, Structure, and Building Exterior Contractors",
    slug: "foundation-structure-and-building-exterior-contractors"
  },
  "2382": {
    description: "This industry group comprises establishments primarily engaged in installing or servicing equipment that forms part of a building mechanical system (e.g., electricity, water, heating, and cooling).  The work performed may include new work, additions, alterations, maintenance, and repairs.  Contractors installing specialized building equipment, such as elevators, escalators, service station equipment, and central vacuum cleaning systems, are also included.",
    industries: ["building-equipment-contractors"],
    name: "Building Equipment Contractors",
    slug: "building-equipment-contractors"
  },
  "2383": {
    description: "This industry group comprises establishments primarily engaged in the specialty trades needed to finish buildings.  The work performed may include new work, additions, alterations, maintenance, and repairs.",
    industries: ["building-finishing-contractors"],
    name: "Building Finishing Contractors",
    slug: "building-finishing-contractors"
  },
  "2389": {
    description: "This industry group comprises establishments primarily engaged in site preparation activities and in specialized trades (except foundation, structure, and building exterior contractors; building equipment contractors; and building finishing contractors).  The specialty trade work performed includes new work, additions, alterations, maintenance, and repairs.",
    industries: ["specialty-trade-contractors"],
    name: "Other Specialty Trade Contractors",
    slug: "other-specialty-trade-contractors"
  },
  "3111": {
    description: "",
    industries: ["animal-feed-manufacturing"],
    name: "Animal Food Manufacturing",
    slug: "animal-food-manufacturing"
  },
  "3112": {
    description: "This industry group comprises establishments primarily engaged in milling flour or meal from grains or vegetables, manufacturing malt, wet milling corn and other vegetables, crushing oilseeds and tree nuts, refining and/or blending vegetable oils, and manufacturing breakfast cereals.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Grain and Oilseed Milling",
    slug: "grain-and-oilseed-milling"
  },
  "3113": {
    description: "This industry group comprises (1) establishments that process agricultural inputs, such as sugarcane, beet, and cacao, to give rise to a new product (sugar or chocolate) and (2) establishments that begin with sugar and chocolate and process these further.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Sugar and Confectionery Product Manufacturing",
    slug: "sugar-and-confectionery-product-manufacturing"
  },
  "3114": {
    description: "This industry group includes (1) establishments that freeze food and (2) establishments that use preservation processes, such as pickling, canning, and dehydrating.  Both types begin their production process with inputs of vegetable or animal origin.",
    industries: ["fruit-and-vegetable-preserves-manufacturing"],
    name: "Fruit and Vegetable Preserving and Specialty Food Manufacturing",
    slug: "fruit-and-vegetable-preserving-and-specialty-food-manufacturing"
  },
  "3115": {
    description: "This industry group comprises establishments that manufacture dairy products from raw milk, processed milk, and dairy substitutes (except butter substitutes).",
    industries: ["dairy-product-manufacturing"],
    name: "Dairy Product Manufacturing",
    slug: "dairy-product-manufacturing"
  },
  "3116": {
    description: "",
    industries: ["meat-products-manufacturing"],
    name: "Animal Slaughtering and Processing",
    slug: "animal-slaughtering-and-processing"
  },
  "3117": {
    description: "",
    industries: ["seafood-product-manufacturing"],
    name: "Seafood Product Preparation and Packaging",
    slug: "seafood-product-preparation-and-packaging"
  },
  "3118": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) manufacturing fresh and frozen bread and other bakery products; (2) retailing bread and other bakery products not for immediate consumption made on the premises from flour, not from prepared dough; (3) manufacturing cookies, crackers, and dry pasta; (4) manufacturing prepared flour mixes or dough from flour ground elsewhere; or (5) manufacturing tortillas.",
    industries: ["baked-goods-manufacturing"],
    name: "Bakeries and Tortilla Manufacturing",
    slug: "bakeries-and-tortilla-manufacturing"
  },
  "3119": {
    description: "This industry group comprises establishments primarily engaged in manufacturing food (except animal food; grain and oilseed milling; sugar and confectionery products; preserved fruits, vegetables, and specialty foods; dairy products; meat products; seafood products; and bakery products and tortillas).  This industry group includes industries with different production processes, such as snack food manufacturing; coffee and tea manufacturing; concentrate, syrup, condiment, and spice manufacturing; and, in general, an entire range of other miscellaneous food product manufacturing.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Other Food Manufacturing",
    slug: "other-food-manufacturing"
  },
  "3121": {
    description: "This industry group comprises establishments primarily engaged in manufacturing soft drinks and ice; purifying and bottling water; and manufacturing brewery, winery, and distillery products.",
    industries: ["beverage-manufacturing"],
    name: "Beverage Manufacturing",
    slug: "beverage-manufacturing"
  },
  "3122": {
    description: "",
    industries: ["tobacco-manufacturing"],
    name: "Tobacco Manufacturing",
    slug: "tobacco-manufacturing"
  },
  "3131": {
    description: "",
    industries: ["textile-manufacturing"],
    name: "Fiber, Yarn, and Thread Mills",
    slug: "fiber-yarn-and-thread-mills"
  },
  "3132": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) weaving broadwoven fabrics and felts (except tire fabrics and rugs); (2) weaving or braiding narrow fabrics; (3) making fabric-covered elastic yarn and thread; (4) manufacturing Schiffli machine embroideries; (5) manufacturing nonwoven fabrics and felts; (6) knitting weft (i.e., circular) and warp (i.e., flat) fabric; (7) knitting and finishing weft and warp fabric; (8) manufacturing lace; or (9) manufacturing, dyeing, and finishing lace and lace goods.",
    industries: ["textile-manufacturing"],
    name: "Fabric Mills",
    slug: "fabric-mills"
  },
  "3133": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) finishing textiles, fabrics, and apparel; (2) converting fabrics and textiles by buying fabric goods in the grey, having them finished on contract, and selling them at wholesale; or (3) coating, laminating, varnishing, waxing, and rubberizing textiles and apparel.",
    industries: ["textile-manufacturing"],
    name: "Textile and Fabric Finishing and Fabric Coating Mills",
    slug: "textile-and-fabric-finishing-and-fabric-coating-mills"
  },
  "3141": {
    description: "This industry group comprises establishments primarily engaged in (1) manufacturing woven, tufted, and other carpets and rugs and (2) manufacturing household textile products from purchased materials.  The household textile products may be made on a stock or custom basis for sale to individual retail customers.",
    industries: ["manufacturing"],
    name: "Textile Furnishings Mills",
    slug: "textile-furnishings-mills"
  },
  "3149": {
    description: "This industry group comprises establishments primarily engaged in making textile products (except carpets and rugs, curtains and draperies, and other household textile products) from purchased materials.",
    industries: ["manufacturing"],
    name: "Other Textile Product Mills",
    slug: "other-textile-product-mills"
  },
  "3151": {
    description: "",
    industries: ["apparel-manufacturing"],
    name: "Apparel Knitting Mills",
    slug: "apparel-knitting-mills"
  },
  "3152": {
    description: "This industry group comprises establishments primarily engaged in manufacturing cut and sew apparel from woven fabric or purchased knit fabric.  Included in this industry group is a diverse range of establishments manufacturing full lines of ready-to-wear apparel and custom apparel: apparel contractors, performing cutting or sewing operations on materials owned by others; jobbers, performing entrepreneurial functions involved in apparel manufacturing; and tailors, manufacturing custom garments for individual clients.  Establishments weaving or knitting fabric, without manufacturing apparel, are classified in Subsector 313, Textile Mills.",
    industries: ["apparel-manufacturing"],
    name: "Cut and Sew Apparel Manufacturing",
    slug: "cut-and-sew-apparel-manufacturing"
  },
  "3159": {
    description: "",
    industries: ["fashion-accessories-manufacturing"],
    name: "Apparel Accessories and Other Apparel Manufacturing",
    slug: "apparel-accessories-and-other-apparel-manufacturing"
  },
  "3161": {
    description: "",
    industries: ["leather-product-manufacturing"],
    name: "Leather and Hide Tanning and Finishing",
    slug: "leather-and-hide-tanning-and-finishing"
  },
  "3162": {
    description: "",
    industries: ["footwear-manufacturing"],
    name: "Footwear Manufacturing",
    slug: "footwear-manufacturing"
  },
  "3169": {
    description: "",
    industries: ["leather-product-manufacturing"],
    name: "Other Leather and Allied Product Manufacturing",
    slug: "other-leather-and-allied-product-manufacturing"
  },
  "3211": {
    description: "",
    industries: ["wood-product-manufacturing"],
    name: "Sawmills and Wood Preservation",
    slug: "sawmills-and-wood-preservation"
  },
  "3212": {
    description: "",
    industries: ["wood-product-manufacturing"],
    name: "Veneer, Plywood, and Engineered Wood Product Manufacturing",
    slug: "veneer-plywood-and-engineered-wood-product-manufacturing"
  },
  "3219": {
    description: "This industry group comprises establishments primarily engaged in manufacturing wood products (except establishments operating sawmills and wood preservation facilities; and establishments manufacturing veneer, plywood, or engineered wood products).",
    industries: ["wood-product-manufacturing"],
    name: "Other Wood Product Manufacturing",
    slug: "other-wood-product-manufacturing"
  },
  "3221": {
    description: "This industry group comprises establishments primarily engaged in manufacturing pulp, paper, or paperboard.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Pulp, Paper, and Paperboard Mills",
    slug: "pulp-paper-and-paperboard-mills"
  },
  "3222": {
    description: "This industry group comprises establishments primarily engaged in converting paper or paperboard without manufacturing paper or paperboard.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Converted Paper Product Manufacturing",
    slug: "converted-paper-product-manufacturing"
  },
  "3231": {
    description: "",
    industries: ["printing-services"],
    name: "Printing and Related Support Activities",
    slug: "printing-and-related-support-activities"
  },
  "3241": {
    description: "",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Petroleum and Coal Products Manufacturing",
    slug: "petroleum-and-coal-products-manufacturing"
  },
  "3251": {
    description: "This industry group comprises establishments primarily engaged in manufacturing chemicals using basic processes, such as thermal cracking and distillation.  Chemicals manufactured in this industry group are usually separate chemical elements or separate chemically-defined compounds.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Basic Chemical Manufacturing",
    slug: "basic-chemical-manufacturing"
  },
  "3252": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) manufacturing synthetic resins, plastics materials, and nonvulcanizable elastomers and mixing and blending resins on a custom basis; (2) manufacturing noncustomized synthetic resins; (3) manufacturing synthetic rubber; (4) manufacturing cellulosic (e.g., rayon, acetate) and noncellulosic (e.g., nylon, polyolefin, polyester) fibers and filaments in the form of monofilament, filament yarn, staple, or tow; or (5) manufacturing and texturizing cellulosic and noncellulosic fibers and filaments.",
    industries: ["artificial-rubber-and-synthetic-fiber-manufacturing"],
    name: "Resin, Synthetic Rubber, and Artificial and Synthetic Fibers and Filaments Manufacturing",
    slug: "resin-synthetic-rubber-and-artificial-and-synthetic-fibers-and-filaments-manufacturing"
  },
  "3253": {
    description: "This industry group comprises establishments primarily engaged in one or more of the following: (1) manufacturing nitrogenous or phosphatic fertilizer materials; (2) manufacturing fertilizers from sewage or animal waste; (3) manufacturing nitrogenous or phosphatic materials and mixing with other ingredients into fertilizers; (4) mixing ingredients made elsewhere into fertilizers; (5) manufacturing compost; and (6) formulating and preparing pesticides and other agricultural chemicals.",
    industries: ["agricultural-chemical-manufacturing"],
    name: "Pesticide, Fertilizer, and Other Agricultural Chemical Manufacturing",
    slug: "pesticide-fertilizer-and-other-agricultural-chemical-manufacturing"
  },
  "3254": {
    description: "",
    industries: ["pharmaceutical-manufacturing"],
    name: "Pharmaceutical and Medicine Manufacturing",
    slug: "pharmaceutical-and-medicine-manufacturing"
  },
  "3255": {
    description: "This industry group comprises establishments primarily engaged in one or more of the following: (1) mixing pigments, solvents, and binders into paints and other coatings; (2) manufacturing allied paint products; and (3) manufacturing adhesives, glues, and caulking compounds.",
    industries: ["paint-coating-and-adhesive-manufacturing"],
    name: "Paint, Coating, and Adhesive Manufacturing",
    slug: "paint-coating-and-adhesive-manufacturing"
  },
  "3256": {
    description: "This industry group comprises establishments primarily engaged in (1) manufacturing and packaging soaps, detergents, polishes, surface active agents, textile and leather finishing agents, and other sanitation goods or (2) preparing, blending, compounding, and packaging toilet preparations.",
    industries: ["chemical-manufacturing"],
    name: "Soap, Cleaning Compound, and Toilet Preparation Manufacturing",
    slug: "soap-cleaning-compound-and-toilet-preparation-manufacturing"
  },
  "3259": {
    description: "This industry group comprises establishments primarily engaged in manufacturing chemical products (except basic chemicals; resins, synthetic rubber, cellulosic and noncellulosic fibers and filaments; pesticides, fertilizers, and other agricultural chemicals; pharmaceuticals and medicines; paints, coatings, and adhesives; soaps and cleaning compounds; and toilet preparations).",
    industries: ["chemical-manufacturing"],
    name: "Other Chemical Product and Preparation Manufacturing",
    slug: "other-chemical-product-and-preparation-manufacturing"
  },
  "3261": {
    description: "This industry group comprises establishments primarily engaged in processing new or spent (i.e., recycled) plastics resins into intermediate or final products, using such processes as compression molding; extrusion molding; injection molding; blow molding; and casting.  Within most of these industries, the production process is such that a wide variety of products can be made.",
    industries: ["plastics-manufacturing"],
    name: "Plastics Product Manufacturing",
    slug: "plastics-product-manufacturing"
  },
  "3262": {
    description: "This industry group comprises establishments primarily engaged in processing natural, synthetic, or reclaimed rubber materials into intermediate or final products using processes, such as vulcanizing, cementing, molding, extruding, and lathe-cutting.",
    industries: ["rubber-products-manufacturing"],
    name: "Rubber Product Manufacturing",
    slug: "rubber-product-manufacturing"
  },
  "3271": {
    description: "This industry group comprises establishments primarily engaged in (1) shaping, molding, glazing, and firing pottery, ceramics, and plumbing fixtures, and electrical supplies made entirely or partly of clay or other ceramic materials or (2) shaping, molding, baking, burning, or hardening clay refractories, nonclay refractories, ceramic tile, structural clay tile, brick, and other structural clay building materials.",
    industries: ["clay-and-refractory-products-manufacturing"],
    name: "Clay Product and Refractory Manufacturing",
    slug: "clay-product-and-refractory-manufacturing"
  },
  "3272": {
    description: "",
    industries: ["glass-product-manufacturing"],
    name: "Glass and Glass Product Manufacturing",
    slug: "glass-and-glass-product-manufacturing"
  },
  "3273": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) manufacturing Portland, natural, masonry, pozzolanic, and other hydraulic cements; (2) acting as batch or mixing plants, manufacturing concrete delivered to a purchaser in a plastic and unhardened state; (3) manufacturing concrete pipe, brick, and block; or (4) manufacturing other concrete products (except block, brick, and pipe).",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Cement and Concrete Product Manufacturing",
    slug: "cement-and-concrete-product-manufacturing"
  },
  "3274": {
    description: "This industry group comprises establishments primarily engaged in (1) manufacturing lime from calcitic limestone, dolomitic limestone, or other calcareous materials or (2) manufacturing gypsum products.",
    industries: ["lime-and-gypsum-products-manufacturing"],
    name: "Lime and Gypsum Product Manufacturing",
    slug: "lime-and-gypsum-product-manufacturing"
  },
  "3279": {
    description: "This industry group comprises establishments manufacturing nonmetallic mineral products (except clay products, refractory products, glass products, cement and concrete products, lime, and gypsum products).",
    industries: ["abrasives-and-nonmetallic-minerals-manufacturing"],
    name: "Other Nonmetallic Mineral Product Manufacturing",
    slug: "other-nonmetallic-mineral-product-manufacturing"
  },
  "3311": {
    description: "",
    industries: ["primary-metal-manufacturing"],
    name: "Iron and Steel Mills and Ferroalloy Manufacturing",
    slug: "iron-and-steel-mills-and-ferroalloy-manufacturing"
  },
  "3312": {
    description: "This industry group comprises establishments primarily engaged in manufacturing iron and steel tube and pipe, drawing steel wire, and rolling or drawing shapes from purchased iron or steel.",
    industries: ["primary-metal-manufacturing"],
    name: "Steel Product Manufacturing from Purchased Steel",
    slug: "steel-product-manufacturing-from-purchased-steel"
  },
  "3313": {
    description: "",
    industries: ["primary-metal-manufacturing"],
    name: "Alumina and Aluminum Production and Processing",
    slug: "alumina-and-aluminum-production-and-processing"
  },
  "3314": {
    description: "This industry group comprises establishments primarily engaged in nonferrous metal (except aluminum) smelting, refining, rolling, drawing, extruding, and alloying.",
    industries: ["primary-metal-manufacturing"],
    name: "Nonferrous Metal (except Aluminum) Production and Processing",
    slug: "nonferrous-metal-except-aluminum-production-and-processing"
  },
  "3315": {
    description: "This industry group comprises establishments primarily engaged in pouring molten metal into molds or dies to form castings.  Establishments making castings and further manufacturing, such as machining or assembling, a specific manufactured product are classified in the industry of the finished product.  Foundries may perform operations, such as cleaning and deburring, on the castings they manufacture.  More involved processes, such as tapping, threading, milling, or machining to tight tolerances, that transform castings into more finished products are classified elsewhere in the Manufacturing sector based on the product made. Establishments in this industry group make castings from purchased metals or in integrated secondary smelting and casting facilities.  When the production of primary metals is combined with making castings, the establishment is classified in Subsector 331, Primary Metal Manufacturing, with the primary metal made.",
    industries: ["primary-metal-manufacturing"],
    name: "Foundries",
    slug: "foundries"
  },
  "3321": {
    description: "",
    industries: ["fabricated-metal-products"],
    name: "Forging and Stamping",
    slug: "forging-and-stamping"
  },
  "3322": {
    description: "",
    industries: ["cutlery-and-handtool-manufacturing"],
    name: "Cutlery and Handtool Manufacturing",
    slug: "cutlery-and-handtool-manufacturing"
  },
  "3323": {
    description: "This industry group comprises establishments primarily engaged in manufacturing one or more of the following: (1) prefabricated metal buildings, panels, and sections; (2) structural metal products; (3) metal plate work products; (4) metal framed windows (i.e., typically using purchased glass) and metal doors; (5) sheet metal work; and (6) ornamental and architectural metal products.",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Architectural and Structural Metals Manufacturing",
    slug: "architectural-and-structural-metals-manufacturing"
  },
  "3324": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) manufacturing power boilers and heat exchangers; (2) cutting, forming, and joining heavy gauge metal to manufacture tanks, vessels, and other containers; or (3) forming light gauge metal containers.",
    industries: ["boilers-tanks-and-shipping-container-manufacturing"],
    name: "Boiler, Tank, and Shipping Container Manufacturing",
    slug: "boiler-tank-and-shipping-container-manufacturing"
  },
  "3325": {
    description: "",
    industries: ["construction-hardware-manufacturing"],
    name: "Hardware Manufacturing",
    slug: "hardware-manufacturing"
  },
  "3326": {
    description: "",
    industries: ["spring-and-wire-product-manufacturing"],
    name: "Spring and Wire Product Manufacturing",
    slug: "spring-and-wire-product-manufacturing"
  },
  "3327": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) operating machine shops primarily engaged in machining metal and plastic parts and parts of other composite materials on a job or order basis; (2) machining precision turned products; or (3) manufacturing metal bolts, nuts, screws, rivets, and other industrial fasteners.",
    industries: ["turned-products-and-fastener-manufacturing"],
    name: "Machine Shops; Turned Product; and Screw, Nut, and Bolt Manufacturing",
    slug: "machine-shops-turned-product-and-screw-nut-and-bolt-manufacturing"
  },
  "3328": {
    description: "",
    industries: ["metal-treatments"],
    name: "Coating, Engraving, Heat Treating, and Allied Activities",
    slug: "coating-engraving-heat-treating-and-allied-activities"
  },
  "3329": {
    description: "This industry group comprises establishments primarily engaged in manufacturing fabricated metal products (except forgings and stampings, cutlery and handtools, architectural and structural metals, boilers, tanks, shipping containers, hardware, spring and wire products, machine shop products, turned products, screws, and nuts and bolts).",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Other Fabricated Metal Product Manufacturing",
    slug: "other-fabricated-metal-product-manufacturing"
  },
  "3331": {
    description: "This industry group comprises establishments primarily engaged in manufacturing one or more of the following: (1) farm machinery and equipment, power mowing equipment, and other powered home lawn and garden equipment; (2) construction machinery, surface mining machinery, and logging equipment; and (3) oil and gas field and underground mining machinery and equipment.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Agriculture, Construction, and Mining Machinery Manufacturing",
    slug: "agriculture-construction-and-mining-machinery-manufacturing"
  },
  "3332": {
    description: "",
    industries: ["industrial-machinery-manufacturing"],
    name: "Industrial Machinery Manufacturing",
    slug: "industrial-machinery-manufacturing"
  },
  "3333": {
    description: "",
    industries: ["commercial-and-service-industry-machinery-manufacturing"],
    name: "Commercial and Service Industry Machinery Manufacturing",
    slug: "commercial-and-service-industry-machinery-manufacturing"
  },
  "3334": {
    description: "",
    industries: ["hvac-and-refrigeration-equipment-manufacturing"],
    name: "Ventilation, Heating, Air-Conditioning, and Commercial Refrigeration Equipment Manufacturing",
    slug: "ventilation-heating-air-conditioning-and-commercial-refrigeration-equipment-manufacturing"
  },
  "3335": {
    description: "",
    industries: ["metalworking-machinery-manufacturing"],
    name: "Metalworking Machinery Manufacturing",
    slug: "metalworking-machinery-manufacturing"
  },
  "3336": {
    description: "",
    name: "Engine, Turbine, and Power Transmission Equipment Manufacturing",
    slug: "engine-turbine-and-power-transmission-equipment-manufacturing",
    industries: ["engines-and-power-transmission-equipment-manufacturing", "renewable-energy-equipment-manufacturing"]
  },
  "3339": {
    description: "This industry group comprises establishments primarily engaged in manufacturing pumps and compressors, material handling equipment, and all other general purpose machinery (except ventilation, heating, air-conditioning, and commercial refrigeration equipment; metalworking machinery; and engines, turbines, and power transmission equipment).",
    industries: ["machinery-manufacturing"],
    name: "Other General Purpose Machinery Manufacturing",
    slug: "other-general-purpose-machinery-manufacturing"
  },
  "3341": {
    description: "",
    industries: ["computer-hardware-manufacturing"],
    name: "Computer and Peripheral Equipment Manufacturing",
    slug: "computer-and-peripheral-equipment-manufacturing"
  },
  "3342": {
    description: "This industry group comprises establishments primarily engaged in manufacturing wire telephone and data communications equipment, radio and television broadcast and wireless communications equipment, and all other communications equipment.",
    industries: ["communications-equipment-manufacturing"],
    name: "Communications Equipment Manufacturing",
    slug: "communications-equipment-manufacturing"
  },
  "3343": {
    description: "",
    industries: ["audio-and-video-equipment-manufacturing"],
    name: "Audio and Video Equipment Manufacturing",
    slug: "audio-and-video-equipment-manufacturing"
  },
  "3344": {
    description: "",
    industries: ["semiconductor-manufacturing"],
    name: "Semiconductor and Other Electronic Component Manufacturing",
    slug: "semiconductor-and-other-electronic-component-manufacturing"
  },
  "3345": {
    description: "",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Navigational, Measuring, Electromedical, and Control Instruments Manufacturing",
    slug: "navigational-measuring-electromedical-and-control-instruments-manufacturing"
  },
  "3346": {
    description: "",
    industries: ["magnetic-and-optical-media-manufacturing"],
    name: "Manufacturing and Reproducing Magnetic and Optical Media",
    slug: "manufacturing-and-reproducing-magnetic-and-optical-media"
  },
  "3351": {
    description: "",
    industries: ["electric-lighting-equipment-manufacturing"],
    name: "Electric Lighting Equipment Manufacturing",
    slug: "electric-lighting-equipment-manufacturing"
  },
  "3352": {
    description: "This industry group comprises establishments primarily engaged in manufacturing small electric appliances, electric housewares, and major household appliances.",
    industries: ["household-appliance-manufacturing"],
    name: "Household Appliance Manufacturing",
    slug: "household-appliance-manufacturing"
  },
  "3353": {
    description: "",
    industries: ["electrical-equipment-manufacturing"],
    name: "Electrical Equipment Manufacturing",
    slug: "electrical-equipment-manufacturing"
  },
  "3359": {
    description: "This industry group comprises establishments manufacturing electrical equipment and components (except electric lighting equipment, household-type appliances, transformers, switchgear, relays, motors, and generators).",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Other Electrical Equipment and Component Manufacturing",
    slug: "other-electrical-equipment-and-component-manufacturing"
  },
  "3361": {
    description: "This industry group comprises establishments primarily engaged in (1) manufacturing complete automobiles, light duty motor vehicles, and heavy duty trucks (i.e., body and chassis or unibody) or (2) manufacturing motor vehicle chassis only.",
    industries: ["motor-vehicle-manufacturing"],
    name: "Motor Vehicle Manufacturing",
    slug: "motor-vehicle-manufacturing"
  },
  "3362": {
    description: "",
    industries: ["transportation-equipment-manufacturing"],
    name: "Motor Vehicle Body and Trailer Manufacturing",
    slug: "motor-vehicle-body-and-trailer-manufacturing"
  },
  "3363": {
    description: "This industry group comprises establishments primarily engaged in manufacturing motor vehicle gasoline engines and engine parts, motor vehicle electrical and electronic equipment, motor vehicle steering and suspension components (except springs), motor vehicle brake systems, motor vehicle transmission and power train parts, motor vehicle seating and interior trim, motor vehicle metal stampings, and other motor vehicle parts and accessories.  This industry group includes establishments that rebuild motor vehicle parts.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Parts Manufacturing",
    slug: "motor-vehicle-parts-manufacturing"
  },
  "3364": {
    description: "",
    industries: ["aviation-and-aerospace-component-manufacturing"],
    name: "Aerospace Product and Parts Manufacturing",
    slug: "aerospace-product-and-parts-manufacturing"
  },
  "3365": {
    description: "",
    industries: ["railroad-equipment-manufacturing"],
    name: "Railroad Rolling Stock Manufacturing",
    slug: "railroad-rolling-stock-manufacturing"
  },
  "3366": {
    description: "",
    industries: ["shipbuilding"],
    name: "Ship and Boat Building",
    slug: "ship-and-boat-building"
  },
  "3369": {
    description: "",
    industries: ["transportation-equipment-manufacturing"],
    name: "Other Transportation Equipment Manufacturing",
    slug: "other-transportation-equipment-manufacturing"
  },
  "3371": {
    description: "This industry group comprises establishments manufacturing household-type furniture, such as living room, kitchen and bedroom furniture and institutional (i.e., public building) furniture, such as furniture for schools, theaters, and churches.",
    industries: ["household-and-institutional-furniture-manufacturing"],
    name: "Household and Institutional Furniture and Kitchen Cabinet Manufacturing",
    slug: "household-and-institutional-furniture-and-kitchen-cabinet-manufacturing"
  },
  "3372": {
    description: "",
    industries: ["office-furniture-and-fixtures-manufacturing"],
    name: "Office Furniture (including Fixtures) Manufacturing",
    slug: "office-furniture-including-fixtures-manufacturing"
  },
  "3379": {
    description: "This industry group comprises establishments manufacturing furniture related products, such as mattresses, blinds, and shades.",
    industries: ["furniture-and-home-furnishings-manufacturing"],
    name: "Other Furniture Related Product Manufacturing",
    slug: "other-furniture-related-product-manufacturing"
  },
  "3391": {
    description: "",
    industries: ["medical-equipment-manufacturing"],
    name: "Medical Equipment and Supplies Manufacturing",
    slug: "medical-equipment-and-supplies-manufacturing"
  },
  "3399": {
    description: "This industry group comprises establishments primarily engaged in miscellaneous manufacturing, such as jewelry and silverware manufacturing, sporting and athletic goods manufacturing, doll, toy, and game manufacturing, office supplies (except paper) manufacturing, sign manufacturing, and all other miscellaneous manufacturing.",
    industries: ["manufacturing"],
    name: "Other Miscellaneous Manufacturing",
    slug: "other-miscellaneous-manufacturing"
  },
  "4231": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of automobiles and other motor vehicles, motor vehicle supplies, tires, and new and used parts.",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Motor Vehicle and Motor Vehicle Parts and Supplies Merchant Wholesalers",
    slug: "motor-vehicle-and-motor-vehicle-parts-and-supplies-merchant-wholesalers"
  },
  "4232": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of furniture (except hospital beds, medical furniture, and drafting tables), home furnishings, and/or housewares.",
    industries: ["wholesale-furniture-and-home-furnishings"],
    name: "Furniture and Home Furnishing Merchant Wholesalers",
    slug: "furniture-and-home-furnishing-merchant-wholesalers"
  },
  "4233": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of lumber, plywood, millwork, and wood panels; brick, stone, and related construction materials; roofing, siding, and insulation materials; and other construction materials, including manufactured homes (i.e., mobile homes) and/or prefabricated buildings.",
    industries: ["wholesale-building-materials"],
    name: "Lumber and Other Construction Materials Merchant Wholesalers",
    slug: "lumber-and-other-construction-materials-merchant-wholesalers"
  },
  "4234": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of photographic equipment and supplies; office, computer, and computer peripheral equipment; and medical, dental, hospital, ophthalmic, and other commercial and professional equipment and supplies.",
    industries: ["retail"],
    name: "Professional and Commercial Equipment and Supplies Merchant Wholesalers",
    slug: "professional-and-commercial-equipment-and-supplies-merchant-wholesalers"
  },
  "4235": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of products of the primary metals industries (including metal service centers) and coal, coke, metal ores, and/or nonmetallic minerals (except precious and semiprecious stones and minerals used in construction).",
    industries: ["wholesale-metals-and-minerals"],
    name: "Metal and Mineral (except Petroleum) Merchant Wholesalers",
    slug: "metal-and-mineral-except-petroleum-merchant-wholesalers"
  },
  "4236": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of electrical apparatus and equipment, wiring supplies, and related equipment; household appliances, electric housewares, and consumer electronics; and other electronic parts and equipment.",
    industries: ["wholesale-appliances-electrical-and-electronics"],
    name: "Household Appliances and Electrical and Electronic Goods Merchant Wholesalers",
    slug: "household-appliances-and-electrical-and-electronic-goods-merchant-wholesalers"
  },
  "4237": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of hardware; plumbing and heating equipment and supplies (hydronics); warm air heating and air-conditioning equipment and supplies; and refrigeration equipment and supplies.",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Hardware, and Plumbing and Heating Equipment and Supplies Merchant Wholesalers",
    slug: "hardware-and-plumbing-and-heating-equipment-and-supplies-merchant-wholesalers"
  },
  "4238": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of construction, mining, farm, garden, industrial, service establishment, and transportation machinery, equipment, and supplies.",
    industries: ["wholesale-machinery"],
    name: "Machinery, Equipment, and Supplies Merchant Wholesalers",
    slug: "machinery-equipment-and-supplies-merchant-wholesalers"
  },
  "4239": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of sporting, recreational, toy, hobby, and jewelry goods and supplies, and precious stones and metals.",
    industries: ["retail"],
    name: "Miscellaneous Durable Goods Merchant Wholesalers",
    slug: "miscellaneous-durable-goods-merchant-wholesalers"
  },
  "4241": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of bulk printing and writing paper; stationery and office supplies; and industrial and personal service paper.",
    industries: ["wholesale-paper-products"],
    name: "Paper and Paper Product Merchant Wholesalers",
    slug: "paper-and-paper-product-merchant-wholesalers"
  },
  "4242": {
    description: "",
    industries: ["wholesale-drugs-and-sundries"],
    name: "Drugs and Druggists' Sundries Merchant Wholesalers",
    slug: "drugs-and-druggists-sundries-merchant-wholesalers"
  },
  "4243": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of piece goods, notions, and other dry goods; footwear; and clothing and clothing accessories.",
    industries: ["wholesale-apparel-and-sewing-supplies"],
    name: "Apparel, Piece Goods, and Notions Merchant Wholesalers",
    slug: "apparel-piece-goods-and-notions-merchant-wholesalers"
  },
  "4244": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of (1) a general line of groceries; (2) packaged frozen food; (3) dairy products; (4) poultry and poultry products; (5) confectioneries; (6) fish and seafood; (7) meats and meat products; (8) fresh fruits and vegetables; and (9) other grocery and related products.",
    industries: ["wholesale-food-and-beverage"],
    name: "Grocery and Related Product Merchant Wholesalers",
    slug: "grocery-and-related-product-merchant-wholesalers"
  },
  "4245": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of agricultural products (except raw milk, live poultry, and fresh fruits and vegetables), such as grains, field beans, livestock, and other farm product raw materials (excluding seeds).",
    industries: ["wholesale-raw-farm-products"],
    name: "Farm Product Raw Material Merchant Wholesalers",
    slug: "farm-product-raw-material-merchant-wholesalers"
  },
  "4246": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of chemicals, plastics materials and basic forms and shapes, and allied products.",
    industries: ["wholesale-chemical-and-allied-products"],
    name: "Chemical and Allied Products Merchant Wholesalers",
    slug: "chemical-and-allied-products-merchant-wholesalers"
  },
  "4247": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of petroleum and petroleum products, including liquefied petroleum gas.",
    industries: ["wholesale-petroleum-and-petroleum-products"],
    name: "Petroleum and Petroleum Products Merchant Wholesalers",
    slug: "petroleum-and-petroleum-products-merchant-wholesalers"
  },
  "4248": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of beer, ale, wine, and/or distilled alcoholic beverages.",
    industries: ["wholesale-alcoholic-beverages"],
    name: "Beer, Wine, and Distilled Alcoholic Beverage Merchant Wholesalers",
    slug: "beer-wine-and-distilled-alcoholic-beverage-merchant-wholesalers"
  },
  "4249": {
    description: "This industry group comprises establishments primarily engaged in the merchant wholesale distribution of nondurable goods, such as farm supplies; books, periodicals, and newspapers; flowers; nursery stock; paints; varnishes; tobacco and tobacco products; and other miscellaneous nondurable goods, such as cut Christmas trees and pet supplies.",
    industries: ["retail"],
    name: "Miscellaneous Nondurable Goods Merchant Wholesalers",
    slug: "miscellaneous-nondurable-goods-merchant-wholesalers"
  },
  "4251": {
    description: "",
    industries: ["internet-marketplace-platforms"],
    name: "Wholesale Trade Agents and Brokers",
    slug: "wholesale-trade-agents-and-brokers"
  },
  "4411": {
    description: "This industry group comprises establishments primarily engaged in retailing new and used automobiles and light trucks, such as sport utility vehicles, and passenger and cargo vans.",
    industries: ["retail-motor-vehicles"],
    name: "Automobile Dealers",
    slug: "automobile-dealers"
  },
  "4412": {
    description: "This industry group comprises establishments primarily engaged in retailing new and used vehicles (except automobiles, light trucks, such as sport utility vehicles, and passenger and cargo vans).",
    industries: ["retail"],
    name: "Other Motor Vehicle Dealers",
    slug: "other-motor-vehicle-dealers"
  },
  "4413": {
    description: "This industry group comprises establishments primarily engaged in retailing new, used, and/or rebuilt automotive parts and accessories, including tires and tubes.  Included in this industry group are establishments primarily engaged in retailing automotive parts and accessories in combination with automotive repair services.",
    industries: ["retail"],
    name: "Automotive Parts, Accessories, and Tire Retailers",
    slug: "automotive-parts-accessories-and-tire-retailers"
  },
  "4441": {
    description: "This industry group comprises establishments primarily engaged in retailing new building materials and supplies.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Building Material and Supplies Dealers",
    slug: "building-material-and-supplies-dealers"
  },
  "4442": {
    description: "This industry group comprises establishments primarily engaged in retailing new lawn and garden equipment and supplies.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Lawn and Garden Equipment and Supplies Retailers",
    slug: "lawn-and-garden-equipment-and-supplies-retailers"
  },
  "4451": {
    description: "This industry group comprises establishments primarily engaged in retailing a general line of food products. This industry group also includes vending machine operators.",
    industries: ["food-and-beverage-retail"],
    name: "Grocery and Convenience Retailers",
    slug: "grocery-and-convenience-retailers"
  },
  "4452": {
    description: "This industry group comprises establishments primarily engaged in retailing specialized lines of food not for immediate consumption.",
    industries: ["food-and-beverage-retail"],
    name: "Specialty Food Retailers",
    slug: "specialty-food-retailers"
  },
  "4453": {
    description: "",
    industries: ["food-and-beverage-retail"],
    name: "Beer, Wine, and Liquor Retailers",
    slug: "beer-wine-and-liquor-retailers"
  },
  "4491": {
    description: "This industry group comprises establishments primarily engaged in retailing new furniture and home furnishings.  Establishments in this industry group with fixed point-of-sale locations may operate from showrooms and have substantial areas for the presentation of their products.  Establishments in this industry group may provide incidental services, including interior decorating, product assembly, installation, or repair services.",
    industries: ["retail-furniture-and-home-furnishings"],
    name: "Furniture and Home Furnishings Retailers",
    slug: "furniture-and-home-furnishings-retailers"
  },
  "4492": {
    description: "",
    industries: ["retail-appliances-electrical-and-electronic-equipment"],
    name: "Electronics and Appliance Retailers",
    slug: "electronics-and-appliance-retailers"
  },
  "4551": {
    description: "",
    industries: ["retail"],
    name: "Department Stores",
    slug: "department-stores"
  },
  "4552": {
    description: "",
    industries: ["retail"],
    name: "Warehouse Clubs, Supercenters, and Other General Merchandise Retailers",
    slug: "warehouse-clubs-supercenters-and-other-general-merchandise-retailers"
  },
  "4561": {
    description: "",
    industries: ["retail-health-and-personal-care-products"],
    name: "Health and Personal Care Retailers",
    slug: "health-and-personal-care-retailers"
  },
  "4571": {
    description: "This industry group comprises establishments primarily engaged in retailing automotive fuels (e.g., gasoline, diesel fuel, gasohol, alternative fuels) and automotive oils or retailing these products in combination with convenience store items.  These establishments have specialized equipment for storing and dispensing automotive fuels.",
    industries: ["retail-gasoline"],
    name: "Gasoline Stations",
    slug: "gasoline-stations"
  },
  "4572": {
    description: "",
    industries: ["retail"],
    name: "Fuel Dealers",
    slug: "fuel-dealers"
  },
  "4581": {
    description: "",
    industries: ["retail-apparel-and-fashion"],
    name: "Clothing and Clothing Accessories Retailers",
    slug: "clothing-and-clothing-accessories-retailers"
  },
  "4582": {
    description: "",
    industries: ["retail-apparel-and-fashion"],
    name: "Shoe Retailers",
    slug: "shoe-retailers"
  },
  "4583": {
    description: "This industry group comprises establishments primarily engaged in retailing new jewelry (except costume jewelry); new sterling and plated silverware; new watches and clocks; and new luggage with or without a general line of new leather goods and accessories, such as hats, gloves, handbags, ties, and belts.",
    industries: ["retail-luxury-goods-and-jewelry"],
    name: "Jewelry, Luggage, and Leather Goods Retailers",
    slug: "jewelry-luggage-and-leather-goods-retailers"
  },
  "4591": {
    description: "This industry group comprises establishments primarily engaged in retailing new sporting goods, toys, games, hobby supplies, sewing supplies, needlework accessories, and musical instruments.",
    industries: ["retail"],
    name: "Sporting Goods, Hobby, and Musical Instrument Retailers",
    slug: "sporting-goods-hobby-and-musical-instrument-retailers"
  },
  "4592": {
    description: "",
    industries: ["retail-books-and-printed-news"],
    name: "Book Retailers and News Dealers",
    slug: "book-retailers-and-news-dealers"
  },
  "4593": {
    description: "",
    industries: ["retail-florists"],
    name: "Florists",
    slug: "florists"
  },
  "4594": {
    description: "This industry group comprises establishments primarily engaged in retailing new office supplies, stationery, gifts, novelty merchandise, and souvenirs.",
    industries: ["retail-office-supplies-and-gifts"],
    name: "Office Supplies, Stationery, and Gift Retailers",
    slug: "office-supplies-stationery-and-gift-retailers"
  },
  "4595": {
    description: "",
    industries: ["retail-recyclable-materials-and-used-merchandise"],
    name: "Used Merchandise Retailers",
    slug: "used-merchandise-retailers"
  },
  "4599": {
    description: "This industry group comprises establishments primarily engaged in retailing miscellaneous specialized lines of merchandise (except motor vehicle and parts dealers; building material and garden equipment and supplies dealers; food and beverage retailers; furniture, home furnishings, electronics, and appliance retailers; general merchandise retailers; health and personal care retailers; gasoline stations and fuel dealers; clothing, clothing accessories, shoe, and jewelry retailers; sporting goods, hobby, and musical instrument retailers; book retailers and news dealers; florists; office supplies, stationery, and gift retailers; and used merchandise retailers).",
    industries: ["retail"],
    name: "Other Miscellaneous Retailers",
    slug: "other-miscellaneous-retailers"
  },
  "4811": {
    description: "",
    industries: ["airlines-and-aviation"],
    name: "Scheduled Air Transportation",
    slug: "scheduled-air-transportation"
  },
  "4812": {
    description: "",
    industries: ["airlines-and-aviation"],
    name: "Nonscheduled Air Transportation",
    slug: "nonscheduled-air-transportation"
  },
  "4821": {
    description: "",
    industries: ["rail-transportation"],
    name: "Rail Transportation",
    slug: "rail-transportation"
  },
  "4831": {
    description: "",
    industries: ["maritime-transportation"],
    name: "Deep Sea, Coastal, and Great Lakes Water Transportation",
    slug: "deep-sea-coastal-and-great-lakes-water-transportation"
  },
  "4832": {
    description: "",
    industries: ["maritime-transportation"],
    name: "Inland Water Transportation",
    slug: "inland-water-transportation"
  },
  "4841": {
    description: "This industry group comprises establishments primarily engaged in providing general freight trucking.  General freight trucking establishments handle a wide variety of commodities, generally palletized, and transported in a container or van trailer.  The establishments of this industry group provide a combination of the following network activities: local pick-up, local sorting and terminal operations, line-haul, destination sorting and terminal operations, and local delivery.",
    industries: ["truck-transportation"],
    name: "General Freight Trucking",
    slug: "general-freight-trucking"
  },
  "4842": {
    description: "This industry group comprises establishments primarily engaged in providing local or long-distance specialized freight trucking.  The establishments of this industry are primarily engaged in the transportation of freight which, because of size, weight, shape, or other inherent characteristics, requires specialized equipment, such as flatbeds, tankers, or refrigerated trailers.  This industry includes the transportation of used household, institutional, and commercial furniture and equipment.",
    industries: ["truck-transportation"],
    name: "Specialized Freight Trucking",
    slug: "specialized-freight-trucking"
  },
  "4851": {
    description: "",
    industries: ["urban-transit-services"],
    name: "Urban Transit Systems",
    slug: "urban-transit-systems"
  },
  "4852": {
    description: "",
    industries: ["interurban-and-rural-bus-services"],
    name: "Interurban and Rural Bus Transportation",
    slug: "interurban-and-rural-bus-transportation"
  },
  "4853": {
    description: "This industry group comprises establishments primarily engaged in providing passenger transportation by automobile or van or providing an array of specialty and luxury passenger transportation services via limousine or luxury sedan generally on a reserved basis.  These establishments do not operate over regular routes and on regular schedules.",
    industries: ["taxi-and-limousine-services"],
    name: "Taxi and Limousine Service",
    slug: "taxi-and-limousine-service"
  },
  "4854": {
    description: "",
    industries: ["school-and-employee-bus-services"],
    name: "School and Employee Bus Transportation",
    slug: "school-and-employee-bus-transportation"
  },
  "4855": {
    description: "",
    industries: ["ground-passenger-transportation"],
    name: "Charter Bus Industry",
    slug: "charter-bus-industry"
  },
  "4859": {
    description: "",
    industries: ["shuttles-and-special-needs-transportation-services"],
    name: "Other Transit and Ground Passenger Transportation",
    slug: "other-transit-and-ground-passenger-transportation"
  },
  "4861": {
    description: "",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation of Crude Oil",
    slug: "pipeline-transportation-of-crude-oil"
  },
  "4862": {
    description: "",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation of Natural Gas",
    slug: "pipeline-transportation-of-natural-gas"
  },
  "4869": {
    description: "This industry group comprises establishments primarily engaged in the pipeline transportation of products (except crude oil and natural gas).",
    industries: ["pipeline-transportation"],
    name: "Other Pipeline Transportation",
    slug: "other-pipeline-transportation"
  },
  "4871": {
    description: "",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Land",
    slug: "scenic-and-sightseeing-transportation-land"
  },
  "4872": {
    description: "",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Water",
    slug: "scenic-and-sightseeing-transportation-water"
  },
  "4879": {
    description: "",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Other",
    slug: "scenic-and-sightseeing-transportation-other"
  },
  "4881": {
    description: "This industry group comprises establishments primarily engaged in providing services to the air transportation industry.  These services include airport operation, servicing, repairing (except factory conversion and overhaul of aircraft), maintaining and storing aircraft, and ferrying aircraft.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Support Activities for Air Transportation",
    slug: "support-activities-for-air-transportation"
  },
  "4882": {
    description: "",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Support Activities for Rail Transportation",
    slug: "support-activities-for-rail-transportation"
  },
  "4883": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) operating ports, harbors (including docking and pier facilities), or canals; (2) providing stevedoring and other marine cargo handling services (except warehousing); (3) providing navigational services to shipping; or (4) providing other services to water transportation.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Support Activities for Water Transportation",
    slug: "support-activities-for-water-transportation"
  },
  "4884": {
    description: "This industry group comprises establishments primarily engaged in (1) towing light or heavy motor vehicles, both local and long-distance, or (2) providing other services to road network users.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Support Activities for Road Transportation",
    slug: "support-activities-for-road-transportation"
  },
  "4885": {
    description: "",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Freight Transportation Arrangement",
    slug: "freight-transportation-arrangement"
  },
  "4889": {
    description: "",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Support Activities for Transportation",
    slug: "other-support-activities-for-transportation"
  },
  "4911": {
    description: "",
    industries: ["postal-services"],
    name: "Postal Service",
    slug: "postal-service"
  },
  "4921": {
    description: "",
    industries: ["freight-and-package-transportation"],
    name: "Couriers and Express Delivery Services",
    slug: "couriers-and-express-delivery-services"
  },
  "4922": {
    description: "",
    industries: ["freight-and-package-transportation"],
    name: "Local Messengers and Local Delivery",
    slug: "local-messengers-and-local-delivery"
  },
  "4931": {
    description: "",
    industries: ["warehousing-and-storage"],
    name: "Warehousing and Storage",
    slug: "warehousing-and-storage"
  },
  "5121": {
    description: "This industry group comprises establishments primarily engaged in the production and/or distribution of motion pictures, videos, television programs, or commercials; in the exhibition of motion pictures; or in the provision of postproduction and related services.",
    industries: ["movies-and-sound-recording"],
    name: "Motion Picture and Video Industries",
    slug: "motion-picture-and-video-industries"
  },
  "5122": {
    description: "This industry group comprises establishments primarily engaged in producing and distributing musical recordings, publishing music, or providing sound recording and related services.",
    industries: ["sound-recording"],
    name: "Sound Recording Industries",
    slug: "sound-recording-industries"
  },
  "5131": {
    description: "This industry group comprises establishments primarily engaged in publishing newspapers, magazines, other periodicals, books, directories and mailing lists, and other works, such as calendars, greeting cards, and maps.  These works are characterized by the intellectual creativity required in their development and are usually protected by copyright.  Publishers distribute or arrange for the distribution of these works. Publishing establishments may create the works in-house, or contract for, purchase, or compile works that were originally created by others.  These works may be published in one or more formats, such as print and/or electronic form, including proprietary electronic networks or exclusively on the Internet.  Establishments in this industry may print, reproduce, or offer direct access to the works themselves or may arrange with others to carry out such functions. Establishments that both print and publish may fill excess capacity with commercial or job printing.  However, the publishing activity is still considered to be the primary activity of these establishments.",
    industries: ["book-and-periodical-publishing"],
    name: "Newspaper, Periodical, Book, and Directory Publishers",
    slug: "newspaper-periodical-book-and-directory-publishers"
  },
  "5132": {
    description: "",
    industries: ["software-development"],
    name: "Software Publishers",
    slug: "software-publishers"
  },
  "5161": {
    description: "This industry group comprises establishments operating radio or television broadcasting stations for the programming and transmission of programs to the public.  Programming may originate in their own studio, from an affiliated network, or from external sources.",
    industries: ["broadcast-media-production-and-distribution"],
    name: "Radio and Television Broadcasting Stations",
    slug: "radio-and-television-broadcasting-stations"
  },
  "5162": {
    description: "",
    industries: ["cable-and-satellite-programming"],
    name: "Media Streaming Distribution Services, Social Networks, and Other Media Networks and Content Providers",
    slug: "media-streaming-distribution-services-social-networks-and-other-media-networks-and-content-providers"
  },
  "5171": {
    description: "This industry group comprises establishments primarily engaged in (1) operating transmission facilities and infrastructure that they own and/or lease, and providing telecommunications services using wired and wireless telecommunications networks; (2) reselling wired and wireless telecommunications services (except satellite); and (3) acting as agents for wireless telecommunications services.",
    industries: ["telecommunications-carriers"],
    name: "Wired and Wireless Telecommunications (except Satellite)",
    slug: "wired-and-wireless-telecommunications-except-satellite"
  },
  "5174": {
    description: "",
    industries: ["satellite-telecommunications"],
    name: "Satellite Telecommunications",
    slug: "satellite-telecommunications"
  },
  "5178": {
    description: "",
    industries: ["telecommunications"],
    name: "All Other Telecommunications",
    slug: "all-other-telecommunications"
  },
  "5182": {
    description: "",
    industries: ["data-infrastructure-and-analytics"],
    name: "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
    slug: "computing-infrastructure-providers-data-processing-web-hosting-and-related-services"
  },
  "5192": {
    description: "",
    industries: ["information-services"],
    name: "Web Search Portals, Libraries, Archives, and Other Information Services",
    slug: "web-search-portals-libraries-archives-and-other-information-services"
  },
  "5211": {
    description: "",
    industries: ["financial-services"],
    name: "Monetary Authorities-Central Bank",
    slug: "monetary-authorities-central-bank"
  },
  "5221": {
    description: "This industry group comprises establishments primarily engaged in accepting deposits (or share deposits) and in lending funds from these deposits.  Within this group, industries are defined on the basis of differences in the types of deposit liabilities assumed and in the nature of the credit extended.",
    industries: ["credit-intermediation"],
    name: "Depository Credit Intermediation",
    slug: "depository-credit-intermediation"
  },
  "5222": {
    description: "This industry group comprises establishments, both public (government-sponsored enterprises) and private, primarily engaged in extending credit or lending funds raised by credit market borrowing, such as issuing commercial paper or other debt instruments or by borrowing from other financial intermediaries.  Within this group, industries are defined on the basis of the type of credit being extended.",
    industries: ["credit-intermediation"],
    name: "Nondepository Credit Intermediation",
    slug: "nondepository-credit-intermediation"
  },
  "5223": {
    description: "This industry group comprises establishments primarily engaged in facilitating credit intermediation by performing activities, such as arranging loans by bringing borrowers and lenders together and clearing checks and credit card transactions.",
    industries: ["credit-intermediation"],
    name: "Activities Related to Credit Intermediation",
    slug: "activities-related-to-credit-intermediation"
  },
  "5231": {
    description: "This industry group comprises establishments primarily engaged in putting capital at risk in the process of underwriting securities issues or in making markets for securities and commodities; and those acting as agents and/or brokers between buyers and sellers of securities and commodities, usually charging a commission.",
    industries: ["capital-markets"],
    name: "Securities and Commodity Contracts Intermediation and Brokerage",
    slug: "securities-and-commodity-contracts-intermediation-and-brokerage"
  },
  "5232": {
    description: "",
    industries: ["securities-and-commodity-exchanges"],
    name: "Securities and Commodity Exchanges",
    slug: "securities-and-commodity-exchanges"
  },
  "5239": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) acting as principals in buying or selling financial contracts (except investment bankers, securities dealers, and commodity contracts dealers); (2) acting as agents (i.e., brokers) (except securities brokerages and commodity contracts brokerages) in buying or selling financial contracts; or (3) providing other investment services (except securities and commodity exchanges), such as portfolio management; investment advice; and trust, fiduciary, and custody services.",
    industries: ["investment-management"],
    name: "Other Financial Investment Activities",
    slug: "other-financial-investment-activities"
  },
  "5241": {
    description: "This industry group comprises establishments primarily engaged in underwriting (assuming the risk, assigning premiums, and so forth) annuities and insurance policies and investing premiums to build up a portfolio of financial assets to be used against future claims.  Direct insurance carriers are establishments that are primarily engaged in initially underwriting and assuming the risk of annuities and insurance policies.  Reinsurance carriers are establishments that are primarily engaged in assuming all or part of the risk associated with an existing insurance policy (or set of policies) originally underwritten by another insurance carrier. Industries are defined in terms of the type of risk being insured against, such as death, loss of employment because of age or disability, and/or property damage.  Contributions and premiums are set on the basis of actuarial calculations of probable payouts based on risk factors from experience tables and expected investment returns on reserves.",
    industries: ["insurance-carriers"],
    name: "Insurance Carriers",
    slug: "insurance-carriers"
  },
  "5242": {
    description: "This industry group comprises establishments primarily engaged in (1) acting as agents (i.e., brokers) in selling annuities and insurance policies or (2) providing other employee benefits and insurance related services, such as claims adjustment and third party administration.",
    industries: ["insurance"],
    name: "Agencies, Brokerages, and Other Insurance Related Activities",
    slug: "agencies-brokerages-and-other-insurance-related-activities"
  },
  "5251": {
    description: "This industry group comprises legal entities (i.e., funds, plans, and/or programs) organized to provide insurance and employee benefits exclusively for the sponsor, firm, or its employees or members.",
    industries: ["insurance-and-employee-benefit-funds"],
    name: "Insurance and Employee Benefit Funds",
    slug: "insurance-and-employee-benefit-funds"
  },
  "5259": {
    description: "This industry group comprises legal entities (i.e., investment pools and/or funds) organized to pool securities or other assets (except insurance and employee benefit funds) on behalf of shareholders, unitholders, or beneficiaries.",
    industries: ["trusts-and-estates"],
    name: "Other Investment Pools and Funds",
    slug: "other-investment-pools-and-funds"
  },
  "5311": {
    description: "This industry group comprises establishments primarily engaged in acting as lessors of (1) residential buildings and dwellings; (2) nonresidential buildings (except miniwarehouses); (3) miniwarehouses and self-storage units; and (4) other real estate property.  This industry group includes equity real estate investment trusts (REITs) primarily engaged in leasing buildings, dwellings, or other real estate property to others.",
    industries: ["leasing-residential-real-estate"],
    name: "Lessors of Real Estate",
    slug: "lessors-of-real-estate"
  },
  "5312": {
    description: "",
    industries: ["real-estate-agents-and-brokers"],
    name: "Offices of Real Estate Agents and Brokers",
    slug: "offices-of-real-estate-agents-and-brokers"
  },
  "5313": {
    description: "This industry group comprises establishments primarily engaged in providing real estate services (except lessors of real estate and offices of real estate agents and brokers).  Included in this industry group are establishments primarily engaged in managing real estate for others and appraising real estate.",
    industries: ["leasing-residential-real-estate"],
    name: "Activities Related to Real Estate",
    slug: "activities-related-to-real-estate"
  },
  "5321": {
    description: "This industry group comprises establishments primarily engaged in renting or leasing passenger cars and trucks without drivers and utility trailers.  These establishments generally operate from a retail-like facility.  Some establishments offer only short-term rental, others only longer-term leases, and some provide both types of services.",
    industries: ["equipment-rental-services"],
    name: "Automotive Equipment Rental and Leasing",
    slug: "automotive-equipment-rental-and-leasing"
  },
  "5322": {
    description: "This industry group comprises establishments primarily engaged in renting personal and household-type goods.  Establishments classified in this industry group generally provide short-term rental although in some instances, the goods may be leased for longer periods of time.  These establishments often operate from a retail-like or storefront facility.",
    industries: ["consumer-goods-rental"],
    name: "Consumer Goods Rental",
    slug: "consumer-goods-rental"
  },
  "5323": {
    description: "",
    industries: ["equipment-rental-services"],
    name: "General Rental Centers",
    slug: "general-rental-centers"
  },
  "5324": {
    description: "This industry group comprises establishments primarily engaged in renting or leasing commercial-type and industrial-type machinery and equipment.  Establishments included in this industry group are generally involved in providing capital or investment-type equipment that clients use in their business operations.  These establishments typically cater to a business clientele and do not generally operate a retail-like or storefront facility.",
    industries: ["commercial-and-industrial-equipment-rental"],
    name: "Commercial and Industrial Machinery and Equipment Rental and Leasing",
    slug: "commercial-and-industrial-machinery-and-equipment-rental-and-leasing"
  },
  "5331": {
    description: "",
    industries: ["real-estate-and-equipment-rental-services"],
    name: "Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)",
    slug: "lessors-of-nonfinancial-intangible-assets-except-copyrighted-works"
  },
  "5411": {
    description: "This industry group comprises establishments primarily engaged in offering legal services, such as those offered by offices of lawyers, offices of notaries, and title abstract and settlement offices, and paralegal services.",
    industries: ["legal-services"],
    name: "Legal Services",
    slug: "legal-services"
  },
  "5412": {
    description: "",
    industries: ["accounting"],
    name: "Accounting, Tax Preparation, Bookkeeping, and Payroll Services",
    slug: "accounting-tax-preparation-bookkeeping-and-payroll-services"
  },
  "5413": {
    description: "This industry group comprises establishments primarily engaged in architectural, engineering, and related services, such as drafting services, building inspection services, geophysical surveying and mapping services, surveying and mapping (except geophysical) services, and testing services.",
    industries: ["professional-services"],
    name: "Architectural, Engineering, and Related Services",
    slug: "architectural-engineering-and-related-services"
  },
  "5414": {
    description: "This industry group comprises establishments providing specialized design services (except architectural, engineering, and computer systems design).",
    industries: ["design-services"],
    name: "Specialized Design Services",
    slug: "specialized-design-services"
  },
  "5415": {
    description: "",
    industries: ["it-services-and-it-consulting"],
    name: "Computer Systems Design and Related Services",
    slug: "computer-systems-design-and-related-services"
  },
  "5416": {
    description: "This industry group comprises establishments primarily engaged in providing advice and assistance to businesses and other organizations on management, environmental, scientific, and technical issues.",
    industries: ["business-consulting-and-services"],
    name: "Management, Scientific, and Technical Consulting Services",
    slug: "management-scientific-and-technical-consulting-services"
  },
  "5417": {
    description: "This industry group comprises establishments engaged in conducting original investigation undertaken on a systematic basis to gain new knowledge (research) and/or the application of research findings or other scientific knowledge for the creation of new or significantly improved products or processes (experimental development).  Techniques may include modeling and simulation.  The industries within this industry group are defined on the basis of the domain of research; that is, on the scientific expertise of the establishment.",
    industries: ["research-services"],
    name: "Scientific Research and Development Services",
    slug: "scientific-research-and-development-services"
  },
  "5418": {
    description: "This industry group comprises establishments primarily engaged in advertising, public relations, and related services, such as media buying, independent media representation, indoor and outdoor display advertising, direct mail advertising, advertising material distribution services, and other services related to advertising.",
    industries: ["marketing-services"],
    name: "Advertising, Public Relations, and Related Services",
    slug: "advertising-public-relations-and-related-services"
  },
  "5419": {
    description: "This industry group comprises establishments engaged in professional, scientific, and technical services (except legal services; accounting, tax preparation, bookkeeping, and related services; architectural, engineering, and related services; specialized design services; computer systems design and related services; management, scientific, and technical consulting services; scientific research and development services; and advertising, public relations, and related services).",
    industries: ["professional-services"],
    name: "Other Professional, Scientific, and Technical Services",
    slug: "other-professional-scientific-and-technical-services"
  },
  "5511": {
    description: "",
    industries: ["holding-companies"],
    name: "Management of Companies and Enterprises",
    slug: "management-of-companies-and-enterprises"
  },
  "5611": {
    description: "",
    industries: ["office-administration"],
    name: "Office Administrative Services",
    slug: "office-administrative-services"
  },
  "5612": {
    description: "",
    industries: ["facilities-services"],
    name: "Facilities Support Services",
    slug: "facilities-support-services"
  },
  "5613": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) listing employment vacancies and referring or placing applicants for employment; (2) providing executive search, recruitment, and placement services; (3) supplying workers to clients' businesses for limited periods of time to supplement the working force of the client; or (4) providing human resources and human resource management services to client businesses and households.",
    industries: ["staffing-and-recruiting"],
    name: "Employment Services",
    slug: "employment-services"
  },
  "5614": {
    description: "This industry group comprises establishments engaged in performing activities that are ongoing routine business support functions for clients on a contract or fee basis, or serving other establishments of the same enterprise.",
    industries: ["administrative-and-support-services"],
    name: "Business Support Services",
    slug: "business-support-services"
  },
  "5615": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) travel agency services; (2) arranging and assembling tours; or (3) other travel arrangement and reservation services.",
    industries: ["travel-arrangements"],
    name: "Travel Arrangement and Reservation Services",
    slug: "travel-arrangement-and-reservation-services"
  },
  "5616": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) investigation, guard, and armored car services; (2) selling security systems, such as burglar and fire alarms and locking devices, along with installation, repair, or monitoring services; or (3) remote monitoring of electronic security alarm systems.",
    industries: ["security-and-investigations"],
    name: "Investigation and Security Services",
    slug: "investigation-and-security-services"
  },
  "5617": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) exterminating and pest control services; (2) janitorial services; (3) landscaping services; (4) carpet and upholstery cleaning services; or (5) other services to buildings and dwellings.",
    industries: ["administrative-and-support-services"],
    name: "Services to Buildings and Dwellings",
    slug: "services-to-buildings-and-dwellings"
  },
  "5619": {
    description: "This industry group comprises establishments primarily engaged in providing day-to-day business and other organizational support services (except office administrative services; facilities support services; employment services; business support services; travel arrangement and reservation services; security and investigation services; and services to buildings and dwellings).",
    industries: ["administrative-and-support-services"],
    name: "Other Support Services",
    slug: "other-support-services"
  },
  "5621": {
    description: "",
    industries: ["waste-collection"],
    name: "Waste Collection",
    slug: "waste-collection"
  },
  "5622": {
    description: "",
    industries: ["waste-treatment-and-disposal"],
    name: "Waste Treatment and Disposal",
    slug: "waste-treatment-and-disposal"
  },
  "5629": {
    description: "This industry group comprises establishments primarily engaged in remediation and other waste management services (except waste collection, waste treatment and disposal, and waste management consulting services).",
    industries: ["administrative-and-support-services"],
    name: "Remediation and Other Waste Management Services",
    slug: "remediation-and-other-waste-management-services"
  },
  "6111": {
    description: "",
    industries: ["primary-and-secondary-education"],
    name: "Elementary and Secondary Schools",
    slug: "elementary-and-secondary-schools"
  },
  "6112": {
    description: "",
    industries: ["education"],
    name: "Junior Colleges",
    slug: "junior-colleges"
  },
  "6113": {
    description: "",
    industries: ["higher-education"],
    name: "Colleges, Universities, and Professional Schools",
    slug: "colleges-universities-and-professional-schools"
  },
  "6114": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) offering courses in office procedures and secretarial and stenographic skills and may offer courses in basic office skills, such as word processing; (2) conducting computer training (except computer repair); or (3) offering an array of short duration courses and seminars for management and professional development.  Instruction may be provided in diverse settings, such as the establishment's or client's training facilities, educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods.",
    industries: ["professional-training-and-coaching"],
    name: "Business Schools and Computer and Management Training",
    slug: "business-schools-and-computer-and-management-training"
  },
  "6115": {
    description: "",
    industries: ["technical-and-vocational-training"],
    name: "Technical and Trade Schools",
    slug: "technical-and-trade-schools"
  },
  "6116": {
    description: "This industry group comprises establishments primarily engaged in offering or providing instruction (except academic schools, colleges, and universities; business, computer, and management instruction; and technical and trade instruction).  Instruction may be provided in diverse settings, such as the establishment's or client's training facilities, educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods.",
    industries: ["education"],
    name: "Other Schools and Instruction",
    slug: "other-schools-and-instruction"
  },
  "6117": {
    description: "",
    industries: ["education"],
    name: "Educational Support Services",
    slug: "educational-support-services"
  },
  "6211": {
    description: "",
    industries: ["physicians"],
    name: "Offices of Physicians",
    slug: "offices-of-physicians"
  },
  "6212": {
    description: "",
    industries: ["dentists"],
    name: "Offices of Dentists",
    slug: "offices-of-dentists"
  },
  "6213": {
    description: "This industry group comprises establishments of independent health practitioners (except physicians and dentists).",
    industries: ["medical-practices"],
    name: "Offices of Other Health Practitioners",
    slug: "offices-of-other-health-practitioners"
  },
  "6214": {
    description: "This industry group comprises establishments with medical staff primarily engaged in providing a range of outpatient services, such as family planning, diagnosis and treatment of mental health disorders and alcohol and other substance abuse, and other general or specialized outpatient care.",
    industries: ["medical-practices"],
    name: "Outpatient Care Centers",
    slug: "outpatient-care-centers"
  },
  "6215": {
    description: "",
    industries: ["medical-and-diagnostic-laboratories"],
    name: "Medical and Diagnostic Laboratories",
    slug: "medical-and-diagnostic-laboratories"
  },
  "6216": {
    description: "",
    industries: ["home-health-care-services"],
    name: "Home Health Care Services",
    slug: "home-health-care-services"
  },
  "6219": {
    description: "This industry group comprises establishments primarily engaged in providing ambulatory health care services (except offices of physicians, dentists, and other health practitioners; outpatient care centers; medical laboratories and diagnostic imaging centers; and home health care providers).",
    industries: ["medical-practices"],
    name: "Other Ambulatory Health Care Services",
    slug: "other-ambulatory-health-care-services"
  },
  "6221": {
    description: "",
    industries: ["hospitals"],
    name: "General Medical and Surgical Hospitals",
    slug: "general-medical-and-surgical-hospitals"
  },
  "6222": {
    description: "",
    industries: ["hospitals"],
    name: "Psychiatric and Substance Abuse Hospitals",
    slug: "psychiatric-and-substance-abuse-hospitals"
  },
  "6223": {
    description: "",
    industries: ["hospitals"],
    name: "Specialty (except Psychiatric and Substance Abuse) Hospitals",
    slug: "specialty-except-psychiatric-and-substance-abuse-hospitals"
  },
  "6231": {
    description: "",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Nursing Care Facilities (Skilled Nursing Facilities)",
    slug: "nursing-care-facilities-skilled-nursing-facilities"
  },
  "6232": {
    description: "This industry group comprises establishments primarily engaged in providing residential care (but not licensed hospital care) to people with intellectual and developmental disabilities, mental illness, or substance abuse problems.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Residential Intellectual and Developmental Disability, Mental Health, and Substance Abuse Facilities",
    slug: "residential-intellectual-and-developmental-disability-mental-health-and-substance-abuse-facilities"
  },
  "6233": {
    description: "",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Continuing Care Retirement Communities and Assisted Living Facilities for the Elderly",
    slug: "continuing-care-retirement-communities-and-assisted-living-facilities-for-the-elderly"
  },
  "6239": {
    description: "",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Other Residential Care Facilities",
    slug: "other-residential-care-facilities"
  },
  "6241": {
    description: "This industry group comprises establishments primarily engaged in providing nonresidential social assistance to children and youth, the elderly, persons with disabilities, and all other individuals and families.",
    industries: ["individual-and-family-services"],
    name: "Individual and Family Services",
    slug: "individual-and-family-services"
  },
  "6242": {
    description: "This industry group comprises establishments primarily engaged in one of the following: (1) collecting, preparing, and delivering food for the needy; (2) providing short-term emergency shelter, temporary residential shelter, transitional housing, volunteer construction or repair of low-cost housing, and/or repair of homes for individuals or families in need; or (3) providing food, shelter, clothing, medical relief, resettlement, and counseling to victims of domestic or international disasters or conflicts (e.g., wars).",
    industries: ["community-services"],
    name: "Community Food and Housing, and Emergency and Other Relief Services",
    slug: "community-food-and-housing-and-emergency-and-other-relief-services"
  },
  "6243": {
    description: "",
    industries: ["vocational-rehabilitation-services"],
    name: "Vocational Rehabilitation Services",
    slug: "vocational-rehabilitation-services"
  },
  "6244": {
    description: "",
    industries: ["child-day-care-services"],
    name: "Child Day Care Services",
    slug: "child-day-care-services"
  },
  "7111": {
    description: "This industry group comprises establishments primarily engaged in producing live presentations involving the performances of actors and actresses, singers, dancers, musical groups and artists, and other performing artists.",
    industries: ["performing-arts"],
    name: "Performing Arts Companies",
    slug: "performing-arts-companies"
  },
  "7112": {
    description: "",
    industries: ["spectator-sports"],
    name: "Spectator Sports",
    slug: "spectator-sports"
  },
  "7113": {
    description: "This industry group comprises establishments primarily engaged in organizing, promoting, and/or managing live performing arts productions, sports events, and similar events, held in facilities that they manage and operate or in facilities that are managed and operated by others.",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Promoters of Performing Arts, Sports, and Similar Events",
    slug: "promoters-of-performing-arts-sports-and-similar-events"
  },
  "7114": {
    description: "",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Agents and Managers for Artists, Athletes, Entertainers, and Other Public Figures",
    slug: "agents-and-managers-for-artists-athletes-entertainers-and-other-public-figures"
  },
  "7115": {
    description: "",
    industries: ["artists-and-writers"],
    name: "Independent Artists, Writers, and Performers",
    slug: "independent-artists-writers-and-performers"
  },
  "7121": {
    description: "",
    industries: ["museums-historical-sites-and-zoos"],
    name: "Museums, Historical Sites, and Similar Institutions",
    slug: "museums-historical-sites-and-similar-institutions"
  },
  "7131": {
    description: "This industry group comprises establishments primarily engaged in operating amusement parks and amusement arcades and parlors.",
    industries: ["amusement-parks-and-arcades"],
    name: "Amusement Parks and Arcades",
    slug: "amusement-parks-and-arcades"
  },
  "7132": {
    description: "This industry group comprises establishments (except casino hotels) primarily engaged in operating gambling facilities, such as casinos, bingo halls, and video gaming terminals, or in the provision of gambling services, such as lotteries and off-track betting.  Casino hotels are classified in Industry 72112, Casino Hotels.",
    industries: ["gambling-facilities-and-casinos"],
    name: "Gambling Industries",
    slug: "gambling-industries"
  },
  "7139": {
    description: "This industry group comprises establishments primarily engaged in operating golf courses and country clubs; skiing facilities; marinas; fitness and recreational sports centers; bowling centers; and providing other amusement and recreation services.",
    industries: ["recreational-facilities"],
    name: "Other Amusement and Recreation Industries",
    slug: "other-amusement-and-recreation-industries"
  },
  "7211": {
    description: "This industry group comprises establishments primarily engaged in providing short-term lodging in facilities, such as hotels, motels, casino hotels, and bed-and-breakfast inns.  In addition to lodging, these establishments may provide a range of other services to their guests.",
    industries: ["hospitality"],
    name: "Traveler Accommodation",
    slug: "traveler-accommodation"
  },
  "7212": {
    description: "",
    industries: ["accommodation-services"],
    name: "RV (Recreational Vehicle) Parks and Recreational Camps",
    slug: "rv-recreational-vehicle-parks-and-recreational-camps"
  },
  "7213": {
    description: "",
    industries: ["accommodation-services"],
    name: "Rooming and Boarding Houses, Dormitories, and Workers' CampsT",
    slug: "rooming-and-boarding-houses-dormitories-and-workers-camps-t"
  },
  "7223": {
    description: "This industry group comprises establishments primarily engaged in providing food services at one or more of the following locations: (1) the customer's location; (2) a location designated by the customer; or (3) from motorized vehicles or nonmotorized carts.",
    industries: ["food-and-beverage-services"],
    name: "Special Food Services",
    slug: "special-food-services"
  },
  "7224": {
    description: "",
    industries: ["bars-taverns-and-nightclubs"],
    name: "Drinking Places (Alcoholic Beverages)",
    slug: "drinking-places-alcoholic-beverages"
  },
  "7225": {
    description: "",
    industries: ["restaurants"],
    name: "Restaurants and Other Eating Places",
    slug: "restaurants-and-other-eating-places"
  },
  "8111": {
    description: "This industry group comprises establishments involved in providing repair and maintenance services for automotive vehicles, such as passenger cars, trucks, and vans, and all trailers.  Establishments in this industry group employ mechanics with specialized technical skills to diagnose and repair the mechanical and electrical systems for automotive vehicles, repair automotive interiors, and paint or repair automotive exteriors.",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Automotive Repair and Maintenance",
    slug: "automotive-repair-and-maintenance"
  },
  "8112": {
    description: "",
    industries: ["electronic-and-precision-equipment-maintenance"],
    name: "Electronic and Precision Equipment Repair and Maintenance",
    slug: "electronic-and-precision-equipment-repair-and-maintenance"
  },
  "8113": {
    description: "",
    industries: ["commercial-and-industrial-machinery-maintenance"],
    name: "Commercial and Industrial Machinery and Equipment (except Automotive and Electronic) Repair and Maintenance",
    slug: "commercial-and-industrial-machinery-and-equipment-except-automotive-and-electronic-repair-and-maintenance"
  },
  "8114": {
    description: "This industry group comprises establishments primarily engaged in home and garden equipment and appliance repair and maintenance; reupholstery and furniture repair; footwear and leather goods repair; and other personal and household goods repair and maintenance.",
    industries: ["repair-and-maintenance"],
    name: "Personal and Household Goods Repair and Maintenance",
    slug: "personal-and-household-goods-repair-and-maintenance"
  },
  "8121": {
    description: "This industry group comprises establishments, such as barber and beauty shops, that provide appearance care services to individual consumers.",
    industries: ["personal-care-services"],
    name: "Personal Care Services",
    slug: "personal-care-services"
  },
  "8122": {
    description: "This industry group comprises establishments primarily engaged in preparing the dead for burial or interment, conducting funerals, operating sites or structures reserved for the interment of human or animals remains, and/or cremating the dead.",
    industries: ["personal-and-laundry-services"],
    name: "Death Care Services",
    slug: "death-care-services"
  },
  "8123": {
    description: "This industry group comprises establishments primarily engaged in operating coin- or card-operated or similar self-service laundries and drycleaners; providing drycleaning and laundry services (except coin- or card-operated); and supplying, on a rental or contract basis, laundered items (e.g., uniforms, gowns, shop towels, etc.).  Included in this industry group are establishments primarily engaged in supplying and servicing coin- or card-operated laundry and drycleaning equipment in places of business operated by others, such as apartments and dormitories.",
    industries: ["laundry-and-drycleaning-services"],
    name: "Drycleaning and Laundry Services",
    slug: "drycleaning-and-laundry-services"
  },
  "8129": {
    description: "This industry group comprises establishments primarily engaged in providing personal services (except personal care services, death care services, or drycleaning and laundry services).",
    industries: ["personal-and-laundry-services"],
    name: "Other Personal Services",
    slug: "other-personal-services"
  },
  "8131": {
    description: "",
    industries: ["religious-institutions"],
    name: "Religious Organizations",
    slug: "religious-organizations"
  },
  "8132": {
    description: "",
    industries: ["philanthropic-fundraising-services"],
    name: "Grantmaking and Giving Services",
    slug: "grantmaking-and-giving-services"
  },
  "8133": {
    description: "",
    industries: ["consumer-services"],
    name: "Social Advocacy Organizations",
    slug: "social-advocacy-organizations"
  },
  "8134": {
    description: "",
    industries: ["civic-and-social-organizations"],
    name: "Civic and Social Organizations",
    slug: "civic-and-social-organizations"
  },
  "8139": {
    description: "This industry group comprises establishments primarily engaged in promoting the interests of their members (except religious organizations, social advocacy organizations, and civic and social organizations).  Examples of establishments in this industry are business associations, professional organizations, labor unions, and political organizations.",
    industries: ["political-organizations"],
    name: "Business, Professional, Labor, Political, and Similar Organizations",
    slug: "business-professional-labor-political-and-similar-organizations"
  },
  "8141": {
    description: "",
    industries: ["household-services"],
    name: "Private Households",
    slug: "private-households"
  },
  "9211": {
    description: "",
    industries: ["public-policy-offices"],
    name: "Executive, Legislative, and Other General Government Support",
    slug: "executive-legislative-and-other-general-government-support"
  },
  "9221": {
    description: "",
    industries: ["government-administration"],
    name: "Justice, Public Order, and Safety Activities",
    slug: "justice-public-order-and-safety-activities"
  },
  "9231": {
    description: "",
    industries: ["health-and-human-services"],
    name: "Administration of Human Resource Programs",
    slug: "administration-of-human-resource-programs"
  },
  "9241": {
    description: "",
    industries: ["environmental-quality-programs"],
    name: "Administration of Environmental Quality Programs",
    slug: "administration-of-environmental-quality-programs"
  },
  "9251": {
    description: "",
    industries: ["housing-and-community-development"],
    name: "Administration of Housing Programs, Urban Planning, and Community Development",
    slug: "administration-of-housing-programs-urban-planning-and-community-development"
  },
  "9261": {
    description: "",
    industries: ["economic-programs"],
    name: "Administration of Economic Programs",
    slug: "administration-of-economic-programs"
  },
  "9271": {
    description: "",
    industries: ["space-research-and-technology"],
    name: "Space Research and Technology",
    slug: "space-research-and-technology"
  },
  "9281": {
    description: "",
    industries: ["military-and-international-affairs"],
    name: "National Security and International Affairs",
    slug: "national-security-and-international-affairs"
  },
  "11111": {
    description: "See industry description for 111110.",
    industries: ["farming"],
    name: "Soybean Farming",
    slug: "soybean-farming"
  },
  "11112": {
    description: "See industry description for 111120.",
    industries: ["farming"],
    name: "Oilseed (except Soybean) Farming",
    slug: "oilseed-except-soybean-farming"
  },
  "11113": {
    description: "See industry description for 111130.",
    industries: ["farming"],
    name: "Dry Pea and Bean Farming",
    slug: "dry-pea-and-bean-farming"
  },
  "11114": {
    description: "See industry description for 111140.",
    industries: ["farming"],
    name: "Wheat Farming",
    slug: "wheat-farming"
  },
  "11115": {
    description: "See industry description for 111150.",
    industries: ["farming"],
    name: "Corn Farming",
    slug: "corn-farming"
  },
  "11116": {
    description: "See industry description for 111160.",
    industries: ["farming"],
    name: "Rice Farming",
    slug: "rice-farming"
  },
  "11119": {
    description: "This industry comprises establishments primarily engaged in (1) growing grain(s) and/or producing grain seeds (except wheat, corn, and rice) or (2) growing a combination of grain(s) and oilseed(s) with no one grain (or family of grains) or oilseed (or family of oilseeds) accounting for one-half of the establishment's agricultural production (i.e., value of crops for market).  Combination grain(s) and oilseed(s) establishments may produce oilseed(s) and grain(s) seeds and/or grow oilseed(s) and grain(s). Illustrative Examples: Barley farming, Rye farming, Milo farming, Sorghum farming, Oat farming, Wild rice farming, Oilseed and grain combination farming.",
    industries: ["farming"],
    name: "Other Grain Farming",
    slug: "other-grain-farming"
  },
  "11121": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) growing vegetable and/or melon crops; (2) producing vegetable and/or melon seeds; and (3) growing vegetable and/or melon bedding plants.  The crops included in this industry have an annual growth cycle and are grown in open fields.  Climate and cultural practices limit producing areas but often permit the growing of a combination of crops in a year.",
    industries: ["farming"],
    name: "Vegetable and Melon Farming",
    slug: "vegetable-and-melon-farming"
  },
  "11131": {
    description: "See industry description for 111310.",
    industries: ["farming"],
    name: "Orange Groves",
    slug: "orange-groves"
  },
  "11132": {
    description: "See industry description for 111320.",
    industries: ["farming"],
    name: "Citrus (except Orange) Groves",
    slug: "citrus-except-orange-groves"
  },
  "11133": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) growing noncitrus fruits (e.g., apples, grapes, berries, peaches); (2) growing tree nuts (e.g., pecans, almonds, pistachios); or (3) growing a combination of fruit(s) and tree nut(s) with no one fruit (or family of fruit) or family of tree nuts accounting for one-half of the establishment's agricultural production (i.e., value of crops for market).",
    industries: ["farming"],
    name: "Noncitrus Fruit and Tree Nut Farming",
    slug: "noncitrus-fruit-and-tree-nut-farming"
  },
  "11141": {
    description: "This industry comprises establishments primarily engaged in growing food crops (e.g., fruits, melons, tomatoes) under glass or protective cover. Cross-References.",
    industries: ["farming"],
    name: "Food Crops Grown Under Cover",
    slug: "food-crops-grown-under-cover"
  },
  "11142": {
    description: "This industry comprises establishments primarily engaged in (1) growing nursery and floriculture products (e.g., nursery stock, shrubbery, cut flowers, flower seeds, foliage plants) under cover or in open fields and/or (2) growing short rotation woody trees with a growing and harvesting cycle of 10 years or less for pulp or tree stock (e.g., cut Christmas trees, cottonwoods).",
    industries: ["farming"],
    name: "Nursery and Floriculture Production",
    slug: "nursery-and-floriculture-production"
  },
  "11191": {
    description: "See industry description for 111910.",
    industries: ["farming"],
    name: "Tobacco Farming",
    slug: "tobacco-farming"
  },
  "11192": {
    description: "See industry description for 111920.",
    industries: ["farming"],
    name: "Cotton Farming",
    slug: "cotton-farming"
  },
  "11193": {
    description: "See industry description for 111930.",
    industries: ["farming"],
    name: "Sugarcane Farming",
    slug: "sugarcane-farming"
  },
  "11194": {
    description: "See industry description for 111940.",
    industries: ["farming"],
    name: "Hay Farming",
    slug: "hay-farming"
  },
  "11199": {
    description: "This industry comprises establishments primarily engaged in (1) growing crops (except oilseeds and/or grains; vegetables and/or melons; fruits and/or tree nuts; greenhouse, nursery, and/or floriculture products; tobacco; cotton; sugarcane; or hay) or (2) growing a combination of crops (except a combination of oilseed(s) and grain(s); and a combination of fruit(s) and tree nut(s)) with no one crop or family of crops accounting for one-half of the establishment's agricultural production (i.e., value of crops for market). Illustrative Examples: Agave farming, Spice farming, General combination crop farming (except oilseed and grain; vegetables and melons; fruit and nut combinations), Tea farming, Hay seed farming, Maple sap gathering, Peanut farming, Sugar beet farming, Grass seed farming.",
    industries: ["farming"],
    name: "All Other Crop Farming",
    slug: "all-other-crop-farming"
  },
  "11211": {
    description: "This industry comprises establishments primarily engaged in raising cattle (including cattle for dairy herd replacements), or feeding cattle for fattening.",
    industries: ["ranching"],
    name: "Beef Cattle Ranching and Farming, including Feedlots",
    slug: "beef-cattle-ranching-and-farming-including-feedlots"
  },
  "11212": {
    description: "See industry description for 112120.",
    industries: ["ranching"],
    name: "Dairy Cattle and Milk Production",
    slug: "dairy-cattle-and-milk-production"
  },
  "11213": {
    description: "See industry description for 112130.",
    industries: ["ranching"],
    name: "Dual-Purpose Cattle Ranching and Farming",
    slug: "dual-purpose-cattle-ranching-and-farming"
  },
  "11221": {
    description: "See industry description for 112210.",
    industries: ["ranching-and-fisheries"],
    name: "Hog and Pig Farming",
    slug: "hog-and-pig-farming"
  },
  "11231": {
    description: "See industry description for 112310.",
    industries: ["ranching-and-fisheries"],
    name: "Chicken Egg Production",
    slug: "chicken-egg-production"
  },
  "11232": {
    description: "See industry description for 112320.",
    industries: ["ranching-and-fisheries"],
    name: "Broilers and Other Meat Type Chicken Production",
    slug: "broilers-and-other-meat-type-chicken-production"
  },
  "11233": {
    description: "See industry description for 112330.",
    industries: ["ranching-and-fisheries"],
    name: "Turkey Production",
    slug: "turkey-production"
  },
  "11234": {
    description: "See industry description for 112340.",
    industries: ["ranching-and-fisheries"],
    name: "Poultry Hatcheries",
    slug: "poultry-hatcheries"
  },
  "11239": {
    description: "See industry description for 112390.",
    industries: ["ranching-and-fisheries"],
    name: "Other Poultry Production",
    slug: "other-poultry-production"
  },
  "11241": {
    description: "See industry description for 112410.",
    industries: ["ranching-and-fisheries"],
    name: "Sheep Farming",
    slug: "sheep-farming"
  },
  "11242": {
    description: "See industry description for 112420.",
    industries: ["ranching-and-fisheries"],
    name: "Goat Farming",
    slug: "goat-farming"
  },
  "11251": {
    description: "This industry comprises establishments primarily engaged in the farm raising and production of aquatic animals or plants in controlled or selected aquatic environments.  These establishments use some form of intervention in the rearing process to enhance production, such as holding in captivity, regular stocking, feeding, and protecting from predators, pests, and disease. Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Aquaculture",
    slug: "aquaculture"
  },
  "11291": {
    description: "See industry description for 112910.",
    industries: ["ranching-and-fisheries"],
    name: "Apiculture",
    slug: "apiculture"
  },
  "11292": {
    description: "See industry description for 112920.",
    industries: ["ranching-and-fisheries"],
    name: "Horses and Other Equine Production",
    slug: "horses-and-other-equine-production"
  },
  "11293": {
    description: "See industry description for 112930.",
    industries: ["ranching-and-fisheries"],
    name: "Fur-Bearing Animal and Rabbit Production",
    slug: "fur-bearing-animal-and-rabbit-production"
  },
  "11299": {
    description: "See industry description for 112990.",
    industries: ["ranching-and-fisheries"],
    name: "All Other Animal Production",
    slug: "all-other-animal-production"
  },
  "11311": {
    description: "See industry description for 113110.",
    industries: ["forestry-and-logging"],
    name: "Timber Tract Operations",
    slug: "timber-tract-operations"
  },
  "11321": {
    description: "See industry description for 113210.",
    industries: ["forestry-and-logging"],
    name: "Forest Nurseries and Gathering of Forest Products",
    slug: "forest-nurseries-and-gathering-of-forest-products"
  },
  "11331": {
    description: "See industry description for 113310.",
    industries: ["forestry-and-logging"],
    name: "Logging",
    slug: "logging"
  },
  "11411": {
    description: "This industry comprises establishments primarily engaged in the commercial catching or taking of finfish, shellfish, or miscellaneous marine products from a natural habitat, such as the catching of bluefish, eels, salmon, tuna, clams, crabs, lobsters, mussels, oysters, shrimp, frogs, sea urchins, and turtles.",
    industries: ["fisheries"],
    name: "Fishing",
    slug: "fishing"
  },
  "11421": {
    description: "See industry description for 114210.",
    industries: ["farming-ranching-forestry"],
    name: "Hunting and Trapping",
    slug: "hunting-and-trapping"
  },
  "11511": {
    description: "This industry comprises establishments primarily engaged in providing support activities for growing crops. Illustrative Examples: Aerial crop dusting or spraying (i.e., using specialized or dedicated aircraft), Farm management services, Cotton ginning, Planting crops, Cultivating services, Vineyard cultivation services.",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Crop Production",
    slug: "support-activities-for-crop-production"
  },
  "11521": {
    description: "See industry description for 115210.",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Animal Production",
    slug: "support-activities-for-animal-production"
  },
  "11531": {
    description: "See industry description for 115310.",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Forestry",
    slug: "support-activities-for-forestry"
  },
  "21112": {
    description: "See industry description for 211120.",
    industries: ["oil-extraction"],
    name: "Crude Petroleum Extraction",
    slug: "crude-petroleum-extraction"
  },
  "21113": {
    description: "See industry description for 211130.",
    industries: ["natural-gas-extraction"],
    name: "Natural Gas Extraction",
    slug: "natural-gas-extraction"
  },
  "21211": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) mining bituminous coal, anthracite coal, and lignite by underground mining, auger mining, strip mining, culm bank mining, and other surface mining; (2) developing coal mine sites; and (3) beneficiating (i.e., preparing) coal (e.g., cleaning, washing, screening, and sizing).",
    industries: ["coal-mining"],
    name: "Coal Mining",
    slug: "coal-mining"
  },
  "21221": {
    description: "See industry description for 212210.",
    industries: ["metal-ore-mining"],
    name: "Iron Ore Mining",
    slug: "iron-ore-mining"
  },
  "21222": {
    description: "See industry description for 212220.",
    industries: ["metal-ore-mining"],
    name: "Gold Ore and Silver Ore Mining",
    slug: "gold-ore-and-silver-ore-mining"
  },
  "21223": {
    description: "See industry description for 212230.",
    industries: ["metal-ore-mining"],
    name: "Copper, Nickel, Lead, and Zinc Mining",
    slug: "copper-nickel-lead-and-zinc-mining"
  },
  "21229": {
    description: "See industry description for 212290.",
    industries: ["metal-ore-mining"],
    name: "Other Metal Ore Mining",
    slug: "other-metal-ore-mining"
  },
  "21231": {
    description: "This industry comprises (1) establishments primarily engaged in developing the mine site, mining or quarrying dimension stone (i.e., rough blocks and/or slabs of stone), or mining and quarrying crushed and broken stone and/or (2) preparation plants primarily engaged in beneficiating stone (e.g., crushing, grinding, washing, screening, pulverizing, and sizing).",
    industries: ["nonmetallic-mineral-mining"],
    name: "Stone Mining and Quarrying",
    slug: "stone-mining-and-quarrying"
  },
  "21232": {
    description: "This industry comprises (1) establishments primarily engaged in developing the mine site and/or mining, quarrying, dredging for sand and gravel, or mining clay (e.g., china clay, paper clay and slip clay) or ceramic and refractory minerals and (2) preparation plants primarily engaged in beneficiating (e.g., washing, screening, and grinding) sand and gravel, clay, and ceramic and refractory minerals.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Sand, Gravel, Clay, and Ceramic and Refractory Minerals Mining and Quarrying",
    slug: "sand-gravel-clay-and-ceramic-and-refractory-minerals-mining-and-quarrying"
  },
  "21239": {
    description: "See industry description for 212390.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Other Nonmetallic Mineral Mining and Quarrying",
    slug: "other-nonmetallic-mineral-mining-and-quarrying"
  },
  "21311": {
    description: "This industry comprises establishments primarily engaged in providing support services, on a contract or fee basis (except geophysical surveying and mapping, mine site preparation, construction, and transportation activities), required for the mining and quarrying of minerals and for the extraction of oil and gas.  Drilling, taking core samples, and making geological observations at prospective sites for minerals, on a contract or fee basis, are included in this industry.",
    industries: ["oil-gas-and-mining"],
    name: "Support Activities for Mining",
    slug: "support-activities-for-mining"
  },
  "22111": {
    description: "This industry comprises establishments primarily engaged in operating electric power generation facilities.  These facilities convert other forms of energy, such as water power (i.e., hydroelectric), fossil fuels, nuclear power, and solar power, into electrical energy.  The establishments in this industry produce electric energy and provide electricity to transmission systems or to electric power distribution systems.",
    name: "Electric Power Generation",
    slug: "electric-power-generation",
    industries: ["electric-power-generation", "renewable-energy-power-generation"]
  },
  "22112": {
    description: "This industry comprises establishments primarily engaged in operating electric power transmission systems, controlling (i.e., regulating voltages) the transmission of electricity, and/or distributing electricity.  The transmission system includes lines and transformer stations.  These establishments arrange, facilitate, or coordinate the transmission of electricity from the generating source to the distribution centers, other electric utilities, or final consumers.  The distribution system consists of lines, poles, meters, and wiring that deliver the electricity to final consumers. Cross-References.",
    industries: ["electric-power-transmission-control-and-distribution"],
    name: "Electric Power Transmission, Control, and Distribution",
    slug: "electric-power-transmission-control-and-distribution"
  },
  "22121": {
    description: "See industry description for 221210.",
    industries: ["natural-gas-distribution"],
    name: "Natural Gas Distribution",
    slug: "natural-gas-distribution"
  },
  "22131": {
    description: "See industry description for 221310.",
    industries: ["water-supply-and-irrigation-systems"],
    name: "Water Supply and Irrigation Systems",
    slug: "water-supply-and-irrigation-systems"
  },
  "22132": {
    description: "See industry description for 221320.",
    industries: ["water-waste-steam-and-air-conditioning-services"],
    name: "Sewage Treatment Facilities",
    slug: "sewage-treatment-facilities"
  },
  "22133": {
    description: "See industry description for 221330.",
    industries: ["steam-and-air-conditioning-supply"],
    name: "Steam and Air-Conditioning Supply",
    slug: "steam-and-air-conditioning-supply"
  },
  "23611": {
    description: "This industry comprises establishments primarily responsible for the construction or remodeling and renovation of single-family and multifamily residential buildings.  Included in this industry are residential housing general contractors (i.e., new construction, remodeling, or renovating existing residential structures), for-sale builders and remodelers of residential structures, residential project construction management firms, and residential design-build firms.",
    industries: ["residential-building-construction"],
    name: "Residential Building Construction",
    slug: "residential-building-construction"
  },
  "23621": {
    description: "See industry description for 236210.",
    industries: ["nonresidential-building-construction"],
    name: "Industrial Building Construction",
    slug: "industrial-building-construction"
  },
  "23622": {
    description: "See industry description for 236220.",
    industries: ["nonresidential-building-construction"],
    name: "Commercial and Institutional Building Construction",
    slug: "commercial-and-institutional-building-construction"
  },
  "23711": {
    description: "See industry description for 237110.",
    industries: ["utility-system-construction"],
    name: "Water and Sewer Line and Related Structures Construction",
    slug: "water-and-sewer-line-and-related-structures-construction"
  },
  "23712": {
    description: "See industry description for 237120.",
    industries: ["utility-system-construction"],
    name: "Oil and Gas Pipeline and Related Structures Construction",
    slug: "oil-and-gas-pipeline-and-related-structures-construction"
  },
  "23713": {
    description: "See industry description for 237130.",
    industries: ["utility-system-construction"],
    name: "Power and Communication Line and Related Structures Construction",
    slug: "power-and-communication-line-and-related-structures-construction"
  },
  "23721": {
    description: "See industry description for 237210.",
    industries: ["subdivision-of-land"],
    name: "Land Subdivision",
    slug: "land-subdivision"
  },
  "23731": {
    description: "See industry description for 237310.",
    industries: ["highway-street-and-bridge-construction"],
    name: "Highway, Street, and Bridge Construction",
    slug: "highway-street-and-bridge-construction"
  },
  "23799": {
    description: "See industry description for 237990.",
    industries: ["civil-engineering"],
    name: "Other Heavy and Civil Engineering Construction",
    slug: "other-heavy-and-civil-engineering-construction"
  },
  "23811": {
    description: "See industry description for 238110.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Poured Concrete Foundation and Structure Contractors",
    slug: "poured-concrete-foundation-and-structure-contractors"
  },
  "23812": {
    description: "See industry description for 238120.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Structural Steel and Precast Concrete Contractors",
    slug: "structural-steel-and-precast-concrete-contractors"
  },
  "23813": {
    description: "See industry description for 238130.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Framing Contractors",
    slug: "framing-contractors"
  },
  "23814": {
    description: "See industry description for 238140.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Masonry Contractors",
    slug: "masonry-contractors"
  },
  "23815": {
    description: "See industry description for 238150.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Glass and Glazing Contractors",
    slug: "glass-and-glazing-contractors"
  },
  "23816": {
    description: "See industry description for 238160.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Roofing Contractors",
    slug: "roofing-contractors"
  },
  "23817": {
    description: "See industry description for 238170.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Siding Contractors",
    slug: "siding-contractors"
  },
  "23819": {
    description: "See industry description for 238190.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Other Foundation, Structure, and Building Exterior Contractors",
    slug: "other-foundation-structure-and-building-exterior-contractors"
  },
  "23821": {
    description: "See industry description for 238210.",
    industries: ["building-equipment-contractors"],
    name: "Electrical Contractors and Other Wiring Installation Contractors",
    slug: "electrical-contractors-and-other-wiring-installation-contractors"
  },
  "23822": {
    description: "See industry description for 238220.",
    industries: ["building-equipment-contractors"],
    name: "Plumbing, Heating, and Air-Conditioning Contractors",
    slug: "plumbing-heating-and-air-conditioning-contractors"
  },
  "23829": {
    description: "See industry description for 238290.",
    industries: ["building-equipment-contractors"],
    name: "Other Building Equipment Contractors",
    slug: "other-building-equipment-contractors"
  },
  "23831": {
    description: "See industry description for 238310.",
    industries: ["building-finishing-contractors"],
    name: "Drywall and Insulation Contractors",
    slug: "drywall-and-insulation-contractors"
  },
  "23832": {
    description: "See industry description for 238320.",
    industries: ["building-finishing-contractors"],
    name: "Painting and Wall Covering Contractors",
    slug: "painting-and-wall-covering-contractors"
  },
  "23833": {
    description: "See industry description for 238330.",
    industries: ["building-finishing-contractors"],
    name: "Flooring Contractors",
    slug: "flooring-contractors"
  },
  "23834": {
    description: "See industry description for 238340.",
    industries: ["building-finishing-contractors"],
    name: "Tile and Terrazzo Contractors",
    slug: "tile-and-terrazzo-contractors"
  },
  "23835": {
    description: "See industry description for 238350.",
    industries: ["building-finishing-contractors"],
    name: "Finish Carpentry Contractors",
    slug: "finish-carpentry-contractors"
  },
  "23839": {
    description: "See industry description for 238390.",
    industries: ["building-finishing-contractors"],
    name: "Other Building Finishing Contractors",
    slug: "other-building-finishing-contractors"
  },
  "23891": {
    description: "See industry description for 238910.",
    industries: ["specialty-trade-contractors"],
    name: "Site Preparation Contractors",
    slug: "site-preparation-contractors"
  },
  "23899": {
    description: "See industry description for 238990.",
    industries: ["specialty-trade-contractors"],
    name: "All Other Specialty Trade Contractors",
    slug: "all-other-specialty-trade-contractors"
  },
  "31111": {
    description: "This industry comprises establishments primarily engaged in manufacturing food and feed for animals from ingredients, such as grains, oilseed mill products, and meat products.",
    industries: ["animal-feed-manufacturing"],
    name: "Animal Food Manufacturing",
    slug: "animal-food-manufacturing"
  },
  "31121": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) milling flour or meal from grains or vegetables; (2) preparing flour mixes or doughs from flour milled in the same establishment; (3) milling, cleaning, and polishing rice; and (4) manufacturing malt from barley, rye, or other grains.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Flour Milling and Malt Manufacturing",
    slug: "flour-milling-and-malt-manufacturing"
  },
  "31122": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) wet milling corn and vegetables; (2) crushing oilseeds and tree nuts; (3) refining and/or blending vegetable oils; (4) manufacturing shortening and margarine; and (5) blending purchased animal fats with vegetable fats.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Starch and Vegetable Fats and Oils Manufacturing",
    slug: "starch-and-vegetable-fats-and-oils-manufacturing"
  },
  "31123": {
    description: "See industry description for 311230.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Breakfast Cereal Manufacturing",
    slug: "breakfast-cereal-manufacturing"
  },
  "31131": {
    description: "This industry comprises establishments primarily engaged in manufacturing raw sugar, liquid sugar, and refined sugar from sugarcane, raw cane sugar and sugar beets.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Sugar Manufacturing",
    slug: "sugar-manufacturing"
  },
  "31134": {
    description: "See industry description for 311340.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Nonchocolate Confectionery Manufacturing",
    slug: "nonchocolate-confectionery-manufacturing"
  },
  "31135": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing chocolate and chocolate confectioneries from cacao beans or (2) manufacturing chocolate confectioneries from chocolate produced elsewhere.  Included in this industry are establishments primarily engaged in retailing chocolate confectionery products not for immediate consumption made on the premises from chocolate made elsewhere.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Chocolate and Confectionery Manufacturing",
    slug: "chocolate-and-confectionery-manufacturing"
  },
  "31141": {
    description: "This industry comprises establishments primarily engaged in manufacturing frozen fruit, frozen juices, frozen vegetables, and frozen specialty foods (except seafood), such as frozen dinners, entrees, and side dishes; frozen pizza; frozen whipped toppings; and frozen waffles, pancakes, and French toast.",
    industries: ["fruit-and-vegetable-preserves-manufacturing"],
    name: "Frozen Food Manufacturing",
    slug: "frozen-food-manufacturing"
  },
  "31142": {
    description: "This industry comprises establishments primarily engaged in manufacturing canned, pickled, and dried fruits, vegetables, and specialty foods.  Establishments in this industry may package the dried or dehydrated ingredients they make with other purchased ingredients.  Examples of products made by these establishments are canned juices; canned baby foods; canned soups (except seafood); canned dry beans; canned tomato-based sauces, such as catsup, salsa, chili sauce, spaghetti sauce, barbeque sauce, and tomato paste; pickles and relishes; jams and jellies; dried soup mixes and bouillon; and sauerkraut.",
    industries: ["fruit-and-vegetable-preserves-manufacturing"],
    name: "Fruit and Vegetable Canning, Pickling, and Drying",
    slug: "fruit-and-vegetable-canning-pickling-and-drying"
  },
  "31151": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing dairy products (except frozen) from raw milk and/or processed milk products; (2) manufacturing dairy substitutes (except frozen) from soybeans and other nondairy substances; and (3) manufacturing dry, condensed, concentrated, and evaporated dairy and dairy substitute products.",
    industries: ["dairy-product-manufacturing"],
    name: "Dairy Product (except Frozen) Manufacturing",
    slug: "dairy-product-except-frozen-manufacturing"
  },
  "31152": {
    description: "See industry description for 311520.",
    industries: ["dairy-product-manufacturing"],
    name: "Ice Cream and Frozen Dessert Manufacturing",
    slug: "ice-cream-and-frozen-dessert-manufacturing"
  },
  "31161": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) slaughtering animals; (2) preparing processed meats and meat byproducts; and (3) rendering and/or refining animal fat, bones, and meat scraps.  This industry includes establishments primarily engaged in assembly cutting and packing of meats (i.e., boxed meats) from purchased carcasses.",
    industries: ["meat-products-manufacturing"],
    name: "Animal Slaughtering and Processing",
    slug: "animal-slaughtering-and-processing"
  },
  "31171": {
    description: "See industry description for 311710.",
    industries: ["seafood-product-manufacturing"],
    name: "Seafood Product Preparation and Packaging",
    slug: "seafood-product-preparation-and-packaging"
  },
  "31181": {
    description: "This industry comprises establishments primarily engaged in manufacturing fresh and frozen bread and other bakery products.",
    industries: ["baked-goods-manufacturing"],
    name: "Bread and Bakery Product Manufacturing",
    slug: "bread-and-bakery-product-manufacturing"
  },
  "31182": {
    description: "This industry comprises establishments primarily engaged in one of the following: (1) manufacturing cookies and crackers; (2) preparing flour and dough mixes and dough from flour ground elsewhere; and (3) manufacturing dry pasta.  The establishments in this industry may package the dry pasta they manufacture with other ingredients.",
    industries: ["baked-goods-manufacturing"],
    name: "Cookie, Cracker, and Pasta Manufacturing",
    slug: "cookie-cracker-and-pasta-manufacturing"
  },
  "31183": {
    description: "See industry description for 311830.",
    industries: ["baked-goods-manufacturing"],
    name: "Tortilla Manufacturing",
    slug: "tortilla-manufacturing"
  },
  "31191": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) salting, roasting, drying, cooking, or canning nuts; (2) processing grains or seeds into snacks; (3) manufacturing peanut butter; and (4) manufacturing potato chips, corn chips, popped popcorn, pretzels (except soft), pork rinds, and similar snacks.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Snack Food Manufacturing",
    slug: "snack-food-manufacturing"
  },
  "31192": {
    description: "See industry description for 311920.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Coffee and Tea Manufacturing",
    slug: "coffee-and-tea-manufacturing"
  },
  "31193": {
    description: "See industry description for 311930.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Flavoring Syrup and Concentrate Manufacturing",
    slug: "flavoring-syrup-and-concentrate-manufacturing"
  },
  "31194": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing dressings and sauces, such as mayonnaise, salad dressing, vinegar, mustard, horseradish, soy sauce, tartar sauce, Worcestershire sauce, and other prepared sauces (except tomato-based and gravies); (2) manufacturing spices, table salt, seasoning, and flavoring extracts (except coffee and meat), and natural food colorings; and (3) manufacturing dry mix food preparations, such as salad dressing mixes, gravy and sauce mixes, frosting mixes, and other dry mix preparations.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Seasoning and Dressing Manufacturing",
    slug: "seasoning-and-dressing-manufacturing"
  },
  "31199": {
    description: "This industry comprises establishments primarily engaged in manufacturing food (except animal food; grain and oilseed milling; sugar and confectionery products; preserved fruits, vegetables, and specialty foods; dairy products; meat products; seafood products; baked goods and tortillas; snack foods; coffee and tea; flavoring syrups and concentrates; seasonings; and dressings).  Included in this industry are establishments primarily engaged in mixing purchased dried and/or dehydrated ingredients, including those mixing purchased dried and/or dehydrated ingredients for soup mixes and bouillon. Illustrative Examples: Baking powder manufacturing, Cut or peeled fresh vegetables manufacturing, Dessert puddings manufacturing, Egg substitutes manufacturing, Sweetening syrups (except pure maple) manufacturing, Fresh pasta manufacturing, Fresh pizza manufacturing, Honey processing, Popcorn (except popped) manufacturing, Powdered drink mixes (except chocolate, coffee, tea, or milk based) manufacturing.",
    industries: ["food-and-beverage-manufacturing"],
    name: "All Other Food Manufacturing",
    slug: "all-other-food-manufacturing"
  },
  "31211": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing soft drinks; (2) manufacturing ice; and (3) purifying and bottling water.",
    industries: ["beverage-manufacturing"],
    name: "Soft Drink and Ice Manufacturing",
    slug: "soft-drink-and-ice-manufacturing"
  },
  "31212": {
    description: "See industry description for 312120.",
    industries: ["breweries"],
    name: "Breweries",
    slug: "breweries"
  },
  "31213": {
    description: "See industry description for 312130.",
    industries: ["wineries"],
    name: "Wineries",
    slug: "wineries"
  },
  "31214": {
    description: "See industry description for 312140.",
    industries: ["distilleries"],
    name: "Distilleries",
    slug: "distilleries"
  },
  "31223": {
    description: "See industry description for 312230.",
    industries: ["tobacco-manufacturing"],
    name: "Tobacco Manufacturing",
    slug: "tobacco-manufacturing"
  },
  "31311": {
    description: "See industry description for 313110.",
    industries: ["textile-manufacturing"],
    name: "Fiber, Yarn, and Thread Mills",
    slug: "fiber-yarn-and-thread-mills"
  },
  "31321": {
    description: "See industry description for 313210.",
    industries: ["textile-manufacturing"],
    name: "Broadwoven Fabric Mills",
    slug: "broadwoven-fabric-mills"
  },
  "31322": {
    description: "See industry description for 313220.",
    industries: ["textile-manufacturing"],
    name: "Narrow Fabric Mills and Schiffli Machine Embroidery",
    slug: "narrow-fabric-mills-and-schiffli-machine-embroidery"
  },
  "31323": {
    description: "See industry description for 313230.",
    industries: ["textile-manufacturing"],
    name: "Nonwoven Fabric Mills",
    slug: "nonwoven-fabric-mills"
  },
  "31324": {
    description: "See industry description for 313240.",
    industries: ["textile-manufacturing"],
    name: "Knit Fabric Mills",
    slug: "knit-fabric-mills"
  },
  "31331": {
    description: "See industry description for 313310.",
    industries: ["textile-manufacturing"],
    name: "Textile and Fabric Finishing Mills",
    slug: "textile-and-fabric-finishing-mills"
  },
  "31332": {
    description: "See industry description for 313320.",
    industries: ["textile-manufacturing"],
    name: "Fabric Coating Mills",
    slug: "fabric-coating-mills"
  },
  "31411": {
    description: "See industry description for 314110.",
    industries: ["manufacturing"],
    name: "Carpet and Rug Mills",
    slug: "carpet-and-rug-mills"
  },
  "31412": {
    description: "See industry description for 314120.",
    industries: ["manufacturing"],
    name: "Curtain and Linen Mills",
    slug: "curtain-and-linen-mills"
  },
  "31491": {
    description: "See industry description for 314910.",
    industries: ["manufacturing"],
    name: "Textile Bag and Canvas Mills",
    slug: "textile-bag-and-canvas-mills"
  },
  "31499": {
    description: "This industry comprises establishments primarily engaged in manufacturing nonapparel textile products (except carpet, rugs, curtains, linens, bags, and canvas products) from purchased materials.  This industry includes establishments primarily engaged in decorative stitching such as embroidery or other art needlework on textile products, including apparel. Illustrative Examples: Batts and batting (except nonwoven fabrics) manufacturing, Carpet cutting and binding, Diapers (except disposable) made from purchased materials, Fishing nets made from purchased materials, Embroidering on textile products or apparel for the trade, Ropes (except wire rope) manufacturing, Sleeping bags manufacturing, Tire cord and fabric, all materials, manufacturing, Twines manufacturing.",
    industries: ["manufacturing"],
    name: "All Other Textile Product Mills",
    slug: "all-other-textile-product-mills"
  },
  "31512": {
    description: "See industry description for 315120.",
    industries: ["apparel-manufacturing"],
    name: "Apparel Knitting Mills",
    slug: "apparel-knitting-mills"
  },
  "31521": {
    description: "See industry description for 315210.",
    industries: ["apparel-manufacturing"],
    name: "Cut and Sew Apparel Contractors",
    slug: "cut-and-sew-apparel-contractors"
  },
  "31525": {
    description: "See industry description for 315250.",
    industries: ["apparel-manufacturing"],
    name: "Cut and Sew Apparel Manufacturing (except Contractors)",
    slug: "cut-and-sew-apparel-manufacturing-except-contractors"
  },
  "31599": {
    description: "See industry description for 315990.",
    industries: ["fashion-accessories-manufacturing"],
    name: "Apparel Accessories and Other Apparel Manufacturing",
    slug: "apparel-accessories-and-other-apparel-manufacturing"
  },
  "31611": {
    description: "See industry description for 316110.",
    industries: ["leather-product-manufacturing"],
    name: "Leather and Hide Tanning and Finishing",
    slug: "leather-and-hide-tanning-and-finishing"
  },
  "31621": {
    description: "See industry description for 316210.",
    industries: ["footwear-manufacturing"],
    name: "Footwear Manufacturing",
    slug: "footwear-manufacturing"
  },
  "31699": {
    description: "See industry description for 316990.",
    industries: ["leather-product-manufacturing"],
    name: "Other Leather and Allied Product Manufacturing",
    slug: "other-leather-and-allied-product-manufacturing"
  },
  "32111": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) sawing dimension lumber, boards, beams, timber, poles, ties, shingles, shakes, siding, and wood chips from logs or bolts; (2) sawing round wood poles, pilings, and posts and treating them with preservatives; and (3) treating wood sawed, planed, or shaped in other establishments with creosote or other preservatives to prevent decay and to protect against fire and insects.  Sawmills may plane the rough lumber that they make with a planing machine to achieve smoothness and uniformity of size.",
    industries: ["wood-product-manufacturing"],
    name: "Sawmills and Wood Preservation",
    slug: "sawmills-and-wood-preservation"
  },
  "32121": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing veneer and/or plywood; (2) manufacturing engineered wood members; and (3) manufacturing reconstituted wood products.  This industry includes manufacturing plywood from veneer made in the same establishment or from veneer made in other establishments, and manufacturing plywood faced with nonwood materials, such as plastics or metal. Illustrative Examples: Fabricated structural wood members manufacturing, Laminated structural wood members manufacturing, Medium density fiberboard (MDF) manufacturing, Oriented strandboard (OSB) manufacturing, Particleboard manufacturing, Plywood manufacturing, Reconstituted wood sheets and boards manufacturing, Roof trusses, wood, manufacturing, Veneer mills, Waferboard manufacturing.",
    industries: ["wood-product-manufacturing"],
    name: "Veneer, Plywood, and Engineered Wood Product Manufacturing",
    slug: "veneer-plywood-and-engineered-wood-product-manufacturing"
  },
  "32191": {
    description: "This industry comprises establishments primarily engaged in manufacturing hardwood and softwood cut stock and dimension stock (i.e., shapes); wood windows and wood doors; and other millwork including wood flooring.  Dimension stock or cut stock is defined as lumber and worked wood products cut or shaped to specialized sizes.  These establishments generally use woodworking machinery, such as jointers, planers, lathes, and routers to shape wood.",
    industries: ["wood-product-manufacturing"],
    name: "Millwork",
    slug: "millwork"
  },
  "32192": {
    description: "See industry description for 321920.",
    industries: ["wood-product-manufacturing"],
    name: "Wood Container and Pallet Manufacturing",
    slug: "wood-container-and-pallet-manufacturing"
  },
  "32199": {
    description: "This industry comprises establishments primarily engaged in manufacturing wood products (except establishments operating sawmills and wood preservation facilities; and establishments manufacturing veneer, plywood, engineered wood products, millwork, wood containers, or pallets). Illustrative Examples: Mobile homes manufacturing, Panels, prefabricated wood building, manufacturing, Prefabricated wood buildings manufacturing, Wood dowels manufacturing, Sections, prefabricated wood building, manufacturing, Wood handles (e.g., broom, handtool, mop) manufacturing.",
    industries: ["wood-product-manufacturing"],
    name: "All Other Wood Product Manufacturing",
    slug: "all-other-wood-product-manufacturing"
  },
  "32211": {
    description: "See industry description for 322110.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Pulp Mills",
    slug: "pulp-mills"
  },
  "32212": {
    description: "See industry description for 322120.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Paper Mills",
    slug: "paper-mills"
  },
  "32213": {
    description: "See industry description for 322130.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Paperboard Mills",
    slug: "paperboard-mills"
  },
  "32221": {
    description: "This industry comprises establishments primarily engaged in converting paperboard into containers without manufacturing paperboard.  These establishments use corrugating, cutting, and shaping machinery to form paperboard into containers.  Products made by these establishments include boxes, corrugated sheets, pads, pallets, paper dishes, and fiber drums and reels.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Paperboard Container Manufacturing",
    slug: "paperboard-container-manufacturing"
  },
  "32222": {
    description: "See industry description for 322220.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Paper Bag and Coated and Treated Paper Manufacturing",
    slug: "paper-bag-and-coated-and-treated-paper-manufacturing"
  },
  "32223": {
    description: "See industry description for 322230.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Stationery Product Manufacturing",
    slug: "stationery-product-manufacturing"
  },
  "32229": {
    description: "This industry comprises establishments primarily engaged in (1) converting paper and paperboard into products (except containers, bags, coated and treated paper and paperboard, and stationery products) or (2) converting pulp into pulp products, such as disposable diapers, or molded pulp egg cartons, food trays, and dishes.  Processes used include laminating or lining purchased paper or paperboard. Illustrative Examples: Crepe paper made from purchased paper, Die-cut paper products (except for office use) made from purchased paper or paperboard, Paper novelties made from purchased paper, Molded pulp products (e.g., egg cartons, food containers, food trays) manufacturing, Sanitary products made from purchased sanitary paper stock.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Other Converted Paper Product Manufacturing",
    slug: "other-converted-paper-product-manufacturing"
  },
  "32311": {
    description: "This industry comprises establishments primarily engaged in printing on apparel and textile products, paper, metal, glass, plastics, and other materials, except fabric (grey goods).  The printing processes employed include, but are not limited to, lithographic, gravure, screen, flexographic, digital, and letterpress.  Establishments in this industry do not manufacture the stock that they print, but may perform postprinting activities, such as folding, cutting, or laminating the materials they print, and mailing.",
    industries: ["printing-services"],
    name: "Printing",
    slug: "printing"
  },
  "32312": {
    description: "See industry description for 323120.",
    industries: ["printing-services"],
    name: "Support Activities for Printing",
    slug: "support-activities-for-printing"
  },
  "32411": {
    description: "See industry description for 324110.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Petroleum Refineries",
    slug: "petroleum-refineries"
  },
  "32412": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing asphalt and tar paving mixtures and blocks and roofing cements and coatings from purchased asphaltic materials and/or (2) saturating purchased mats and felts with asphalt or tar from purchased asphaltic materials.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Asphalt Paving, Roofing, and Saturated Materials Manufacturing",
    slug: "asphalt-paving-roofing-and-saturated-materials-manufacturing"
  },
  "32419": {
    description: "This industry comprises establishments primarily engaged in manufacturing petroleum products (except asphalt paving, roofing, and saturated materials) from refined petroleum or coal products made in coke ovens not integrated with a steel mill. Illustrative Examples: Biodiesel fuels not made in petroleum refineries and blended with purchased refined petroleum, Coke oven products (e.g., coke, gases, tars) made in coke oven establishments, Petroleum brake fluids made from refined petroleum, Petroleum jelly made from refined petroleum, Petroleum briquettes made from refined petroleum, Petroleum lubricating oils and greases made from refined petroleum, Petroleum waxes made from refined petroleum, Re-refining used petroleum lubricating oils.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Other Petroleum and Coal Products Manufacturing",
    slug: "other-petroleum-and-coal-products-manufacturing"
  },
  "32511": {
    description: "See industry description for 325110.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Petrochemical Manufacturing",
    slug: "petrochemical-manufacturing"
  },
  "32512": {
    description: "See industry description for 325120.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Industrial Gas Manufacturing",
    slug: "industrial-gas-manufacturing"
  },
  "32513": {
    description: "See industry description for 325130.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Synthetic Dye and Pigment Manufacturing",
    slug: "synthetic-dye-and-pigment-manufacturing"
  },
  "32518": {
    description: "See industry description for 325180.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Other Basic Inorganic Chemical Manufacturing",
    slug: "other-basic-inorganic-chemical-manufacturing"
  },
  "32519": {
    description: "This industry comprises establishments primarily engaged in manufacturing basic organic chemicals (except petrochemicals, industrial gases, and synthetic dyes and pigments). Illustrative Examples: Biodiesel fuels not made in petroleum refineries and not blended with petroleum, Carbon organic compounds, not specified elsewhere by process, manufacturing, Cyclic intermediates made from refined petroleum or natural gas (except aromatic petrochemicals), Enzyme proteins (i.e., basic synthetic chemicals) (except pharmaceutical use) manufacturing, Gum and wood chemicals manufacturing, Fatty acids (e.g., margaric, oleic, stearic) manufacturing, Organo-inorganic compound manufacturing, Plasticizers (i.e., basic synthetic chemical) manufacturing, Silicone (except resins) manufacturing, Synthetic sweeteners (i.e., sweetening agents) manufacturing.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Other Basic Organic Chemical Manufacturing",
    slug: "other-basic-organic-chemical-manufacturing"
  },
  "32521": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing synthetic resins, plastics materials, and nonvulcanizable elastomers and mixing and blending resins on a custom basis; (2) manufacturing noncustomized synthetic resins; and (3) manufacturing synthetic rubber.",
    industries: ["artificial-rubber-and-synthetic-fiber-manufacturing"],
    name: "Resin and Synthetic Rubber Manufacturing",
    slug: "resin-and-synthetic-rubber-manufacturing"
  },
  "32522": {
    description: "See industry description for 325220.",
    industries: ["artificial-rubber-and-synthetic-fiber-manufacturing"],
    name: "Artificial and Synthetic Fibers and Filaments Manufacturing",
    slug: "artificial-and-synthetic-fibers-and-filaments-manufacturing"
  },
  "32531": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing nitrogenous or phosphatic fertilizer materials; (2) manufacturing fertilizers from sewage or animal waste; (3) manufacturing nitrogenous or phosphatic materials and mixing with other ingredients into fertilizers; and (4) mixing ingredients made elsewhere into fertilizers.",
    industries: ["agricultural-chemical-manufacturing"],
    name: "Fertilizer Manufacturing",
    slug: "fertilizer-manufacturing"
  },
  "32532": {
    description: "See industry description for 325320.",
    industries: ["agricultural-chemical-manufacturing"],
    name: "Pesticide and Other Agricultural Chemical Manufacturing",
    slug: "pesticide-and-other-agricultural-chemical-manufacturing"
  },
  "32541": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing biological and medicinal products; (2) processing (i.e., grading, grinding, and milling) botanical drugs and herbs; (3) isolating active medicinal principals from botanical drugs and herbs; and (4) manufacturing pharmaceutical products intended for internal and external consumption in such forms as ampoules, tablets, capsules, vials, ointments, powders, solutions, and suspensions.",
    industries: ["pharmaceutical-manufacturing"],
    name: "Pharmaceutical and Medicine Manufacturing",
    slug: "pharmaceutical-and-medicine-manufacturing"
  },
  "32551": {
    description: "See industry description for 325510.",
    industries: ["paint-coating-and-adhesive-manufacturing"],
    name: "Paint and Coating Manufacturing",
    slug: "paint-and-coating-manufacturing"
  },
  "32552": {
    description: "See industry description for 325520.",
    industries: ["paint-coating-and-adhesive-manufacturing"],
    name: "Adhesive Manufacturing",
    slug: "adhesive-manufacturing"
  },
  "32561": {
    description: "This industry comprises establishments primarily engaged in manufacturing and packaging soaps and other cleaning compounds, surface active agents, and textile and leather finishing agents used to reduce tension or speed the drying process.",
    industries: ["soap-and-cleaning-product-manufacturing"],
    name: "Soap and Cleaning Compound Manufacturing",
    slug: "soap-and-cleaning-compound-manufacturing"
  },
  "32562": {
    description: "See industry description for 325620.",
    industries: ["personal-care-product-manufacturing"],
    name: "Toilet Preparation Manufacturing",
    slug: "toilet-preparation-manufacturing"
  },
  "32591": {
    description: "See industry description for 325910.",
    industries: ["chemical-manufacturing"],
    name: "Printing Ink Manufacturing",
    slug: "printing-ink-manufacturing"
  },
  "32592": {
    description: "See industry description for 325920.",
    industries: ["chemical-manufacturing"],
    name: "Explosives Manufacturing",
    slug: "explosives-manufacturing"
  },
  "32599": {
    description: "This industry comprises establishments primarily engaged in manufacturing chemical products (except basic chemicals, resins, and synthetic rubber; cellulosic and noncellulosic fibers and filaments; pesticides, fertilizers, and other agricultural chemicals; pharmaceuticals and medicines; paints, coatings, and adhesives; soaps, cleaning compounds, and toilet preparations; printing inks; and explosives). Illustrative Examples: Activated carbon and charcoal manufacturing, Antifreeze preparations manufacturing, Custom compounding (i.e., blending and mixing) of purchased plastics resins, Electronic cigarette vapor liquids, including closed refills, manufacturing, Industrial salt manufacturing, Matches and matchbook manufacturing, Photographic chemicals manufacturing, Pyrotechnics (e.g., flares, flashlight bombs, signals) manufacturing, Sugar substitutes (i.e., synthetic sweeteners blended with other ingredients) made from purchased synthetic sweeteners, Swimming pool chemical preparations manufacturing, Writing inks manufacturing.",
    industries: ["chemical-manufacturing"],
    name: "All Other Chemical Product and Preparation Manufacturing",
    slug: "all-other-chemical-product-and-preparation-manufacturing"
  },
  "32611": {
    description: "This industry comprises establishments primarily engaged in (1) converting plastics resins into unsupported plastics film and sheet and/or (2) forming, coating, or laminating plastics film and sheet into plastics bags.",
    industries: ["plastics-manufacturing"],
    name: "Plastics Packaging Materials and Unlaminated Film and Sheet Manufacturing",
    slug: "plastics-packaging-materials-and-unlaminated-film-and-sheet-manufacturing"
  },
  "32612": {
    description: "This industry comprises establishments primarily engaged in manufacturing plastics pipes and pipe fittings, and plastics profile shapes such as rod, tube, and sausage casings.",
    industries: ["plastics-manufacturing"],
    name: "Plastics Pipe, Pipe Fitting, and Unlaminated Profile Shape Manufacturing",
    slug: "plastics-pipe-pipe-fitting-and-unlaminated-profile-shape-manufacturing"
  },
  "32613": {
    description: "See industry description for 326130.",
    industries: ["plastics-manufacturing"],
    name: "Laminated Plastics Plate, Sheet (except Packaging), and Shape Manufacturing",
    slug: "laminated-plastics-plate-sheet-except-packaging-and-shape-manufacturing"
  },
  "32614": {
    description: "See industry description for 326140.",
    industries: ["plastics-manufacturing"],
    name: "Polystyrene Foam Product Manufacturing",
    slug: "polystyrene-foam-product-manufacturing"
  },
  "32615": {
    description: "See industry description for 326150.",
    industries: ["plastics-manufacturing"],
    name: "Urethane and Other Foam Product (except Polystyrene) Manufacturing",
    slug: "urethane-and-other-foam-product-except-polystyrene-manufacturing"
  },
  "32616": {
    description: "See industry description for 326160.",
    industries: ["plastics-manufacturing"],
    name: "Plastics Bottle Manufacturing",
    slug: "plastics-bottle-manufacturing"
  },
  "32619": {
    description: "This industry comprises establishments primarily engaged in manufacturing plastics plumbing fixtures and other plastics products (except film, sheet, bags, profile shapes, pipes, pipe fittings, laminates, foam products, bottles, and hoses). Illustrative Examples: Inflatable plastics swimming pool rafts and similar flotation devices manufacturing, Plastics air mattresses manufacturing, Plastics bottle caps and lids manufacturing, Plastics bowls and bowl covers manufacturing, Plastics clothes hangers manufacturing, Plastics cups (except foam) manufacturing, Plastics dinnerware (except foam) manufacturing, Plastics gloves manufacturing, Plastics hardware manufacturing, Plastics ice chests or coolers (except plastics foam) manufacturing, Plastics or fiberglass plumbing fixtures (e.g., toilets, shower stalls, urinals) manufacturing, Plastics prefabricated buildings manufacturing, Plastics siding manufacturing, Plastics trash containers manufacturing, Resilient floor coverings (e.g., sheet, tiles) manufacturing.",
    industries: ["plastics-manufacturing"],
    name: "Other Plastics Product Manufacturing",
    slug: "other-plastics-product-manufacturing"
  },
  "32621": {
    description: "This industry comprises establishments primarily engaged in manufacturing tires and inner tubes from natural and synthetic rubber and retreading or rebuilding tires.",
    industries: ["rubber-products-manufacturing"],
    name: "Tire Manufacturing",
    slug: "tire-manufacturing"
  },
  "32622": {
    description: "See industry description for 326220.",
    industries: ["rubber-products-manufacturing"],
    name: "Rubber and Plastics Hoses and Belting Manufacturing",
    slug: "rubber-and-plastics-hoses-and-belting-manufacturing"
  },
  "32629": {
    description: "This industry comprises establishments primarily engaged in manufacturing rubber products (except tires, hoses, and belting) from natural and synthetic rubber. Illustrative Examples: Birth control devices (e.g., diaphragms, prophylactics) manufacturing, Latex foam rubber manufacturing, Mechanical rubber goods (i.e., molded, extruded, lathe-cut) manufacturing, Reclaiming rubber from waste and scrap, Rubber balloons manufacturing, Rubber bands manufacturing, Rubber floor mats (e.g., door, bath) manufacturing, Rubber hair care products (e.g., combs, curlers) manufacturing, Rubber tubing manufacturing.",
    industries: ["rubber-products-manufacturing"],
    name: "Other Rubber Product Manufacturing",
    slug: "other-rubber-product-manufacturing"
  },
  "32711": {
    description: "See industry description for 327110.",
    industries: ["clay-and-refractory-products-manufacturing"],
    name: "Pottery, Ceramics, and Plumbing Fixture Manufacturing",
    slug: "pottery-ceramics-and-plumbing-fixture-manufacturing"
  },
  "32712": {
    description: "See industry description for 327120.",
    industries: ["clay-and-refractory-products-manufacturing"],
    name: "Clay Building Material and Refractories Manufacturing",
    slug: "clay-building-material-and-refractories-manufacturing"
  },
  "32721": {
    description: "This industry comprises establishments primarily engaged in manufacturing glass and/or glass products.  Establishments in this industry may manufacture glass and/or glass products by melting silica sand or cullet, or from purchased glass.",
    industries: ["glass-product-manufacturing"],
    name: "Glass and Glass Product Manufacturing",
    slug: "glass-and-glass-product-manufacturing"
  },
  "32731": {
    description: "See industry description for 327310.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Cement Manufacturing",
    slug: "cement-manufacturing"
  },
  "32732": {
    description: "See industry description for 327320.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Ready-Mix Concrete Manufacturing",
    slug: "ready-mix-concrete-manufacturing"
  },
  "32733": {
    description: "This industry comprises establishments primarily engaged in manufacturing concrete pipe, brick, and block. Cross-References.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Concrete Pipe, Brick, and Block Manufacturing",
    slug: "concrete-pipe-brick-and-block-manufacturing"
  },
  "32739": {
    description: "See industry description for 327390.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Other Concrete Product Manufacturing",
    slug: "other-concrete-product-manufacturing"
  },
  "32741": {
    description: "See industry description for 327410.",
    industries: ["lime-and-gypsum-products-manufacturing"],
    name: "Lime Manufacturing",
    slug: "lime-manufacturing"
  },
  "32742": {
    description: "See industry description for 327420.",
    industries: ["lime-and-gypsum-products-manufacturing"],
    name: "Gypsum Product Manufacturing",
    slug: "gypsum-product-manufacturing"
  },
  "32791": {
    description: "See industry description for 327910.",
    industries: ["abrasives-and-nonmetallic-minerals-manufacturing"],
    name: "Abrasive Product Manufacturing",
    slug: "abrasive-product-manufacturing"
  },
  "32799": {
    description: "This industry comprises establishments primarily engaged in manufacturing nonmetallic mineral products (except pottery and plumbing fixtures; clay building materials and refractories; glass and glass products; cement; ready-mix concrete; concrete products; lime; gypsum products; and abrasive products).",
    industries: ["abrasives-and-nonmetallic-minerals-manufacturing"],
    name: "All Other Nonmetallic Mineral Product Manufacturing",
    slug: "all-other-nonmetallic-mineral-product-manufacturing"
  },
  "33111": {
    description: "See industry description for 331110.",
    industries: ["primary-metal-manufacturing"],
    name: "Iron and Steel Mills and Ferroalloy Manufacturing",
    slug: "iron-and-steel-mills-and-ferroalloy-manufacturing"
  },
  "33121": {
    description: "See industry description for 331210.",
    industries: ["primary-metal-manufacturing"],
    name: "Iron and Steel Pipe and Tube Manufacturing from Purchased Steel",
    slug: "iron-and-steel-pipe-and-tube-manufacturing-from-purchased-steel"
  },
  "33122": {
    description: "This industry comprises establishments primarily engaged in rolling and/or drawing steel shapes, such as plate, sheet, strip, rod, and bar, from purchased steel.",
    industries: ["primary-metal-manufacturing"],
    name: "Rolling and Drawing of Purchased Steel",
    slug: "rolling-and-drawing-of-purchased-steel"
  },
  "33131": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) refining alumina; (2) making (i.e., the primary production) aluminum from alumina; (3) recovering aluminum from scrap or dross; (4) alloying purchased aluminum; and (5) manufacturing aluminum primary forms (e.g., bar, foil, pipe, plate, rod, sheet, tube, wire).",
    industries: ["primary-metal-manufacturing"],
    name: "Alumina and Aluminum Production and Processing",
    slug: "alumina-and-aluminum-production-and-processing"
  },
  "33141": {
    description: "See industry description for 331410.",
    industries: ["primary-metal-manufacturing"],
    name: "Nonferrous Metal (except Aluminum) Smelting and Refining",
    slug: "nonferrous-metal-except-aluminum-smelting-and-refining"
  },
  "33142": {
    description: "See industry description for 331420.",
    industries: ["primary-metal-manufacturing"],
    name: "Copper Rolling, Drawing, Extruding, and Alloying",
    slug: "copper-rolling-drawing-extruding-and-alloying"
  },
  "33149": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) recovering nonferrous metals (except copper and aluminum) and nonferrous metal alloys from scrap; (2) alloying purchased nonferrous metals (except copper and aluminum); (3) rolling, drawing, and extruding shapes from purchased nonferrous metals (except copper and aluminum); and (4) recovering nonferrous metals from scrap (except copper and aluminum) and rolling, drawing, or extruding shapes in integrated facilities.",
    industries: ["primary-metal-manufacturing"],
    name: "Nonferrous Metal (except Copper and Aluminum) Rolling, Drawing, Extruding, and Alloying",
    slug: "nonferrous-metal-except-copper-and-aluminum-rolling-drawing-extruding-and-alloying"
  },
  "33151": {
    description: "This industry comprises establishments primarily engaged in pouring molten iron and steel into molds of a desired shape to make castings.  Establishments in this industry purchase iron and steel made in other establishments. Cross-References.",
    industries: ["primary-metal-manufacturing"],
    name: "Ferrous Metal Foundries",
    slug: "ferrous-metal-foundries"
  },
  "33152": {
    description: "This industry comprises establishments primarily engaged in pouring and/or introducing molten nonferrous metal, under high pressure, into metal molds or dies to manufacture castings.  Establishments in this industry purchase nonferrous metals made in other establishments.",
    industries: ["primary-metal-manufacturing"],
    name: "Nonferrous Metal Foundries",
    slug: "nonferrous-metal-foundries"
  },
  "33211": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing forgings from purchased metals; (2) manufacturing metal custom roll forming products; (3) manufacturing metal stamped and spun products (except automotive, cans, coins); and (4) manufacturing powder metallurgy products.  Establishments making metal forgings, metal stampings, and metal spun products and further manufacturing (e.g., machining, assembling) a specific manufactured product are classified in the industry of the finished product.  Metal forging, metal stamping, and metal spun products establishments may perform surface finishing operations, such as cleaning and deburring, on the products they manufacture.",
    industries: ["fabricated-metal-products"],
    name: "Forging and Stamping",
    slug: "forging-and-stamping"
  },
  "33221": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing metal kitchen cookware (except by casting (e.g., cast iron skillets) or stamped without further fabrication), utensils, and/or nonprecious and precious plated metal cutlery and flatware; (2) manufacturing saw blades, all types (including those for power sawing machines); and (3) manufacturing nonpowered handtools and edge tools.",
    industries: ["cutlery-and-handtool-manufacturing"],
    name: "Cutlery and Handtool Manufacturing",
    slug: "cutlery-and-handtool-manufacturing"
  },
  "33231": {
    description: "This industry comprises establishments primarily engaged in manufacturing one or more of the following: (1) prefabricated metal buildings, panels, and sections; (2) structural metal products; and (3) metal plate work products.",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Plate Work and Fabricated Structural Product Manufacturing",
    slug: "plate-work-and-fabricated-structural-product-manufacturing"
  },
  "33232": {
    description: "This industry comprises establishments primarily engaged in manufacturing one or more of the following: (1) metal framed windows (i.e., typically using purchased glass) and metal doors; (2) sheet metal work; and (3) ornamental and architectural metal products.",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Ornamental and Architectural Metal Products Manufacturing",
    slug: "ornamental-and-architectural-metal-products-manufacturing"
  },
  "33241": {
    description: "See industry description for 332410.",
    industries: ["boilers-tanks-and-shipping-container-manufacturing"],
    name: "Power Boiler and Heat Exchanger Manufacturing",
    slug: "power-boiler-and-heat-exchanger-manufacturing"
  },
  "33242": {
    description: "See industry description for 332420.",
    industries: ["boilers-tanks-and-shipping-container-manufacturing"],
    name: "Metal Tank (Heavy Gauge) Manufacturing",
    slug: "metal-tank-heavy-gauge-manufacturing"
  },
  "33243": {
    description: "This industry comprises establishments primarily engaged in forming light gauge metal containers.",
    industries: ["boilers-tanks-and-shipping-container-manufacturing"],
    name: "Metal Can, Box, and Other Metal Container (Light Gauge) Manufacturing",
    slug: "metal-can-box-and-other-metal-container-light-gauge-manufacturing"
  },
  "33251": {
    description: "See industry description for 332510.",
    industries: ["construction-hardware-manufacturing"],
    name: "Hardware Manufacturing",
    slug: "hardware-manufacturing"
  },
  "33261": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing steel springs by forming, such as cutting, bending, and heat winding, metal rod or strip stock and/or (2) manufacturing wire springs and fabricated wire products from wire drawn elsewhere (except watch and clock springs).",
    industries: ["spring-and-wire-product-manufacturing"],
    name: "Spring and Wire Product Manufacturing",
    slug: "spring-and-wire-product-manufacturing"
  },
  "33271": {
    description: "See industry description for 332710.",
    industries: ["turned-products-and-fastener-manufacturing"],
    name: "Machine Shops",
    slug: "machine-shops"
  },
  "33272": {
    description: "This industry comprises establishments primarily engaged in (1) machining precision turned products or (2) manufacturing metal bolts, nuts, screws, rivets, and other industrial fasteners.  Included in this industry are establishments primarily engaged in manufacturing parts for machinery and equipment on a custom basis.",
    industries: ["turned-products-and-fastener-manufacturing"],
    name: "Turned Product and Screw, Nut, and Bolt Manufacturing",
    slug: "turned-product-and-screw-nut-and-bolt-manufacturing"
  },
  "33281": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) heat treating metals and metal products; (2) enameling, lacquering, and varnishing metals and metal products; (3) hot dip galvanizing metals and metal products; (4) engraving, chasing, or etching metals and metal products (except jewelry; personal goods carried on or about the person, such as compacts and cigarette cases; precious metal products (except precious plated flatware and other plated ware); and printing plates); (5) powder coating metals and metal products; (6) electroplating, plating, anodizing, coloring, and finishing metals and metal products; and (7) providing other metal surfacing services for the trade.  Establishments in this industry coat, engrave, and heat treat metals and metal formed products fabricated elsewhere.  Included in this industry are establishments that perform these processes on other materials, such as plastics, in addition to metals.",
    industries: ["metal-treatments"],
    name: "Coating, Engraving, Heat Treating, and Allied Activities",
    slug: "coating-engraving-heat-treating-and-allied-activities"
  },
  "33291": {
    description: "This industry comprises establishments primarily engaged in manufacturing one or more of the following metal valves: (1) industrial valves; (2) fluid power valves and hose fittings; (3) plumbing fixture fittings and trim; and (4) other metal valves and pipe fittings.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Metal Valve Manufacturing",
    slug: "metal-valve-manufacturing"
  },
  "33299": {
    description: "This industry comprises establishments primarily engaged in manufacturing fabricated metal products (except forgings and stampings, cutlery and handtools, architectural and structural metal products, boilers, tanks, shipping containers, hardware, spring and wire products, machine shop products, turned products, screws, nuts and bolts, and metal valves). Illustrative Examples: Ammunition manufacturing, Ball and roller bearing manufacturing, Enameled iron and metal sanitary ware manufacturing , Fabricated pipe and pipe fittings made from purchased metal pipe , Foil containers (except bags) manufacturing, Industrial pattern manufacturing, Metal safes manufacturing, Portable metal ladders manufacturing, Small arms and other ordnance manufacturing, Steel wool manufacturing.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "All Other Fabricated Metal Product Manufacturing",
    slug: "all-other-fabricated-metal-product-manufacturing"
  },
  "33311": {
    description: "This industry comprises establishments primarily engaged in manufacturing farm machinery and equipment, powered mowing equipment, and other powered home lawn and garden equipment. Illustrative Examples: Combines (i.e., harvester-threshers) manufacturing, Cotton ginning machinery manufacturing, Fertilizing machinery, farm-type, manufacturing, Haying machines manufacturing, Milking machines manufacturing, Planting machines, farm-type, manufacturing, Plows, farm-type, manufacturing, Powered lawnmowers manufacturing, Poultry brooders, feeders, and waterers manufacturing, Snowblowers and throwers, residential-type, manufacturing, Tractors and attachments, lawn and garden-type and farm-type, manufacturing.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Agricultural Implement Manufacturing",
    slug: "agricultural-implement-manufacturing"
  },
  "33312": {
    description: "See industry description for 333120.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Construction Machinery Manufacturing",
    slug: "construction-machinery-manufacturing"
  },
  "33313": {
    description: "This industry comprises establishments primarily engaged in manufacturing oil and gas field and underground mining machinery and equipment. Illustrative Examples: Coal breakers, cutters, and pulverizers manufacturing, Core drills, underground mining-type, manufacturing, Mineral processing and beneficiating machinery manufacturing, Mining cars manufacturing, Oil and gas field-type derricks manufacturing, Oil and gas field-type drilling machinery and equipment (except offshore floating platforms) manufacturing, Stationary rock crushing machinery manufacturing, Water well drilling machinery manufacturing.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Mining and Oil and Gas Field Machinery Manufacturing",
    slug: "mining-and-oil-and-gas-field-machinery-manufacturing"
  },
  "33324": {
    description: "This industry comprises establishments primarily engaged in manufacturing industrial machinery, such as food and beverage manufacturing machinery, semiconductor manufacturing machinery, sawmill and woodworking machinery (except handheld), machinery for making paper and paper products, printing and binding machinery and equipment, textile making machinery, and machinery for making plastics and rubber products.",
    industries: ["industrial-machinery-manufacturing"],
    name: "Industrial Machinery Manufacturing",
    slug: "industrial-machinery-manufacturing"
  },
  "33331": {
    description: "See industry description for 333310.",
    industries: ["commercial-and-service-industry-machinery-manufacturing"],
    name: "Commercial and Service Industry Machinery Manufacturing",
    slug: "commercial-and-service-industry-machinery-manufacturing"
  },
  "33341": {
    description: "This industry comprises establishments primarily engaged in manufacturing ventilating, heating, air-conditioning, and commercial and industrial refrigeration and freezer equipment. Illustrative Examples: Air-conditioner filters manufacturing, Air-conditioning and warm air heating combination units manufacturing, Attic fans manufacturing, Dust and fume collecting equipment manufacturing , Gas fireplaces manufacturing, Heating boilers manufacturing, Industrial and commercial-type fans manufacturing, Refrigerated counter and display cases manufacturing, Refrigerated drinking fountains manufacturing, Space heaters (except portable electric) manufacturing .",
    industries: ["hvac-and-refrigeration-equipment-manufacturing"],
    name: "Ventilation, Heating, Air-Conditioning, and Commercial Refrigeration Equipment Manufacturing",
    slug: "ventilation-heating-air-conditioning-and-commercial-refrigeration-equipment-manufacturing"
  },
  "33351": {
    description: "This industry comprises establishments primarily engaged in manufacturing metalworking machinery, such as metal cutting and metal forming machine tools; cutting tools; accessories for metalworking machinery; special dies, tools, jigs, and fixtures; industrial molds; rolling mill machinery; assembly machinery; coil handling, conversion, or straightening equipment; and wire drawing and fabricating machines.",
    industries: ["metalworking-machinery-manufacturing"],
    name: "Metalworking Machinery Manufacturing",
    slug: "metalworking-machinery-manufacturing"
  },
  "33361": {
    description: "This industry comprises establishments primarily engaged in manufacturing turbines, power transmission equipment, and internal combustion engines (except automotive gasoline and aircraft). Illustrative Examples: Clutches and brakes (except electromagnetic industrial controls, motor vehicle) manufacturing, Diesel and semidiesel engines manufacturing, Electric outboard motors manufacturing, Internal combustion engines for hybrid drive systems (except automotive) manufacturing, Plain bearings (except internal combustion engine) manufacturing, Power transmission pulleys manufacturing, Plain bushings (except internal combustion engine) manufacturing, Speed changers (i.e., power transmission equipment) manufacturing, Speed reducers (i.e., power transmission equipment) manufacturing, Turbine generator set units manufacturing, Universal joints (except aircraft, motor vehicle) manufacturing.",
    industries: ["engines-and-power-transmission-equipment-manufacturing"],
    name: "Engine, Turbine, and Power Transmission Equipment Manufacturing",
    slug: "engine-turbine-and-power-transmission-equipment-manufacturing"
  },
  "33391": {
    description: "This industry comprises establishments primarily engaged in manufacturing pumps and compressors, such as general purpose air and gas compressors, nonagricultural spraying and dusting equipment, general purpose pumps and pumping equipment (except fluid power pumps and motors), and measuring and dispensing pumps.",
    industries: ["machinery-manufacturing"],
    name: "Pump and Compressor Manufacturing",
    slug: "pump-and-compressor-manufacturing"
  },
  "33392": {
    description: "This industry comprises establishments primarily engaged in manufacturing material handling equipment, such as elevators and moving stairs; conveyors and conveying equipment; overhead traveling cranes, hoists, and monorail systems; and industrial trucks, tractors, trailers, and stacker machinery.",
    industries: ["machinery-manufacturing"],
    name: "Material Handling Equipment Manufacturing",
    slug: "material-handling-equipment-manufacturing"
  },
  "33399": {
    description: "This industry comprises establishments primarily engaged in manufacturing general purpose machinery (except ventilation, heating, air-conditioning, and commercial refrigeration equipment; metalworking machinery; engines, turbines, and power transmission equipment; pumps and compressors; and material handling equipment). Illustrative Examples: Automatic fire sprinkler systems manufacturing, Bridge and gate lifting machinery manufacturing, Fluid power cylinders manufacturing, Fluid power pumps manufacturing, Hydraulic and pneumatic jacks manufacturing, Industrial-type furnaces manufacturing, Packaging machinery manufacturing, Power-driven handtools manufacturing, Scales and balances manufacturing, Welding equipment manufacturing.",
    industries: ["machinery-manufacturing"],
    name: "All Other General Purpose Machinery Manufacturing",
    slug: "all-other-general-purpose-machinery-manufacturing"
  },
  "33411": {
    description: "This industry comprises establishments primarily engaged in manufacturing and/or assembling electronic computers, such as mainframes, personal computers, workstations, laptops, and computer servers; and computer peripheral equipment, such as storage devices, printers, monitors, and input/output devices and terminals.  Computers can be analog, digital, or hybrid.  Digital computers, the most common type, are devices that do all of the following: (1) store the processing program or programs and the data immediately necessary for the execution of the program; (2) can be freely programmed in accordance with the requirements of the user; (3) perform arithmetical computations specified by the user; and (4) execute, without human intervention, a processing program that requires the computer to modify its execution by logical decision during the processing run.  Analog computers are capable of simulating mathematical models and comprise at least analog, control, and programming elements.",
    industries: ["computer-hardware-manufacturing"],
    name: "Computer and Peripheral Equipment Manufacturing",
    slug: "computer-and-peripheral-equipment-manufacturing"
  },
  "33421": {
    description: "See industry description for 334210.",
    industries: ["communications-equipment-manufacturing"],
    name: "Telephone Apparatus Manufacturing",
    slug: "telephone-apparatus-manufacturing"
  },
  "33422": {
    description: "See industry description for 334220.",
    industries: ["communications-equipment-manufacturing"],
    name: "Radio and Television Broadcasting and Wireless Communications Equipment Manufacturing",
    slug: "radio-and-television-broadcasting-and-wireless-communications-equipment-manufacturing"
  },
  "33429": {
    description: "See industry description for 334290.",
    industries: ["communications-equipment-manufacturing"],
    name: "Other Communications Equipment Manufacturing",
    slug: "other-communications-equipment-manufacturing"
  },
  "33431": {
    description: "See industry description for 334310.",
    industries: ["audio-and-video-equipment-manufacturing"],
    name: "Audio and Video Equipment Manufacturing",
    slug: "audio-and-video-equipment-manufacturing"
  },
  "33441": {
    description: "This industry comprises establishments primarily engaged in manufacturing semiconductors and other components for electronic applications.  Examples of products made by these establishments are capacitors, resistors, microprocessors, bare and loaded printed circuit boards, electron tubes, electronic connectors, and computer modems.",
    industries: ["renewable-energy-semiconductor-manufacturing"],
    name: "Semiconductor and Other Electronic Component Manufacturing",
    slug: "semiconductor-and-other-electronic-component-manufacturing"
  },
  "33451": {
    description: "This industry comprises establishments primarily engaged in manufacturing navigational, measuring, electromedical, and control instruments.  Examples of products made by these establishments are aeronautical instruments, appliance regulators and controls (except switches), laboratory analytical instruments, navigation and guidance systems, and physical properties testing equipment.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Navigational, Measuring, Electromedical, and Control Instruments Manufacturing",
    slug: "navigational-measuring-electromedical-and-control-instruments-manufacturing"
  },
  "33461": {
    description: "See industry description for 334610.",
    industries: ["magnetic-and-optical-media-manufacturing"],
    name: "Manufacturing and Reproducing Magnetic and Optical Media",
    slug: "manufacturing-and-reproducing-magnetic-and-optical-media"
  },
  "33513": {
    description: "This industry comprises establishments primarily engaged in manufacturing electric lighting fixtures (except vehicular), nonelectric lighting equipment, lamp shades (except glass and plastics), electric light bulbs, tubes, and parts (except glass blanks for electric light bulbs and light emitting diodes (LEDs)), and lighting fixture components (except current-carrying wiring devices).",
    industries: ["electric-lighting-equipment-manufacturing"],
    name: "Electric Lighting Equipment Manufacturing",
    slug: "electric-lighting-equipment-manufacturing"
  },
  "33521": {
    description: "See industry description for 335210.",
    industries: ["household-appliance-manufacturing"],
    name: "Small Electrical Appliance Manufacturing",
    slug: "small-electrical-appliance-manufacturing"
  },
  "33522": {
    description: "See industry description for 335220.",
    industries: ["household-appliance-manufacturing"],
    name: "Major Household Appliance Manufacturing",
    slug: "major-household-appliance-manufacturing"
  },
  "33531": {
    description: "This industry comprises establishments primarily engaged in manufacturing power, distribution, and specialty transformers; electric motors, generators, and motor generator sets; switchgear and switchboard apparatus; relays; and industrial controls.",
    industries: ["electrical-equipment-manufacturing"],
    name: "Electrical Equipment Manufacturing",
    slug: "electrical-equipment-manufacturing"
  },
  "33591": {
    description: "See industry description for 335910.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Battery Manufacturing",
    slug: "battery-manufacturing"
  },
  "33592": {
    description: "This industry comprises establishments primarily engaged in insulating fiber optic cable, and manufacturing insulated nonferrous wire and cable from nonferrous wire drawn in other establishments.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Communication and Energy Wire and Cable Manufacturing",
    slug: "communication-and-energy-wire-and-cable-manufacturing"
  },
  "33593": {
    description: "This industry comprises establishments primarily engaged in manufacturing current-carrying wiring devices and noncurrent-carrying wiring devices for wiring electrical circuits.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Wiring Device Manufacturing",
    slug: "wiring-device-manufacturing"
  },
  "33599": {
    description: "This industry comprises establishments primarily engaged in manufacturing electrical equipment (except electric lighting equipment, household-type appliances, transformers, motors, generators, switchgear, relays, industrial controls, batteries, communication and energy wire and cable, and wiring devices). Illustrative Examples: Carbon and graphite electrodes and brushes manufacturing, Extension cords made from purchased insulated wire, Door opening and closing devices, electrical, manufacturing, Surge suppressors manufacturing .",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "All Other Electrical Equipment and Component Manufacturing",
    slug: "all-other-electrical-equipment-and-component-manufacturing"
  },
  "33611": {
    description: "See industry description for 336110.",
    industries: ["motor-vehicle-manufacturing"],
    name: "Automobile and Light Duty Motor Vehicle Manufacturing",
    slug: "automobile-and-light-duty-motor-vehicle-manufacturing"
  },
  "33612": {
    description: "See industry description for 336120.",
    industries: ["motor-vehicle-manufacturing"],
    name: "Heavy Duty Truck Manufacturing",
    slug: "heavy-duty-truck-manufacturing"
  },
  "33621": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing motor vehicle bodies and cabs or (2) manufacturing truck, automobile and utility trailers, truck trailer chassis, detachable trailer bodies, and detachable trailer chassis.  The products made may be sold separately or may be assembled on purchased chassis and sold as complete vehicles.  Motor homes are units where the motor and the living quarters are contained in the same integrated unit, while travel trailers are designed to be towed by a motor unit, such as an automobile or a light truck. Illustrative Examples: Bodies and cabs, truck, manufacturing, Camper units, slide-in, for pick-up trucks, manufacturing, Pick-up canopies, caps, or covers manufacturing, Motor homes, self-contained, assembling on purchased chassis, Semi-trailers manufacturing, Travel trailers, recreational, manufacturing.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Motor Vehicle Body and Trailer Manufacturing",
    slug: "motor-vehicle-body-and-trailer-manufacturing"
  },
  "33631": {
    description: "See industry description for 336310.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Gasoline Engine and Engine Parts Manufacturing",
    slug: "motor-vehicle-gasoline-engine-and-engine-parts-manufacturing"
  },
  "33632": {
    description: "See industry description for 336320.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Electrical and Electronic Equipment Manufacturing",
    slug: "motor-vehicle-electrical-and-electronic-equipment-manufacturing"
  },
  "33633": {
    description: "See industry description for 336330.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Steering and Suspension Components (except Spring) Manufacturing",
    slug: "motor-vehicle-steering-and-suspension-components-except-spring-manufacturing"
  },
  "33634": {
    description: "See industry description for 336340.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Brake System Manufacturing",
    slug: "motor-vehicle-brake-system-manufacturing"
  },
  "33635": {
    description: "See industry description for 336350.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Transmission and Power Train Parts Manufacturing",
    slug: "motor-vehicle-transmission-and-power-train-parts-manufacturing"
  },
  "33636": {
    description: "See industry description for 336360.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Seating and Interior Trim Manufacturing",
    slug: "motor-vehicle-seating-and-interior-trim-manufacturing"
  },
  "33637": {
    description: "See industry description for 336370.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Metal Stamping",
    slug: "motor-vehicle-metal-stamping"
  },
  "33639": {
    description: "See industry description for 336390.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Other Motor Vehicle Parts Manufacturing",
    slug: "other-motor-vehicle-parts-manufacturing"
  },
  "33641": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing complete aircraft, missiles, or space vehicles; (2) manufacturing aerospace engines, propulsion units, auxiliary equipment, or parts; (3) developing and making prototypes of aerospace products; (4) aircraft conversion (i.e., major modifications to systems); and (5) complete aircraft or propulsion systems overhaul and rebuilding (i.e., periodic restoration of aircraft to original design specifications). Cross-References.",
    industries: ["aviation-and-aerospace-component-manufacturing"],
    name: "Aerospace Product and Parts Manufacturing",
    slug: "aerospace-product-and-parts-manufacturing"
  },
  "33651": {
    description: "See industry description for 336510.",
    industries: ["railroad-equipment-manufacturing"],
    name: "Railroad Rolling Stock Manufacturing",
    slug: "railroad-rolling-stock-manufacturing"
  },
  "33661": {
    description: "This industry comprises establishments primarily engaged in operating shipyards or boat yards (i.e., ship or boat manufacturing facilities).  Shipyards are fixed facilities with drydocks and fabrication equipment capable of building a ship, defined as watercraft typically suitable or intended for other than personal or recreational use.  Boats are defined as watercraft typically suitable or intended for personal use.  Activities of shipyards include the construction of ships, their repair, conversion and alteration, production of prefabricated ship and barge sections, and specialized services, such as ship scaling. Illustrative Examples: Barge building, Boat yards (i.e., boat manufacturing facilities), Cargo ship building, Drilling and production platforms, floating, oil and gas, building, Passenger ship building, Inflatable plastic boats, heavy-duty, manufacturing, Inflatable rubber boats, heavy-duty, manufacturing, Rigid inflatable boats (RIBs) manufacturing, Boats (e.g., motorboats, rowboats, canoes, kayaks) manufacturing .",
    industries: ["shipbuilding"],
    name: "Ship and Boat Building",
    slug: "ship-and-boat-building"
  },
  "33699": {
    description: "This industry comprises establishments primarily engaged in manufacturing motorcycles, bicycles, metal tricycles, complete military armored vehicles, tanks, self-propelled weapons, vehicles pulled by draft animals, and other transportation equipment (except motor vehicles, boats, ships, railroad rolling stock, and aerospace products), including parts thereof.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Other Transportation Equipment Manufacturing",
    slug: "other-transportation-equipment-manufacturing"
  },
  "33711": {
    description: "See industry description for 337110.",
    industries: ["household-and-institutional-furniture-manufacturing"],
    name: "Wood Kitchen Cabinet and Countertop Manufacturing",
    slug: "wood-kitchen-cabinet-and-countertop-manufacturing"
  },
  "33712": {
    description: "This industry comprises establishments primarily engaged in manufacturing household-type and public building furniture (e.g., library, school, theater, and church furniture).  This industry includes establishments that manufacture general purpose hospital, laboratory, and/or dental furniture (e.g., stools, tables, benches).  The furniture may be made on a stock or custom basis and may be assembled or unassembled (i.e., knockdown).",
    industries: ["household-and-institutional-furniture-manufacturing"],
    name: "Household and Institutional Furniture Manufacturing",
    slug: "household-and-institutional-furniture-manufacturing"
  },
  "33721": {
    description: "This industry comprises establishments primarily engaged in manufacturing office furniture and/or office and store fixtures.  The furniture may be made on a stock or custom basis and may be assembled or unassembled (i.e., knockdown).",
    industries: ["office-furniture-and-fixtures-manufacturing"],
    name: "Office Furniture (including Fixtures) Manufacturing",
    slug: "office-furniture-including-fixtures-manufacturing"
  },
  "33791": {
    description: "See industry description for 337910.",
    industries: ["furniture-and-home-furnishings-manufacturing"],
    name: "Mattress Manufacturing",
    slug: "mattress-manufacturing"
  },
  "33792": {
    description: "See industry description for 337920.",
    industries: ["furniture-and-home-furnishings-manufacturing"],
    name: "Blind and Shade Manufacturing",
    slug: "blind-and-shade-manufacturing"
  },
  "33911": {
    description: "This industry comprises establishments primarily engaged in manufacturing medical equipment and supplies.  Examples of products made by these establishments are surgical and medical instruments, surgical appliances and supplies, dental equipment and supplies, orthodontic goods, ophthalmic goods, dentures, and orthodontic appliances.",
    industries: ["medical-equipment-manufacturing"],
    name: "Medical Equipment and Supplies Manufacturing",
    slug: "medical-equipment-and-supplies-manufacturing"
  },
  "33991": {
    description: "See industry description for 339910.",
    industries: ["manufacturing"],
    name: "Jewelry and Silverware Manufacturing",
    slug: "jewelry-and-silverware-manufacturing"
  },
  "33992": {
    description: "See industry description for 339920.",
    industries: ["sporting-goods-manufacturing"],
    name: "Sporting and Athletic Goods Manufacturing",
    slug: "sporting-and-athletic-goods-manufacturing"
  },
  "33993": {
    description: "See industry description for 339930.",
    industries: ["manufacturing"],
    name: "Doll, Toy, and Game Manufacturing",
    slug: "doll-toy-and-game-manufacturing"
  },
  "33994": {
    description: "See industry description for 339940.",
    industries: ["manufacturing"],
    name: "Office Supplies (except Paper) Manufacturing",
    slug: "office-supplies-except-paper-manufacturing"
  },
  "33995": {
    description: "See industry description for 339950.",
    industries: ["manufacturing"],
    name: "Sign Manufacturing",
    slug: "sign-manufacturing"
  },
  "33999": {
    description: "This industry comprises establishments primarily engaged in miscellaneous manufacturing (except medical equipment and supplies, jewelry and silverware, sporting and athletic goods, dolls, toys, games, office supplies, and signs). Illustrative Examples: Artificial Christmas trees manufacturing, Burial caskets and cases manufacturing, Candles manufacturing, Coin- or card-operated amusement machines (except jukebox) manufacturing, Electronic cigarettes manufacturing, Floor and dust mops manufacturing, Musical instruments (except toy) manufacturing, Fasteners, buttons, needles, and pins (except precious metals or precious and semiprecious stones and gems) manufacturing, Gasket, packing, and sealing devices manufacturing, Portable fire extinguishers manufacturing, Umbrellas manufacturing.",
    industries: ["manufacturing"],
    name: "All Other Miscellaneous Manufacturing",
    slug: "all-other-miscellaneous-manufacturing"
  },
  "42311": {
    description: "See industry description for 423110.",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Automobile and Other Motor Vehicle Merchant Wholesalers",
    slug: "automobile-and-other-motor-vehicle-merchant-wholesalers"
  },
  "42312": {
    description: "See industry description for 423120.",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Motor Vehicle Supplies and New Parts Merchant Wholesalers",
    slug: "motor-vehicle-supplies-and-new-parts-merchant-wholesalers"
  },
  "42313": {
    description: "See industry description for 423130.",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Tire and Tube Merchant Wholesalers",
    slug: "tire-and-tube-merchant-wholesalers"
  },
  "42314": {
    description: "See industry description for 423140.",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Motor Vehicle Parts (Used) Merchant Wholesalers",
    slug: "motor-vehicle-parts-used-merchant-wholesalers"
  },
  "42321": {
    description: "See industry description for 423210.",
    industries: ["wholesale-furniture-and-home-furnishings"],
    name: "Furniture Merchant Wholesalers",
    slug: "furniture-merchant-wholesalers"
  },
  "42322": {
    description: "See industry description for 423220.",
    industries: ["wholesale-furniture-and-home-furnishings"],
    name: "Home Furnishing Merchant Wholesalers",
    slug: "home-furnishing-merchant-wholesalers"
  },
  "42331": {
    description: "See industry description for 423310.",
    industries: ["wholesale-building-materials"],
    name: "Lumber, Plywood, Millwork, and Wood Panel Merchant Wholesalers",
    slug: "lumber-plywood-millwork-and-wood-panel-merchant-wholesalers"
  },
  "42332": {
    description: "See industry description for 423320.",
    industries: ["wholesale-building-materials"],
    name: "Brick, Stone, and Related Construction Material Merchant Wholesalers",
    slug: "brick-stone-and-related-construction-material-merchant-wholesalers"
  },
  "42333": {
    description: "See industry description for 423330.",
    industries: ["wholesale-building-materials"],
    name: "Roofing, Siding, and Insulation Material Merchant Wholesalers",
    slug: "roofing-siding-and-insulation-material-merchant-wholesalers"
  },
  "42339": {
    description: "See industry description for 423390.",
    industries: ["wholesale-building-materials"],
    name: "Other Construction Material Merchant Wholesalers",
    slug: "other-construction-material-merchant-wholesalers"
  },
  "42341": {
    description: "See industry description for 423410.",
    industries: ["wholesale-photography-equipment-and-supplies"],
    name: "Photographic Equipment and Supplies Merchant Wholesalers",
    slug: "photographic-equipment-and-supplies-merchant-wholesalers"
  },
  "42342": {
    description: "See industry description for 423420.",
    industries: ["wholesale-computer-equipment"],
    name: "Office Equipment Merchant Wholesalers",
    slug: "office-equipment-merchant-wholesalers"
  },
  "42343": {
    description: "See industry description for 423430.",
    industries: ["wholesale-computer-equipment"],
    name: "Computer and Computer Peripheral Equipment and Software Merchant Wholesalers",
    slug: "computer-and-computer-peripheral-equipment-and-software-merchant-wholesalers"
  },
  "42344": {
    description: "See industry description for 423440.",
    industries: ["wholesale-machinery"],
    name: "Other Commercial Equipment Merchant Wholesalers",
    slug: "other-commercial-equipment-merchant-wholesalers"
  },
  "42345": {
    description: "See industry description for 423450.",
    industries: ["wholesale-drugs-and-sundries"],
    name: "Medical, Dental, and Hospital Equipment and Supplies Merchant Wholesalers",
    slug: "medical-dental-and-hospital-equipment-and-supplies-merchant-wholesalers"
  },
  "42346": {
    description: "See industry description for 423460.",
    industries: ["wholesale-drugs-and-sundries"],
    name: "Ophthalmic Goods Merchant Wholesalers",
    slug: "ophthalmic-goods-merchant-wholesalers"
  },
  "42349": {
    description: "See industry description for 423490.",
    industries: ["wholesale"],
    name: "Other Professional Equipment and Supplies Merchant Wholesalers",
    slug: "other-professional-equipment-and-supplies-merchant-wholesalers"
  },
  "42351": {
    description: "See industry description for 423510.",
    industries: ["wholesale-metals-and-minerals"],
    name: "Metal Service Centers and Other Metal Merchant Wholesalers",
    slug: "metal-service-centers-and-other-metal-merchant-wholesalers"
  },
  "42352": {
    description: "See industry description for 423520.",
    industries: ["wholesale-metals-and-minerals"],
    name: "Coal and Other Mineral and Ore Merchant Wholesalers",
    slug: "coal-and-other-mineral-and-ore-merchant-wholesalers"
  },
  "42361": {
    description: "See industry description for 423610.",
    industries: ["wholesale-appliances-electrical-and-electronics"],
    name: "Electrical Apparatus and Equipment, Wiring Supplies, and Related Equipment Merchant Wholesalers",
    slug: "electrical-apparatus-and-equipment-wiring-supplies-and-related-equipment-merchant-wholesalers"
  },
  "42362": {
    description: "See industry description for 423620.",
    industries: ["wholesale-appliances-electrical-and-electronics"],
    name: "Household Appliances, Electric Housewares, and Consumer Electronics Merchant Wholesalers",
    slug: "household-appliances-electric-housewares-and-consumer-electronics-merchant-wholesalers"
  },
  "42369": {
    description: "See industry description for 423690.",
    industries: ["wholesale-appliances-electrical-and-electronics"],
    name: "Other Electronic Parts and Equipment Merchant Wholesalers",
    slug: "other-electronic-parts-and-equipment-merchant-wholesalers"
  },
  "42371": {
    description: "See industry description for 423710.",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Hardware Merchant Wholesalers",
    slug: "hardware-merchant-wholesalers"
  },
  "42372": {
    description: "See industry description for 423720.",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Plumbing and Heating Equipment and Supplies (Hydronics) Merchant Wholesalers",
    slug: "plumbing-and-heating-equipment-and-supplies-hydronics-merchant-wholesalers"
  },
  "42373": {
    description: "See industry description for 423730.",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Warm Air Heating and Air-Conditioning Equipment and Supplies Merchant Wholesalers",
    slug: "warm-air-heating-and-air-conditioning-equipment-and-supplies-merchant-wholesalers"
  },
  "42374": {
    description: "See industry description for 423740.",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Refrigeration Equipment and Supplies Merchant Wholesalers",
    slug: "refrigeration-equipment-and-supplies-merchant-wholesalers"
  },
  "42381": {
    description: "See industry description for 423810.",
    industries: ["wholesale-machinery"],
    name: "Construction and Mining (except Oil Well) Machinery and Equipment Merchant Wholesalers",
    slug: "construction-and-mining-except-oil-well-machinery-and-equipment-merchant-wholesalers"
  },
  "42382": {
    description: "See industry description for 423820.",
    industries: ["wholesale-machinery"],
    name: "Farm and Garden Machinery and Equipment Merchant Wholesalers",
    slug: "farm-and-garden-machinery-and-equipment-merchant-wholesalers"
  },
  "42383": {
    description: "See industry description for 423830.",
    industries: ["wholesale-machinery"],
    name: "Industrial Machinery and Equipment Merchant Wholesalers",
    slug: "industrial-machinery-and-equipment-merchant-wholesalers"
  },
  "42384": {
    description: "See industry description for 423840.",
    industries: ["wholesale-machinery"],
    name: "Industrial Supplies Merchant Wholesalers",
    slug: "industrial-supplies-merchant-wholesalers"
  },
  "42385": {
    description: "See industry description for 423850.",
    industries: ["wholesale-machinery"],
    name: "Service Establishment Equipment and Supplies Merchant Wholesalers",
    slug: "service-establishment-equipment-and-supplies-merchant-wholesalers"
  },
  "42386": {
    description: "See industry description for 423860.",
    industries: ["wholesale-machinery"],
    name: "Transportation Equipment and Supplies (except Motor Vehicle) Merchant Wholesalers",
    slug: "transportation-equipment-and-supplies-except-motor-vehicle-merchant-wholesalers"
  },
  "42391": {
    description: "See industry description for 423910.",
    industries: ["wholesale"],
    name: "Sporting and Recreational Goods and Supplies Merchant Wholesalers",
    slug: "sporting-and-recreational-goods-and-supplies-merchant-wholesalers"
  },
  "42392": {
    description: "See industry description for 423920.",
    industries: ["wholesale"],
    name: "Toy and Hobby Goods and Supplies Merchant Wholesalers",
    slug: "toy-and-hobby-goods-and-supplies-merchant-wholesalers"
  },
  "42393": {
    description: "See industry description for 423930.",
    industries: ["wholesale-recyclable-materials"],
    name: "Recyclable Material Merchant Wholesalers",
    slug: "recyclable-material-merchant-wholesalers"
  },
  "42394": {
    description: "See industry description for 423940.",
    industries: ["wholesale-luxury-goods-and-jewelry"],
    name: "Jewelry, Watch, Precious Stone, and Precious Metal Merchant Wholesalers",
    slug: "jewelry-watch-precious-stone-and-precious-metal-merchant-wholesalers"
  },
  "42399": {
    description: "See industry description for 423990.",
    industries: ["wholesale"],
    name: "Other Miscellaneous Durable Goods Merchant Wholesalers",
    slug: "other-miscellaneous-durable-goods-merchant-wholesalers"
  },
  "42411": {
    description: "See industry description for 424110.",
    industries: ["wholesale-paper-products"],
    name: "Printing and Writing Paper Merchant Wholesalers",
    slug: "printing-and-writing-paper-merchant-wholesalers"
  },
  "42412": {
    description: "See industry description for 424120.",
    industries: ["wholesale-paper-products"],
    name: "Stationery and Office Supplies Merchant Wholesalers",
    slug: "stationery-and-office-supplies-merchant-wholesalers"
  },
  "42413": {
    description: "See industry description for 424130.",
    industries: ["wholesale-paper-products"],
    name: "Industrial and Personal Service Paper Merchant Wholesalers",
    slug: "industrial-and-personal-service-paper-merchant-wholesalers"
  },
  "42421": {
    description: "See industry description for 424210.",
    industries: ["wholesale-drugs-and-sundries"],
    name: "Drugs and Druggists' Sundries Merchant Wholesalers",
    slug: "drugs-and-druggists-sundries-merchant-wholesalers"
  },
  "42431": {
    description: "See industry description for 424310.",
    industries: ["wholesale-apparel-and-sewing-supplies"],
    name: "Piece Goods, Notions, and Other Dry Goods Merchant Wholesalers",
    slug: "piece-goods-notions-and-other-dry-goods-merchant-wholesalers"
  },
  "42434": {
    description: "See industry description for 424340.",
    industries: ["wholesale-footwear"],
    name: "Footwear Merchant Wholesalers",
    slug: "footwear-merchant-wholesalers"
  },
  "42435": {
    description: "See industry description for 424350.",
    industries: ["wholesale-apparel-and-sewing-supplies"],
    name: "Clothing and Clothing Accessories Merchant Wholesalers",
    slug: "clothing-and-clothing-accessories-merchant-wholesalers"
  },
  "42441": {
    description: "See industry description for 424410.",
    industries: ["wholesale-food-and-beverage"],
    name: "General Line Grocery Merchant Wholesalers",
    slug: "general-line-grocery-merchant-wholesalers"
  },
  "42442": {
    description: "See industry description for 424420.",
    industries: ["wholesale-food-and-beverage"],
    name: "Packaged Frozen Food Merchant Wholesalers",
    slug: "packaged-frozen-food-merchant-wholesalers"
  },
  "42443": {
    description: "See industry description for 424430.",
    industries: ["wholesale-food-and-beverage"],
    name: "Dairy Product (except Dried or Canned) Merchant Wholesalers",
    slug: "dairy-product-except-dried-or-canned-merchant-wholesalers"
  },
  "42444": {
    description: "See industry description for 424440.",
    industries: ["wholesale-food-and-beverage"],
    name: "Poultry and Poultry Product Merchant Wholesalers",
    slug: "poultry-and-poultry-product-merchant-wholesalers"
  },
  "42445": {
    description: "See industry description for 424450.",
    industries: ["wholesale-food-and-beverage"],
    name: "Confectionery Merchant Wholesalers",
    slug: "confectionery-merchant-wholesalers"
  },
  "42446": {
    description: "See industry description for 424460.",
    industries: ["wholesale-food-and-beverage"],
    name: "Fish and Seafood Merchant Wholesalers",
    slug: "fish-and-seafood-merchant-wholesalers"
  },
  "42447": {
    description: "See industry description for 424470.",
    industries: ["wholesale-food-and-beverage"],
    name: "Meat and Meat Product Merchant Wholesalers",
    slug: "meat-and-meat-product-merchant-wholesalers"
  },
  "42448": {
    description: "See industry description for 424480.",
    industries: ["wholesale-food-and-beverage"],
    name: "Fresh Fruit and Vegetable Merchant Wholesalers",
    slug: "fresh-fruit-and-vegetable-merchant-wholesalers"
  },
  "42449": {
    description: "See industry description for 424490.",
    industries: ["wholesale-food-and-beverage"],
    name: "Other Grocery and Related Products Merchant Wholesalers",
    slug: "other-grocery-and-related-products-merchant-wholesalers"
  },
  "42451": {
    description: "See industry description for 424510.",
    industries: ["wholesale-raw-farm-products"],
    name: "Grain and Field Bean Merchant Wholesalers",
    slug: "grain-and-field-bean-merchant-wholesalers"
  },
  "42452": {
    description: "See industry description for 424520.",
    industries: ["wholesale-raw-farm-products"],
    name: "Livestock Merchant Wholesalers",
    slug: "livestock-merchant-wholesalers"
  },
  "42459": {
    description: "See industry description for 424590.",
    industries: ["wholesale-raw-farm-products"],
    name: "Other Farm Product Raw Material Merchant Wholesalers",
    slug: "other-farm-product-raw-material-merchant-wholesalers"
  },
  "42461": {
    description: "See industry description for 424610.",
    industries: ["wholesale-chemical-and-allied-products"],
    name: "Plastics Materials and Basic Forms and Shapes Merchant Wholesalers",
    slug: "plastics-materials-and-basic-forms-and-shapes-merchant-wholesalers"
  },
  "42469": {
    description: "See industry description for 424690.",
    industries: ["wholesale-chemical-and-allied-products"],
    name: "Other Chemical and Allied Products Merchant Wholesalers",
    slug: "other-chemical-and-allied-products-merchant-wholesalers"
  },
  "42471": {
    description: "See industry description for 424710.",
    industries: ["wholesale-petroleum-and-petroleum-products"],
    name: "Petroleum Bulk Stations and Terminals",
    slug: "petroleum-bulk-stations-and-terminals"
  },
  "42472": {
    description: "See industry description for 424720.",
    industries: ["wholesale-petroleum-and-petroleum-products"],
    name: "Petroleum and Petroleum Products Merchant Wholesalers (except Bulk Stations and Terminals)",
    slug: "petroleum-and-petroleum-products-merchant-wholesalers-except-bulk-stations-and-terminals"
  },
  "42481": {
    description: "See industry description for 424810.",
    industries: ["wholesale-alcoholic-beverages"],
    name: "Beer and Ale Merchant Wholesalers",
    slug: "beer-and-ale-merchant-wholesalers"
  },
  "42482": {
    description: "See industry description for 424820.",
    industries: ["wholesale-alcoholic-beverages"],
    name: "Wine and Distilled Alcoholic Beverage Merchant Wholesalers",
    slug: "wine-and-distilled-alcoholic-beverage-merchant-wholesalers"
  },
  "42491": {
    description: "See industry description for 424910.",
    industries: ["wholesale-raw-farm-products"],
    name: "Farm Supplies Merchant Wholesalers",
    slug: "farm-supplies-merchant-wholesalers"
  },
  "42492": {
    description: "See industry description for 424920.",
    industries: ["wholesale"],
    name: "Book, Periodical, and Newspaper Merchant Wholesalers",
    slug: "book-periodical-and-newspaper-merchant-wholesalers"
  },
  "42493": {
    description: "See industry description for 424930.",
    industries: ["wholesale"],
    name: "Flower, Nursery Stock, and Florists' Supplies Merchant Wholesalers",
    slug: "flower-nursery-stock-and-florists-supplies-merchant-wholesalers"
  },
  "42494": {
    description: "See industry description for 424940.",
    industries: ["wholesale"],
    name: "Tobacco Product and Electronic Cigarette Merchant Wholesalers",
    slug: "tobacco-product-and-electronic-cigarette-merchant-wholesalers"
  },
  "42495": {
    description: "See industry description for 424950.",
    industries: ["wholesale-chemical-and-allied-products"],
    name: "Paint, Varnish, and Supplies Merchant Wholesalers",
    slug: "paint-varnish-and-supplies-merchant-wholesalers"
  },
  "42499": {
    description: "See industry description for 424990.",
    industries: ["wholesale"],
    name: "Other Miscellaneous Nondurable Goods Merchant Wholesalers",
    slug: "other-miscellaneous-nondurable-goods-merchant-wholesalers"
  },
  "42512": {
    description: "See industry description for 425120.",
    industries: ["internet-marketplace-platforms"],
    name: "Wholesale Trade Agents and Brokers",
    slug: "wholesale-trade-agents-and-brokers"
  },
  "44111": {
    description: "See industry description for 441110.",
    industries: ["retail-motor-vehicles"],
    name: "New Car Dealers",
    slug: "new-car-dealers"
  },
  "44112": {
    description: "See industry description for 441120.",
    industries: ["retail-motor-vehicles"],
    name: "Used Car Dealers",
    slug: "used-car-dealers"
  },
  "44121": {
    description: "See industry description for 441210.",
    industries: ["retail"],
    name: "Recreational Vehicle Dealers",
    slug: "recreational-vehicle-dealers"
  },
  "44122": {
    description: "This industry comprises establishments primarily engaged in retailing new and used motorcycles, boats, and other vehicles (except automobiles, light trucks, and recreational vehicles), or retailing these new vehicles in combination with activities, such as repair services and selling replacement parts and accessories. Illustrative Examples: Aircraft dealers, Motorcycle dealers, All-terrain vehicle (ATV) dealers, Utility trailer dealers, Boat dealers, new and used.",
    industries: ["retail"],
    name: "Motorcycle, Boat, and Other Motor Vehicle Dealers",
    slug: "motorcycle-boat-and-other-motor-vehicle-dealers"
  },
  "44133": {
    description: "See industry description for 441330.",
    industries: ["retail-motor-vehicles"],
    name: "Automotive Parts and Accessories Retailers",
    slug: "automotive-parts-and-accessories-retailers"
  },
  "44134": {
    description: "See industry description for 441340.",
    industries: ["retail-motor-vehicles"],
    name: "Tire Dealers",
    slug: "tire-dealers"
  },
  "44411": {
    description: "See industry description for 444110.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Home Centers",
    slug: "home-centers"
  },
  "44412": {
    description: "See industry description for 444120.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Paint and Wallpaper Retailers",
    slug: "paint-and-wallpaper-retailers"
  },
  "44414": {
    description: "See industry description for 444140.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Hardware Retailers",
    slug: "hardware-retailers"
  },
  "44418": {
    description: "See industry description for 444180.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Other Building Material Dealers",
    slug: "other-building-material-dealers"
  },
  "44423": {
    description: "See industry description for 444230.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Outdoor Power Equipment Retailers",
    slug: "outdoor-power-equipment-retailers"
  },
  "44424": {
    description: "See industry description for 444240.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Nursery, Garden Center, and Farm Supply Retailers",
    slug: "nursery-garden-center-and-farm-supply-retailers"
  },
  "44511": {
    description: "See industry description for 445110.",
    industries: ["retail-groceries"],
    name: "Supermarkets and Other Grocery Retailers (except Convenience Retailers)",
    slug: "supermarkets-and-other-grocery-retailers-except-convenience-retailers"
  },
  "44513": {
    description: "This industry comprises establishments primarily engaged in retailing a limited line of groceries that generally includes milk, bread, soda, and snacks.  Included in this industry are convenience retailers, such as convenience stores or food marts (except those operating fuel pumps), and vending machine operators.",
    industries: ["food-and-beverage-retail"],
    name: "Convenience Retailers and Vending Machine Operators",
    slug: "convenience-retailers-and-vending-machine-operators"
  },
  "44523": {
    description: "See industry description for 445230.",
    industries: ["food-and-beverage-retail"],
    name: "Fruit and Vegetable Retailers",
    slug: "fruit-and-vegetable-retailers"
  },
  "44524": {
    description: "See industry description for 445240.",
    industries: ["food-and-beverage-retail"],
    name: "Meat Retailers",
    slug: "meat-retailers"
  },
  "44525": {
    description: "See industry description for 445250.",
    industries: ["food-and-beverage-retail"],
    name: "Fish and Seafood Retailers",
    slug: "fish-and-seafood-retailers"
  },
  "44529": {
    description: "This industry comprises establishments primarily engaged in retailing specialty foods (except fruits and vegetables, meat, fish, and seafood) not for immediate consumption and not made on the premises. Illustrative Examples: Baked goods (not made on premises) retailers , Dairy product retailers, Coffee and tea (i.e., packaged) retailers, Gourmet food retailers, Confectionery (i.e., packaged) retailers, Nut (i.e., packaged) retailers.",
    industries: ["food-and-beverage-retail"],
    name: "Other Specialty Food Retailers",
    slug: "other-specialty-food-retailers"
  },
  "44532": {
    description: "See industry description for 445320.",
    industries: ["food-and-beverage-retail"],
    name: "Beer, Wine, and Liquor Retailers",
    slug: "beer-wine-and-liquor-retailers"
  },
  "44911": {
    description: "See industry description for 449110.",
    industries: ["retail-furniture-and-home-furnishings"],
    name: "Furniture Retailers",
    slug: "furniture-retailers"
  },
  "44912": {
    description: "This industry comprises establishments primarily engaged in retailing new home furnishings (except furniture). Illustrative Examples: Bath shops, Chinaware retailers, Floor covering retailers (except hardwood or ceramic tile only), Glassware retailers, Kitchenware retailers, Window treatment retailers.",
    industries: ["retail-furniture-and-home-furnishings"],
    name: "Home Furnishings Retailers",
    slug: "home-furnishings-retailers"
  },
  "44921": {
    description: "See industry description for 449210.",
    industries: ["retail-appliances-electrical-and-electronic-equipment"],
    name: "Electronics and Appliance Retailers",
    slug: "electronics-and-appliance-retailers"
  },
  "45511": {
    description: "See industry description for 455110.",
    industries: ["retail"],
    name: "Department Stores",
    slug: "department-stores"
  },
  "45521": {
    description: "This industry comprises establishments primarily engaged in retailing new general merchandise (except department stores).  These establishments retail a general line of new merchandise, such as apparel, automotive parts, dry goods, hardware, groceries, housewares, and home furnishings, with no one merchandise line predominating.  Establishments generally known as warehouse clubs, superstores, or supercenters, and retailing a general line of merchandise in combination with a significant amount and variety of perishable groceries, are included in this industry.  This industry also includes establishments primarily engaged in retailing a general line of new and used merchandise on an auction basis. Illustrative Examples: Dollar stores, General merchandise (new and used) auction houses, General merchandise showrooms, General merchandise trading posts, General stores, Home and auto supply stores, Superstores (i.e., food and general merchandise), Variety stores, Warehouse clubs (i.e., food and general merchandise).",
    industries: ["retail"],
    name: "Warehouse Clubs, Supercenters, and Other General Merchandise Retailers",
    slug: "warehouse-clubs-supercenters-and-other-general-merchandise-retailers"
  },
  "45611": {
    description: "See industry description for 456110.",
    industries: ["retail-pharmacies"],
    name: "Pharmacies and Drug Retailers",
    slug: "pharmacies-and-drug-retailers"
  },
  "45612": {
    description: "See industry description for 456120.",
    industries: ["retail-health-and-personal-care-products"],
    name: "Cosmetics, Beauty Supplies, and Perfume Retailers",
    slug: "cosmetics-beauty-supplies-and-perfume-retailers"
  },
  "45613": {
    description: "See industry description for 456130.",
    industries: ["retail-health-and-personal-care-products"],
    name: "Optical Goods Retailers",
    slug: "optical-goods-retailers"
  },
  "45619": {
    description: "This industry comprises establishments primarily engaged in retailing health and personal care items (except drugs, medicines, cosmetics, beauty supplies, perfumes, and optical goods). Illustrative Examples: Convalescent supply retailers, Vitamin retailers, Sick room supply retailers, Hearing aid retailers, Wheelchair retailers .",
    industries: ["retail-health-and-personal-care-products"],
    name: "Other Health and Personal Care Retailers",
    slug: "other-health-and-personal-care-retailers"
  },
  "45711": {
    description: "See industry description for 457110.",
    industries: ["retail-gasoline"],
    name: "Gasoline Stations with Convenience Stores",
    slug: "gasoline-stations-with-convenience-stores"
  },
  "45712": {
    description: "See industry description for 457120.",
    industries: ["retail-gasoline"],
    name: "Other Gasoline Stations",
    slug: "other-gasoline-stations"
  },
  "45721": {
    description: "See industry description for 457210.",
    industries: ["retail"],
    name: "Fuel Dealers",
    slug: "fuel-dealers"
  },
  "45811": {
    description: "See industry description for 458110.",
    industries: ["retail-apparel-and-fashion"],
    name: "Clothing and Clothing Accessories Retailers",
    slug: "clothing-and-clothing-accessories-retailers"
  },
  "45821": {
    description: "See industry description for 458210.",
    industries: ["retail-apparel-and-fashion"],
    name: "Shoe Retailers",
    slug: "shoe-retailers"
  },
  "45831": {
    description: "See industry description for 458310.",
    industries: ["retail-luxury-goods-and-jewelry"],
    name: "Jewelry Retailers",
    slug: "jewelry-retailers"
  },
  "45832": {
    description: "See industry description for 458320.",
    industries: ["retail-luxury-goods-and-jewelry"],
    name: "Luggage and Leather Goods Retailers",
    slug: "luggage-and-leather-goods-retailers"
  },
  "45911": {
    description: "See industry description for 459110.",
    industries: ["retail"],
    name: "Sporting Goods Retailers",
    slug: "sporting-goods-retailers"
  },
  "45912": {
    description: "See industry description for 459120.",
    industries: ["retail"],
    name: "Hobby, Toy, and Game Retailers",
    slug: "hobby-toy-and-game-retailers"
  },
  "45913": {
    description: "See industry description for 459130.",
    industries: ["retail"],
    name: "Sewing, Needlework, and Piece Goods Retailers",
    slug: "sewing-needlework-and-piece-goods-retailers"
  },
  "45914": {
    description: "See industry description for 459140.",
    industries: ["retail-musical-instruments"],
    name: "Musical Instrument and Supplies Retailers",
    slug: "musical-instrument-and-supplies-retailers"
  },
  "45921": {
    description: "See industry description for 459210.",
    industries: ["retail-books-and-printed-news"],
    name: "Book Retailers and News Dealers",
    slug: "book-retailers-and-news-dealers"
  },
  "45931": {
    description: "See industry description for 459310.",
    industries: ["retail-florists"],
    name: "Florists",
    slug: "florists"
  },
  "45941": {
    description: "See industry description for 459410.",
    industries: ["retail-office-equipment"],
    name: "Office Supplies and Stationery Retailers",
    slug: "office-supplies-and-stationery-retailers"
  },
  "45942": {
    description: "See industry description for 459420.",
    industries: ["retail-office-supplies-and-gifts"],
    name: "Gift, Novelty, and Souvenir Retailers",
    slug: "gift-novelty-and-souvenir-retailers"
  },
  "45951": {
    description: "See industry description for 459510.",
    industries: ["retail-recyclable-materials-and-used-merchandise"],
    name: "Used Merchandise Retailers",
    slug: "used-merchandise-retailers"
  },
  "45991": {
    description: "See industry description for 459910.",
    industries: ["retail"],
    name: "Pet and Pet Supplies Retailers",
    slug: "pet-and-pet-supplies-retailers"
  },
  "45992": {
    description: "See industry description for 459920.",
    industries: ["retail-art-dealers"],
    name: "Art Dealers",
    slug: "art-dealers"
  },
  "45993": {
    description: "See industry description for 459930.",
    industries: ["retail"],
    name: "Manufactured (Mobile) Home Dealers",
    slug: "manufactured-mobile-home-dealers"
  },
  "45999": {
    description: "This industry comprises establishments primarily engaged in retailing miscellaneous specialized lines of merchandise (except motor vehicle and parts dealers; building material and garden equipment and supplies dealers; food and beverage retailers; furniture, home furnishings, electronics, and appliance retailers; general merchandise retailers; health and personal care retailers; gasoline stations and fuel dealers; clothing, clothing accessories, shoe, and jewelry retailers; sporting goods, hobby, and musical instrument retailers; book retailers and news dealers; florists; office supplies, stationery, and gift retailers; used merchandise retailers; pet and pet supplies retailers; art dealers; and manufactured (mobile) home dealers). Illustrative Examples: Art supply retailers, Swimming pool (above-ground) and supply retailers, Cemetery memorial (e.g., headstones, markers, vaults) dealers, Home security equipment retailers, Tobacco retailers, Cigar retailers, E-cigarette retailers, Vape shops.",
    industries: ["retail"],
    name: "All Other Miscellaneous Retailers",
    slug: "all-other-miscellaneous-retailers"
  },
  "48111": {
    description: "This industry comprises establishments primarily engaged in providing air transportation of passengers and/or cargo over regular routes and on regular schedules.  Establishments in this industry operate flights even if partially loaded.  Establishments primarily engaged in providing scheduled air transportation of mail on a contract basis are included in this industry. Illustrative Examples: Air commuter carriers, scheduled, Scheduled air passenger carriers, Scheduled air cargo carriers (except air couriers), Scheduled helicopter passenger carriers .",
    industries: ["airlines-and-aviation"],
    name: "Scheduled Air Transportation",
    slug: "scheduled-air-transportation"
  },
  "48121": {
    description: "This industry comprises establishments primarily engaged in (1) providing air transportation of passengers and/or cargo with no regular routes and regular schedules or (2) providing specialty flying services with no regular routes and regular schedules using general purpose aircraft.  These establishments have more flexibility with respect to choice of airports, hours of operation, load factors, and similar operational characteristics. Illustrative Examples: Air taxi services, Nonscheduled air freight transportation services, Aircraft charter services, Nonscheduled air passenger transportation services.",
    industries: ["airlines-and-aviation"],
    name: "Nonscheduled Air Transportation",
    slug: "nonscheduled-air-transportation"
  },
  "48211": {
    description: "This industry comprises establishments primarily engaged in operating railroads (except street railroads, commuter rail, urban rapid transit, and scenic and sightseeing trains).  Line-haul railroads and short-line railroads are included in this industry.",
    industries: ["rail-transportation"],
    name: "Rail Transportation",
    slug: "rail-transportation"
  },
  "48311": {
    description: "This industry comprises establishments primarily engaged in providing deep sea, coastal, Great Lakes, and St. Lawrence Seaway water transportation.  Marine transportation establishments using the facilities of the St. Lawrence Seaway Authority Commission are considered to be using the Great Lakes Water Transportation System.",
    industries: ["maritime-transportation"],
    name: "Deep Sea, Coastal, and Great Lakes Water Transportation",
    slug: "deep-sea-coastal-and-great-lakes-water-transportation"
  },
  "48321": {
    description: "This industry comprises establishments primarily engaged in providing inland water transportation of passengers and/or cargo on lakes, rivers, or intracoastal waterways (except on the Great Lakes System).",
    industries: ["maritime-transportation"],
    name: "Inland Water Transportation",
    slug: "inland-water-transportation"
  },
  "48411": {
    description: "See industry description for 484110.",
    industries: ["truck-transportation"],
    name: "General Freight Trucking, Local",
    slug: "general-freight-trucking-local"
  },
  "48412": {
    description: "This industry comprises establishments primarily engaged in providing long-distance general freight trucking.  General freight trucking establishments handle a wide variety of commodities, generally palletized and transported in a container or van trailer.  Long-distance general freight trucking establishments usually provide trucking between metropolitan areas which may cross North American country borders.  Included in this industry are establishments operating as truckload (TL) or less than truckload (LTL) carriers.",
    industries: ["truck-transportation"],
    name: "General Freight Trucking, Long-Distance",
    slug: "general-freight-trucking-long-distance"
  },
  "48421": {
    description: "See industry description for 484210.",
    industries: ["truck-transportation"],
    name: "Used Household and Office Goods Moving",
    slug: "used-household-and-office-goods-moving"
  },
  "48422": {
    description: "See industry description for 484220.",
    industries: ["truck-transportation"],
    name: "Specialized Freight (except Used Goods) Trucking, Local",
    slug: "specialized-freight-except-used-goods-trucking-local"
  },
  "48423": {
    description: "See industry description for 484230.",
    industries: ["truck-transportation"],
    name: "Specialized Freight (except Used Goods) Trucking, Long-Distance",
    slug: "specialized-freight-except-used-goods-trucking-long-distance"
  },
  "48511": {
    description: "This industry comprises establishments primarily engaged in operating local and suburban passenger transit systems over regular routes and on regular schedules within a metropolitan area and its adjacent nonurban areas.  Such transportation systems involve the use of one or more modes of transport including light rail, commuter rail, subways, and streetcars, as well as buses and other motor vehicles.",
    industries: ["urban-transit-services"],
    name: "Urban Transit Systems",
    slug: "urban-transit-systems"
  },
  "48521": {
    description: "See industry description for 485210.",
    industries: ["interurban-and-rural-bus-services"],
    name: "Interurban and Rural Bus Transportation",
    slug: "interurban-and-rural-bus-transportation"
  },
  "48531": {
    description: "See industry description for 485310.",
    industries: ["taxi-and-limousine-services"],
    name: "Taxi and Ridesharing Services",
    slug: "taxi-and-ridesharing-services"
  },
  "48532": {
    description: "See industry description for 485320.",
    industries: ["taxi-and-limousine-services"],
    name: "Limousine Service",
    slug: "limousine-service"
  },
  "48541": {
    description: "See industry description for 485410.",
    industries: ["school-and-employee-bus-services"],
    name: "School and Employee Bus Transportation",
    slug: "school-and-employee-bus-transportation"
  },
  "48551": {
    description: "See industry description for 485510.",
    industries: ["ground-passenger-transportation"],
    name: "Charter Bus Industry",
    slug: "charter-bus-industry"
  },
  "48599": {
    description: "This industry comprises establishments primarily engaged in providing other transit and ground passenger transportation (except urban transit systems, interurban and rural bus transportation, taxi and ridesharing services, school and employee bus transportation, charter bus services, and limousine services (except shuttle services)).  Shuttle services (except school and employee bus) and special needs transportation services are included in this industry.  Shuttle services establishments generally travel within a metropolitan area and its adjacent nonurban areas on regular routes, on regular schedules and provide services between hotels, airports, or other destination points.  Establishments in the Special Needs Transportation industry provide passenger transportation for the disabled or elderly.  These establishments may use specially equipped vehicles to provide passenger transportation.",
    industries: ["shuttles-and-special-needs-transportation-services"],
    name: "Other Transit and Ground Passenger Transportation",
    slug: "other-transit-and-ground-passenger-transportation"
  },
  "48611": {
    description: "See industry description for 486110.",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation of Crude Oil",
    slug: "pipeline-transportation-of-crude-oil"
  },
  "48621": {
    description: "See industry description for 486210.",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation of Natural Gas",
    slug: "pipeline-transportation-of-natural-gas"
  },
  "48691": {
    description: "See industry description for 486910.",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation of Refined Petroleum Products",
    slug: "pipeline-transportation-of-refined-petroleum-products"
  },
  "48699": {
    description: "See industry description for 486990.",
    industries: ["pipeline-transportation"],
    name: "All Other Pipeline Transportation",
    slug: "all-other-pipeline-transportation"
  },
  "48711": {
    description: "See industry description for 487110.",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Land",
    slug: "scenic-and-sightseeing-transportation-land"
  },
  "48721": {
    description: "See industry description for 487210.",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Water",
    slug: "scenic-and-sightseeing-transportation-water"
  },
  "48799": {
    description: "See industry description for 487990.",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Other",
    slug: "scenic-and-sightseeing-transportation-other"
  },
  "48811": {
    description: "This industry comprises establishments primarily engaged in (1) operating international, national, or civil airports or public flying fields or (2) supporting airport operations (except special food services contractors), such as rental of hangar space, air traffic control (except military) services, baggage handling services, and cargo handling services. Cross-References.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Airport Operations",
    slug: "airport-operations"
  },
  "48819": {
    description: "See industry description for 488190.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Support Activities for Air Transportation",
    slug: "other-support-activities-for-air-transportation"
  },
  "48821": {
    description: "See industry description for 488210.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Support Activities for Rail Transportation",
    slug: "support-activities-for-rail-transportation"
  },
  "48831": {
    description: "See industry description for 488310.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Port and Harbor Operations",
    slug: "port-and-harbor-operations"
  },
  "48832": {
    description: "See industry description for 488320.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Marine Cargo Handling",
    slug: "marine-cargo-handling"
  },
  "48833": {
    description: "See industry description for 488330.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Navigational Services to Shipping",
    slug: "navigational-services-to-shipping"
  },
  "48839": {
    description: "See industry description for 488390.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Support Activities for Water Transportation",
    slug: "other-support-activities-for-water-transportation"
  },
  "48841": {
    description: "See industry description for 488410.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Motor Vehicle Towing",
    slug: "motor-vehicle-towing"
  },
  "48849": {
    description: "See industry description for 488490.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Support Activities for Road Transportation",
    slug: "other-support-activities-for-road-transportation"
  },
  "48851": {
    description: "See industry description for 488510.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Freight Transportation Arrangement",
    slug: "freight-transportation-arrangement"
  },
  "48899": {
    description: "This industry comprises establishments primarily engaged in providing support activities to transportation (except for air transportation; rail transportation; water transportation; road transportation; and freight transportation arrangement). Illustrative Examples: Arrangement of vanpools or car pools (except ridesharing arrangement services), Independent pipeline terminal facilities , Stockyards (i.e., not for fattening or selling livestock).",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Support Activities for Transportation",
    slug: "other-support-activities-for-transportation"
  },
  "49111": {
    description: "See industry description for 491110.",
    industries: ["postal-services"],
    name: "Postal Service",
    slug: "postal-service"
  },
  "49211": {
    description: "See industry description for 492110.",
    industries: ["freight-and-package-transportation"],
    name: "Couriers and Express Delivery Services",
    slug: "couriers-and-express-delivery-services"
  },
  "49221": {
    description: "See industry description for 492210.",
    industries: ["freight-and-package-transportation"],
    name: "Local Messengers and Local Delivery",
    slug: "local-messengers-and-local-delivery"
  },
  "49311": {
    description: "See industry description for 493110.",
    industries: ["warehousing-and-storage"],
    name: "General Warehousing and Storage",
    slug: "general-warehousing-and-storage"
  },
  "49312": {
    description: "See industry description for 493120.",
    industries: ["warehousing-and-storage"],
    name: "Refrigerated Warehousing and Storage",
    slug: "refrigerated-warehousing-and-storage"
  },
  "49313": {
    description: "See industry description for 493130.",
    industries: ["warehousing-and-storage"],
    name: "Farm Product Warehousing and Storage",
    slug: "farm-product-warehousing-and-storage"
  },
  "49319": {
    description: "See industry description for 493190.",
    industries: ["warehousing-and-storage"],
    name: "Other Warehousing and Storage",
    slug: "other-warehousing-and-storage"
  },
  "51211": {
    description: "See industry description for 512110.",
    industries: ["media-production"],
    name: "Motion Picture and Video Production",
    slug: "motion-picture-and-video-production"
  },
  "51212": {
    description: "See industry description for 512120.",
    industries: ["movies-and-sound-recording"],
    name: "Motion Picture and Video Distribution",
    slug: "motion-picture-and-video-distribution"
  },
  "51213": {
    description: "This industry comprises establishments primarily engaged in operating motion picture theaters and/or exhibiting motion pictures or videos at film festivals, and so forth.",
    industries: ["movies-and-sound-recording"],
    name: "Motion Picture and Video Exhibition",
    slug: "motion-picture-and-video-exhibition"
  },
  "51219": {
    description: "This industry comprises establishments primarily engaged in providing postproduction services and other services to the motion picture industry, including specialized motion picture or video postproduction services, such as editing, film/tape transfers, titling, subtitling, credits, closed captioning, and computer-produced graphics, animation and special effects, as well as developing and processing motion picture film. Illustrative Examples: Motion picture film laboratories, Stock footage film libraries, Postproduction facilities, Teleproduction services .",
    industries: ["animation-and-post-production"],
    name: "Postproduction Services and Other Motion Picture and Video Industries",
    slug: "postproduction-services-and-other-motion-picture-and-video-industries"
  },
  "51223": {
    description: "See industry description for 512230.",
    industries: ["sheet-music-publishing"],
    name: "Music Publishers",
    slug: "music-publishers"
  },
  "51224": {
    description: "See industry description for 512240.",
    industries: ["sound-recording"],
    name: "Sound Recording Studios",
    slug: "sound-recording-studios"
  },
  "51225": {
    description: "See industry description for 512250.",
    industries: ["sound-recording"],
    name: "Record Production and Distribution",
    slug: "record-production-and-distribution"
  },
  "51229": {
    description: "See industry description for 512290.",
    industries: ["sound-recording"],
    name: "Other Sound Recording Industries",
    slug: "other-sound-recording-industries"
  },
  "51311": {
    description: "See industry description for 513110.",
    industries: ["newspaper-publishing"],
    name: "Newspaper Publishers",
    slug: "newspaper-publishers"
  },
  "51312": {
    description: "See industry description for 513120.",
    industries: ["periodical-publishing"],
    name: "Periodical Publishers",
    slug: "periodical-publishers"
  },
  "51313": {
    description: "See industry description for 513130.",
    industries: ["book-publishing"],
    name: "Book Publishers",
    slug: "book-publishers"
  },
  "51314": {
    description: "See industry description for 513140.",
    industries: ["book-and-periodical-publishing"],
    name: "Directory and Mailing List Publishers",
    slug: "directory-and-mailing-list-publishers"
  },
  "51319": {
    description: "This industry comprises establishments known as publishers (except newspaper, magazine, book, directory, mailing list, music, and software publishers).  These establishments may publish works in print or electronic form, including exclusively on the Internet. Illustrative Examples: Art publishers, Greeting card publishers, Calendar publishers, Cross-References.",
    industries: ["book-and-periodical-publishing"],
    name: "Other Publishers",
    slug: "other-publishers"
  },
  "51321": {
    description: "See industry description for 513210.",
    industries: ["software-development"],
    name: "Software Publishers",
    slug: "software-publishers"
  },
  "51611": {
    description: "See industry description for 516110.",
    industries: ["broadcast-media-production-and-distribution"],
    name: "Radio Broadcasting Stations",
    slug: "radio-broadcasting-stations"
  },
  "51612": {
    description: "See industry description for 516120.",
    industries: ["radio-and-television-broadcasting"],
    name: "Television Broadcasting Stations",
    slug: "television-broadcasting-stations"
  },
  "51621": {
    description: "See industry description for 516210.",
    industries: ["cable-and-satellite-programming"],
    name: "Media Streaming Distribution Services, Social Networks, and Other Media Networks and Content Providers",
    slug: "media-streaming-distribution-services-social-networks-and-other-media-networks-and-content-providers"
  },
  "51711": {
    description: "This industry comprises establishments primarily engaged in operating, maintaining, and/or providing access to switching and transmission facilities and infrastructure that they own and/or lease for the transmission of voice, data, text, sound, and video using wired and wireless telecommunications networks (except satellite).  Transmission facilities may be based on a single technology or a combination of technologies.  By exception, establishments providing satellite television distribution services using facilities and infrastructure that they operate are included in this industry. Illustrative Examples: Broadband Internet service providers, wired (e.g., cable, DSL), Cable television distribution services, Cellular telephone service carriers, Direct-to-home satellite system (DTH) services, Satellite television distribution systems, Telecommunications carriers, wired and wireless, VoIP service providers, using own operated wired telecommunications infrastructure, Wireless Internet service providers, except satellite, Wireless telecommunications carriers, except satellite, Wireless telephone communications carriers, except satellite .",
    industries: ["telecommunications-carriers"],
    name: "Wired and Wireless Telecommunications Carriers (except Satellite)",
    slug: "wired-and-wireless-telecommunications-carriers-except-satellite"
  },
  "51712": {
    description: "This industry comprises establishments primarily engaged in (1) purchasing access and network capacity from owners and operators of telecommunications networks and reselling wired and wireless telecommunications services to businesses and households (except satellite telecommunications) or (2) acting as agents for wireless telecommunications carriers and resellers, selling wireless plans on a commission basis.  Establishments in this industry do not operate as telecommunications carriers.  Mobile virtual network operators (MVNOs) are included in this industry. Illustrative Examples: Agents for wireless telecommunications carriers, Cellular telephone stores, selling cellular phone service plans on an agent basis, Mobile phone stores, selling mobile phone service plans on an agent basis, Wired telecommunications resellers, Wireless phone service plan sales agents, selling on behalf of wireless telecommunications carriers, Wireless telecommunications resellers (except satellite telecommunications) .",
    industries: ["telecommunications"],
    name: "Telecommunications Resellers and Agents for Wireless Telecommunication Services",
    slug: "telecommunications-resellers-and-agents-for-wireless-telecommunication-services"
  },
  "51741": {
    description: "See industry description for 517410.",
    industries: ["satellite-telecommunications"],
    name: "Satellite Telecommunications",
    slug: "satellite-telecommunications"
  },
  "51781": {
    description: "See industry description for 517810.",
    industries: ["telecommunications"],
    name: "All Other Telecommunications",
    slug: "all-other-telecommunications"
  },
  "51821": {
    description: "See industry description for 518210.",
    industries: ["data-infrastructure-and-analytics"],
    name: "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
    slug: "computing-infrastructure-providers-data-processing-web-hosting-and-related-services"
  },
  "51921": {
    description: "See industry description for 519210.",
    industries: ["libraries"],
    name: "Libraries and Archives",
    slug: "libraries-and-archives"
  },
  "51929": {
    description: "See industry description for 519290.",
    industries: ["information-services"],
    name: "Web Search Portals and All Other Information Services",
    slug: "web-search-portals-and-all-other-information-services"
  },
  "52111": {
    description: "See industry description for 521110.",
    industries: ["financial-services"],
    name: "Monetary Authorities-Central Bank",
    slug: "monetary-authorities-central-bank"
  },
  "52211": {
    description: "See industry description for 522110.",
    industries: ["banking"],
    name: "Commercial Banking",
    slug: "commercial-banking"
  },
  "52213": {
    description: "See industry description for 522130.",
    industries: ["credit-intermediation"],
    name: "Credit Unions",
    slug: "credit-unions"
  },
  "52218": {
    description: "See industry description for 522180.",
    industries: ["savings-institutions"],
    name: "Savings Institutions and Other Depository Credit Intermediation",
    slug: "savings-institutions-and-other-depository-credit-intermediation"
  },
  "52221": {
    description: "See industry description for 522210.",
    industries: ["credit-intermediation"],
    name: "Credit Card Issuing",
    slug: "credit-card-issuing"
  },
  "52222": {
    description: "See industry description for 522220.",
    industries: ["credit-intermediation"],
    name: "Sales Financing",
    slug: "sales-financing"
  },
  "52229": {
    description: "This industry comprises establishments primarily engaged in making cash loans or extending credit through credit instruments (except credit cards and sales finance agreements). Illustrative Examples: Consumer finance companies (i.e., unsecured cash loans), Mortgage companies , International trade financing, Secondary market financing.",
    industries: ["credit-intermediation"],
    name: "Other Nondepository Credit Intermediation",
    slug: "other-nondepository-credit-intermediation"
  },
  "52231": {
    description: "See industry description for 522310.",
    industries: ["loan-brokers"],
    name: "Mortgage and Nonmortgage Loan Brokers",
    slug: "mortgage-and-nonmortgage-loan-brokers"
  },
  "52232": {
    description: "See industry description for 522320.",
    industries: ["credit-intermediation"],
    name: "Financial Transactions Processing, Reserve, and Clearinghouse Activities",
    slug: "financial-transactions-processing-reserve-and-clearinghouse-activities"
  },
  "52239": {
    description: "See industry description for 522390.",
    industries: ["credit-intermediation"],
    name: "Other Activities Related to Credit Intermediation",
    slug: "other-activities-related-to-credit-intermediation"
  },
  "52315": {
    description: "See industry description for 523150.",
    industries: ["investment-banking"],
    name: "Investment Banking and Securities Intermediation",
    slug: "investment-banking-and-securities-intermediation"
  },
  "52316": {
    description: "See industry description for 523160.",
    industries: ["capital-markets"],
    name: "Commodity Contracts Intermediation",
    slug: "commodity-contracts-intermediation"
  },
  "52321": {
    description: "See industry description for 523210.",
    industries: ["securities-and-commodity-exchanges"],
    name: "Securities and Commodity Exchanges",
    slug: "securities-and-commodity-exchanges"
  },
  "52391": {
    description: "See industry description for 523910.",
    industries: ["venture-capital-and-private-equity-principals"],
    name: "Miscellaneous Intermediation",
    slug: "miscellaneous-intermediation"
  },
  "52394": {
    description: "See industry description for 523940.",
    industries: ["investment-advice"],
    name: "Portfolio Management and Investment Advice",
    slug: "portfolio-management-and-investment-advice"
  },
  "52399": {
    description: "This industry comprises establishments primarily engaged in acting as agents or brokers (except securities brokerages and commodity contracts brokerages) in buying and selling financial contracts and those providing financial investment services (except securities and commodity exchanges, portfolio management, and investment advice). Illustrative Examples: Bank trust offices, Fiduciary agencies (except real estate), Exchange clearinghouses, commodities or securities, Escrow agencies (except real estate), Stock quotation services, Cross-References.",
    industries: ["investment-management"],
    name: "All Other Financial Investment Activities",
    slug: "all-other-financial-investment-activities"
  },
  "52411": {
    description: "This industry comprises establishments primarily engaged in initially underwriting (i.e., assuming the risk and assigning premiums) annuities and life insurance policies, disability income insurance policies, accidental death and dismemberment insurance policies, and health and medical insurance policies. Cross-References.",
    industries: ["insurance-carriers"],
    name: "Direct Life, Health, and Medical Insurance Carriers",
    slug: "direct-life-health-and-medical-insurance-carriers"
  },
  "52412": {
    description: "This industry comprises establishments primarily engaged in initially underwriting (i.e., assuming the risk and assigning premiums) various types of insurance policies (except life, disability income, accidental death and dismemberment, and health and medical insurance policies). Illustrative Examples: Automobile insurance carriers, direct, Property and casualty insurance carriers, direct, Bank deposit insurance carriers, direct, Title insurance carriers, real estate, direct, Mortgage guaranty insurance carriers, direct, Warranty insurance carriers (e.g., appliance, automobile, homeowners', product), direct, Cross-References.",
    industries: ["insurance-carriers"],
    name: "Direct Insurance (except Life, Health, and Medical) Carriers",
    slug: "direct-insurance-except-life-health-and-medical-carriers"
  },
  "52413": {
    description: "See industry description for 524130.",
    industries: ["insurance-carriers"],
    name: "Reinsurance Carriers",
    slug: "reinsurance-carriers"
  },
  "52421": {
    description: "See industry description for 524210.",
    industries: ["insurance-agencies-and-brokerages"],
    name: "Insurance Agencies and Brokerages",
    slug: "insurance-agencies-and-brokerages"
  },
  "52429": {
    description: "This industry comprises establishments primarily engaged in providing services related to insurance (except insurance agencies and brokerages). Illustrative Examples: Claims adjusting, insurance, Insurance plan administrative services, third party, Insurance actuarial services, Insurance claims adjusting.",
    industries: ["claims-adjusting-actuarial-services"],
    name: "Other Insurance Related Activities",
    slug: "other-insurance-related-activities"
  },
  "52511": {
    description: "See industry description for 525110.",
    industries: ["pension-funds"],
    name: "Pension Funds",
    slug: "pension-funds"
  },
  "52512": {
    description: "See industry description for 525120.",
    industries: ["insurance-and-employee-benefit-funds"],
    name: "Health and Welfare Funds",
    slug: "health-and-welfare-funds"
  },
  "52519": {
    description: "See industry description for 525190.",
    industries: ["insurance-and-employee-benefit-funds"],
    name: "Other Insurance Funds",
    slug: "other-insurance-funds"
  },
  "52591": {
    description: "See industry description for 525910.",
    industries: ["trusts-and-estates"],
    name: "Open-End Investment Funds",
    slug: "open-end-investment-funds"
  },
  "52592": {
    description: "See industry description for 525920.",
    industries: ["trusts-and-estates"],
    name: "Trusts, Estates, and Agency Accounts",
    slug: "trusts-estates-and-agency-accounts"
  },
  "52599": {
    description: "See industry description for 525990.",
    industries: ["trusts-and-estates"],
    name: "Other Financial Vehicles",
    slug: "other-financial-vehicles"
  },
  "53111": {
    description: "See industry description for 531110.",
    industries: ["leasing-residential-real-estate"],
    name: "Lessors of Residential Buildings and Dwellings",
    slug: "lessors-of-residential-buildings-and-dwellings"
  },
  "53112": {
    description: "See industry description for 531120.",
    industries: ["leasing-non-residential-real-estate"],
    name: "Lessors of Nonresidential Buildings (except Miniwarehouses)",
    slug: "lessors-of-nonresidential-buildings-except-miniwarehouses"
  },
  "53113": {
    description: "See industry description for 531130.",
    industries: ["leasing-residential-real-estate"],
    name: "Lessors of Miniwarehouses and Self-Storage Units",
    slug: "lessors-of-miniwarehouses-and-self-storage-units"
  },
  "53119": {
    description: "See industry description for 531190.",
    industries: ["leasing-residential-real-estate"],
    name: "Lessors of Other Real Estate Property",
    slug: "lessors-of-other-real-estate-property"
  },
  "53121": {
    description: "See industry description for 531210.",
    industries: ["real-estate-agents-and-brokers"],
    name: "Offices of Real Estate Agents and Brokers",
    slug: "offices-of-real-estate-agents-and-brokers"
  },
  "53131": {
    description: "This industry comprises establishments primarily engaged in managing real property for others.  Management includes ensuring that various activities associated with the overall operation of the property are performed, such as collecting rents and overseeing other services (e.g., maintenance, security, trash removal). Cross-References.",
    industries: ["leasing-residential-real-estate"],
    name: "Real Estate Property Managers",
    slug: "real-estate-property-managers"
  },
  "53132": {
    description: "See industry description for 531320.",
    industries: ["leasing-residential-real-estate"],
    name: "Offices of Real Estate Appraisers",
    slug: "offices-of-real-estate-appraisers"
  },
  "53139": {
    description: "See industry description for 531390.",
    industries: ["leasing-residential-real-estate"],
    name: "Other Activities Related to Real Estate",
    slug: "other-activities-related-to-real-estate"
  },
  "53211": {
    description: "This industry comprises establishments primarily engaged in renting or leasing passenger cars without drivers. Illustrative Examples: Passenger car rental or leasing, Passenger truck (light duty) rental or leasing, Passenger van rental or leasing, Sport utility vehicle rental or leasing.",
    industries: ["equipment-rental-services"],
    name: "Passenger Car Rental and Leasing",
    slug: "passenger-car-rental-and-leasing"
  },
  "53212": {
    description: "See industry description for 532120.",
    industries: ["equipment-rental-services"],
    name: "Truck, Utility Trailer, and RV (Recreational Vehicle) Rental and Leasing",
    slug: "truck-utility-trailer-and-rv-recreational-vehicle-rental-and-leasing"
  },
  "53221": {
    description: "See industry description for 532210.",
    industries: ["consumer-goods-rental"],
    name: "Consumer Electronics and Appliances Rental",
    slug: "consumer-electronics-and-appliances-rental"
  },
  "53228": {
    description: "This industry comprises establishments primarily engaged in renting consumer goods (except consumer electronics and appliances). Illustrative Examples: Costume rental, Formal wear rental, Furniture (i.e., residential) rental centers, Hospital bed rental and leasing (i.e., home use), Party rental supply centers, Sporting goods rental, Video disc rental for home electronic equipment (e.g., DVD) .",
    industries: ["consumer-goods-rental"],
    name: "Other Consumer Goods Rental",
    slug: "other-consumer-goods-rental"
  },
  "53231": {
    description: "See industry description for 532310.",
    industries: ["equipment-rental-services"],
    name: "General Rental Centers",
    slug: "general-rental-centers"
  },
  "53241": {
    description: "This industry comprises establishments primarily engaged in renting or leasing one or more of the following without operators: heavy construction, off-highway transportation, mining, and forestry machinery and equipment.  Establishments in this industry may rent or lease products, such as aircraft, railroad cars, steamships, tugboats, bulldozers, earthmoving equipment, well drilling machinery and equipment, or cranes.",
    industries: ["commercial-and-industrial-equipment-rental"],
    name: "Construction, Transportation, Mining, and Forestry Machinery and Equipment Rental and Leasing",
    slug: "construction-transportation-mining-and-forestry-machinery-and-equipment-rental-and-leasing"
  },
  "53242": {
    description: "See industry description for 532420.",
    industries: ["commercial-and-industrial-equipment-rental"],
    name: "Office Machinery and Equipment Rental and Leasing",
    slug: "office-machinery-and-equipment-rental-and-leasing"
  },
  "53249": {
    description: "See industry description for 532490.",
    industries: ["commercial-and-industrial-equipment-rental"],
    name: "Other Commercial and Industrial Machinery and Equipment Rental and Leasing",
    slug: "other-commercial-and-industrial-machinery-and-equipment-rental-and-leasing"
  },
  "53311": {
    description: "See industry description for 533110.",
    industries: ["real-estate-and-equipment-rental-services"],
    name: "Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)",
    slug: "lessors-of-nonfinancial-intangible-assets-except-copyrighted-works"
  },
  "54111": {
    description: "See industry description for 541110.",
    industries: ["law-practice"],
    name: "Offices of Lawyers",
    slug: "offices-of-lawyers"
  },
  "54112": {
    description: "See industry description for 541120.",
    industries: ["legal-services"],
    name: "Offices of Notaries",
    slug: "offices-of-notaries"
  },
  "54119": {
    description: "This industry comprises establishments of legal practitioners (except lawyers and attorneys) primarily engaged in providing specialized legal or paralegal services. Illustrative Examples: Notary public services, Process serving services, Paralegal services, Real estate settlement offices, Patent agent services (i.e., patent filing and searching services), Real estate title abstract companies, Cross-References.",
    industries: ["legal-services"],
    name: "Other Legal Services",
    slug: "other-legal-services"
  },
  "54121": {
    description: "This industry comprises establishments primarily engaged in providing services, such as auditing of accounting records, designing accounting systems, preparing financial statements, developing budgets, preparing tax returns, processing payrolls, bookkeeping, and billing. Illustrative Examples: Accountants' offices, Payroll processing services, Bookkeeping services, Tax return preparation services.",
    industries: ["accounting"],
    name: "Accounting, Tax Preparation, Bookkeeping, and Payroll Services",
    slug: "accounting-tax-preparation-bookkeeping-and-payroll-services"
  },
  "54131": {
    description: "See industry description for 541310.",
    industries: ["architecture-and-planning"],
    name: "Architectural Services",
    slug: "architectural-services"
  },
  "54132": {
    description: "See industry description for 541320.",
    industries: ["professional-services"],
    name: "Landscape Architectural Services",
    slug: "landscape-architectural-services"
  },
  "54133": {
    description: "See industry description for 541330.",
    industries: ["engineering-services"],
    name: "Engineering Services",
    slug: "engineering-services"
  },
  "54134": {
    description: "See industry description for 541340.",
    industries: ["professional-services"],
    name: "Drafting Services",
    slug: "drafting-services"
  },
  "54135": {
    description: "See industry description for 541350.",
    industries: ["professional-services"],
    name: "Building Inspection Services",
    slug: "building-inspection-services"
  },
  "54136": {
    description: "See industry description for 541360.",
    industries: ["surveying-and-mapping-services"],
    name: "Geophysical Surveying and Mapping Services",
    slug: "geophysical-surveying-and-mapping-services"
  },
  "54137": {
    description: "See industry description for 541370.",
    industries: ["surveying-and-mapping-services"],
    name: "Surveying and Mapping (except Geophysical) Services",
    slug: "surveying-and-mapping-except-geophysical-services"
  },
  "54138": {
    description: "See industry description for 541380.",
    industries: ["professional-services"],
    name: "Testing Laboratories and Services",
    slug: "testing-laboratories-and-services"
  },
  "54141": {
    description: "See industry description for 541410.",
    industries: ["interior-design"],
    name: "Interior Design Services",
    slug: "interior-design-services"
  },
  "54142": {
    description: "See industry description for 541420.",
    industries: ["design-services"],
    name: "Industrial Design Services",
    slug: "industrial-design-services"
  },
  "54143": {
    description: "See industry description for 541430.",
    industries: ["graphic-design"],
    name: "Graphic Design Services",
    slug: "graphic-design-services"
  },
  "54149": {
    description: "See industry description for 541490.",
    industries: ["design-services"],
    name: "Other Specialized Design Services",
    slug: "other-specialized-design-services"
  },
  "54151": {
    description: "This industry comprises establishments primarily engaged in providing expertise in the field of information technologies through one or more of the following activities: (1) writing, modifying, testing, and supporting software to meet the needs of a particular customer; (2) planning and designing computer systems that integrate computer hardware, software, and communication technologies; (3) on-site management and operation of clients' computer systems and/or data processing facilities; and (4) other professional and technical computer related advice and services. Illustrative Examples: Computer facilities management services, Custom computer programming services, Computer systems integration design services, Computer hardware or software consulting services, Software installation services.",
    industries: ["it-services-and-it-consulting"],
    name: "Computer Systems Design and Related Services",
    slug: "computer-systems-design-and-related-services"
  },
  "54161": {
    description: "This industry comprises establishments primarily engaged in providing advice and assistance to businesses and other organizations on management issues, such as strategic and organizational planning; financial planning and budgeting; marketing objectives and policies; human resource policies, practices, and planning; production scheduling; and control planning. Illustrative Examples: Actuarial, benefit, and compensation consulting services, Human resources consulting services, Marketing consulting services, Administrative and general management consulting services, Process, physical distribution, and logistics consulting services, Cross-References.",
    industries: ["business-consulting-and-services"],
    name: "Management Consulting Services",
    slug: "management-consulting-services"
  },
  "54162": {
    description: "See industry description for 541620.",
    industries: ["environmental-services"],
    name: "Environmental Consulting Services",
    slug: "environmental-consulting-services"
  },
  "54169": {
    description: "See industry description for 541690.",
    industries: ["business-consulting-and-services"],
    name: "Other Scientific and Technical Consulting Services",
    slug: "other-scientific-and-technical-consulting-services"
  },
  "54171": {
    description: "This industry comprises establishments primarily engaged in conducting research and experimental development in the physical, engineering, and life sciences, such as agriculture, electronics, environmental, biology, botany, biotechnology, computers, chemistry, food, fisheries, forests, geology, health, mathematics, medicine, nanotechnology, oceanography, pharmacy, physics, veterinary, and other allied subjects.",
    industries: ["research-services"],
    name: "Research and Development in the Physical, Engineering, and Life Sciences",
    slug: "research-and-development-in-the-physical-engineering-and-life-sciences"
  },
  "54172": {
    description: "See industry description for 541720.",
    industries: ["think-tanks"],
    name: "Research and Development in the Social Sciences and Humanities",
    slug: "research-and-development-in-the-social-sciences-and-humanities"
  },
  "54181": {
    description: "See industry description for 541810.",
    industries: ["marketing-services"],
    name: "Advertising Agencies",
    slug: "advertising-agencies"
  },
  "54182": {
    description: "See industry description for 541820.",
    industries: ["public-relations-and-communications-services"],
    name: "Public Relations Agencies",
    slug: "public-relations-agencies"
  },
  "54183": {
    description: "See industry description for 541830.",
    industries: ["marketing-services"],
    name: "Media Buying Agencies",
    slug: "media-buying-agencies"
  },
  "54184": {
    description: "See industry description for 541840.",
    industries: ["marketing-services"],
    name: "Media Representatives",
    slug: "media-representatives"
  },
  "54185": {
    description: "See industry description for 541850.",
    industries: ["marketing-services"],
    name: "Indoor and Outdoor Display Advertising",
    slug: "indoor-and-outdoor-display-advertising"
  },
  "54186": {
    description: "See industry description for 541860.",
    industries: ["marketing-services"],
    name: "Direct Mail Advertising",
    slug: "direct-mail-advertising"
  },
  "54187": {
    description: "See industry description for 541870.",
    industries: ["marketing-services"],
    name: "Advertising Material Distribution Services",
    slug: "advertising-material-distribution-services"
  },
  "54189": {
    description: "See industry description for 541890.",
    industries: ["marketing-services"],
    name: "Other Services Related to Advertising",
    slug: "other-services-related-to-advertising"
  },
  "54191": {
    description: "See industry description for 541910.",
    industries: ["market-research"],
    name: "Marketing Research and Public Opinion Polling",
    slug: "marketing-research-and-public-opinion-polling"
  },
  "54192": {
    description: "This industry comprises establishments primarily engaged in providing still, video, or digital photography services.  These establishments may specialize in a particular field of photography, such as commercial and industrial photography, portrait photography, and special events photography.  Commercial or portrait photography studios are included in this industry.",
    industries: ["photography"],
    name: "Photographic Services",
    slug: "photographic-services"
  },
  "54193": {
    description: "See industry description for 541930.",
    industries: ["translation-and-localization"],
    name: "Translation and Interpretation Services",
    slug: "translation-and-interpretation-services"
  },
  "54194": {
    description: "See industry description for 541940.",
    industries: ["veterinary-services"],
    name: "Veterinary Services",
    slug: "veterinary-services"
  },
  "54199": {
    description: "See industry description for 541990.",
    industries: ["professional-services"],
    name: "All Other Professional, Scientific, and Technical Services",
    slug: "all-other-professional-scientific-and-technical-services"
  },
  "55111": {
    description: "This industry comprises (1) establishments primarily engaged in holding the securities of (or other equity interests in) companies and enterprises for the purpose of owning a controlling interest or influencing management decisions or (2) establishments (except government establishments) that administer, oversee, and manage other establishments of the company or enterprise and that normally undertake the strategic or organizational planning and decision-making role of the company or enterprise.  Establishments that administer, oversee, and manage may hold the securities of the company or enterprise. Cross-References.",
    industries: ["holding-companies"],
    name: "Management of Companies and Enterprises",
    slug: "management-of-companies-and-enterprises"
  },
  "56111": {
    description: "See industry description for 561110.",
    industries: ["office-administration"],
    name: "Office Administrative Services",
    slug: "office-administrative-services"
  },
  "56121": {
    description: "See industry description for 561210.",
    industries: ["facilities-services"],
    name: "Facilities Support Services",
    slug: "facilities-support-services"
  },
  "56131": {
    description: "This industry comprises establishments primarily engaged in one of the following: (1) listing employment vacancies and referring or placing applicants for employment; or (2) providing executive search, recruitment, and placement services. Illustrative Examples: Employment agencies, Executive placement agencies or services, Executive search services.",
    industries: ["staffing-and-recruiting"],
    name: "Employment Placement Agencies and Executive Search Services",
    slug: "employment-placement-agencies-and-executive-search-services"
  },
  "56132": {
    description: "See industry description for 561320.",
    industries: ["temporary-help-services"],
    name: "Temporary Help Services",
    slug: "temporary-help-services"
  },
  "56133": {
    description: "See industry description for 561330.",
    industries: ["staffing-and-recruiting"],
    name: "Professional Employer Organizations",
    slug: "professional-employer-organizations"
  },
  "56141": {
    description: "See industry description for 561410.",
    industries: ["writing-and-editing"],
    name: "Document Preparation Services",
    slug: "document-preparation-services"
  },
  "56142": {
    description: "This industry comprises (1) establishments primarily engaged in answering telephone calls and relaying messages and (2) establishments primarily engaged in providing telemarketing services, such as promoting products or services by telephone; taking orders by telephone, facsimile, email, or other communication modes; and soliciting contributions or providing information by telephone.  Telephone call centers provide these services on behalf of clients and do not own the products or provide the services that they are representing, or they serve other establishments of the same enterprise.",
    industries: ["telephone-call-centers"],
    name: "Telephone Call Centers",
    slug: "telephone-call-centers"
  },
  "56143": {
    description: "This industry comprises (1) establishments primarily engaged in providing mailbox rental and other postal and mailing services (except direct mail advertising); (2) establishments, generally known as copy centers or shops, primarily engaged in providing photocopying, duplicating, blueprinting, and other document copying services without also providing printing services (i.e., offset printing, quick printing, digital printing, prepress services); and (3) establishments that provide a range of office support services (except printing services), such as mailing services, document copying services, facsimile services, word processing services, on-site PC rental services, and office product sales.",
    industries: ["administrative-and-support-services"],
    name: "Business Service Centers",
    slug: "business-service-centers"
  },
  "56144": {
    description: "See industry description for 561440.",
    industries: ["collection-agencies"],
    name: "Collection Agencies",
    slug: "collection-agencies"
  },
  "56145": {
    description: "See industry description for 561450.",
    industries: ["administrative-and-support-services"],
    name: "Credit Bureaus",
    slug: "credit-bureaus"
  },
  "56149": {
    description: "This industry comprises establishments primarily engaged in providing business support services (except secretarial and other document preparation services; telephone answering or telemarketing services; private mail services or document copying services conducted as separate activities or in conjunction with other office support services; monetary debt collection services; and credit reporting services). Illustrative Examples: Address bar coding services, Mail presorting services, Bar code imprinting services, Court reporting services, Repossession services, Real-time (i.e., simultaneous) closed captioning of live television performances, meetings, conferences, Charitable fundraising campaign organization services on a contract or fee basis.",
    industries: ["administrative-and-support-services"],
    name: "Other Business Support Services",
    slug: "other-business-support-services"
  },
  "56151": {
    description: "See industry description for 561510.",
    industries: ["travel-arrangements"],
    name: "Travel Agencies",
    slug: "travel-agencies"
  },
  "56152": {
    description: "See industry description for 561520.",
    industries: ["travel-arrangements"],
    name: "Tour Operators",
    slug: "tour-operators"
  },
  "56159": {
    description: "This industry comprises establishments (except travel agencies and tour operators) primarily engaged in providing travel arrangement and reservation services. Illustrative Examples: Condominium time-share exchange services, Road and travel services automobile clubs, Convention or visitors bureaus, Ticket (e.g., amusement, sports, theatrical) agencies, Ticket (e.g., airline, bus, cruise ship, sports, theatrical) offices, Reservation (e.g., airline, car rental, hotel, restaurant) services, Cross-References.",
    industries: ["travel-arrangements"],
    name: "Other Travel Arrangement and Reservation Services",
    slug: "other-travel-arrangement-and-reservation-services"
  },
  "56161": {
    description: "This industry comprises establishments primarily engaged in providing one or more of the following: (1) investigation, detective, and personal background check services; (2) guard and patrol services; and (3) picking up and delivering money, receipts, or other valuable items with personnel and equipment to protect such properties while in transit. Illustrative Examples: Armored car services, Private detective services, Bodyguard services, Security guard services, Polygraph services, Cross-References.",
    industries: ["security-and-investigations"],
    name: "Investigation, Guard, and Armored Car Services",
    slug: "investigation-guard-and-armored-car-services"
  },
  "56162": {
    description: "This industry comprises establishments engaged in (1) selling security systems, such as burglar and fire alarms and locking devices, along with installation, repair, or monitoring services or (2) remote monitoring of electronic security alarm systems.",
    industries: ["security-systems-services"],
    name: "Security Systems Services",
    slug: "security-systems-services"
  },
  "56171": {
    description: "See industry description for 561710.",
    industries: ["administrative-and-support-services"],
    name: "Exterminating and Pest Control Services",
    slug: "exterminating-and-pest-control-services"
  },
  "56172": {
    description: "See industry description for 561720.",
    industries: ["janitorial-services"],
    name: "Janitorial Services",
    slug: "janitorial-services"
  },
  "56173": {
    description: "See industry description for 561730.",
    industries: ["administrative-and-support-services"],
    name: "Landscaping Services",
    slug: "landscaping-services"
  },
  "56174": {
    description: "See industry description for 561740.",
    industries: ["administrative-and-support-services"],
    name: "Carpet and Upholstery Cleaning Services",
    slug: "carpet-and-upholstery-cleaning-services"
  },
  "56179": {
    description: "See industry description for 561790.",
    industries: ["administrative-and-support-services"],
    name: "Other Services to Buildings and Dwellings",
    slug: "other-services-to-buildings-and-dwellings"
  },
  "56191": {
    description: "See industry description for 561910.",
    industries: ["administrative-and-support-services"],
    name: "Packaging and Labeling Services",
    slug: "packaging-and-labeling-services"
  },
  "56192": {
    description: "See industry description for 561920.",
    industries: ["events-services"],
    name: "Convention and Trade Show Organizers",
    slug: "convention-and-trade-show-organizers"
  },
  "56199": {
    description: "See industry description for 561990.",
    industries: ["administrative-and-support-services"],
    name: "All Other Support Services",
    slug: "all-other-support-services"
  },
  "56211": {
    description: "This industry comprises establishments primarily engaged in (1) collecting and/or hauling hazardous waste, nonhazardous waste, and/or recyclable materials within a local area and/or (2) operating hazardous or nonhazardous waste transfer stations.  Hazardous waste collection establishments may be responsible for the identification, treatment, packaging, and labeling of waste for the purposes of transport.",
    industries: ["waste-collection"],
    name: "Waste Collection",
    slug: "waste-collection"
  },
  "56221": {
    description: "This industry comprises establishments primarily engaged in (1) operating waste treatment or disposal facilities (except sewer systems or sewage treatment facilities) or (2) the combined activity of collecting and/or hauling of waste materials within a local area and operating waste treatment or disposal facilities.  Waste combustors or incinerators (including those that may produce byproducts, such as electricity), solid waste landfills, and compost dumps are included in this industry.",
    industries: ["waste-treatment-and-disposal"],
    name: "Waste Treatment and Disposal",
    slug: "waste-treatment-and-disposal"
  },
  "56291": {
    description: "See industry description for 562910.",
    industries: ["administrative-and-support-services"],
    name: "Remediation Services",
    slug: "remediation-services"
  },
  "56292": {
    description: "See industry description for 562920.",
    industries: ["administrative-and-support-services"],
    name: "Materials Recovery Facilities",
    slug: "materials-recovery-facilities"
  },
  "56299": {
    description: "This industry comprises establishments primarily engaged in waste management services (except waste collection, waste treatment and disposal, remediation, operation of materials recovery facilities, and waste management consulting services). Illustrative Examples: Beach cleaning and maintenance services, Pumping (i.e., cleaning) cesspools, portable toilets, or septic tanks, Cesspool cleaning services, Sewer cleaning and rodding services, Portable toilet renting and/or servicing, Sewer or storm basin cleanout services.",
    industries: ["administrative-and-support-services"],
    name: "All Other Waste Management Services",
    slug: "all-other-waste-management-services"
  },
  "61111": {
    description: "See industry description for 611110.",
    industries: ["primary-and-secondary-education"],
    name: "Elementary and Secondary Schools",
    slug: "elementary-and-secondary-schools"
  },
  "61121": {
    description: "See industry description for 611210.",
    industries: ["education"],
    name: "Junior Colleges",
    slug: "junior-colleges"
  },
  "61131": {
    description: "See industry description for 611310.",
    industries: ["higher-education"],
    name: "Colleges, Universities, and Professional Schools",
    slug: "colleges-universities-and-professional-schools"
  },
  "61141": {
    description: "See industry description for 611410.",
    industries: ["secretarial-schools"],
    name: "Business and Secretarial Schools",
    slug: "business-and-secretarial-schools"
  },
  "61142": {
    description: "See industry description for 611420.",
    industries: ["professional-training-and-coaching"],
    name: "Computer Training",
    slug: "computer-training"
  },
  "61143": {
    description: "See industry description for 611430.",
    industries: ["professional-training-and-coaching"],
    name: "Professional and Management Development Training",
    slug: "professional-and-management-development-training"
  },
  "61151": {
    description: "This industry comprises establishments primarily engaged in offering vocational and technical training in a variety of technical subjects and trades.  The training often leads to job-specific certification.  Instruction may be provided in diverse settings, such as the establishment's or client's training facilities, educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods. Illustrative Examples: Apprenticeship training programs, Graphic arts schools, Aviation and flight training schools, Modeling schools, Computer repair training, Nursing schools (except academic), Cosmetology schools, Real estate schools, Electronic equipment repair training, Truck driving schools .",
    industries: ["technical-and-vocational-training"],
    name: "Technical and Trade Schools",
    slug: "technical-and-trade-schools"
  },
  "61161": {
    description: "See industry description for 611610.",
    industries: ["fine-arts-schools"],
    name: "Fine Arts Schools",
    slug: "fine-arts-schools"
  },
  "61162": {
    description: "See industry description for 611620.",
    industries: ["sports-and-recreation-instruction"],
    name: "Sports and Recreation Instruction",
    slug: "sports-and-recreation-instruction"
  },
  "61163": {
    description: "See industry description for 611630.",
    industries: ["language-schools"],
    name: "Language Schools",
    slug: "language-schools"
  },
  "61169": {
    description: "This industry comprises establishments primarily engaged in offering instruction (except academic schools, colleges, and universities; business, computer, and management instruction; technical and trade instruction; and fine arts, sports, recreation, and language instruction). Illustrative Examples: Academic tutoring services, Public speaking training, Automobile driving schools, Speed reading instruction, Exam preparation services.",
    industries: ["education"],
    name: "All Other Schools and Instruction",
    slug: "all-other-schools-and-instruction"
  },
  "61171": {
    description: "See industry description for 611710.",
    industries: ["education"],
    name: "Educational Support Services",
    slug: "educational-support-services"
  },
  "62111": {
    description: "This industry comprises establishments of health practitioners having the degree of M.D. (Doctor of Medicine) or D.O. (Doctor of Osteopathic Medicine) primarily engaged in the independent practice of general or specialized medicine (e.g., anesthesiology, oncology, ophthalmology, psychiatry) or surgery.  These practitioners operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers. Cross-References.",
    industries: ["physicians"],
    name: "Offices of Physicians",
    slug: "offices-of-physicians"
  },
  "62121": {
    description: "See industry description for 621210.",
    industries: ["dentists"],
    name: "Offices of Dentists",
    slug: "offices-of-dentists"
  },
  "62131": {
    description: "See industry description for 621310.",
    industries: ["chiropractors"],
    name: "Offices of Chiropractors",
    slug: "offices-of-chiropractors"
  },
  "62132": {
    description: "See industry description for 621320.",
    industries: ["optometrists"],
    name: "Offices of Optometrists",
    slug: "offices-of-optometrists"
  },
  "62133": {
    description: "See industry description for 621330.",
    industries: ["mental-health-care"],
    name: "Offices of Mental Health Practitioners (except Physicians)",
    slug: "offices-of-mental-health-practitioners-except-physicians"
  },
  "62134": {
    description: "See industry description for 621340.",
    industries: ["physical-occupational-and-speech-therapists"],
    name: "Offices of Physical, Occupational and Speech Therapists, and Audiologists",
    slug: "offices-of-physical-occupational-and-speech-therapists-and-audiologists"
  },
  "62139": {
    description: "This industry comprises establishments of independent health practitioners (except physicians; dentists; chiropractors; optometrists; mental health specialists; physical, occupational, and speech therapists; and audiologists).  These practitioners operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers. Illustrative Examples: Acupuncturists' (except MDs or DOs) offices, Inhalation or respiratory therapists' offices, Dental hygienists' offices, Midwives' offices, Denturists' offices, Naturopaths' offices, Dietitians' offices, Podiatrists' offices, Homeopaths' offices, Registered or licensed practical nurses' offices.",
    industries: ["medical-practices"],
    name: "Offices of All Other Health Practitioners",
    slug: "offices-of-all-other-health-practitioners"
  },
  "62141": {
    description: "See industry description for 621410.",
    industries: ["family-planning-centers"],
    name: "Family Planning Centers",
    slug: "family-planning-centers"
  },
  "62142": {
    description: "See industry description for 621420.",
    industries: ["medical-practices"],
    name: "Outpatient Mental Health and Substance Abuse Centers",
    slug: "outpatient-mental-health-and-substance-abuse-centers"
  },
  "62149": {
    description: "This industry comprises establishments with medical staff primarily engaged in providing general or specialized outpatient care (except family planning centers and outpatient mental health and substance abuse centers).  Centers or clinics of health practitioners with different degrees from more than one industry practicing within the same establishment (e.g., Doctor of Medicine and Doctor of Dental Medicine) are included in this industry. Illustrative Examples: Dialysis centers and clinics, Outpatient biofeedback centers and clinics, Freestanding ambulatory surgical centers and clinics, Outpatient community health centers and clinics, Freestanding emergency medical centers and clinics, Outpatient sleep disorder centers and clinics, Health maintenance organization (HMO) medical centers and clinics, Cross-References.",
    industries: ["outpatient-care-centers"],
    name: "Other Outpatient Care Centers",
    slug: "other-outpatient-care-centers"
  },
  "62151": {
    description: "This industry comprises establishments known as medical and diagnostic laboratories primarily engaged in providing analytic or diagnostic services, including body fluid analysis and diagnostic imaging, generally to the medical profession or to the patient on referral from a health practitioner. Illustrative Examples: Dental or medical X-ray laboratories, Medical pathology laboratories, Diagnostic imaging centers, Medical testing laboratories, Medical forensic laboratories, Cross-References.",
    industries: ["medical-and-diagnostic-laboratories"],
    name: "Medical and Diagnostic Laboratories",
    slug: "medical-and-diagnostic-laboratories"
  },
  "62161": {
    description: "See industry description for 621610.",
    industries: ["home-health-care-services"],
    name: "Home Health Care Services",
    slug: "home-health-care-services"
  },
  "62191": {
    description: "See industry description for 621910.",
    industries: ["ambulance-services"],
    name: "Ambulance Services",
    slug: "ambulance-services"
  },
  "62199": {
    description: "This industry comprises establishments primarily engaged in providing ambulatory health care services (except offices of physicians, dentists, and other health practitioners; outpatient care centers; medical and diagnostic laboratories; home health care providers; and ambulances). Illustrative Examples: Blood donor stations, Pacemaker monitoring services, Blood or body organ banks, Physical fitness evaluation services (except by offices of health practitioners), Health screening services (except by offices of health practitioners), Smoking cessation programs, Hearing testing services (except by offices of audiologists) , Cross-References.",
    industries: ["medical-practices"],
    name: "All Other Ambulatory Health Care Services",
    slug: "all-other-ambulatory-health-care-services"
  },
  "62211": {
    description: "See industry description for 622110.",
    industries: ["hospitals"],
    name: "General Medical and Surgical Hospitals",
    slug: "general-medical-and-surgical-hospitals"
  },
  "62221": {
    description: "See industry description for 622210.",
    industries: ["hospitals"],
    name: "Psychiatric and Substance Abuse Hospitals",
    slug: "psychiatric-and-substance-abuse-hospitals"
  },
  "62231": {
    description: "See industry description for 622310.",
    industries: ["hospitals"],
    name: "Specialty (except Psychiatric and Substance Abuse) Hospitals",
    slug: "specialty-except-psychiatric-and-substance-abuse-hospitals"
  },
  "62311": {
    description: "See industry description for 623110.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Nursing Care Facilities (Skilled Nursing Facilities)",
    slug: "nursing-care-facilities-skilled-nursing-facilities"
  },
  "62321": {
    description: "See industry description for 623210.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Residential Intellectual and Developmental Disability Facilities",
    slug: "residential-intellectual-and-developmental-disability-facilities"
  },
  "62322": {
    description: "See industry description for 623220.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Residential Mental Health and Substance Abuse Facilities",
    slug: "residential-mental-health-and-substance-abuse-facilities"
  },
  "62331": {
    description: "This industry comprises establishments primarily engaged in providing residential and personal care services for (1) the elderly and other persons who are unable to fully care for themselves and/or (2) the elderly and other persons who do not desire to live independently.  The care typically includes room, board, supervision, and assistance in daily living, such as housekeeping services.  In some instances, these establishments provide skilled nursing care for residents in separate on-site facilities. Illustrative Examples: Assisted living facilities with on-site nursing care facilities, Assisted living facilities for the elderly without nursing care, Continuing care retirement communities, Rest homes without nursing care, Cross-References.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Continuing Care Retirement Communities and Assisted Living Facilities for the Elderly",
    slug: "continuing-care-retirement-communities-and-assisted-living-facilities-for-the-elderly"
  },
  "62399": {
    description: "See industry description for 623990.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Other Residential Care Facilities",
    slug: "other-residential-care-facilities"
  },
  "62411": {
    description: "See industry description for 624110.",
    industries: ["individual-and-family-services"],
    name: "Child and Youth Services",
    slug: "child-and-youth-services"
  },
  "62412": {
    description: "See industry description for 624120.",
    industries: ["services-for-the-elderly-and-disabled"],
    name: "Services for the Elderly and Persons with Disabilities",
    slug: "services-for-the-elderly-and-persons-with-disabilities"
  },
  "62419": {
    description: "See industry description for 624190.",
    industries: ["individual-and-family-services"],
    name: "Other Individual and Family Services",
    slug: "other-individual-and-family-services"
  },
  "62421": {
    description: "See industry description for 624210.",
    industries: ["community-services"],
    name: "Community Food Services",
    slug: "community-food-services"
  },
  "62422": {
    description: "This industry comprises establishments primarily engaged in providing one or more of the following community housing services: (1) short-term emergency shelter for victims of domestic violence, sexual assault, or child abuse; (2) temporary residential shelter for the homeless, runaway youths, and patients and families caught in medical crises; (3) transitional housing for low-income individuals and families; (4) volunteer construction or repair of low-cost housing, in partnership with the homeowner who may assist in construction or repair work; and (5) repair of homes for elderly or disabled homeowners.  These establishments may operate their own shelter, they may subsidize housing using existing homes, apartments, hotels, or motels, or they may require a low-cost mortgage or work (sweat) equity. Cross-References.",
    industries: ["community-services"],
    name: "Community Housing Services",
    slug: "community-housing-services"
  },
  "62423": {
    description: "See industry description for 624230.",
    industries: ["emergency-and-relief-services"],
    name: "Emergency and Other Relief Services",
    slug: "emergency-and-other-relief-services"
  },
  "62431": {
    description: "See industry description for 624310.",
    industries: ["vocational-rehabilitation-services"],
    name: "Vocational Rehabilitation Services",
    slug: "vocational-rehabilitation-services"
  },
  "62441": {
    description: "See industry description for 624410.",
    industries: ["child-day-care-services"],
    name: "Child Day Care Services",
    slug: "child-day-care-services"
  },
  "71111": {
    description: "See industry description for 711110.",
    industries: ["theater-companies"],
    name: "Theater Companies and Dinner Theaters",
    slug: "theater-companies-and-dinner-theaters"
  },
  "71112": {
    description: "See industry description for 711120.",
    industries: ["dance-companies"],
    name: "Dance Companies",
    slug: "dance-companies"
  },
  "71113": {
    description: "See industry description for 711130.",
    industries: ["musicians"],
    name: "Musical Groups and Artists",
    slug: "musical-groups-and-artists"
  },
  "71119": {
    description: "See industry description for 711190.",
    industries: ["circuses-and-magic-shows"],
    name: "Other Performing Arts Companies",
    slug: "other-performing-arts-companies"
  },
  "71121": {
    description: "This industry comprises (1) sports teams or clubs primarily participating in live sporting events before a paying audience; (2) establishments primarily engaged in operating racetracks without casinos; (3) independent athletes engaged in participating in live sporting or racing events before a paying audience; (4) owners of racing participants, such as cars, dogs, and horses, primarily engaged in entering them in racing events or other spectator sports events; and (5) establishments, such as sports trainers, primarily engaged in providing specialized services to support participants in sports events or competitions.  The sports teams and clubs included in this industry may or may not operate their own arena, stadium, or other facility for presenting their games or other spectator sports events. Cross-References.",
    industries: ["spectator-sports"],
    name: "Spectator Sports",
    slug: "spectator-sports"
  },
  "71131": {
    description: "See industry description for 711310.",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Promoters of Performing Arts, Sports, and Similar Events with Facilities",
    slug: "promoters-of-performing-arts-sports-and-similar-events-with-facilities"
  },
  "71132": {
    description: "See industry description for 711320.",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Promoters of Performing Arts, Sports, and Similar Events without Facilities",
    slug: "promoters-of-performing-arts-sports-and-similar-events-without-facilities"
  },
  "71141": {
    description: "See industry description for 711410.",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Agents and Managers for Artists, Athletes, Entertainers, and Other Public Figures",
    slug: "agents-and-managers-for-artists-athletes-entertainers-and-other-public-figures"
  },
  "71151": {
    description: "See industry description for 711510.",
    industries: ["artists-and-writers"],
    name: "Independent Artists, Writers, and Performers",
    slug: "independent-artists-writers-and-performers"
  },
  "71211": {
    description: "See industry description for 712110.",
    industries: ["museums"],
    name: "Museums",
    slug: "museums"
  },
  "71212": {
    description: "See industry description for 712120.",
    industries: ["historical-sites"],
    name: "Historical Sites",
    slug: "historical-sites"
  },
  "71213": {
    description: "See industry description for 712130.",
    industries: ["zoos-and-botanical-gardens"],
    name: "Zoos and Botanical Gardens",
    slug: "zoos-and-botanical-gardens"
  },
  "71219": {
    description: "See industry description for 712190.",
    industries: ["museums-historical-sites-and-zoos"],
    name: "Nature Parks and Other Similar Institutions",
    slug: "nature-parks-and-other-similar-institutions"
  },
  "71311": {
    description: "See industry description for 713110.",
    industries: ["amusement-parks-and-arcades"],
    name: "Amusement and Theme Parks",
    slug: "amusement-and-theme-parks"
  },
  "71312": {
    description: "See industry description for 713120.",
    industries: ["amusement-parks-and-arcades"],
    name: "Amusement Arcades",
    slug: "amusement-arcades"
  },
  "71321": {
    description: "See industry description for 713210.",
    industries: ["gambling-facilities-and-casinos"],
    name: "Casinos (except Casino Hotels)",
    slug: "casinos-except-casino-hotels"
  },
  "71329": {
    description: "See industry description for 713290.",
    industries: ["gambling-facilities-and-casinos"],
    name: "Other Gambling Industries",
    slug: "other-gambling-industries"
  },
  "71391": {
    description: "See industry description for 713910.",
    industries: ["golf-courses-and-country-clubs"],
    name: "Golf Courses and Country Clubs",
    slug: "golf-courses-and-country-clubs"
  },
  "71392": {
    description: "See industry description for 713920.",
    industries: ["skiing-facilities"],
    name: "Skiing Facilities",
    slug: "skiing-facilities"
  },
  "71393": {
    description: "See industry description for 713930.",
    industries: ["recreational-facilities"],
    name: "Marinas",
    slug: "marinas"
  },
  "71394": {
    description: "See industry description for 713940.",
    industries: ["wellness-and-fitness-services"],
    name: "Fitness and Recreational Sports Centers",
    slug: "fitness-and-recreational-sports-centers"
  },
  "71395": {
    description: "See industry description for 713950.",
    industries: ["recreational-facilities"],
    name: "Bowling Centers",
    slug: "bowling-centers"
  },
  "71399": {
    description: "See industry description for 713990.",
    industries: ["recreational-facilities"],
    name: "All Other Amusement and Recreation Industries",
    slug: "all-other-amusement-and-recreation-industries"
  },
  "72111": {
    description: "See industry description for 721110.",
    industries: ["hotels-and-motels"],
    name: "Hotels (except Casino Hotels) and Motels",
    slug: "hotels-except-casino-hotels-and-motels"
  },
  "72112": {
    description: "See industry description for 721120.",
    industries: ["hospitality"],
    name: "Casino Hotels",
    slug: "casino-hotels"
  },
  "72119": {
    description: "This industry comprises establishments primarily engaged in providing short-term lodging (except hotels, motels, and casino hotels). Illustrative Examples: Bed-and-breakfast inns, Tourist homes, Guest houses, Hostels, Housekeeping cabins and cottages.",
    industries: ["bed-and-breakfasts-hostels-homestays"],
    name: "Other Traveler Accommodation",
    slug: "other-traveler-accommodation"
  },
  "72121": {
    description: "This industry comprises establishments primarily engaged in operating recreational vehicle parks and campgrounds and recreational and vacation camps.  These establishments cater to outdoor enthusiasts and are characterized by the type of accommodation and by the nature and the range of recreational facilities and activities provided to their clients. Illustrative Examples: Campgrounds, Fishing and hunting camps, Travel trailer campsites, Outdoor adventure retreats, Vacation camps (except instructional, day), Recreational vehicle parks.",
    industries: ["accommodation-services"],
    name: "RV (Recreational Vehicle) Parks and Recreational Camps",
    slug: "rv-recreational-vehicle-parks-and-recreational-camps"
  },
  "72131": {
    description: "See industry description for 721310.",
    industries: ["accommodation-services"],
    name: "Rooming and Boarding Houses, Dormitories, and Workers' CampsT",
    slug: "rooming-and-boarding-houses-dormitories-and-workers-camps-t"
  },
  "72231": {
    description: "See industry description for 722310.",
    industries: ["food-and-beverage-services"],
    name: "Food Service Contractors",
    slug: "food-service-contractors"
  },
  "72232": {
    description: "See industry description for 722320.",
    industries: ["caterers"],
    name: "Caterers",
    slug: "caterers"
  },
  "72233": {
    description: "See industry description for 722330.",
    industries: ["mobile-food-services"],
    name: "Mobile Food Services",
    slug: "mobile-food-services"
  },
  "72241": {
    description: "See industry description for 722410.",
    industries: ["bars-taverns-and-nightclubs"],
    name: "Drinking Places (Alcoholic Beverages)",
    slug: "drinking-places-alcoholic-beverages"
  },
  "72251": {
    description: "This industry comprises establishments primarily engaged in one of the following: (1) providing food services to patrons who order and are served while seated (i.e., waiter/waitress service) and pay after eating; (2) providing food services to patrons who generally order or select items (e.g., at a counter, in a buffet line) and pay before eating; or (3) preparing and/or serving a specialty snack (e.g., ice cream, frozen yogurt, cookies) and/or nonalcoholic beverages (e.g., coffee, juices, sodas) for consumption on or near the premises.",
    industries: ["restaurants"],
    name: "Restaurants and Other Eating Places",
    slug: "restaurants-and-other-eating-places"
  },
  "81111": {
    description: "This industry comprises establishments primarily engaged in providing mechanical or electrical repair and maintenance services for automotive vehicles, such as passenger cars, trucks, and vans, and all trailers.  These establishments may specialize in a single service or may provide a wide range of these services.",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Automotive Mechanical and Electrical Repair and Maintenance",
    slug: "automotive-mechanical-and-electrical-repair-and-maintenance"
  },
  "81112": {
    description: "This industry comprises establishments primarily engaged in providing one or more of the following: (1) repairing or customizing automotive vehicle and trailer bodies and interiors; (2) painting automotive vehicle and trailer bodies; (3) replacing, repairing, and/or tinting automotive vehicle glass; and (4) customizing automobile, truck, and van interiors for the physically disabled or other customers with special requirements. Illustrative Examples: Automotive body shops, Automotive paint shops, Automotive glass shops, Restoration shops, antique and classic automotive .",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Automotive Body, Paint, Interior, and Glass Repair",
    slug: "automotive-body-paint-interior-and-glass-repair"
  },
  "81119": {
    description: "This industry comprises establishments primarily engaged in providing automotive repair and maintenance services (except mechanical and electrical repair and maintenance and body, paint, interior, and glass repair) for automotive vehicles, such as passenger cars, trucks, and vans, and all trailers. Illustrative Examples: Automotive air-conditioning repair shops, Automotive tire repair (except retreading) shops, Automotive oil change and lubrication shops, Car washes, Automotive rustproofing and undercoating shops .",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Other Automotive Repair and Maintenance",
    slug: "other-automotive-repair-and-maintenance"
  },
  "81121": {
    description: "See industry description for 811210.",
    industries: ["electronic-and-precision-equipment-maintenance"],
    name: "Electronic and Precision Equipment Repair and Maintenance",
    slug: "electronic-and-precision-equipment-repair-and-maintenance"
  },
  "81131": {
    description: "See industry description for 811310.",
    industries: ["commercial-and-industrial-machinery-maintenance"],
    name: "Commercial and Industrial Machinery and Equipment (except Automotive and Electronic) Repair and Maintenance",
    slug: "commercial-and-industrial-machinery-and-equipment-except-automotive-and-electronic-repair-and-maintenance"
  },
  "81141": {
    description: "This industry comprises establishments primarily engaged in repairing and servicing home and garden equipment and/or household-type appliances without retailing new equipment or appliances.  Establishments in this industry repair and maintain items, such as lawnmowers, edgers, snowblowers, leaf blowers, washing machines, clothes dryers, and refrigerators.",
    industries: ["repair-and-maintenance"],
    name: "Home and Garden Equipment and Appliance Repair and Maintenance",
    slug: "home-and-garden-equipment-and-appliance-repair-and-maintenance"
  },
  "81142": {
    description: "See industry description for 811420.",
    industries: ["reupholstery-and-furniture-repair"],
    name: "Reupholstery and Furniture Repair",
    slug: "reupholstery-and-furniture-repair"
  },
  "81143": {
    description: "See industry description for 811430.",
    industries: ["footwear-and-leather-goods-repair"],
    name: "Footwear and Leather Goods Repair",
    slug: "footwear-and-leather-goods-repair"
  },
  "81149": {
    description: "See industry description for 811490.",
    industries: ["repair-and-maintenance"],
    name: "Other Personal and Household Goods Repair and Maintenance",
    slug: "other-personal-and-household-goods-repair-and-maintenance"
  },
  "81211": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) providing hair care services; (2) providing nail care services; and (3) providing facials or applying makeup (except permanent makeup). Illustrative Examples: Barber shops, Hair stylist shops, Beauty salons, Nail salons, Cosmetology salons.",
    industries: ["personal-care-services"],
    name: "Hair, Nail, and Skin Care Services",
    slug: "hair-nail-and-skin-care-services"
  },
  "81219": {
    description: "This industry comprises establishments primarily engaged in providing personal care services (except hair, nail, facial, or nonpermanent makeup services). Illustrative Examples: Day spas, Depilatory or electrolysis (i.e., hair removal) salons, Permanent makeup salons, Ear piercing services, Steam baths, Hair replacement (except by offices of physicians) or weaving services, Tanning salons, Tattoo parlors, Non-medical diet and weight reducing centers.",
    industries: ["personal-care-services"],
    name: "Other Personal Care Services",
    slug: "other-personal-care-services"
  },
  "81221": {
    description: "See industry description for 812210.",
    industries: ["personal-and-laundry-services"],
    name: "Funeral Homes and Funeral Services",
    slug: "funeral-homes-and-funeral-services"
  },
  "81222": {
    description: "See industry description for 812220.",
    industries: ["personal-and-laundry-services"],
    name: "Cemeteries and Crematories",
    slug: "cemeteries-and-crematories"
  },
  "81231": {
    description: "See industry description for 812310.",
    industries: ["laundry-and-drycleaning-services"],
    name: "Coin-Operated Laundries and Drycleaners",
    slug: "coin-operated-laundries-and-drycleaners"
  },
  "81232": {
    description: "See industry description for 812320.",
    industries: ["laundry-and-drycleaning-services"],
    name: "Drycleaning and Laundry Services (except Coin-Operated)",
    slug: "drycleaning-and-laundry-services-except-coin-operated"
  },
  "81233": {
    description: "This industry comprises establishments primarily engaged in supplying, on a rental or contract basis, laundered items, such as uniforms, gowns and coats, table linens, bed linens, towels, clean room apparel, and treated mops or shop towels.",
    industries: ["laundry-and-drycleaning-services"],
    name: "Linen and Uniform Supply",
    slug: "linen-and-uniform-supply"
  },
  "81291": {
    description: "See industry description for 812910.",
    industries: ["pet-services"],
    name: "Pet Care (except Veterinary) Services",
    slug: "pet-care-except-veterinary-services"
  },
  "81292": {
    description: "This industry comprises establishments primarily engaged in developing film and/or making photographic slides, prints, and enlargements. Cross-References.",
    industries: ["personal-and-laundry-services"],
    name: "Photofinishing",
    slug: "photofinishing"
  },
  "81293": {
    description: "See industry description for 812930.",
    industries: ["personal-and-laundry-services"],
    name: "Parking Lots and Garages",
    slug: "parking-lots-and-garages"
  },
  "81299": {
    description: "See industry description for 812990.",
    industries: ["personal-and-laundry-services"],
    name: "All Other Personal Services",
    slug: "all-other-personal-services"
  },
  "81311": {
    description: "See industry description for 813110.",
    industries: ["religious-institutions"],
    name: "Religious Organizations",
    slug: "religious-organizations"
  },
  "81321": {
    description: "This industry comprises (1) establishments known as grantmaking foundations or charitable trusts and (2) establishments primarily engaged in raising funds for a wide range of social welfare activities, such as health, educational, scientific, and cultural activities.",
    industries: ["philanthropic-fundraising-services"],
    name: "Grantmaking and Giving Services",
    slug: "grantmaking-and-giving-services"
  },
  "81331": {
    description: "This industry comprises establishments primarily engaged in promoting a particular cause or working for the realization of a specific social or political goal to benefit a broad or specific constituency.  These organizations may solicit contributions and offer memberships to support these goals. Illustrative Examples: Community action advocacy organizations, Firearms advocacy organizations, Conservation advocacy organizations, Human rights advocacy organizations, Environmental advocacy organizations, Wildlife preservation organizations, Cross-References.",
    industries: ["consumer-services"],
    name: "Social Advocacy Organizations",
    slug: "social-advocacy-organizations"
  },
  "81341": {
    description: "See industry description for 813410.",
    industries: ["civic-and-social-organizations"],
    name: "Civic and Social Organizations",
    slug: "civic-and-social-organizations"
  },
  "81391": {
    description: "See industry description for 813910.",
    industries: ["industry-associations"],
    name: "Business Associations",
    slug: "business-associations"
  },
  "81392": {
    description: "See industry description for 813920.",
    industries: ["professional-organizations"],
    name: "Professional Organizations",
    slug: "professional-organizations"
  },
  "81393": {
    description: "See industry description for 813930.",
    industries: ["political-organizations"],
    name: "Labor Unions and Similar Labor Organizations",
    slug: "labor-unions-and-similar-labor-organizations"
  },
  "81394": {
    description: "See industry description for 813940.",
    industries: ["political-organizations"],
    name: "Political Organizations",
    slug: "political-organizations"
  },
  "81399": {
    description: "See industry description for 813990.",
    industries: ["political-organizations"],
    name: "Other Similar Organizations (except Business, Professional, Labor, and Political Organizations)",
    slug: "other-similar-organizations-except-business-professional-labor-and-political-organizations"
  },
  "81411": {
    description: "See industry description for 814110.",
    industries: ["household-services"],
    name: "Private Households",
    slug: "private-households"
  },
  "92111": {
    description: "See industry description for 921110.",
    industries: ["executive-offices"],
    name: "Executive Offices",
    slug: "executive-offices"
  },
  "92112": {
    description: "See industry description for 921120.",
    industries: ["legislative-offices"],
    name: "Legislative Bodies",
    slug: "legislative-bodies"
  },
  "92113": {
    description: "See industry description for 921130.",
    industries: ["public-policy-offices"],
    name: "Public Finance Activities",
    slug: "public-finance-activities"
  },
  "92114": {
    description: "See industry description for 921140.",
    industries: ["public-policy-offices"],
    name: "Executive and Legislative Offices, Combined",
    slug: "executive-and-legislative-offices-combined"
  },
  "92115": {
    description: "See industry description for 921150.",
    industries: ["public-policy-offices"],
    name: "American Indian and Alaska Native Tribal Governments",
    slug: "american-indian-and-alaska-native-tribal-governments"
  },
  "92119": {
    description: "See industry description for 921190.",
    industries: ["public-policy-offices"],
    name: "Other General Government Support",
    slug: "other-general-government-support"
  },
  "92211": {
    description: "See industry description for 922110.",
    industries: ["administration-of-justice"],
    name: "Courts",
    slug: "courts"
  },
  "92212": {
    description: "See industry description for 922120.",
    industries: ["law-enforcement"],
    name: "Police Protection",
    slug: "police-protection"
  },
  "92213": {
    description: "See industry description for 922130.",
    industries: ["government-administration"],
    name: "Legal Counsel and Prosecution",
    slug: "legal-counsel-and-prosecution"
  },
  "92214": {
    description: "See industry description for 922140.",
    industries: ["government-administration"],
    name: "Correctional Institutions",
    slug: "correctional-institutions"
  },
  "92215": {
    description: "See industry description for 922150.",
    industries: ["government-administration"],
    name: "Parole Offices and Probation Offices",
    slug: "parole-offices-and-probation-offices"
  },
  "92216": {
    description: "See industry description for 922160.",
    industries: ["government-administration"],
    name: "Fire Protection",
    slug: "fire-protection"
  },
  "92219": {
    description: "See industry description for 922190.",
    industries: ["public-safety"],
    name: "Other Justice, Public Order, and Safety Activities",
    slug: "other-justice-public-order-and-safety-activities"
  },
  "92311": {
    description: "See industry description for 923110.",
    industries: ["education-administration-programs"],
    name: "Administration of Education Programs",
    slug: "administration-of-education-programs"
  },
  "92312": {
    description: "See industry description for 923120.",
    industries: ["public-health"],
    name: "Administration of Public Health Programs",
    slug: "administration-of-public-health-programs"
  },
  "92313": {
    description: "See industry description for 923130.",
    industries: ["public-assistance-programs"],
    name: "Administration of Human Resource Programs (except Education, Public Health, and Veterans' Affairs Programs)",
    slug: "administration-of-human-resource-programs-except-education-public-health-and-veterans-affairs-programs"
  },
  "92314": {
    description: "See industry description for 923140.",
    industries: ["health-and-human-services"],
    name: "Administration of Veterans' Affairs",
    slug: "administration-of-veterans-affairs"
  },
  "92411": {
    description: "See industry description for 924110.",
    industries: ["air-water-and-waste-program-management"],
    name: "Administration of Air and Water Resource and Solid Waste Management Programs",
    slug: "administration-of-air-and-water-resource-and-solid-waste-management-programs"
  },
  "92412": {
    description: "See industry description for 924120.",
    industries: ["conservation-programs"],
    name: "Administration of Conservation Programs",
    slug: "administration-of-conservation-programs"
  },
  "92511": {
    description: "See industry description for 925110.",
    industries: ["housing-and-community-development"],
    name: "Administration of Housing Programs",
    slug: "administration-of-housing-programs"
  },
  "92512": {
    description: "See industry description for 925120.",
    industries: ["community-development-and-urban-planning"],
    name: "Administration of Urban Planning and Community and Rural Development",
    slug: "administration-of-urban-planning-and-community-and-rural-development"
  },
  "92611": {
    description: "See industry description for 926110.",
    industries: ["economic-programs"],
    name: "Administration of General Economic Programs",
    slug: "administration-of-general-economic-programs"
  },
  "92612": {
    description: "See industry description for 926120.",
    industries: ["economic-programs"],
    name: "Regulation and Administration of Transportation Programs",
    slug: "regulation-and-administration-of-transportation-programs"
  },
  "92613": {
    description: "See industry description for 926130.",
    industries: ["economic-programs"],
    name: "Regulation and Administration of Communications, Electric, Gas, and Other Utilities",
    slug: "regulation-and-administration-of-communications-electric-gas-and-other-utilities"
  },
  "92614": {
    description: "See industry description for 926140.",
    industries: ["economic-programs"],
    name: "Regulation of Agricultural Marketing and Commodities",
    slug: "regulation-of-agricultural-marketing-and-commodities"
  },
  "92615": {
    description: "See industry description for 926150.",
    industries: ["economic-programs"],
    name: "Regulation, Licensing, and Inspection of Miscellaneous Commercial Sectors",
    slug: "regulation-licensing-and-inspection-of-miscellaneous-commercial-sectors"
  },
  "92711": {
    description: "See industry description for 927110.",
    industries: ["space-research-and-technology"],
    name: "Space Research and Technology",
    slug: "space-research-and-technology"
  },
  "92811": {
    description: "See industry description for 928110.",
    industries: ["armed-forces"],
    name: "National Security",
    slug: "national-security"
  },
  "92812": {
    description: "See industry description for 928120.",
    industries: ["international-affairs"],
    name: "International Affairs",
    slug: "international-affairs"
  },
  "111110": {
    description: "This industry comprises establishments primarily engaged in growing soybeans and/or producing soybean seeds. Cross-References.",
    industries: ["farming"],
    name: "Soybean Farming",
    slug: "soybean-farming"
  },
  "111120": {
    description: "This industry comprises establishments primarily engaged in growing fibrous oilseed producing plants and/or producing oilseed seeds, such as sunflower, safflower, flax, rape, canola, and sesame.",
    industries: ["farming"],
    name: "Oilseed (except Soybean) Farming",
    slug: "oilseed-except-soybean-farming"
  },
  "111130": {
    description: "This industry comprises establishments primarily engaged in growing dry peas, beans, and/or lentils. Cross-References.",
    industries: ["farming"],
    name: "Dry Pea and Bean Farming",
    slug: "dry-pea-and-bean-farming"
  },
  "111140": {
    description: "This industry comprises establishments primarily engaged in growing wheat and/or producing wheat seeds. Cross-References.",
    industries: ["farming"],
    name: "Wheat Farming",
    slug: "wheat-farming"
  },
  "111150": {
    description: "This industry comprises establishments primarily engaged in growing corn (except sweet corn) and/or producing corn seeds.",
    industries: ["farming"],
    name: "Corn Farming",
    slug: "corn-farming"
  },
  "111160": {
    description: "This industry comprises establishments primarily engaged in growing rice (except wild rice) and/or producing rice seeds.",
    industries: ["farming"],
    name: "Rice Farming",
    slug: "rice-farming"
  },
  "111191": {
    description: "This U.S. industry comprises establishments engaged in growing a combination of oilseed(s) and grain(s) with no one oilseed (or family of oilseeds) or grain (or family of grains) accounting for one-half of the establishment's agricultural production (value of crops for market).  These establishments may produce oilseed(s) and grain(s) seeds and/or grow oilseed(s) and grain(s). Cross-References.",
    industries: ["farming"],
    name: "Oilseed and Grain Combination Farming",
    slug: "oilseed-and-grain-combination-farming"
  },
  "111199": {
    description: "This U.S. industry comprises establishments primarily engaged in growing grains and/or producing grain(s) seeds (except wheat, corn, rice, and oilseed(s) and grain(s) combinations). Illustrative Examples: Barley farming, Sorghum farming, Oat farming, Wild rice farming, Rye farming.",
    industries: ["farming"],
    name: "All Other Grain Farming",
    slug: "all-other-grain-farming"
  },
  "111211": {
    description: "This U.S. industry comprises establishments primarily engaged in growing potatoes and/or producing seed potatoes. Cross-References.",
    industries: ["farming"],
    name: "Potato Farming",
    slug: "potato-farming"
  },
  "111219": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) growing melons and/or vegetables (except potatoes; dry peas; dry beans; field, silage, or seed corn; and sugar beets); (2) producing vegetable and/or melon seeds; and (3) growing vegetable and/or melon bedding plants. Illustrative Examples: Carrot farming, Squash farming, Green bean farming, Tomato farming, Watermelon farming , Melon farming (e.g., cantaloupe, casaba, honeydew, watermelon), Vegetable (except potato) farming, Pepper farming (e.g., bell, chili, green, red, sweet peppers).",
    industries: ["farming"],
    name: "Other Vegetable (except Potato) and Melon Farming",
    slug: "other-vegetable-except-potato-and-melon-farming"
  },
  "111310": {
    description: "This industry comprises establishments primarily engaged in growing oranges.",
    industries: ["farming"],
    name: "Orange Groves",
    slug: "orange-groves"
  },
  "111320": {
    description: "This industry comprises establishments primarily engaged in growing citrus fruits (except oranges). Illustrative Examples: Citrus groves (except oranges), Mandarin groves, Grapefruit groves, Tangelo groves, Lemon groves, Tangerine groves, Cross-References.",
    industries: ["farming"],
    name: "Citrus (except Orange) Groves",
    slug: "citrus-except-orange-groves"
  },
  "111331": {
    description: "This U.S. industry comprises establishments primarily engaged in growing apples.",
    industries: ["farming"],
    name: "Apple Orchards",
    slug: "apple-orchards"
  },
  "111332": {
    description: "This U.S. industry comprises establishments primarily engaged in growing grapes and/or growing grapes to sun dry into raisins.",
    industries: ["farming"],
    name: "Grape Vineyards",
    slug: "grape-vineyards"
  },
  "111333": {
    description: "This U.S. industry comprises establishments primarily engaged in growing strawberries. Cross-References.",
    industries: ["farming"],
    name: "Strawberry Farming",
    slug: "strawberry-farming"
  },
  "111334": {
    description: "This U.S. industry comprises establishments primarily engaged in growing berries. Illustrative Examples: Berry (except strawberries) farming, Cranberry farming, Blackberry farming, Currant farming, Blueberry farming, Raspberry farming.",
    industries: ["farming"],
    name: "Berry (except Strawberry) Farming",
    slug: "berry-except-strawberry-farming"
  },
  "111335": {
    description: "This U.S. industry comprises establishments primarily engaged in growing tree nuts. Illustrative Examples: Almond farming, Pistachio farming, Filbert farming, Tree nut farming, Macadamia farming, Walnut farming, Pecan farming.",
    industries: ["farming"],
    name: "Tree Nut Farming",
    slug: "tree-nut-farming"
  },
  "111336": {
    description: "This U.S. industry comprises establishments primarily engaged in growing a combination of fruit(s) and tree nut(s) with no one fruit (or family of fruit) or family of tree nuts accounting for one-half of the establishment's agricultural production (i.e., value of crops for market). Cross-References.",
    industries: ["farming"],
    name: "Fruit and Tree Nut Combination Farming",
    slug: "fruit-and-tree-nut-combination-farming"
  },
  "111339": {
    description: "This U.S. industry comprises establishments primarily engaged in growing noncitrus fruits (except apples, grapes, berries, and fruit(s) and tree nut(s) combinations). Illustrative Examples: Apricot farming, Fig farming, Banana farming, Noncitrus fruit farming, Cherry farming, Peach farming, Coffee farming, Pineapple farming, Date farming, Prune farming.",
    industries: ["farming"],
    name: "Other Noncitrus Fruit Farming",
    slug: "other-noncitrus-fruit-farming"
  },
  "111411": {
    description: "This U.S. industry comprises establishments primarily engaged in growing mushrooms under cover in mines underground, or in other controlled environments.",
    industries: ["farming"],
    name: "Mushroom Production",
    slug: "mushroom-production"
  },
  "111419": {
    description: "This U.S. industry comprises establishments primarily engaged in growing food crops (except mushrooms) under glass or protective cover. Illustrative Examples: Alfalfa sprout farming, grown under cover, Melon farming, grown under cover, Vegetable farming, grown under cover, Hydroponic crop farming, Fruit farming, grown under cover, Cross-References.",
    industries: ["farming"],
    name: "Other Food Crops Grown Under Cover",
    slug: "other-food-crops-grown-under-cover"
  },
  "111421": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) growing nursery products, nursery stock, shrubbery, bulbs, fruit stock, sod, and so forth, under cover or in open fields and/or (2) growing short rotation woody trees with a growth and harvest cycle of 10 years or less for pulp or tree stock.",
    industries: ["farming"],
    name: "Nursery and Tree Production",
    slug: "nursery-and-tree-production"
  },
  "111422": {
    description: "This U.S. industry comprises establishments primarily engaged in growing and/or producing floriculture products (e.g., cut flowers and roses, cut cultivated greens, potted flowering and foliage plants, and flower seeds) under cover and in open fields. Cross-References.",
    industries: ["farming"],
    name: "Floriculture Production",
    slug: "floriculture-production"
  },
  "111910": {
    description: "This industry comprises establishments primarily engaged in growing tobacco.",
    industries: ["farming"],
    name: "Tobacco Farming",
    slug: "tobacco-farming"
  },
  "111920": {
    description: "This industry comprises establishments primarily engaged in growing cotton. Cross-References.",
    industries: ["farming"],
    name: "Cotton Farming",
    slug: "cotton-farming"
  },
  "111930": {
    description: "This industry comprises establishments primarily engaged in growing sugarcane.",
    industries: ["farming"],
    name: "Sugarcane Farming",
    slug: "sugarcane-farming"
  },
  "111940": {
    description: "This industry comprises establishments primarily engaged in growing hay, alfalfa, clover, and/or mixed hay.",
    industries: ["farming"],
    name: "Hay Farming",
    slug: "hay-farming"
  },
  "111991": {
    description: "This U.S. industry comprises establishments primarily engaged in growing sugar beets. Cross-References.",
    industries: ["farming"],
    name: "Sugar Beet Farming",
    slug: "sugar-beet-farming"
  },
  "111992": {
    description: "This U.S. industry comprises establishments primarily engaged in growing peanuts.",
    industries: ["farming"],
    name: "Peanut Farming",
    slug: "peanut-farming"
  },
  "111998": {
    description: "This U.S. industry comprises establishments primarily engaged in one of the following: (1) growing crops (except oilseeds and/or grains; vegetables and/or melons; fruits and/or tree nuts; greenhouse, nursery, and/or floriculture products; tobacco; cotton; sugarcane; hay; sugar beets; or peanuts); (2) growing a combination of crops (except a combination of oilseed(s) and grain(s); and a combination of fruit(s) and tree nut(s)) with no one crop or family of crops accounting for one-half of the establishment's agricultural production (i.e., value of crops for market); or (3) gathering tea or maple sap. Illustrative Examples: Agave farming, Mint farming, General combination crop farming (except oilseed and grain; vegetables and melons; fruit and tree nut combinations), Hay seed farming, Grass seed farming, Hop farming, Spice farming.",
    industries: ["farming"],
    name: "All Other Miscellaneous Crop Farming",
    slug: "all-other-miscellaneous-crop-farming"
  },
  "112111": {
    description: "This U.S. industry comprises establishments primarily engaged in raising cattle (including cattle for dairy herd replacements). Cross-References.",
    industries: ["ranching"],
    name: "Beef Cattle Ranching and Farming",
    slug: "beef-cattle-ranching-and-farming"
  },
  "112112": {
    description: "This U.S. industry comprises establishments primarily engaged in feeding cattle for fattening. Cross-References.",
    industries: ["ranching"],
    name: "Cattle Feedlots",
    slug: "cattle-feedlots"
  },
  "112120": {
    description: "This industry comprises establishments primarily engaged in milking dairy cattle.",
    industries: ["ranching"],
    name: "Dairy Cattle and Milk Production",
    slug: "dairy-cattle-and-milk-production"
  },
  "112130": {
    description: "This industry comprises establishments primarily engaged in raising cattle for both milking and meat production.",
    industries: ["ranching"],
    name: "Dual-Purpose Cattle Ranching and Farming",
    slug: "dual-purpose-cattle-ranching-and-farming"
  },
  "112210": {
    description: "This industry comprises establishments primarily engaged in raising hogs and pigs.  These establishments may include farming activities, such as breeding, farrowing, and the raising of weanling pigs, feeder pigs, or market size hogs. Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Hog and Pig Farming",
    slug: "hog-and-pig-farming"
  },
  "112310": {
    description: "This industry comprises establishments primarily engaged in raising chickens for egg production.  The eggs produced may be for use as table eggs or hatching eggs. Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Chicken Egg Production",
    slug: "chicken-egg-production"
  },
  "112320": {
    description: "This industry comprises establishments primarily engaged in raising broilers, fryers, roasters, and other meat type chickens. Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Broilers and Other Meat Type Chicken Production",
    slug: "broilers-and-other-meat-type-chicken-production"
  },
  "112330": {
    description: "This industry comprises establishments primarily engaged in raising turkeys for meat or egg production.",
    industries: ["ranching-and-fisheries"],
    name: "Turkey Production",
    slug: "turkey-production"
  },
  "112340": {
    description: "This industry comprises establishments primarily engaged in hatching poultry of any kind.",
    industries: ["ranching-and-fisheries"],
    name: "Poultry Hatcheries",
    slug: "poultry-hatcheries"
  },
  "112390": {
    description: "This industry comprises establishments primarily engaged in raising poultry (except chickens for meat or egg production and turkeys). Illustrative Examples: Duck production, Ostrich production, Emu production, Pheasant production, Geese production, Quail production.",
    industries: ["ranching-and-fisheries"],
    name: "Other Poultry Production",
    slug: "other-poultry-production"
  },
  "112410": {
    description: "This industry comprises establishments primarily engaged in raising sheep and lambs, or feeding lambs for fattening.  The sheep or lambs may be raised for sale or wool production. Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Sheep Farming",
    slug: "sheep-farming"
  },
  "112420": {
    description: "This industry comprises establishments primarily engaged in raising goats.",
    industries: ["ranching-and-fisheries"],
    name: "Goat Farming",
    slug: "goat-farming"
  },
  "112511": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) farm raising finfish (e.g., catfish, trout, goldfish, tropical fish, minnows) and/or (2) hatching fish of any kind. Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Finfish Farming and Fish Hatcheries",
    slug: "finfish-farming-and-fish-hatcheries"
  },
  "112512": {
    description: "This U.S. industry comprises establishments primarily engaged in farm raising shellfish (e.g., crayfish, shrimp, oysters, clams, mollusks). Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Shellfish Farming",
    slug: "shellfish-farming"
  },
  "112519": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) farm raising of aquatic animals (except finfish and shellfish) and/or (2) farm raising of aquatic plants.  Alligator, algae, frog, seaweed, or turtle production is included in this industry.",
    industries: ["ranching-and-fisheries"],
    name: "Other Aquaculture",
    slug: "other-aquaculture"
  },
  "112910": {
    description: "This industry comprises establishments primarily engaged in raising bees.  These establishments may collect and gather honey; and/or sell queen bees, packages of bees, royal jelly, bees' wax, propolis, venom, pollen, and/or other bee products.",
    industries: ["ranching-and-fisheries"],
    name: "Apiculture",
    slug: "apiculture"
  },
  "112920": {
    description: "This industry comprises establishments primarily engaged in raising horses, mules, donkeys, and other equines. Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Horses and Other Equine Production",
    slug: "horses-and-other-equine-production"
  },
  "112930": {
    description: "This industry comprises establishments primarily engaged in raising fur-bearing animals including rabbits.  These animals may be raised for sale or for their pelt production. Cross-References.",
    industries: ["ranching-and-fisheries"],
    name: "Fur-Bearing Animal and Rabbit Production",
    slug: "fur-bearing-animal-and-rabbit-production"
  },
  "112990": {
    description: "This industry comprises establishments primarily engaged in (1) raising animals (except cattle, hogs and pigs, poultry, sheep and goats, aquaculture, apiculture, horses and other equines; and fur-bearing animals including rabbits) or (2) raising a combination of animals, with no one animal or family of animals accounting for one-half of the establishment's agricultural production (i.e., value of animals for market). Illustrative Examples: Bird production (e.g., canaries, parakeets, parrots), Laboratory animal production (e.g., rats, mice, guinea pigs), Deer production, Combination animal farming (except dairy, poultry), Companion animals production (e.g., cats, dogs), Worm production, Llama production.",
    industries: ["ranching-and-fisheries"],
    name: "All Other Animal Production",
    slug: "all-other-animal-production"
  },
  "113110": {
    description: "This industry comprises establishments primarily engaged in the operation of timber tracts for the purpose of selling standing timber.",
    industries: ["forestry-and-logging"],
    name: "Timber Tract Operations",
    slug: "timber-tract-operations"
  },
  "113210": {
    description: "This industry comprises establishments primarily engaged in (1) growing trees for reforestation and/or (2) gathering forest products, such as gums, barks, balsam needles, rhizomes, fibers, Spanish moss, ginseng, and truffles.",
    industries: ["forestry-and-logging"],
    name: "Forest Nurseries and Gathering of Forest Products",
    slug: "forest-nurseries-and-gathering-of-forest-products"
  },
  "113310": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) cutting timber; (2) cutting and transporting timber; and (3) producing wood chips in the field.",
    industries: ["forestry-and-logging"],
    name: "Logging",
    slug: "logging"
  },
  "114111": {
    description: "This U.S. industry comprises establishments primarily engaged in the commercial catching or taking of finfish (e.g., bluefish, salmon, trout, tuna) from their natural habitat.",
    industries: ["fisheries"],
    name: "Finfish Fishing",
    slug: "finfish-fishing"
  },
  "114112": {
    description: "This U.S. industry comprises establishments primarily engaged in the commercial catching or taking of shellfish (e.g., clams, crabs, lobsters, mussels, oysters, sea urchins, shrimp) from their natural habitat. Cross-References.",
    industries: ["fisheries"],
    name: "Shellfish Fishing",
    slug: "shellfish-fishing"
  },
  "114119": {
    description: "This U.S. industry comprises establishments primarily engaged in the commercial catching or taking of marine animals (except finfish and shellfish).",
    industries: ["fisheries"],
    name: "Other Marine Fishing",
    slug: "other-marine-fishing"
  },
  "114210": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) commercial hunting and trapping; (2) operating commercial game preserves, such as game retreats; and (3) operating hunting preserves.",
    industries: ["farming-ranching-forestry"],
    name: "Hunting and Trapping",
    slug: "hunting-and-trapping"
  },
  "115111": {
    description: "This U.S. industry comprises establishments primarily engaged in ginning cotton.",
    industries: ["farming-ranching-forestry"],
    name: "Cotton Ginning",
    slug: "cotton-ginning"
  },
  "115112": {
    description: "This U.S. industry comprises establishments primarily engaged in performing a soil preparation activity or crop production service, such as plowing, fertilizing, seed bed preparation, planting, cultivating, and crop protecting services.",
    industries: ["farming-ranching-forestry"],
    name: "Soil Preparation, Planting, and Cultivating",
    slug: "soil-preparation-planting-and-cultivating"
  },
  "115113": {
    description: "This U.S. industry comprises establishments primarily engaged in mechanical harvesting, picking, and combining of crops, and related activities.  The machinery used is provided by the servicing establishment.",
    industries: ["farming-ranching-forestry"],
    name: "Crop Harvesting, Primarily by Machine",
    slug: "crop-harvesting-primarily-by-machine"
  },
  "115114": {
    description: "This U.S. industry comprises establishments primarily engaged in performing services on crops, subsequent to their harvest, with the intent of preparing them for market or further processing.  These establishments provide postharvest activities, such as crop cleaning, sun drying, shelling, fumigating, curing, sorting, grading, packing, and cooling.",
    industries: ["farming-ranching-forestry"],
    name: "Postharvest Crop Activities (except Cotton Ginning)",
    slug: "postharvest-crop-activities-except-cotton-ginning"
  },
  "115115": {
    description: "This U.S. industry comprises establishments primarily engaged in supplying labor for agricultural production or harvesting.",
    industries: ["farming-ranching-forestry"],
    name: "Farm Labor Contractors and Crew Leaders",
    slug: "farm-labor-contractors-and-crew-leaders"
  },
  "115116": {
    description: "This U.S. industry comprises establishments primarily engaged in providing farm management services on a contract or fee basis usually to citrus groves, orchards, or vineyards.  These establishments always provide management and may arrange or contract for the partial or the complete operations of the farm establishment(s) they manage.  Operational activities may include cultivating, harvesting, and/or other specialized agricultural support activities. Cross-References.",
    industries: ["farming-ranching-forestry"],
    name: "Farm Management Services",
    slug: "farm-management-services"
  },
  "115210": {
    description: "This industry comprises establishments primarily engaged in performing support activities related to raising livestock (e.g., cattle, goats, hogs, horses, poultry, sheep).  These establishments may perform one or more of the following: (1) breeding services for animals, including companion animals (e.g., cats, dogs, pet birds); (2) pedigree record services; (3) boarding horses; (4) dairy herd improvement activities; (5) livestock spraying; and (6) sheep dipping and shearing. Cross-References.",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Animal Production",
    slug: "support-activities-for-animal-production"
  },
  "115310": {
    description: "This industry comprises establishments primarily engaged in performing particular support activities related to timber production, wood technology, forestry economics and marketing, and forest protection.  These establishments may provide support activities for forestry, such as estimating timber, forest firefighting, forest pest control, treating burned forests from the air for reforestation or on an emergency basis, and consulting on wood attributes and reforestation.",
    industries: ["farming-ranching-forestry"],
    name: "Support Activities for Forestry",
    slug: "support-activities-for-forestry"
  },
  "211120": {
    description: "This industry comprises establishments primarily engaged in (1) the exploration, development, and/or the production of petroleum from wells in which the hydrocarbons will initially flow or can be produced using normal or enhanced drilling and extraction techniques or (2) the production of crude petroleum from surface shales or tar sands or from reservoirs in which the hydrocarbons are semisolids.  Establishments in this industry operate oil wells on their own account or for others on a contract or fee basis.",
    industries: ["oil-extraction"],
    name: "Crude Petroleum Extraction",
    slug: "crude-petroleum-extraction"
  },
  "211130": {
    description: "This industry comprises establishments primarily engaged in (1) the exploration, development, and/or the production of natural gas from wells in which the hydrocarbons will initially flow or can be produced using normal or enhanced drilling and extraction techniques or (2) the recovery of liquid hydrocarbons from oil and gas field gases.  Establishments primarily engaged in sulfur recovery from natural gas are included in this industry.",
    industries: ["natural-gas-extraction"],
    name: "Natural Gas Extraction",
    slug: "natural-gas-extraction"
  },
  "212114": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) surface mining of bituminous coal, lignite, and anthracite coal; (2) developing bituminous coal, lignite, and anthracite coal surface mine sites; (3) surface mining and beneficiating (e.g., cleaning, washing, screening, and sizing) of bituminous coal, lignite, and anthracite coal; or (4) beneficiating (e.g., cleaning, washing, screening, and sizing), but not mining, bituminous coal, lignite, and anthracite coal.",
    industries: ["coal-mining"],
    name: "Surface Coal Mining",
    slug: "surface-coal-mining"
  },
  "212115": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) underground mining of bituminous and anthracite coal; (2) developing bituminous and anthracite coal underground mine sites; and (3) underground mining and beneficiating (e.g., cleaning, washing, screening, and sizing) of bituminous and anthracite coal.",
    industries: ["coal-mining"],
    name: "Underground Coal Mining",
    slug: "underground-coal-mining"
  },
  "212210": {
    description: "This industry comprises establishments primarily engaged in (1) developing mine sites, mining, and/or beneficiating (i.e., preparing) iron ores and manganiferous ores valued chiefly for their iron content and/or (2) producing sinter iron ore (except iron ore produced in iron and steel mills) and other iron ore agglomerates. Cross-References.",
    industries: ["metal-ore-mining"],
    name: "Iron Ore Mining",
    slug: "iron-ore-mining"
  },
  "212220": {
    description: "This industry comprises establishments primarily engaged in developing the mine site, mining, and/or beneficiating (i.e., preparing) ores valued chiefly for their gold and/or silver content.  Establishments primarily engaged in the transformation of the gold and silver into bullion or dore bar in combination with mining activities are included in this industry. Cross-References.",
    industries: ["metal-ore-mining"],
    name: "Gold Ore and Silver Ore Mining",
    slug: "gold-ore-and-silver-ore-mining"
  },
  "212230": {
    description: "This industry comprises establishments primarily engaged in developing the mine site, mining, and/or beneficiating (i.e., preparing) ores valued chiefly for their copper, nickel, lead, or zinc content.  Beneficiating includes the transformation of ores into concentrates.  Establishments primarily engaged in recovering copper concentrates by the precipitation, leaching, or electrowinning of copper ore are included in this industry.",
    industries: ["metal-ore-mining"],
    name: "Copper, Nickel, Lead, and Zinc Mining",
    slug: "copper-nickel-lead-and-zinc-mining"
  },
  "212290": {
    description: "This industry comprises establishments primarily engaged in developing the mine site, mining, and/or beneficiating (i.e., preparing) metal ores (except iron and manganiferous ores valued for their iron content, gold ore, silver ore, copper, nickel, lead, and zinc ore). Illustrative Examples: Antimony ores mining and/or beneficiating, Tantalum ores mining and/or beneficiating, Columbite ores mining and/or beneficiating, Tungsten ores mining and/or beneficiating, Ilmenite ores mining and/or beneficiating, Uranium-radium-vanadium ores mining and/or beneficiating, Molybdenum ores mining and/or beneficiating.",
    industries: ["metal-ore-mining"],
    name: "Other Metal Ore Mining",
    slug: "other-metal-ore-mining"
  },
  "212311": {
    description: "This U.S. industry comprises establishments primarily engaged in developing the mine site and/or mining or quarrying dimension stone (i.e., rough blocks and/or slabs of stone). Cross-References.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Dimension Stone Mining and Quarrying",
    slug: "dimension-stone-mining-and-quarrying"
  },
  "212312": {
    description: "This U.S. industry comprises (1) establishments primarily engaged in developing the mine site, mining or quarrying crushed and broken limestone (including related rocks, such as dolomite, cement rock, marl, travertine, and calcareous tufa) and (2) preparation plants primarily engaged in beneficiating limestone (e.g., grinding or pulverizing).",
    industries: ["nonmetallic-mineral-mining"],
    name: "Crushed and Broken Limestone Mining and Quarrying",
    slug: "crushed-and-broken-limestone-mining-and-quarrying"
  },
  "212313": {
    description: "This U.S. industry comprises (1) establishments primarily engaged in developing the mine site, and/or mining or quarrying crushed and broken granite (including related rocks, such as gneiss, syenite (except nepheline), and diorite) and (2) preparation plants primarily engaged in beneficiating granite (e.g., grinding or pulverizing).",
    industries: ["nonmetallic-mineral-mining"],
    name: "Crushed and Broken Granite Mining and Quarrying",
    slug: "crushed-and-broken-granite-mining-and-quarrying"
  },
  "212319": {
    description: "This U.S. industry comprises: (1) establishments primarily engaged in developing the mine site and/or mining or quarrying crushed and broken stone (except limestone and granite); (2) preparation plants primarily engaged in beneficiating (e.g., grinding and pulverizing) stone (except limestone and granite); and (3) establishments primarily engaged in mining or quarrying bituminous limestone and bituminous sandstone. Illustrative Examples: Bituminous limestone mining and/or beneficiating, Marble crushed and broken stone mining and/or beneficiating, Bituminous sandstone mining and/or beneficiating, Sandstone crushed and broken stone mining and/or beneficiating.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Other Crushed and Broken Stone Mining and Quarrying",
    slug: "other-crushed-and-broken-stone-mining-and-quarrying"
  },
  "212321": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) operating commercial grade (i.e., construction) sand and gravel pits; (2) dredging for commercial grade sand and gravel; and (3) washing, screening, or otherwise preparing commercial grade sand and gravel.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Construction Sand and Gravel Mining",
    slug: "construction-sand-and-gravel-mining"
  },
  "212322": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) operating industrial grade sand pits; (2) dredging for industrial grade sand; and (3) washing, screening, or otherwise preparing industrial grade sand.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Industrial Sand Mining",
    slug: "industrial-sand-mining"
  },
  "212323": {
    description: "This U.S. industry comprises (1) establishments primarily engaged in developing the mine site and/or mining clay (e.g., china clay, paper clay and slip clay) or ceramic and refractory minerals and (2) establishments primarily engaged in beneficiating (i.e., preparing) clay or ceramic and refractory minerals. Illustrative Examples: Bentonite mining and/or beneficiating, Fuller's earth mining and/or beneficiating, Common clay mining and/or beneficiating, Kaolin mining and/or beneficiating, Feldspar mining and/or beneficiating, Ball clay mining and/or beneficiating, Fire clay mining and/or beneficiating, Shale (except oil shale) mining and/or beneficiating.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Kaolin, Clay, and Ceramic and Refractory Minerals Mining",
    slug: "kaolin-clay-and-ceramic-and-refractory-minerals-mining"
  },
  "212390": {
    description: "This industry comprises establishments primarily engaged in developing the mine site, mining, and/or milling or otherwise beneficiating (i.e., preparing) nonmetallic minerals (except coal, stone, sand, gravel, clay, and ceramic and refractory minerals).  Drylake brine operations are included in this industry, as well as establishments engaged in producing the specified minerals from underground and open pit mines. Illustrative Examples: Barite mining and/or beneficiating, Phosphate rock mining and/or beneficiating, Borate, natural, mining and/or beneficiating, Potash mining and/or beneficiating, Peat mining and/or beneficiating, Rock salt mining and/or beneficiating.",
    industries: ["nonmetallic-mineral-mining"],
    name: "Other Nonmetallic Mineral Mining and Quarrying",
    slug: "other-nonmetallic-mineral-mining-and-quarrying"
  },
  "213111": {
    description: "This U.S. industry comprises establishments primarily engaged in drilling oil and gas wells for others on a contract or fee basis.  This industry includes contractors that specialize in spudding in, drilling in, redrilling, and directional drilling.",
    industries: ["oil-gas-and-mining"],
    name: "Drilling Oil and Gas Wells",
    slug: "drilling-oil-and-gas-wells"
  },
  "213112": {
    description: "This U.S. industry comprises establishments primarily engaged in performing support activities, on a contract or fee basis, for oil and gas operations (except geophysical surveying and mapping, site preparation, construction, and transportation activities).  Services included are exploration; excavating slush pits and cellars, well surveying; running, cutting, and pulling casings, tubes, and rods; cementing wells, shooting wells; perforating well casings; acidizing and chemically treating wells; and cleaning out, bailing, and swabbing wells.",
    industries: ["oil-gas-and-mining"],
    name: "Support Activities for Oil and Gas Operations",
    slug: "support-activities-for-oil-and-gas-operations"
  },
  "213113": {
    description: "This U.S. industry comprises establishments primarily engaged in providing support activities for coal mining (except geophysical surveying and mapping, site preparation, construction, and transportation activities) on a contract or fee basis.  Exploration for coal is included in this industry.  Exploration services include traditional prospecting methods, such as taking core samples and making geological observations at prospective sites.",
    industries: ["oil-gas-and-mining"],
    name: "Support Activities for Coal Mining",
    slug: "support-activities-for-coal-mining"
  },
  "213114": {
    description: "This U.S. industry comprises establishments primarily engaged in providing support activities (except geophysical surveying and mapping, site preparation, construction, and transportation activities), on a contract or fee basis, for the mining and quarrying of metallic minerals and for the extraction of metal ores.  Exploration for these minerals is included in this industry.  Exploration services include traditional prospecting methods, such as taking core samples and making geological observations at prospective sites.",
    industries: ["oil-gas-and-mining"],
    name: "Support Activities for Metal Mining",
    slug: "support-activities-for-metal-mining"
  },
  "213115": {
    description: "This U.S. industry comprises establishments primarily engaged in providing support activities, on a contract or fee basis, for the mining and quarrying of nonmetallic minerals (except fuel) and for the extraction of nonmetallic minerals (except geophysical surveying and mapping, site preparation, construction, and transportation activities).  Exploration for these minerals is included in this industry.  Exploration services include traditional prospecting methods, such as taking core samples and making geological observations at prospective sites.",
    industries: ["oil-gas-and-mining"],
    name: "Support Activities for Nonmetallic Minerals (except Fuels) Mining",
    slug: "support-activities-for-nonmetallic-minerals-except-fuels-mining"
  },
  "221111": {
    description: "This U.S. industry comprises establishments primarily engaged in operating hydroelectric power generation facilities.  These facilities use water power to drive a turbine and produce electric energy.  The electric energy produced in these establishments is provided to electric power transmission systems or to electric power distribution systems.",
    industries: ["hydroelectric-power-generation"],
    name: "Hydroelectric Power Generation",
    slug: "hydroelectric-power-generation"
  },
  "221112": {
    description: "This U.S. industry comprises establishments primarily engaged in operating fossil fuel powered electric power generation facilities.  These facilities use fossil fuels, such as coal, oil, or gas, in internal combustion or combustion turbine conventional steam process to produce electric energy.  The electric energy produced in these establishments is provided to electric power transmission systems or to electric power distribution systems.",
    industries: ["fossil-fuel-electric-power-generation"],
    name: "Fossil Fuel Electric Power Generation",
    slug: "fossil-fuel-electric-power-generation"
  },
  "221113": {
    description: "This U.S. industry comprises establishments primarily engaged in operating nuclear electric power generation facilities.  These facilities use nuclear power to produce electric energy.  The electric energy produced in these establishments is provided to electric power transmission systems or to electric power distribution systems.",
    industries: ["nuclear-electric-power-generation"],
    name: "Nuclear Electric Power Generation",
    slug: "nuclear-electric-power-generation"
  },
  "221114": {
    description: "This U.S. industry comprises establishments primarily engaged in operating solar electric power generation facilities.  These facilities use energy from the sun to produce electric energy.  The electric energy produced in these establishments is provided to electric power transmission systems or to electric power distribution systems. Cross-References.",
    industries: ["solar-electric-power-generation"],
    name: "Solar Electric Power Generation",
    slug: "solar-electric-power-generation"
  },
  "221115": {
    description: "This U.S. industry comprises establishments primarily engaged in operating wind electric power generation facilities.  These facilities use wind power to drive a turbine and produce electric energy.  The electric energy produced in these establishments is provided to electric power transmission systems or to electric power distribution systems.",
    industries: ["wind-electric-power-generation"],
    name: "Wind Electric Power Generation",
    slug: "wind-electric-power-generation"
  },
  "221116": {
    description: "This U.S. industry comprises establishments primarily engaged in operating geothermal electric power generation facilities.  These facilities use heat derived from the Earth to produce electric energy.  The electric energy produced in these establishments is provided to electric power transmission systems or to electric power distribution systems.",
    industries: ["geothermal-electric-power-generation"],
    name: "Geothermal Electric Power Generation",
    slug: "geothermal-electric-power-generation"
  },
  "221117": {
    description: "This U.S. industry comprises establishments primarily engaged in operating biomass electric power generation facilities.  These facilities use biomass (e.g., wood, waste, alcohol fuels) to produce electric energy.  The electric energy produced in these establishments is provided to electric power transmission systems or to electric power distribution systems. Cross-References.",
    industries: ["biomass-electric-power-generation"],
    name: "Biomass Electric Power Generation",
    slug: "biomass-electric-power-generation"
  },
  "221118": {
    description: "This U.S. industry comprises establishments primarily engaged in operating electric power generation facilities (except hydroelectric, fossil fuel, nuclear, solar, wind, geothermal, biomass).  These facilities convert other forms of energy, such as tidal power, into electric energy.  The electric energy produced in these establishments is provided to electric power transmission systems or to electric power distribution systems.",
    industries: ["electric-power-generation"],
    name: "Other Electric Power Generation",
    slug: "other-electric-power-generation"
  },
  "221121": {
    description: "This U.S. industry comprises establishments primarily engaged in operating electric power transmission systems and/or controlling (i.e., regulating voltages) the transmission of electricity from the generating source to distribution centers or other electric utilities.  The transmission system includes lines and transformer stations.",
    industries: ["electric-power-transmission-control-and-distribution"],
    name: "Electric Bulk Power Transmission and Control",
    slug: "electric-bulk-power-transmission-and-control"
  },
  "221122": {
    description: "This U.S. industry comprises electric power establishments primarily engaged in either (1) operating electric power distribution systems (i.e., consisting of lines, poles, meters, and wiring) or (2) operating as electric power brokers or agents that arrange the sale of electricity via power distribution systems operated by others.",
    industries: ["electric-power-transmission-control-and-distribution"],
    name: "Electric Power Distribution",
    slug: "electric-power-distribution"
  },
  "221210": {
    description: "This industry comprises: (1) establishments primarily engaged in operating gas distribution systems (e.g., mains, meters); (2) establishments known as gas marketers that buy gas from the well and sell it to a distribution system; (3) establishments known as gas brokers or agents that arrange the sale of gas over gas distribution systems operated by others; and (4) establishments primarily engaged in transmitting and distributing gas to final consumers.",
    industries: ["natural-gas-distribution"],
    name: "Natural Gas Distribution",
    slug: "natural-gas-distribution"
  },
  "221310": {
    description: "This industry comprises establishments primarily engaged in operating water treatment plants and/or operating water supply systems.  The water supply system may include pumping stations, aqueducts, and/or distribution mains.  The water may be used for drinking, irrigation, or other uses. Cross-References.",
    industries: ["water-supply-and-irrigation-systems"],
    name: "Water Supply and Irrigation Systems",
    slug: "water-supply-and-irrigation-systems"
  },
  "221320": {
    description: "This industry comprises establishments primarily engaged in operating sewer systems or sewage treatment facilities that collect, treat, and dispose of waste.",
    industries: ["water-waste-steam-and-air-conditioning-services"],
    name: "Sewage Treatment Facilities",
    slug: "sewage-treatment-facilities"
  },
  "221330": {
    description: "This industry comprises establishments primarily engaged in providing steam, heated air, or cooled air.  The steam distribution may be through mains.",
    industries: ["steam-and-air-conditioning-supply"],
    name: "Steam and Air-Conditioning Supply",
    slug: "steam-and-air-conditioning-supply"
  },
  "236115": {
    description: "This U.S. industry comprises general contractor establishments primarily responsible for the entire construction of new single-family housing, such as single-family detached houses and town houses or row houses where each housing unit (1) is separated from its neighbors by a ground-to-roof wall and (2) has no housing units constructed above or below.  This industry includes general contractors responsible for the on-site assembly of modular and prefabricated houses.  Single-family housing design-build firms and single-family construction management firms acting as general contractors are included in this industry.",
    industries: ["residential-building-construction"],
    name: "New Single-Family Housing Construction (except For-Sale Builders)",
    slug: "new-single-family-housing-construction-except-for-sale-builders"
  },
  "236116": {
    description: "This U.S. industry comprises general contractor establishments primarily responsible for the construction of new multifamily residential housing units (e.g., high-rise, garden, town house apartments, and condominiums where each unit is not separated from its neighbors by a ground-to-roof wall).  Multifamily design-build firms and multifamily housing construction management firms acting as general contractors are included in this industry.",
    industries: ["residential-building-construction"],
    name: "New Multifamily Housing Construction (except For-Sale Builders)",
    slug: "new-multifamily-housing-construction-except-for-sale-builders"
  },
  "236117": {
    description: "This U.S. industry comprises establishments primarily engaged in building new homes on land that is owned or controlled by the builder rather than the homebuyer or investor.  The land is included with the sale of the home.  Establishments in this industry build single-family and/or multifamily homes.  These establishments are often referred to as merchant builders, but are also known as production or for-sale builders.",
    industries: ["residential-building-construction"],
    name: "New Housing For-Sale Builders",
    slug: "new-housing-for-sale-builders"
  },
  "236118": {
    description: "This U.S. industry comprises establishments primarily responsible for the remodeling construction (including additions, alterations, reconstruction, maintenance, and repairs) of houses and other residential buildings, single-family and multifamily.  Included in this industry are remodeling general contractors, for-sale remodelers, remodeling design-build firms, and remodeling project construction management firms.",
    industries: ["residential-building-construction"],
    name: "Residential Remodelers",
    slug: "residential-remodelers"
  },
  "236210": {
    description: "This industry comprises establishments primarily responsible for the construction (including new work, additions, alterations, maintenance, and repairs) of industrial buildings (except warehouses).  The construction of selected additional structures, whose production processes are similar to those for industrial buildings (e.g., incinerators, cement plants, blast furnaces, and similar nonbuilding structures), is included in this industry.  Included in this industry are industrial building general contractors, industrial building for-sale builders, industrial building design-build firms, and industrial building construction management firms. Illustrative Examples: Assembly plant construction, Furnace, industrial plant, construction, Cannery construction, Mine loading and discharging station construction, Cement plant construction, Paper or pulp mill construction, Steel mill construction, Chemical plant (except petrochemical) construction, Pharmaceutical manufacturing plant construction, Factory construction, Food processing plant construction, Waste disposal plant (except sewage treatment) construction.",
    industries: ["nonresidential-building-construction"],
    name: "Industrial Building Construction",
    slug: "industrial-building-construction"
  },
  "236220": {
    description: "This industry comprises establishments primarily responsible for the construction (including new work, additions, alterations, maintenance, and repairs) of commercial and institutional buildings and related structures, such as stadiums, grain elevators, and indoor swimming facilities.  This industry includes establishments responsible for the on-site assembly of modular or prefabricated commercial and institutional buildings.  Included in this industry are commercial and institutional building general contractors, commercial and institutional building for-sale builders, commercial and institutional building design-build firms, and commercial and institutional building project construction management firms. Illustrative Examples: Airport building construction, Office building construction, Arena construction, Parking garage construction, Barrack construction, Prison construction, Farm building construction, Radio and television broadcast studio construction, Fire station construction, Grain elevator or bin construction, Data center construction, Religious building (e.g., church, synagogue, mosque, temple) construction, Restaurant construction, Hospital construction, School building construction, Hotel construction, Shopping mall construction, Indoor swimming facility construction, Warehouse construction (e.g., commercial, industrial, manufacturing, private).",
    industries: ["nonresidential-building-construction"],
    name: "Commercial and Institutional Building Construction",
    slug: "commercial-and-institutional-building-construction"
  },
  "237110": {
    description: "This industry comprises establishments primarily engaged in the construction of water and sewer lines, mains, pumping stations, treatment plants, and storage tanks.  The work performed may include new work, reconstruction, rehabilitation, and repairs.  Specialty trade contractors are included in this industry if they are engaged in activities primarily related to water, sewer line, and related structures construction.  All structures (including buildings) that are integral parts of water and sewer networks (e.g., storage tanks, pumping stations, water treatment plants, and sewage treatment plants) are included in this industry. Illustrative Examples: Distribution line, sewer and water, construction, Sewer main, pipe and connection, construction, Fire hydrant installation, Storm sewer construction, Irrigation systems construction, Water main and line construction, Sewage disposal plant construction, Water treatment plant construction, Pumping station, water and sewage system, construction, Water system storage tank and tower construction, Reservoir construction, Water well drilling, digging, boring, or sinking (except water intake wells in oil and gas fields).",
    industries: ["utility-system-construction"],
    name: "Water and Sewer Line and Related Structures Construction",
    slug: "water-and-sewer-line-and-related-structures-construction"
  },
  "237120": {
    description: "This industry comprises establishments primarily engaged in the construction of oil and gas lines, mains, refineries, and storage tanks.  The work performed may include new work, reconstruction, rehabilitation, and repairs.  Specialty trade contractors are included in this industry if they are engaged in activities primarily related to oil and gas pipeline and related structures construction.  All structures (including buildings) that are integral parts of oil and gas networks (e.g., storage tanks, pumping stations, and refineries) are included in this industry. Illustrative Examples: Distribution line, gas and oil, construction, Oil refinery construction, Gas main construction, Petrochemical plant construction, Gathering line, gas and oil field, construction, Natural gas pipeline construction, Pumping station, gas and oil transmission, construction, Storage tank, natural gas or oil, tank farm or field, construction, Natural gas processing plant construction.",
    industries: ["utility-system-construction"],
    name: "Oil and Gas Pipeline and Related Structures Construction",
    slug: "oil-and-gas-pipeline-and-related-structures-construction"
  },
  "237130": {
    description: "This industry comprises establishments primarily engaged in the construction of power lines and towers, power plants, and radio, television, and telecommunications transmitting/receiving towers.  The work performed may include new work, reconstruction, rehabilitation, and repairs.  Specialty trade contractors are included in this industry if they are engaged in activities primarily related to power and communication line and related structures construction.  All structures (including buildings) that are integral parts of power and communication networks (e.g., transmitting towers, substations, and power plants) are included. Illustrative Examples: Alternative energy (e.g., geothermal, ocean wave, solar, wind) structure construction, Power line stringing, Cellular phone tower construction, Radio transmitting tower construction, Co-generation plant construction, Satellite receiving station construction, Communication tower construction, Nuclear power plant construction, Telephone line stringing, Electric light and power plant (except hydroelectric) construction, Transformer station and substation, electric power, construction, Electric power transmission line and tower construction, Underground cable (e.g., fiber optic, electricity, telephone, cable television) laying.",
    industries: ["utility-system-construction"],
    name: "Power and Communication Line and Related Structures Construction",
    slug: "power-and-communication-line-and-related-structures-construction"
  },
  "237210": {
    description: "This industry comprises establishments primarily engaged in servicing land and subdividing real property into lots, for subsequent sale to builders.  Servicing of land may include excavation work for the installation of roads and utility lines.  The extent of work may vary from project to project.  Land subdivision precedes building activity and the subsequent building is often residential, but may also be commercial tracts and industrial parks.  These establishments may do all the work themselves or subcontract the work to others.  Establishments that perform only the legal subdivision of land are not included in this industry.",
    industries: ["subdivision-of-land"],
    name: "Land Subdivision",
    slug: "land-subdivision"
  },
  "237310": {
    description: "This industry comprises establishments primarily engaged in the construction of highways (including elevated), streets, roads, airport runways, public sidewalks, or bridges.  The work performed may include new work, reconstruction, rehabilitation, and repairs.  Specialty trade contractors are included in this industry if they are engaged in activities primarily related to highway, street, and bridge construction (e.g., installing guardrails on highways). Illustrative Examples: Airport runway construction, Highway line painting, Causeway construction, Painting traffic lanes or parking lot lines, Culverts, highway, road, and street, construction, Pothole filling, highway, road, street, or bridge, Elevated highway construction, Resurfacing, highway, road, street, or bridge, Guardrail construction, Sign erection, highway, road, street, or bridge .",
    industries: ["highway-street-and-bridge-construction"],
    name: "Highway, Street, and Bridge Construction",
    slug: "highway-street-and-bridge-construction"
  },
  "237990": {
    description: "This industry comprises establishments primarily engaged in heavy and civil engineering construction projects (excluding highway, street, bridge, and distribution line construction).  The work performed may include new work, reconstruction, rehabilitation, and repairs.  Specialty trade contractors are included in this industry if they are engaged in activities primarily related to heavy and civil engineering construction projects (excluding highway, street, bridge, distribution line, oil and gas structure, and utilities building and structure construction).  Construction projects involving water resources (e.g., dredging and land drainage), development of marine facilities, and projects involving open space improvement (e.g., parks and trails) are included in this industry. Illustrative Examples: Channel construction, Land drainage contractors, Dam construction, Marine construction, Dock construction, Microtunneling contractors, Dredging (e.g., canal, channel, ditch, waterway), Nuclear waste disposal site construction, Earth retention system construction, Flood control project construction, Park ground and recreational open space improvement construction, Railroad construction, Golf course construction, Subway construction, Horizontal drilling (e.g., cable, pipeline, sewer installation), Trenching, underwater, Hydroelectric generating station construction, Tunnel construction .",
    industries: ["civil-engineering"],
    name: "Other Heavy and Civil Engineering Construction",
    slug: "other-heavy-and-civil-engineering-construction"
  },
  "238110": {
    description: "This industry comprises establishments primarily engaged in pouring and finishing concrete foundations and structural elements.  This industry also includes establishments performing grout and shotcrete work.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Concrete pouring and finishing, Gunite contractors, Concrete pumping (i.e., placement), Mud-jacking contractors, Concrete work (except paving), Shotcrete contractors, Footing and foundation concrete contractors.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Poured Concrete Foundation and Structure Contractors",
    slug: "poured-concrete-foundation-and-structure-contractors"
  },
  "238120": {
    description: "This industry comprises establishments primarily engaged in (1) erecting and assembling structural parts made from steel or precast concrete (e.g., steel beams, structural steel components, and similar products of precast concrete) and/or (2) assembling and installing other steel construction products (e.g., steel rods, bars, rebar, mesh, and cages) to reinforce poured-in-place concrete.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Concrete product (e.g., structural precast, structural prestressed) installation, Rebar contractors, Erecting structural steel, Reinforcing steel contractors, Placing and tying reinforcing rod at a construction site, Structural steel contractors, Precast concrete panel, slab, or form installation.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Structural Steel and Precast Concrete Contractors",
    slug: "structural-steel-and-precast-concrete-contractors"
  },
  "238130": {
    description: "This industry comprises establishments primarily engaged in structural framing and sheathing using materials other than structural steel or concrete.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Building framing (except structural steel), Post framing contractors, Foundation, building, wood, contractors, Steel framing contractors, Framing contractors, Wood frame component (e.g., truss) fabrication on site.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Framing Contractors",
    slug: "framing-contractors"
  },
  "238140": {
    description: "This industry comprises establishments primarily engaged in masonry work, stone setting, bricklaying, and other stone work. The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Block laying, Marble, granite, and slate, exterior, contractors, Bricklaying, Masonry pointing, cleaning, or caulking, Concrete block laying, Stucco contractors, Foundation (e.g., brick, block, stone), building, contractors.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Masonry Contractors",
    slug: "masonry-contractors"
  },
  "238150": {
    description: "This industry comprises establishments primarily engaged in installing glass panes in prepared openings (i.e., glazing work) and other glass work for buildings.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Decorative glass and mirror installation, Glazing contractors, Glass cladding installation, Stained glass installation, Window pane or sheet installation, Glass coating and tinting (except automotive) contractors, Glass installation (except automotive) contractors.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Glass and Glazing Contractors",
    slug: "glass-and-glazing-contractors"
  },
  "238160": {
    description: "This industry comprises establishments primarily engaged in roofing.  This industry also includes establishments treating roofs (i.e., spraying, painting, or coating) and installing skylights.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Painting, spraying, or coating, roof, Sheet metal roofing installation, Shake and shingle, roof, installation, Skylight installation.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Roofing Contractors",
    slug: "roofing-contractors"
  },
  "238170": {
    description: "This industry comprises establishments primarily engaged in installing siding of wood, aluminum, vinyl, or other exterior finish material (except brick, stone, stucco, or curtain wall).  This industry also includes establishments installing gutters and downspouts.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Downspout, gutter, and gutter guard installation, Fascia and soffit installation, Siding (e.g., vinyl, wood, aluminum) installation.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Siding Contractors",
    slug: "siding-contractors"
  },
  "238190": {
    description: "This industry comprises establishments primarily engaged in building foundation and structure trades work (except poured concrete, structural steel, precast concrete, framing, masonry, glass and glazing, roofing, and siding).  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Curtain wall, metal, installation, Forms for poured concrete, erecting and dismantling, Welding, on-site, contractors, Ornamental metal work installation, Fire escape installation, Decorative steel and wrought iron work installation.",
    industries: ["building-structure-and-exterior-contractors"],
    name: "Other Foundation, Structure, and Building Exterior Contractors",
    slug: "other-foundation-structure-and-building-exterior-contractors"
  },
  "238210": {
    description: "This industry comprises establishments primarily engaged in installing and servicing electrical wiring and equipment.  Contractors included in this industry may include both the parts and labor when performing work.  These contractors may perform new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Airport runway lighting contractors, Fiber optic cable (except transmission line) contractors, Alarm system (e.g., fire, burglar), electric, installation only, Highway, street, and bridge lighting and electrical signal installation, Audio equipment (except automotive) installation contractors, Home automation system installation, Lighting system installation, Cable television hookup contractors, Telecommunications equipment and wiring (except transmission line) installation contractors, Computer and network cable installation, Traffic signal installation, Environmental control system installation, Cable splicing, electrical or fiber optic.",
    industries: ["building-equipment-contractors"],
    name: "Electrical Contractors and Other Wiring Installation Contractors",
    slug: "electrical-contractors-and-other-wiring-installation-contractors"
  },
  "238220": {
    description: "This industry comprises establishments primarily engaged in installing and servicing plumbing, heating, and air-conditioning equipment.  Contractors in this industry may provide both parts and labor when performing work.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Cooling tower installation, Heating, ventilation, and air-conditioning (HVAC) contractors, Duct work (e.g., cooling, dust collection, exhaust, heating, ventilation) installation, Lawn sprinkler system installation, Fire sprinkler system installation, Mechanical contractors, Fireplace, natural gas, installation, Refrigeration system (e.g., commercial, industrial, scientific) installation, Furnace installation, Sewer hookup and connection, building.",
    industries: ["building-equipment-contractors"],
    name: "Plumbing, Heating, and Air-Conditioning Contractors",
    slug: "plumbing-heating-and-air-conditioning-contractors"
  },
  "238290": {
    description: "This industry comprises establishments primarily engaged in installing or servicing building equipment (except electrical, plumbing, heating, cooling, or ventilation equipment).  The repair and maintenance of miscellaneous building equipment is included in this industry.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Automated and revolving door installation, Lightning protection equipment (e.g., lightning rod) installation, Boiler and pipe insulation installation, Machine rigging, Commercial-type door installation, Millwrights, Conveyor system installation, Revolving door installation, Overhead door, commercial- or industrial-type, installation, Dismantling large-scale machinery and equipment, Elevator installation, Satellite dish, household-type, installation, Escalator installation, Vacuum cleaning system, built-in, installation, Gasoline pump, service station, installation.",
    industries: ["building-equipment-contractors"],
    name: "Other Building Equipment Contractors",
    slug: "other-building-equipment-contractors"
  },
  "238310": {
    description: "This industry comprises establishments primarily engaged in drywall, plaster work, and building insulation work.  Plaster work includes applying plain or ornamental plaster, and installation of lath to receive plaster.  The work performed may include new work, additions, alterations, maintenance, and repairs.  Establishments primarily engaged in providing firestop services are included in this industry. Illustrative Examples: Acoustical ceiling tile and panel installation, Lathing contractors, Drop ceiling installation, Plastering (i.e., ornamental, plain) contractors, Drywall contractors, Soundproofing contractors, Firestop contractors, Fresco (i.e., decorative plaster finishing) contractors, Taping and finishing drywall, Gypsum board installation, Wall cavity and attic space insulation installation.",
    industries: ["building-finishing-contractors"],
    name: "Drywall and Insulation Contractors",
    slug: "drywall-and-insulation-contractors"
  },
  "238320": {
    description: "This industry comprises establishments primarily engaged in interior or exterior painting or interior wall covering.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Bridge painting, Paperhanging or removal contractors, House painting, Ship painting contractors, Paint and wallpaper stripping, Wallpaper hanging and removal contractors.",
    industries: ["building-finishing-contractors"],
    name: "Painting and Wall Covering Contractors",
    slug: "painting-and-wall-covering-contractors"
  },
  "238330": {
    description: "This industry comprises establishments primarily engaged in the installation of resilient floor tile, carpeting, linoleum, and hardwood flooring.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Carpet, installation only, Resilient floor tile or sheet (e.g., linoleum, rubber, vinyl), installation only, Vinyl flooring contractors, Resurfacing hardwood flooring, Hardwood flooring, installation only, Floor laying, scraping, finishing, and refinishing.",
    industries: ["building-finishing-contractors"],
    name: "Flooring Contractors",
    slug: "flooring-contractors"
  },
  "238340": {
    description: "This industry comprises establishments primarily engaged in setting and installing ceramic tile, stone (interior only), and mosaic and/or mixing marble particles and cement to make terrazzo at the job site.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Ceramic tile installation, Mosaic work, Mantel, marble or stone, installation, Tile (except resilient) laying and setting, Stone flooring installation, Marble, granite, and slate, interior installation contractors.",
    industries: ["building-finishing-contractors"],
    name: "Tile and Terrazzo Contractors",
    slug: "tile-and-terrazzo-contractors"
  },
  "238350": {
    description: "This industry comprises establishments primarily engaged in finish carpentry work.  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Built-in wood cabinets constructed on site, Molding or trim, wood or plastic, installation, Countertop (except ceramic tile or stone), residential-type, installation, Paneling installation, Door and window frame construction, Garage door, residential-type, installation, Prefabricated kitchen and bath cabinet, residential-type, installation, Ship joinery contractors, Millwork installation, Window and door, residential-type, of any material, prefabricated, installation.",
    industries: ["building-finishing-contractors"],
    name: "Finish Carpentry Contractors",
    slug: "finish-carpentry-contractors"
  },
  "238390": {
    description: "This industry comprises establishments primarily engaged in building finishing trade work (except drywall, plaster, and insulation work; painting and wall covering work; flooring work; tile and terrazzo work; and finish carpentry work).  The work performed may include new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Bathtub refinishing, on-site, Fabricating metal cabinets or countertops on site, Closet organizer system installation, Modular furniture system attachment and installation, Concrete coating, glazing, or sealing, Trade show exhibit installation and dismantling, Countertop and cabinet, metal (except residential-type), installation, Waterproofing contractors, Drapery fixture (e.g., hardware, rods, tracks) installation, Window shade and blind installation.",
    industries: ["building-finishing-contractors"],
    name: "Other Building Finishing Contractors",
    slug: "other-building-finishing-contractors"
  },
  "238910": {
    description: "This industry comprises establishments primarily engaged in site preparation activities, such as excavating and grading, demolition of buildings and other structures, and septic system installation.  Earthmoving and land clearing for all types of sites (e.g., building, nonbuilding, mining) is included in this industry.  Establishments primarily engaged in construction equipment rental with operator (except cranes) are also included. Illustrative Examples: Blasting, building demolition, Building interior gutting and stripping, Concrete breaking and cutting for demolition, Cutting new rights of way, Demolition, building and structure, Exterior demolition contractors, Foundation digging (i.e., excavation), Foundation drilling contractors, Grading construction sites, Interior demolition contractors, Dewatering contractors, Dirt moving for construction, Equipment rental (except crane), construction, with operator, Excavating, earthmoving, or land clearing contractors, Line slashing or cutting (except maintenance), Septic system contractors, Trenching (except underwater), Underground tank (except hazardous) removal, Wrecking, building or other structure, Whole building deconstruction.",
    industries: ["specialty-trade-contractors"],
    name: "Site Preparation Contractors",
    slug: "site-preparation-contractors"
  },
  "238990": {
    description: "This industry comprises establishments primarily engaged in specialized trades (except foundation, structure, and building exterior contractors; building equipment contractors; building finishing contractors; and site preparation contractors).  The specialty trade work performed includes new work, additions, alterations, maintenance, and repairs. Illustrative Examples: Billboard erection, Outdoor swimming pool construction, Paver, brick (e.g., driveway, patio, sidewalk), installation, Crane rental with operator, Paving, residential and commercial driveway and parking lot, Sandblasting building exteriors, Fence installation, Scaffold erecting and dismantling, Interlocking brick and block installation, Steeplejack work, Manufactured (mobile) home set up and tie-down work, Driveway paving or sealing.",
    industries: ["specialty-trade-contractors"],
    name: "All Other Specialty Trade Contractors",
    slug: "all-other-specialty-trade-contractors"
  },
  "311111": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing dog and cat food from ingredients, such as grains, oilseed mill products, and meat products.",
    industries: ["animal-feed-manufacturing"],
    name: "Dog and Cat Food Manufacturing",
    slug: "dog-and-cat-food-manufacturing"
  },
  "311119": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing animal food (except dog and cat) from ingredients, such as grains, oilseed mill products, and meat products.",
    industries: ["animal-feed-manufacturing"],
    name: "Other Animal Food Manufacturing",
    slug: "other-animal-food-manufacturing"
  },
  "311211": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) milling flour or meal from grains (except rice) or vegetables and/or (2) milling flour and preparing flour mixes or doughs.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Flour Milling",
    slug: "flour-milling"
  },
  "311212": {
    description: "This U.S. industry comprises establishments primarily engaged in one of the following: (1) milling rice; (2) cleaning and polishing rice; or (3) milling, cleaning, and polishing rice.  The establishments in this industry may package the rice they mill with other ingredients.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Rice Milling",
    slug: "rice-milling"
  },
  "311213": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing malt from barley, rye, or other grains.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Malt Manufacturing",
    slug: "malt-manufacturing"
  },
  "311221": {
    description: "This U.S. industry comprises establishments primarily engaged in wet milling corn and other vegetables (except to make ethyl alcohol).  Examples of products made in these establishments are corn sweeteners, such as glucose, dextrose, and fructose; corn oil; and starches (except laundry).",
    industries: ["food-and-beverage-manufacturing"],
    name: "Wet Corn Milling",
    slug: "wet-corn-milling"
  },
  "311224": {
    description: "This U.S. industry comprises establishments primarily engaged in crushing oilseeds and tree nuts, such as soybeans, cottonseeds, linseeds, peanuts, and sunflower seeds.  Examples of products produced in these establishments are oilseed oils, cakes, meals, and protein isolates and concentrates.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Soybean and Other Oilseed Processing",
    slug: "soybean-and-other-oilseed-processing"
  },
  "311225": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing shortening and margarine from purchased fats and oils; (2) refining and/or blending vegetable, oilseed, and tree nut oils from purchased oils; and (3) blending purchased animal fats with purchased vegetable fats.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Fats and Oils Refining and Blending",
    slug: "fats-and-oils-refining-and-blending"
  },
  "311230": {
    description: "This industry comprises establishments primarily engaged in manufacturing breakfast cereal foods.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Breakfast Cereal Manufacturing",
    slug: "breakfast-cereal-manufacturing"
  },
  "311313": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing refined beet sugar from sugar beets.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Beet Sugar Manufacturing",
    slug: "beet-sugar-manufacturing"
  },
  "311314": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) processing sugarcane and/or (2) refining cane sugar from raw cane sugar.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Cane Sugar Manufacturing",
    slug: "cane-sugar-manufacturing"
  },
  "311340": {
    description: "This industry comprises establishments primarily engaged in manufacturing nonchocolate confectioneries.  Included in this industry are establishments primarily engaged in retailing nonchocolate confectionery products not for immediate consumption made on the premises.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Nonchocolate Confectionery Manufacturing",
    slug: "nonchocolate-confectionery-manufacturing"
  },
  "311351": {
    description: "This U.S. industry comprises establishments primarily engaged in shelling, roasting, and grinding cacao beans and making chocolate cacao products and chocolate confectioneries.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Chocolate and Confectionery Manufacturing from Cacao Beans",
    slug: "chocolate-and-confectionery-manufacturing-from-cacao-beans"
  },
  "311352": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing chocolate confectioneries from chocolate produced elsewhere.  Included in this industry are establishments primarily engaged in retailing chocolate confectionery products not for immediate consumption made on the premises from chocolate made elsewhere.",
    industries: ["sugar-and-confectionery-product-manufacturing"],
    name: "Confectionery Manufacturing from Purchased Chocolate",
    slug: "confectionery-manufacturing-from-purchased-chocolate"
  },
  "311411": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing frozen fruits; frozen vegetables; and frozen fruit juices, ades, drinks, cocktail mixes and concentrates. Cross-References.",
    industries: ["fruit-and-vegetable-preserves-manufacturing"],
    name: "Frozen Fruit, Juice, and Vegetable Manufacturing",
    slug: "frozen-fruit-juice-and-vegetable-manufacturing"
  },
  "311412": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing frozen specialty foods (except seafood), such as frozen dinners, entrees, and side dishes; frozen pizza; frozen whipped topping; and frozen waffles, pancakes, and French toast.",
    industries: ["fruit-and-vegetable-preserves-manufacturing"],
    name: "Frozen Specialty Food Manufacturing",
    slug: "frozen-specialty-food-manufacturing"
  },
  "311421": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing canned, pickled, and brined fruits and vegetables.  Examples of products made in these establishments are canned juices; canned jams and jellies; canned tomato-based sauces, such as catsup, salsa, chili sauce, spaghetti sauce, barbeque sauce, and tomato paste; and pickles, relishes, and sauerkraut.",
    industries: ["fruit-and-vegetable-preserves-manufacturing"],
    name: "Fruit and Vegetable Canning",
    slug: "fruit-and-vegetable-canning"
  },
  "311422": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing canned specialty foods.  Examples of products made in these establishments are canned baby food, canned baked beans, canned soups (except seafood), canned spaghetti, and other canned nationality foods.",
    industries: ["fruit-and-vegetable-preserves-manufacturing"],
    name: "Specialty Canning",
    slug: "specialty-canning"
  },
  "311423": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) drying (including freeze-dried) and/or dehydrating fruits, vegetables, and soup mixes and bouillon and/or (2) drying and/or dehydrating ingredients and packaging them with other purchased ingredients, such as rice and dry pasta.",
    industries: ["fruit-and-vegetable-preserves-manufacturing"],
    name: "Dried and Dehydrated Food Manufacturing",
    slug: "dried-and-dehydrated-food-manufacturing"
  },
  "311511": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing processed milk products, such as pasteurized milk or cream and sour cream and/or (2) manufacturing fluid milk dairy substitutes from soybeans and other nondairy substances.",
    industries: ["dairy-product-manufacturing"],
    name: "Fluid Milk Manufacturing",
    slug: "fluid-milk-manufacturing"
  },
  "311512": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing creamery butter from milk and/or processed milk products. Cross-References.",
    industries: ["dairy-product-manufacturing"],
    name: "Creamery Butter Manufacturing",
    slug: "creamery-butter-manufacturing"
  },
  "311513": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing cheese products (except cottage cheese) from raw milk and/or processed milk products and/or (2) manufacturing cheese substitutes from soybean and other nondairy substances.",
    industries: ["dairy-product-manufacturing"],
    name: "Cheese Manufacturing",
    slug: "cheese-manufacturing"
  },
  "311514": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing dry, condensed, and evaporated milk and dairy substitute products.",
    industries: ["dairy-product-manufacturing"],
    name: "Dry, Condensed, and Evaporated Dairy Product Manufacturing",
    slug: "dry-condensed-and-evaporated-dairy-product-manufacturing"
  },
  "311520": {
    description: "This industry comprises establishments primarily engaged in manufacturing ice cream, frozen yogurts, frozen ices, sherbets, frozen tofu, and other frozen desserts (except bakery products).",
    industries: ["dairy-product-manufacturing"],
    name: "Ice Cream and Frozen Dessert Manufacturing",
    slug: "ice-cream-and-frozen-dessert-manufacturing"
  },
  "311611": {
    description: "This U.S. industry comprises establishments primarily engaged in slaughtering animals (except poultry and small game).  Establishments that slaughter and prepare meats are included in this industry.",
    industries: ["meat-products-manufacturing"],
    name: "Animal (except Poultry) Slaughtering",
    slug: "animal-except-poultry-slaughtering"
  },
  "311612": {
    description: "This U.S. industry comprises establishments primarily engaged in processing or preserving meat and meat byproducts (except poultry and small game) from purchased meats.  This industry includes establishments primarily engaged in assembly cutting and packing of meats (i.e., boxed meats) from purchased meats.",
    industries: ["meat-products-manufacturing"],
    name: "Meat Processed from Carcasses",
    slug: "meat-processed-from-carcasses"
  },
  "311613": {
    description: "This U.S. industry comprises establishments primarily engaged in rendering animal fat, bones, and meat scraps.",
    industries: ["meat-products-manufacturing"],
    name: "Rendering and Meat Byproduct Processing",
    slug: "rendering-and-meat-byproduct-processing"
  },
  "311615": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) slaughtering poultry and small game and/or (2) preparing processed poultry and small game meat and meat byproducts.",
    industries: ["meat-products-manufacturing"],
    name: "Poultry Processing",
    slug: "poultry-processing"
  },
  "311710": {
    description: 'This industry comprises establishments primarily engaged in one or more of the following: (1) canning seafood (including soup); (2) smoking, salting, and drying seafood; (3) eviscerating fresh fish by removing heads, fins, scales, bones, and entrails; (4) shucking and packing fresh shellfish; (5) processing marine fats and oils; and (6) freezing seafood.  Establishments known as "floating factory ships" that are engaged in the gathering and processing of seafood into canned seafood products are included in this industry.',
    industries: ["seafood-product-manufacturing"],
    name: "Seafood Product Preparation and Packaging",
    slug: "seafood-product-preparation-and-packaging"
  },
  "311811": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing bread and other bakery products not for immediate consumption made on the premises from flour, not from prepared dough.",
    industries: ["baked-goods-manufacturing"],
    name: "Retail Bakeries",
    slug: "retail-bakeries"
  },
  "311812": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fresh and frozen bread and bread-type rolls and other fresh bakery (except cookies and crackers) products.",
    industries: ["baked-goods-manufacturing"],
    name: "Commercial Bakeries",
    slug: "commercial-bakeries"
  },
  "311813": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing frozen bakery products (except bread), such as cakes, pies, and doughnuts.",
    industries: ["baked-goods-manufacturing"],
    name: "Frozen Cakes, Pies, and Other Pastries Manufacturing",
    slug: "frozen-cakes-pies-and-other-pastries-manufacturing"
  },
  "311821": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing cookies, crackers, and other products, such as ice cream cones.",
    industries: ["baked-goods-manufacturing"],
    name: "Cookie and Cracker Manufacturing",
    slug: "cookie-and-cracker-manufacturing"
  },
  "311824": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing dry pasta and/or (2) manufacturing prepared flour mixes or dough from flour ground elsewhere.  The establishments in this industry may package the dry pasta they manufacture with other ingredients.",
    industries: ["baked-goods-manufacturing"],
    name: "Dry Pasta, Dough, and Flour Mixes Manufacturing from Purchased Flour",
    slug: "dry-pasta-dough-and-flour-mixes-manufacturing-from-purchased-flour"
  },
  "311830": {
    description: "This industry comprises establishments primarily engaged in manufacturing tortillas.",
    industries: ["baked-goods-manufacturing"],
    name: "Tortilla Manufacturing",
    slug: "tortilla-manufacturing"
  },
  "311911": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) salting, roasting, drying, cooking, or canning nuts; (2) processing grains or seeds into snacks; and (3) manufacturing peanut butter and other nut butters.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Roasted Nuts and Peanut Butter Manufacturing",
    slug: "roasted-nuts-and-peanut-butter-manufacturing"
  },
  "311919": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing snack foods (except roasted nuts and peanut butter). Illustrative Examples: Corn chips and related corn snacks manufacturing, Popped popcorn (except candy-covered) manufacturing, Pork rinds manufacturing, Potato chips manufacturing, Pretzels (except soft) manufacturing, Tortilla chips manufacturing.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Other Snack Food Manufacturing",
    slug: "other-snack-food-manufacturing"
  },
  "311920": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) roasting coffee; (2) manufacturing coffee and tea concentrates (including instant and freeze-dried); (3) blending tea; (4) manufacturing herbal tea; and (5) manufacturing coffee extracts, flavorings, and syrups. Cross-References.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Coffee and Tea Manufacturing",
    slug: "coffee-and-tea-manufacturing"
  },
  "311930": {
    description: "This industry comprises establishments primarily engaged in manufacturing flavoring syrup drink concentrates and related products for soda fountain use or for the manufacture of soft drinks.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Flavoring Syrup and Concentrate Manufacturing",
    slug: "flavoring-syrup-and-concentrate-manufacturing"
  },
  "311941": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing mayonnaise, salad dressing, vinegar, mustard, horseradish, soy sauce, tartar sauce, Worcestershire sauce, and other prepared sauces (except tomato-based and gravy).",
    industries: ["food-and-beverage-manufacturing"],
    name: "Mayonnaise, Dressing, and Other Prepared Sauce Manufacturing",
    slug: "mayonnaise-dressing-and-other-prepared-sauce-manufacturing"
  },
  "311942": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing spices, table salt, seasonings, flavoring extracts (except coffee and meat), and natural food colorings and/or (2) manufacturing dry mix food preparations, such as salad dressing mixes, gravy and sauce mixes, frosting mixes, and other dry mix preparations.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Spice and Extract Manufacturing",
    slug: "spice-and-extract-manufacturing"
  },
  "311991": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing perishable prepared foods, such as salads, sandwiches, prepared meals, fresh pizza, fresh pasta, and peeled or cut vegetables. Cross-References.",
    industries: ["food-and-beverage-manufacturing"],
    name: "Perishable Prepared Food Manufacturing",
    slug: "perishable-prepared-food-manufacturing"
  },
  "311999": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing food (except animal food; grain and oilseed milling; sugar and confectionery products; preserved fruits, vegetables, and specialties; dairy products; meat products; seafood products; bakeries and tortillas; snack foods; coffee and tea; flavoring syrups and concentrates; seasonings and dressings; and perishable prepared food).  Included in this industry are establishments primarily engaged in mixing purchased dried and/or dehydrated ingredients including those mixing purchased dried and/or dehydrated ingredients for soup mixes and bouillon. Illustrative Examples: Baking powder manufacturing, Cake frosting, prepared, manufacturing, Dessert puddings manufacturing, Sweetening syrups (except pure maple) manufacturing, Egg substitutes manufacturing, Gelatin dessert preparations manufacturing , Honey processing, Powdered drink mixes (except chocolate, coffee, tea, or milk based) manufacturing, Popcorn (except popped) manufacturing, Yeast manufacturing.",
    industries: ["food-and-beverage-manufacturing"],
    name: "All Other Miscellaneous Food Manufacturing",
    slug: "all-other-miscellaneous-food-manufacturing"
  },
  "312111": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing soft drinks and artificially carbonated waters.",
    industries: ["beverage-manufacturing"],
    name: "Soft Drink Manufacturing",
    slug: "soft-drink-manufacturing"
  },
  "312112": {
    description: "This U.S. industry comprises establishments primarily engaged in purifying and bottling water (including naturally carbonated).",
    industries: ["beverage-manufacturing"],
    name: "Bottled Water Manufacturing",
    slug: "bottled-water-manufacturing"
  },
  "312113": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing ice.",
    industries: ["beverage-manufacturing"],
    name: "Ice Manufacturing",
    slug: "ice-manufacturing"
  },
  "312120": {
    description: "This industry comprises establishments primarily engaged in brewing beer, ale, lager, malt liquors, and nonalcoholic beer.",
    industries: ["breweries"],
    name: "Breweries",
    slug: "breweries"
  },
  "312130": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) growing grapes and manufacturing wines and brandies; (2) manufacturing wines and brandies from grapes and other fruits grown elsewhere; and (3) blending wines and brandies.",
    industries: ["wineries"],
    name: "Wineries",
    slug: "wineries"
  },
  "312140": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) distilling potable liquors (except brandies); (2) distilling and blending liquors; and (3) blending and mixing liquors and other ingredients.",
    industries: ["distilleries"],
    name: "Distilleries",
    slug: "distilleries"
  },
  "312230": {
    description: "This industry comprises establishments primarily engaged in (1) stemming and redrying tobacco and/or (2) manufacturing cigarettes or other tobacco products. Illustrative Examples: Chewing tobacco manufacturing, Cigar manufacturing, Cigarettes manufacturing (except electronic), Snuff manufacturing, Prepared pipe tobacco manufacturing, Tobacco leaf processing and aging.",
    industries: ["tobacco-manufacturing"],
    name: "Tobacco Manufacturing",
    slug: "tobacco-manufacturing"
  },
  "313110": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) spinning yarn; (2) manufacturing thread of any fiber; (3) texturizing, throwing, twisting, and winding purchased yarn or manmade fibers and filaments; and (4) producing hemp yarn and further processing into rope or bags. Cross-References.",
    industries: ["textile-manufacturing"],
    name: "Fiber, Yarn, and Thread Mills",
    slug: "fiber-yarn-and-thread-mills"
  },
  "313210": {
    description: "This industry comprises establishments primarily engaged in weaving broadwoven fabrics and felts (except tire fabrics and rugs).  Establishments in this industry may weave only, weave and finish, or weave, finish, and further fabricate fabric products.",
    industries: ["textile-manufacturing"],
    name: "Broadwoven Fabric Mills",
    slug: "broadwoven-fabric-mills"
  },
  "313220": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) weaving or braiding narrow fabrics in their final form or initially made in wider widths that are specially constructed for narrower widths; (2) making fabric-covered elastic yarn and thread; and (3) manufacturing Schiffli machine embroideries.  Establishments in this industry may weave only; weave and finish; or weave, finish, and further fabricate fabric products.",
    industries: ["textile-manufacturing"],
    name: "Narrow Fabric Mills and Schiffli Machine Embroidery",
    slug: "narrow-fabric-mills-and-schiffli-machine-embroidery"
  },
  "313230": {
    description: "This industry comprises establishments primarily engaged in manufacturing nonwoven fabrics and felts. Processes used include bonding and/or interlocking fibers by mechanical, chemical, thermal, or solvent means, or by combinations thereof.",
    industries: ["textile-manufacturing"],
    name: "Nonwoven Fabric Mills",
    slug: "nonwoven-fabric-mills"
  },
  "313240": {
    description: "This industry comprises establishments primarily engaged in one of the following: (1) knitting weft (i.e., circular) and warp (i.e., flat) fabric; (2) knitting and finishing weft and warp fabric; (3) manufacturing lace; or (4) manufacturing, dyeing, and finishing lace and lace goods.  Establishments in this industry may knit only; knit and finish; or knit, finish, and further fabricate fabric products (except apparel). Cross-References.",
    industries: ["textile-manufacturing"],
    name: "Knit Fabric Mills",
    slug: "knit-fabric-mills"
  },
  "313310": {
    description: "This industry comprises (1) establishments primarily engaged in finishing textiles, fabrics, and apparel and (2) establishments of converters who buy fabric goods in the grey, have them finished on contract, and sell at wholesale.  Finishing operations include: bleaching, dyeing, printing (e.g., roller, screen, flock, plisse), stonewashing, and other mechanical finishing, such as preshrinking, shrinking, sponging, calendering, mercerizing, and napping; as well as cleaning, scouring, and the preparation of natural fibers and raw stock.",
    industries: ["textile-manufacturing"],
    name: "Textile and Fabric Finishing Mills",
    slug: "textile-and-fabric-finishing-mills"
  },
  "313320": {
    description: "This industry comprises establishments primarily engaged in coating, laminating, varnishing, waxing, and rubberizing textiles and apparel. Cross-References.",
    industries: ["textile-manufacturing"],
    name: "Fabric Coating Mills",
    slug: "fabric-coating-mills"
  },
  "314110": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing woven, tufted, and other carpets and rugs, such as art squares, floor mattings, needlepunch carpeting, and door mats and mattings, from textile materials or from twisted paper, grasses, reeds, sisal, jute, or rags and/or (2) finishing carpets and rugs.",
    industries: ["manufacturing"],
    name: "Carpet and Rug Mills",
    slug: "carpet-and-rug-mills"
  },
  "314120": {
    description: "This industry comprises establishments primarily engaged in manufacturing household textile products, such as curtains, draperies, linens, bedspreads, sheets, tablecloths, towels, and shower curtains, from purchased materials.  The household textile products may be made on a stock or custom basis for sale to individual retail customers.",
    industries: ["manufacturing"],
    name: "Curtain and Linen Mills",
    slug: "curtain-and-linen-mills"
  },
  "314910": {
    description: "This industry comprises establishments primarily engaged in manufacturing textile bags (except luggage) or other canvas and canvas-like products, such as awnings, sails, tarpaulins, and tents from purchased textile fabrics or yarns. Illustrative Examples: Covers (e.g., boat, swimming pool, truck) made from purchased fabrics, Laundry bags made from purchased woven or knitted materials, Seed bags made from purchased woven or knitted materials, Textile bags made from purchased woven or knitted materials.",
    industries: ["manufacturing"],
    name: "Textile Bag and Canvas Mills",
    slug: "textile-bag-and-canvas-mills"
  },
  "314994": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing rope, cable, cordage, twine, and related products from all materials (e.g., abaca, sisal, henequen, cotton, paper, jute, flax, manmade fibers including glass) and/or (2) manufacturing cord and fabric of polyester, rayon, cotton, glass, steel, or other materials for use in reinforcing rubber tires, industrial belting, and similar uses. Cross-References.",
    industries: ["manufacturing"],
    name: "Rope, Cordage, Twine, Tire Cord, and Tire Fabric Mills",
    slug: "rope-cordage-twine-tire-cord-and-tire-fabric-mills"
  },
  "314999": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing textile products (except carpets and rugs; curtains and linens; textile bags and canvas products; rope, cordage, and twine; and tire cords and tire fabrics) from purchased materials.  These establishments may further embellish the textile products they manufacture with decorative stitching.  Establishments primarily engaged in adding decorative stitching such as embroidery or other art needlework on textile products, including apparel, on a contract or fee basis for the trade, are included in this industry. Illustrative Examples: Batts and batting (except nonwoven fabrics) manufacturing, Embroidering on textile products or apparel for the trade, Fishing nets made from purchased materials, Carpet cutting and binding, Sleeping bags manufacturing, Diapers (except disposable) made from purchased materials, Textile fire hoses made from purchased materials, Dust cloths made from purchased fabric, Weatherstripping made from purchased textiles.",
    industries: ["manufacturing"],
    name: "All Other Miscellaneous Textile Product Mills",
    slug: "all-other-miscellaneous-textile-product-mills"
  },
  "315120": {
    description: "This industry comprises establishments primarily engaged in knitting apparel or knitting fabric and then manufacturing apparel.  Jobbers, performing entrepreneurial functions involved in knitting apparel and accessories, are included.",
    industries: ["apparel-manufacturing"],
    name: "Apparel Knitting Mills",
    slug: "apparel-knitting-mills"
  },
  "315210": {
    description: "This industry comprises establishments commonly referred to as contractors primarily engaged in (1) cutting materials owned by others for apparel and accessories and/or (2) sewing materials owned by others for apparel and accessories.",
    industries: ["apparel-manufacturing"],
    name: "Cut and Sew Apparel Contractors",
    slug: "cut-and-sew-apparel-contractors"
  },
  "315250": {
    description: "This industry comprises establishments primarily engaged in manufacturing cut and sew apparel from purchased fabric.  Clothing jobbers, who perform entrepreneurial functions involved in apparel manufacture, including buying raw materials, designing and preparing samples, arranging for apparel to be made from their materials, and marketing finished apparel, are included.",
    industries: ["apparel-manufacturing"],
    name: "Cut and Sew Apparel Manufacturing (except Contractors)",
    slug: "cut-and-sew-apparel-manufacturing-except-contractors"
  },
  "315990": {
    description: "This industry comprises establishments primarily engaged in manufacturing apparel and accessories (except apparel knitting mills, cut and sew apparel contractors, and cut and sew apparel manufacturing (except contractors)).  Jobbers, who perform entrepreneurial functions involved in apparel accessories manufacture, including buying raw materials, designing and preparing samples, arranging for apparel accessories to be made from their materials, and marketing finished apparel accessories, are included.  Examples of products made by these establishments are belts, caps, gloves (except medical, safety, sporting), hats, and neckties.",
    industries: ["fashion-accessories-manufacturing"],
    name: "Apparel Accessories and Other Apparel Manufacturing",
    slug: "apparel-accessories-and-other-apparel-manufacturing"
  },
  "316110": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) tanning, currying, and finishing hides and skins; (2) having others process hides and skins on a contract basis; and (3) dyeing or dressing furs.",
    industries: ["leather-product-manufacturing"],
    name: "Leather and Hide Tanning and Finishing",
    slug: "leather-and-hide-tanning-and-finishing"
  },
  "316210": {
    description: "This industry comprises establishments primarily engaged in manufacturing footwear (except orthopedic extension footwear). Illustrative Examples: Athletic shoes manufacturing, Ballet slippers manufacturing, Cleated athletic shoes manufacturing, Shoes, children's and infants' (except orthopedic extension), manufacturing, Shoes, men's (except orthopedic extension), manufacturing, Shoes, women's (except orthopedic extension), manufacturing, Cross-References.",
    industries: ["footwear-manufacturing"],
    name: "Footwear Manufacturing",
    slug: "footwear-manufacturing"
  },
  "316990": {
    description: "This industry comprises establishments primarily engaged in manufacturing leather products (except footwear and apparel) from purchased leather or leather substitutes (e.g., fabric, plastics). Illustrative Examples: Billfolds, all materials, manufacturing, Boot and shoe cut stock and findings, leather, manufacturing, Dog furnishings (e.g., collars, harnesses, leashes, muzzles), manufacturing , Luggage, all materials, manufacturing, Shoe soles, leather, manufacturing, Purses, women's, all materials (except metal), manufacturing, Toilet kits and cases (except metal) manufacturing, Watch bands (except metal) manufacturing, Welders' jackets, leggings, and sleeves, leather, manufacturing.",
    name: "Other Leather and Allied Product Manufacturing",
    slug: "other-leather-and-allied-product-manufacturing",
    industries: ["leather-product-manufacturing", "women-s-handbag-manufacturing"]
  },
  "321113": {
    description: "This U.S. industry comprises establishments primarily engaged in sawing dimension lumber, boards, beams, timbers, poles, ties, shingles, shakes, siding, and wood chips from logs or bolts.  Sawmills may plane the rough lumber that they make with a planing machine to achieve smoothness and uniformity of size.",
    industries: ["wood-product-manufacturing"],
    name: "Sawmills",
    slug: "sawmills"
  },
  "321114": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) treating wood sawed, planed, or shaped in other establishments with creosote or other preservatives, such as alkaline copper quat, copper azole, and sodium borates, to prevent decay and to protect against fire and insects and/or (2) sawing round wood poles, pilings, and posts and treating them with preservatives. Cross-References.",
    industries: ["wood-product-manufacturing"],
    name: "Wood Preservation",
    slug: "wood-preservation"
  },
  "321211": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing hardwood veneer and/or hardwood plywood.",
    industries: ["wood-product-manufacturing"],
    name: "Hardwood Veneer and Plywood Manufacturing",
    slug: "hardwood-veneer-and-plywood-manufacturing"
  },
  "321212": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing softwood veneer and/or softwood plywood.",
    industries: ["wood-product-manufacturing"],
    name: "Softwood Veneer and Plywood Manufacturing",
    slug: "softwood-veneer-and-plywood-manufacturing"
  },
  "321215": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fabricated or laminated wood arches, wood roof and floor trusses, and/or other fabricated or laminated wood structural members. Illustrative Examples: Finger joint lumber manufacturing, I-joists, wood, fabricating, Laminated veneer lumber (LVL) manufacturing, Parallel strand lumber manufacturing, Timbers, structural, glue laminated or pre-engineered wood, manufacturing, Trusses, wood roof or floor, manufacturing.",
    industries: ["wood-product-manufacturing"],
    name: "Engineered Wood Member Manufacturing",
    slug: "engineered-wood-member-manufacturing"
  },
  "321219": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing reconstituted wood sheets and boards. Illustrative Examples: Medium density fiberboard (MDF) manufacturing, Oriented strandboard (OSB) manufacturing, Particleboard manufacturing, Reconstituted wood sheets and boards manufacturing, Waferboard manufacturing .",
    industries: ["wood-product-manufacturing"],
    name: "Reconstituted Wood Product Manufacturing",
    slug: "reconstituted-wood-product-manufacturing"
  },
  "321911": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing window and door units, sash, window and door frames, and doors from wood or wood clad with metal or plastics. Cross-References.",
    industries: ["wood-product-manufacturing"],
    name: "Wood Window and Door Manufacturing",
    slug: "wood-window-and-door-manufacturing"
  },
  "321912": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing dimension lumber from purchased lumber; (2) manufacturing dimension stock (i.e., shapes) or cut stock; (3) resawing the output of sawmills; and (4) planing purchased lumber.  These establishments generally use woodworking machinery, such as jointers, planers, lathes, and routers to shape wood.",
    industries: ["wood-product-manufacturing"],
    name: "Cut Stock, Resawing Lumber, and Planing",
    slug: "cut-stock-resawing-lumber-and-planing"
  },
  "321918": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing millwork (except wood windows, wood doors, and cut stock). Illustrative Examples: Clear and finger joint wood moldings manufacturing, Decorative wood moldings (e.g., base, chair rail, crown, shoe) manufacturing, Ornamental woodwork (e.g., cornices, mantel) manufacturing , Planing mills, millwork , Stairwork (e.g., newel posts, railings, stairs, staircases), wood, manufacturing, Wood flooring manufacturing, Wood shutters manufacturing.",
    industries: ["wood-product-manufacturing"],
    name: "Other Millwork (including Flooring)",
    slug: "other-millwork-including-flooring"
  },
  "321920": {
    description: "This industry comprises establishments primarily engaged in manufacturing wood pallets, wood box shook, wood boxes, other wood containers, and wood parts for pallets and containers. Cross-References.",
    industries: ["wood-product-manufacturing"],
    name: "Wood Container and Pallet Manufacturing",
    slug: "wood-container-and-pallet-manufacturing"
  },
  "321991": {
    description: "This U.S. industry comprises establishments primarily engaged in making manufactured homes (i.e., mobile homes) and nonresidential mobile buildings.  Manufactured homes are designed to accept permanent water, sewer, and utility connections and although equipped with wheels, they are not intended for regular highway movement.",
    industries: ["wood-product-manufacturing"],
    name: "Manufactured Home (Mobile Home) Manufacturing",
    slug: "manufactured-home-mobile-home-manufacturing"
  },
  "321992": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing prefabricated wood buildings and wood sections and panels for prefabricated wood buildings.",
    industries: ["wood-product-manufacturing"],
    name: "Prefabricated Wood Building Manufacturing",
    slug: "prefabricated-wood-building-manufacturing"
  },
  "321999": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing wood products (except establishments operating sawmills and preservation facilities; establishments manufacturing veneer, engineered wood products, millwork, wood containers, pallets, and wood container parts; and establishments making manufactured homes (i.e., mobile homes) and prefabricated buildings and components). Illustrative Examples: Cabinets (i.e., housings), wood (e.g., sewing machines, stereo, television), manufacturing, Cork products (except gaskets) manufacturing, Kiln drying lumber, Shoe trees manufacturing, Wood dowels manufacturing, Wood extension ladders manufacturing, Wood handles (e.g., broom, handtool, mop), manufacturing, Wood kitchenware manufacturing, Wood stepladders manufacturing, Wood toilet seats manufacturing, Wood toothpicks manufacturing.",
    industries: ["wood-product-manufacturing"],
    name: "All Other Miscellaneous Wood Product Manufacturing",
    slug: "all-other-miscellaneous-wood-product-manufacturing"
  },
  "322110": {
    description: "This industry comprises establishments primarily engaged in manufacturing pulp without manufacturing paper or paperboard.  The pulp is made by separating the cellulose fibers from the other impurities in wood or other materials, such as used or recycled rags, linters, scrap paper, and straw.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Pulp Mills",
    slug: "pulp-mills"
  },
  "322120": {
    description: "This industry comprises establishments primarily engaged in manufacturing paper from pulp. These establishments may manufacture or purchase pulp.  In addition, the establishments may convert the paper they make.  The activity of making paper classifies an establishment into this industry regardless of the output.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Paper Mills",
    slug: "paper-mills"
  },
  "322130": {
    description: "This industry comprises establishments primarily engaged in manufacturing paperboard (e.g., can/drum stock, container board, corrugating medium, folding carton stock, linerboard, tube) from pulp.  These establishments may manufacture or purchase pulp.  In addition, the establishments may also convert the paperboard they make.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Paperboard Mills",
    slug: "paperboard-mills"
  },
  "322211": {
    description: "This U.S. industry comprises establishments primarily engaged in laminating purchased paper or paperboard into corrugated or solid fiber boxes and related products, such as pads, partitions, pallets, and corrugated paper without manufacturing paperboard.  These boxes are generally used for shipping.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Corrugated and Solid Fiber Box Manufacturing",
    slug: "corrugated-and-solid-fiber-box-manufacturing"
  },
  "322212": {
    description: "This U.S. industry comprises establishments primarily engaged in converting paperboard (except corrugated) into folding paperboard boxes without manufacturing paper and paperboard.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Folding Paperboard Box Manufacturing",
    slug: "folding-paperboard-box-manufacturing"
  },
  "322219": {
    description: "This U.S. industry comprises establishments primarily engaged in converting paperboard into paperboard containers (except corrugated, solid fiber, and folding paperboard boxes) without manufacturing paperboard. Illustrative Examples: Fiber cans and drums (i.e., all-fiber, nonfiber ends of any material) made from purchased paperboard, Milk cartons made from purchased paper or paperboard, Sanitary food containers (except folding) made from purchased paper or paperboard , Setup (i.e., not shipped flat) boxes made from purchased paperboard.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Other Paperboard Container Manufacturing",
    slug: "other-paperboard-container-manufacturing"
  },
  "322220": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) cutting and coating paper and paperboard; (2) cutting and laminating paper, paperboard, and other flexible materials (except plastics film to plastics film); (3) manufacturing bags, multiwall bags, sacks of paper, metal foil, coated paper, laminates, or coated combinations of paper and foil with plastics film; (4) manufacturing laminated aluminum and other converted metal foils from purchased foils; and (5) surface coating paper or paperboard.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Paper Bag and Coated and Treated Paper Manufacturing",
    slug: "paper-bag-and-coated-and-treated-paper-manufacturing"
  },
  "322230": {
    description: "This industry comprises establishments primarily engaged in converting paper or paperboard into products used for writing, filing, art work, and similar applications. Illustrative Examples: Computer paper, die-cut, made from purchased paper, Die-cut paper products for office use made from purchased paper or paperboard, Envelopes (i.e., mailing, stationery) made from any material, Stationery made from purchased paper, Tablets (e.g., memo, note, writing) made from purchased paper, Tapes (e.g., adding machine, calculator, cash register) made from purchased paper, Cross-References.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Stationery Product Manufacturing",
    slug: "stationery-product-manufacturing"
  },
  "322291": {
    description: "This U.S. industry comprises establishments primarily engaged in converting purchased sanitary paper stock or wadding into sanitary paper products, such as facial tissues, handkerchiefs, table napkins, toilet paper, towels, disposable diapers, sanitary napkins, and tampons.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "Sanitary Paper Product Manufacturing",
    slug: "sanitary-paper-product-manufacturing"
  },
  "322299": {
    description: "This U.S. industry comprises establishments primarily engaged in converting paper or paperboard into products (except containers, bags, coated and treated paper, stationery products, and sanitary paper products) or converting pulp into pulp products, such as egg cartons, food trays, and other food containers from molded pulp. Illustrative Examples: Crepe paper made from purchased paper, Die-cut paper products (except for office use) made from purchased paper or paperboard, Molded pulp products (e.g., egg cartons, food containers, food trays) manufacturing, Paper novelties made from purchased paper.",
    industries: ["paper-and-forest-product-manufacturing"],
    name: "All Other Converted Paper Product Manufacturing",
    slug: "all-other-converted-paper-product-manufacturing"
  },
  "323111": {
    description: "This U.S. industry comprises establishments primarily engaged in commercial printing (except screen printing, books printing) without publishing (except fabric grey goods printing).  The printing processes used in this industry include, but are not limited to, lithographic, gravure, flexographic, letterpress, engraving, and various digital printing technologies.  This industry includes establishments engaged in commercial printing on purchased stock materials, such as stationery, invitations, labels, and similar items, on a job-order basis.  Establishments primarily engaged in traditional printing activities combined with document photocopying services (i.e., quick printers) or primarily engaged in printing graphical materials using digital printing equipment are included in this industry.",
    industries: ["printing-services"],
    name: "Commercial Printing (except Screen and Books)",
    slug: "commercial-printing-except-screen-and-books"
  },
  "323113": {
    description: "This U.S. industry comprises establishments primarily engaged in screen printing without publishing (except books, fabric grey goods, and manifold business forms).  This industry includes establishments engaged in screen printing on purchased stock materials, such as stationery, invitations, labels, and similar items, on a job-order basis.  Establishments primarily engaged in printing on apparel and textile products, such as T-shirts, caps, jackets, towels, and napkins, are included in this industry.",
    industries: ["printing-services"],
    name: "Commercial Screen Printing",
    slug: "commercial-screen-printing"
  },
  "323117": {
    description: "This U.S. industry comprises establishments primarily engaged in printing or printing and binding books and pamphlets without publishing.",
    industries: ["printing-services"],
    name: "Books Printing",
    slug: "books-printing"
  },
  "323120": {
    description: "This industry comprises establishments primarily engaged in performing prepress and postpress services in support of printing activities.  Prepress services may include such things as platemaking, typesetting, trade binding, and sample mounting.  Postpress services include such things as book or paper bronzing, die cutting, edging, embossing, folding, gilding, gluing, and indexing.",
    industries: ["printing-services"],
    name: "Support Activities for Printing",
    slug: "support-activities-for-printing"
  },
  "324110": {
    description: "This industry comprises establishments primarily engaged in refining crude petroleum into refined petroleum.  Petroleum refining involves one or more of the following activities: (1) fractionation; (2) straight distillation of crude oil; and (3) cracking.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Petroleum Refineries",
    slug: "petroleum-refineries"
  },
  "324121": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing asphalt and tar paving mixtures and blocks from purchased asphaltic materials. Cross-References.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Asphalt Paving Mixture and Block Manufacturing",
    slug: "asphalt-paving-mixture-and-block-manufacturing"
  },
  "324122": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) saturating purchased mats and felts with asphalt or tar from purchased asphaltic materials and (2) manufacturing asphalt and tar and roofing cements and coatings from purchased asphaltic materials.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Asphalt Shingle and Coating Materials Manufacturing",
    slug: "asphalt-shingle-and-coating-materials-manufacturing"
  },
  "324191": {
    description: "This U.S. industry comprises establishments primarily engaged in blending or compounding refined petroleum to make lubricating oils and greases and/or re-refining used petroleum lubricating oils.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "Petroleum Lubricating Oil and Grease Manufacturing",
    slug: "petroleum-lubricating-oil-and-grease-manufacturing"
  },
  "324199": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing petroleum products (except asphalt paving, roofing, and saturated materials and lubricating oils and greases) from refined petroleum and coal products made in coke ovens not integrated with a steel mill. Illustrative Examples: Biodiesel fuels not made in petroleum refineries and blended with purchased refined petroleum, Coke oven products (e.g., coke, gases, tars) made in coke oven establishments, Petroleum briquettes made from refined petroleum, Petroleum jelly made from refined petroleum, Petroleum waxes made from refined petroleum.",
    industries: ["oil-and-coal-product-manufacturing"],
    name: "All Other Petroleum and Coal Products Manufacturing",
    slug: "all-other-petroleum-and-coal-products-manufacturing"
  },
  "325110": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing acyclic (i.e., aliphatic) hydrocarbons such as ethylene, propylene, and butylene made from refined petroleum or liquid hydrocarbons and/or (2) manufacturing cyclic aromatic hydrocarbons such as benzene, toluene, styrene, xylene, ethyl benzene, and cumene made from refined petroleum or liquid hydrocarbons.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Petrochemical Manufacturing",
    slug: "petrochemical-manufacturing"
  },
  "325120": {
    description: "This industry comprises establishments primarily engaged in manufacturing industrial organic and inorganic gases in compressed, liquid, and solid forms.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Industrial Gas Manufacturing",
    slug: "industrial-gas-manufacturing"
  },
  "325130": {
    description: "This industry comprises establishments primarily engaged in manufacturing synthetic organic and inorganic dyes and pigments, such as lakes and toners (except electrostatic and photographic).",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Synthetic Dye and Pigment Manufacturing",
    slug: "synthetic-dye-and-pigment-manufacturing"
  },
  "325180": {
    description: "This industry comprises establishments primarily engaged in manufacturing basic inorganic chemicals (except industrial gases and synthetic dyes and pigments). Illustrative Examples: Alkalies manufacturing, Aluminum compounds, not specified elsewhere by process, manufacturing, Carbides (e.g., baron, calcium, silicon, tungsten) manufacturing, Carbon black manufacturing, Chlorine manufacturing, Hydrochloric acid manufacturing, Potassium inorganic compounds, not specified elsewhere by process, manufacturing, Radioactive isotopes manufacturing, Sulfides and sulfites manufacturing, Sulfuric acid manufacturing.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Other Basic Inorganic Chemical Manufacturing",
    slug: "other-basic-inorganic-chemical-manufacturing"
  },
  "325193": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing nonpotable ethyl alcohol.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Ethyl Alcohol Manufacturing",
    slug: "ethyl-alcohol-manufacturing"
  },
  "325194": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) distilling wood or gum into products, such as tall oil and wood distillates; (2) distilling coal tars; (3) manufacturing wood or gum chemicals, such as naval stores, natural tanning materials, charcoal briquettes, and charcoal (except activated); and (4) manufacturing cyclic crudes or cyclic intermediates (i.e., hydrocarbons, except aromatic petrochemicals) from refined petroleum or natural gas.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "Cyclic Crude, Intermediate, and Gum and Wood Chemical Manufacturing",
    slug: "cyclic-crude-intermediate-and-gum-and-wood-chemical-manufacturing"
  },
  "325199": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing basic organic chemical products (except aromatic petrochemicals, industrial gases, synthetic organic dyes and pigments, gum and wood chemicals, cyclic crudes and intermediates, and ethyl alcohol). Illustrative Examples: Biodiesel fuels not made in petroleum refineries and not blended with petroleum, Calcium organic compounds, not specified elsewhere by process, manufacturing, Carbon organic compounds, not specified elsewhere by process, manufacturing, Enzyme proteins (i.e., basic synthetic chemicals) (except pharmaceutical use) manufacturing, Organo-inorganic compound manufacturing, Fatty acids (e.g., margaric, oleic, stearic) manufacturing, Plasticizers (i.e., basic synthetic chemicals) manufacturing, Raw organic waxes manufacturing, Refrigerant mixtures and blends manufacturing, Silicone (except resins) manufacturing, Synthetic sweeteners (i.e., sweetening agents) manufacturing.",
    industries: ["chemical-raw-materials-manufacturing"],
    name: "All Other Basic Organic Chemical Manufacturing",
    slug: "all-other-basic-organic-chemical-manufacturing"
  },
  "325211": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing resins, plastics materials, and nonvulcanizable thermoplastic elastomers and mixing and blending resins on a custom basis and/or (2) manufacturing noncustomized synthetic resins.",
    industries: ["artificial-rubber-and-synthetic-fiber-manufacturing"],
    name: "Plastics Material and Resin Manufacturing",
    slug: "plastics-material-and-resin-manufacturing"
  },
  "325212": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing synthetic rubber.",
    industries: ["artificial-rubber-and-synthetic-fiber-manufacturing"],
    name: "Synthetic Rubber Manufacturing",
    slug: "synthetic-rubber-manufacturing"
  },
  "325220": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing cellulosic (e.g., rayon, acetate) and noncellulosic (e.g., nylon, polyolefin, polyester) fibers and filaments in the form of monofilament, filament yarn, staple, or tow or (2) manufacturing and texturizing cellulosic and noncellulosic fibers and filaments.",
    industries: ["artificial-rubber-and-synthetic-fiber-manufacturing"],
    name: "Artificial and Synthetic Fibers and Filaments Manufacturing",
    slug: "artificial-and-synthetic-fibers-and-filaments-manufacturing"
  },
  "325311": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing nitrogenous fertilizer materials; (2) manufacturing fertilizers from sewage or animal waste; and (3) manufacturing nitrogenous materials and mixing with other ingredients into fertilizers. Cross-References.",
    industries: ["agricultural-chemical-manufacturing"],
    name: "Nitrogenous Fertilizer Manufacturing",
    slug: "nitrogenous-fertilizer-manufacturing"
  },
  "325312": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing phosphatic fertilizer materials or (2) manufacturing phosphatic materials and mixing with other ingredients into fertilizers. Cross-References.",
    industries: ["agricultural-chemical-manufacturing"],
    name: "Phosphatic Fertilizer Manufacturing",
    slug: "phosphatic-fertilizer-manufacturing"
  },
  "325314": {
    description: "This U.S. industry comprises establishments primarily engaged in mixing ingredients made elsewhere into fertilizers.",
    industries: ["agricultural-chemical-manufacturing"],
    name: "Fertilizer (Mixing Only) Manufacturing",
    slug: "fertilizer-mixing-only-manufacturing"
  },
  "325315": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing compost through the controlled aerobic, biological decomposition and curing of biodegradable materials.",
    industries: ["agricultural-chemical-manufacturing"],
    name: "Compost Manufacturing",
    slug: "compost-manufacturing"
  },
  "325320": {
    description: "This industry comprises establishments primarily engaged in the formulation and preparation of agricultural and household pest control chemicals (except fertilizers).",
    industries: ["agricultural-chemical-manufacturing"],
    name: "Pesticide and Other Agricultural Chemical Manufacturing",
    slug: "pesticide-and-other-agricultural-chemical-manufacturing"
  },
  "325411": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing uncompounded medicinal chemicals and their derivatives (i.e., generally for use by pharmaceutical preparation manufacturers) and/or (2) grading, grinding, and milling uncompounded botanicals.",
    industries: ["pharmaceutical-manufacturing"],
    name: "Medicinal and Botanical Manufacturing",
    slug: "medicinal-and-botanical-manufacturing"
  },
  "325412": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing in-vivo diagnostic substances and pharmaceutical preparations (except biological) intended for internal and external consumption in dose forms, such as ampoules, tablets, capsules, vials, ointments, powders, solutions, and suspensions.",
    industries: ["pharmaceutical-manufacturing"],
    name: "Pharmaceutical Preparation Manufacturing",
    slug: "pharmaceutical-preparation-manufacturing"
  },
  "325413": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing in-vitro (i.e., not taken internally) diagnostic substances, such as chemical, biological, or radioactive substances. The substances are used for diagnostic tests that are performed in test tubes, petri dishes, machines, and other diagnostic test-type devices. Cross-References.",
    industries: ["pharmaceutical-manufacturing"],
    name: "In-Vitro Diagnostic Substance Manufacturing",
    slug: "in-vitro-diagnostic-substance-manufacturing"
  },
  "325414": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing vaccines, toxoids, blood fractions, and culture media of plant or animal origin (except diagnostic).",
    industries: ["pharmaceutical-manufacturing"],
    name: "Biological Product (except Diagnostic) Manufacturing",
    slug: "biological-product-except-diagnostic-manufacturing"
  },
  "325510": {
    description: "This industry comprises establishments primarily engaged in (1) mixing pigments, solvents, and binders into paints and other coatings, such as stains, varnishes, lacquers, enamels, shellacs, and water-repellent coatings for concrete and masonry, and/or (2) manufacturing allied paint products, such as putties, paint and varnish removers, paint brush cleaners, and frit.",
    industries: ["paint-coating-and-adhesive-manufacturing"],
    name: "Paint and Coating Manufacturing",
    slug: "paint-and-coating-manufacturing"
  },
  "325520": {
    description: "This industry comprises establishments primarily engaged in manufacturing adhesives, glues, and caulking compounds.",
    industries: ["paint-coating-and-adhesive-manufacturing"],
    name: "Adhesive Manufacturing",
    slug: "adhesive-manufacturing"
  },
  "325611": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing and packaging bath, facial, and hand soaps, hand sanitizers, and other detergents, such as laundry and dishwashing detergents; toothpaste gels and tooth powders; and natural glycerin.",
    industries: ["soap-and-cleaning-product-manufacturing"],
    name: "Soap and Other Detergent Manufacturing",
    slug: "soap-and-other-detergent-manufacturing"
  },
  "325612": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing and packaging polishes and specialty cleaning preparations.",
    industries: ["soap-and-cleaning-product-manufacturing"],
    name: "Polish and Other Sanitation Good Manufacturing",
    slug: "polish-and-other-sanitation-good-manufacturing"
  },
  "325613": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing bulk surface active agents for use as wetting agents, emulsifiers, and penetrants and/or (2) manufacturing textile and leather finishing agents used to reduce tension or speed the drying process.",
    industries: ["soap-and-cleaning-product-manufacturing"],
    name: "Surface Active Agent Manufacturing",
    slug: "surface-active-agent-manufacturing"
  },
  "325620": {
    description: "This industry comprises establishments primarily engaged in preparing, blending, compounding, and packaging toilet preparations, such as perfumes, shaving preparations, hair preparations, face creams, lotions (including sunscreens), and other cosmetic preparations. Cross-References.",
    industries: ["personal-care-product-manufacturing"],
    name: "Toilet Preparation Manufacturing",
    slug: "toilet-preparation-manufacturing"
  },
  "325910": {
    description: "This industry comprises establishments primarily engaged in manufacturing printing and inkjet inks and inkjet cartridges.",
    industries: ["chemical-manufacturing"],
    name: "Printing Ink Manufacturing",
    slug: "printing-ink-manufacturing"
  },
  "325920": {
    description: "This industry comprises establishments primarily engaged in manufacturing explosives.",
    industries: ["chemical-manufacturing"],
    name: "Explosives Manufacturing",
    slug: "explosives-manufacturing"
  },
  "325991": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) custom mixing and blending plastics resins made elsewhere or (2) reformulating plastics resins from recycled plastics products. Cross-References.",
    industries: ["chemical-manufacturing"],
    name: "Custom Compounding of Purchased Resins",
    slug: "custom-compounding-of-purchased-resins"
  },
  "325992": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing sensitized film, sensitized paper, sensitized cloth, sensitized plates, toners (i.e., for photocopiers, laser printers, and similar electrostatic printing devices), toner cartridges, and photographic chemicals.",
    industries: ["chemical-manufacturing"],
    name: "Photographic Film, Paper, Plate, and Chemical Manufacturing",
    slug: "photographic-film-paper-plate-and-chemical-manufacturing"
  },
  "325998": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing chemical products (except basic chemicals, resins, and synthetic rubber; cellulosic and noncellulosic fibers and filaments; pesticides, fertilizers, and other agricultural chemicals; pharmaceuticals and medicines; paints, coatings and adhesives; soaps, cleaning compounds, and toilet preparations; printing inks; explosives; custom compounding of purchased resins; and photographic films, papers, plates, chemicals, and copy toners). Illustrative Examples: Activated carbon and charcoal manufacturing, Antifreeze preparations manufacturing, Electronic cigarette vapor liquids, including closed refills, manufacturing, Industrial salt manufacturing, Lighter fluids (e.g., charcoal, cigarette) manufacturing, Matches and matchbook manufacturing, Pyrotechnics (e.g., flares, flashlight bombs, signals) manufacturing, Sugar substitutes (i.e., synthetic sweeteners blended with other ingredients) made from purchased synthetic sweeteners, Swimming pool chemical preparations manufacturing, Writing inks manufacturing.",
    industries: ["chemical-manufacturing"],
    name: "All Other Miscellaneous Chemical Product and Preparation Manufacturing",
    slug: "all-other-miscellaneous-chemical-product-and-preparation-manufacturing"
  },
  "326111": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) converting plastics resins into plastics bags or pouches and/or (2) forming, coating, or laminating plastics film or sheet into single-web or multiweb plastics bags or pouches.  Establishments in this industry may print on the bags or pouches they manufacture.",
    industries: ["plastics-manufacturing"],
    name: "Plastics Bag and Pouch Manufacturing",
    slug: "plastics-bag-and-pouch-manufacturing"
  },
  "326112": {
    description: "This U.S. industry comprises establishments primarily engaged in converting plastics resins into plastics packaging (flexible) film and packaging sheet.",
    industries: ["packaging-and-containers-manufacturing"],
    name: "Plastics Packaging Film and Sheet (including Laminated) Manufacturing",
    slug: "plastics-packaging-film-and-sheet-including-laminated-manufacturing"
  },
  "326113": {
    description: "This U.S. industry comprises establishments primarily engaged in converting plastics resins into plastics film and unlaminated sheet (except packaging).",
    industries: ["plastics-manufacturing"],
    name: "Unlaminated Plastics Film and Sheet (except Packaging) Manufacturing",
    slug: "unlaminated-plastics-film-and-sheet-except-packaging-manufacturing"
  },
  "326121": {
    description: "This U.S. industry comprises establishments primarily engaged in converting plastics resins into nonrigid plastics profile shapes (except film, sheet, bags, and hoses), such as rod, tube, and sausage casings.",
    industries: ["plastics-manufacturing"],
    name: "Unlaminated Plastics Profile Shape Manufacturing",
    slug: "unlaminated-plastics-profile-shape-manufacturing"
  },
  "326122": {
    description: "This U.S. industry comprises establishments primarily engaged in converting plastics resins into plastics pipes and pipe fittings.",
    industries: ["plastics-manufacturing"],
    name: "Plastics Pipe and Pipe Fitting Manufacturing",
    slug: "plastics-pipe-and-pipe-fitting-manufacturing"
  },
  "326130": {
    description: "This industry comprises establishments primarily engaged in laminating plastics profile shapes such as plate, sheet (except packaging), and rod. The lamination process generally involves bonding or impregnating profiles with plastics resins and compressing them under heat.",
    industries: ["plastics-manufacturing"],
    name: "Laminated Plastics Plate, Sheet (except Packaging), and Shape Manufacturing",
    slug: "laminated-plastics-plate-sheet-except-packaging-and-shape-manufacturing"
  },
  "326140": {
    description: "This industry comprises establishments primarily engaged in manufacturing polystyrene foam products. Cross-References.",
    industries: ["plastics-manufacturing"],
    name: "Polystyrene Foam Product Manufacturing",
    slug: "polystyrene-foam-product-manufacturing"
  },
  "326150": {
    description: "This industry comprises establishments primarily engaged in manufacturing plastics foam products (except polystyrene). Cross-References.",
    industries: ["plastics-manufacturing"],
    name: "Urethane and Other Foam Product (except Polystyrene) Manufacturing",
    slug: "urethane-and-other-foam-product-except-polystyrene-manufacturing"
  },
  "326160": {
    description: "This industry comprises establishments primarily engaged in manufacturing plastics bottles. Cross-References.",
    industries: ["plastics-manufacturing"],
    name: "Plastics Bottle Manufacturing",
    slug: "plastics-bottle-manufacturing"
  },
  "326191": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing plastics or fiberglass plumbing fixtures.  Examples of products made by these establishments are plastics or fiberglass bathtubs, hot tubs, portable toilets, and shower stalls.",
    industries: ["plastics-manufacturing"],
    name: "Plastics Plumbing Fixture Manufacturing",
    slug: "plastics-plumbing-fixture-manufacturing"
  },
  "326199": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing plastics products (except film, sheet, bags, profile shapes, pipes, pipe fittings, laminates, foam products, bottles, plumbing fixtures, and hoses). Illustrative Examples: Inflatable plastics swimming pool rafts and similar flotation devices manufacturing, Plastics air mattresses manufacturing, Plastics bottle caps and lids manufacturing, Plastics bowls and bowl covers manufacturing, Plastics clothes hangers manufacturing, Plastics cups (except foam) manufacturing, Plastics dinnerware (except foam) manufacturing, Plastics gloves manufacturing, Plastics hardware manufacturing, Plastics siding manufacturing, Plastics trash containers manufacturing, Resilient floor coverings (e.g., sheet, tiles) manufacturing.",
    industries: ["plastics-manufacturing"],
    name: "All Other Plastics Product Manufacturing",
    slug: "all-other-plastics-product-manufacturing"
  },
  "326211": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing tires and inner tubes from natural and synthetic rubber. Cross-References.",
    industries: ["rubber-products-manufacturing"],
    name: "Tire Manufacturing (except Retreading)",
    slug: "tire-manufacturing-except-retreading"
  },
  "326212": {
    description: "This U.S. industry comprises establishments primarily engaged in retreading or rebuilding tires.",
    industries: ["rubber-products-manufacturing"],
    name: "Tire Retreading",
    slug: "tire-retreading"
  },
  "326220": {
    description: "This industry comprises establishments primarily engaged in manufacturing rubber hose and/or plastics (reinforced) hose and belting from natural and synthetic rubber and/or plastics resins.  Establishments manufacturing garden hoses from purchased hose are included in this industry.",
    industries: ["rubber-products-manufacturing"],
    name: "Rubber and Plastics Hoses and Belting Manufacturing",
    slug: "rubber-and-plastics-hoses-and-belting-manufacturing"
  },
  "326291": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing rubber goods (except tubing) for mechanical applications, using the processes of molding, extruding or lathe-cutting.  Products of this industry are generally parts for motor vehicles, machinery, and equipment. Cross-References.",
    industries: ["rubber-products-manufacturing"],
    name: "Rubber Product Manufacturing for Mechanical Use",
    slug: "rubber-product-manufacturing-for-mechanical-use"
  },
  "326299": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing rubber products (except tires; hoses and belting; and molded, extruded, and lathe-cut rubber goods for mechanical applications (except rubber tubing)) from natural and synthetic rubber.  Establishments manufacturing rubber tubing made from natural and synthetic rubber, regardless of process used, are included in this industry. Illustrative Examples: Birth control devices (i.e., diaphragms, prophylactics) manufacturing, Latex foam rubber manufacturing, Reclaiming rubber from waste and scrap, Rubber balloons manufacturing, Rubber bands manufacturing, Rubber floor mats (e.g., door, bath) manufacturing, Rubber hair care products (e.g., combs, curlers) manufacturing, Rubber tubing manufacturing.",
    industries: ["rubber-products-manufacturing"],
    name: "All Other Rubber Product Manufacturing",
    slug: "all-other-rubber-product-manufacturing"
  },
  "327110": {
    description: "This industry comprises establishments primarily engaged in shaping, molding, glazing, and firing pottery, ceramics, plumbing fixtures, and electrical supplies made entirely or partly of clay or other ceramic materials. Illustrative Examples: Bathroom accessories, vitreous china and earthenware, manufacturing, Ceramic or ferrite permanent magnets manufacturing, Chemical stoneware (i.e., pottery products) manufacturing, Clay and ceramic statuary manufacturing, Earthenware table and kitchen articles, coarse, manufacturing, Porcelain electrical insulators manufacturing, Vitreous china plumbing fixtures manufacturing.",
    industries: ["clay-and-refractory-products-manufacturing"],
    name: "Pottery, Ceramics, and Plumbing Fixture Manufacturing",
    slug: "pottery-ceramics-and-plumbing-fixture-manufacturing"
  },
  "327120": {
    description: "This industry comprises establishments primarily engaged in shaping, molding, baking, burning, or hardening clay refractories, nonclay refractories, ceramic tile, structural clay tile, brick, and other structural clay building materials.  A refractory is a material that will retain its shape and chemical identity when subjected to high temperatures and is used in applications that require extreme resistance to heat, such as furnace linings.",
    industries: ["clay-and-refractory-products-manufacturing"],
    name: "Clay Building Material and Refractories Manufacturing",
    slug: "clay-building-material-and-refractories-manufacturing"
  },
  "327211": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing flat glass by melting silica sand or cullet or (2) manufacturing both flat glass and laminated glass by melting silica sand or cullet. Cross-References.",
    industries: ["glass-product-manufacturing"],
    name: "Flat Glass Manufacturing",
    slug: "flat-glass-manufacturing"
  },
  "327212": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing glass by melting silica sand or cullet and making pressed, blown, or shaped glass or glassware (except glass packaging containers).",
    industries: ["glass-product-manufacturing"],
    name: "Other Pressed and Blown Glass and Glassware Manufacturing",
    slug: "other-pressed-and-blown-glass-and-glassware-manufacturing"
  },
  "327213": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing glass packaging, bottling, and canning containers.",
    industries: ["glass-product-manufacturing"],
    name: "Glass Container Manufacturing",
    slug: "glass-container-manufacturing"
  },
  "327215": {
    description: "This U.S. industry comprises establishments primarily engaged in coating, laminating, tempering, or shaping purchased glass.",
    industries: ["glass-product-manufacturing"],
    name: "Glass Product Manufacturing Made of Purchased Glass",
    slug: "glass-product-manufacturing-made-of-purchased-glass"
  },
  "327310": {
    description: "This industry comprises establishments primarily engaged in manufacturing Portland, natural, masonry, pozzolanic, and other hydraulic cements.  Cement manufacturing establishments may calcine earths or mine, quarry, manufacture, or purchase lime.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Cement Manufacturing",
    slug: "cement-manufacturing"
  },
  "327320": {
    description: "This industry comprises establishments, such as batch plants or mix plants, primarily engaged in manufacturing concrete delivered to a purchaser in a plastic and unhardened state.  Ready-mix concrete manufacturing establishments may mine, quarry, or purchase sand and gravel.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Ready-Mix Concrete Manufacturing",
    slug: "ready-mix-concrete-manufacturing"
  },
  "327331": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing concrete block and brick.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Concrete Block and Brick Manufacturing",
    slug: "concrete-block-and-brick-manufacturing"
  },
  "327332": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing concrete pipe.",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Concrete Pipe Manufacturing",
    slug: "concrete-pipe-manufacturing"
  },
  "327390": {
    description: "This industry comprises establishments primarily engaged in manufacturing concrete products (except block, brick, and pipe).",
    industries: ["glass-ceramics-and-concrete-manufacturing"],
    name: "Other Concrete Product Manufacturing",
    slug: "other-concrete-product-manufacturing"
  },
  "327410": {
    description: "This industry comprises establishments primarily engaged in manufacturing lime from calcitic limestone, dolomitic limestone, or other calcareous materials, such as coral, chalk, and shells.  Lime manufacturing establishments may mine, quarry, collect, or purchase the sources of calcium carbonate. Cross-References.",
    industries: ["lime-and-gypsum-products-manufacturing"],
    name: "Lime Manufacturing",
    slug: "lime-manufacturing"
  },
  "327420": {
    description: "This industry comprises establishments primarily engaged in manufacturing gypsum products, such as wallboard, plaster, plasterboard, molding, ornamental moldings, statuary, and architectural plaster work.  Gypsum product manufacturing establishments may mine, quarry, or purchase gypsum. Cross-References.",
    industries: ["lime-and-gypsum-products-manufacturing"],
    name: "Gypsum Product Manufacturing",
    slug: "gypsum-product-manufacturing"
  },
  "327910": {
    description: "This industry comprises establishments primarily engaged in manufacturing abrasive grinding wheels of natural or synthetic materials, abrasive-coated products, and other abrasive products. Illustrative Examples: Aluminum oxide (fused) abrasives manufacturing, Buffing and polishing wheels, abrasive and nonabrasive, manufacturing, Diamond dressing wheels manufacturing, Sandpaper manufacturing, Silicon carbide abrasives manufacturing, Whetstones manufacturing.",
    industries: ["abrasives-and-nonmetallic-minerals-manufacturing"],
    name: "Abrasive Product Manufacturing",
    slug: "abrasive-product-manufacturing"
  },
  "327991": {
    description: "This U.S. industry comprises establishments primarily engaged in cutting, shaping, and finishing granite, marble, limestone, slate, and other stone for building and miscellaneous uses.  Stone product manufacturing establishments may mine, quarry, or purchase stone.",
    industries: ["abrasives-and-nonmetallic-minerals-manufacturing"],
    name: "Cut Stone and Stone Product Manufacturing",
    slug: "cut-stone-and-stone-product-manufacturing"
  },
  "327992": {
    description: "This U.S. industry comprises establishments primarily engaged in calcining, dead burning, or otherwise processing beyond beneficiation, clays, ceramic and refractory minerals, barite, and miscellaneous nonmetallic minerals. Cross-References.",
    industries: ["abrasives-and-nonmetallic-minerals-manufacturing"],
    name: "Ground or Treated Mineral and Earth Manufacturing",
    slug: "ground-or-treated-mineral-and-earth-manufacturing"
  },
  "327993": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing mineral wool and mineral wool (i.e., fiberglass) insulation products made of such siliceous materials as rock, slag, and glass or combinations thereof. Cross-References.",
    industries: ["abrasives-and-nonmetallic-minerals-manufacturing"],
    name: "Mineral Wool Manufacturing",
    slug: "mineral-wool-manufacturing"
  },
  "327999": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing nonmetallic mineral products (except pottery, ceramics, and plumbing fixtures; clay building materials and refractories; glass and glass products; cement; ready-mix concrete; concrete products; lime; gypsum products; abrasive products; cut stone and stone products; ground and treated minerals and earth; and mineral wool). Illustrative Examples: Dry mix concrete manufacturing, Mica products manufacturing, Manmade and engineered proppants (e.g., resin-coated sand, ceramic materials) manufacturing, Stucco and stucco products manufacturing, Synthetic stones, for gem stones and industrial use, manufacturing .",
    industries: ["abrasives-and-nonmetallic-minerals-manufacturing"],
    name: "All Other Miscellaneous Nonmetallic Mineral Product Manufacturing",
    slug: "all-other-miscellaneous-nonmetallic-mineral-product-manufacturing"
  },
  "331110": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) direct reduction of iron ore; (2) manufacturing pig iron in molten or solid form; (3) converting pig iron into steel; (4) making steel; (5) making steel and manufacturing shapes (e.g., bar, plate, rod, sheet, strip, wire); (6) making steel and forming pipe and tube; and (7) manufacturing electrometallurgical ferroalloys. Ferroalloys add critical elements, such as silicon and manganese for carbon steel and chromium, vanadium, tungsten, titanium, and molybdenum for low- and high-alloy metals.  Ferroalloys include iron-rich alloys and more pure forms of elements added during the steel manufacturing process that alter or improve the characteristics of the metal.",
    industries: ["primary-metal-manufacturing"],
    name: "Iron and Steel Mills and Ferroalloy Manufacturing",
    slug: "iron-and-steel-mills-and-ferroalloy-manufacturing"
  },
  "331210": {
    description: "This industry comprises establishments primarily engaged in manufacturing welded, riveted, or seamless pipe and tube from purchased iron or steel. Cross-References.",
    industries: ["primary-metal-manufacturing"],
    name: "Iron and Steel Pipe and Tube Manufacturing from Purchased Steel",
    slug: "iron-and-steel-pipe-and-tube-manufacturing-from-purchased-steel"
  },
  "331221": {
    description: "This U.S. industry comprises establishments primarily engaged in rolling or drawing shapes (except wire), such as plate, sheet, strip, rod, and bar, from purchased steel.",
    industries: ["primary-metal-manufacturing"],
    name: "Rolled Steel Shape Manufacturing",
    slug: "rolled-steel-shape-manufacturing"
  },
  "331222": {
    description: "This U.S. industry comprises establishments primarily engaged in drawing wire from purchased steel.",
    industries: ["primary-metal-manufacturing"],
    name: "Steel Wire Drawing",
    slug: "steel-wire-drawing"
  },
  "331313": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) refining alumina (i.e., aluminum oxide) generally from bauxite; (2) making aluminum from alumina; and/or (3) making aluminum from alumina and rolling, drawing, extruding, or casting the aluminum they make into primary forms.  Establishments in this industry may make primary aluminum or aluminum-based alloys from alumina.",
    industries: ["primary-metal-manufacturing"],
    name: "Alumina Refining and Primary Aluminum Production",
    slug: "alumina-refining-and-primary-aluminum-production"
  },
  "331314": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) recovering aluminum and aluminum alloys from scrap and/or dross (i.e., secondary smelting) and making billet or ingot (except by rolling) and/or (2) manufacturing alloys, powder, paste, or flake from purchased aluminum.",
    industries: ["primary-metal-manufacturing"],
    name: "Secondary Smelting and Alloying of Aluminum",
    slug: "secondary-smelting-and-alloying-of-aluminum"
  },
  "331315": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) flat rolling or continuous casting sheet, plate, foil and welded tube from purchased aluminum and/or (2) recovering aluminum from scrap and flat rolling or continuous casting sheet, plate, foil, and welded tube in integrated mills. Cross-References.",
    industries: ["primary-metal-manufacturing"],
    name: "Aluminum Sheet, Plate, and Foil Manufacturing",
    slug: "aluminum-sheet-plate-and-foil-manufacturing"
  },
  "331318": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) rolling, drawing, or extruding shapes (except flat rolled sheet, plate, foil, and welded tube) from purchased aluminum and/or (2) recovering aluminum from scrap and rolling, drawing, or extruding shapes (except flat rolled sheet, plate, foil, and welded tube) in integrated mills. Illustrative Examples: Aluminum bar made by extruding purchased aluminum, Nails, aluminum, made in wire drawing plants , Structural shapes made by rolling purchased aluminum, Rod made by extruding purchased aluminum, Tube made by drawing or extruding purchased aluminum, Wire, bare, made in aluminum wire drawing plants.",
    industries: ["primary-metal-manufacturing"],
    name: "Other Aluminum Rolling, Drawing, and Extruding",
    slug: "other-aluminum-rolling-drawing-and-extruding"
  },
  "331410": {
    description: "This industry comprises establishments primarily engaged in (1) smelting ores into nonferrous metals and/or (2) the primary refining of nonferrous metals (except aluminum) by electrolytic methods or other processes.",
    industries: ["primary-metal-manufacturing"],
    name: "Nonferrous Metal (except Aluminum) Smelting and Refining",
    slug: "nonferrous-metal-except-aluminum-smelting-and-refining"
  },
  "331420": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) recovering copper or copper alloys from scraps; (2) alloying purchased copper; (3) rolling, drawing, or extruding shapes (e.g., bar, plate, sheet, strip, tube, wire) from purchased copper; and (4) recovering copper or copper alloys from scrap and rolling, drawing, or extruding shapes (e.g., bar, plate, sheet, strip, tube, wire).",
    industries: ["primary-metal-manufacturing"],
    name: "Copper Rolling, Drawing, Extruding, and Alloying",
    slug: "copper-rolling-drawing-extruding-and-alloying"
  },
  "331491": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) rolling, drawing, or extruding shapes (e.g., bar, plate, sheet, strip, tube) from purchased nonferrous metals and/or (2) recovering nonferrous metals from scrap and rolling, drawing, and/or extruding shapes (e.g., bar, plate, sheet, strip, tube) in integrated mills.",
    industries: ["primary-metal-manufacturing"],
    name: "Nonferrous Metal (except Copper and Aluminum) Rolling, Drawing, and Extruding",
    slug: "nonferrous-metal-except-copper-and-aluminum-rolling-drawing-and-extruding"
  },
  "331492": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) alloying purchased nonferrous metals and/or (2) recovering nonferrous metals from scrap.  Establishments in this industry make primary forms (e.g., bar, billet, bloom, cake, ingot, slab, slug, wire) using smelting or refining processes.",
    industries: ["primary-metal-manufacturing"],
    name: "Secondary Smelting, Refining, and Alloying of Nonferrous Metal (except Copper and Aluminum)",
    slug: "secondary-smelting-refining-and-alloying-of-nonferrous-metal-except-copper-and-aluminum"
  },
  "331511": {
    description: "This U.S. industry comprises establishments primarily engaged in pouring molten pig iron or iron alloys into molds to manufacture castings (e.g., cast iron manhole covers, cast iron pipe, cast iron skillets).  Establishments in this industry purchase iron made in other establishments. Cross-References.",
    industries: ["primary-metal-manufacturing"],
    name: "Iron Foundries",
    slug: "iron-foundries"
  },
  "331512": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing steel investment castings.  Investment molds are formed by covering a wax shape with a refractory slurry.  After the refractory slurry hardens, the wax is melted, leaving a seamless mold.  Investment molds provide highly detailed, consistent castings.  Establishments in this industry purchase steel made in other establishments.",
    industries: ["primary-metal-manufacturing"],
    name: "Steel Investment Foundries",
    slug: "steel-investment-foundries"
  },
  "331513": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing steel castings (except steel investment castings).  Establishments in this industry purchase steel made in other establishments.",
    industries: ["primary-metal-manufacturing"],
    name: "Steel Foundries (except Investment)",
    slug: "steel-foundries-except-investment"
  },
  "331523": {
    description: "This U.S. industry comprises establishments primarily engaged in introducing molten nonferrous metal, under high pressure, into molds or dies to make nonferrous metal die-castings.  Establishments in this industry purchase nonferrous metals made in other establishments.",
    industries: ["primary-metal-manufacturing"],
    name: "Nonferrous Metal Die-Casting Foundries",
    slug: "nonferrous-metal-die-casting-foundries"
  },
  "331524": {
    description: "This U.S. industry comprises establishments primarily engaged in pouring molten aluminum into molds to manufacture aluminum castings (except nonferrous die-castings).  Establishments in this industry purchase aluminum made in other establishments.",
    industries: ["primary-metal-manufacturing"],
    name: "Aluminum Foundries (except Die-Casting)",
    slug: "aluminum-foundries-except-die-casting"
  },
  "331529": {
    description: "This U.S. industry comprises establishments primarily engaged in pouring molten nonferrous metals (except aluminum) into molds to manufacture nonferrous castings (except nonferrous die-castings and aluminum castings).  Establishments in this industry purchase nonferrous metals, such as copper, nickel, lead, and zinc, made in other establishments.",
    industries: ["primary-metal-manufacturing"],
    name: "Other Nonferrous Metal Foundries (except Die-Casting)",
    slug: "other-nonferrous-metal-foundries-except-die-casting"
  },
  "332111": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing iron and steel forgings from purchased iron and steel by hammering mill shapes.  Establishments making iron and steel forgings and further manufacturing (e.g., machining, assembling) a specific manufactured product are classified in the industry of the finished product.  Iron and steel forging establishments may perform surface finishing operations, such as cleaning and deburring, on the forgings they manufacture.",
    industries: ["fabricated-metal-products"],
    name: "Iron and Steel Forging",
    slug: "iron-and-steel-forging"
  },
  "332112": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing nonferrous forgings from purchased nonferrous metals by hammering mill shapes.  Establishments making nonferrous forgings and further manufacturing (e.g., machining, assembling) a specific manufactured product are classified in the industry of the finished product.  Nonferrous forging establishments may perform surface finishing operations, such as cleaning and deburring, on the forgings they manufacture.",
    industries: ["fabricated-metal-products"],
    name: "Nonferrous Forging",
    slug: "nonferrous-forging"
  },
  "332114": {
    description: "This U.S. industry comprises establishments primarily engaged in custom roll forming metal products by use of rotary motion of rolls with various contours to bend or shape the products. Cross-References.",
    industries: ["fabricated-metal-products"],
    name: "Custom Roll Forming",
    slug: "custom-roll-forming"
  },
  "332117": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing powder metallurgy products using any of the various powder metallurgy processing techniques, such as pressing and sintering or metal injection molding.  Establishments in this industry generally make a wide range of parts on a job or order basis.",
    industries: ["fabricated-metal-products"],
    name: "Powder Metallurgy Part Manufacturing",
    slug: "powder-metallurgy-part-manufacturing"
  },
  "332119": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) stamping metal crowns and closures, such as bottle caps and home canning lids and rings, and/or (2) manufacturing other unfinished metal stampings and spinning unfinished metal products (except automotive, cans, and coins).  Establishments making metal stampings and metal spun products and further manufacturing (e.g., machining, assembling) a specific product are classified in the industry of the finished product.  Metal stamping and metal spun products establishments may perform surface finishing operations, such as cleaning and deburring, on the products they manufacture.",
    industries: ["fabricated-metal-products"],
    name: "Metal Crown, Closure, and Other Metal Stamping (except Automotive)",
    slug: "metal-crown-closure-and-other-metal-stamping-except-automotive"
  },
  "332215": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing metal kitchen cookware (except by casting (e.g., cast iron skillets) or stamped without further fabrication), utensils, and/or nonprecious and precious plated metal cutlery and flatware.",
    industries: ["cutlery-and-handtool-manufacturing"],
    name: "Metal Kitchen Cookware, Utensil, Cutlery, and Flatware (except Precious) Manufacturing",
    slug: "metal-kitchen-cookware-utensil-cutlery-and-flatware-except-precious-manufacturing"
  },
  "332216": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing saw blades, all types (including those for power sawing machines) and/or (2) manufacturing nonpowered handtools and edge tools.",
    industries: ["cutlery-and-handtool-manufacturing"],
    name: "Saw Blade and Handtool Manufacturing",
    slug: "saw-blade-and-handtool-manufacturing"
  },
  "332311": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing prefabricated metal buildings, panels, and sections.",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Prefabricated Metal Building and Component Manufacturing",
    slug: "prefabricated-metal-building-and-component-manufacturing"
  },
  "332312": {
    description: "This U.S. industry comprises establishments primarily engaged in fabricating structural metal products, such as assemblies of concrete reinforcing bars and fabricated bar joists.",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Fabricated Structural Metal Manufacturing",
    slug: "fabricated-structural-metal-manufacturing"
  },
  "332313": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fabricated metal plate work by cutting, punching, bending, shaping, and welding purchased metal plate.",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Plate Work Manufacturing",
    slug: "plate-work-manufacturing"
  },
  "332321": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing metal framed windows (i.e., typically using purchased glass) and metal doors.  Examples of products made by these establishments are metal door frames; metal framed window and door screens; and metal molding and trim (except automotive).",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Metal Window and Door Manufacturing",
    slug: "metal-window-and-door-manufacturing"
  },
  "332322": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing sheet metal work (except stampings).",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Sheet Metal Work Manufacturing",
    slug: "sheet-metal-work-manufacturing"
  },
  "332323": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing ornamental and architectural metal work, such as staircases, metal open steel flooring, fire escapes, railings, and scaffolding. Cross-References.",
    industries: ["architectural-and-structural-metal-manufacturing"],
    name: "Ornamental and Architectural Metal Work Manufacturing",
    slug: "ornamental-and-architectural-metal-work-manufacturing"
  },
  "332410": {
    description: "This industry comprises establishments primarily engaged in manufacturing power boilers and heat exchangers.  Establishments in this industry may perform installation in addition to manufacturing power boilers and heat exchangers.",
    industries: ["boilers-tanks-and-shipping-container-manufacturing"],
    name: "Power Boiler and Heat Exchanger Manufacturing",
    slug: "power-boiler-and-heat-exchanger-manufacturing"
  },
  "332420": {
    description: "This industry comprises establishments primarily engaged in cutting, forming, and joining heavy gauge metal to manufacture tanks, vessels, and other containers.",
    industries: ["boilers-tanks-and-shipping-container-manufacturing"],
    name: "Metal Tank (Heavy Gauge) Manufacturing",
    slug: "metal-tank-heavy-gauge-manufacturing"
  },
  "332431": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing metal cans, lids, and ends.",
    industries: ["boilers-tanks-and-shipping-container-manufacturing"],
    name: "Metal Can Manufacturing",
    slug: "metal-can-manufacturing"
  },
  "332439": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing metal (light gauge) containers (except cans). Illustrative Examples: Light gauge metal bins manufacturing, Light gauge metal drums manufacturing, Light gauge metal garbage cans manufacturing, Light gauge metal lunch boxes manufacturing, Light gauge metal mailboxes manufacturing, Light gauge metal tool boxes manufacturing, Light gauge metal vats manufacturing, Metal air cargo containers manufacturing, Metal barrels manufacturing, Vacuum bottles and jugs manufacturing.",
    industries: ["boilers-tanks-and-shipping-container-manufacturing"],
    name: "Other Metal Container Manufacturing",
    slug: "other-metal-container-manufacturing"
  },
  "332510": {
    description: "This industry comprises establishments primarily engaged in manufacturing metal hardware, such as metal hinges, metal handles, keys, and locks (except coin- or card-operated, time locks).",
    industries: ["construction-hardware-manufacturing"],
    name: "Hardware Manufacturing",
    slug: "hardware-manufacturing"
  },
  "332613": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing springs from purchased wire, strip, or rod.",
    industries: ["spring-and-wire-product-manufacturing"],
    name: "Spring Manufacturing",
    slug: "spring-manufacturing"
  },
  "332618": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fabricated wire products (except springs) made from purchased wire. Illustrative Examples: Barbed wire made from purchased wire, Chain link fencing and fence gates made from purchased wire, Metal baskets made from purchased wire, Paper clips made from purchased wire, Nails, brads, and staples made from purchased wire, Noninsulated wire cable made from purchased wire, Woven wire cloth made from purchased wire.",
    industries: ["spring-and-wire-product-manufacturing"],
    name: "Other Fabricated Wire Product Manufacturing",
    slug: "other-fabricated-wire-product-manufacturing"
  },
  "332710": {
    description: "This industry comprises establishments known as machine shops primarily engaged in machining metal and plastic parts and parts of other composite materials on a job or order basis.  Generally machine shop jobs are low volume using machine tools, such as lathes (including computer numerically controlled); automatic screw machines; and machines for boring, grinding, milling, and additive manufacturing.",
    industries: ["turned-products-and-fastener-manufacturing"],
    name: "Machine Shops",
    slug: "machine-shops"
  },
  "332721": {
    description: "This U.S. industry comprises establishments known as precision turned manufacturers primarily engaged in machining precision products of all materials on a job or order basis.  Generally precision turned product jobs are large volume using machines, such as automatic screw machines, rotary transfer machines, computer numerically controlled (CNC) lathes, or turning centers.",
    industries: ["turned-products-and-fastener-manufacturing"],
    name: "Precision Turned Product Manufacturing",
    slug: "precision-turned-product-manufacturing"
  },
  "332722": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing metal bolts, nuts, screws, rivets, washers, and other industrial fasteners using machines, such as headers, threaders, and nut forming machines.",
    industries: ["turned-products-and-fastener-manufacturing"],
    name: "Bolt, Nut, Screw, Rivet, and Washer Manufacturing",
    slug: "bolt-nut-screw-rivet-and-washer-manufacturing"
  },
  "332811": {
    description: "This U.S. industry comprises establishments primarily engaged in heat treating, such as annealing, tempering, and brazing, and cryogenically treating metals and metal products for the trade. Cross-References.",
    industries: ["metal-treatments"],
    name: "Metal Heat Treating",
    slug: "metal-heat-treating"
  },
  "332812": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) enameling, lacquering, and varnishing metals and metal products; (2) hot dip galvanizing metals and metal products; (3) engraving, chasing, or etching metals and metal products (except jewelry; personal goods carried on or about the person, such as compacts and cigarette cases; precious metal products (except precious plated flatware and other plated ware); and printing plates); (4) powder coating metals and metal products; and (5) providing other metal surfacing services for the trade.  Included in this industry are establishments that perform these processes on other materials, such as plastics, in addition to metals.",
    industries: ["metal-treatments"],
    name: "Metal Coating, Engraving (except Jewelry and Silverware), and Allied Services to Manufacturers",
    slug: "metal-coating-engraving-except-jewelry-and-silverware-and-allied-services-to-manufacturers"
  },
  "332813": {
    description: "This U.S. industry comprises establishments primarily engaged in electroplating, plating, anodizing, coloring, buffing, polishing, cleaning, and sandblasting metals and metal products for the trade.  Included in this industry are establishments that perform these processes on other materials, such as plastics, in addition to metals. Cross-References.",
    industries: ["metal-treatments"],
    name: "Electroplating, Plating, Polishing, Anodizing, and Coloring",
    slug: "electroplating-plating-polishing-anodizing-and-coloring"
  },
  "332911": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing industrial valves and valves for water works and municipal water systems. Illustrative Examples: Complete fire hydrants manufacturing, Industrial-type ball valves manufacturing, Industrial-type butterfly valves manufacturing, Industrial-type check valves manufacturing, Industrial-type gate valves manufacturing, Industrial-type globe valves manufacturing, Industrial-type plug valves manufacturing, Industrial-type solenoid valves (except fluid power) manufacturing, Industrial-type steam traps manufacturing, Valves for nuclear applications manufacturing.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Industrial Valve Manufacturing",
    slug: "industrial-valve-manufacturing"
  },
  "332912": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fluid power valves and hose fittings. Illustrative Examples: Fluid power aircraft subassemblies manufacturing, Hose assemblies for fluid power systems manufacturing, Hydraulic and pneumatic hose and tube fittings manufacturing, Hydraulic and pneumatic valves manufacturing.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Fluid Power Valve and Hose Fitting Manufacturing",
    slug: "fluid-power-valve-and-hose-fitting-manufacturing"
  },
  "332913": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing plumbing fixture fittings and trim of all materials, such as faucets, flush valves, and shower heads.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Plumbing Fixture Fitting and Trim Manufacturing",
    slug: "plumbing-fixture-fitting-and-trim-manufacturing"
  },
  "332919": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing metal valves (except industrial valves, fluid power valves, fluid power hose fittings, and plumbing fixture fittings and trim). Illustrative Examples: Aerosol valves manufacturing, Firefighting nozzles manufacturing, Lawn hose nozzles manufacturing, Lawn sprinklers manufacturing, Water traps manufacturing, Metal hose couplings (except fluid power) manufacturing, Metal pipe flanges and flange unions manufacturing, Plumbing and heating inline valves (e.g., check, cutoff, stop) manufacturing.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Other Metal Valve and Pipe Fitting Manufacturing",
    slug: "other-metal-valve-and-pipe-fitting-manufacturing"
  },
  "332991": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing ball and roller bearings of all materials. Cross-References.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Ball and Roller Bearing Manufacturing",
    slug: "ball-and-roller-bearing-manufacturing"
  },
  "332992": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing small arms ammunition.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Small Arms Ammunition Manufacturing",
    slug: "small-arms-ammunition-manufacturing"
  },
  "332993": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing ammunition (except small arms).  Examples of products made by these establishments are bombs, depth charges, rockets (except guided missiles), grenades, mines, and torpedoes.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Ammunition (except Small Arms) Manufacturing",
    slug: "ammunition-except-small-arms-manufacturing"
  },
  "332994": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing small arms, other ordnance, and/or ordnance accessories.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Small Arms, Ordnance, and Ordnance Accessories Manufacturing",
    slug: "small-arms-ordnance-and-ordnance-accessories-manufacturing"
  },
  "332996": {
    description: "This U.S. industry comprises establishments primarily engaged in fabricating, such as cutting, threading, and bending, metal pipes and pipe fittings made from purchased metal pipe.",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "Fabricated Pipe and Pipe Fitting Manufacturing",
    slug: "fabricated-pipe-and-pipe-fitting-manufacturing"
  },
  "332999": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fabricated metal products (except forgings and stampings, cutlery and handtools, architectural and structural metals, boilers, tanks, shipping containers, hardware, spring and wire products, machine shop products, turned products, screws, nuts and bolts, metal valves, ball and roller bearings, ammunition, small arms and other ordnances and accessories, and fabricated pipes and pipe fittings). Illustrative Examples: Foil containers (except bags) manufacturing, Industrial pattern manufacturing, Metal hair curlers manufacturing, Metal ironing boards manufacturing, Metal pallets manufacturing, Metal pipe hangers and supports manufacturing, Metal safes manufacturing, Metal vaults (except burial) manufacturing, Permanent metallic magnets manufacturing, Portable metal ladders manufacturing, Sanitary ware (e.g., bathtubs, lavatories, sinks), metal and enameled metal, manufacturing, Steel wool manufacturing .",
    industries: ["metal-valve-ball-and-roller-manufacturing"],
    name: "All Other Miscellaneous Fabricated Metal Product Manufacturing",
    slug: "all-other-miscellaneous-fabricated-metal-product-manufacturing"
  },
  "333111": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing agricultural and farm machinery and equipment, and other turf and grounds care equipment, including planting, harvesting, and grass mowing equipment (except lawn and garden-type). Illustrative Examples: Combines (i.e., harvester-threshers) manufacturing, Cotton ginning machinery manufacturing, Feed processing equipment, farm-type, manufacturing, Fertilizing machinery, farm-type, manufacturing, Grass mowing equipment (except lawn and garden) manufacturing, Haying machines manufacturing, Milking machines manufacturing, Planting machines, farm-type, manufacturing, Plows, farm-type, manufacturing, Poultry brooders, feeders, and waterers manufacturing, Tractors and attachments, farm-type, manufacturing.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Farm Machinery and Equipment Manufacturing",
    slug: "farm-machinery-and-equipment-manufacturing"
  },
  "333112": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing powered lawnmowers, lawn and garden tractors, and other home lawn and garden equipment, such as tillers, shredders, yard vacuums, and leaf blowers.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Lawn and Garden Tractor and Home Lawn and Garden Equipment Manufacturing",
    slug: "lawn-and-garden-tractor-and-home-lawn-and-garden-equipment-manufacturing"
  },
  "333120": {
    description: "This industry comprises establishments primarily engaged in manufacturing construction machinery, surface mining machinery, and logging equipment. Illustrative Examples: Backhoes manufacturing, Bulldozers manufacturing, Construction and surface mining-type rock drill bits manufacturing, Construction-type tractors and attachments manufacturing, Off-highway trucks manufacturing, Pile-driving equipment manufacturing, Portable crushing, pulverizing, and screening machinery manufacturing, Powered post hole diggers manufacturing, Road graders manufacturing, Surface mining machinery (except drilling) manufacturing.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Construction Machinery Manufacturing",
    slug: "construction-machinery-manufacturing"
  },
  "333131": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing underground mining machinery and equipment, such as coal breakers, mining cars, core drills, coal cutters, and rock drills, and (2) manufacturing mineral beneficiating machinery and equipment used in surface or underground mines.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Mining Machinery and Equipment Manufacturing",
    slug: "mining-machinery-and-equipment-manufacturing"
  },
  "333132": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing oil and gas field machinery and equipment, such as oil and gas field drilling machinery and equipment; oil and gas field production machinery and equipment; and oil and gas field derricks, and (2) manufacturing water well drilling machinery.",
    industries: ["agriculture-construction-mining-machinery-manufacturing"],
    name: "Oil and Gas Field Machinery and Equipment Manufacturing",
    slug: "oil-and-gas-field-machinery-and-equipment-manufacturing"
  },
  "333241": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing food and beverage manufacturing-type machinery and equipment, such as dairy product plant machinery and equipment (e.g., homogenizers, pasteurizers, ice cream freezers), bakery machinery and equipment (e.g., dough mixers, bake ovens, pastry rolling machines), meat and poultry processing and preparation machinery, and other commercial food products machinery (e.g., slicers, choppers, and mixers).",
    industries: ["industrial-machinery-manufacturing"],
    name: "Food Product Machinery Manufacturing",
    slug: "food-product-machinery-manufacturing"
  },
  "333242": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing wafer processing equipment, semiconductor assembly and packaging equipment, and other semiconductor making machinery.",
    industries: ["industrial-machinery-manufacturing"],
    name: "Semiconductor Machinery Manufacturing",
    slug: "semiconductor-machinery-manufacturing"
  },
  "333243": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing sawmill and woodworking machinery (except handheld), such as circular and band sawing equipment, planing machinery, and sanding machinery, and/or (2) manufacturing paper industry machinery for making paper and paper products, such as pulp making machinery, paper and paperboard making machinery, and paper and paperboard converting machinery.",
    industries: ["industrial-machinery-manufacturing"],
    name: "Sawmill, Woodworking, and Paper Machinery Manufacturing",
    slug: "sawmill-woodworking-and-paper-machinery-manufacturing"
  },
  "333248": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing industrial machinery (except agricultural and farm-type; construction and mining machinery; food manufacturing-type machinery; semiconductor making machinery; and sawmill, woodworking, and paper making machinery). Illustrative Examples: Additive manufacturing machinery manufacturing, Bookbinding machines manufacturing, Chemical processing machinery and equipment manufacturing, Cigarette making machinery manufacturing, Glass making machinery (e.g., blowing, forming, molding) manufacturing, Petroleum refining machinery manufacturing, Plastics working machinery manufacturing, Printing presses manufacturing, Rubber working machinery manufacturing, Sewing machines (including household-type) manufacturing, Shoe making and repairing machinery manufacturing, Tannery machinery manufacturing , Textile making machinery manufacturing.",
    industries: ["industrial-machinery-manufacturing"],
    name: "All Other Industrial Machinery Manufacturing",
    slug: "all-other-industrial-machinery-manufacturing"
  },
  "333310": {
    description: "This industry comprises establishments primarily engaged in manufacturing commercial and service industry machinery, such as optical instruments and lenses (except ophthalmic), photographic and photocopying equipment, automatic vending machinery, commercial laundry and drycleaning machinery, office machinery, automotive maintenance equipment (except mechanics' handtools), and commercial-type cooking equipment.",
    industries: ["commercial-and-service-industry-machinery-manufacturing"],
    name: "Commercial and Service Industry Machinery Manufacturing",
    slug: "commercial-and-service-industry-machinery-manufacturing"
  },
  "333413": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing stationary air purification equipment, such as industrial dust and fume collection equipment, electrostatic precipitation equipment, warm air furnace filters, air washers, and other dust collection equipment, and/or (2) manufacturing attic fans and industrial and commercial fans and blowers, such as commercial exhaust fans and commercial ventilating fans.",
    industries: ["hvac-and-refrigeration-equipment-manufacturing"],
    name: "Industrial and Commercial Fan and Blower and Air Purification Equipment Manufacturing",
    slug: "industrial-and-commercial-fan-and-blower-and-air-purification-equipment-manufacturing"
  },
  "333414": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing heating equipment (except electric and warm air furnaces), such as heating boilers, heating stoves, floor and wall furnaces, and wall and baseboard heating units.",
    industries: ["hvac-and-refrigeration-equipment-manufacturing"],
    name: "Heating Equipment (except Warm Air Furnaces) Manufacturing",
    slug: "heating-equipment-except-warm-air-furnaces-manufacturing"
  },
  "333415": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing air-conditioning (except motor vehicle) and warm air furnace equipment and/or (2) manufacturing commercial and industrial refrigeration and freezer equipment. Illustrative Examples: Air-conditioning and warm air heating combination units manufacturing, Air-conditioning compressors (except motor vehicle) manufacturing, Air-conditioning condensers and condensing units manufacturing, Dehumidifiers (except portable electric) manufacturing, Heat pumps manufacturing, Humidifying equipment (except portable) manufacturing, Refrigerated counter and display cases manufacturing, Refrigerated drinking fountains manufacturing, Snow making machinery manufacturing, Soda fountain cooling and dispensing equipment manufacturing.",
    industries: ["hvac-and-refrigeration-equipment-manufacturing"],
    name: "Air-Conditioning and Warm Air Heating Equipment and Commercial and Industrial Refrigeration Equipment Manufacturing",
    slug: "air-conditioning-and-warm-air-heating-equipment-and-commercial-and-industrial-refrigeration-equipment-manufacturing"
  },
  "333511": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing industrial molds for casting metals or forming other materials, such as plastics, glass, or rubber. Cross-References.",
    industries: ["metalworking-machinery-manufacturing"],
    name: "Industrial Mold Manufacturing",
    slug: "industrial-mold-manufacturing"
  },
  "333514": {
    description: "This U.S. industry comprises establishments, known as tool and die shops, primarily engaged in manufacturing special tools and fixtures, such as cutting dies and jigs.",
    industries: ["metalworking-machinery-manufacturing"],
    name: "Special Die and Tool, Die Set, Jig, and Fixture Manufacturing",
    slug: "special-die-and-tool-die-set-jig-and-fixture-manufacturing"
  },
  "333515": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing accessories and attachments for metal cutting and metal forming machine tools. Illustrative Examples: Knives and bits for metalworking lathes, planers, and shapers manufacturing, Measuring attachments (e.g., sine bars) for machine tool manufacturing, Metalworking drill bits manufacturing, Taps and dies (i.e., machine tool accessories) manufacturing, Cross-References.",
    industries: ["metalworking-machinery-manufacturing"],
    name: "Cutting Tool and Machine Tool Accessory Manufacturing",
    slug: "cutting-tool-and-machine-tool-accessory-manufacturing"
  },
  "333517": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing metal cutting machine tools (except handtools) and/or (2) manufacturing metal forming machine tools (except handtools), such as punching, sheering, bending, forming, pressing, forging and die-casting machines. Illustrative Examples: Bending and forming machines, metalworking, manufacturing, Buffing and polishing machines, metalworking, manufacturing, Drilling machines, metalworking, manufacturing, Grinding machines, metalworking, manufacturing, Home workshop metal cutting machine tools (except handtools, welding equipment) manufacturing, Metalworking lathes manufacturing, Milling machines, metalworking, manufacturing, Stamping machines, metalworking, manufacturing.",
    industries: ["metalworking-machinery-manufacturing"],
    name: "Machine Tool Manufacturing",
    slug: "machine-tool-manufacturing"
  },
  "333519": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing rolling mill machinery and equipment and/or other metalworking machinery (except industrial molds; special dies and tools, die sets, jigs, and fixtures; cutting tools and machine tool accessories; and machine tools). Illustrative Examples: Assembly machines (i.e., wire making equipment) manufacturing, Cradle assembly machinery (i.e., wire making equipment) manufacturing, Metalworking coil winding and cutting machinery manufacturing, Rolling mill roll machines, metalworking, manufacturing, Wire drawing and fabricating machinery and equipment (except dies) manufacturing.",
    industries: ["metalworking-machinery-manufacturing"],
    name: "Rolling Mill and Other Metalworking Machinery Manufacturing",
    slug: "rolling-mill-and-other-metalworking-machinery-manufacturing"
  },
  "333611": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing turbines (except aircraft); and complete turbine generator set units, such as steam, hydraulic, gas, and wind.",
    industries: ["engines-and-power-transmission-equipment-manufacturing"],
    name: "Turbine and Turbine Generator Set Units Manufacturing",
    slug: "turbine-and-turbine-generator-set-units-manufacturing"
  },
  "333612": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing gears, speed changers, and industrial high-speed drives (except hydrostatic).",
    industries: ["engines-and-power-transmission-equipment-manufacturing"],
    name: "Speed Changer, Industrial High-Speed Drive, and Gear Manufacturing",
    slug: "speed-changer-industrial-high-speed-drive-and-gear-manufacturing"
  },
  "333613": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing mechanical power transmission equipment (except motor vehicle and aircraft), such as plain bearings, clutches (except motor vehicle and electromagnetic industrial control), couplings, joints, and drive chains.",
    industries: ["engines-and-power-transmission-equipment-manufacturing"],
    name: "Mechanical Power Transmission Equipment Manufacturing",
    slug: "mechanical-power-transmission-equipment-manufacturing"
  },
  "333618": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing internal combustion engines (except automotive gasoline and aircraft).",
    industries: ["engines-and-power-transmission-equipment-manufacturing"],
    name: "Other Engine Equipment Manufacturing",
    slug: "other-engine-equipment-manufacturing"
  },
  "333912": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing general purpose air and gas compressors, such as reciprocating compressors, centrifugal compressors, vacuum pumps (except laboratory), and nonagricultural spraying and dusting compressors and spray gun units.",
    industries: ["machinery-manufacturing"],
    name: "Air and Gas Compressor Manufacturing",
    slug: "air-and-gas-compressor-manufacturing"
  },
  "333914": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing measuring and dispensing pumps, such as gasoline pumps and lubricating oil measuring and dispensing pumps and/or (2) manufacturing general purpose pumps and pumping equipment (except fluid power pumps and motors), such as reciprocating pumps, turbine pumps, centrifugal pumps, rotary pumps, diaphragm pumps, domestic water system pumps, oil well and oil field pumps, and sump pumps.",
    industries: ["machinery-manufacturing"],
    name: "Measuring, Dispensing, and Other Pumping Equipment Manufacturing",
    slug: "measuring-dispensing-and-other-pumping-equipment-manufacturing"
  },
  "333921": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing elevators and moving stairways. Illustrative Examples: Automobile lifts (i.e., garage-type, service station) manufacturing, Escalators manufacturing, Moving walkways manufacturing, Passenger and freight elevators manufacturing, Cross-References.",
    industries: ["machinery-manufacturing"],
    name: "Elevator and Moving Stairway Manufacturing",
    slug: "elevator-and-moving-stairway-manufacturing"
  },
  "333922": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing conveyors and conveying equipment, such as gravity conveyors, trolley conveyors, tow conveyors, pneumatic tube conveyors, carousel conveyors, farm conveyors, and belt conveyors.",
    industries: ["machinery-manufacturing"],
    name: "Conveyor and Conveying Equipment Manufacturing",
    slug: "conveyor-and-conveying-equipment-manufacturing"
  },
  "333923": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing overhead traveling cranes, hoists, and monorail systems. Illustrative Examples: Aerial work platforms manufacturing, Automobile wrecker (i.e., tow truck) hoists manufacturing, Block and tackle manufacturing, Metal pulleys (except power transmission) manufacturing, Winches manufacturing .",
    industries: ["machinery-manufacturing"],
    name: "Overhead Traveling Crane, Hoist, and Monorail System Manufacturing",
    slug: "overhead-traveling-crane-hoist-and-monorail-system-manufacturing"
  },
  "333924": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing industrial trucks, tractors, trailers, and stackers (i.e., truck-type) such as forklifts, pallet loaders and unloaders, and portable loading docks.",
    industries: ["machinery-manufacturing"],
    name: "Industrial Truck, Tractor, Trailer, and Stacker Machinery Manufacturing",
    slug: "industrial-truck-tractor-trailer-and-stacker-machinery-manufacturing"
  },
  "333991": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing power-driven (e.g., battery, corded, pneumatic) handtools, such as drills, screwguns, circular saws, chain saws, staplers, and nailers.",
    industries: ["machinery-manufacturing"],
    name: "Power-Driven Handtool Manufacturing",
    slug: "power-driven-handtool-manufacturing"
  },
  "333992": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing welding and soldering equipment and accessories (except transformers), such as arc, resistance, gas, plasma, laser, electron beam, and ultrasonic welding equipment; welding electrodes; coated or cored welding wire; and soldering equipment (except handheld).",
    industries: ["machinery-manufacturing"],
    name: "Welding and Soldering Equipment Manufacturing",
    slug: "welding-and-soldering-equipment-manufacturing"
  },
  "333993": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing packaging machinery, such as wrapping, bottling, canning, and labeling machinery.",
    industries: ["machinery-manufacturing"],
    name: "Packaging Machinery Manufacturing",
    slug: "packaging-machinery-manufacturing"
  },
  "333994": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing industrial process ovens, induction and dielectric heating equipment, and kilns (except cement, chemical, wood).  Included in this industry are establishments manufacturing laboratory furnaces and ovens.",
    industries: ["machinery-manufacturing"],
    name: "Industrial Process Furnace and Oven Manufacturing",
    slug: "industrial-process-furnace-and-oven-manufacturing"
  },
  "333995": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fluid power (i.e., hydraulic and pneumatic) cylinders and actuators. Cross-References.",
    industries: ["machinery-manufacturing"],
    name: "Fluid Power Cylinder and Actuator Manufacturing",
    slug: "fluid-power-cylinder-and-actuator-manufacturing"
  },
  "333996": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fluid power (i.e., hydraulic and pneumatic) pumps and motors.",
    industries: ["machinery-manufacturing"],
    name: "Fluid Power Pump and Motor Manufacturing",
    slug: "fluid-power-pump-and-motor-manufacturing"
  },
  "333998": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing general purpose machinery (except ventilating, heating, air-conditioning, and commercial refrigeration equipment; metalworking machinery; engines, turbines, and power transmission equipment; pumps and compressors; material handling equipment; power-driven handtools; welding and soldering equipment; packaging machinery; industrial process furnaces and ovens; fluid power cylinders and actuators; and fluid power pumps and motors). Illustrative Examples: Automatic fire sprinkler systems manufacturing, Baling machinery (e.g., paper, scrap metal) manufacturing, Bridge and gate lifting machinery manufacturing, Centrifuges, industrial and laboratory-type, manufacturing, Cremating ovens manufacturing, General purpose-type sieves and screening equipment manufacturing, Hydraulic and pneumatic jacks manufacturing, Industrial and general purpose-type filters (except internal combustion engine, warm air furnace) manufacturing, Scales and balances manufacturing.",
    industries: ["machinery-manufacturing"],
    name: "All Other Miscellaneous General Purpose Machinery Manufacturing",
    slug: "all-other-miscellaneous-general-purpose-machinery-manufacturing"
  },
  "334111": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing and/or assembling electronic computers, such as mainframes, personal computers, workstations, laptops, and computer servers.  Computers can be analog, digital, or hybrid.  Digital computers, the most common type, are devices that do all of the following: (1) store the processing program or programs and the data immediately necessary for the execution of the program; (2) can be freely programmed in accordance with the requirements of the user; (3) perform arithmetical computations specified by the user; and (4) execute, without human intervention, a processing program that requires the computer to modify its execution by logical decision during the processing run.  Analog computers are capable of simulating mathematical models and contain at least analog, control, and programming elements.  The manufacture of computers includes the assembly or integration of processors, coprocessors, memory, storage, and input/output devices into a user-programmable final product.",
    industries: ["computer-hardware-manufacturing"],
    name: "Electronic Computer Manufacturing",
    slug: "electronic-computer-manufacturing"
  },
  "334112": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing computer storage devices that allow the storage and retrieval of data from a phase change, magnetic, optical, or magnetic/optical media.  Examples of products made by these establishments are computer disk drives, USB flash drives, and tape storage and backup units.",
    industries: ["computer-hardware-manufacturing"],
    name: "Computer Storage Device Manufacturing",
    slug: "computer-storage-device-manufacturing"
  },
  "334118": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing computer terminals and other computer peripheral equipment (except storage devices). Illustrative Examples: Automated teller machines (ATM) manufacturing, Computer terminals manufacturing, Joystick devices manufacturing, Keyboards, computer peripheral equipment, manufacturing, Monitors, computer peripheral equipment, manufacturing, Mouse devices, computer peripheral equipment, manufacturing, Optical readers and scanners manufacturing, Plotters, computer, manufacturing, Point-of-sale terminals manufacturing, Printers, computer, manufacturing .",
    industries: ["computer-hardware-manufacturing"],
    name: "Computer Terminal and Other Computer Peripheral Equipment Manufacturing",
    slug: "computer-terminal-and-other-computer-peripheral-equipment-manufacturing"
  },
  "334210": {
    description: "This industry comprises establishments primarily engaged in manufacturing wire telephone and data communications equipment.  These products may be stand-alone or board-level components of a larger system.  Examples of products made by these establishments are central office switching equipment, cordless and wire telephones (except cellular), PBX equipment, telephone answering machines, LAN modems, multi-user modems, and other data communications equipment, such as bridges, routers, and gateways.",
    industries: ["communications-equipment-manufacturing"],
    name: "Telephone Apparatus Manufacturing",
    slug: "telephone-apparatus-manufacturing"
  },
  "334220": {
    description: "This industry comprises establishments primarily engaged in manufacturing radio and television broadcast and wireless communications equipment.  Examples of products made by these establishments are transmitting and receiving antennas, cable television equipment, GPS equipment, pagers, cellular phones, mobile communications equipment, and radio and television studio and broadcasting equipment.",
    industries: ["communications-equipment-manufacturing"],
    name: "Radio and Television Broadcasting and Wireless Communications Equipment Manufacturing",
    slug: "radio-and-television-broadcasting-and-wireless-communications-equipment-manufacturing"
  },
  "334290": {
    description: "This industry comprises establishments primarily engaged in manufacturing communications equipment (except telephone apparatus, radio and television broadcast equipment, and wireless communications equipment). Illustrative Examples: Fire detection and alarm systems manufacturing, Signals (e.g., highway, pedestrian, railway, traffic) manufacturing, Intercom systems and equipment manufacturing, Video-based stadium displays manufacturing .",
    industries: ["communications-equipment-manufacturing"],
    name: "Other Communications Equipment Manufacturing",
    slug: "other-communications-equipment-manufacturing"
  },
  "334310": {
    description: "This industry comprises establishments primarily engaged in manufacturing electronic audio and video equipment for home entertainment, motor vehicles, and public address and musical instrument amplification.  Examples of products made by these establishments are digital video recorders, televisions, stereo equipment, speaker systems, household-type video cameras, jukeboxes, and amplifiers for musical instruments and public address systems.",
    industries: ["audio-and-video-equipment-manufacturing"],
    name: "Audio and Video Equipment Manufacturing",
    slug: "audio-and-video-equipment-manufacturing"
  },
  "334412": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing bare (i.e., rigid or flexible) printed circuit boards without mounted electronic components.  These establishments print, perforate, plate, screen, etch, or photoprint interconnecting pathways for electric current on laminates.",
    industries: ["computers-and-electronics-manufacturing"],
    name: "Bare Printed Circuit Board Manufacturing",
    slug: "bare-printed-circuit-board-manufacturing"
  },
  "334413": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing semiconductors and related solid-state devices.  Examples of products made by these establishments are integrated circuits, memory chips, microprocessors, diodes, transistors, solar cells, and other optoelectronic devices.",
    industries: ["computers-and-electronics-manufacturing"],
    name: "Semiconductor and Related Device Manufacturing",
    slug: "semiconductor-and-related-device-manufacturing"
  },
  "334416": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing electronic fixed and variable capacitors and condensers; (2) manufacturing electronic resistors, such as fixed and variable resistors, resistor networks, thermistors, and varistors; and (3) manufacturing electronic inductors, such as coils and transformers.",
    industries: ["computers-and-electronics-manufacturing"],
    name: "Capacitor, Resistor, Coil, Transformer, and Other Inductor Manufacturing",
    slug: "capacitor-resistor-coil-transformer-and-other-inductor-manufacturing"
  },
  "334417": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing electronic connectors, such as coaxial, cylindrical, rack and panel, pin and sleeve, printed circuit, and fiber optic. Cross-References.",
    industries: ["computers-and-electronics-manufacturing"],
    name: "Electronic Connector Manufacturing",
    slug: "electronic-connector-manufacturing"
  },
  "334418": {
    description: "This U.S. industry comprises establishments primarily engaged in loading components onto printed circuit boards or who manufacture and ship loaded printed circuit boards.  Also known as printed circuit assemblies, electronics assemblies, or modules, these products are printed circuit boards that have some or all of the semiconductor and electronic components inserted or mounted and are inputs to a wide variety of electronic systems and devices.",
    industries: ["computers-and-electronics-manufacturing"],
    name: "Printed Circuit Assembly (Electronic Assembly) Manufacturing",
    slug: "printed-circuit-assembly-electronic-assembly-manufacturing"
  },
  "334419": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing electronic components (except bare printed circuit boards; semiconductors and related devices; electronic capacitors; electronic resistors; coils, transformers, and other inductors; connectors; and loaded printed circuit boards). Illustrative Examples: Crystals and crystal assemblies, electronic, manufacturing, Electron tubes manufacturing, LCD (liquid crystal display) unit screens manufacturing, Microwave components manufacturing, Piezoelectric devices manufacturing, Printed circuit laminates manufacturing, Switches for electronic applications manufacturing, Transducers (except pressure) manufacturing.",
    industries: ["computers-and-electronics-manufacturing"],
    name: "Other Electronic Component Manufacturing",
    slug: "other-electronic-component-manufacturing"
  },
  "334510": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing electromedical and electrotherapeutic apparatus, such as magnetic resonance imaging equipment, medical ultrasound equipment, pacemakers, hearing aids, electrocardiographs, and electromedical endoscopic equipment.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Electromedical and Electrotherapeutic Apparatus Manufacturing",
    slug: "electromedical-and-electrotherapeutic-apparatus-manufacturing"
  },
  "334511": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing search, detection, navigation, guidance, aeronautical, and nautical systems and instruments.  Examples of products made by these establishments are aircraft instruments (except engine), flight recorders, navigational instruments and systems, radar systems and equipment, and sonar systems and equipment.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Search, Detection, Navigation, Guidance, Aeronautical, and Nautical System and Instrument Manufacturing",
    slug: "search-detection-navigation-guidance-aeronautical-and-nautical-system-and-instrument-manufacturing"
  },
  "334512": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing automatic controls and regulators for applications, such as heating, air-conditioning, refrigeration, and appliances.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Automatic Environmental Control Manufacturing for Residential, Commercial, and Appliance Use",
    slug: "automatic-environmental-control-manufacturing-for-residential-commercial-and-appliance-use"
  },
  "334513": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing instruments and related devices for measuring, displaying, indicating, recording, transmitting, and controlling industrial process variables.  These instruments measure, display, or control (monitor, analyze, and so forth) industrial process variables, such as temperature, humidity, pressure, vacuum, combustion, flow, level, viscosity, density, acidity, concentration, and rotation.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Instruments and Related Products Manufacturing for Measuring, Displaying, and Controlling Industrial Process Variables",
    slug: "instruments-and-related-products-manufacturing-for-measuring-displaying-and-controlling-industrial-process-variables"
  },
  "334514": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing totalizing (i.e., registering) fluid meters and counting devices.  Examples of products made by these establishments are gas consumption meters, water consumption meters, parking meters, taxi meters, motor vehicle gauges, and fare collection equipment.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Totalizing Fluid Meter and Counting Device Manufacturing",
    slug: "totalizing-fluid-meter-and-counting-device-manufacturing"
  },
  "334515": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing instruments for measuring and testing the characteristics of electricity and electrical signals.  Examples of products made by these establishments are circuit and continuity testers, voltmeters, ohm meters, wattmeters, multimeters, and semiconductor test equipment. Cross-References.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Instrument Manufacturing for Measuring and Testing Electricity and Electrical Signals",
    slug: "instrument-manufacturing-for-measuring-and-testing-electricity-and-electrical-signals"
  },
  "334516": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing instruments and instrumentation systems for laboratory analysis of the chemical or physical composition or concentration of samples of solid, fluid, gaseous, or composite material.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Analytical Laboratory Instrument Manufacturing",
    slug: "analytical-laboratory-instrument-manufacturing"
  },
  "334517": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing irradiation apparatus and tubes for applications, such as medical diagnostic, medical therapeutic, industrial, research, and scientific evaluation.  Irradiation can take the form of beta-rays, gamma-rays, X-rays, or other ionizing radiation.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Irradiation Apparatus Manufacturing",
    slug: "irradiation-apparatus-manufacturing"
  },
  "334519": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing measuring and controlling devices (except search, detection, navigation, guidance, aeronautical, and nautical instruments and systems; automatic environmental controls for residential, commercial, and appliance use; instruments for measurement, display, and control of industrial process variables; totalizing fluid meters and counting devices; instruments for measuring and testing electricity and electrical signals; analytical laboratory instruments; irradiation equipment; and electromedical and electrotherapeutic apparatus). Illustrative Examples: Aircraft engine instruments manufacturing, Automotive emissions testing equipment manufacturing, Clocks assembling, Meteorological instruments manufacturing, Physical properties testing and inspection equipment manufacturing, Polygraph machines manufacturing, Radiation detection and monitoring instruments manufacturing, Surveying instruments manufacturing, Thermometers, liquid-in-glass and bimetal types (except medical), manufacturing, Watches (except smartwatches) and parts (except crystals) manufacturing.",
    industries: ["measuring-and-control-instrument-manufacturing"],
    name: "Other Measuring and Controlling Device Manufacturing",
    slug: "other-measuring-and-controlling-device-manufacturing"
  },
  "334610": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing optical and magnetic media, such as blank audio tapes, blank video tapes, and blank diskettes, and/or (2) mass duplicating (i.e., making copies) audio, video, software, and other data on magnetic, optical, and similar media.  These establishments do not generally develop software or produce audio or video content.",
    industries: ["magnetic-and-optical-media-manufacturing"],
    name: "Manufacturing and Reproducing Magnetic and Optical Media",
    slug: "manufacturing-and-reproducing-magnetic-and-optical-media"
  },
  "335121": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fixed or portable residential electric lighting fixtures and lamp shades of metal, paper, or textiles.  Residential electric lighting fixtures include those for use both inside and outside the residence. Illustrative Examples: Ceiling lighting fixtures, residential, manufacturing, Chandeliers, residential, manufacturing, Table lamps (i.e., lighting fixtures) manufacturing.",
    industries: ["electric-lighting-equipment-manufacturing"],
    name: "Residential Electric Lighting Fixture Manufacturing",
    slug: "residential-electric-lighting-fixture-manufacturing"
  },
  "335132": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing commercial, industrial, and institutional electric lighting fixtures.",
    industries: ["electric-lighting-equipment-manufacturing"],
    name: "Commercial, Industrial, and Institutional Electric Lighting Fixture Manufacturing",
    slug: "commercial-industrial-and-institutional-electric-lighting-fixture-manufacturing"
  },
  "335139": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing electric light bulbs, tubes, and parts (except glass blanks for electric light bulbs and light emitting diodes (LEDs)), electric lighting fixtures (except residential, commercial, industrial, institutional, and vehicular electric lighting fixtures), and nonelectric lighting equipment. Illustrative Examples: Christmas tree lighting sets, electric, manufacturing, Electric light bulbs, complete, manufacturing, Fireplace logs, electric, manufacturing, Flashlights manufacturing, Insect lamps, electric, manufacturing, Spotlights (except vehicular) manufacturing, Lanterns (e.g., carbide, electric, gas, gasoline, kerosene) manufacturing, LED (light emitting diode) light bulbs manufacturing, Street lighting fixtures (except traffic signals) manufacturing.",
    industries: ["electric-lighting-equipment-manufacturing"],
    name: "Electric Lamp Bulb and Other Lighting Equipment Manufacturing",
    slug: "electric-lamp-bulb-and-other-lighting-equipment-manufacturing"
  },
  "335210": {
    description: "This industry comprises establishments primarily engaged in manufacturing small electric appliances and electric housewares, household-type fans (except attic fans), household-type vacuum cleaners, and other electric household-type floor care machines. Illustrative Examples: Bath fans, residential, manufacturing, Carpet and floor cleaning equipment, household-type electric, manufacturing, Ceiling fans, residential, manufacturing, Curling irons, household-type electric, manufacturing, Electric blankets manufacturing, Portable electric space heaters manufacturing, Portable hair dryers, electric, manufacturing, Portable cooking appliances (except microwave, convection ovens), household-type electric, manufacturing, Portable humidifiers and dehumidifiers manufacturing, Scissors, electric, manufacturing, Ventilating and exhaust fans (except attic fans), household-type, manufacturing.",
    industries: ["household-appliance-manufacturing"],
    name: "Small Electrical Appliance Manufacturing",
    slug: "small-electrical-appliance-manufacturing"
  },
  "335220": {
    description: "This industry comprises establishments primarily engaged in manufacturing household-type cooking appliances, household-type laundry equipment, household-type refrigerators, upright and chest freezers, and other electrical and nonelectrical major household-type appliances, such as dishwashers, water heaters, and garbage disposal units.",
    industries: ["household-appliance-manufacturing"],
    name: "Major Household Appliance Manufacturing",
    slug: "major-household-appliance-manufacturing"
  },
  "335311": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing power, distribution, and specialty transformers (except electronic components).  Industrial-type and consumer-type transformers in this industry vary (e.g., step up or step down) voltage but do not convert alternating to direct or direct to alternating current. Illustrative Examples: Fluorescent ballasts (i.e., transformers) manufacturing, Substation transformers, electric power distribution, manufacturing, Distribution transformers, electric, manufacturing, Transmission and distribution voltage regulators manufacturing, Cross-References.",
    industries: ["electrical-equipment-manufacturing"],
    name: "Power, Distribution, and Specialty Transformer Manufacturing",
    slug: "power-distribution-and-specialty-transformer-manufacturing"
  },
  "335312": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing electric motors (except internal combustion engine starting motors), power generators (except battery charging alternators for internal combustion engines), and motor generator sets (except turbine generator set units).  This industry includes establishments rewinding armatures on a factory basis. ",
    industries: ["electrical-equipment-manufacturing"],
    name: "Motor and Generator Manufacturing",
    slug: "motor-and-generator-manufacturing"
  },
  "335313": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing switchgear and switchboard apparatus. Illustrative Examples: Circuit breakers, power, manufacturing, Control panels, electric power distribution, manufacturing, Ducts for electrical switchboard apparatus manufacturing, Fuses, electric, manufacturing, Power switching equipment manufacturing, Switches, electric power (except pushbutton, snap, solenoid, tumbler), manufacturing.",
    industries: ["electrical-equipment-manufacturing"],
    name: "Switchgear and Switchboard Apparatus Manufacturing",
    slug: "switchgear-and-switchboard-apparatus-manufacturing"
  },
  "335314": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing relays, motor starters and controllers, and other industrial controls and control accessories.",
    industries: ["electrical-equipment-manufacturing"],
    name: "Relay and Industrial Control Manufacturing",
    slug: "relay-and-industrial-control-manufacturing"
  },
  "335910": {
    description: "This industry comprises establishments primarily engaged in manufacturing primary and storage batteries. Illustrative Examples: Disposable flashlight batteries manufacturing, Dry cells, primary (e.g., AAA, AA, C, D, 9V), manufacturing, Lead acid storage batteries manufacturing, Lithium batteries manufacturing, Rechargeable nickel-cadmium (NICAD) batteries manufacturing, Watch batteries manufacturing",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Battery Manufacturing",
    slug: "battery-manufacturing"
  },
  "335921": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing insulated fiber optic cable from purchased fiber optic strand.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Fiber Optic Cable Manufacturing",
    slug: "fiber-optic-cable-manufacturing"
  },
  "335929": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing insulated wire and cable of nonferrous metals from purchased wire.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Other Communication and Energy Wire Manufacturing",
    slug: "other-communication-and-energy-wire-manufacturing"
  },
  "335931": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing current-carrying wiring devices. Illustrative Examples: Bus bars, electrical conductors (except switchgear-type), manufacturing, GFCI (ground fault circuit interrupters) manufacturing, Lamp holders manufacturing, Lightning arrestors and coils manufacturing, Receptacles (i.e., outlets), electrical, manufacturing, Switches for electrical wiring (e.g., pressure, pushbutton, snap, tumbler) manufacturing.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Current-Carrying Wiring Device Manufacturing",
    slug: "current-carrying-wiring-device-manufacturing"
  },
  "335932": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing noncurrent-carrying wiring devices. Illustrative Examples: Boxes, electrical wiring (e.g., junction, outlet, switch), manufacturing, Conduits and fittings, electrical, manufacturing, Face plates (i.e., outlet or switch covers) manufacturing, Transmission pole and line hardware manufacturing.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Noncurrent-Carrying Wiring Device Manufacturing",
    slug: "noncurrent-carrying-wiring-device-manufacturing"
  },
  "335991": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing carbon, graphite, and metal-graphite brushes and brush stock; carbon or graphite electrodes for thermal and electrolytic uses; carbon and graphite fibers; and other carbon, graphite, and metal-graphite products. Cross-References.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "Carbon and Graphite Product Manufacturing",
    slug: "carbon-and-graphite-product-manufacturing"
  },
  "335999": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing industrial and commercial electric apparatus and other equipment (except lighting equipment, household appliances, transformers, motors, generators, switchgear, relays, industrial controls, batteries, communication and energy wire and cable, wiring devices, and carbon and graphite products).  Examples of products made by these establishments are power converters (i.e., AC to DC and DC to AC), power supplies, surge suppressors, and similar equipment for industrial-type and consumer-type equipment. Illustrative Examples: Appliance cords made from purchased insulated wire, Battery chargers, solid-state, manufacturing, Door opening and closing devices, electrical, manufacturing, Electric bells manufacturing, Extension cords made from purchased insulated wire, Inverters manufacturing, Surge suppressers manufacturing, Uninterruptible power supplies (UPS) manufacturing.",
    industries: ["appliances-electrical-and-electronics-manufacturing"],
    name: "All Other Miscellaneous Electrical Equipment and Component Manufacturing",
    slug: "all-other-miscellaneous-electrical-equipment-and-component-manufacturing"
  },
  "336110": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing complete automobiles and light duty motor vehicles (i.e., body and chassis or unibody) or (2) manufacturing automobile and light duty motor vehicle chassis only.  Vehicles made include passenger cars, light duty trucks, light duty vans, pick-up trucks, minivans, and sport utility vehicles.",
    industries: ["motor-vehicle-manufacturing"],
    name: "Automobile and Light Duty Motor Vehicle Manufacturing",
    slug: "automobile-and-light-duty-motor-vehicle-manufacturing"
  },
  "336120": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing heavy duty truck chassis and assembling complete heavy duty trucks, buses, heavy duty motor homes, and other special purpose heavy duty motor vehicles for highway use or (2) manufacturing heavy duty truck chassis only.",
    industries: ["motor-vehicle-manufacturing"],
    name: "Heavy Duty Truck Manufacturing",
    slug: "heavy-duty-truck-manufacturing"
  },
  "336211": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing truck and bus bodies and cabs and automobile bodies.  The products made may be sold separately or may be assembled on purchased chassis and sold as complete vehicles.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Motor Vehicle Body Manufacturing",
    slug: "motor-vehicle-body-manufacturing"
  },
  "336212": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing truck trailers, truck trailer chassis, cargo container chassis, detachable trailer bodies, and detachable trailer chassis for sale separately. Cross-References.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Truck Trailer Manufacturing",
    slug: "truck-trailer-manufacturing"
  },
  "336213": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing motor homes on purchased chassis and/or (2) manufacturing conversion vans on an assembly line basis.  Motor homes are units where the motor and the living quarters are integrated in the same unit.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Motor Home Manufacturing",
    slug: "motor-home-manufacturing"
  },
  "336214": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing travel trailers and campers designed to attach to motor vehicles; (2) manufacturing pick-up coaches (i.e., campers) and caps (i.e., covers) for mounting on pick-up trucks; and (3) manufacturing automobile, utility, and light-truck trailers.  Travel trailers do not have their own motor but are designed to be towed by a motor unit, such as an automobile or a light truck. Illustrative Examples: Automobile transporter trailers, single car, manufacturing, Camper units, slide-in, for pick-up trucks, manufacturing, Camping trailers and chassis manufacturing, Horse trailers (except fifth-wheel-type) manufacturing, Pick-up canopies, caps, or covers manufacturing, Travel trailers, recreational, manufacturing, Utility trailers manufacturing.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Travel Trailer and Camper Manufacturing",
    slug: "travel-trailer-and-camper-manufacturing"
  },
  "336310": {
    description: "This industry comprises establishments primarily engaged in (1) manufacturing and/or rebuilding motor vehicle gasoline engines and engine parts and/or (2) manufacturing and/or rebuilding carburetors, pistons, piston rings, and engine valves, whether or not for vehicular use. Illustrative Examples: Carburetors, all types, manufacturing, Crankshaft assemblies, automotive and truck gasoline engine, manufacturing , Cylinder heads, automotive and truck gasoline engine, manufacturing, Fuel injection systems and parts, automotive and truck gasoline engine, manufacturing, Gasoline engines for hybrid automotive vehicles manufacturing, Pistons and piston rings manufacturing, Manifolds (i.e., intake and exhaust), automotive and truck gasoline engine, manufacturing, Pumps (e.g., fuel, oil, water), mechanical, automotive and truck gasoline engine (except power steering), manufacturing, Timing gears and chains, automotive and truck gasoline engine, manufacturing, Valves, engine, intake and exhaust, manufacturing .",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Gasoline Engine and Engine Parts Manufacturing",
    slug: "motor-vehicle-gasoline-engine-and-engine-parts-manufacturing"
  },
  "336320": {
    description: "This industry comprises establishments primarily engaged in manufacturing and/or rebuilding electrical and electronic equipment for motor vehicles and internal combustion engines.  The products made can be used for all types of transportation equipment (i.e., aircraft, automobiles, trucks, trains, ships) or stationary internal combustion engine applications. Illustrative Examples: Alternators and generators for internal combustion engines manufacturing, Automotive lighting fixtures manufacturing, Coils, ignition, internal combustion engines, manufacturing, Distributors for internal combustion engines manufacturing, Electrical control chips (modules), motor vehicle, manufacturing, Electrical ignition cable sets for internal combustion engines manufacturing, Generators for internal combustion engines manufacturing, Ignition wiring harness for internal combustion engines manufacturing, Instrument control panels (i.e., assembling purchased gauges), automotive, truck, and bus, manufacturing, Spark plugs for internal combustion engines manufacturing, Windshield washer pumps, automotive, truck, and bus, manufacturing.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Electrical and Electronic Equipment Manufacturing",
    slug: "motor-vehicle-electrical-and-electronic-equipment-manufacturing"
  },
  "336330": {
    description: "This industry comprises establishments primarily engaged in manufacturing and/or rebuilding motor vehicle steering mechanisms and suspension components (except springs). Illustrative Examples: Rack and pinion steering assemblies manufacturing, Shock absorbers, automotive, truck, and bus, manufacturing, Steering columns, automotive, truck, and bus, manufacturing, Steering wheels, automotive, truck, and bus, manufacturing , Struts, automotive, truck, and bus, manufacturing, Power steering pumps manufacturing, Cross-References.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Steering and Suspension Components (except Spring) Manufacturing",
    slug: "motor-vehicle-steering-and-suspension-components-except-spring-manufacturing"
  },
  "336340": {
    description: "This industry comprises establishments primarily engaged in manufacturing and/or rebuilding motor vehicle brake systems and related components. Illustrative Examples: Brake cylinders, master and wheel, automotive, truck, and bus, manufacturing, Brake drums, automotive, truck, and bus, manufacturing, Brake hose assemblies manufacturing, Brake pads and shoes, automotive, truck, and bus, manufacturing, Calipers, brake, automotive, truck, and bus, manufacturing, Cross-References.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Brake System Manufacturing",
    slug: "motor-vehicle-brake-system-manufacturing"
  },
  "336350": {
    description: "This industry comprises establishments primarily engaged in manufacturing and/or rebuilding motor vehicle transmissions and power train parts. Illustrative Examples: Automatic transmissions, automotive, truck, and bus, manufacturing, Axle bearings, automotive, truck, and bus, manufacturing, Constant velocity joints, automotive, truck, and bus, manufacturing, Differential and rear axle assemblies, automotive, truck, and bus, manufacturing, Torque converters, automotive, truck, and bus, manufacturing, Universal joints, automotive, truck, and bus, manufacturing",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Transmission and Power Train Parts Manufacturing",
    slug: "motor-vehicle-transmission-and-power-train-parts-manufacturing"
  },
  "336360": {
    description: "This industry comprises establishments primarily engaged in manufacturing motor vehicle seating, seats, seat frames, seat belts, and interior trimmings.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Seating and Interior Trim Manufacturing",
    slug: "motor-vehicle-seating-and-interior-trim-manufacturing"
  },
  "336370": {
    description: "This industry comprises establishments primarily engaged in manufacturing motor vehicle stampings, such as fenders, tops, body parts, trim, and molding.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Motor Vehicle Metal Stamping",
    slug: "motor-vehicle-metal-stamping"
  },
  "336390": {
    description: "This industry comprises establishments primarily engaged in manufacturing and/or rebuilding motor vehicle parts and accessories (except motor vehicle gasoline engines and engine parts, motor vehicle electrical and electronic equipment, motor vehicle steering and suspension components, motor vehicle brake systems, motor vehicle transmissions and power train parts, motor vehicle seating and interior trim, and motor vehicle stampings). Illustrative Examples: Air bag assemblies manufacturing, Air-conditioners, motor vehicle, manufacturing, Air filters, automotive, truck, and bus, manufacturing, Radiators and cores manufacturing, Catalytic converters, engine exhaust, automotive, truck, and bus, manufacturing, Compressors, motor vehicle air-conditioning, manufacturing, Mufflers and resonators, motor vehicle, manufacturing, Wheels (i.e., rims), automotive, truck, and bus, manufacturing.",
    industries: ["motor-vehicle-parts-manufacturing"],
    name: "Other Motor Vehicle Parts Manufacturing",
    slug: "other-motor-vehicle-parts-manufacturing"
  },
  "336411": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing or assembling complete aircraft; (2) developing and making aircraft prototypes; (3) aircraft conversion (i.e., major modifications to systems); and (4) complete aircraft overhaul and rebuilding (i.e., periodic restoration of aircraft to original design specifications). Cross-References.",
    industries: ["aviation-and-aerospace-component-manufacturing"],
    name: "Aircraft Manufacturing",
    slug: "aircraft-manufacturing"
  },
  "336412": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing aircraft engines and engine parts; (2) developing and making prototypes of aircraft engines and engine parts; (3) aircraft propulsion system conversion (i.e., major modifications to systems); and (4) aircraft propulsion systems overhaul and rebuilding (i.e., periodic restoration of aircraft propulsion system to original design specifications). Cross-References.",
    industries: ["aviation-and-aerospace-component-manufacturing"],
    name: "Aircraft Engine and Engine Parts Manufacturing",
    slug: "aircraft-engine-and-engine-parts-manufacturing"
  },
  "336413": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing aircraft parts or auxiliary equipment (except engines and aircraft fluid power subassemblies) and/or (2) developing and making prototypes of aircraft parts and auxiliary equipment.  Auxiliary equipment includes such items as crop dusting apparatus, armament racks, inflight refueling equipment, and external fuel tanks. Cross-References.",
    industries: ["aviation-and-aerospace-component-manufacturing"],
    name: "Other Aircraft Parts and Auxiliary Equipment Manufacturing",
    slug: "other-aircraft-parts-and-auxiliary-equipment-manufacturing"
  },
  "336414": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing complete guided missiles and space vehicles and/or (2) developing and making prototypes of guided missiles or space vehicles. Cross-References.",
    industries: ["defense-and-space-manufacturing"],
    name: "Guided Missile and Space Vehicle Manufacturing",
    slug: "guided-missile-and-space-vehicle-manufacturing"
  },
  "336415": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing guided missile and/or space vehicle propulsion units and propulsion unit parts and/or (2) developing and making prototypes of guided missile and space vehicle propulsion units and propulsion unit parts. Cross-References.",
    industries: ["aviation-and-aerospace-component-manufacturing"],
    name: "Guided Missile and Space Vehicle Propulsion Unit and Propulsion Unit Parts Manufacturing",
    slug: "guided-missile-and-space-vehicle-propulsion-unit-and-propulsion-unit-parts-manufacturing"
  },
  "336419": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) manufacturing guided missile and space vehicle parts and auxiliary equipment (except guided missile and space vehicle propulsion units and propulsion unit parts) and/or (2) developing and making prototypes of guided missile and space vehicle parts and auxiliary equipment. Cross-References.",
    industries: ["aviation-and-aerospace-component-manufacturing"],
    name: "Other Guided Missile and Space Vehicle Parts and Auxiliary Equipment Manufacturing",
    slug: "other-guided-missile-and-space-vehicle-parts-and-auxiliary-equipment-manufacturing"
  },
  "336510": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing and/or rebuilding locomotives, locomotive frames, and parts; (2) manufacturing railroad, street, and rapid transit cars and car equipment for operation on rails for freight and passenger service; and (3) manufacturing rail layers, ballast distributors, rail tamping equipment, and other railway track maintenance equipment. Cross-References.",
    industries: ["railroad-equipment-manufacturing"],
    name: "Railroad Rolling Stock Manufacturing",
    slug: "railroad-rolling-stock-manufacturing"
  },
  "336611": {
    description: "This U.S. industry comprises establishments primarily engaged in operating shipyards.  Shipyards are fixed facilities with drydocks and fabrication equipment capable of building a ship, defined as watercraft typically suitable or intended for other than personal or recreational use.  Activities of shipyards include the construction of ships, their repair, conversion and alteration, the production of prefabricated ship and barge sections, and specialized services, such as ship scaling. Illustrative Examples: Barge building, Cargo ship building, Drilling and production platforms, floating, oil and gas, building, Passenger ship building, Submarine building, Yachts built in shipyards .",
    industries: ["shipbuilding"],
    name: "Ship Building and Repairing",
    slug: "ship-building-and-repairing"
  },
  "336612": {
    description: "This U.S. industry comprises establishments primarily engaged in building boats.  Boats are defined as watercraft not built in shipyards and typically of the type suitable or intended for personal use.  Included in this industry are establishments that manufacture heavy-duty inflatable rubber or inflatable plastic boats (RIBs). Illustrative Examples: Inflatable plastic boats, heavy-duty, manufacturing, Inflatable rubber boats, heavy-duty, manufacturing, Boats (e.g., motorboats, rowboats, canoes, kayaks) manufacturing, Rigid inflatable boats (RIBs) manufacturing, Sailboat building, not done in shipyards, Yacht building, not done in shipyards.",
    industries: ["shipbuilding"],
    name: "Boat Building",
    slug: "boat-building"
  },
  "336991": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing motorcycles, bicycles, tricycles and similar equipment, and parts.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Motorcycle, Bicycle, and Parts Manufacturing",
    slug: "motorcycle-bicycle-and-parts-manufacturing"
  },
  "336992": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing complete military armored vehicles, combat tanks, specialized components for combat tanks, and self-propelled weapons. Cross-References.",
    industries: ["transportation-equipment-manufacturing"],
    name: "Military Armored Vehicle, Tank, and Tank Component Manufacturing",
    slug: "military-armored-vehicle-tank-and-tank-component-manufacturing"
  },
  "336999": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing transportation equipment (except motor vehicles, motor vehicle parts, boats, ships, railroad rolling stock, aerospace products, motorcycles, bicycles, armored vehicles, and tanks). Illustrative Examples: All-terrain vehicles (ATVs), wheeled or tracked, manufacturing, Animal-drawn vehicles and parts manufacturing, Gocarts (except children's) manufacturing, Golf carts and similar motorized passenger carriers manufacturing, Race cars manufacturing, Snowmobiles and parts manufacturing.",
    industries: ["transportation-equipment-manufacturing"],
    name: "All Other Transportation Equipment Manufacturing",
    slug: "all-other-transportation-equipment-manufacturing"
  },
  "337110": {
    description: "This industry comprises establishments primarily engaged in manufacturing wood or plastics laminated on wood kitchen cabinets, bathroom vanities, and countertops (except freestanding).  The cabinets and counters may be made on a stock or custom basis.",
    industries: ["household-and-institutional-furniture-manufacturing"],
    name: "Wood Kitchen Cabinet and Countertop Manufacturing",
    slug: "wood-kitchen-cabinet-and-countertop-manufacturing"
  },
  "337121": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing upholstered household-type furniture.  The furniture may be made on a stock or custom basis.",
    industries: ["household-and-institutional-furniture-manufacturing"],
    name: "Upholstered Household Furniture Manufacturing",
    slug: "upholstered-household-furniture-manufacturing"
  },
  "337122": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing nonupholstered wood household-type furniture and freestanding cabinets (except television, stereo, and sewing machine cabinets).  The furniture may be made on a stock or custom basis and may be assembled or unassembled (i.e., knockdown).",
    industries: ["household-and-institutional-furniture-manufacturing"],
    name: "Nonupholstered Wood Household Furniture Manufacturing",
    slug: "nonupholstered-wood-household-furniture-manufacturing"
  },
  "337126": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing nonupholstered household-type furniture of materials other than wood, such as metal, plastics, reed, rattan, wicker, and fiberglass.  The furniture may be partially upholstered (e.g., chairs with upholstered seats or backs), made on a stock or custom basis, and may be assembled or unassembled (i.e., knockdown).",
    industries: ["household-and-institutional-furniture-manufacturing"],
    name: "Household Furniture (except Wood and Upholstered) Manufacturing",
    slug: "household-furniture-except-wood-and-upholstered-manufacturing"
  },
  "337127": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing institutional-type furniture (e.g., library, school, theater, and church furniture).  Included in this industry are establishments primarily engaged in manufacturing general purpose hospital, laboratory, and dental furniture (e.g., tables, stools, and benches).  The furniture may be made on a stock or custom basis and may be assembled or unassembled (i.e., knockdown).",
    industries: ["household-and-institutional-furniture-manufacturing"],
    name: "Institutional Furniture Manufacturing",
    slug: "institutional-furniture-manufacturing"
  },
  "337211": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing wood office-type furniture.  The furniture may be made on a stock or custom basis and may be assembled or unassembled (i.e., knockdown).",
    industries: ["office-furniture-and-fixtures-manufacturing"],
    name: "Wood Office Furniture Manufacturing",
    slug: "wood-office-furniture-manufacturing"
  },
  "337212": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing custom designed interiors consisting of architectural woodwork and fixtures utilizing wood, wood products, and plastics laminates.  All of the industry output is made to individual order on a job shop basis and requires skilled craftsmen as a labor input.  A job might include custom manufacturing of display fixtures, gondolas, wall shelving units, entrance and window architectural detail, sales and reception counters, wall paneling, and matching furniture.",
    industries: ["furniture-and-home-furnishings-manufacturing"],
    name: "Custom Architectural Woodwork and Millwork Manufacturing",
    slug: "custom-architectural-woodwork-and-millwork-manufacturing"
  },
  "337214": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing nonwood office-type furniture.  The furniture may be made on a stock or custom basis and may be assembled or unassembled (i.e., knockdown). Cross-References.",
    industries: ["office-furniture-and-fixtures-manufacturing"],
    name: "Office Furniture (except Wood) Manufacturing",
    slug: "office-furniture-except-wood-manufacturing"
  },
  "337215": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing wood and nonwood office and store fixtures, shelving, lockers, frames, partitions, and related fabricated products of wood and nonwood materials, including plastics laminated fixture tops.  The products are made on a stock or custom basis and may be assembled or unassembled (i.e., knockdown).  Establishments exclusively making furniture parts (e.g., frames) are included in this industry.",
    industries: ["office-furniture-and-fixtures-manufacturing"],
    name: "Showcase, Partition, Shelving, and Locker Manufacturing",
    slug: "showcase-partition-shelving-and-locker-manufacturing"
  },
  "337910": {
    description: "This industry comprises establishments primarily engaged in manufacturing innerspring, box spring, and noninnerspring mattresses.",
    industries: ["mattress-and-blinds-manufacturing"],
    name: "Mattress Manufacturing",
    slug: "mattress-manufacturing"
  },
  "337920": {
    description: "This industry comprises establishments primarily engaged in manufacturing one or more of the following: venetian blinds, other window blinds, and shades; curtain and drapery rods and poles; and/or curtain and drapery fixtures.  The blinds and shades may be made on a stock or custom basis and may be made of any material.",
    industries: ["mattress-and-blinds-manufacturing"],
    name: "Blind and Shade Manufacturing",
    slug: "blind-and-shade-manufacturing"
  },
  "339112": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing medical, surgical, ophthalmic, and veterinary instruments and apparatus (except electrotherapeutic, electromedical, and irradiation apparatus).  Examples of products made by these establishments are syringes, hypodermic needles, anesthesia apparatus, blood transfusion equipment, catheters, surgical clamps, and medical thermometers.",
    industries: ["medical-equipment-manufacturing"],
    name: "Surgical and Medical Instrument Manufacturing",
    slug: "surgical-and-medical-instrument-manufacturing"
  },
  "339113": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing surgical appliances and supplies.  Examples of products made by these establishments are orthopedic devices, prosthetic appliances, surgical dressings, crutches, surgical sutures, personal industrial safety devices (except protective eyewear), hospital beds, and operating room tables.",
    industries: ["medical-equipment-manufacturing"],
    name: "Surgical Appliance and Supplies Manufacturing",
    slug: "surgical-appliance-and-supplies-manufacturing"
  },
  "339114": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing dental equipment and supplies used by dental laboratories and offices of dentists, such as dental chairs, dental instrument delivery systems, dental hand instruments, dental impression material, and dental cements. Cross-References.",
    industries: ["medical-equipment-manufacturing"],
    name: "Dental Equipment and Supplies Manufacturing",
    slug: "dental-equipment-and-supplies-manufacturing"
  },
  "339115": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing ophthalmic goods.  Examples of products made by these establishments are prescription eyeglasses (except grinding lenses in a retail setting), contact lenses, sunglasses, eyeglass frames, reading glasses made to standard powers, and protective eyewear.",
    industries: ["medical-equipment-manufacturing"],
    name: "Ophthalmic Goods Manufacturing",
    slug: "ophthalmic-goods-manufacturing"
  },
  "339116": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing dentures, crowns, bridges, and orthodontic appliances customized for individual application. Cross-References.",
    industries: ["medical-equipment-manufacturing"],
    name: "Dental Laboratories",
    slug: "dental-laboratories"
  },
  "339910": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) manufacturing, engraving, chasing, or etching fine and costume jewelry; (2) manufacturing, engraving, chasing, or etching metal personal goods (i.e., small articles carried on or about the person, such as compacts or cigarette cases); (3) manufacturing, engraving, chasing, or etching precious metal solid, precious metal clad, or pewter flatware and other hollowware; (4) stamping coins; (5) manufacturing unassembled jewelry parts and stock shop products, such as sheet, wire, and tubing; (6) cutting, slabbing, tumbling, carving, engraving, polishing, or faceting precious or semiprecious stones and gems; (7) recutting, repolishing, and setting gem stones; and (8) drilling, sawing, and peeling cultured and costume pearls.  This industry includes establishments primarily engaged in manufacturing precious solid, precious clad, and precious plated jewelry and personal goods.",
    industries: ["manufacturing"],
    name: "Jewelry and Silverware Manufacturing",
    slug: "jewelry-and-silverware-manufacturing"
  },
  "339920": {
    description: "This industry comprises establishments primarily engaged in manufacturing sporting and athletic goods (except apparel and footwear).",
    industries: ["sporting-goods-manufacturing"],
    name: "Sporting and Athletic Goods Manufacturing",
    slug: "sporting-and-athletic-goods-manufacturing"
  },
  "339930": {
    description: "This industry comprises establishments primarily engaged in manufacturing complete dolls, doll parts, doll clothes, action figures, toys, games (including electronic, such as gaming consoles and devices and handheld video games), hobby kits, and children's vehicles (except metal bicycles and tricycles).",
    industries: ["manufacturing"],
    name: "Doll, Toy, and Game Manufacturing",
    slug: "doll-toy-and-game-manufacturing"
  },
  "339940": {
    description: "This industry comprises establishments primarily engaged in manufacturing office supplies.  Examples of products made by these establishments are pens, pencils, felt tip markers, crayons, chalk, pencil sharpeners, staplers, modeling clay, hand operated stamps, stamp pads, stencils, carbon paper, and inked ribbons.",
    industries: ["manufacturing"],
    name: "Office Supplies (except Paper) Manufacturing",
    slug: "office-supplies-except-paper-manufacturing"
  },
  "339950": {
    description: "This industry comprises establishments primarily engaged in manufacturing signs and related displays of all materials (except printing paper and paperboard signs, notices, displays).",
    industries: ["manufacturing"],
    name: "Sign Manufacturing",
    slug: "sign-manufacturing"
  },
  "339991": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing gaskets, packing, and sealing devices of all materials.",
    industries: ["manufacturing"],
    name: "Gasket, Packing, and Sealing Device Manufacturing",
    slug: "gasket-packing-and-sealing-device-manufacturing"
  },
  "339992": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing musical instruments (except toys). Cross-References.",
    industries: ["manufacturing"],
    name: "Musical Instrument Manufacturing",
    slug: "musical-instrument-manufacturing"
  },
  "339993": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing fasteners, buttons, needles, pins, and buckles (except made of precious metals or precious and semiprecious stones and gems).",
    industries: ["manufacturing"],
    name: "Fastener, Button, Needle, and Pin Manufacturing",
    slug: "fastener-button-needle-and-pin-manufacturing"
  },
  "339994": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing brooms, mops, and brushes.",
    industries: ["manufacturing"],
    name: "Broom, Brush, and Mop Manufacturing",
    slug: "broom-brush-and-mop-manufacturing"
  },
  "339995": {
    description: "This U.S. industry comprises establishments primarily engaged in manufacturing burial caskets, cases, and vaults (except concrete). Cross-References.",
    industries: ["manufacturing"],
    name: "Burial Casket Manufacturing",
    slug: "burial-casket-manufacturing"
  },
  "339999": {
    description: "This U.S. industry comprises establishments primarily engaged in miscellaneous manufacturing (except medical equipment and supplies, jewelry and silverware, sporting and athletic goods, dolls, toys, games, office supplies, signs, gaskets, packing, and sealing devices, musical instruments, fasteners, buttons, needles, pins, brooms, brushes, mops, and burial caskets). Illustrative Examples: Artificial Christmas trees manufacturing, Candles manufacturing, Christmas tree ornaments (except glass and electric) manufacturing, Cigarette lighters (except precious metal) manufacturing, Coin- or card-operated amusement machines (except jukebox) manufacturing, Electronic cigarettes manufacturing, Hairpieces (e.g., wigs, toupees, wiglets) manufacturing, Portable fire extinguishers manufacturing, Potpourri manufacturing, Tobacco pipes manufacturing, Umbrellas manufacturing.",
    industries: ["manufacturing"],
    name: "All Other Miscellaneous Manufacturing",
    slug: "all-other-miscellaneous-manufacturing"
  },
  "423110": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of new and used passenger automobiles, trucks, trailers, and other motor vehicles, such as motorcycles, motor homes, and snowmobiles.",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Automobile and Other Motor Vehicle Merchant Wholesalers",
    slug: "automobile-and-other-motor-vehicle-merchant-wholesalers"
  },
  "423120": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of motor vehicle supplies, accessories, tools, and equipment; and new motor vehicle parts (except new tires and tubes).",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Motor Vehicle Supplies and New Parts Merchant Wholesalers",
    slug: "motor-vehicle-supplies-and-new-parts-merchant-wholesalers"
  },
  "423130": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of new and/or used tires and tubes for passenger and commercial vehicles.",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Tire and Tube Merchant Wholesalers",
    slug: "tire-and-tube-merchant-wholesalers"
  },
  "423140": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of used motor vehicle parts (except used tires and tubes) and establishments primarily engaged in dismantling motor vehicles for the purpose of selling the parts.",
    industries: ["wholesale-motor-vehicles-and-parts"],
    name: "Motor Vehicle Parts (Used) Merchant Wholesalers",
    slug: "motor-vehicle-parts-used-merchant-wholesalers"
  },
  "423210": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of furniture (except hospital beds, medical furniture, and drafting tables). Illustrative Examples: Household-type furniture merchant wholesalers, Outdoor furniture merchant wholesalers, Mattresses merchant wholesalers, Public building furniture merchant wholesalers, Office furniture merchant wholesalers, Religious furniture merchant wholesalers.",
    industries: ["wholesale-furniture-and-home-furnishings"],
    name: "Furniture Merchant Wholesalers",
    slug: "furniture-merchant-wholesalers"
  },
  "423220": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of home furnishings and/or housewares. Illustrative Examples: Carpet merchant wholesalers, Floor coverings (except wood flooring) merchant wholesalers, Glassware, household-type, merchant wholesalers, Chinaware, household-type, merchant wholesalers, Lamps (i.e., lighting fixtures) merchant wholesalers, Household-type cooking utensils merchant wholesalers, Curtains merchant wholesalers, Draperies merchant wholesalers, Linens (e.g., bath, bed, table) merchant wholesalers, Window shades and blinds merchant wholesalers.",
    industries: ["wholesale-furniture-and-home-furnishings"],
    name: "Home Furnishing Merchant Wholesalers",
    slug: "home-furnishing-merchant-wholesalers"
  },
  "423310": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of lumber; plywood; reconstituted wood fiber products; wood fencing; doors and windows and their frames (all materials); wood roofing and siding; and/or other wood or metal millwork.",
    industries: ["wholesale-building-materials"],
    name: "Lumber, Plywood, Millwork, and Wood Panel Merchant Wholesalers",
    slug: "lumber-plywood-millwork-and-wood-panel-merchant-wholesalers"
  },
  "423320": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of stone, cement, lime, construction sand, and gravel; brick; asphalt and concrete mixtures; and/or concrete, stone, and structural clay products.",
    industries: ["wholesale-building-materials"],
    name: "Brick, Stone, and Related Construction Material Merchant Wholesalers",
    slug: "brick-stone-and-related-construction-material-merchant-wholesalers"
  },
  "423330": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of nonwood roofing and nonwood siding and insulation materials. Cross-References.",
    industries: ["wholesale-building-materials"],
    name: "Roofing, Siding, and Insulation Material Merchant Wholesalers",
    slug: "roofing-siding-and-insulation-material-merchant-wholesalers"
  },
  "423390": {
    description: "This industry comprises (1) establishments primarily engaged in the merchant wholesale distribution of manufactured homes (i.e., mobile homes) and/or prefabricated buildings and (2) establishments primarily engaged in the merchant wholesale distribution of construction materials (except lumber, plywood, millwork, wood panels, brick, stone, roofing, siding, electrical and wiring supplies, and insulation materials). Illustrative Examples: Flat glass merchant wholesalers, Prefabricated buildings (except wood) merchant wholesalers, Ornamental ironwork merchant wholesalers, Fencing and fencing accessories (except wood) merchant wholesalers, Plate glass merchant wholesalers, Storage bins merchant wholesalers.",
    industries: ["wholesale-building-materials"],
    name: "Other Construction Material Merchant Wholesalers",
    slug: "other-construction-material-merchant-wholesalers"
  },
  "423410": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of photographic equipment and supplies (except office equipment). Illustrative Examples: Photofinishing equipment merchant wholesalers, Television cameras merchant wholesalers, Photographic camera equipment and supplies merchant wholesalers, Video cameras (except household-type) merchant wholesalers, Photographic film and plates merchant wholesalers.",
    industries: ["wholesale-photography-equipment-and-supplies"],
    name: "Photographic Equipment and Supplies Merchant Wholesalers",
    slug: "photographic-equipment-and-supplies-merchant-wholesalers"
  },
  "423420": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of office machines and related equipment (except computers and computer peripheral equipment). Illustrative Examples: Automated teller machines (ATM) merchant wholesalers, Calculators and calculating machines merchant wholesalers, Cash registers merchant wholesalers, Security safes merchant wholesalers, Copying machines merchant wholesalers, Point of sale terminals merchant wholesalers, Paper shredders merchant wholesalers.",
    industries: ["wholesale"],
    name: "Office Equipment Merchant Wholesalers",
    slug: "office-equipment-merchant-wholesalers"
  },
  "423430": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of computers, computer peripheral equipment, loaded computer boards, and/or computer software.",
    industries: ["wholesale-computer-equipment"],
    name: "Computer and Computer Peripheral Equipment and Software Merchant Wholesalers",
    slug: "computer-and-computer-peripheral-equipment-and-software-merchant-wholesalers"
  },
  "423440": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of commercial and related machines and equipment (except photographic equipment and supplies; office equipment; and computers and computer peripheral equipment and software) generally used in restaurants and stores. Illustrative Examples: Balances and scales (except laboratory) merchant wholesalers, Commercial shelving merchant wholesalers, Coin- or card-operated merchandising machine merchant wholesalers, Electrical signs merchant wholesalers, Commercial chinaware merchant wholesalers, Partitions merchant wholesalers, Commercial cooking equipment merchant wholesalers, Store fixtures (except refrigerated) merchant wholesalers.",
    industries: ["wholesale"],
    name: "Other Commercial Equipment Merchant Wholesalers",
    slug: "other-commercial-equipment-merchant-wholesalers"
  },
  "423450": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of professional medical equipment, instruments, and supplies (except ophthalmic equipment and instruments and goods used by ophthalmologists, optometrists, and opticians). Illustrative Examples: Dental equipment and supplies merchant wholesalers, Surgical dressings merchant wholesalers, Electromedical equipment merchant wholesalers, Patient monitoring equipment merchant wholesalers, Hospital beds merchant wholesalers, Prosthetic appliances and supplies merchant wholesalers, Hospital furniture merchant wholesalers, Surgical instruments and apparatus merchant wholesalers, Medical and dental X-ray machines and parts merchant wholesalers, First-aid kits (except household) merchant wholesalers, Surgical and medical masks merchant wholesalers.",
    industries: ["wholesale"],
    name: "Medical, Dental, and Hospital Equipment and Supplies Merchant Wholesalers",
    slug: "medical-dental-and-hospital-equipment-and-supplies-merchant-wholesalers"
  },
  "423460": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of professional equipment, instruments, and/or goods sold, prescribed, or used by ophthalmologists, optometrists, and opticians. Illustrative Examples: Binoculars merchant wholesalers, Optometric equipment and supplies merchant wholesalers, Ophthalmic frames merchant wholesalers, Sunglasses merchant wholesalers, Ophthalmic lenses merchant wholesalers",
    industries: ["wholesale"],
    name: "Ophthalmic Goods Merchant Wholesalers",
    slug: "ophthalmic-goods-merchant-wholesalers"
  },
  "423490": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of professional equipment and supplies (except ophthalmic goods and medical, dental, and hospital equipment and supplies). Illustrative Examples: Church supplies (except silverware, plated ware) merchant wholesalers, School equipment and supplies (except books, furniture) merchant wholesalers, Drafting tables and instruments merchant wholesalers, Scientific instruments merchant wholesalers, Laboratory equipment (except medical, dental) merchant wholesalers, Surveying equipment and supplies merchant wholesalers.",
    industries: ["wholesale"],
    name: "Other Professional Equipment and Supplies Merchant Wholesalers",
    slug: "other-professional-equipment-and-supplies-merchant-wholesalers"
  },
  "423510": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of products of the primary metals industries.  Service centers maintain inventory and may perform functions, such as sawing, shearing, bending, leveling, cleaning, or edging, on a custom basis as part of sales transactions. Illustrative Examples: Cast iron pipe merchant wholesalers, Metal rods merchant wholesalers, Metal bars (except precious) merchant wholesalers, Metal sheets merchant wholesalers, Metal spikes merchant wholesalers, Metal ingots (except precious) merchant wholesalers, Metal pipe merchant wholesalers, Nails merchant wholesalers, Metal plates merchant wholesalers, Noninsulated wire merchant wholesalers.",
    industries: ["wholesale-metals-and-minerals"],
    name: "Metal Service Centers and Other Metal Merchant Wholesalers",
    slug: "metal-service-centers-and-other-metal-merchant-wholesalers"
  },
  "423520": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of coal, coke, metal ores, and/or nonmetallic minerals (except precious and semiprecious stones and minerals used in construction, such as sand and gravel).",
    industries: ["wholesale-metals-and-minerals"],
    name: "Coal and Other Mineral and Ore Merchant Wholesalers",
    slug: "coal-and-other-mineral-and-ore-merchant-wholesalers"
  },
  "423610": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of electrical construction materials; wiring supplies; electric light fixtures; light bulbs; and/or electrical power equipment for the generation, transmission, distribution, or control of electric energy.",
    industries: ["wholesale-appliances-electrical-and-electronics"],
    name: "Electrical Apparatus and Equipment, Wiring Supplies, and Related Equipment Merchant Wholesalers",
    slug: "electrical-apparatus-and-equipment-wiring-supplies-and-related-equipment-merchant-wholesalers"
  },
  "423620": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of household-type gas and electric appliances (except water heaters and heating stoves (i.e., noncooking)), room air-conditioners, and/or household-type audio or video equipment. Illustrative Examples: Household-type sewing machines merchant wholesalers, Household-type radios (including automotive) merchant wholesalers, Household-type video cameras merchant wholesalers, Household-type refrigerators merchant wholesalers, Television sets merchant wholesalers, Toothbrushes, electric, merchant wholesalers, Curling irons, electric, merchant wholesalers.",
    industries: ["wholesale-appliances-electrical-and-electronics"],
    name: "Household Appliances, Electric Housewares, and Consumer Electronics Merchant Wholesalers",
    slug: "household-appliances-electric-housewares-and-consumer-electronics-merchant-wholesalers"
  },
  "423690": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of electronic parts and equipment (except electrical apparatus and equipment, wiring supplies, and construction materials; electrical and electronic appliances; and television sets and radios). Illustrative Examples: Blank compact discs (CDs) merchant wholesalers, Blank digital video discs (DVDs) merchant wholesalers, Broadcasting equipment merchant wholesalers, Cellular telephones merchant wholesalers, Communications equipment merchant wholesalers, Modems and routers merchant wholesalers, Radar equipment merchant wholesalers, Teleconferencing equipment merchant wholesalers, Unloaded computer boards merchant wholesalers.",
    industries: ["wholesale-appliances-electrical-and-electronics"],
    name: "Other Electronic Parts and Equipment Merchant Wholesalers",
    slug: "other-electronic-parts-and-equipment-merchant-wholesalers"
  },
  "423710": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of hardware, knives, or handtools. Illustrative Examples: Brads merchant wholesalers, Cutlery merchant wholesalers, Knives (except disposable plastics) merchant wholesalers, Power handtools (e.g., drills, saws, sanders) merchant wholesalers, Fasteners (e.g., bolts, nuts, rivets, screws) merchant wholesalers, Staples merchant wholesalers, Handtools (except motor vehicle, machinists' precision) merchant wholesalers, Hardware (except motor vehicle) merchant wholesalers, Tacks merchant wholesalers, Keys and locks merchant wholesalers.",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Hardware Merchant Wholesalers",
    slug: "hardware-merchant-wholesalers"
  },
  "423720": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of plumbing equipment, hydronic heating equipment, household-type water heaters, and/or supplies.",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Plumbing and Heating Equipment and Supplies (Hydronics) Merchant Wholesalers",
    slug: "plumbing-and-heating-equipment-and-supplies-hydronics-merchant-wholesalers"
  },
  "423730": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of warm air heating and air-conditioning equipment and supplies. Illustrative Examples: Air pollution control equipment and supplies merchant wholesalers, Nonportable electric baseboard heaters merchant wholesalers, Air-conditioning equipment (except room units) merchant wholesalers, Warm air central heating equipment merchant wholesalers, Automotive air-conditioners merchant wholesalers, Ductless heating and air-conditioning system equipment merchant wholesalers .",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Warm Air Heating and Air-Conditioning Equipment and Supplies Merchant Wholesalers",
    slug: "warm-air-heating-and-air-conditioning-equipment-and-supplies-merchant-wholesalers"
  },
  "423740": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of refrigeration equipment (except household-type refrigerators, freezers, and air-conditioners). Illustrative Examples: Cold storage machinery merchant wholesalers, Refrigerated display cases merchant wholesalers, Commercial refrigerators merchant wholesalers, Water coolers merchant wholesalers.",
    industries: ["wholesale-hardware-plumbing-heating-equipment"],
    name: "Refrigeration Equipment and Supplies Merchant Wholesalers",
    slug: "refrigeration-equipment-and-supplies-merchant-wholesalers"
  },
  "423810": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of specialized machinery, equipment, and related parts generally used in construction, mining (except oil well), and logging activities. Illustrative Examples: Excavating machinery and equipment merchant wholesalers, Road construction and maintenance machinery merchant wholesalers, Forestry machinery and equipment merchant wholesalers, Scaffolding merchant wholesalers, Mining cranes merchant wholesalers, Cross-References.",
    industries: ["wholesale-machinery"],
    name: "Construction and Mining (except Oil Well) Machinery and Equipment Merchant Wholesalers",
    slug: "construction-and-mining-except-oil-well-machinery-and-equipment-merchant-wholesalers"
  },
  "423820": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of specialized machinery, equipment, and related parts generally used in agricultural, farm, and lawn and garden activities. Illustrative Examples: Animal feeders merchant wholesalers, Lawnmowers merchant wholesalers, Milking machinery and equipment merchant wholesalers, Harvesting machinery and equipment merchant wholesalers, Planting machinery and equipment merchant wholesalers",
    industries: ["wholesale-machinery"],
    name: "Farm and Garden Machinery and Equipment Merchant Wholesalers",
    slug: "farm-and-garden-machinery-and-equipment-merchant-wholesalers"
  },
  "423830": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of specialized machinery, equipment, and related parts generally used in manufacturing, oil well, and warehousing activities. Illustrative Examples: Fluid power transmission equipment merchant wholesalers, Metalworking machinery and equipment merchant wholesalers, Food processing machinery and equipment merchant wholesalers, Oil well machinery and equipment merchant wholesalers, Material handling machinery and equipment merchant wholesalers.",
    industries: ["wholesale-machinery"],
    name: "Industrial Machinery and Equipment Merchant Wholesalers",
    slug: "industrial-machinery-and-equipment-merchant-wholesalers"
  },
  "423840": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of supplies for machinery and equipment generally used in manufacturing, oil well, and warehousing activities. Illustrative Examples: Industrial containers merchant wholesalers, Industrial diamonds merchant wholesalers, Printing inks merchant wholesalers, Industrial sand merchant wholesalers, Refractory materials (e.g., brick, blocks, shapes) merchant wholesalers, Welding supplies (except welding gases) merchant wholesalers.",
    industries: ["wholesale"],
    name: "Industrial Supplies Merchant Wholesalers",
    slug: "industrial-supplies-merchant-wholesalers"
  },
  "423850": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of specialized equipment and supplies of the type used by service establishments (except specialized equipment and supplies used in offices, stores, hotels, restaurants, schools, health and medical facilities, photographic facilities, and specialized equipment used in transportation and construction activities). Illustrative Examples: Amusement park equipment merchant wholesalers, Beauty parlor equipment and supplies merchant wholesalers, Car wash equipment and supplies merchant wholesalers, Drycleaning equipment and supplies merchant wholesalers, Voting machines merchant wholesalers, Janitorial equipment and supplies merchant wholesalers, Undertakers' equipment and supplies merchant wholesalers, Upholsterers' equipment and supplies (except fabrics) merchant wholesalers, Water treatment equipment, municipal, merchant wholesalers.",
    industries: ["wholesale"],
    name: "Service Establishment Equipment and Supplies Merchant Wholesalers",
    slug: "service-establishment-equipment-and-supplies-merchant-wholesalers"
  },
  "423860": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of transportation equipment and supplies (except marine pleasure craft and motor vehicles). Illustrative Examples: Aircraft merchant wholesalers, Railroad cars merchant wholesalers, Ships merchant wholesalers, Motorized passenger golf carts merchant wholesalers, Boats (except pleasure) merchant wholesalers.",
    industries: ["wholesale"],
    name: "Transportation Equipment and Supplies (except Motor Vehicle) Merchant Wholesalers",
    slug: "transportation-equipment-and-supplies-except-motor-vehicle-merchant-wholesalers"
  },
  "423910": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of sporting goods and accessories; billiard and pool supplies; sporting firearms and ammunition; and/or marine pleasure craft, equipment, and supplies.",
    industries: ["wholesale"],
    name: "Sporting and Recreational Goods and Supplies Merchant Wholesalers",
    slug: "sporting-and-recreational-goods-and-supplies-merchant-wholesalers"
  },
  "423920": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of games, toys, fireworks, playing cards, hobby goods and supplies, and/or related goods.",
    industries: ["wholesale"],
    name: "Toy and Hobby Goods and Supplies Merchant Wholesalers",
    slug: "toy-and-hobby-goods-and-supplies-merchant-wholesalers"
  },
  "423930": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of automotive scrap, industrial scrap, and other recyclable materials.  Included in this industry are auto wreckers primarily engaged in dismantling motor vehicles for the purpose of wholesaling scrap.",
    industries: ["wholesale-recyclable-materials"],
    name: "Recyclable Material Merchant Wholesalers",
    slug: "recyclable-material-merchant-wholesalers"
  },
  "423940": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of jewelry, precious and semiprecious stones, precious metals and metal flatware, costume jewelry, watches, clocks, silverware, and/or jewelers' findings.",
    industries: ["wholesale-luxury-goods-and-jewelry"],
    name: "Jewelry, Watch, Precious Stone, and Precious Metal Merchant Wholesalers",
    slug: "jewelry-watch-precious-stone-and-precious-metal-merchant-wholesalers"
  },
  "423990": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of durable goods (except motor vehicles and motor vehicle parts and supplies; furniture and home furnishings; lumber and other construction materials; professional and commercial equipment and supplies; metals and minerals (except petroleum); electrical goods; hardware, and plumbing and heating equipment and supplies; machinery, equipment, and supplies; sporting and recreational goods and supplies; toy and hobby goods and supplies; recyclable materials; and jewelry, watches, precious stones, and precious metals). Illustrative Examples: Firearms (except sporting) merchant wholesalers, Musical instruments merchant wholesalers, Luggage merchant wholesalers, Monuments and grave markers merchant wholesalers, Prerecorded compact discs (CDs) and digital video discs (DVDs) merchant wholesalers, Timber and timber products (except lumber) merchant wholesalers, Personal safety devices and supplies (e.g., eye shields, face shields, nonelectric respirators) merchant wholesalers, Signs (except electrical) merchant wholesalers.",
    industries: ["wholesale"],
    name: "Other Miscellaneous Durable Goods Merchant Wholesalers",
    slug: "other-miscellaneous-durable-goods-merchant-wholesalers"
  },
  "424110": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of bulk printing and/or writing paper generally on rolls for further processing. Illustrative Examples: Bulk envelope paper merchant wholesalers, Bulk paper (e.g., fine, printing, writing) merchant wholesalers, Bulk groundwood paper merchant wholesalers, Newsprint merchant wholesalers , Cross-References.",
    industries: ["wholesale-paper-products"],
    name: "Printing and Writing Paper Merchant Wholesalers",
    slug: "printing-and-writing-paper-merchant-wholesalers"
  },
  "424120": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of stationery, office supplies, and/or gift wrap. Illustrative Examples: Photocopy supplies merchant wholesalers, Envelopes merchant wholesalers, Social stationery merchant wholesalers, File cards and folders merchant wholesalers, Greeting cards merchant wholesalers, Office paper (e.g., printer, copier) merchant wholesalers, Writing pens merchant wholesalers, Pencils merchant wholesalers, Cross-References.",
    industries: ["wholesale-paper-products"],
    name: "Stationery and Office Supplies Merchant Wholesalers",
    slug: "stationery-and-office-supplies-merchant-wholesalers"
  },
  "424130": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of kraft wrapping and other coarse paper, paperboard, converted paper (except stationery and office supplies), and/or related disposable plastics products. Illustrative Examples: Corrugated paper merchant wholesalers, Paper and disposable plastics tableware (e.g., utensils, dishes, cups) merchant wholesalers, Paper napkins merchant wholesalers, Paperboard and disposable plastics boxes and containers merchant wholesalers, Plastics bags merchant wholesalers, Paper and disposable plastics shipping supplies merchant wholesalers, Paper towels merchant wholesalers, Paper bags merchant wholesalers, Sanitary paper products merchant wholesalers, Wrapping paper (except gift wrap) merchant wholesalers, Waxed paper merchant wholesalers, Plastics foam products, disposable (except packaging, packing), merchant wholesalers .",
    industries: ["wholesale-paper-products"],
    name: "Industrial and Personal Service Paper Merchant Wholesalers",
    slug: "industrial-and-personal-service-paper-merchant-wholesalers"
  },
  "424210": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of biological and medical products; botanical drugs and herbs; and pharmaceutical products intended for internal and/or external consumption in such forms as ampoules, tablets, capsules, vials, ointments, powders, solutions, and suspensions. Illustrative Examples: Antibiotics merchant wholesalers, Endocrine substances merchant wholesalers, Blood derivatives merchant wholesalers, In-vitro and in-vivo diagnostics merchant wholesalers, Drugs merchant wholesalers, Botanicals merchant wholesalers, Vaccines merchant wholesalers, Cosmetics merchant wholesalers, Vitamins merchant wholesalers, Veterinarians' medicines merchant wholesalers, Cross-References.",
    industries: ["wholesale-drugs-and-sundries"],
    name: "Drugs and Druggists' Sundries Merchant Wholesalers",
    slug: "drugs-and-druggists-sundries-merchant-wholesalers"
  },
  "424310": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of piece goods, fabrics, knitting yarns (except industrial), thread and other notions, and/or hair accessories. Cross-References.",
    industries: ["wholesale-apparel-and-sewing-supplies"],
    name: "Piece Goods, Notions, and Other Dry Goods Merchant Wholesalers",
    slug: "piece-goods-notions-and-other-dry-goods-merchant-wholesalers"
  },
  "424340": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of footwear of leather, rubber, and other materials, including athletic footwear (except specialty athletic footwear). Cross-References.",
    industries: ["wholesale-footwear"],
    name: "Footwear Merchant Wholesalers",
    slug: "footwear-merchant-wholesalers"
  },
  "424350": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of clothing and clothing accessories. Illustrative Examples: Baby bottles merchant wholesalers, Diapers (except disposable) merchant wholesalers, Dresses merchant wholesalers, Fur clothing merchant wholesalers, Handbags merchant wholesalers, Hosiery merchant wholesalers, Lingerie merchant wholesalers, Millinery merchant wholesalers, Nightwear merchant wholesalers, Outerwear merchant wholesalers, Sportswear merchant wholesalers, Swimwear merchant wholesalers, Cross-References.",
    industries: ["wholesale-apparel-and-sewing-supplies"],
    name: "Clothing and Clothing Accessories Merchant Wholesalers",
    slug: "clothing-and-clothing-accessories-merchant-wholesalers"
  },
  "424410": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of a general line (wide range) of groceries. Cross-References.",
    industries: ["wholesale-food-and-beverage"],
    name: "General Line Grocery Merchant Wholesalers",
    slug: "general-line-grocery-merchant-wholesalers"
  },
  "424420": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of packaged frozen foods (except dairy products). Illustrative Examples: Frozen bakery products merchant wholesalers, Packaged frozen fish merchant wholesalers, Frozen juices merchant wholesalers, Packaged frozen meats merchant wholesalers, Frozen vegetables merchant wholesalers, Packaged frozen poultry merchant wholesalers, Cross-References.",
    industries: ["wholesale-food-and-beverage"],
    name: "Packaged Frozen Food Merchant Wholesalers",
    slug: "packaged-frozen-food-merchant-wholesalers"
  },
  "424430": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of dairy products (except dried or canned). Illustrative Examples: Butter merchant wholesalers, Fluid milk (except canned) merchant wholesalers, Cheese merchant wholesalers, Ice cream and ices merchant wholesalers, Cream merchant wholesalers, Yogurt merchant wholesalers .",
    industries: ["wholesale-food-and-beverage"],
    name: "Dairy Product (except Dried or Canned) Merchant Wholesalers",
    slug: "dairy-product-except-dried-or-canned-merchant-wholesalers"
  },
  "424440": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of poultry and/or poultry products (except canned and packaged frozen).",
    industries: ["wholesale-food-and-beverage"],
    name: "Poultry and Poultry Product Merchant Wholesalers",
    slug: "poultry-and-poultry-product-merchant-wholesalers"
  },
  "424450": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of confectioneries; salted or roasted nuts; popcorn; potato, corn, and similar chips; and/or fountain fruits and syrups.",
    industries: ["wholesale-food-and-beverage"],
    name: "Confectionery Merchant Wholesalers",
    slug: "confectionery-merchant-wholesalers"
  },
  "424460": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of fish and seafood (except canned or packaged frozen).",
    industries: ["wholesale-food-and-beverage"],
    name: "Fish and Seafood Merchant Wholesalers",
    slug: "fish-and-seafood-merchant-wholesalers"
  },
  "424470": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of meats and meat products (except canned and packaged frozen) and/or lard.",
    industries: ["wholesale-food-and-beverage"],
    name: "Meat and Meat Product Merchant Wholesalers",
    slug: "meat-and-meat-product-merchant-wholesalers"
  },
  "424480": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of fresh fruits and vegetables.",
    industries: ["wholesale-food-and-beverage"],
    name: "Fresh Fruit and Vegetable Merchant Wholesalers",
    slug: "fresh-fruit-and-vegetable-merchant-wholesalers"
  },
  "424490": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of groceries and related products (except a general line of groceries; packaged frozen food; dairy products (except dried and canned); poultry products (except canned); confectioneries; fish and seafood (except canned); meat products (except canned); and fresh fruits and vegetables).  Included in this industry are establishments primarily engaged in the bottling and merchant wholesale distribution of spring and mineral waters processed by others. Illustrative Examples: Bakery products (except frozen) merchant wholesalers, Canned seafood merchant wholesalers, Canned fish merchant wholesalers, Canned vegetables merchant wholesalers, Canned fruits merchant wholesalers, Dried milk merchant wholesalers, Canned meats merchant wholesalers, Soft drinks merchant wholesalers, Canned milk merchant wholesalers.",
    industries: ["wholesale-food-and-beverage"],
    name: "Other Grocery and Related Products Merchant Wholesalers",
    slug: "other-grocery-and-related-products-merchant-wholesalers"
  },
  "424510": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of grains, such as corn, wheat, oats, barley, and unpolished rice; dry beans; and soybeans and other inedible beans.  Included in this industry are establishments primarily engaged in operating country or terminal grain elevators primarily for the purpose of wholesaling.",
    industries: ["wholesale-raw-farm-products"],
    name: "Grain and Field Bean Merchant Wholesalers",
    slug: "grain-and-field-bean-merchant-wholesalers"
  },
  "424520": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of livestock (except horses and mules). Illustrative Examples: Cattle merchant wholesalers, Hogs merchant wholesalers, Goats merchant wholesalers, Sheep merchant wholesalers, Cross-References.",
    industries: ["wholesale-raw-farm-products"],
    name: "Livestock Merchant Wholesalers",
    slug: "livestock-merchant-wholesalers"
  },
  "424590": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of farm products (except grain and field beans, livestock, raw milk, live poultry, and fresh fruits and vegetables). Illustrative Examples: Chicks, live, merchant wholesalers, Hemp merchant wholesalers, Mules merchant wholesalers, Hides merchant wholesalers, Raw cotton merchant wholesalers, Horses merchant wholesalers, Nuts, unprocessed or shelled only, merchant wholesalers, Raw pelts merchant wholesalers, Leaf tobacco merchant wholesalers, Sod merchant wholesalers.",
    industries: ["wholesale-raw-farm-products"],
    name: "Other Farm Product Raw Material Merchant Wholesalers",
    slug: "other-farm-product-raw-material-merchant-wholesalers"
  },
  "424610": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of plastics materials and resins, and unsupported plastics film, sheet, sheeting, rod, tube, and other basic forms and shapes.",
    industries: ["wholesale-chemical-and-allied-products"],
    name: "Plastics Materials and Basic Forms and Shapes Merchant Wholesalers",
    slug: "plastics-materials-and-basic-forms-and-shapes-merchant-wholesalers"
  },
  "424690": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of chemicals and allied products (except agricultural and medicinal chemicals, paints and varnishes, fireworks, and plastics materials and basic forms and shapes). Illustrative Examples: Acids merchant wholesalers, Industrial chemicals merchant wholesalers, Automotive chemicals (except lubricating oils and greases) merchant wholesalers, Industrial salts merchant wholesalers, Dyestuffs merchant wholesalers, Rosins merchant wholesalers, Explosives (except ammunition and fireworks) merchant wholesalers, Turpentine merchant wholesalers .",
    industries: ["wholesale-chemical-and-allied-products"],
    name: "Other Chemical and Allied Products Merchant Wholesalers",
    slug: "other-chemical-and-allied-products-merchant-wholesalers"
  },
  "424710": {
    description: "This industry comprises establishments with bulk liquid storage facilities primarily engaged in the merchant wholesale distribution of crude petroleum and petroleum products, including liquefied petroleum gas. Cross-References.",
    industries: ["wholesale-petroleum-and-petroleum-products"],
    name: "Petroleum Bulk Stations and Terminals",
    slug: "petroleum-bulk-stations-and-terminals"
  },
  "424720": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of petroleum and petroleum products (except from bulk liquid storage facilities). Illustrative Examples: Gasoline merchant wholesalers (except bulk stations, terminals), Lubricating oil and grease merchant wholesalers (except bulk stations, terminals), Bottled liquid petroleum gas merchant wholesalers, Fuel oil merchant wholesalers (except bulk stations, terminals).",
    industries: ["wholesale-petroleum-and-petroleum-products"],
    name: "Petroleum and Petroleum Products Merchant Wholesalers (except Bulk Stations and Terminals)",
    slug: "petroleum-and-petroleum-products-merchant-wholesalers-except-bulk-stations-and-terminals"
  },
  "424810": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of beer, ale, porter, and other fermented malt beverages.",
    industries: ["wholesale-alcoholic-beverages"],
    name: "Beer and Ale Merchant Wholesalers",
    slug: "beer-and-ale-merchant-wholesalers"
  },
  "424820": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of wine, distilled alcoholic beverages, and/or neutral spirits and ethyl alcohol used in blended wines and distilled liquors.",
    industries: ["wholesale-alcoholic-beverages"],
    name: "Wine and Distilled Alcoholic Beverage Merchant Wholesalers",
    slug: "wine-and-distilled-alcoholic-beverage-merchant-wholesalers"
  },
  "424910": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of farm supplies, such as animal feeds, fertilizers, agricultural chemicals, pesticides, plant seeds, and plant bulbs.",
    industries: ["wholesale-raw-farm-products"],
    name: "Farm Supplies Merchant Wholesalers",
    slug: "farm-supplies-merchant-wholesalers"
  },
  "424920": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of books, periodicals, and newspapers.",
    industries: ["wholesale"],
    name: "Book, Periodical, and Newspaper Merchant Wholesalers",
    slug: "book-periodical-and-newspaper-merchant-wholesalers"
  },
  "424930": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of flowers, florists' supplies, and/or nursery stock (except plant seeds and plant bulbs).",
    industries: ["wholesale"],
    name: "Flower, Nursery Stock, and Florists' Supplies Merchant Wholesalers",
    slug: "flower-nursery-stock-and-florists-supplies-merchant-wholesalers"
  },
  "424940": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of tobacco products, such as cigarettes, snuff, cigars, pipe tobacco, and electronic cigarettes (including related parts and accessories).",
    industries: ["wholesale"],
    name: "Tobacco Product and Electronic Cigarette Merchant Wholesalers",
    slug: "tobacco-product-and-electronic-cigarette-merchant-wholesalers"
  },
  "424950": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of paints, varnishes, and similar coatings; pigments; wallpaper; and supplies, such as paintbrushes and rollers. Cross-References.",
    industries: ["wholesale-chemical-and-allied-products"],
    name: "Paint, Varnish, and Supplies Merchant Wholesalers",
    slug: "paint-varnish-and-supplies-merchant-wholesalers"
  },
  "424990": {
    description: "This industry comprises establishments primarily engaged in the merchant wholesale distribution of nondurable goods (except printing and writing paper; stationery and office supplies; industrial and personal service paper; drugs and druggists' sundries; apparel, piece goods, and notions; grocery and related products; farm product raw materials; chemical and allied products; petroleum and petroleum products; beer, wine, and distilled alcoholic beverages; farm supplies; books, periodicals, and newspapers; flowers, nursery stock, and florists' supplies; tobacco and tobacco products; and paint, varnishes, wallpaper, and supplies). Illustrative Examples: Artists' supplies merchant wholesalers, Pet supplies (except pet food) merchant wholesalers, Burlap merchant wholesalers, Statuary (except religious) merchant wholesalers, Christmas trees (e.g., artificial, cut) merchant wholesalers, Textile bags merchant wholesalers, Industrial yarns merchant wholesalers.",
    industries: ["wholesale"],
    name: "Other Miscellaneous Nondurable Goods Merchant Wholesalers",
    slug: "other-miscellaneous-nondurable-goods-merchant-wholesalers"
  },
  "425120": {
    description: "This industry comprises wholesale trade agents and brokers acting on behalf of buyers or sellers in the wholesale distribution of goods, including those that use the Internet or other electronic means to bring together buyers and sellers.  Agents and brokers do not take title to the goods being sold but rather receive a commission or fee for their service.  Agents and brokers for all durable and nondurable goods are included in this industry. Illustrative Examples: Independent sales representatives, Manufacturers' sales representatives, Group purchasing organizations (acting as agents for goods distribution), Cross-References.",
    industries: ["internet-marketplace-platforms"],
    name: "Wholesale Trade Agents and Brokers",
    slug: "wholesale-trade-agents-and-brokers"
  },
  "441110": {
    description: "This industry comprises establishments primarily engaged in retailing new automobiles and light trucks, such as sport utility vehicles, and passenger and cargo vans, or retailing these new vehicles in combination with activities, such as repair services, retailing used cars, and selling replacement parts and accessories. Illustrative Examples: Automobile dealers, new only, or new and used, Light utility truck dealers, new only, or new and used.",
    industries: ["retail-motor-vehicles"],
    name: "New Car Dealers",
    slug: "new-car-dealers"
  },
  "441120": {
    description: "This industry comprises establishments primarily engaged in retailing used automobiles and light trucks, such as sport utility vehicles, and passenger and cargo vans. Illustrative Examples: Antique auto dealers, Light truck dealers, used only, Automobile dealers, used only.",
    industries: ["retail-motor-vehicles"],
    name: "Used Car Dealers",
    slug: "used-car-dealers"
  },
  "441210": {
    description: "This industry comprises establishments primarily engaged in retailing new and/or used recreational vehicles commonly referred to as RVs or retailing these new vehicles in combination with activities, such as repair services and selling replacement parts and accessories. Illustrative Examples: Motor home dealers, Recreational vehicle (RV) parts and accessories stores, Recreational vehicle (RV) dealers, Travel trailer dealers .",
    industries: ["retail"],
    name: "Recreational Vehicle Dealers",
    slug: "recreational-vehicle-dealers"
  },
  "441222": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) retailing new and/or used boats or retailing new boats in combination with activities, such as repair services and selling replacement parts and accessories, and/or (2) retailing new and/or used outboard motors, boat trailers, marine supplies, parts, and accessories. Illustrative Examples: Boat dealers (e.g., power boats, rowboats, sailboats), Outboard motor dealers, Marine supply dealers.",
    industries: ["retail"],
    name: "Boat Dealers",
    slug: "boat-dealers"
  },
  "441227": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing new and/or used motorcycles, motor scooters, motorbikes, mopeds, off-road all-terrain vehicles (ATV), personal watercraft, utility trailers, and other motor vehicles (except automobiles, light trucks, recreational vehicles, and boats) or retailing these new vehicles in combination with activities, such as repair services and selling replacement parts and accessories. Illustrative Examples: All-terrain vehicle (ATV) dealers, Motorcycle dealers, Moped dealers, Motorcycle parts and accessories dealers, Personal watercraft dealers, Aircraft dealers, Snowmobile dealers, Powered golf cart dealers, Utility trailer dealers.",
    industries: ["retail"],
    name: "Motorcycle, ATV, and All Other Motor Vehicle Dealers",
    slug: "motorcycle-atv-and-all-other-motor-vehicle-dealers"
  },
  "441330": {
    description: "This industry comprises establishments primarily engaged in retailing new, used, and/or rebuilt automotive parts and accessories, with or without repairing automobiles; and/or establishments primarily engaged in retailing and installing automotive accessories. Illustrative Examples: Automotive parts and supply retailers, Truck cap retailers, Automotive stereo retailers, Used automotive parts retailers.",
    industries: ["retail"],
    name: "Automotive Parts and Accessories Retailers",
    slug: "automotive-parts-and-accessories-retailers"
  },
  "441340": {
    description: "This industry comprises establishments primarily engaged in retailing new and/or used tires and tubes or retailing new tires in combination with automotive repair services.",
    industries: ["retail"],
    name: "Tire Dealers",
    slug: "tire-dealers"
  },
  "444110": {
    description: "This industry comprises establishments generally known as home centers primarily engaged in retailing a general line of new home repair and improvement materials and supplies, such as lumber, plumbing goods, electrical goods, tools, housewares, hardware, and lawn and garden supplies, with no one merchandise line predominating. Cross-References.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Home Centers",
    slug: "home-centers"
  },
  "444120": {
    description: "This industry comprises establishments primarily engaged in retailing paint, wallpaper, and related supplies. Cross-References.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Paint and Wallpaper Retailers",
    slug: "paint-and-wallpaper-retailers"
  },
  "444140": {
    description: "This industry comprises establishments primarily engaged in retailing a general line of new hardware items, such as tools and builders' hardware.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Hardware Retailers",
    slug: "hardware-retailers"
  },
  "444180": {
    description: "This industry comprises establishments (except home centers, paint and wallpaper retailers, and hardware retailers) primarily engaged in retailing specialized lines of new building materials, such as lumber, fencing, glass, doors, plumbing fixtures and supplies, electrical supplies, prefabricated buildings and kits, and kitchen and bath cabinets and countertops to be installed. Illustrative Examples: Electrical supply retailers, Kitchen cabinet (except custom) retailers, Fencing dealers, Lumber yards, retail, Floor covering retailers, hardwood or ceramic tile only, Plumbing supply retailers, Garage door dealers, Prefabricated building (except manufactured home) dealers, Glass (except automotive) retailers.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Other Building Material Dealers",
    slug: "other-building-material-dealers"
  },
  "444230": {
    description: "This industry comprises establishments primarily engaged in retailing new outdoor power equipment or retailing new outdoor power equipment in combination with activities, such as repair services and selling replacement parts.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Outdoor Power Equipment Retailers",
    slug: "outdoor-power-equipment-retailers"
  },
  "444240": {
    description: "This industry comprises establishments primarily engaged in retailing nursery and garden products, such as trees, shrubs, plants, seeds, bulbs, and sod, that are predominantly grown elsewhere.  These establishments may sell a limited amount of a product they grow themselves.  Also included in this industry are establishments primarily engaged in retailing farm supplies, such as animal (except pet) feed, fertilizers, agricultural chemicals, and pesticides.",
    industries: ["retail-building-materials-and-garden-equipment"],
    name: "Nursery, Garden Center, and Farm Supply Retailers",
    slug: "nursery-garden-center-and-farm-supply-retailers"
  },
  "445110": {
    description: "This industry comprises establishments generally known as supermarkets and other grocery retailers (except convenience retailers) primarily engaged in retailing a general line of food, such as canned and frozen foods; fresh fruits and vegetables; and fresh and prepared meats, fish, and poultry.  Included in this industry are delicatessen-type establishments primarily engaged in retailing a general line of food.",
    industries: ["retail-groceries"],
    name: "Supermarkets and Other Grocery Retailers (except Convenience Retailers)",
    slug: "supermarkets-and-other-grocery-retailers-except-convenience-retailers"
  },
  "445131": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing a limited line of groceries that generally includes milk, bread, soda, and snacks, such as convenience stores or food marts (except those operating fuel pumps).",
    industries: ["food-and-beverage-retail"],
    name: "Convenience Retailers",
    slug: "convenience-retailers"
  },
  "445132": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing merchandise through vending machines that they service.",
    industries: ["retail"],
    name: "Vending Machine Operators",
    slug: "vending-machine-operators"
  },
  "445230": {
    description: "This industry comprises establishments primarily engaged in retailing fresh fruits and vegetables.",
    industries: ["food-and-beverage-retail"],
    name: "Fruit and Vegetable Retailers",
    slug: "fruit-and-vegetable-retailers"
  },
  "445240": {
    description: "This industry comprises establishments primarily engaged in retailing fresh, frozen, or cured meats and poultry not for immediate consumption.  Delicatessen-type establishments primarily engaged in retailing fresh meat are included in this industry. Illustrative Examples: Baked ham retailers, Meat retailers, Butcher shops, Poultry dealers, Frozen meat retailers.",
    industries: ["food-and-beverage-retail"],
    name: "Meat Retailers",
    slug: "meat-retailers"
  },
  "445250": {
    description: "This industry comprises establishments primarily engaged in retailing fresh, frozen, or cured fish and seafood products not for immediate consumption. Cross-References.",
    industries: ["food-and-beverage-retail"],
    name: "Fish and Seafood Retailers",
    slug: "fish-and-seafood-retailers"
  },
  "445291": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing baked goods not for immediate consumption and not made on the premises.",
    industries: ["food-and-beverage-retail"],
    name: "Baked Goods Retailers",
    slug: "baked-goods-retailers"
  },
  "445292": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing candy and other confections, nuts, and popcorn not for immediate consumption and not made on the premises.",
    industries: ["food-and-beverage-retail"],
    name: "Confectionery and Nut Retailers",
    slug: "confectionery-and-nut-retailers"
  },
  "445298": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing miscellaneous specialty foods (except fruit and vegetables, meat, fish, seafood, confections, nuts, popcorn, and baked goods) not for immediate consumption and not made on the premises. Illustrative Examples: Coffee and tea (i.e., packaged) retailers, Soft drink (i.e., bottled) retailers, Dairy product retailers, Spice retailers, Gourmet food retailers, Water (i.e., bottled) retailers.",
    industries: ["food-and-beverage-retail"],
    name: "All Other Specialty Food Retailers",
    slug: "all-other-specialty-food-retailers"
  },
  "445320": {
    description: "This industry comprises establishments primarily engaged in retailing packaged alcoholic beverages, such as ale, beer, wine, and liquor.",
    industries: ["food-and-beverage-retail"],
    name: "Beer, Wine, and Liquor Retailers",
    slug: "beer-wine-and-liquor-retailers"
  },
  "449110": {
    description: "This industry comprises establishments primarily engaged in retailing new furniture, such as household furniture (e.g., baby furniture, box springs, and mattresses) and outdoor furniture; office furniture (except sold in combination with office supplies and equipment); and/or furniture sold in combination with major appliances, home electronics, home furnishings, or floor coverings.",
    industries: ["retail-furniture-and-home-furnishings"],
    name: "Furniture Retailers",
    slug: "furniture-retailers"
  },
  "449121": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing new floor coverings, such as rugs and carpets, laminate and vinyl floor coverings, linoleum flooring, and floor tile (except ceramic tile or hardwood floor coverings only); or retailing new floor coverings in combination with installation and repair services.",
    industries: ["retail-furniture-and-home-furnishings"],
    name: "Floor Covering Retailers",
    slug: "floor-covering-retailers"
  },
  "449122": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing new window treatments, such as curtains, drapes, blinds, and shades.",
    industries: ["retail-furniture-and-home-furnishings"],
    name: "Window Treatment Retailers",
    slug: "window-treatment-retailers"
  },
  "449129": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing new home furnishings (except furniture, floor coverings, and window treatments). Illustrative Examples: Bath shops, Kitchenware retailers, Chinaware retailers, Linen retailers, Electric lamp retailers, Picture frame retailers, custom, Glassware retailers, Wood-burning stove retailers, Housewares retailers.",
    industries: ["retail-furniture-and-home-furnishings"],
    name: "All Other Home Furnishings Retailers",
    slug: "all-other-home-furnishings-retailers"
  },
  "449210": {
    description: "This industry comprises establishments primarily engaged in one of the following: (1) retailing an array of new household-type appliances and consumer-type electronic products, such as televisions, computers, electronic tablets, and cameras; (2) specializing in retailing a single line of new consumer-type electronic products; (3) retailing these new products in combination with repair and support services; (4) retailing new prepackaged or downloadable computer software (without publishing); and/or (5) retailing prerecorded audio and video media, such as downloadable digital music and video files (without production or publishing), CDs, and DVDs. Illustrative Examples: Appliance retailers, household-type, Consumer-type electronics retailers (e.g., televisions, computers, cameras), Cellular telephone accessories retailers, Stereo and sound system retailers (except automotive) .",
    industries: ["retail-appliances-electrical-and-electronic-equipment"],
    name: "Electronics and Appliance Retailers",
    slug: "electronics-and-appliance-retailers"
  },
  "455110": {
    description: "This industry comprises establishments generally known as department stores that have separate departments for general lines of new merchandise, such as apparel, jewelry, home furnishings, and toys, with no one merchandise line predominating.  Department stores may sell perishable groceries, such as fresh fruits, vegetables, and dairy products, but such sales are insignificant.  Department stores with fixed point-of-sale locations may have separate customer checkout areas in each department, central customer checkout areas, or both.",
    industries: ["retail"],
    name: "Department Stores",
    slug: "department-stores"
  },
  "455211": {
    description: "This U.S. industry comprises establishments generally known as warehouse clubs, superstores, or supercenters, primarily engaged in retailing a general line of groceries, including a significant amount and variety of fresh fruits, vegetables, dairy products, meats, and other perishable groceries, in combination with a general line of new merchandise, such as apparel, furniture, and appliances.",
    industries: ["retail"],
    name: "Warehouse Clubs and Supercenters",
    slug: "warehouse-clubs-and-supercenters"
  },
  "455219": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing new and used general merchandise (except department stores, warehouse clubs, superstores, and supercenters).  These establishments retail a general line of new and used merchandise, such as apparel, automotive parts, dry goods, groceries, hardware, housewares or home furnishings, and other lines in limited amounts, with none of the lines predominating.  This industry also includes establishments primarily engaged in retailing a general line of new and used merchandise on an auction basis. Illustrative Examples: Dollar stores, General merchandise (new and used) auction houses, General merchandise showrooms, General merchandise trading posts, General stores, Home and auto supply stores, Variety stores.",
    industries: ["retail"],
    name: "All Other General Merchandise Retailers",
    slug: "all-other-general-merchandise-retailers"
  },
  "456110": {
    description: "This industry comprises establishments generally known as pharmacies and drug retailers engaged in retailing prescription or nonprescription drugs and medicines.",
    industries: ["retail-pharmacies"],
    name: "Pharmacies and Drug Retailers",
    slug: "pharmacies-and-drug-retailers"
  },
  "456120": {
    description: "This industry comprises establishments primarily engaged in retailing cosmetics, perfumes, toiletries, and personal grooming products. Cross-References.",
    industries: ["retail-health-and-personal-care-products"],
    name: "Cosmetics, Beauty Supplies, and Perfume Retailers",
    slug: "cosmetics-beauty-supplies-and-perfume-retailers"
  },
  "456130": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) retailing and fitting prescription eyeglasses and contact lenses; (2) retailing prescription eyeglasses in combination with the grinding of lenses to order on the premises; and (3) retailing nonprescription eyeglasses.",
    industries: ["retail-health-and-personal-care-products"],
    name: "Optical Goods Retailers",
    slug: "optical-goods-retailers"
  },
  "456191": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing food supplement products, such as vitamins, nutrition supplements, and body enhancing supplements.",
    industries: ["retail-health-and-personal-care-products"],
    name: "Food (Health) Supplement Retailers",
    slug: "food-health-supplement-retailers"
  },
  "456199": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing specialized lines of health and personal care merchandise (except drugs, medicines, cosmetics, beauty supplies, perfumes, optical goods, and food supplement products). Illustrative Examples: Convalescent supply retailers, Sick room supply retailers, Hearing aid retailers, Wheelchair retailers .",
    industries: ["retail-health-and-personal-care-products"],
    name: "All Other Health and Personal Care Retailers",
    slug: "all-other-health-and-personal-care-retailers"
  },
  "457110": {
    description: "This industry comprises establishments primarily engaged in retailing automotive fuels (e.g., gasoline, diesel fuel, gasohol, alternative fuels) in combination with a limited line of groceries.  These establishments can either be in a convenience store (i.e., food mart) setting or a gasoline station setting.  These establishments may also provide automotive repair services.",
    industries: ["retail-gasoline"],
    name: "Gasoline Stations with Convenience Stores",
    slug: "gasoline-stations-with-convenience-stores"
  },
  "457120": {
    description: "This industry comprises establishments generally known as gasoline stations (except those with convenience stores) or truck stops primarily engaged in (1) retailing automotive fuels (e.g., gasoline, diesel fuel, gasohol, alternative fuels) or (2) retailing these fuels in combination with activities, such as providing repair services; selling automotive oils, replacement parts, and accessories; and/or providing food services. Illustrative Examples: Gasoline stations without convenience stores, Truck stops, Marine service stations.",
    industries: ["retail-gasoline"],
    name: "Other Gasoline Stations",
    slug: "other-gasoline-stations"
  },
  "457210": {
    description: "This industry comprises establishments primarily engaged in retailing heating oil, liquefied petroleum (LP) gas, and other fuels via direct selling (i.e., home delivery).",
    industries: ["retail"],
    name: "Fuel Dealers",
    slug: "fuel-dealers"
  },
  "458110": {
    description: "This industry comprises establishments primarily engaged in retailing general or specialized lines of new clothing and clothing accessories, such as hats and caps, costume jewelry, gloves, handbags, ties, wigs, toupees, and belts.  These establishments may provide basic alterations, such as hemming, taking in or letting out seams, or lengthening or shortening sleeves.",
    industries: ["retail-apparel-and-fashion"],
    name: "Clothing and Clothing Accessories Retailers",
    slug: "clothing-and-clothing-accessories-retailers"
  },
  "458210": {
    description: "This industry comprises establishments primarily engaged in retailing all types of new footwear (except hosiery and specialty sports footwear, such as golf shoes, bowling shoes, and cleated shoes).  Establishments primarily engaged in retailing new tennis shoes or sneakers are included in this industry.",
    industries: ["retail-apparel-and-fashion"],
    name: "Shoe Retailers",
    slug: "shoe-retailers"
  },
  "458310": {
    description: "This industry comprises establishments primarily engaged in retailing one or more of the following items: (1) new jewelry (except costume jewelry); (2) new sterling and plated silverware; and (3) new watches and clocks.  Also included are establishments retailing these new products in combination with lapidary work and/or repair services.",
    industries: ["retail-luxury-goods-and-jewelry"],
    name: "Jewelry Retailers",
    slug: "jewelry-retailers"
  },
  "458320": {
    description: "This industry comprises establishments primarily engaged in retailing new luggage, briefcases, and trunks, or retailing these new products in combination with a general line of leather items (except leather apparel), such as belts, gloves, and handbags.",
    industries: ["retail-luxury-goods-and-jewelry"],
    name: "Luggage and Leather Goods Retailers",
    slug: "luggage-and-leather-goods-retailers"
  },
  "459110": {
    description: "This industry comprises establishments primarily engaged in retailing new sporting goods, such as bicycles and bicycle parts; camping equipment; exercise and fitness equipment; athletic uniforms; specialty sports footwear; and other sporting goods, equipment, and accessories. Illustrative Examples: Athletic uniform supply retailers, Fishing supply retailers, Bicycle (except motorized) retailers, Golf pro shops, Bowling equipment and supply retailers, Tack shops, Diving equipment retailers, Sporting goods (e.g., scuba, skiing, ball sports) retailers, Exercise equipment retailers, Sporting gun and hunting equipment retailers, Camping and hiking equipment retailers.",
    industries: ["retail"],
    name: "Sporting Goods Retailers",
    slug: "sporting-goods-retailers"
  },
  "459120": {
    description: "This industry comprises establishments primarily engaged in retailing new toys, games, and hobby and craft supplies (except needlecraft).",
    industries: ["retail"],
    name: "Hobby, Toy, and Game Retailers",
    slug: "hobby-toy-and-game-retailers"
  },
  "459130": {
    description: "This industry comprises establishments primarily engaged in retailing new sewing supplies, fabrics, patterns, yarns, and other needlework accessories or retailing these products in combination with new sewing machines. Illustrative Examples: Fabric retailers, Sewing supply retailers, Needlecraft sewing supply retailers, Upholstery materials retailers, Quilting supply retailers.",
    industries: ["retail"],
    name: "Sewing, Needlework, and Piece Goods Retailers",
    slug: "sewing-needlework-and-piece-goods-retailers"
  },
  "459140": {
    description: "This industry comprises establishments primarily engaged in retailing new musical instruments, sheet music, and related supplies; or retailing these new products in combination with musical instrument repair, rental, or music instruction. Illustrative Examples: Musical instrument retailers, Sheet music retailers, Piano retailers.",
    industries: ["retail-musical-instruments"],
    name: "Musical Instrument and Supplies Retailers",
    slug: "musical-instrument-and-supplies-retailers"
  },
  "459210": {
    description: "This industry comprises establishments primarily engaged in retailing new books, newspapers, magazines, and other periodicals (without publishing).",
    industries: ["retail-books-and-printed-news"],
    name: "Book Retailers and News Dealers",
    slug: "book-retailers-and-news-dealers"
  },
  "459310": {
    description: "This industry comprises establishments generally known as florists primarily engaged in retailing cut flowers, floral arrangements, and potted plants grown elsewhere.  These establishments may prepare the arrangements they sell.",
    industries: ["retail-florists"],
    name: "Florists",
    slug: "florists"
  },
  "459410": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) retailing new office supplies, stationery, and school supplies; (2) retailing a combination of new office equipment, furniture, and supplies; and (3) retailing new office equipment, furniture, and supplies in combination with selling new computers.",
    industries: ["retail-office-equipment"],
    name: "Office Supplies and Stationery Retailers",
    slug: "office-supplies-and-stationery-retailers"
  },
  "459420": {
    description: "This industry comprises establishments primarily engaged in retailing new gifts, novelty merchandise, souvenirs, greeting cards, seasonal and holiday decorations, and curios. Illustrative Examples: Greeting card retailers, Seasonal and holiday decoration retailers, Novelty retailers, Souvenir retailers, Gift retailers, Fruit basket or fruit bouquet retailers .",
    industries: ["retail-office-supplies-and-gifts"],
    name: "Gift, Novelty, and Souvenir Retailers",
    slug: "gift-novelty-and-souvenir-retailers"
  },
  "459510": {
    description: "This industry comprises establishments primarily engaged in retailing used merchandise, antiques, and secondhand goods (except motor vehicles, such as automobiles, RVs, motorcycles, and boats; motor vehicle parts; tires; and mobile homes).  This industry includes establishments retailing used merchandise on an auction basis. Illustrative Examples: Antique retailers, Used household appliance retailers, Used book retailers, Used merchandise thrift shops, Used clothing retailers, Used sporting goods retailers.",
    industries: ["retail-recyclable-materials-and-used-merchandise"],
    name: "Used Merchandise Retailers",
    slug: "used-merchandise-retailers"
  },
  "459910": {
    description: "This industry comprises establishments primarily engaged in retailing pets, pet foods, and pet supplies.",
    industries: ["retail"],
    name: "Pet and Pet Supplies Retailers",
    slug: "pet-and-pet-supplies-retailers"
  },
  "459920": {
    description: "This industry comprises establishments primarily engaged in retailing original and limited edition art works created by others.  Included in this industry are establishments primarily engaged in displaying works of art for retail sale in art galleries.",
    industries: ["retail-art-dealers"],
    name: "Art Dealers",
    slug: "art-dealers"
  },
  "459930": {
    description: "This industry comprises establishments primarily engaged in retailing new and/or used manufactured homes (i.e., mobile homes), parts, and equipment.",
    industries: ["retail"],
    name: "Manufactured (Mobile) Home Dealers",
    slug: "manufactured-mobile-home-dealers"
  },
  "459991": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing cigarettes, electronic cigarettes, cigars, tobacco, pipes, and other smokers' supplies. Illustrative Examples: Cigar retailers, E-cigarette retailers, Smokers' supply retailers, Cigarette retailers, Tobacco retailers, Vape shops",
    industries: ["retail"],
    name: "Tobacco, Electronic Cigarette, and Other Smoking Supplies Retailers",
    slug: "tobacco-electronic-cigarette-and-other-smoking-supplies-retailers"
  },
  "459999": {
    description: "This U.S. industry comprises establishments primarily engaged in retailing miscellaneous specialized lines of merchandise (except motor vehicle and parts dealers; building material and garden equipment and supplies dealers; food and beverage retailers; furniture, home furnishings, electronics, and appliance retailers; general merchandise retailers; health and personal care retailers; gasoline stations and fuel dealers; clothing, clothing accessories, shoe, and jewelry retailers; sporting goods, hobby, and musical instrument retailers; book retailers and news dealers; florists; office supplies, stationery, and gift retailers; used merchandise retailers; pet and pet supplies retailers; art dealers; manufactured (mobile) home dealers; and tobacco, electronic cigarette, and other smoking supplies retailers). Illustrative Examples: Art supply retailers, Candle retailers, Home security equipment retailers, Cemetery memorial (e.g., headstones, markers, vaults) dealers, Hot tub retailers, Fireworks retailers, Collectors' items (e.g., autograph, coin, card, stamp) retailers (except used rare items), Swimming pool (above-ground) and supply retailers, Trophy (e.g., awards and plaques) retailers, Flower retailers, artificial or dried.",
    industries: ["retail"],
    name: "All Other Miscellaneous Retailers",
    slug: "all-other-miscellaneous-retailers"
  },
  "481111": {
    description: "This U.S. industry comprises establishments primarily engaged in providing air transportation of passengers or passengers and freight over regular routes and on regular schedules.  Establishments in this industry operate flights even if partially loaded.  Scheduled air passenger carriers including commuter and helicopter carriers (except scenic and sightseeing) are included in this industry.",
    industries: ["airlines-and-aviation"],
    name: "Scheduled Passenger Air Transportation",
    slug: "scheduled-passenger-air-transportation"
  },
  "481112": {
    description: "This U.S. industry comprises establishments primarily engaged in providing air transportation of cargo without transporting passengers over regular routes and on regular schedules.  Establishments in this industry operate flights even if partially loaded.  Establishments primarily engaged in providing scheduled air transportation of mail on a contract basis are included in this industry.",
    industries: ["airlines-and-aviation"],
    name: "Scheduled Freight Air Transportation",
    slug: "scheduled-freight-air-transportation"
  },
  "481211": {
    description: "This U.S. industry comprises establishments primarily engaged in providing air transportation of passengers or passengers and cargo with no regular routes and regular schedules.",
    industries: ["airlines-and-aviation"],
    name: "Nonscheduled Chartered Passenger Air Transportation",
    slug: "nonscheduled-chartered-passenger-air-transportation"
  },
  "481212": {
    description: "This U.S. industry comprises establishments primarily engaged in providing air transportation of cargo without transporting passengers with no regular routes and regular schedules.",
    industries: ["airlines-and-aviation"],
    name: "Nonscheduled Chartered Freight Air Transportation",
    slug: "nonscheduled-chartered-freight-air-transportation"
  },
  "481219": {
    description: "This U.S. industry comprises establishments primarily engaged in providing air transportation with no regular routes and regular schedules (except nonscheduled chartered passenger and/or cargo air transportation).  These establishments provide a variety of specialty air transportation or flying services based on individual customer needs using general purpose aircraft. Illustrative Examples: Aircraft charter services (i.e., general purpose aircraft used for a variety of specialty air and flying services), Aviation clubs providing a variety of air transportation activities to the general public.",
    industries: ["airlines-and-aviation"],
    name: "Other Nonscheduled Air Transportation",
    slug: "other-nonscheduled-air-transportation"
  },
  "482111": {
    description: "This U.S. industry comprises establishments known as line-haul railroads primarily engaged in operating railroads for the transport of passengers and/or cargo over a long distance within a rail network.  These establishments provide for the intercity movement of trains between the terminals and stations on main and branch lines of a line-haul rail network (except for local switching services).",
    industries: ["rail-transportation"],
    name: "Line-Haul Railroads",
    slug: "line-haul-railroads"
  },
  "482112": {
    description: "This U.S. industry comprises establishments known as short-line railroads primarily engaged in operating railroads for the transport of cargo over a short distance on local rail lines not part of a rail network.",
    industries: ["rail-transportation"],
    name: "Short Line Railroads",
    slug: "short-line-railroads"
  },
  "483111": {
    description: "This U.S. industry comprises establishments primarily engaged in providing deep sea transportation of cargo to or from foreign ports. Cross-References.",
    industries: ["maritime-transportation"],
    name: "Deep Sea Freight Transportation",
    slug: "deep-sea-freight-transportation"
  },
  "483112": {
    description: "This U.S. industry comprises establishments primarily engaged in providing deep sea transportation of passengers to or from foreign ports.",
    industries: ["maritime-transportation"],
    name: "Deep Sea Passenger Transportation",
    slug: "deep-sea-passenger-transportation"
  },
  "483113": {
    description: "This U.S. industry comprises establishments primarily engaged in providing water transportation of cargo in coastal waters, on the Great Lakes System, or deep seas between ports of the United States, Puerto Rico, and United States island possessions or protectorates.  Marine transportation establishments using the facilities of the St. Lawrence Seaway Authority Commission are considered to be using the Great Lakes Water Transportation System.  Establishments primarily engaged in providing coastal and/or Great Lakes barge transportation services are included in this industry.",
    industries: ["maritime-transportation"],
    name: "Coastal and Great Lakes Freight Transportation",
    slug: "coastal-and-great-lakes-freight-transportation"
  },
  "483114": {
    description: "This U.S. industry comprises establishments primarily engaged in providing water transportation of passengers in coastal waters, the Great Lakes System, or deep seas between ports of the United States, Puerto Rico, and United States island possessions and protectorates.  Marine transportation establishments using the facilities of the St. Lawrence Seaway Authority Commission are considered to be using the Great Lakes Water Transportation System.",
    industries: ["maritime-transportation"],
    name: "Coastal and Great Lakes Passenger Transportation",
    slug: "coastal-and-great-lakes-passenger-transportation"
  },
  "483211": {
    description: "This U.S. industry comprises establishments primarily engaged in providing inland water transportation of cargo on lakes, rivers, or intracoastal waterways (except on the Great Lakes System).",
    industries: ["maritime-transportation"],
    name: "Inland Water Freight Transportation",
    slug: "inland-water-freight-transportation"
  },
  "483212": {
    description: "This U.S. industry comprises establishments primarily engaged in providing inland water transportation of passengers on lakes, rivers, or intracoastal waterways (except on the Great Lakes System).",
    industries: ["maritime-transportation"],
    name: "Inland Water Passenger Transportation",
    slug: "inland-water-passenger-transportation"
  },
  "484110": {
    description: "This industry comprises establishments primarily engaged in providing local general freight trucking.  General freight trucking establishments handle a wide variety of commodities, generally palletized and transported in a container or van trailer.  Local general freight trucking establishments usually provide trucking within a metropolitan area which may cross state lines.  Generally the trips are same-day return.",
    industries: ["truck-transportation"],
    name: "General Freight Trucking, Local",
    slug: "general-freight-trucking-local"
  },
  "484121": {
    description: "This U.S. industry comprises establishments primarily engaged in providing long-distance general freight truckload (TL) trucking.  These long-distance general freight truckload carrier establishments provide full truck movement of freight from origin to destination.  The shipment of freight on a truck is characterized as a full single load not combined with other shipments.",
    industries: ["truck-transportation"],
    name: "General Freight Trucking, Long-Distance, Truckload",
    slug: "general-freight-trucking-long-distance-truckload"
  },
  "484122": {
    description: "This U.S. industry comprises establishments primarily engaged in providing long-distance, general freight, less than truckload (LTL) trucking.  LTL carriage is characterized as multiple shipments combined onto a single truck for multiple deliveries within a network.  These establishments are generally characterized by the following network activities: local pick-up, local sorting and terminal operations, line-haul, destination sorting and terminal operations, and local delivery.",
    industries: ["truck-transportation"],
    name: "General Freight Trucking, Long-Distance, Less Than Truckload",
    slug: "general-freight-trucking-long-distance-less-than-truckload"
  },
  "484210": {
    description: "This industry comprises establishments primarily engaged in providing local or long-distance trucking of used household, used institutional, or used commercial furniture and equipment.  Incidental packing and storage activities are often provided by these establishments.",
    industries: ["truck-transportation"],
    name: "Used Household and Office Goods Moving",
    slug: "used-household-and-office-goods-moving"
  },
  "484220": {
    description: "This industry comprises establishments primarily engaged in providing local, specialized trucking.  Local trucking establishments provide trucking within a metropolitan area that may cross state lines.  Generally the trips are same-day return. Illustrative Examples: Local agricultural products trucking, Local dump trucking (e.g., gravel, sand, top-soil), Local boat hauling, Local livestock trucking, Local bulk liquids trucking.",
    industries: ["truck-transportation"],
    name: "Specialized Freight (except Used Goods) Trucking, Local",
    slug: "specialized-freight-except-used-goods-trucking-local"
  },
  "484230": {
    description: "This industry comprises establishments primarily engaged in providing long-distance specialized trucking.  These establishments provide trucking between metropolitan areas that may cross North American country borders. Illustrative Examples: Long-distance automobile carrier trucking, Long-distance refrigerated product trucking, Long-distance bulk liquid trucking, Long-distance trucking of waste, Long-distance hazardous material trucking.",
    industries: ["truck-transportation"],
    name: "Specialized Freight (except Used Goods) Trucking, Long-Distance",
    slug: "specialized-freight-except-used-goods-trucking-long-distance"
  },
  "485111": {
    description: "This U.S. industry comprises establishments primarily engaged in operating local and suburban ground passenger transit systems using more than one mode of transport over regular routes and on regular schedules within a metropolitan area and its adjacent nonurban areas.",
    industries: ["urban-transit-services"],
    name: "Mixed Mode Transit Systems",
    slug: "mixed-mode-transit-systems"
  },
  "485112": {
    description: "This U.S. industry comprises establishments primarily engaged in operating local and suburban commuter rail systems over regular routes and on a regular schedule within a metropolitan area and its adjacent nonurban areas.  Commuter rail is usually characterized by reduced fares, multiple ride and commutation tickets, and mostly used by passengers during the morning and evening peak periods.",
    industries: ["urban-transit-services"],
    name: "Commuter Rail Systems",
    slug: "commuter-rail-systems"
  },
  "485113": {
    description: "This U.S. industry comprises establishments primarily engaged in operating local and suburban passenger transportation systems using buses or other motor vehicles over regular routes and on regular schedules within a metropolitan area and its adjacent nonurban areas.",
    industries: ["urban-transit-services"],
    name: "Bus and Other Motor Vehicle Transit Systems",
    slug: "bus-and-other-motor-vehicle-transit-systems"
  },
  "485119": {
    description: "This U.S. industry comprises establishments primarily engaged in operating local and suburban ground passenger transit systems (except mixed mode transit systems, commuter rail systems, and buses and other motor vehicles) over regular routes and on regular schedules within a metropolitan area and its adjacent nonurban areas. Illustrative Examples: Commuter cable car systems (i.e., stand-alone), Light rail systems (i.e., stand-alone), Commuter tramway systems (i.e., stand-alone), Monorail transit systems (i.e., stand-alone), Commuter trolley systems (i.e., stand-alone), Subway systems (i.e., stand-alone).",
    industries: ["urban-transit-services"],
    name: "Other Urban Transit Systems",
    slug: "other-urban-transit-systems"
  },
  "485210": {
    description: "This industry comprises establishments primarily engaged in providing bus passenger transportation over regular routes and on regular schedules, principally outside a single metropolitan area and its adjacent nonurban areas.",
    industries: ["interurban-and-rural-bus-services"],
    name: "Interurban and Rural Bus Transportation",
    slug: "interurban-and-rural-bus-transportation"
  },
  "485310": {
    description: "This industry comprises establishments primarily engaged in providing passenger transportation by automobile or van, not operated over regular routes and on regular schedules.  Establishments of taxicab owner/operators, taxicab fleet operators, taxicab organizations, ridesharing services (including arrangement services), and ride hailing services (including arrangement services) are included in this industry.",
    industries: ["taxi-and-limousine-services"],
    name: "Taxi and Ridesharing Services",
    slug: "taxi-and-ridesharing-services"
  },
  "485320": {
    description: "This industry comprises establishments primarily engaged in providing an array of specialty and luxury passenger transportation services via limousine or luxury sedan generally on a reserved basis.  These establishments do not operate over regular routes and on regular schedules.",
    industries: ["taxi-and-limousine-services"],
    name: "Limousine Service",
    slug: "limousine-service"
  },
  "485410": {
    description: "This industry comprises establishments primarily engaged in providing buses and other motor vehicles to transport pupils to and from school or employees to and from work.",
    industries: ["school-and-employee-bus-services"],
    name: "School and Employee Bus Transportation",
    slug: "school-and-employee-bus-transportation"
  },
  "485510": {
    description: "This industry comprises establishments primarily engaged in providing buses for charter.  These establishments provide bus services to meet customers' road transportation needs and generally do not operate over fixed routes and on regular schedules.",
    industries: ["ground-passenger-transportation"],
    name: "Charter Bus Industry",
    slug: "charter-bus-industry"
  },
  "485991": {
    description: "This U.S. industry comprises establishments primarily engaged in providing special needs transportation (except to and from school or work) for the disabled or elderly.  These establishments may use specially equipped vehicles to provide passenger transportation.",
    industries: ["shuttles-and-special-needs-transportation-services"],
    name: "Special Needs Transportation",
    slug: "special-needs-transportation"
  },
  "485999": {
    description: "This U.S. industry comprises establishments primarily engaged in providing ground passenger transportation (except urban transit systems; interurban and rural bus transportation, taxi and/or limousine services (except shuttle services), school and employee bus transportation, charter bus services, and special needs transportation).  Establishments primarily engaged in operating shuttle services and car pools or vanpools (except ridesharing and ridesharing arrangement services) are included in this industry.  Shuttle services establishments generally provide travel on regular routes and on regular schedules between hotels, airports, or other destination points.",
    industries: ["shuttles-and-special-needs-transportation-services"],
    name: "All Other Transit and Ground Passenger Transportation",
    slug: "all-other-transit-and-ground-passenger-transportation"
  },
  "486110": {
    description: "This industry comprises establishments primarily engaged in the pipeline transportation of crude oil.",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation of Crude Oil",
    slug: "pipeline-transportation-of-crude-oil"
  },
  "486210": {
    description: "This industry comprises establishments primarily engaged in the pipeline transportation of natural gas from processing plants to local distribution systems. This industry includes the storage of natural gas because the storage is usually done by the pipeline establishment and because a pipeline is inherently a network in which all the nodes are interdependent.",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation of Natural Gas",
    slug: "pipeline-transportation-of-natural-gas"
  },
  "486910": {
    description: "This industry comprises establishments primarily engaged in the pipeline transportation of refined petroleum products.",
    industries: ["pipeline-transportation"],
    name: "Pipeline Transportation of Refined Petroleum Products",
    slug: "pipeline-transportation-of-refined-petroleum-products"
  },
  "486990": {
    description: "This industry comprises establishments primarily engaged in the pipeline transportation of products (except crude oil, natural gas, and refined petroleum products).",
    industries: ["pipeline-transportation"],
    name: "All Other Pipeline Transportation",
    slug: "all-other-pipeline-transportation"
  },
  "487110": {
    description: "This industry comprises establishments primarily engaged in providing scenic and sightseeing transportation on land, such as sightseeing buses and trolleys, steam train excursions, and horse-drawn sightseeing rides.  The services provided are usually local and involve same-day return to place of origin.",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Land",
    slug: "scenic-and-sightseeing-transportation-land"
  },
  "487210": {
    description: "This industry comprises establishments primarily engaged in providing scenic and sightseeing transportation on water.  The services provided are usually local and involve same-day return to place of origin. Illustrative Examples: Airboat (i.e., swamp buggy) operation, Excursion boat operation, Charter fishing boat services, Harbor sightseeing tours, Dinner cruises.",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Water",
    slug: "scenic-and-sightseeing-transportation-water"
  },
  "487990": {
    description: "This industry comprises establishments primarily engaged in providing scenic and sightseeing transportation (except on land and water).  The services provided are usually local and involve same-day return to place of departure. Illustrative Examples: Aerial cable cars, scenic and sightseeing operation, Helicopter rides, scenic and sightseeing operation, Glider excursions, Aerial tramways, scenic and sightseeing operation, Hot air balloon rides, scenic and sightseeing operation.",
    industries: ["sightseeing-transportation"],
    name: "Scenic and Sightseeing Transportation, Other",
    slug: "scenic-and-sightseeing-transportation-other"
  },
  "488111": {
    description: "This U.S. industry comprises establishments primarily engaged in providing air traffic control (except military) services to regulate the flow of air traffic. Cross-References.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Air Traffic Control",
    slug: "air-traffic-control"
  },
  "488119": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) operating international, national, or civil airports, or public flying fields or (2) supporting airport operations, such as rental of hangar space, and providing baggage handling and/or cargo handling services.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Airport Operations",
    slug: "other-airport-operations"
  },
  "488190": {
    description: "This industry comprises establishments primarily engaged in providing specialized services for air transportation (except air traffic control and other airport operations). Illustrative Examples: Aircraft maintenance and repair services (except factory conversions, overhauls, rebuilding), Aircraft passenger screening security services, Aircraft testing services.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Support Activities for Air Transportation",
    slug: "other-support-activities-for-air-transportation"
  },
  "488210": {
    description: "This industry comprises establishments primarily engaged in providing specialized services for railroad transportation, including servicing, routine repairing (except factory conversion, overhaul, or rebuilding of rolling stock), and maintaining rail cars; loading and unloading rail cars; and operating independent terminals.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Support Activities for Rail Transportation",
    slug: "support-activities-for-rail-transportation"
  },
  "488310": {
    description: "This industry comprises establishments primarily engaged in operating ports, harbors (including docking and pier facilities), or canals.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Port and Harbor Operations",
    slug: "port-and-harbor-operations"
  },
  "488320": {
    description: "This industry comprises establishments primarily engaged in providing stevedoring and other marine cargo handling services (except warehousing).",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Marine Cargo Handling",
    slug: "marine-cargo-handling"
  },
  "488330": {
    description: "This industry comprises establishments primarily engaged in providing navigational services to shipping.  Marine salvage establishments are included in this industry. Illustrative Examples: Docking and undocking marine vessel services, Piloting services, water transportation, Marine vessel traffic reporting services, Tugboat services, harbor operation.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Navigational Services to Shipping",
    slug: "navigational-services-to-shipping"
  },
  "488390": {
    description: "This industry comprises establishments primarily engaged in providing services to water transportation (except port and harbor operations; marine cargo handling services; and navigational services to shipping). Illustrative Examples: Floating drydocks (i.e., routine repair and maintenance of ships), Ship scaling services, Marine cargo checkers and surveyors.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Support Activities for Water Transportation",
    slug: "other-support-activities-for-water-transportation"
  },
  "488410": {
    description: "This industry comprises establishments primarily engaged in towing light or heavy motor vehicles, both local and long-distance.  These establishments may provide incidental services, such as storage and emergency road repair services.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Motor Vehicle Towing",
    slug: "motor-vehicle-towing"
  },
  "488490": {
    description: "This industry comprises establishments primarily engaged in providing services (except motor vehicle towing) to road network users. Illustrative Examples: Bridge, tunnel, and highway operations, Pilot car services (i.e., wide load warning services), Truck or weighing station operations, Driving services, independent (e.g., automobile, truck delivery).",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Other Support Activities for Road Transportation",
    slug: "other-support-activities-for-road-transportation"
  },
  "488510": {
    description: "This industry comprises establishments primarily engaged in arranging transportation of freight between shippers and carriers.  These establishments are usually known as freight forwarders, marine shipping agents, or customs brokers and offer a combination of services spanning transportation modes but do not directly provide shipping services. Cross-References.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Freight Transportation Arrangement",
    slug: "freight-transportation-arrangement"
  },
  "488991": {
    description: "This U.S. industry comprises establishments primarily engaged in packing, crating, and otherwise preparing goods for transportation.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Packing and Crating",
    slug: "packing-and-crating"
  },
  "488999": {
    description: "This U.S. industry comprises establishments primarily engaged in providing support activities to transportation (except for air transportation; rail transportation; water transportation; road transportation; freight transportation arrangement; and packing and crating). Illustrative Examples: Arrangement of vanpools or car pools (except ridesharing arrangement services), Independent pipeline terminal facilities, Stockyards (i.e., not for fattening or selling livestock).",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "All Other Support Activities for Transportation",
    slug: "all-other-support-activities-for-transportation"
  },
  "491110": {
    description: "This industry comprises establishments primarily engaged in providing mail services under a universal service obligation.  Mail services include the carriage of letters, printed matter, or mailable packages, including acceptance, collection, processing, and delivery.  Due to the infrastructure requirements of providing mail service under a universal service obligation, postal service establishments often provide parcel and express delivery services in addition to the mail service.  Establishments primarily engaged in performing one or more parts of the basic mail service, such as sorting, routing and/or delivery (except bulk transportation of mail) are included in this industry.",
    industries: ["postal-services"],
    name: "Postal Service",
    slug: "postal-service"
  },
  "492110": {
    description: "This industry comprises establishments primarily engaged in providing air, surface, or combined mode courier and express delivery services of parcels, but not operating under a universal service obligation.  These parcels can include goods and documents, but the express delivery services are not part of the normal mail service.  These services are generally between metropolitan areas, urban centers, or international, but the establishments of this industry form a network that includes local pick-up and delivery to serve their customers' needs. Illustrative Examples: Air courier services, except establishments operating under a universal service obligation, Express delivery services, except establishments operating under a universal service obligation, Courier services (i.e., intercity network), except establishments operating under a universal service obligation.",
    industries: ["freight-and-package-transportation"],
    name: "Couriers and Express Delivery Services",
    slug: "couriers-and-express-delivery-services"
  },
  "492210": {
    description: "This industry comprises establishments primarily engaged in providing local messenger and delivery services of small items within a single metropolitan area or within an urban center.  These establishments generally provide point-to-point pick-up and delivery and do not operate as part of an intercity courier network. Illustrative Examples: Letters, documents, or small parcels local delivery services, Grocery delivery services (i.e., independent service from grocery store), Alcoholic beverages delivery services, Restaurant meals order and delivery services (i.e., independent order and delivery services).",
    industries: ["freight-and-package-transportation"],
    name: "Local Messengers and Local Delivery",
    slug: "local-messengers-and-local-delivery"
  },
  "493110": {
    description: "This industry comprises establishments primarily engaged in operating merchandise warehousing and storage facilities.  These establishments generally handle goods in containers, such as boxes, barrels, and/or drums, using equipment, such as forklifts, pallets, and racks.  They are not specialized in handling bulk products of any particular type, size, or quantity of goods or products.",
    industries: ["warehousing-and-storage"],
    name: "General Warehousing and Storage",
    slug: "general-warehousing-and-storage"
  },
  "493120": {
    description: "This industry comprises establishments primarily engaged in operating refrigerated warehousing and storage facilities.  Establishments primarily engaged in the storage of furs for the trade are included in this industry.  The services provided by these establishments include blast freezing, tempering, and modified atmosphere storage services. Cross-References.",
    industries: ["warehousing-and-storage"],
    name: "Refrigerated Warehousing and Storage",
    slug: "refrigerated-warehousing-and-storage"
  },
  "493130": {
    description: "This industry comprises establishments primarily engaged in operating bulk farm product warehousing and storage facilities (except refrigerated).  Grain elevators primarily engaged in storage are included in this industry.",
    industries: ["warehousing-and-storage"],
    name: "Farm Product Warehousing and Storage",
    slug: "farm-product-warehousing-and-storage"
  },
  "493190": {
    description: "This industry comprises establishments primarily engaged in operating warehousing and storage facilities (except general merchandise, refrigerated, and farm product warehousing and storage). Illustrative Examples: Bulk petroleum storage, Lumber storage terminals, Document storage and warehousing, Whiskey warehousing.",
    industries: ["warehousing-and-storage"],
    name: "Other Warehousing and Storage",
    slug: "other-warehousing-and-storage"
  },
  "512110": {
    description: "This industry comprises establishments primarily engaged in producing, or producing and distributing motion pictures, videos, television programs, or television commercials.",
    industries: ["media-production"],
    name: "Motion Picture and Video Production",
    slug: "motion-picture-and-video-production"
  },
  "512120": {
    description: "This industry comprises establishments primarily engaged in acquiring distribution rights and distributing film and video productions to motion picture theaters, television networks and stations, other media broadcast and streaming outlets, and exhibitors.",
    industries: ["movies-and-sound-recording"],
    name: "Motion Picture and Video Distribution",
    slug: "motion-picture-and-video-distribution"
  },
  "512131": {
    description: "This U.S. industry comprises establishments primarily engaged in operating motion picture theaters (except drive-ins) and/or exhibiting motion pictures or videos at film festivals, and so forth.",
    industries: ["movies-and-sound-recording"],
    name: "Motion Picture Theaters (except Drive-Ins)",
    slug: "motion-picture-theaters-except-drive-ins"
  },
  "512132": {
    description: "This U.S. industry comprises establishments primarily engaged in operating drive-in motion picture theaters.",
    industries: ["movies-and-sound-recording"],
    name: "Drive-In Motion Picture Theaters",
    slug: "drive-in-motion-picture-theaters"
  },
  "512191": {
    description: "This U.S. industry comprises establishments primarily engaged in providing specialized motion picture or video postproduction services, such as editing, film/tape transfers, subtitling, credits, closed captioning, and animation and special effects.",
    industries: ["animation-and-post-production"],
    name: "Teleproduction and Other Postproduction Services",
    slug: "teleproduction-and-other-postproduction-services"
  },
  "512199": {
    description: "This U.S. industry comprises establishments primarily engaged in providing motion picture and video services (except motion picture and video production, distribution, exhibition, and teleproduction and other postproduction services). Illustrative Examples: Motion picture film laboratories, Stock footage film libraries, Film preservation services.",
    industries: ["animation-and-post-production"],
    name: "Other Motion Picture and Video Industries",
    slug: "other-motion-picture-and-video-industries"
  },
  "512230": {
    description: "This industry comprises establishments primarily engaged in acquiring and registering copyrights for musical compositions in accordance with law and promoting and authorizing the use of these compositions in recordings, radio, television, motion pictures, live performances, print, or other media.  Establishments in this industry represent the interests of the songwriter or other owners of musical compositions to produce revenues from the use of such works, generally through licensing agreements.  These establishments may own the copyright or act as administrator of the music copyrights on behalf of copyright owners.  Publishers of music books and sheet music are included in this industry. Cross-References.",
    industries: ["sheet-music-publishing"],
    name: "Music Publishers",
    slug: "music-publishers"
  },
  "512240": {
    description: "This industry comprises establishments primarily engaged in providing the facilities and technical expertise for sound recording in a studio.  This industry includes establishments that provide audio production and postproduction services to produce master recordings.  These establishments may provide audio services for film, television, and video productions.",
    industries: ["sound-recording"],
    name: "Sound Recording Studios",
    slug: "sound-recording-studios"
  },
  "512250": {
    description: "This industry comprises establishments primarily engaged in record production and/or releasing, promoting, and distributing sound recordings to wholesalers, retailers, or directly to the public.  These establishments contract with artists, arrange and finance the production of original master recordings, and/or produce master recordings themselves, such as digital music and compact discs.  Establishments in this industry hold the copyright to the master recording, or obtain reproduction and distribution rights to master recordings produced by others, and derive most of their revenues from the sales, leasing, licensing, or distribution of master recordings.  ",
    industries: ["sound-recording"],
    name: "Record Production and Distribution",
    slug: "record-production-and-distribution"
  },
  "512290": {
    description: "This industry comprises establishments primarily engaged in providing sound recording services (except record production, distribution, music publishing, and sound recording in a studio).  Establishments in this industry provide services, such as the audio recording of meetings and conferences.",
    industries: ["sound-recording"],
    name: "Other Sound Recording Industries",
    slug: "other-sound-recording-industries"
  },
  "513110": {
    description: "This industry comprises establishments known as newspaper publishers.  Establishments in this industry carry out operations necessary for producing and distributing newspapers, including gathering news; writing news columns, feature stories, and editorials; and selling and preparing advertisements.  These establishments may publish newspapers in print or electronic form, including exclusively on the Internet. Cross-References.",
    industries: ["newspaper-publishing"],
    name: "Newspaper Publishers",
    slug: "newspaper-publishers"
  },
  "513120": {
    description: "This industry comprises establishments known either as magazine publishers or periodical publishers.  These establishments carry out the operations necessary for producing and distributing magazines and other periodicals, such as gathering, writing, and editing articles, and selling and preparing advertisements.  These establishments may publish magazines and other periodicals in print or electronic form, including exclusively on the Internet. Illustrative Examples: Comic book publishers, Magazine publishers, Scholarly journal publishers, Newsletter publishers, Trade journal publishers, Cross-References.",
    industries: ["periodical-publishing"],
    name: "Periodical Publishers",
    slug: "periodical-publishers"
  },
  "513130": {
    description: "This industry comprises establishments known as book publishers.  Establishments in this industry carry out design, editing, and marketing activities necessary for producing and distributing books.  These establishments may publish books in print, electronic, or audio form, including exclusively on the Internet. Illustrative Examples: Atlas publishers, Religious book publishers, Graphic novel publishers, School textbook publishers, Encyclopedia publishers, Technical manual publishers, Map publishers , Travel guide book publishers, Cross-References.",
    industries: ["book-publishing"],
    name: "Book Publishers",
    slug: "book-publishers"
  },
  "513140": {
    description: "This industry comprises establishments primarily engaged in publishing directories, mailing lists, and collections or compilations of fact.  The products are typically protected in their selection, arrangement and/or presentation.  Examples are lists of mailing addresses, telephone directories, directories of businesses, collections or compilations of proprietary drugs or legal case results, compilations of public records, etc.  These establishments may publish directories and mailing lists in print or electronic form, including exclusively on the Internet. Illustrative Examples: Business directory publishers, Mailing list publishers, Directory publishers, Telephone directory publishers.",
    industries: ["book-and-periodical-publishing"],
    name: "Directory and Mailing List Publishers",
    slug: "directory-and-mailing-list-publishers"
  },
  "513191": {
    description: "This U.S. industry comprises establishments primarily engaged in publishing greeting cards.  These establishments may publish works in print or electronic form, including exclusively on the Internet. Cross-References.",
    industries: ["book-and-periodical-publishing"],
    name: "Greeting Card Publishers",
    slug: "greeting-card-publishers"
  },
  "513199": {
    description: "This U.S. industry comprises establishments generally known as publishers (except newspaper, magazine, book, directory, mailing list, music, software, and greeting card publishers).  These establishments may publish works in print or electronic form, including exclusively on the Internet. Illustrative Examples: Art publishers, Calendar publishers, Cross-References.",
    industries: ["book-and-periodical-publishing"],
    name: "All Other Publishers",
    slug: "all-other-publishers"
  },
  "513210": {
    description: "This industry comprises establishments primarily engaged in software publishing.  Establishments in this industry carry out operations necessary for producing and distributing computer software, such as designing, providing documentation, assisting in installation, and providing support services to software purchasers.  These establishments may design, develop, and publish, or publish only.  These establishments may publish and distribute software through subscriptions and/or downloads. ",
    industries: ["software-development"],
    name: "Software Publishers",
    slug: "software-publishers"
  },
  "516110": {
    description: "This industry comprises establishments primarily engaged in broadcasting aural programs by radio to the public.  These establishments operate radio broadcasting studios and facilities for the programming and transmission of programs to the public.  Programming may originate in their own studio, from an affiliated network, or from external sources.",
    industries: ["radio-and-television-broadcasting"],
    name: "Radio Broadcasting Stations",
    slug: "radio-broadcasting-stations"
  },
  "516120": {
    description: "This industry comprises establishments primarily engaged in broadcasting images together with sound.  These establishments operate television broadcasting studios and facilities for the programming and transmission of programs to the public.  Programming may originate in their own studio, from an affiliated network, or from external sources.",
    industries: ["radio-and-television-broadcasting"],
    name: "Television Broadcasting Stations",
    slug: "television-broadcasting-stations"
  },
  "516210": {
    description: "This industry comprises establishments primarily providing media streaming distribution services, operating social network sites, operating media broadcasting and cable television networks, and supplying information, such as news reports, articles, pictures, and features, to the news media.  These establishments distribute textual, audio, and/or video content of general or specific interest. Illustrative Examples: Broadcasting networks, radio, Broadcasting networks, television, Cable broadcasting networks, Cable television networks, Internet broadcasting, Pay-per-view cable programming, Satellite radio networks, Satellite television networks, Social network sites, Internet, Stand-alone streaming services, Subscription Video on Demand (SVOD) services, Syndicates, news, Virtual Multichannel Video Programming Distributors (vMVPDs), Web broadcasting, Wiki sites, Internet .",
    industries: ["cable-and-satellite-programming"],
    name: "Media Streaming Distribution Services, Social Networks, and Other Media Networks and Content Providers",
    slug: "media-streaming-distribution-services-social-networks-and-other-media-networks-and-content-providers"
  },
  "517111": {
    description: "This U.S. industry comprises establishments primarily engaged in operating, maintaining, and/or providing access to transmission facilities and infrastructure that they own and/or lease for the transmission of voice, data, text, sound, and video using wired telecommunications networks.  Transmission facilities may be based on a single technology or a combination of technologies.  Establishments in this industry use the wired telecommunications network facilities that they operate to provide a variety of services, such as wired telephony services, including VoIP services; wired (cable) audio and video programming distribution; and wired broadband Internet services.  By exception, establishments providing satellite television distribution services using facilities and infrastructure that they operate are included in this industry. Illustrative Examples: Broadband Internet service providers, wired (e.g., cable, DSL), Cable television distribution services, Closed-circuit television (CCTV) services, Direct-to-home satellite system (DTH) services, Local telephone carriers, wired, Long-distance telephone carriers, wired, Multichannel multipoint distribution services (MMDS), Satellite television distribution systems, Telecommunications carriers, wired, VoIP service providers, using own operated wired telecommunications infrastructure .",
    industries: ["telecommunications-carriers"],
    name: "Wired Telecommunications Carriers",
    slug: "wired-telecommunications-carriers"
  },
  "517112": {
    description: "This U.S. industry comprises establishments primarily engaged in operating and maintaining switching and transmission facilities to provide communications via the airwaves.  Establishments in this industry have spectrum licenses and provide services using that spectrum, such as cellular phone services, paging services, wireless Internet access, and wireless video services. Illustrative Examples: Cellular telephone service carriers, Wireless Internet service providers, except satellite, Paging services, except satellite, Wireless telephone communications carriers, except satellite .",
    industries: ["wireless-services"],
    name: "Wireless Telecommunications Carriers (except Satellite)",
    slug: "wireless-telecommunications-carriers-except-satellite"
  },
  "517121": {
    description: "This U.S. industry comprises establishments engaged in purchasing access and network capacity from owners and operators of telecommunications networks and reselling wired and wireless telecommunications services to businesses and households (except satellite telecommunications and agents for wireless telecommunications services).  Establishments in this industry resell telecommunications; they do not operate transmission facilities and infrastructure. Illustrative Examples: Mobile virtual network operators (MVNOs) (except agents), Pre-paid calling cards, telecommunications resellers, Wired telecommunications resellers, Wireless telecommunications resellers (except satellite telecommunications and agents) .",
    industries: ["telecommunications"],
    name: "Telecommunications Resellers",
    slug: "telecommunications-resellers"
  },
  "517122": {
    description: "This U.S. industry comprises establishments primarily engaged in acting as agents for wireless telecommunications carriers and resellers, selling wireless plans on a commission basis. Illustrative Examples: Agents for mobile virtual network operators (MVNOs), Agents for wireless telecommunications carriers, Cellular telephone stores, selling cellular phone service plans on an agent basis, Mobile phone stores, selling mobile phone service plans on an agent basis, Wireless phone service plan sales agents, selling on behalf of wireless telecommunications carriers.",
    industries: ["telecommunications"],
    name: "Agents for Wireless Telecommunications Services",
    slug: "agents-for-wireless-telecommunications-services"
  },
  "517410": {
    description: "This industry comprises establishments primarily engaged in providing telecommunications services to other establishments in the telecommunications and broadcasting industries by forwarding and receiving communications signals via a system of satellites or reselling satellite telecommunications.",
    industries: ["satellite-telecommunications"],
    name: "Satellite Telecommunications",
    slug: "satellite-telecommunications"
  },
  "517810": {
    description: "This industry comprises establishments primarily engaged in providing specialized telecommunications services, such as satellite tracking, communications telemetry, and radar station operation.  This industry also includes establishments primarily engaged in providing satellite terminal stations and associated facilities connected with one or more terrestrial systems and capable of transmitting telecommunications to, and receiving telecommunications from, satellite systems.  Establishments providing Internet services or Voice over Internet protocol (VoIP) services via client-supplied telecommunications connections are also included in this industry.  Establishments in this industry do not operate as telecommunications carriers. Illustrative Examples: Dial-up Internet service providers, Internet service providers, using client-supplied telecommunications connections (e.g., dial-up ISPs), Satellite tracking stations, VoIP service providers, using client-supplied telecommunications connections.",
    industries: ["telecommunications"],
    name: "All Other Telecommunications",
    slug: "all-other-telecommunications"
  },
  "518210": {
    description: "This industry comprises establishments primarily engaged in providing computing infrastructure, data processing services, Web hosting services (except software publishing), and related services, including streaming support services (except streaming distribution services).  Data processing establishments provide complete processing and specialized reports from data supplied by clients or provide automated data processing and data entry services. Illustrative Examples: Application hosting, Cloud storage services, Computer data storage services, Computing platform infrastructure provision, Infrastructure as a service (IaaS), Optical scanning services, Platform as a service (PaaS), Video and audio technical streaming support services, Web hosting.",
    industries: ["data-infrastructure-and-analytics"],
    name: "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
    slug: "computing-infrastructure-providers-data-processing-web-hosting-and-related-services"
  },
  "519210": {
    description: "This industry comprises establishments primarily engaged in providing library or archive services.  These establishments are engaged in maintaining collections of documents (e.g., books, journals, newspapers, and music) and facilitating the use of such documents (recorded information regardless of its physical form and characteristics) as required to meet the informational, research, educational, or recreational needs of their user.  These establishments may also acquire, research, store, preserve, and generally make accessible to the public historical documents, photographs, maps, audio material, audiovisual material, and other archival material of historical interest.  All or portions of these collections may be accessible electronically.",
    industries: ["libraries"],
    name: "Libraries and Archives",
    slug: "libraries-and-archives"
  },
  "519290": {
    description: "This industry comprises establishments primarily engaged in operating Web sites that use a search engine to generate and maintain extensive databases of Internet addresses and content in an easily searchable format (and known as Web search portals) or providing other information services not elsewhere classified.  Establishments known as Web search portals often provide additional Internet services, such as email, connections to other Web sites, auctions, news, and other limited content. Illustrative Examples: News clipping services, Telephone-based recorded information services, Stock photo agencies, Web search portals .",
    industries: ["information-services"],
    name: "Web Search Portals and All Other Information Services",
    slug: "web-search-portals-and-all-other-information-services"
  },
  "521110": {
    description: "This industry comprises establishments primarily engaged in performing central banking functions, such as issuing currency, managing the Nation's money supply and international reserves, holding deposits that represent the reserves of other banks and other central banks, and acting as a fiscal agent for the central government. Cross-References.",
    industries: ["financial-services"],
    name: "Monetary Authorities-Central Bank",
    slug: "monetary-authorities-central-bank"
  },
  "522110": {
    description: "This industry comprises establishments primarily engaged in accepting demand and other deposits and making commercial, industrial, and consumer loans.  Commercial banks and branches of foreign banks are included in this industry. Cross-References.",
    industries: ["banking"],
    name: "Commercial Banking",
    slug: "commercial-banking"
  },
  "522130": {
    description: "This industry comprises establishments primarily engaged in accepting members' share deposits in cooperatives that are organized to offer consumer loans to their members.",
    industries: ["credit-intermediation"],
    name: "Credit Unions",
    slug: "credit-unions"
  },
  "522180": {
    description: "This industry comprises establishments primarily engaged in accepting deposits, making mortgage, real estate, and other consumer and commercial loans, and investing in high-grade securities (except commercial banking and credit unions).  Savings and loan associations, savings banks, private banks (i.e., unincorporated banks), and establishments known as industrial banks or Morris Plans and primarily engaged in accepting deposits are included in this industry.",
    industries: ["savings-institutions"],
    name: "Savings Institutions and Other Depository Credit Intermediation",
    slug: "savings-institutions-and-other-depository-credit-intermediation"
  },
  "522210": {
    description: "This industry comprises establishments primarily engaged in providing credit by issuing credit cards.  Credit card issuance provides the funds required to purchase goods and services in return for payment of the full balance or payments on an installment basis.  Credit card banks are included in this industry.",
    industries: ["credit-intermediation"],
    name: "Credit Card Issuing",
    slug: "credit-card-issuing"
  },
  "522220": {
    description: "This industry comprises establishments primarily engaged in sales financing or sales financing in combination with leasing.  Sales financing establishments are primarily engaged in lending money for the purpose of providing collateralized goods through a contractual installment sales agreement, either directly from or through arrangements with dealers. Cross-References.",
    industries: ["credit-intermediation"],
    name: "Sales Financing",
    slug: "sales-financing"
  },
  "522291": {
    description: "This U.S. industry comprises establishments primarily engaged in making unsecured cash loans to consumers. Illustrative Examples: Finance companies (i.e., unsecured cash loans), Personal credit institutions (i.e., unsecured cash loans), Loan companies (i.e., consumer, personal, student, small), Student loan companies.",
    industries: ["credit-intermediation"],
    name: "Consumer Lending",
    slug: "consumer-lending"
  },
  "522292": {
    description: "This U.S. industry comprises establishments primarily engaged in lending funds with real estate as collateral. Illustrative Examples: Home equity credit lending, Mortgage companies, Reverse mortgage lending, Mortgage banking (i.e., nondepository mortgage lending).",
    industries: ["credit-intermediation"],
    name: "Real Estate Credit",
    slug: "real-estate-credit"
  },
  "522299": {
    description: "This U.S. industry comprises (1) establishments primarily engaged in providing working capital funds to U.S. exporters, lending funds to foreign buyers of U.S. goods, and/or lending funds to domestic buyers of imported goods; (2) establishments primarily engaged in buying, pooling, and repackaging loans for sale to others on the secondary market; and (3) establishments primarily providing other nondepository credit (except credit card issuing, sales financing, consumer lending, and real estate credit).  Examples of types of lending in this industry are short-term inventory credit, agricultural lending (except real estate and sales financing), and consumer cash lending secured by personal property. Illustrative Examples: Commodity Credit Corporation, Factoring accounts receivable, Industrial banks (i.e., known as), nondepository, International trade financing, Morris Plans (i.e., known as), nondepository, Pawnshops, Secondary market financing, Cross-References.",
    industries: ["international-trade-and-development"],
    name: "International, Secondary Market, and All Other Nondepository Credit Intermediation",
    slug: "international-secondary-market-and-all-other-nondepository-credit-intermediation"
  },
  "522310": {
    description: "This industry comprises establishments primarily engaged in arranging loans by bringing borrowers and lenders together on a commission or fee basis.",
    industries: ["loan-brokers"],
    name: "Mortgage and Nonmortgage Loan Brokers",
    slug: "mortgage-and-nonmortgage-loan-brokers"
  },
  "522320": {
    description: "This industry comprises establishments primarily engaged in providing one or more of the following: (1) financial transaction processing (except central bank); (2) reserve and liquidity services (except central bank); and/or (3) check or other financial instrument clearinghouse services (except central bank). Illustrative Examples: Automated clearinghouses, bank or check (except central bank), Credit card processing services, Check clearing services (except central bank), Electronic funds transfer services, including peer-to-peer payment services , Cross-References.",
    industries: ["credit-intermediation"],
    name: "Financial Transactions Processing, Reserve, and Clearinghouse Activities",
    slug: "financial-transactions-processing-reserve-and-clearinghouse-activities"
  },
  "522390": {
    description: "This industry comprises establishments primarily engaged in facilitating credit intermediation (except mortgage and loan brokerage; and financial transactions processing, reserve, and clearinghouse activities). Illustrative Examples: Check cashing services, Money order issuance services, Loan servicing, Travelers' check issuance services, Money transmission services, Payday lending services.",
    industries: ["credit-intermediation"],
    name: "Other Activities Related to Credit Intermediation",
    slug: "other-activities-related-to-credit-intermediation"
  },
  "523150": {
    description: "This industry comprises establishments primarily engaged in underwriting, originating, and/or maintaining markets for issues of securities, or acting as agents (i.e., brokers) between buyers and sellers in buying or selling securities on a commission or transaction fee basis.  Investment bankers act as principals (i.e., investors who buy or sell on their own account) in firm commitment transactions or act as agents in best effort and standby commitments.  This industry also includes establishments acting as principals in buying or selling securities generally on a spread basis, such as securities dealers or stock option dealers. Illustrative Examples: Bond dealing (i.e., acting as a principal in dealing securities to investors), Mutual fund agencies (i.e., brokerages), Securities brokerages, Securities underwriting, Stock brokerages, Stock options dealing, Cross-References.",
    industries: ["investment-banking"],
    name: "Investment Banking and Securities Intermediation",
    slug: "investment-banking-and-securities-intermediation"
  },
  "523160": {
    description: "This industry comprises establishments primarily engaged in acting as principals (i.e., investors who buy or sell for their own account) in buying or selling spot or futures commodity contracts or options, such as precious metals, foreign currency, oil, or agricultural products, generally on a spread basis, or acting as agents (i.e., brokers) in buying or selling spot or futures commodity contracts or options on a commission or transaction fee basis. Illustrative Examples: Commodity contract trading companies, Commodity contracts brokerages, Commodity futures brokerages, Futures commodity contracts dealing (i.e., acting as a principal in dealing commodities to investors).",
    industries: ["capital-markets"],
    name: "Commodity Contracts Intermediation",
    slug: "commodity-contracts-intermediation"
  },
  "523210": {
    description: "This industry comprises establishments primarily engaged in furnishing physical or electronic marketplaces for the purpose of facilitating the buying and selling of stocks, stock options, bonds, or commodity contracts.",
    industries: ["securities-and-commodity-exchanges"],
    name: "Securities and Commodity Exchanges",
    slug: "securities-and-commodity-exchanges"
  },
  "523910": {
    description: "This industry comprises establishments primarily engaged in acting as principals (except investment bankers, securities dealers, and commodity contracts dealers) in buying or selling financial contracts generally on a spread basis.  Principals are investors that buy or sell for their own account. Illustrative Examples: Investment clubs, Tax liens dealing (i.e., acting as a principal in dealing tax liens to investors), Venture capital companies, Mineral royalties or leases dealing (i.e., acting as a principal in dealing royalties or leases to investors).",
    industries: ["venture-capital-and-private-equity-principals"],
    name: "Miscellaneous Intermediation",
    slug: "miscellaneous-intermediation"
  },
  "523940": {
    description: "This industry comprises establishments primarily engaged in managing the portfolio assets (i.e., funds) of others on a fee or commission basis and/or providing customized investment advice to clients on a fee basis.  Establishments providing portfolio management have the authority to make investment decisions, and they derive fees based on the size and/or overall performance of the portfolio.  Establishments providing investment advice provide financial planning advice and investment counseling to meet the goals and needs of specific clients, but do not have the authority to execute trades. Illustrative Examples: Financial planning services, customized, fees paid by client, Investment advisory services, customized, fees paid by client, Managing trusts, Portfolio fund managing, Cross-References.",
    industries: ["investment-advice"],
    name: "Portfolio Management and Investment Advice",
    slug: "portfolio-management-and-investment-advice"
  },
  "523991": {
    description: "This U.S. industry comprises establishments primarily engaged in providing trust, fiduciary, and custody services to others, as instructed, on a fee or contract basis, such as bank trust offices and escrow agencies (except real estate). Cross-References.",
    industries: ["investment-management"],
    name: "Trust, Fiduciary, and Custody Activities",
    slug: "trust-fiduciary-and-custody-activities"
  },
  "523999": {
    description: "This U.S. industry comprises establishments primarily engaged in acting as agents and/or brokers (except securities brokerages and commodity contracts brokerages) in buying or selling financial contracts and those providing financial investment services (except securities and commodity exchanges; portfolio management; investment advice; and trust, fiduciary, and custody services) on a fee or commission basis. Illustrative Examples: Exchange clearinghouses, commodities or securities, Stock quotation services, Gas lease brokers' offices.",
    industries: ["investment-management"],
    name: "Miscellaneous Financial Investment Activities",
    slug: "miscellaneous-financial-investment-activities"
  },
  "524113": {
    description: "This U.S. industry comprises establishments primarily engaged in initially underwriting (i.e., assuming the risk and assigning premiums) annuities and life insurance policies, disability income insurance policies, and accidental death and dismemberment insurance policies. Cross-References.",
    industries: ["insurance-carriers"],
    name: "Direct Life Insurance Carriers",
    slug: "direct-life-insurance-carriers"
  },
  "524114": {
    description: "This U.S. industry comprises establishments primarily engaged in initially underwriting (i.e., assuming the risk and assigning premiums) health and medical insurance policies.  Group hospitalization plans and HMO establishments that provide health and medical insurance policies without providing health care services are included in this industry. Cross-References.",
    industries: ["insurance-carriers"],
    name: "Direct Health and Medical Insurance Carriers",
    slug: "direct-health-and-medical-insurance-carriers"
  },
  "524126": {
    description: "This U.S. industry comprises establishments primarily engaged in initially underwriting (i.e., assuming the risk and assigning premiums) insurance policies that protect policyholders against losses that may occur as a result of property damage or liability. Illustrative Examples: Automobile insurance carriers, direct, Malpractice insurance carriers, direct, Fidelity insurance carriers, direct, Mortgage guaranty insurance carriers, direct, Homeowners' insurance carriers, direct, Surety insurance carriers, direct, Liability insurance carriers, direct, Cross-References.",
    industries: ["insurance-carriers"],
    name: "Direct Property and Casualty Insurance Carriers",
    slug: "direct-property-and-casualty-insurance-carriers"
  },
  "524127": {
    description: "This U.S. industry comprises establishments primarily engaged in initially underwriting (i.e., assuming the risk and assigning premiums) insurance policies to protect the owners of real estate or real estate creditors against loss sustained by reason of any title defect to real property.",
    industries: ["insurance-carriers"],
    name: "Direct Title Insurance Carriers",
    slug: "direct-title-insurance-carriers"
  },
  "524128": {
    description: "This U.S. industry comprises establishments primarily engaged in initially underwriting (e.g., assuming the risk, assigning premiums) insurance policies (except life, disability income, accidental death and dismemberment, health and medical, property and casualty, and title insurance policies). Illustrative Examples: Bank deposit insurance carriers, direct, Product warranty insurance carriers, direct, Deposit or share insurance carriers, direct, Warranty insurance carriers (e.g., appliance, automobile, homeowners', product), direct.",
    industries: ["insurance-carriers"],
    name: "Other Direct Insurance (except Life, Health, and Medical) Carriers",
    slug: "other-direct-insurance-except-life-health-and-medical-carriers"
  },
  "524130": {
    description: "This industry comprises establishments primarily engaged in assuming all or part of the risk associated with existing insurance policies originally underwritten by other insurance carriers.",
    industries: ["insurance-carriers"],
    name: "Reinsurance Carriers",
    slug: "reinsurance-carriers"
  },
  "524210": {
    description: "This industry comprises establishments primarily engaged in acting as agents (i.e., brokers) in selling annuities and insurance policies.",
    industries: ["insurance-agencies-and-brokerages"],
    name: "Insurance Agencies and Brokerages",
    slug: "insurance-agencies-and-brokerages"
  },
  "524291": {
    description: "This U.S. industry comprises establishments primarily engaged in investigating, appraising, and settling insurance claims.",
    industries: ["claims-adjusting-actuarial-services"],
    name: "Claims Adjusting",
    slug: "claims-adjusting"
  },
  "524292": {
    description: "This U.S. industry comprises establishments primarily engaged in providing pharmacy benefit management (PBM) services and other third party administration services of insurance and pension funds, such as claims processing and other administrative services to insurance carriers, employee benefit plans, and self-insurance funds.",
    industries: ["claims-adjusting-actuarial-services"],
    name: "Pharmacy Benefit Management and Other Third Party Administration of Insurance and Pension Funds",
    slug: "pharmacy-benefit-management-and-other-third-party-administration-of-insurance-and-pension-funds"
  },
  "524298": {
    description: "This U.S. industry comprises establishments primarily engaged in providing insurance services on a contract or fee basis (except insurance agencies and brokerages, claims adjusting, and third party administration).  Insurance advisory services, insurance actuarial services, and insurance ratemaking services are included in this industry.",
    industries: ["claims-adjusting-actuarial-services"],
    name: "All Other Insurance Related Activities",
    slug: "all-other-insurance-related-activities"
  },
  "525110": {
    description: "This industry comprises legal entities (i.e., funds, plans, and/or programs) organized to provide retirement income benefits exclusively for the sponsor's employees or members. Illustrative Examples: Employee benefit plans, Retirement plans, Pension funds and plans.",
    industries: ["pension-funds"],
    name: "Pension Funds",
    slug: "pension-funds"
  },
  "525120": {
    description: "This industry comprises legal entities (i.e., funds, plans, and/or programs) organized to provide medical, surgical, hospital, vacation, training, and other health- and welfare-related employee benefits exclusively for the sponsor's employees or members.",
    industries: ["insurance-and-employee-benefit-funds"],
    name: "Health and Welfare Funds",
    slug: "health-and-welfare-funds"
  },
  "525190": {
    description: "This industry comprises legal entities (i.e., funds (except pension, and health- and welfare-related employee benefit funds)) organized to provide insurance exclusively for the sponsor, firm, or its employees or members.  Self-insurance funds (except employee benefit funds) and workers' compensation insurance funds are included in this industry. Cross-References.",
    industries: ["insurance-and-employee-benefit-funds"],
    name: "Other Insurance Funds",
    slug: "other-insurance-funds"
  },
  "525910": {
    description: "This industry comprises legal entities (i.e., open-end investment funds) organized to pool assets that consist of securities or other financial instruments.  Shares in these pools are offered to the public in an initial offering with additional shares offered continuously and perpetually and redeemed at a specific price determined by the net asset value. Illustrative Examples: Investment funds, open-ended, Money market mutual funds, open-ended",
    industries: ["trusts-and-estates"],
    name: "Open-End Investment Funds",
    slug: "open-end-investment-funds"
  },
  "525920": {
    description: "This industry comprises legal entities, trusts, estates, or agency accounts, administered on behalf of the beneficiaries under the terms of a trust agreement, will, or agency agreement. Illustrative Examples: Bankruptcy estates, Private estates (i.e., administering on behalf of beneficiaries), Personal investment trusts, Testamentary trusts.",
    industries: ["trusts-and-estates"],
    name: "Trusts, Estates, and Agency Accounts",
    slug: "trusts-estates-and-agency-accounts"
  },
  "525990": {
    description: "This industry comprises legal entities (i.e., funds (except insurance and employee benefit funds; open-end investment funds; trusts, estates, and agency accounts)).  Included in this industry are mortgage real estate investment trusts (REITs). Illustrative Examples: Closed-end investment funds, Special purpose financial vehicles, Collateralized mortgage obligations (CMOs), Unit investment trust funds, Face-amount certificate funds, Mortgage real estate investment trusts (REITs), Real estate mortgage investment conduits (REMICs), Cross-References.",
    industries: ["trusts-and-estates"],
    name: "Other Financial Vehicles",
    slug: "other-financial-vehicles"
  },
  "531110": {
    description: "This industry comprises establishments primarily engaged in acting as lessors of buildings used as residences or dwellings, such as single-family homes, apartment buildings, and town homes.  Included in this industry are owner-lessors and establishments renting real estate and then acting as lessors in subleasing it to others.  The establishments in this industry may manage the property themselves or have another establishment manage it for them.",
    industries: ["leasing-residential-real-estate"],
    name: "Lessors of Residential Buildings and Dwellings",
    slug: "lessors-of-residential-buildings-and-dwellings"
  },
  "531120": {
    description: "This industry comprises establishments primarily engaged in acting as lessors of buildings (except miniwarehouses and self-storage units) that are not used as residences or dwellings.  Included in this industry are: (1) owner-lessors of nonresidential buildings; (2) establishments renting real estate and then acting as lessors in subleasing it to others; and (3) establishments providing full service office space, whether on a lease or service contract basis.  The establishments in this industry may manage the property themselves or have another establishment manage it for them.",
    industries: ["leasing-non-residential-real-estate"],
    name: "Lessors of Nonresidential Buildings (except Miniwarehouses)",
    slug: "lessors-of-nonresidential-buildings-except-miniwarehouses"
  },
  "531130": {
    description: "This industry comprises establishments primarily engaged in renting or leasing space for self-storage.  These establishments provide secure space (i.e., rooms, compartments, lockers, containers, or outdoor space) where clients can store and retrieve their goods.",
    industries: ["leasing-residential-real-estate"],
    name: "Lessors of Miniwarehouses and Self-Storage Units",
    slug: "lessors-of-miniwarehouses-and-self-storage-units"
  },
  "531190": {
    description: "This industry comprises establishments primarily engaged in acting as lessors of real estate (except buildings), such as manufactured home (i.e., mobile home) sites, vacant lots, and grazing land.",
    industries: ["leasing-residential-real-estate"],
    name: "Lessors of Other Real Estate Property",
    slug: "lessors-of-other-real-estate-property"
  },
  "531210": {
    description: "This industry comprises establishments primarily engaged in acting as agents and/or brokers in one or more of the following: (1) selling real estate for others; (2) buying real estate for others; and (3) renting real estate for others.",
    industries: ["real-estate-agents-and-brokers"],
    name: "Offices of Real Estate Agents and Brokers",
    slug: "offices-of-real-estate-agents-and-brokers"
  },
  "531311": {
    description: "This U.S. industry comprises establishments primarily engaged in managing residential real estate for others. Cross-References.",
    industries: ["leasing-residential-real-estate"],
    name: "Residential Property Managers",
    slug: "residential-property-managers"
  },
  "531312": {
    description: "This U.S. industry comprises establishments primarily engaged in managing nonresidential real estate for others. Cross-References.",
    industries: ["leasing-residential-real-estate"],
    name: "Nonresidential Property Managers",
    slug: "nonresidential-property-managers"
  },
  "531320": {
    description: "This industry comprises establishments primarily engaged in estimating the fair market value of real estate.",
    industries: ["leasing-residential-real-estate"],
    name: "Offices of Real Estate Appraisers",
    slug: "offices-of-real-estate-appraisers"
  },
  "531390": {
    description: "This industry comprises establishments primarily engaged in performing real estate related services (except lessors of real estate, offices of real estate agents and brokers, real estate property managers, and offices of real estate appraisers). Illustrative Examples: Real estate escrow agencies, Real estate listing services, Real estate fiduciaries' offices, Landman services.",
    industries: ["leasing-residential-real-estate"],
    name: "Other Activities Related to Real Estate",
    slug: "other-activities-related-to-real-estate"
  },
  "532111": {
    description: "This U.S. industry comprises establishments primarily engaged in renting passenger cars without drivers, generally for short periods of time. Illustrative Examples: Passenger car rental, Passenger truck (light duty) rental, Passenger van rental, Sport utility vehicle rental.",
    industries: ["equipment-rental-services"],
    name: "Passenger Car Rental",
    slug: "passenger-car-rental"
  },
  "532112": {
    description: "This U.S. industry comprises establishments primarily engaged in leasing passenger cars without drivers, generally for long periods of time. Illustrative Examples: Passenger car leasing, Passenger truck (light duty) leasing, Passenger van leasing, Sport utility vehicle leasing.",
    industries: ["equipment-rental-services"],
    name: "Passenger Car Leasing",
    slug: "passenger-car-leasing"
  },
  "532120": {
    description: "This industry comprises establishments primarily engaged in renting or leasing, without drivers, one or more of the following: trucks, truck tractors, buses, semi-trailers, utility trailers, or RVs (recreational vehicles).",
    industries: ["equipment-rental-services"],
    name: "Truck, Utility Trailer, and RV (Recreational Vehicle) Rental and Leasing",
    slug: "truck-utility-trailer-and-rv-recreational-vehicle-rental-and-leasing"
  },
  "532210": {
    description: "This industry comprises establishments primarily engaged in renting consumer electronics equipment and appliances, such as televisions, stereos, and refrigerators.  Included in this industry are appliance rental centers.",
    industries: ["consumer-goods-rental"],
    name: "Consumer Electronics and Appliances Rental",
    slug: "consumer-electronics-and-appliances-rental"
  },
  "532281": {
    description: "This U.S. industry comprises establishments primarily engaged in renting clothing, such as formal wear, costumes (e.g., theatrical), or other clothing (except laundered uniforms and work apparel). Cross-References.",
    industries: ["consumer-goods-rental"],
    name: "Formal Wear and Costume Rental",
    slug: "formal-wear-and-costume-rental"
  },
  "532282": {
    description: "This U.S. industry comprises establishments primarily engaged in renting prerecorded video tapes and discs for home electronic equipment, including renting through vending machines.",
    industries: ["consumer-goods-rental"],
    name: "Video Tape and Disc Rental",
    slug: "video-tape-and-disc-rental"
  },
  "532283": {
    description: "This U.S. industry comprises establishments primarily engaged in renting home-type health and invalid equipment, such as wheelchairs, hospital beds, oxygen tanks, walkers, and crutches.",
    industries: ["consumer-goods-rental"],
    name: "Home Health Equipment Rental",
    slug: "home-health-equipment-rental"
  },
  "532284": {
    description: "This U.S. industry comprises establishments primarily engaged in renting recreational goods, such as bicycles, canoes, motorcycles, skis, sailboats, beach chairs, and beach umbrellas.",
    industries: ["consumer-goods-rental"],
    name: "Recreational Goods Rental",
    slug: "recreational-goods-rental"
  },
  "532289": {
    description: "This U.S. industry comprises establishments primarily engaged in renting consumer goods and products (except consumer electronics and appliances; formal wear and costumes; prerecorded video tapes and discs for home electronic equipment; home health furniture and equipment; and recreational goods).  Included in this industry are furniture rental centers and party rental supply centers.",
    industries: ["consumer-goods-rental"],
    name: "All Other Consumer Goods Rental",
    slug: "all-other-consumer-goods-rental"
  },
  "532310": {
    description: "This industry comprises establishments primarily engaged in renting a range of consumer, commercial, and industrial equipment.  Establishments in this industry typically operate from conveniently located facilities where they maintain inventories of goods and equipment that they rent for short periods of time.  The type of equipment that establishments in this industry provide often includes, but is not limited to: audio visual equipment, contractors' and builders' tools and equipment, home repair tools, lawn and garden equipment, moving equipment and supplies, and party and banquet equipment and supplies.",
    industries: ["equipment-rental-services"],
    name: "General Rental Centers",
    slug: "general-rental-centers"
  },
  "532411": {
    description: "This U.S. industry comprises establishments primarily engaged in renting or leasing off-highway transportation equipment without operators, such as aircraft, railroad cars, steamships, or tugboats.",
    industries: ["commercial-and-industrial-equipment-rental"],
    name: "Commercial Air, Rail, and Water Transportation Equipment Rental and Leasing",
    slug: "commercial-air-rail-and-water-transportation-equipment-rental-and-leasing"
  },
  "532412": {
    description: "This U.S. industry comprises establishments primarily engaged in renting or leasing heavy equipment without operators that may be used for construction, mining, or forestry, such as bulldozers, earthmoving equipment, well drilling machinery and equipment, or cranes.",
    industries: ["commercial-and-industrial-equipment-rental"],
    name: "Construction, Mining, and Forestry Machinery and Equipment Rental and Leasing",
    slug: "construction-mining-and-forestry-machinery-and-equipment-rental-and-leasing"
  },
  "532420": {
    description: "This industry comprises establishments primarily engaged in renting or leasing office machinery and equipment, such as computers, office furniture, duplicating machines (i.e., copiers), or facsimile machines.",
    industries: ["commercial-and-industrial-equipment-rental"],
    name: "Office Machinery and Equipment Rental and Leasing",
    slug: "office-machinery-and-equipment-rental-and-leasing"
  },
  "532490": {
    description: "This industry comprises establishments primarily engaged in renting or leasing nonconsumer-type machinery and equipment (except heavy construction, transportation, mining, and forestry machinery and equipment without operators; and office machinery and equipment).  Establishments in this industry rent or lease products, such as manufacturing equipment; metalworking, telecommunications, motion picture, theatrical machinery and equipment, or service industry machinery; institutional (i.e., public building) furniture, such as furniture for schools, theaters, or buildings; or agricultural equipment without operators.",
    industries: ["commercial-and-industrial-equipment-rental"],
    name: "Other Commercial and Industrial Machinery and Equipment Rental and Leasing",
    slug: "other-commercial-and-industrial-machinery-and-equipment-rental-and-leasing"
  },
  "533110": {
    description: "This industry comprises establishments primarily engaged in assigning rights to assets, such as patents, trademarks, brand names, and/or franchise agreements, for which a royalty payment or licensing fee is paid to the asset holder. Cross-References.",
    industries: ["real-estate-and-equipment-rental-services"],
    name: "Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)",
    slug: "lessors-of-nonfinancial-intangible-assets-except-copyrighted-works"
  },
  "541110": {
    description: "This industry comprises offices of legal practitioners known as lawyers or attorneys (i.e., counselors-at-law) primarily engaged in the practice of law.  Establishments in this industry may provide expertise in a range or in specific areas of law, such as criminal law, corporate law, family and estate law, patent law, real estate law, or tax law. Cross-References.",
    industries: ["law-practice"],
    name: "Offices of Lawyers",
    slug: "offices-of-lawyers"
  },
  "541120": {
    description: "This industry comprises establishments (except offices of lawyers and attorneys) primarily engaged in drafting, approving, and executing legal documents, such as real estate transactions, wills, and contracts; and in receiving, indexing, and storing such documents. Cross-References.",
    industries: ["legal-services"],
    name: "Offices of Notaries",
    slug: "offices-of-notaries"
  },
  "541191": {
    description: "This U.S. industry comprises establishments (except offices of lawyers and attorneys) primarily engaged in one or more of the following activities: (1) researching public land records to gather information relating to real estate titles; (2) preparing documents necessary for the transfer of the title, financing, and settlement; (3) conducting final real estate settlements and closings; and (4) filing legal and other documents relating to the sale of real estate.  Real estate settlement offices, title abstract companies, and title search companies are included in this industry. Cross-References.",
    industries: ["legal-services"],
    name: "Title Abstract and Settlement Offices",
    slug: "title-abstract-and-settlement-offices"
  },
  "541199": {
    description: "This U.S. industry comprises establishments of legal practitioners (except offices of lawyers and attorneys, settlement offices, and title abstract offices).  These establishments are primarily engaged in providing specialized legal or paralegal services. Illustrative Examples: Notary public services, Patent agent services (i.e., patent filing and searching services), Paralegal services, Process serving services, Cross-References.",
    industries: ["legal-services"],
    name: "All Other Legal Services",
    slug: "all-other-legal-services"
  },
  "541211": {
    description: "This U.S. industry comprises establishments of accountants that are certified to audit the accounting records of public and private organizations and to attest to compliance with generally accepted accounting practices.  Offices of certified public accountants (CPAs) may provide one or more of the following accounting services: (1) auditing financial statements; (2) designing accounting systems; (3) preparing financial statements; (4) developing budgets; and (5) providing advice on matters related to accounting.  These establishments may also provide related services, such as bookkeeping, tax return preparation, and payroll processing.",
    industries: ["accounting"],
    name: "Offices of Certified Public Accountants",
    slug: "offices-of-certified-public-accountants"
  },
  "541213": {
    description: "This U.S. industry comprises establishments (except offices of CPAs) engaged in providing tax return preparation services without also providing accounting, bookkeeping, billing, or payroll processing services.  Basic knowledge of tax law and filing requirements is required. Cross-References.",
    industries: ["accounting"],
    name: "Tax Preparation Services",
    slug: "tax-preparation-services"
  },
  "541214": {
    description: "This U.S. industry comprises establishments (except offices of CPAs) engaged in the following without also providing accounting, bookkeeping, or billing services: (1) collecting information on hours worked, pay rates, deductions, and other payroll-related data from their clients and (2) using that information to generate paychecks, payroll reports, and tax filings.  These establishments may use data processing and tabulating techniques as part of providing their services. Cross-References.",
    industries: ["accounting"],
    name: "Payroll Services",
    slug: "payroll-services"
  },
  "541219": {
    description: "This U.S. industry comprises establishments (except offices of CPAs) engaged in providing accounting services (except tax return preparation services only or payroll services only).  These establishments may also provide tax return preparation or payroll services.  Accountant (except CPA) offices, bookkeeper offices, and billing offices are included in this industry. Cross-References.",
    industries: ["accounting"],
    name: "Other Accounting Services",
    slug: "other-accounting-services"
  },
  "541310": {
    description: "This industry comprises establishments primarily engaged in planning and designing residential, institutional, leisure, commercial, and industrial buildings and structures by applying knowledge of design, construction procedures, zoning regulations, building codes, and building materials.",
    name: "Architectural Services",
    slug: "architectural-services",
    industries: ["architecture-and-planning", "accessible-architecture-and-design"]
  },
  "541320": {
    description: "This industry comprises establishments primarily engaged in planning and designing the development of land areas for projects, such as parks and other recreational areas; airports; highways; hospitals; schools; land subdivisions; and commercial, industrial, and residential areas, by applying knowledge of land characteristics, location of buildings and structures, use of land areas, and design of landscape projects. Illustrative Examples: Garden planning services, Landscape architects' offices, Golf course or ski area design services, Landscape consulting services, Industrial land use planning services, Landscape design services, Cross-References.",
    industries: ["professional-services"],
    name: "Landscape Architectural Services",
    slug: "landscape-architectural-services"
  },
  "541330": {
    description: "This industry comprises establishments primarily engaged in applying physical laws and principles of engineering in the design, development, and utilization of machines, materials, instruments, structures, processes, and systems.  The assignments undertaken by these establishments may involve any of the following activities: provision of advice, preparation of feasibility studies, preparation of preliminary and final plans and designs, provision of technical services during the construction or installation phase, inspection and evaluation of engineering projects, and related services. Illustrative Examples: Civil engineering services, Environmental engineering services, Construction engineering services, Mechanical engineering services, Engineers' offices, Robotics automation engineering services.",
    industries: ["professional-services"],
    name: "Engineering Services",
    slug: "engineering-services"
  },
  "541340": {
    description: "This industry comprises establishments primarily engaged in drawing detailed layouts, plans, and illustrations of buildings, structures, systems, or components from engineering and architectural specifications.",
    industries: ["professional-services"],
    name: "Drafting Services",
    slug: "drafting-services"
  },
  "541350": {
    description: "This industry comprises establishments primarily engaged in providing building inspection services.  These establishments typically evaluate all aspects of the building structure and component systems and prepare a report on the physical condition of the property, generally for buyers or others involved in real estate transactions.  Building inspection bureaus and establishments providing home inspection services are included in this industry.",
    industries: ["professional-services"],
    name: "Building Inspection Services",
    slug: "building-inspection-services"
  },
  "541360": {
    description: "This industry comprises establishments primarily engaged in gathering, interpreting, and mapping geophysical data.  Establishments in this industry often specialize in locating and measuring the extent of subsurface resources, such as oil, gas, and minerals, but they may also conduct surveys for engineering purposes.  Establishments in this industry use a variety of surveying techniques depending on the purpose of the survey, including magnetic surveys, gravity surveys, seismic surveys, or electrical and electromagnetic surveys. Cross-References.",
    industries: ["surveying-and-mapping-services"],
    name: "Geophysical Surveying and Mapping Services",
    slug: "geophysical-surveying-and-mapping-services"
  },
  "541370": {
    description: "This industry comprises establishments primarily engaged in performing surveying and mapping services of the surface of the earth, including the sea floor.  These services may include surveying and mapping of areas above or below the surface of the earth, such as the creation of view easements or segregating rights in parcels of land by creating underground utility easements. Illustrative Examples: Cadastral surveying services, Mapping (except geophysical) services, Cartographic surveying services, Topographic surveying services, Geodetic surveying services.",
    industries: ["surveying-and-mapping-services"],
    name: "Surveying and Mapping (except Geophysical) Services",
    slug: "surveying-and-mapping-except-geophysical-services"
  },
  "541380": {
    description: "This industry comprises establishments primarily engaged in performing physical, chemical, and other analytical testing services, such as acoustics or vibration testing, assaying, biological testing (except medical and veterinary), calibration testing, electrical and electronic testing, geotechnical testing, mechanical testing, nondestructive testing, or thermal testing.  The testing may occur in a laboratory or on-site.",
    industries: ["professional-services"],
    name: "Testing Laboratories and Services",
    slug: "testing-laboratories-and-services"
  },
  "541410": {
    description: "This industry comprises establishments primarily engaged in planning, designing, and administering projects in interior spaces to meet the physical and aesthetic needs of people using them, taking into consideration building codes, health and safety regulations, traffic patterns and floor planning, mechanical and electrical needs, and interior fittings and furniture.  Interior designers and interior design consultants work in areas, such as hospitality design, health care design, institutional design, commercial and corporate design, and residential design.  This industry also includes interior decorating consultants engaged exclusively in providing aesthetic services associated with interior spaces.",
    industries: ["interior-design"],
    name: "Interior Design Services",
    slug: "interior-design-services"
  },
  "541420": {
    description: "This industry comprises establishments primarily engaged in creating and developing designs and specifications that optimize the use, value, and appearance of products.  These services can include the determination of the materials, construction, mechanisms, shape, color, and surface finishes of the product, taking into consideration human characteristics and needs, safety, market appeal, and efficiency in production, distribution, use, and maintenance.  Establishments providing automobile or furniture industrial design services or industrial design consulting services are included in this industry.",
    industries: ["design-services"],
    name: "Industrial Design Services",
    slug: "industrial-design-services"
  },
  "541430": {
    description: "This industry comprises establishments primarily engaged in planning, designing, and managing the production of visual communication in order to convey specific messages or concepts, clarify complex information, or project visual identities.  These services can include the design of printed materials, packaging, advertising, signage systems, and corporate identification (logos).  This industry also includes commercial artists engaged exclusively in generating drawings and illustrations requiring technical accuracy or interpretative skills. Illustrative Examples: Commercial art studios, Corporate identification (i.e., logo) design services, Independent commercial or graphic artists, Medical art or illustration services, Graphic design consulting services, Cross-References.",
    industries: ["graphic-design"],
    name: "Graphic Design Services",
    slug: "graphic-design-services"
  },
  "541490": {
    description: "This industry comprises establishments primarily engaged in providing professional design services (except architectural, landscape architecture, engineering, interior, industrial, graphic, and computer systems design). Illustrative Examples: Costume design services (except independent theatrical costume designers), Jewelry design services, Fashion design services, Float design services, Shoe design services, Lighting design services, Textile design services.",
    industries: ["design-services"],
    name: "Other Specialized Design Services",
    slug: "other-specialized-design-services"
  },
  "541511": {
    description: "This U.S. industry comprises establishments primarily engaged in writing, modifying, testing, and supporting software to meet the needs of a particular customer.",
    industries: ["it-system-custom-software-development"],
    name: "Custom Computer Programming Services",
    slug: "custom-computer-programming-services"
  },
  "541512": {
    description: "This U.S. industry comprises establishments primarily engaged in planning and designing computer systems that integrate computer hardware, software, and communication technologies.  The hardware and software components of the system may be provided by this establishment or company as part of integrated services or may be provided by third parties or vendors.  These establishments often install the system and train and support users of the system. Illustrative Examples: Computer systems integration design consulting services, Local area network (LAN) computer systems integration design services, Information management computer systems integration design services, Office automation computer systems integration design services.",
    industries: ["it-system-design-services"],
    name: "Computer Systems Design Services",
    slug: "computer-systems-design-services"
  },
  "541513": {
    description: "This U.S. industry comprises establishments primarily engaged in providing on-site management and operation of clients' computer systems and/or data processing facilities.  Establishments providing computer systems or data processing facilities support services are included in this industry. Cross-References.",
    industries: ["it-system-operations-and-maintenance"],
    name: "Computer Facilities Management Services",
    slug: "computer-facilities-management-services"
  },
  "541519": {
    description: "This U.S. industry comprises establishments primarily engaged in providing computer related services (except custom programming, systems integration design, and facilities management services). Establishments providing computer disaster recovery services or software installation services are included in this industry.",
    industries: ["it-services-and-it-consulting"],
    name: "Other Computer Related Services",
    slug: "other-computer-related-services"
  },
  "541611": {
    description: "This U.S. industry comprises establishments primarily engaged in providing operating advice and assistance to businesses and other organizations on administrative management issues, such as financial planning and budgeting, equity and asset management, records management, office planning, strategic and organizational planning, site selection, new business start-up, and business process improvement.  This industry also includes establishments of general management consultants that provide a full range of administrative, human resource, marketing, process, physical distribution, logistics, or other management consulting services to clients. Illustrative Examples: Administrative management consulting services, Financial management (except investment advice) consulting services, General management consulting services, Site selection consulting services, Strategic planning consulting services, Cross-References.",
    industries: ["strategic-management-services"],
    name: "Administrative Management and General Management Consulting Services",
    slug: "administrative-management-and-general-management-consulting-services"
  },
  "541612": {
    description: "This U.S. industry comprises establishments primarily engaged in providing advice and assistance to businesses and other organizations in one or more of the following areas: (1) human resource and personnel policies, practices, and procedures; (2) employee benefits planning, communication, and administration; (3) compensation systems planning; and (4) wage and salary administration. Illustrative Examples: Benefit or compensation consulting services, Employee assessment consulting services, Personnel management consulting services, Human resources consulting services.",
    industries: ["human-resources-services"],
    name: "Human Resources Consulting Services",
    slug: "human-resources-consulting-services"
  },
  "541613": {
    description: "This U.S. industry comprises establishments primarily engaged in providing operating advice and assistance to businesses and other organizations on marketing issues, such as developing marketing objectives and policies, sales forecasting, new product developing and pricing, licensing and franchise planning, and marketing planning and strategy. Illustrative Examples: Customer services management consulting services, New product development consulting services, Marketing management consulting services, Sales management consulting services.",
    industries: ["advertising-services"],
    name: "Marketing Consulting Services",
    slug: "marketing-consulting-services"
  },
  "541614": {
    description: "This U.S. industry comprises establishments primarily engaged in providing operating advice and assistance to businesses and other organizations in: (1) manufacturing operations improvement; (2) productivity improvement; (3) production planning and control; (4) quality assurance and quality control; (5) inventory management; (6) distribution networks; (7) warehouse use, operations, and utilization; (8) transportation and shipment of goods and materials; and (9) materials management and handling. Illustrative Examples: Freight rate or tariff rate consulting services, Productivity improvement consulting services, Manufacturing management consulting services, Inventory planning and control management consulting services, Transportation management consulting services.",
    industries: ["operations-consulting"],
    name: "Process, Physical Distribution, and Logistics Consulting Services",
    slug: "process-physical-distribution-and-logistics-consulting-services"
  },
  "541618": {
    description: "This U.S. industry comprises establishments primarily engaged in providing management consulting services (except administrative and general management consulting; human resources consulting; marketing consulting; or process, physical distribution, and logistics consulting).  Establishments providing telecommunications or utilities management consulting services are included in this industry.",
    industries: ["business-consulting-and-services"],
    name: "Other Management Consulting Services",
    slug: "other-management-consulting-services"
  },
  "541620": {
    description: "This industry comprises establishments primarily engaged in providing advice and assistance to businesses and other organizations on environmental issues, such as the control of environmental contamination from pollutants, toxic substances, and hazardous materials.  These establishments identify problems (e.g., inspect buildings for hazardous materials), measure and evaluate risks, and recommend solutions.  They employ a multidisciplined staff of scientists, engineers, and other technicians with expertise in areas, such as air and water quality, asbestos contamination, remediation, ecological restoration, and environmental law.  Establishments providing sanitation or site remediation consulting services are included in this industry. Cross-References.",
    industries: ["environmental-services"],
    name: "Environmental Consulting Services",
    slug: "environmental-consulting-services"
  },
  "541690": {
    description: "This industry comprises establishments primarily engaged in providing advice and assistance to businesses and other organizations on scientific and technical issues (except environmental). Illustrative Examples: Agricultural consulting services, Motion picture consulting services, Biological consulting services, Physics consulting services, Chemical consulting services, Radio consulting services, Economic consulting services, Safety consulting services, Energy consulting services, Security consulting services.",
    industries: ["business-consulting-and-services"],
    name: "Other Scientific and Technical Consulting Services",
    slug: "other-scientific-and-technical-consulting-services"
  },
  "541713": {
    description: "This U.S. industry comprises establishments primarily engaged in conducting nanotechnology research and experimental development.  Nanotechnology research and experimental development involves the study of matter at the nanoscale (i.e., a scale of about 1 to 100 nanometers).  This research and development in nanotechnology may result in development of new nanotechnology processes or in prototypes of new or altered materials and/or products that may be reproduced, utilized, or implemented by various industries.",
    industries: ["nanotechnology-research"],
    name: "Research and Development in Nanotechnology",
    slug: "research-and-development-in-nanotechnology"
  },
  "541714": {
    description: "This U.S. industry comprises establishments primarily engaged in conducting biotechnology (except nanobiotechnology) research and experimental development.  Biotechnology (except nanobiotechnology) research and experimental development involves the study of the use of microorganisms and cellular and biomolecular processes to develop or alter living or non-living materials.  This research and development in biotechnology (except nanobiotechnology) may result in development of new biotechnology (except nanobiotechnology) processes or in prototypes of new or genetically-altered products that may be reproduced, utilized, or implemented by various industries. Illustrative Examples: Cloning research and experimental development laboratories , DNA technologies (e.g., microarrays) research and experimental development laboratories, Nucleic acid chemistry research and experimental development laboratories, Protein engineering research and experimental development laboratories, Recombinant DNA research and experimental development laboratories .",
    industries: ["biotechnology-research"],
    name: "Research and Development in Biotechnology (except Nanobiotechnology)",
    slug: "research-and-development-in-biotechnology-except-nanobiotechnology"
  },
  "541715": {
    description: "This U.S. industry comprises establishments primarily engaged in conducting research and experimental development (except nanotechnology and biotechnology research and experimental development) in the physical, engineering, and life sciences, such as agriculture, electronics, environmental, biology, botany, computers, chemistry, food, fisheries, forests, geology, health, mathematics, medicine, oceanography, pharmacy, physics, veterinary, and other allied subjects.",
    industries: ["research-services"],
    name: "Research and Development in the Physical, Engineering, and Life Sciences (except Nanotechnology and Biotechnology)",
    slug: "research-and-development-in-the-physical-engineering-and-life-sciences-except-nanotechnology-and-biotechnology"
  },
  "541720": {
    description: "This industry comprises establishments primarily engaged in conducting research and analyses in cognitive development, sociology, psychology, language, behavior, economic, and other social science and humanities research.",
    industries: ["think-tanks"],
    name: "Research and Development in the Social Sciences and Humanities",
    slug: "research-and-development-in-the-social-sciences-and-humanities"
  },
  "541810": {
    description: "This industry comprises establishments primarily engaged in creating advertising campaigns and placing such advertising in print and digital periodicals, newspapers, radio and television, or other media.  These establishments are organized to provide a full range of services (i.e., through in-house capabilities or subcontracting), including advice, creative services, account management, production of advertising content, media planning, and buying (i.e., placing advertising).",
    industries: ["marketing-services"],
    name: "Advertising Agencies",
    slug: "advertising-agencies"
  },
  "541820": {
    description: "This industry comprises establishments primarily engaged in designing and implementing public relations campaigns.  These campaigns are designed to promote the interests and image of their clients.  Establishments providing lobbying, political consulting, or public relations consulting are included in this industry.",
    industries: ["public-relations-and-communications-services"],
    name: "Public Relations Agencies",
    slug: "public-relations-agencies"
  },
  "541830": {
    description: "This industry comprises establishments primarily engaged in purchasing advertising time or space from media outlets and reselling it to advertising agencies or individual companies directly.",
    industries: ["marketing-services"],
    name: "Media Buying Agencies",
    slug: "media-buying-agencies"
  },
  "541840": {
    description: "This industry comprises establishments of independent representatives primarily engaged in selling media time or space for media owners. Illustrative Examples: Newspaper advertising representatives (i.e., independent of media owners), Radio advertising representatives (i.e., independent of media owners), Publishers' advertising representatives (i.e., independent of media owners), Television advertising representatives (i.e., independent of media owners).",
    industries: ["marketing-services"],
    name: "Media Representatives",
    slug: "media-representatives"
  },
  "541850": {
    description: "This industry comprises establishments primarily engaged in creating and designing public display advertising campaign materials, such as printed, painted, or electronic displays; and/or placing such displays on indoor or outdoor billboards and panels, or on or within transit vehicles or facilities, shopping malls, retail (in-store) displays, and other display structures or sites.",
    industries: ["marketing-services"],
    name: "Indoor and Outdoor Display Advertising",
    slug: "indoor-and-outdoor-display-advertising"
  },
  "541860": {
    description: "This industry comprises establishments primarily engaged in (1) creating and designing advertising campaigns for the purpose of distributing advertising materials (e.g., coupons, flyers, samples) or specialties (e.g., keychains, magnets, pens with customized messages imprinted) by mail or other direct distribution and/or (2) preparing such advertising materials or specialties for mailing or other direct distribution.  These establishments may also compile, maintain, sell, and rent mailing lists.",
    industries: ["marketing-services"],
    name: "Direct Mail Advertising",
    slug: "direct-mail-advertising"
  },
  "541870": {
    description: "This industry comprises establishments primarily engaged in the direct distribution or delivery of advertisements (e.g., circulars, coupons, handbills) or samples.  Establishments in this industry use methods, such as delivering advertisements or samples door-to-door, placing flyers or coupons on car windshields in parking lots, or handing out samples in retail stores.",
    industries: ["marketing-services"],
    name: "Advertising Material Distribution Services",
    slug: "advertising-material-distribution-services"
  },
  "541890": {
    description: "This industry comprises establishments primarily engaged in providing advertising services (except advertising agency services, public relations agency services, media buying agency services, media representative services, display advertising services, direct mail advertising services, advertising material distribution services, and marketing consulting services). Illustrative Examples: Advertising specialties (e.g., keychains, magnets, pens) distribution services (except direct mail), Sign lettering and painting services, Display lettering services, Store window dressing or trimming services, Mannequin decorating services, Welcoming services (i.e., advertising services), Merchandise demonstration services.",
    industries: ["marketing-services"],
    name: "Other Services Related to Advertising",
    slug: "other-services-related-to-advertising"
  },
  "541910": {
    description: "This industry comprises establishments primarily engaged in systematically gathering, recording, tabulating, and presenting marketing and public opinion data. Illustrative Examples: Broadcast media rating services, Political opinion polling services, Marketing analysis or research services, Statistical sampling services, Opinion research services.",
    industries: ["market-research"],
    name: "Marketing Research and Public Opinion Polling",
    slug: "marketing-research-and-public-opinion-polling"
  },
  "541921": {
    description: "This U.S. industry comprises establishments known as portrait studios primarily engaged in providing still, video, or digital portrait photography services. Illustrative Examples: Home photography services, School photography services, Passport photography services, Videotaping services for special events (e.g., weddings).",
    industries: ["photography"],
    name: "Photography Studios, Portrait",
    slug: "photography-studios-portrait"
  },
  "541922": {
    description: "This U.S. industry comprises establishments primarily engaged in providing commercial photography services, generally for advertising agencies, publishers, and other business and industrial users.",
    industries: ["photography"],
    name: "Commercial Photography",
    slug: "commercial-photography"
  },
  "541930": {
    description: "This industry comprises establishments primarily engaged in translating written material and interpreting speech from one language to another and establishments primarily engaged in providing sign language services.",
    industries: ["translation-and-localization"],
    name: "Translation and Interpretation Services",
    slug: "translation-and-interpretation-services"
  },
  "541940": {
    description: "This industry comprises establishments of licensed veterinary practitioners primarily engaged in the practice of veterinary medicine, dentistry, or surgery for animals; and establishments primarily engaged in providing testing services for licensed veterinary practitioners. Illustrative Examples: Animal hospitals, Veterinary clinics, Veterinarians' offices, Veterinary testing laboratories.",
    industries: ["veterinary-services"],
    name: "Veterinary Services",
    slug: "veterinary-services"
  },
  "541990": {
    description: "This industry comprises establishments primarily engaged in the provision of professional, scientific, or technical services (except legal services; accounting, tax preparation, bookkeeping, and related services; architectural, engineering, and related services; specialized design services; computer systems design and related services; management, scientific, and technical consulting services; scientific research and development services; advertising, public relations, and related services; market research and public opinion polling; photographic services; translation and interpretation services; and veterinary services). Illustrative Examples: Appraisal (except real estate) services, Marine surveyor (i.e., appraiser) services, Arbitration and conciliation services (except by lawyer, attorney, or paralegal offices), Patent broker services (i.e., patent marketing services), Commodity inspector services, Pipeline or power line inspection (i.e., visual) services, Consumer credit counseling services, Weather forecasting services, Handwriting analysis services .",
    industries: ["professional-services"],
    name: "All Other Professional, Scientific, and Technical Services",
    slug: "all-other-professional-scientific-and-technical-services"
  },
  "551111": {
    description: "This U.S. industry comprises legal entities known as bank holding companies primarily engaged in holding the securities of (or other equity interests in) companies and enterprises for the purpose of owning a controlling interest or influencing the management decisions of these firms.  The holding companies in this industry do not administer, oversee, and manage other establishments of the company or enterprise whose securities they hold.",
    industries: ["holding-companies"],
    name: "Offices of Bank Holding Companies",
    slug: "offices-of-bank-holding-companies"
  },
  "551112": {
    description: "This U.S. industry comprises legal entities known as holding companies (except bank holding) primarily engaged in holding the securities of (or other equity interests in) companies and enterprises for the purpose of owning a controlling interest or influencing the management decisions of these firms.  The holding companies in this industry do not administer, oversee, and manage other establishments of the company or enterprise whose securities they hold.",
    industries: ["holding-companies"],
    name: "Offices of Other Holding Companies",
    slug: "offices-of-other-holding-companies"
  },
  "551114": {
    description: "This U.S. industry comprises establishments (except government establishments) primarily engaged in administering, overseeing, and managing other establishments of the company or enterprise.  These establishments normally undertake the strategic or organizational planning and decision-making role of the company or enterprise.  Establishments in this industry may hold the securities of the company or enterprise. Illustrative Examples: Centralized administrative offices, Head offices, Corporate offices, Holding companies that manage, District and regional offices, Subsidiary management offices, Cross-References.",
    industries: ["holding-companies"],
    name: "Corporate, Subsidiary, and Regional Managing Offices",
    slug: "corporate-subsidiary-and-regional-managing-offices"
  },
  "561110": {
    description: "This industry comprises establishments primarily engaged in providing a range of day-to-day office administrative services, such as financial planning; billing and recordkeeping; personnel; and physical distribution and logistics, for others on a contract or fee basis.  These establishments do not provide operating staff to carry out the complete operations of a business.",
    industries: ["office-administration"],
    name: "Office Administrative Services",
    slug: "office-administrative-services"
  },
  "561210": {
    description: "This industry comprises establishments primarily engaged in providing operating staff to perform a combination of support services within a client's facilities.  Establishments in this industry typically provide a combination of services, such as janitorial, maintenance, trash disposal, guard and security, mail routing, reception, laundry, and related services to support operations within facilities.  These establishments provide operating staff to carry out these support activities, but are not involved with or responsible for the core business or activities of the client.  Establishments providing facilities (except computer and/or data processing) operation support services and establishments providing private jail services or operating correctional facilities (i.e., jails) on a contract or fee basis are included in this industry. Cross-References.",
    industries: ["facilities-services"],
    name: "Facilities Support Services",
    slug: "facilities-support-services"
  },
  "561311": {
    description: "This U.S. industry comprises establishments primarily engaged in listing employment vacancies and in recruiting, referring, or placing applicants for employment.  The individuals referred or placed are not employees of the employment agencies. Illustrative Examples: Babysitting bureaus (i.e., registries), Employment registries, Model registries, Casting agencies or bureaus (i.e., motion picture, theatrical, video), Employment agencies.",
    industries: ["staffing-and-recruiting"],
    name: "Employment Placement Agencies",
    slug: "employment-placement-agencies"
  },
  "561312": {
    description: "This U.S. industry comprises establishments primarily engaged in providing executive search, recruitment, and placement services for clients with specific executive and senior management position requirements.  The range of services provided by these establishments may include developing a search strategy and position specification based on the culture and needs of the client; researching, identifying, screening, and interviewing candidates; verifying candidate qualifications; and assisting in final offer negotiations and assimilation of the selected candidate.  The individuals identified, recruited, or placed are not employees of the executive search services establishments. Illustrative Examples: Senior executive search services, Executive placement services, Executive search services.",
    industries: ["executive-search-services"],
    name: "Executive Search Services",
    slug: "executive-search-services"
  },
  "561320": {
    description: "This industry comprises establishments primarily engaged in supplying workers to clients' businesses for limited periods of time to supplement the working force of the client.  The individuals provided are employees of the temporary help services establishment.  However, these establishments do not provide direct supervision of their employees at the clients' work sites. Illustrative Examples: Help supply services, Model supply services, Labor (except farm) contractors (i.e., personnel suppliers), Temporary employment or temporary staffing services, Manpower pools.",
    industries: ["temporary-help-services"],
    name: "Temporary Help Services",
    slug: "temporary-help-services"
  },
  "561330": {
    description: "This industry comprises establishments primarily engaged in providing human resources and human resource management services to client businesses and households.  Establishments in this industry operate in a co-employment relationship with client businesses or organizations and are specialized in performing a wide range of human resource and personnel management duties, such as payroll, payroll tax, benefits administration, workers' compensation, unemployment, and human resource administration.  Professional employer organizations (PEOs) are responsible for payroll, including withholding and remitting employment-related taxes, for some or all of the employees of their clients, and also serve as the employer of those employees for benefits and related purposes.",
    industries: ["staffing-and-recruiting"],
    name: "Professional Employer Organizations",
    slug: "professional-employer-organizations"
  },
  "561410": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) letter or resume writing; (2) document editing or proofreading; (3) typing, word processing, or desktop publishing; and (4) stenography (except court reporting or stenotype recording), transcription, and other secretarial services.",
    industries: ["writing-and-editing"],
    name: "Document Preparation Services",
    slug: "document-preparation-services"
  },
  "561421": {
    description: "This U.S. industry comprises establishments primarily engaged in answering telephone calls and relaying messages on behalf of clients or for other establishments of the same enterprise. Cross-References.",
    industries: ["telephone-call-centers"],
    name: "Telephone Answering Services",
    slug: "telephone-answering-services"
  },
  "561422": {
    description: "This U.S. industry comprises establishments primarily engaged in operating call centers that initiate or receive communications via telephone, facsimile, email, or other communication modes for purposes such as: (1) promoting products or services, (2) taking orders, (3) soliciting contributions, and (4) providing information or assistance regarding products or services.  Telemarketing bureaus and other contact centers provide these services on behalf of clients and do not own the products or provide the services that they are representing, or they serve other establishments of the same enterprise.",
    industries: ["telephone-call-centers"],
    name: "Telemarketing Bureaus and Other Contact Centers",
    slug: "telemarketing-bureaus-and-other-contact-centers"
  },
  "561431": {
    description: "This U.S. industry comprises (1) establishments primarily engaged in providing mailbox rental and other postal and mailing (except direct mail advertising) services or (2) establishments engaged in providing these mailing services along with one or more other office support services, such as facsimile services, word processing services, on-site PC rental services, and office product sales.",
    industries: ["administrative-and-support-services"],
    name: "Private Mail Centers",
    slug: "private-mail-centers"
  },
  "561439": {
    description: "This U.S. industry comprises (1) establishments generally known as copy centers or shops primarily engaged in providing photocopying, duplicating, blueprinting, and other document copying services, without also providing printing services (e.g., offset printing, quick printing, digital printing, prepress services) and (2) establishments (except private mail centers) engaged in providing a range of office support services (except printing services), such as document copying services, facsimile services, word processing services, on-site PC rental services, and office product sales. Cross-References.",
    industries: ["administrative-and-support-services"],
    name: "Other Business Service Centers (including Copy Shops)",
    slug: "other-business-service-centers-including-copy-shops"
  },
  "561440": {
    description: "This industry comprises establishments primarily engaged in collecting payments for claims and remitting payments collected to their clients. Illustrative Examples: Account or delinquent account collection services, Tax collection services on a contract or fee basis, Bill or debt collection services.",
    industries: ["collection-agencies"],
    name: "Collection Agencies",
    slug: "collection-agencies"
  },
  "561450": {
    description: "This industry comprises establishments primarily engaged in compiling information, such as credit and employment histories, and providing the information to financial institutions, retailers, and others who have a need to evaluate the creditworthiness of individuals and businesses. Illustrative Examples: Credit agencies, Credit rating services, Credit investigation services, Credit reporting bureaus",
    industries: ["administrative-and-support-services"],
    name: "Credit Bureaus",
    slug: "credit-bureaus"
  },
  "561491": {
    description: "This U.S. industry comprises establishments primarily engaged in repossessing tangible assets (e.g., automobiles, boats, equipment, planes, furniture, appliances) for the creditor as a result of delinquent debts.",
    industries: ["administrative-and-support-services"],
    name: "Repossession Services",
    slug: "repossession-services"
  },
  "561492": {
    description: "This U.S. industry comprises establishments primarily engaged in providing verbatim reporting and stenotype recording of live legal proceedings and transcribing subsequent recorded materials. Illustrative Examples: Real-time (i.e., simultaneous) closed captioning of live television performances, meetings, conferences, Court reporting or stenotype recording services, Public stenography services.",
    industries: ["administrative-and-support-services"],
    name: "Court Reporting and Stenotype Services",
    slug: "court-reporting-and-stenotype-services"
  },
  "561499": {
    description: "This U.S. industry comprises establishments primarily engaged in providing business support services (except secretarial and other document preparation services; telephone answering and telemarketing services; private mail services or document copying services conducted as separate activities or in conjunction with other office support services; monetary debt collection services; credit reporting services; repossession services; and court reporting and stenotype recording services). Illustrative Examples: Address bar coding services, Charitable fundraising campaign organization services on a contract or fee basis, Bar code imprinting services, Mail presorting services.",
    industries: ["fundraising"],
    name: "All Other Business Support Services",
    slug: "all-other-business-support-services"
  },
  "561510": {
    description: "This industry comprises establishments primarily engaged in acting as agents in selling travel, tour, and accommodation services to the general public and commercial clients.",
    industries: ["travel-arrangements"],
    name: "Travel Agencies",
    slug: "travel-agencies"
  },
  "561520": {
    description: "This industry comprises establishments primarily engaged in arranging and assembling tours.  The tours are sold through travel agencies or tour operators.  Travel or wholesale tour operators are included in this industry.",
    industries: ["travel-arrangements"],
    name: "Tour Operators",
    slug: "tour-operators"
  },
  "561591": {
    description: "This U.S. industry comprises establishments primarily engaged in marketing and promoting communities and facilities to businesses and leisure travelers through a range of activities, such as assisting organizations in locating meeting and convention sites; providing travel information on area attractions, lodging accommodations, restaurants; providing maps; and organizing group tours of local historical, recreational, and cultural attractions.",
    industries: ["travel-arrangements"],
    name: "Convention and Visitors Bureaus",
    slug: "convention-and-visitors-bureaus"
  },
  "561599": {
    description: "This U.S. industry comprises establishments (except travel agencies, tour operators, and convention and visitors bureaus) primarily engaged in providing travel arrangement and reservation services. Illustrative Examples: Condominium time-share exchange services, Ticket (e.g., airline, bus, cruise ship, sports, theatrical) offices, Road and travel services automobile clubs, Reservation (e.g., airline, car rental, hotel, restaurant) services, Ticket (e.g., amusement, sports, theatrical) agencies, Cross-References.",
    industries: ["travel-arrangements"],
    name: "All Other Travel Arrangement and Reservation Services",
    slug: "all-other-travel-arrangement-and-reservation-services"
  },
  "561611": {
    description: "This U.S. industry comprises establishments primarily engaged in providing investigation, detective, and personal background check services. Illustrative Examples: Fingerprint services, Private detective services, Polygraph services, Private investigative services (except credit), Cross-References.",
    industries: ["security-and-investigations"],
    name: "Investigation Services",
    slug: "investigation-services"
  },
  "561612": {
    description: "This U.S. industry comprises establishments primarily engaged in providing guard and patrol services, such as bodyguard, guard dog, and parking security services. Cross-References.",
    industries: ["security-guards-and-patrol-services"],
    name: "Security Guards and Patrol Services",
    slug: "security-guards-and-patrol-services"
  },
  "561613": {
    description: "This U.S. industry comprises establishments primarily engaged in picking up and delivering money, receipts, or other valuable items.  These establishments maintain personnel and equipment to protect such properties while in transit.",
    industries: ["security-and-investigations"],
    name: "Armored Car Services",
    slug: "armored-car-services"
  },
  "561621": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) selling security alarm systems, such as burglar and fire alarms, along with installation, repair, or monitoring services or (2) remote monitoring of electronic security alarm systems.",
    industries: ["security-systems-services"],
    name: "Security Systems Services (except Locksmiths)",
    slug: "security-systems-services-except-locksmiths"
  },
  "561622": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) selling mechanical or electronic locking devices, safes, and security vaults, along with installation, repair, rebuilding, or adjusting services or (2) installing, repairing, rebuilding, and adjusting mechanical or electronic locking devices, safes, and security vaults.",
    industries: ["security-systems-services"],
    name: "Locksmiths",
    slug: "locksmiths"
  },
  "561710": {
    description: "This industry comprises establishments primarily engaged in exterminating and controlling birds, mosquitoes, rodents, termites, and other insects and pests (except for crop production and forestry production).  Establishments providing fumigation services are included in this industry. Cross-References.",
    industries: ["administrative-and-support-services"],
    name: "Exterminating and Pest Control Services",
    slug: "exterminating-and-pest-control-services"
  },
  "561720": {
    description: "This industry comprises establishments primarily engaged in cleaning building interiors, interiors of transportation equipment (e.g., aircraft, rail cars, ships), and/or windows. Illustrative Examples: Custodial services, Service station cleaning and degreasing services, Housekeeping (i.e., cleaning) services, Washroom sanitation services, Maid (i.e., cleaning) services.",
    industries: ["janitorial-services"],
    name: "Janitorial Services",
    slug: "janitorial-services"
  },
  "561730": {
    description: "This industry comprises (1) establishments primarily engaged in providing landscape care and maintenance services and/or installing trees, shrubs, plants, lawns, or gardens and (2) establishments primarily engaged in providing these services along with the design of landscape plans and/or the construction (i.e., installation) of walkways, retaining walls, decks, fences, ponds, and similar structures.",
    industries: ["landscaping-services"],
    name: "Landscaping Services",
    slug: "landscaping-services"
  },
  "561740": {
    description: "This industry comprises establishments primarily engaged in cleaning and dyeing used rugs, carpets, and upholstery.",
    industries: ["administrative-and-support-services"],
    name: "Carpet and Upholstery Cleaning Services",
    slug: "carpet-and-upholstery-cleaning-services"
  },
  "561790": {
    description: "This industry comprises establishments primarily engaged in providing services to buildings and dwellings (except exterminating and pest control; janitorial; landscaping care and maintenance; and carpet and upholstery cleaning). Illustrative Examples: Building exterior cleaning services (except sandblasting, window cleaning), Swimming pool cleaning and maintenance services, Chimney cleaning services, Ventilation duct cleaning services, Drain or gutter cleaning services.",
    industries: ["administrative-and-support-services"],
    name: "Other Services to Buildings and Dwellings",
    slug: "other-services-to-buildings-and-dwellings"
  },
  "561910": {
    description: "This industry comprises establishments primarily engaged in packaging client-owned materials.  The services may include labeling and/or imprinting the package. Illustrative Examples: Apparel and textile folding and packaging services, Kit assembling and packaging services, Blister packaging services, Shrink wrapping services, Gift wrapping services.",
    industries: ["administrative-and-support-services"],
    name: "Packaging and Labeling Services",
    slug: "packaging-and-labeling-services"
  },
  "561920": {
    description: "This industry comprises establishments primarily engaged in organizing, promoting, and/or managing events, such as business and trade shows, conventions, conferences, and meetings (whether or not they manage and provide the staff to operate the facilities in which these events take place). Cross-References.",
    industries: ["events-services"],
    name: "Convention and Trade Show Organizers",
    slug: "convention-and-trade-show-organizers"
  },
  "561990": {
    description: "This industry comprises establishments primarily engaged in providing day-to-day business and other organizational support services (except office administrative services, facilities support services, employment services, business support services, travel arrangement and reservation services, security and investigation services, services to buildings and other structures, packaging and labeling services, and convention and trade show organizing services). Illustrative Examples: Bartering services, Flagging (i.e., traffic control) services, Bottle exchanges, Float decorating services, Inventory taking services, Contract meter reading services, Lumber grading services, Diving services on a contract or fee basis.",
    industries: ["administrative-and-support-services"],
    name: "All Other Support Services",
    slug: "all-other-support-services"
  },
  "562111": {
    description: "This U.S. industry comprises establishments primarily engaged in one or more of the following: (1) collecting and/or hauling nonhazardous solid waste (i.e., garbage) within a local area; (2) operating nonhazardous solid waste transfer stations; and (3) collecting and/or hauling mixed recyclable materials within a local area.",
    industries: ["waste-collection"],
    name: "Solid Waste Collection",
    slug: "solid-waste-collection"
  },
  "562112": {
    description: "This U.S. industry comprises establishments primarily engaged in collecting and/or hauling hazardous waste within a local area and/or operating hazardous waste transfer stations.  Hazardous waste collection establishments may be responsible for the identification, treatment, packaging, and labeling of waste for the purposes of transport.",
    industries: ["waste-collection"],
    name: "Hazardous Waste Collection",
    slug: "hazardous-waste-collection"
  },
  "562119": {
    description: "This U.S. industry comprises establishments primarily engaged in collecting and/or hauling waste (except nonhazardous solid waste and hazardous waste) within a local area.  Establishments engaged in brush or rubble removal services are included in this industry.",
    industries: ["waste-collection"],
    name: "Other Waste Collection",
    slug: "other-waste-collection"
  },
  "562211": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) operating treatment and/or disposal facilities for hazardous waste or (2) the combined activity of collecting and/or hauling of hazardous waste materials within a local area and operating treatment or disposal facilities for hazardous waste.",
    industries: ["waste-treatment-and-disposal"],
    name: "Hazardous Waste Treatment and Disposal",
    slug: "hazardous-waste-treatment-and-disposal"
  },
  "562212": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) operating landfills for the disposal of nonhazardous solid waste or (2) the combined activity of collecting and/or hauling nonhazardous waste materials within a local area and operating landfills for the disposal of nonhazardous solid waste.  These establishments may produce byproducts, such as methane.",
    industries: ["waste-treatment-and-disposal"],
    name: "Solid Waste Landfill",
    slug: "solid-waste-landfill"
  },
  "562213": {
    description: "This U.S. industry comprises establishments primarily engaged in operating combustors and incinerators for the disposal of nonhazardous solid waste.  These establishments may produce byproducts, such as electricity and steam.",
    industries: ["waste-treatment-and-disposal"],
    name: "Solid Waste Combustors and Incinerators",
    slug: "solid-waste-combustors-and-incinerators"
  },
  "562219": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) operating nonhazardous waste treatment and disposal facilities (except landfills, combustors, incinerators, and sewer systems or sewage treatment facilities) or (2) the combined activity of collecting and/or hauling of nonhazardous waste materials within a local area and operating waste treatment or disposal facilities (except landfills, combustors, incinerators, and sewer systems or sewage treatment facilities).  Compost dumps are included in this industry.",
    industries: ["waste-treatment-and-disposal"],
    name: "Other Nonhazardous Waste Treatment and Disposal",
    slug: "other-nonhazardous-waste-treatment-and-disposal"
  },
  "562910": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) remediation and cleanup of contaminated buildings, mine sites, soil, or ground water; (2) integrated mine reclamation activities, including demolition, soil remediation, waste water treatment, hazardous material removal, contouring land, and revegetation; and (3) asbestos, lead paint, and other toxic material abatement.",
    industries: ["administrative-and-support-services"],
    name: "Remediation Services",
    slug: "remediation-services"
  },
  "562920": {
    description: "This industry comprises establishments primarily engaged in (1) operating facilities for separating and sorting recyclable materials from nonhazardous waste streams (i.e., garbage) and/or (2) operating facilities where commingled recyclable materials, such as paper, plastics, used beverage cans, and metals, are sorted into distinct categories. Cross-References.",
    industries: ["administrative-and-support-services"],
    name: "Materials Recovery Facilities",
    slug: "materials-recovery-facilities"
  },
  "562991": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) pumping (i.e., cleaning) septic tanks and cesspools and/or (2) renting and/or servicing portable toilets.",
    industries: ["administrative-and-support-services"],
    name: "Septic Tank and Related Services",
    slug: "septic-tank-and-related-services"
  },
  "562998": {
    description: "This U.S. industry comprises establishments primarily engaged in providing waste management services (except waste collection, waste treatment and disposal, remediation, operation of materials recovery facilities, septic tank pumping and related services, and waste management consulting services). Illustrative Examples: Beach cleaning and maintenance services, Sewer or storm basin cleanout services, Catch basin cleaning services, Tank cleaning and disposal services, commercial or industrial, Sewer cleaning and rodding services.",
    industries: ["administrative-and-support-services"],
    name: "All Other Miscellaneous Waste Management Services",
    slug: "all-other-miscellaneous-waste-management-services"
  },
  "611110": {
    description: "This industry comprises establishments primarily engaged in furnishing academic courses and associated course work that comprise a basic preparatory education.  A basic preparatory education ordinarily constitutes kindergarten through 12th grade.  This industry includes school boards and school districts. Illustrative Examples: Elementary schools, Parochial schools, elementary or secondary, High schools, Primary schools, Charter schools, Kindergartens, Military academies, elementary or secondary, Schools for the physically disabled, elementary or secondary, Cross-References.",
    industries: ["primary-and-secondary-education"],
    name: "Elementary and Secondary Schools",
    slug: "elementary-and-secondary-schools"
  },
  "611210": {
    description: "This industry comprises establishments primarily engaged in furnishing academic, or academic and technical, courses and granting associate degrees, certificates, or diplomas below the baccalaureate level.  The requirement for admission to an associate or equivalent degree program is at least a high school diploma or equivalent general academic training.  Instruction may be provided in diverse settings, such as the establishment's or client's training facilities, educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods. Cross-References.",
    industries: ["education"],
    name: "Junior Colleges",
    slug: "junior-colleges"
  },
  "611310": {
    description: "This industry comprises establishments primarily engaged in furnishing academic courses and granting degrees at baccalaureate or graduate levels.  The requirement for admission is at least a high school diploma or equivalent general academic training.  Instruction may be provided in diverse settings, such as the establishment's or client's training facilities, educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods. Illustrative Examples: Colleges (except junior colleges), Theological seminaries offering baccalaureate or graduate degrees, Military academies, college-level, Universities, Professional schools (e.g., business administration, dental, law, medical), Cross-References.",
    industries: ["higher-education"],
    name: "Colleges, Universities, and Professional Schools",
    slug: "colleges-universities-and-professional-schools"
  },
  "611410": {
    description: "This industry comprises establishments primarily engaged in offering courses in office procedures and secretarial and stenographic skills and may offer courses in basic office skills, such as word processing.  In addition, these establishments may offer such classes as office machine operation, reception, communications, and other skills designed for individuals pursuing a clerical or secretarial career.  Instruction may be provided in diverse settings, such as the establishment's or client's training facilities, educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods.",
    industries: ["secretarial-schools"],
    name: "Business and Secretarial Schools",
    slug: "business-and-secretarial-schools"
  },
  "611420": {
    description: "This industry comprises establishments primarily engaged in conducting computer training (except computer repair), such as instruction in computer programming, software applications, computerized business systems, computer electronics technology, computer operations, and local area network management.  Instruction may be provided in diverse settings, such as the establishment's or client's training facilities, educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods.",
    industries: ["professional-training-and-coaching"],
    name: "Computer Training",
    slug: "computer-training"
  },
  "611430": {
    description: "This industry comprises establishments primarily engaged in offering an array of short duration courses and seminars for management and professional development.  Training for career development may be provided directly to individuals or through employers' training programs, and courses may be customized or modified to meet the special needs of customers.  Instruction may be provided in diverse settings, such as the establishment's or client's training facilities, educational institutions, the workplace, or the home, and through diverse means, such as correspondence, television, the Internet, or other electronic and distance-learning methods.  The training provided by these establishments may include the use of simulators and simulation methods.",
    industries: ["professional-training-and-coaching"],
    name: "Professional and Management Development Training",
    slug: "professional-and-management-development-training"
  },
  "611511": {
    description: "This U.S. industry comprises establishments primarily engaged in offering training in barbering, hair styling, or the cosmetic arts, such as makeup or skin care.  These schools provide job-specific certification.",
    industries: ["cosmetology-and-barber-schools"],
    name: "Cosmetology and Barber Schools",
    slug: "cosmetology-and-barber-schools"
  },
  "611512": {
    description: "This U.S. industry comprises establishments primarily engaged in offering aviation and flight training.  These establishments may offer vocational training, recreational training, or both.",
    industries: ["flight-training"],
    name: "Flight Training",
    slug: "flight-training"
  },
  "611513": {
    description: "This U.S. industry comprises establishments primarily engaged in offering apprenticeship training programs.  These programs involve applied training as well as course work. Cross-References.",
    industries: ["technical-and-vocational-training"],
    name: "Apprenticeship Training",
    slug: "apprenticeship-training"
  },
  "611519": {
    description: "This U.S. industry comprises establishments primarily engaged in offering job or career vocational or technical courses (except cosmetology and barber training, aviation and flight training, and apprenticeship training).  The curriculums offered by these schools are highly structured and specialized and lead to job-specific certification. Illustrative Examples: Bartending schools, Modeling schools, Broadcasting schools, Real estate schools, Computer repair training, Truck driving schools, Graphic arts schools, Specialized military training (except flight instruction, academies, and basic training).",
    industries: ["technical-and-vocational-training"],
    name: "Other Technical and Trade Schools",
    slug: "other-technical-and-trade-schools"
  },
  "611610": {
    description: "This industry comprises establishments primarily engaged in offering instruction in the arts, including dance, art, drama, and music. Illustrative Examples: Art (except commercial and graphic) instruction, Music instruction (e.g., piano, guitar), Dance instruction, Music schools (except academic), Dance studios, Performing arts schools (except academic), Drama schools (except academic), Photography schools (except commercial photography), Fine arts schools (except academic), Cross-References.",
    industries: ["fine-arts-schools"],
    name: "Fine Arts Schools",
    slug: "fine-arts-schools"
  },
  "611620": {
    description: "This industry comprises establishments, such as camps and schools, primarily engaged in offering instruction in athletic activities.  Overnight and day sports instruction camps are included in this industry. Illustrative Examples: Camps, sports instruction, Professional sports instructors (i.e., not participating in sporting events), Cheerleading instruction, Riding instruction academies or schools, Gymnastics instruction, Sports (e.g., baseball, basketball, football, golf) instruction, Martial arts instruction, camps or schools, Swimming instruction, Cross-References.",
    industries: ["sports-and-recreation-instruction"],
    name: "Sports and Recreation Instruction",
    slug: "sports-and-recreation-instruction"
  },
  "611630": {
    description: "This industry comprises establishments primarily engaged in offering foreign language instruction (including sign language).  These establishments are designed to offer language instruction ranging from conversational skills for personal enrichment to intensive training courses for career or educational opportunities.",
    industries: ["language-schools"],
    name: "Language Schools",
    slug: "language-schools"
  },
  "611691": {
    description: "This U.S. industry comprises establishments primarily engaged in offering preparation for standardized examinations and/or academic tutoring services. Illustrative Examples: Academic tutoring services, Learning centers offering remedial courses, College board preparation centers, Professional examination review instruction",
    industries: ["education"],
    name: "Exam Preparation and Tutoring",
    slug: "exam-preparation-and-tutoring"
  },
  "611692": {
    description: "This U.S. industry comprises establishments primarily engaged in offering automobile driving instruction. Cross-References.",
    industries: ["education"],
    name: "Automobile Driving Schools",
    slug: "automobile-driving-schools"
  },
  "611699": {
    description: "This U.S. industry comprises establishments primarily engaged in offering instruction (except academic schools, colleges, and universities; business, computer, and management instruction; technical and trade instruction; and fine arts, sports, recreation, language, exam preparation, tutoring, and automobile driving instruction). Illustrative Examples: Public speaking training, Survival training, Speed reading instruction, Yoga instruction, camps or schools.",
    industries: ["education"],
    name: "All Other Miscellaneous Schools and Instruction",
    slug: "all-other-miscellaneous-schools-and-instruction"
  },
  "611710": {
    description: "This industry comprises establishments primarily engaged in providing non-instructional services that support educational processes or systems. Illustrative Examples: Educational consultants, Educational testing services, Educational guidance counseling services, Student exchange programs, Educational testing evaluation services.",
    industries: ["education"],
    name: "Educational Support Services",
    slug: "educational-support-services"
  },
  "621111": {
    description: "This U.S. industry comprises establishments of health practitioners having the degree of M.D. (Doctor of Medicine) or D.O. (Doctor of Osteopathic Medicine) primarily engaged in the independent practice of general or specialized medicine (except psychiatry or psychoanalysis) or surgery.  These practitioners operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers. Cross-References.",
    industries: ["physicians"],
    name: "Offices of Physicians (except Mental Health Specialists)",
    slug: "offices-of-physicians-except-mental-health-specialists"
  },
  "621112": {
    description: "This U.S. industry comprises establishments of health practitioners having the degree of M.D. (Doctor of Medicine) or D.O. (Doctor of Osteopathic Medicine) primarily engaged in the independent practice of psychiatry or psychoanalysis.  These practitioners operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers. Cross-References.",
    industries: ["physicians"],
    name: "Offices of Physicians, Mental Health Specialists",
    slug: "offices-of-physicians-mental-health-specialists"
  },
  "621210": {
    description: "This industry comprises establishments of health practitioners having the degree of D.M.D. (Doctor of Dental Medicine), D.D.S. (Doctor of Dental Surgery), or D.D.Sc. (Doctor of Dental Science) primarily engaged in the independent practice of general or specialized dentistry or dental surgery.  These practitioners operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers.  They can provide either comprehensive preventive, cosmetic, or emergency care, or specialize in a single field of dentistry. Cross-References.",
    industries: ["dentists"],
    name: "Offices of Dentists",
    slug: "offices-of-dentists"
  },
  "621310": {
    description: "This industry comprises establishments of health practitioners having the degree of D.C. (Doctor of Chiropractic) primarily engaged in the independent practice of chiropractic.  These practitioners provide diagnostic and therapeutic treatment of neuromusculoskeletal and related disorders through the manipulation and adjustment of the spinal column and extremities, and operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers.",
    industries: ["chiropractors"],
    name: "Offices of Chiropractors",
    slug: "offices-of-chiropractors"
  },
  "621320": {
    description: "This industry comprises establishments of health practitioners having the degree of O.D. (Doctor of Optometry) primarily engaged in the independent practice of optometry.  These practitioners examine, diagnose, treat, and manage diseases and disorders of the visual system, the eye, and associated structures as well as diagnose related systemic conditions.  Offices of optometrists prescribe and/or provide eyeglasses, contact lenses, low vision aids, and vision therapy.  They operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers, and may also provide the same services as opticians, such as selling and fitting prescription eyeglasses and contact lenses. Cross-References.",
    industries: ["optometrists"],
    name: "Offices of Optometrists",
    slug: "offices-of-optometrists"
  },
  "621330": {
    description: "This industry comprises establishments of independent mental health practitioners (except physicians) primarily engaged in (1) the diagnosis and treatment of mental, emotional, and behavioral disorders and/or (2) the diagnosis and treatment of individual or group social dysfunction brought about by such causes as mental illness, alcohol and substance abuse, physical and emotional trauma, or stress.  These practitioners operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers. Cross-References.",
    industries: ["mental-health-care"],
    name: "Offices of Mental Health Practitioners (except Physicians)",
    slug: "offices-of-mental-health-practitioners-except-physicians"
  },
  "621340": {
    description: "This industry comprises establishments of independent health practitioners primarily engaged in one of the following: (1) providing physical therapy services to patients who have impairments, functional limitations, disabilities, or changes in physical functions and health status resulting from injury, disease or other causes, or who require prevention, wellness or fitness services; (2) planning and administering educational, recreational, and social activities designed to help patients or individuals with disabilities regain physical or mental functioning or adapt to their disabilities; and (3) diagnosing and treating speech, language, or hearing problems.  These practitioners operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers. Illustrative Examples: Audiologists' offices, Recreational (e.g., art, dance, music) therapists' offices , Industrial therapists' offices, Speech pathologists' offices , Occupational therapists' offices, Physical therapists' offices, Cross-References.",
    industries: ["physical-occupational-and-speech-therapists"],
    name: "Offices of Physical, Occupational and Speech Therapists, and Audiologists",
    slug: "offices-of-physical-occupational-and-speech-therapists-and-audiologists"
  },
  "621391": {
    description: "This U.S. industry comprises establishments of health practitioners having the degree of D.P.M. (Doctor of Podiatric Medicine) primarily engaged in the independent practice of podiatry.  These practitioners diagnose and treat diseases and deformities of the foot and operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers. Cross-References.",
    industries: ["medical-practices"],
    name: "Offices of Podiatrists",
    slug: "offices-of-podiatrists"
  },
  "621399": {
    description: "This U.S. industry comprises establishments of independent health practitioners (except physicians; dentists; chiropractors; optometrists; mental health specialists; physical, occupational, and speech therapists; audiologists; and podiatrists).  These practitioners operate private or group practices in their own offices (e.g., centers, clinics) or in the facilities of others, such as hospitals or HMO medical centers. Illustrative Examples: Acupuncturists' (except MDs or DOs) offices, Hypnotherapists' offices, Dental hygienists' offices, Inhalation or respiratory therapists' offices, Denturists' offices, Midwives' offices, Dietitians' offices, Naturopaths' offices, Homeopaths' offices, Registered or licensed practical nurses' offices.",
    industries: ["alternative-medicine"],
    name: "Offices of All Other Miscellaneous Health Practitioners",
    slug: "offices-of-all-other-miscellaneous-health-practitioners"
  },
  "621410": {
    description: "This industry comprises establishments with medical staff primarily engaged in providing a range of family planning services on an outpatient basis, such as contraceptive services, genetic and prenatal counseling, voluntary sterilization, and therapeutic and medically induced termination of pregnancy. Illustrative Examples: Birth control clinics, Fertility clinics, Childbirth preparation classes, Pregnancy counseling centers",
    industries: ["family-planning-centers"],
    name: "Family Planning Centers",
    slug: "family-planning-centers"
  },
  "621420": {
    description: "This industry comprises establishments with medical staff primarily engaged in providing outpatient services related to the diagnosis and treatment of mental health disorders and alcohol and other substance abuse.  These establishments generally treat patients who do not require inpatient treatment.  They may provide a counseling staff and information regarding a wide range of mental health and substance abuse issues and/or refer patients to more extensive treatment programs, if necessary. Illustrative Examples: Outpatient alcoholism treatment centers and clinics (except hospitals), Outpatient mental health centers and clinics (except hospitals), Outpatient detoxification centers and clinics (except hospitals), Outpatient substance abuse treatment centers and clinics (except hospitals), Outpatient drug addiction treatment centers and clinics (except hospitals), Cross-References.",
    industries: ["medical-practices"],
    name: "Outpatient Mental Health and Substance Abuse Centers",
    slug: "outpatient-mental-health-and-substance-abuse-centers"
  },
  "621491": {
    description: "This U.S. industry comprises establishments with physicians and other medical staff primarily engaged in providing a range of outpatient medical services to the health maintenance organization (HMO) subscribers with a focus generally on primary health care.  These establishments are owned by the HMO.  Included in this industry are HMO establishments that both provide health care services and underwrite health and medical insurance policies. Cross-References.",
    industries: ["outpatient-care-centers"],
    name: "HMO Medical Centers",
    slug: "hmo-medical-centers"
  },
  "621492": {
    description: "This U.S. industry comprises establishments with medical staff primarily engaged in providing outpatient kidney or renal dialysis services.",
    industries: ["outpatient-care-centers"],
    name: "Kidney Dialysis Centers",
    slug: "kidney-dialysis-centers"
  },
  "621493": {
    description: "This U.S. industry comprises establishments with physicians and other medical staff primarily engaged in (1) providing surgical services (e.g., orthoscopic and cataract surgery) on an outpatient basis or (2) providing emergency care services (e.g., setting broken bones, treating lacerations, or tending to patients suffering injuries as a result of accidents, trauma, or medical conditions necessitating immediate medical care) on an outpatient basis.  Outpatient surgical establishments have specialized facilities, such as operating and recovery rooms, and specialized equipment, such as anesthetic or X-ray equipment. Illustrative Examples: Freestanding ambulatory surgical centers and clinics, Freestanding emergency medical centers and clinics, Freestanding trauma centers (except hospitals), Urgent medical care centers and clinics (except hospitals), Cross-References.",
    industries: ["outpatient-care-centers"],
    name: "Freestanding Ambulatory Surgical and Emergency Centers",
    slug: "freestanding-ambulatory-surgical-and-emergency-centers"
  },
  "621498": {
    description: "This U.S. industry comprises establishments with medical staff primarily engaged in providing general or specialized outpatient care (except family planning centers, outpatient mental health and substance abuse centers, HMO medical centers, kidney dialysis centers, and freestanding ambulatory surgical and emergency centers).  Centers or clinics of health practitioners with different degrees from more than one industry practicing within the same establishment (e.g., Doctor of Medicine and Doctor of Dental Medicine) are included in this industry. Illustrative Examples: Outpatient biofeedback centers and clinics, Outpatient pain therapy centers and clinics, Outpatient community health centers and clinics, Outpatient sleep disorder centers and clinics, Cross-References.",
    industries: ["outpatient-care-centers"],
    name: "All Other Outpatient Care Centers",
    slug: "all-other-outpatient-care-centers"
  },
  "621511": {
    description: "This U.S. industry comprises establishments known as medical laboratories primarily engaged in providing analytic or diagnostic services, including body fluid analysis, generally to the medical profession or to the patient on referral from a health practitioner. Illustrative Examples: Blood analysis laboratories, Medical pathology laboratories, Medical bacteriological laboratories, Medical testing laboratories, Medical forensic laboratories, Cross-References.",
    industries: ["medical-and-diagnostic-laboratories"],
    name: "Medical Laboratories",
    slug: "medical-laboratories"
  },
  "621512": {
    description: "This U.S. industry comprises establishments known as diagnostic imaging centers primarily engaged in producing images of the patient generally on referral from a health practitioner. Illustrative Examples: Computer tomography (CT-scan) centers, Medical radiological laboratories, Dental or medical X-ray laboratories, Ultrasound imaging centers, Magnetic resonance imaging (MRI) centers",
    industries: ["medical-and-diagnostic-laboratories"],
    name: "Diagnostic Imaging Centers",
    slug: "diagnostic-imaging-centers"
  },
  "621610": {
    description: "This industry comprises establishments primarily engaged in providing skilled nursing services in the home, along with a range of the following: personal care services; homemaker and companion services; physical therapy; medical social services; medications; medical equipment and supplies; counseling; 24-hour home care; occupation and vocational therapy; dietary and nutritional services; speech therapy; audiology; and high-tech care, such as intravenous therapy. Illustrative Examples: Home health care agencies, Visiting nurse associations, Home infusion therapy services, In-home hospice care services, Cross-References.",
    industries: ["home-health-care-services"],
    name: "Home Health Care Services",
    slug: "home-health-care-services"
  },
  "621910": {
    description: "This industry comprises establishments primarily engaged in providing transportation of patients by ground or air, along with medical care.  These services are often provided during a medical emergency but are not restricted to emergencies.  The vehicles are equipped with lifesaving equipment operated by medically trained personnel. Cross-References.",
    industries: ["ambulance-services"],
    name: "Ambulance Services",
    slug: "ambulance-services"
  },
  "621991": {
    description: "This U.S. industry comprises establishments primarily engaged in collecting, storing, and distributing blood and blood products and storing and distributing body organs. Cross-References.",
    industries: ["medical-practices"],
    name: "Blood and Organ Banks",
    slug: "blood-and-organ-banks"
  },
  "621999": {
    description: "This U.S. industry comprises establishments primarily engaged in providing ambulatory health care services (except offices of physicians, dentists, and other health practitioners; outpatient care centers; medical and diagnostic laboratories; home health care providers; ambulances; and blood and organ banks). Illustrative Examples: Health screening services (except by offices of health practitioners), Physical fitness evaluation services (except by offices of health practitioners), Hearing testing services (except by offices of audiologists), Smoking cessation programs, Pacemaker monitoring services, Cross-References.",
    industries: ["medical-practices"],
    name: "All Other Miscellaneous Ambulatory Health Care Services",
    slug: "all-other-miscellaneous-ambulatory-health-care-services"
  },
  "622110": {
    description: "This industry comprises establishments known and licensed as general medical and surgical hospitals primarily engaged in providing diagnostic and medical treatment (both surgical and nonsurgical) to inpatients with any of a wide variety of medical conditions.  These establishments maintain inpatient beds and provide patients with food services that meet their nutritional requirements.  These hospitals have an organized staff of physicians and other medical staff to provide patient care services.  These establishments usually provide other services, such as outpatient services, anatomical pathology services, diagnostic X-ray services, clinical laboratory services, operating room services for a variety of procedures, and pharmacy services.",
    industries: ["hospitals"],
    name: "General Medical and Surgical Hospitals",
    slug: "general-medical-and-surgical-hospitals"
  },
  "622210": {
    description: "This industry comprises establishments known and licensed as psychiatric and substance abuse hospitals primarily engaged in providing diagnostic, medical treatment, and monitoring services for inpatients who suffer from mental illness or substance abuse disorders.  The treatment often requires an extended stay in the hospital.  These establishments maintain inpatient beds and provide patients with food services that meet their nutritional requirements.  They have an organized staff of physicians and other medical staff to provide patient care services.  Psychiatric, psychological, and social work services are available at the facility.  These hospitals usually provide other services, such as outpatient services, clinical laboratory services, diagnostic X-ray services, and electroencephalograph services. Cross-References.",
    industries: ["hospitals"],
    name: "Psychiatric and Substance Abuse Hospitals",
    slug: "psychiatric-and-substance-abuse-hospitals"
  },
  "622310": {
    description: "This industry comprises establishments known and licensed as specialty hospitals primarily engaged in providing diagnostic and medical treatment to inpatients with a specific type of disease or medical condition (except psychiatric or substance abuse).  Hospitals providing long-term care for the chronically ill and hospitals providing rehabilitation, restorative, and adjustive services to physically challenged or disabled people are included in this industry.  These establishments maintain inpatient beds and provide patients with food services that meet their nutritional requirements.  They have an organized staff of physicians and other medical staff to provide patient care services.  These hospitals may provide other services, such as outpatient services, diagnostic X-ray services, clinical laboratory services, operating room services, physical therapy services, educational and vocational services, and psychological and social work services. Cross-References.",
    industries: ["hospitals"],
    name: "Specialty (except Psychiatric and Substance Abuse) Hospitals",
    slug: "specialty-except-psychiatric-and-substance-abuse-hospitals"
  },
  "623110": {
    description: "This industry comprises establishments primarily engaged in providing inpatient nursing and rehabilitative services.  The care is generally provided for an extended period of time to individuals requiring nursing care.  These establishments have a permanent core staff of registered or licensed practical nurses who, along with other staff, provide nursing and continuous personal care services. Illustrative Examples: Convalescent homes or convalescent hospitals (except psychiatric), Nursing homes, Rest homes with nursing care, Assisted living facilities (without nursing facilities) for the elderly with nursing care, Inpatient care hospices, Cross-References.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Nursing Care Facilities (Skilled Nursing Facilities)",
    slug: "nursing-care-facilities-skilled-nursing-facilities"
  },
  "623210": {
    description: "This industry comprises establishments (e.g., group homes, hospitals, intermediate care facilities) primarily engaged in providing residential care services for persons with intellectual and developmental disabilities.  These facilities may provide some health care, though the focus is room, board, protective supervision, and counseling. Cross-References.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Residential Intellectual and Developmental Disability Facilities",
    slug: "residential-intellectual-and-developmental-disability-facilities"
  },
  "623220": {
    description: "This industry comprises establishments primarily engaged in providing residential care and treatment for patients with mental health and substance abuse illnesses.  These establishments provide room, board, supervision, and counseling services.  Although medical services may be available at these establishments, they are incidental to the counseling, mental rehabilitation, and support services offered.  These establishments generally provide a wide range of social services in addition to counseling. Illustrative Examples: Alcoholism or drug addiction rehabilitation , facilities (except licensed hospitals) , Psychiatric convalescent homes or hospitals, Mental health halfway houses, Residential group homes for the emotionally disturbed, Cross-References.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Residential Mental Health and Substance Abuse Facilities",
    slug: "residential-mental-health-and-substance-abuse-facilities"
  },
  "623311": {
    description: "This U.S. industry comprises establishments primarily engaged in providing a range of residential and personal care services with on-site nursing care facilities for (1) the elderly and other persons who are unable to fully care for themselves and/or (2) the elderly and other persons who do not desire to live independently.  Individuals live in a variety of residential settings with meals, housekeeping, social, leisure, and other services available to assist residents in daily living.  Assisted living facilities with on-site nursing care facilities are included in this industry. Cross-References.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Continuing Care Retirement Communities",
    slug: "continuing-care-retirement-communities"
  },
  "623312": {
    description: "This U.S. industry comprises establishments primarily engaged in providing residential and personal care services without nursing care for (1) the elderly or other persons who are unable to fully care for themselves and/or (2) the elderly or other persons who do not desire to live independently.  The care typically includes room, board, supervision, and assistance in daily living, such as housekeeping services. Illustrative Examples: Assisted living facilities for the elderly without nursing care, Rest homes without nursing care , Cross-References.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Assisted Living Facilities for the Elderly",
    slug: "assisted-living-facilities-for-the-elderly"
  },
  "623990": {
    description: "This industry comprises establishments primarily engaged in providing residential care (except residential intellectual and developmental disability facilities, residential mental health and substance abuse facilities, continuing care retirement communities, and assisted living facilities for the elderly).  These establishments also provide supervision and personal care services. Illustrative Examples: Boot or disciplinary camps (except correctional) for delinquent youth, Group homes for the hearing or visually impaired, Child group foster homes, Delinquent youth halfway group homes, Halfway group homes for delinquents or ex-offenders, Homes for unwed mothers, Group homes for the disabled without nursing care, Orphanages, Cross-References.",
    industries: ["nursing-homes-and-residential-care-facilities"],
    name: "Other Residential Care Facilities",
    slug: "other-residential-care-facilities"
  },
  "624110": {
    description: "This industry comprises establishments primarily engaged in providing nonresidential social assistance services for children and youth.  These establishments provide for the welfare of children in such areas as adoption and foster care, drug prevention, life skills training, and positive social development. Illustrative Examples: Adoption agencies, Youth centers (except recreational only), Child guidance organizations, Youth self-help organizations, Foster care placement services, Cross-References.",
    industries: ["individual-and-family-services"],
    name: "Child and Youth Services",
    slug: "child-and-youth-services"
  },
  "624120": {
    description: "This industry comprises establishments primarily engaged in providing nonresidential social assistance services to improve the quality of life for the elderly or persons with intellectual and/or developmental disabilities.  These establishments provide for the welfare of these individuals in such areas as day care, non-medical home care or homemaker services, social activities, group support, and companionship.",
    industries: ["services-for-the-elderly-and-disabled"],
    name: "Services for the Elderly and Persons with Disabilities",
    slug: "services-for-the-elderly-and-persons-with-disabilities"
  },
  "624190": {
    description: "This industry comprises establishments primarily engaged in providing nonresidential individual and family social assistance services (except those specifically directed toward children, the elderly, or persons with intellectual and/or developmental disabilities). Illustrative Examples: Community action services agencies, Marriage counseling services (except by offices of mental health practitioners), Crisis intervention centers, Multi-purpose social services centers, Family social services agencies, Family welfare services, Self-help organizations (except for disabled persons, the elderly), Suicide crisis centers, Hotline centers, Telephone counseling services.",
    industries: ["individual-and-family-services"],
    name: "Other Individual and Family Services",
    slug: "other-individual-and-family-services"
  },
  "624210": {
    description: "This industry comprises establishments primarily engaged in the collection, preparation, and delivery of food for the needy.  Establishments in this industry may also distribute clothing and blankets to the poor.  These establishments may prepare and deliver meals to persons who by reason of age, disability, or illness are unable to prepare meals for themselves; collect and distribute salvageable or donated food; or prepare and provide meals at fixed or mobile locations.  Food banks, meal delivery programs, and soup kitchens are included in this industry.",
    industries: ["community-services"],
    name: "Community Food Services",
    slug: "community-food-services"
  },
  "624221": {
    description: "This U.S. industry comprises establishments primarily engaged in providing (1) short-term emergency shelter for victims of domestic violence, sexual assault, or child abuse and/or (2) temporary residential shelter for homeless individuals or families, runaway youth, and patients and families caught in medical crises.  These establishments may operate their own shelters or may subsidize housing using existing homes, apartments, hotels, or motels. Cross-References.",
    industries: ["community-services"],
    name: "Temporary Shelters",
    slug: "temporary-shelters"
  },
  "624229": {
    description: "This U.S. industry comprises establishments primarily engaged in providing one or more of the following community housing services: (1) transitional housing to low-income individuals and families; (2) volunteer construction or repair of low-cost housing, in partnership with the homeowner who may assist in the construction or repair work; and (3) the repair of homes for elderly or disabled homeowners.  These establishments may subsidize housing using existing homes, apartments, hotels, or motels or may require a low-cost mortgage or sweat equity.  These establishments may also provide low-income families with furniture and household supplies. Cross-References.",
    industries: ["community-services"],
    name: "Other Community Housing Services",
    slug: "other-community-housing-services"
  },
  "624230": {
    description: "This industry comprises establishments primarily engaged in providing food, shelter, clothing, medical relief, resettlement, and counseling to victims of domestic or international disasters or conflicts (e.g., wars).",
    industries: ["emergency-and-relief-services"],
    name: "Emergency and Other Relief Services",
    slug: "emergency-and-other-relief-services"
  },
  "624310": {
    description: "This industry comprises (1) establishments primarily engaged in providing vocational rehabilitation or habilitation services, such as job counseling, job training, and work experience, to unemployed and underemployed persons, persons with disabilities, and persons who have a job market disadvantage because of lack of education, job skill, or experience and (2) establishments primarily engaged in providing training and employment to persons with disabilities.  Vocational rehabilitation job training facilities (except schools) and sheltered workshops (i.e., work experience centers) are included in this industry. Cross-References.",
    industries: ["vocational-rehabilitation-services"],
    name: "Vocational Rehabilitation Services",
    slug: "vocational-rehabilitation-services"
  },
  "624410": {
    description: "This industry comprises establishments primarily engaged in providing care and early learning opportunities for infants and children.  These establishments generally care for children from birth through school age and may also offer pre-kindergarten, kindergarten, and/or before- or after-school educational programs.  The care and early learning provided by these establishments may include opportunities for development in health, social and emotional learning, and family engagement. Illustrative Examples: Before-school and after-school educational programs, separate from schools, Child care babysitting services, Child or infant care centers, Head Start and Early Head Start programs, separate from schools, Nursery schools, Preschool centers.",
    industries: ["child-day-care-services"],
    name: "Child Day Care Services",
    slug: "child-day-care-services"
  },
  "711110": {
    description: "This industry comprises (1) companies, groups, or theaters primarily engaged in producing the following live theatrical presentations: musicals; operas; plays; and comedy, improvisational, mime, and puppet shows and (2) establishments, commonly known as dinner theaters, engaged in producing live theatrical productions and in providing food and beverages for consumption on the premises.  Theater groups or companies may or may not operate their own theater or other facility for staging their shows. Illustrative Examples: Comedy troupes, Opera companies, Live theatrical productions (except dance), Theatrical stock or repertory companies, Musical theater companies, Cross-References.",
    industries: ["theater-companies"],
    name: "Theater Companies and Dinner Theaters",
    slug: "theater-companies-and-dinner-theaters"
  },
  "711120": {
    description: "This industry comprises companies, groups, or theaters primarily engaged in producing all types of live theatrical dance (e.g., ballet, contemporary dance, folk dance) presentations.  Dance companies or groups may or may not operate their own theater or other facility for staging their shows. Cross-References.",
    industries: ["dance-companies"],
    name: "Dance Companies",
    slug: "dance-companies"
  },
  "711130": {
    description: "This industry comprises (1) groups primarily engaged in producing live musical entertainment (except theatrical musical or opera productions) and (2) independent (i.e., freelance) artists primarily engaged in providing live musical entertainment.  Musical groups and artists may perform in front of a live audience or in a studio, and may or may not operate their own facilities for staging their shows. Illustrative Examples: Bands, Musical groups (except theatrical musical groups), Drum and bugle corps (i.e., drill teams), Orchestras, Independent musicians or vocalists, Cross-References.",
    industries: ["musicians"],
    name: "Musical Groups and Artists",
    slug: "musical-groups-and-artists"
  },
  "711190": {
    description: "This industry comprises companies or groups (except theater companies, dance companies, and musical groups and artists) primarily engaged in producing live theatrical presentations. Illustrative Examples: Carnival traveling shows, Ice skating companies, Circuses, Magic shows, Cross-References.",
    industries: ["circuses-and-magic-shows"],
    name: "Other Performing Arts Companies",
    slug: "other-performing-arts-companies"
  },
  "711211": {
    description: "This U.S. industry comprises professional or semiprofessional sports teams or clubs primarily engaged in participating in live sporting events, such as baseball, basketball, football, hockey, soccer, and jai alai games, before a paying audience.  These establishments may or may not operate their own arena, stadium, or other facility for presenting these events. Cross-References.",
    industries: ["sports-teams-and-clubs"],
    name: "Sports Teams and Clubs",
    slug: "sports-teams-and-clubs"
  },
  "711212": {
    description: "This U.S. industry comprises establishments primarily engaged in operating racetracks without casinos.  These establishments may also present and/or promote the events, such as auto, dog, and horse races, held in these facilities. Cross-References.",
    industries: ["racetracks"],
    name: "Racetracks",
    slug: "racetracks"
  },
  "711219": {
    description: "This U.S. industry comprises (1) independent athletes, such as professional or semiprofessional golfers, boxers, and race car drivers, primarily engaged in participating in live sporting or racing events before a paying audience; (2) owners of racing participants, such as cars, dogs, and horses, primarily engaged in entering them in racing events or other spectator events; and (3) establishments, such as sports trainers, primarily engaged in providing specialized services required to support participants in sports events or competitions. Cross-References.",
    industries: ["spectator-sports"],
    name: "Other Spectator Sports",
    slug: "other-spectator-sports"
  },
  "711310": {
    description: "This industry comprises establishments primarily engaged in (1) organizing, promoting, and/or managing live performing arts productions, sports events, and similar events, such as state fairs, county fairs, agricultural fairs, concerts, and festivals, held in facilities that they manage and operate and/or (2) managing and providing the staff to operate arenas, stadiums, theaters, or other related facilities for rent to other promoters.",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Promoters of Performing Arts, Sports, and Similar Events with Facilities",
    slug: "promoters-of-performing-arts-sports-and-similar-events-with-facilities"
  },
  "711320": {
    description: "This industry comprises promoters primarily engaged in organizing, promoting, and/or managing live performing arts productions, sports events, and similar events, such as state fairs, county fairs, agricultural fairs, concerts, and festivals, in facilities that are managed and operated by others.  Theatrical (except motion picture) booking agencies are included in this industry.",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Promoters of Performing Arts, Sports, and Similar Events without Facilities",
    slug: "promoters-of-performing-arts-sports-and-similar-events-without-facilities"
  },
  "711410": {
    description: "This industry comprises establishments of agents and managers primarily engaged in representing and/or managing creative and performing artists, sports figures, entertainers, and other public figures.  The representation and management includes activities, such as representing clients in contract negotiations; managing or organizing clients' financial affairs; and generally promoting the careers of their clients. Illustrative Examples: Celebrities' agents or managers, Sports figures' agents or managers, Literary agents, Talent agents, Modeling agents, Cross-References.",
    industries: ["performing-arts-and-spectator-sports"],
    name: "Agents and Managers for Artists, Athletes, Entertainers, and Other Public Figures",
    slug: "agents-and-managers-for-artists-athletes-entertainers-and-other-public-figures"
  },
  "711510": {
    description: "This industry comprises independent (i.e., freelance) individuals primarily engaged in performing in artistic productions, in creating artistic and cultural works or productions, or in providing technical expertise necessary for these productions.  This industry also includes athletes and other celebrities exclusively engaged in endorsing products and making speeches or public appearances for which they receive a fee. Illustrative Examples: Independent actors or actresses, Independent producers, Independent art restorers, Independent recording technicians, Independent artists (except musical, commercial, medical), Independent speakers , Independent cartoonists, Independent theatrical costume designers, Independent dancers, Independent theatrical lighting technicians, Independent journalists, Independent technical writers, Cross-References.",
    industries: ["artists-and-writers"],
    name: "Independent Artists, Writers, and Performers",
    slug: "independent-artists-writers-and-performers"
  },
  "712110": {
    description: "This industry comprises establishments primarily engaged in the preservation and exhibition of objects of historical, cultural, and/or educational value. Illustrative Examples: Art galleries (except retail), Planetariums, Art museums, Science or technology museums, Halls of fame, Wax museums, Cross-References.",
    industries: ["museums"],
    name: "Museums",
    slug: "museums"
  },
  "712120": {
    description: "This industry comprises establishments primarily engaged in the preservation and exhibition of sites, buildings, forts, or communities that describe events or persons of particular historical interest.  Archeological sites, battlefields, historical ships, and pioneer villages are included in this industry.",
    industries: ["historical-sites"],
    name: "Historical Sites",
    slug: "historical-sites"
  },
  "712130": {
    description: "This industry comprises establishments primarily engaged in the preservation and exhibition of live plant and animal life displays. Illustrative Examples: Aquariums, Wild animal parks, Arboreta, Zoological gardens, Aviaries, Cross-References.",
    industries: ["zoos-and-botanical-gardens"],
    name: "Zoos and Botanical Gardens",
    slug: "zoos-and-botanical-gardens"
  },
  "712190": {
    description: "This industry comprises establishments primarily engaged in the preservation and exhibition of natural areas or settings. Illustrative Examples: Bird or wildlife sanctuaries, Natural wonder tourist attractions (e.g., caverns, waterfalls), Conservation areas, Nature centers or preserves, National parks, Cross-References.",
    industries: ["museums-historical-sites-and-zoos"],
    name: "Nature Parks and Other Similar Institutions",
    slug: "nature-parks-and-other-similar-institutions"
  },
  "713110": {
    description: "This industry comprises establishments, known as amusement or theme parks, primarily engaged in operating a variety of attractions, such as mechanical rides, water rides, games, shows, theme exhibits, refreshment stands, and picnic grounds.  These establishments may lease space to others on a concession basis.",
    industries: ["amusement-parks-and-arcades"],
    name: "Amusement and Theme Parks",
    slug: "amusement-and-theme-parks"
  },
  "713120": {
    description: "This industry comprises establishments primarily engaged in operating amusement (except gambling, billiard, or pool) arcades and parlors.",
    industries: ["amusement-parks-and-arcades"],
    name: "Amusement Arcades",
    slug: "amusement-arcades"
  },
  "713210": {
    description: "This industry comprises establishments primarily engaged in operating gambling facilities that offer table wagering games along with other gambling activities, such as slot machines and sports betting.  These establishments often provide food and beverage services.  Included in this industry are floating casinos (i.e., gambling cruises, riverboat casinos) and casinos with racetracks.",
    industries: ["gambling-facilities-and-casinos"],
    name: "Casinos (except Casino Hotels)",
    slug: "casinos-except-casino-hotels"
  },
  "713290": {
    description: "This industry comprises establishments primarily engaged in operating gambling facilities (except casinos or casino hotels) or providing gambling services. Illustrative Examples: Bingo, off-track betting, or slot machine parlors, Coin- or card-operated gambling device concession operators (i.e., supplying and servicing in others' facilities), Bookmakers, Lottery ticket sales agents (except retail stores), Card rooms (e.g., poker rooms).",
    industries: ["gambling-facilities-and-casinos"],
    name: "Other Gambling Industries",
    slug: "other-gambling-industries"
  },
  "713910": {
    description: "This industry comprises (1) establishments primarily engaged in operating golf courses (except miniature) and (2) establishments primarily engaged in operating golf courses, along with dining facilities and other recreational facilities that are known as country clubs.  These establishments often provide food and beverage services, equipment rental services, and golf instruction services.",
    industries: ["golf-courses-and-country-clubs"],
    name: "Golf Courses and Country Clubs",
    slug: "golf-courses-and-country-clubs"
  },
  "713920": {
    description: "This industry comprises establishments engaged in (1) operating downhill, cross country, or related skiing areas and/or (2) operating equipment, such as ski lifts and tows.  These establishments often provide food and beverage services, equipment rental services, and ski instruction services.  Four season resorts without accommodations are included in this industry. Cross-References.",
    industries: ["skiing-facilities"],
    name: "Skiing Facilities",
    slug: "skiing-facilities"
  },
  "713930": {
    description: "This industry comprises establishments, commonly known as marinas, engaged in operating docking and/or storage facilities for pleasure craft owners, with or without one or more related activities, such as retailing fuel and marine supplies; and repairing, maintaining, or renting pleasure boats.",
    industries: ["recreational-facilities"],
    name: "Marinas",
    slug: "marinas"
  },
  "713940": {
    description: "This industry comprises establishments primarily engaged in operating fitness and recreational sports facilities featuring exercise and other active physical fitness conditioning or recreational sports activities, such as swimming, skating, or racquet sports. Illustrative Examples: Aerobic dance or exercise centers, Ice or roller skating rinks, Gymnasiums, Physical fitness centers, Handball, racquetball, or tennis club facilities, Swimming or wave pools, Cross-References.",
    industries: ["wellness-and-fitness-services"],
    name: "Fitness and Recreational Sports Centers",
    slug: "fitness-and-recreational-sports-centers"
  },
  "713950": {
    description: "This industry comprises establishments engaged in operating bowling centers.  These establishments often provide food and beverage services.",
    industries: ["recreational-facilities"],
    name: "Bowling Centers",
    slug: "bowling-centers"
  },
  "713990": {
    description: "This industry comprises establishments (except amusement parks and arcades; gambling industries; golf courses and country clubs; skiing facilities; marinas; fitness and recreational sports centers; and bowling centers) primarily engaged in providing recreational and amusement services. Illustrative Examples: Amusement ride or coin- or card-operated nongambling amusement device concession operators (i.e., supplying and servicing in others' facilities), Outdoor adventure operations (e.g., white water rafting) without accommodations, Miniature golf courses, Golf driving ranges, Archery or shooting ranges, Recreational day camps (except instructional), Billiard or pool parlors, Recreational chess or bridge clubs, Boating clubs (without marinas), Recreational or youth sports clubs (i.e., sports teams) not operating sports facilities, Escape rooms, Riding stables (except boarding horses) , Cross-References.",
    industries: ["recreational-facilities"],
    name: "All Other Amusement and Recreation Industries",
    slug: "all-other-amusement-and-recreation-industries"
  },
  "721110": {
    description: "This industry comprises establishments primarily engaged in providing short-term lodging in facilities known as hotels, motor hotels, resort hotels, and motels.  The establishments in this industry may offer food and beverage services, recreational services, conference rooms, convention services, laundry services, parking, and other services.",
    industries: ["hotels-and-motels"],
    name: "Hotels (except Casino Hotels) and Motels",
    slug: "hotels-except-casino-hotels-and-motels"
  },
  "721120": {
    description: "This industry comprises establishments primarily engaged in providing short-term lodging in hotel facilities with a casino on the premises.  The casino on premises includes table wagering games and may include other gambling activities, such as slot machines and sports betting.  These establishments generally offer a range of services and amenities, such as food and beverage services, entertainment, valet parking, swimming pools, and conference and convention facilities.  Included in this industry are casino hotels with racetracks.",
    industries: ["hospitality"],
    name: "Casino Hotels",
    slug: "casino-hotels"
  },
  "721191": {
    description: "This U.S. industry comprises establishments primarily engaged in providing short-term lodging in facilities known as bed-and-breakfast inns.  These establishments provide short-term lodging in private homes or small buildings converted for this purpose.  Bed-and-breakfast inns are characterized by a highly personalized service and inclusion of a full breakfast in the room rate.",
    industries: ["bed-and-breakfasts-hostels-homestays"],
    name: "Bed-and-Breakfast Inns",
    slug: "bed-and-breakfast-inns"
  },
  "721199": {
    description: "This U.S. industry comprises establishments primarily engaged in providing short-term lodging (except hotels, motels, casino hotels, and bed-and-breakfast inns). Illustrative Examples: Guest houses, Tourist homes, Housekeeping cabins and cottages, Hostels.",
    industries: ["bed-and-breakfasts-hostels-homestays"],
    name: "All Other Traveler Accommodation",
    slug: "all-other-traveler-accommodation"
  },
  "721211": {
    description: "This U.S. industry comprises establishments primarily engaged in operating sites to accommodate campers and their equipment, including tents, tent trailers, travel trailers, and RVs (recreational vehicles).  These establishments may provide access to facilities, such as washrooms, laundry rooms, recreation halls, playgrounds, stores, and snack bars.",
    industries: ["accommodation-services"],
    name: "RV (Recreational Vehicle) Parks and Campgrounds",
    slug: "rv-recreational-vehicle-parks-and-campgrounds"
  },
  "721214": {
    description: "This U.S. industry comprises establishments primarily engaged in operating overnight recreational camps, such as children's camps, family vacation camps, hunting and fishing camps, and outdoor adventure retreats, that offer trail riding, white water rafting, hiking, and similar activities.  These establishments provide accommodation facilities, such as cabins and fixed campsites, and other amenities, such as food services, recreational facilities and equipment, and organized recreational activities. Illustrative Examples: Fishing camps with accommodation facilities, Dude ranches, Vacation camps (except campgrounds, day, instructional), Hunting camps with accommodation facilities, Wilderness camps, Outdoor adventure retreats with accommodation facilities .",
    industries: ["accommodation-services"],
    name: "Recreational and Vacation Camps (except Campgrounds)",
    slug: "recreational-and-vacation-camps-except-campgrounds"
  },
  "721310": {
    description: "This industry comprises establishments primarily engaged in operating rooming and boarding houses and similar facilities, such as fraternity houses, sorority houses, off campus dormitories, residential clubs, and workers' camps.  These establishments provide temporary or longer-term accommodations, which, for the period of occupancy, may serve as a principal residence.  These establishments also may provide complementary services, such as housekeeping, meals, and laundry services. Illustrative Examples: Dormitories (off campus), Sorority houses, Fraternity houses, Workers' camps, Rooming houses",
    industries: ["accommodation-services"],
    name: "Rooming and Boarding Houses, Dormitories, and Workers' Camps",
    slug: "rooming-and-boarding-houses-dormitories-and-workers-camps"
  },
  "722310": {
    description: "This industry comprises establishments primarily engaged in providing food services at institutional, governmental, commercial, or industrial locations of others based on contractual arrangements with these types of organizations for a specified period of time.  The establishments of this industry provide food services for the convenience of the contracting organization or the contracting organization's customers.  The contractual arrangement of these establishments with contracting organizations may vary by type of facility operated (e.g., cafeteria, restaurant, fast-food eating place), revenue sharing, cost structure, and personnel provided.  Management staff is always provided by food service contractors. Illustrative Examples: Airline food service contractors, Food concession contractors (e.g., at sporting, entertainment, convention facilities), Cafeteria food service contractors (e.g., at schools, hospitals, government offices).",
    industries: ["food-and-beverage-services"],
    name: "Food Service Contractors",
    slug: "food-service-contractors"
  },
  "722320": {
    description: "This industry comprises establishments primarily engaged in providing single event-based food services.  These establishments generally have equipment and vehicles to transport meals and snacks to events and/or prepare food at an off-premise site.  Banquet halls with catering staff are included in this industry.  Examples of events catered by establishments in this industry are graduation parties, wedding receptions, business or retirement luncheons, and trade shows.",
    industries: ["caterers"],
    name: "Caterers",
    slug: "caterers"
  },
  "722330": {
    description: "This industry comprises establishments primarily engaged in preparing and serving meals and snacks for immediate consumption from motorized vehicles or nonmotorized carts.  The establishment is the central location from which the caterer route is serviced, not each vehicle or cart.  Included in this industry are establishments primarily engaged in providing food services from vehicles, such as hot dog carts and ice cream trucks. Illustrative Examples: Ice cream truck vendors, Mobile food concession stands, Mobile canteens, Mobile refreshment stands, Mobile food carts and trucks, Mobile snack stands.",
    industries: ["mobile-food-services"],
    name: "Mobile Food Services",
    slug: "mobile-food-services"
  },
  "722410": {
    description: "This industry comprises establishments known as bars, taverns, nightclubs, or drinking places primarily engaged in preparing and serving alcoholic beverages for immediate consumption.  These establishments may also provide limited food services.",
    industries: ["bars-taverns-and-nightclubs"],
    name: "Drinking Places (Alcoholic Beverages)",
    slug: "drinking-places-alcoholic-beverages"
  },
  "722511": {
    description: "This U.S. industry comprises establishments primarily engaged in providing food services to patrons who order and are served while seated (i.e., waiter/waitress service) and pay after eating.  These establishments may provide this type of food service to patrons in combination with selling alcoholic beverages, providing carryout services, or presenting live nontheatrical entertainment.",
    industries: ["restaurants"],
    name: "Full-Service Restaurants",
    slug: "full-service-restaurants"
  },
  "722513": {
    description: "This U.S. industry comprises establishments primarily engaged in providing food services (except snack and nonalcoholic beverage bars) where patrons generally order or select items and pay before eating.  Food and drink may be consumed on premises, taken out, or delivered to the customer's location.  Some establishments in this industry may provide these food services in combination with selling alcoholic beverages. Illustrative Examples: Delicatessen restaurants, Pizza delivery shops, Family restaurants, limited-service, Takeout eating places, Fast-food restaurants, Fast casual restaurants, Takeout sandwich shops, Limited-service pizza parlors.",
    industries: ["restaurants"],
    name: "Limited-Service Restaurants",
    slug: "limited-service-restaurants"
  },
  "722514": {
    description: "This U.S. industry comprises establishments, known as cafeterias, grill buffets, or buffets, primarily engaged in preparing and serving meals for immediate consumption using cafeteria-style or buffet serving equipment, such as steam tables, refrigerated areas, display grills, and self-service nonalcoholic beverage dispensing equipment.  Patrons select from food and drink items on display in a continuous cafeteria line or from buffet stations.",
    industries: ["restaurants"],
    name: "Cafeterias, Grill Buffets, and Buffets",
    slug: "cafeterias-grill-buffets-and-buffets"
  },
  "722515": {
    description: "This U.S. industry comprises establishments primarily engaged in (1) preparing and/or serving a specialty snack, such as ice cream, frozen yogurt, cookies, or popcorn, or (2) serving nonalcoholic beverages, such as coffee, juices, or sodas for consumption on or near the premises.  These establishments may carry and sell a combination of snack, nonalcoholic beverage, and other related products (e.g., coffee beans, mugs, coffee makers) but generally promote and sell a unique snack or nonalcoholic beverage. Illustrative Examples: Beverage bars, nonalcoholic, fixed location, Doughnut shops, on premise baking and carryout service, Bagel shops, on premise baking and carryout service, Pretzel shops, on premise baking and carryout service, Cookie shops, on premise baking and carryout service, Coffee shops, on premise brewing, Ice cream parlors, Juice bars, nonalcoholic, fixed location.",
    industries: ["restaurants"],
    name: "Snack and Nonalcoholic Beverage Bars",
    slug: "snack-and-nonalcoholic-beverage-bars"
  },
  "811111": {
    description: "This U.S. industry comprises establishments primarily engaged in providing (1) a wide range of mechanical and electrical repair and maintenance services for automotive vehicles, such as passenger cars, trucks, and vans, and all trailers or (2) engine repair and replacement. Illustrative Examples: Automobile repair garages (except gasoline service stations), General automotive repair shops, Automotive engine repair and replacement shops.",
    industries: ["vehicle-repair-and-maintenance"],
    name: "General Automotive Repair",
    slug: "general-automotive-repair"
  },
  "811114": {
    description: "This U.S. industry comprises establishments primarily engaged in providing specialized mechanical or electrical repair and maintenance services (except engine repair and replacement) for automotive vehicles, such as passenger cars, trucks, and vans, and all trailers. Illustrative Examples: Automotive brake repair shops, Automotive suspension repair shops, Automotive exhaust system replacement and repair shops, Automotive electrical repair shops, Automotive transmission repair shops, Automotive tune-up shops.",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Specialized Automotive Repair",
    slug: "specialized-automotive-repair"
  },
  "811121": {
    description: "This U.S. industry comprises establishments primarily engaged in repairing or customizing automotive vehicle and trailer bodies and interiors; and/or painting automotive vehicles and trailer bodies. Illustrative Examples: Automotive body shops, Automotive body conversion services, Restoration shops, antique and classic automotive, Automotive paint shops .",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Automotive Body, Paint, and Interior Repair and Maintenance",
    slug: "automotive-body-paint-and-interior-repair-and-maintenance"
  },
  "811122": {
    description: "This U.S. industry comprises establishments primarily engaged in replacing, repairing, and/or tinting automotive vehicle glass, such as passenger car, truck, and van glass. Cross-References.",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Automotive Glass Replacement Shops",
    slug: "automotive-glass-replacement-shops"
  },
  "811191": {
    description: "This U.S. industry comprises establishments primarily engaged in changing motor oil and lubricating the chassis of automotive vehicles, such as passenger cars, trucks, and vans. Cross-References.",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Automotive Oil Change and Lubrication Shops",
    slug: "automotive-oil-change-and-lubrication-shops"
  },
  "811192": {
    description: "This U.S. industry comprises establishments primarily engaged in cleaning, washing, and/or waxing automotive vehicles, such as passenger cars, trucks, and vans, and trailers. Illustrative Examples: Automotive detail shops, Mobile car and truck washes, Car washes, Self-service car washes",
    industries: ["vehicle-repair-and-maintenance"],
    name: "Car Washes",
    slug: "car-washes"
  },
  "811198": {
    description: "This U.S. industry comprises establishments primarily engaged in providing automotive repair and maintenance services (except mechanical and electrical repair and maintenance; body, paint, interior, and glass repair; motor oil change and lubrication; and car washing) for automotive vehicles, such as passenger cars, trucks, and vans, and all trailers. Illustrative Examples: Automotive air-conditioning repair shops, Automotive tire repair (except retreading) shops, Automotive rustproofing and undercoating shops.",
    industries: ["vehicle-repair-and-maintenance"],
    name: "All Other Automotive Repair and Maintenance",
    slug: "all-other-automotive-repair-and-maintenance"
  },
  "811210": {
    description: "This industry comprises establishments primarily engaged in repairing and maintaining one or more of the following: (1) consumer electronic equipment; (2) computers; (3) office machines; (4) communication equipment; and (5) other electronic and precision equipment and instruments, without retailing these products as new.  Establishments in this industry repair items, such as microscopes, radar and sonar equipment, televisions, stereos, video recorders, computers, fax machines, photocopying machines, two-way radios, cellular telephones, and other communications equipment, scientific instruments, and medical equipment.",
    industries: ["electronic-and-precision-equipment-maintenance"],
    name: "Electronic and Precision Equipment Repair and Maintenance",
    slug: "electronic-and-precision-equipment-repair-and-maintenance"
  },
  "811310": {
    description: "This industry comprises establishments primarily engaged in the repair and maintenance of commercial and industrial machinery and equipment.  Establishments in this industry either sharpen/install commercial and industrial machinery blades and saws or provide welding (e.g., automotive, general) repair services; or repair agricultural and other heavy and industrial machinery and equipment (e.g., forklifts and other material handling equipment, machine tools, commercial refrigeration equipment, construction equipment, and mining machinery).",
    industries: ["commercial-and-industrial-machinery-maintenance"],
    name: "Commercial and Industrial Machinery and Equipment (except Automotive and Electronic) Repair and Maintenance",
    slug: "commercial-and-industrial-machinery-and-equipment-except-automotive-and-electronic-repair-and-maintenance"
  },
  "811411": {
    description: "This U.S. industry comprises establishments primarily engaged in repairing and servicing home and garden equipment without retailing new home and garden equipment, such as lawnmowers, handheld power tools, edgers, snowblowers, leaf blowers, and trimmers. Cross-References.",
    industries: ["repair-and-maintenance"],
    name: "Home and Garden Equipment Repair and Maintenance",
    slug: "home-and-garden-equipment-repair-and-maintenance"
  },
  "811412": {
    description: "This U.S. industry comprises establishments primarily engaged in repairing and servicing household appliances without retailing new appliances, such as refrigerators, stoves, washing machines, clothes dryers, and room air-conditioners.",
    industries: ["repair-and-maintenance"],
    name: "Appliance Repair and Maintenance",
    slug: "appliance-repair-and-maintenance"
  },
  "811420": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) reupholstering furniture; (2) refinishing furniture; (3) repairing furniture; and (4) repairing and restoring furniture.",
    industries: ["reupholstery-and-furniture-repair"],
    name: "Reupholstery and Furniture Repair",
    slug: "reupholstery-and-furniture-repair"
  },
  "811430": {
    description: "This industry comprises establishments primarily engaged in repairing footwear and/or repairing other leather or leather-like goods without retailing new footwear and leather or leather-like goods, such as handbags and briefcases.",
    industries: ["footwear-and-leather-goods-repair"],
    name: "Footwear and Leather Goods Repair",
    slug: "footwear-and-leather-goods-repair"
  },
  "811490": {
    description: "This industry comprises establishments primarily engaged in repairing and servicing personal or household-type goods without retailing new personal or household-type goods (except home and garden equipment, appliances, furniture, and footwear and leather goods).  Establishments in this industry repair items, such as garments; watches; jewelry; musical instruments; bicycles and motorcycles; and motorboats, canoes, sailboats, and other recreational boats.",
    industries: ["repair-and-maintenance"],
    name: "Other Personal and Household Goods Repair and Maintenance",
    slug: "other-personal-and-household-goods-repair-and-maintenance"
  },
  "812111": {
    description: "This U.S. industry comprises establishments known as barber shops or men's hair stylist shops primarily engaged in cutting, trimming, and styling men's and boys' hair; and/or shaving and trimming men's beards.",
    industries: ["personal-care-services"],
    name: "Barber Shops",
    slug: "barber-shops"
  },
  "812112": {
    description: "This U.S. industry comprises establishments (except those known as barber shops or men's hair stylist shops) primarily engaged in one or more of the following: (1) cutting, trimming, shampooing, coloring, waving, or styling hair; (2) providing facials; and (3) applying makeup (except permanent makeup). Illustrative Examples: Beauty parlors or shops, Facial salons or shops, Combined beauty and barber shops, Hairdressing salons or shops, Cosmetology salons or shops, Unisex or women's hair stylist shops.",
    industries: ["personal-care-services"],
    name: "Beauty Salons",
    slug: "beauty-salons"
  },
  "812113": {
    description: "This U.S. industry comprises establishments primarily engaged in providing nail care services, such as manicures, pedicures, and nail extensions.",
    industries: ["personal-care-services"],
    name: "Nail Salons",
    slug: "nail-salons"
  },
  "812191": {
    description: "This U.S. industry comprises establishments primarily engaged in providing non-medical services to assist clients in attaining or maintaining a desired weight.  The sale of weight reduction products, such as food supplements, may be an integral component of the program.  These services typically include individual or group counseling, menu and exercise planning, and weight and body measurement monitoring.",
    industries: ["personal-care-services"],
    name: "Diet and Weight Reducing Centers",
    slug: "diet-and-weight-reducing-centers"
  },
  "812199": {
    description: "This U.S. industry comprises establishments primarily engaged in providing personal care services (except hair, nail, facial, nonpermanent makeup, or non-medical diet and weight reducing services). Illustrative Examples: Day spas, Depilatory or electrolysis (i.e., hair removal) salons, Saunas, Ear piercing services, Steam baths, Hair replacement (except by offices of physicians) or weaving services, Tanning salons, Tattoo parlors, Permanent makeup salons.",
    industries: ["personal-care-services"],
    name: "Other Personal Care Services",
    slug: "other-personal-care-services"
  },
  "812210": {
    description: "This industry comprises establishments primarily engaged in preparing the dead for burial or interment and conducting funerals (i.e., providing facilities for wakes, arranging transportation for the dead, selling caskets and related merchandise).  Funeral homes combined with crematories are included in this industry. Cross-References.",
    industries: ["personal-and-laundry-services"],
    name: "Funeral Homes and Funeral Services",
    slug: "funeral-homes-and-funeral-services"
  },
  "812220": {
    description: "This industry comprises establishments primarily engaged in operating sites or structures reserved for the interment of human or animal remains and/or cremating the dead. Illustrative Examples: Cemetery associations (i.e., operators of cemeteries), Memorial gardens (i.e., burial places), Pet cemeteries, Crematories (except combined with funeral homes), Mausoleums , Cross-References.",
    industries: ["personal-and-laundry-services"],
    name: "Cemeteries and Crematories",
    slug: "cemeteries-and-crematories"
  },
  "812310": {
    description: "This industry comprises establishments primarily engaged in (1) operating facilities with coin- or card-operated or similar self-service laundry and drycleaning equipment for customer use on the premises and/or (2) supplying and servicing coin- or card-operated or similar self-service laundry and drycleaning equipment for customer use in places of business operated by others, such as apartments and dormitories.",
    industries: ["laundry-and-drycleaning-services"],
    name: "Coin-Operated Laundries and Drycleaners",
    slug: "coin-operated-laundries-and-drycleaners"
  },
  "812320": {
    description: "This industry comprises establishments primarily engaged in one or more of the following: (1) providing drycleaning services (except coin- or card-operated); (2) providing laundering services (except linen and uniform supply or coin- or card-operated); (3) providing drop-off and pick-up sites for laundries and/or drycleaners; and (4) providing specialty cleaning services for specific types of garments and other textile items (except carpets and upholstery), such as fur, leather, or suede garments; wedding gowns; hats; draperies; and pillows.  These establishments may provide all, a combination of, or none of the cleaning services on the premises.",
    industries: ["laundry-and-drycleaning-services"],
    name: "Drycleaning and Laundry Services (except Coin-Operated)",
    slug: "drycleaning-and-laundry-services-except-coin-operated"
  },
  "812331": {
    description: "This U.S. industry comprises establishments primarily engaged in supplying, on a rental or contract basis, laundered items, such as table and bed linens; towels; diapers; and uniforms, gowns, or coats of the type used by doctors, nurses, barbers, beauticians, and waitresses. Cross-References.",
    industries: ["laundry-and-drycleaning-services"],
    name: "Linen Supply",
    slug: "linen-supply"
  },
  "812332": {
    description: "This U.S. industry comprises establishments primarily engaged in supplying, on a rental or contract basis, laundered industrial work uniforms and related work clothing, such as protective apparel (flame and heat resistant) and clean room apparel; dust control items, such as treated mops, rugs, mats, dust tool covers, cloths, and shop or wiping towels. Cross-References.",
    industries: ["laundry-and-drycleaning-services"],
    name: "Industrial Launderers",
    slug: "industrial-launderers"
  },
  "812910": {
    description: "This industry comprises establishments primarily engaged in providing pet care services (except veterinary), such as boarding, grooming, sitting, walking, and training pets.",
    industries: ["pet-services"],
    name: "Pet Care (except Veterinary) Services",
    slug: "pet-care-except-veterinary-services"
  },
  "812921": {
    description: 'This U.S. industry comprises establishments (except those known as "one-hour" photofinishing labs) primarily engaged in developing film and/or making photographic slides, prints, and enlargements. Cross-References.',
    industries: ["personal-and-laundry-services"],
    name: "Photofinishing Laboratories (except One-Hour)",
    slug: "photofinishing-laboratories-except-one-hour"
  },
  "812922": {
    description: 'This U.S. industry comprises establishments known as "one-hour" photofinishing labs primarily engaged in developing film and/or making photographic slides, prints, and enlargements on a short turnaround or while-you-wait basis. Cross-References.',
    industries: ["personal-and-laundry-services"],
    name: "One-Hour Photofinishing",
    slug: "one-hour-photofinishing"
  },
  "812930": {
    description: "This industry comprises establishments primarily engaged in providing parking space for motor vehicles, usually on an hourly, daily, or monthly basis and/or valet parking services. Cross-References.",
    industries: ["personal-and-laundry-services"],
    name: "Parking Lots and Garages",
    slug: "parking-lots-and-garages"
  },
  "812990": {
    description: "This industry comprises establishments primarily engaged in providing personal services (except personal care services, death care services, drycleaning and laundry services, pet care services, photofinishing services, or parking space and/or valet parking services). Illustrative Examples: Bail bonding or bondsperson services, Shoeshine services, Coin- or card-operated personal services machine (e.g., blood pressure, locker, photographic, scale, shoeshine) concession operators, Social escort services, Consumer buying services, Wedding planning services, Dating services, Personal fitness training services.",
    industries: ["personal-and-laundry-services"],
    name: "All Other Personal Services",
    slug: "all-other-personal-services"
  },
  "813110": {
    description: "This industry comprises (1) establishments primarily engaged in operating religious organizations, such as churches, religious temples, mosques, and monasteries, and/or (2) establishments primarily engaged in administering an organized religion or promoting religious activities. Illustrative Examples: Churches, Shrines, religious, Monasteries (except schools), Synagogues, Mosques, religious, Temples, religious, Cross-References.",
    industries: ["religious-institutions"],
    name: "Religious Organizations",
    slug: "religious-organizations"
  },
  "813211": {
    description: "This U.S. industry comprises establishments known as grantmaking foundations or charitable trusts.  Establishments in this industry award grants from trust funds based on a competitive selection process or the preferences of the foundation managers and grantors; or fund a single entity, such as a museum or university. Illustrative Examples: Community foundations, Philanthropic trusts, Corporate foundations, awarding grants, Scholarship trusts, Grantmaking foundations, Cross-References.",
    industries: ["philanthropic-fundraising-services"],
    name: "Grantmaking Foundations",
    slug: "grantmaking-foundations"
  },
  "813212": {
    description: "This U.S. industry comprises establishments primarily engaged in raising funds for health related research, such as disease (e.g., heart, cancer, diabetes) prevention, health education, and patient services. Illustrative Examples: Disease awareness fundraising organizations, Health research fundraising organizations, Voluntary health organizations, Disease research (e.g., heart, cancer) fundraising organizations, Cross-References.",
    industries: ["philanthropic-fundraising-services"],
    name: "Voluntary Health Organizations",
    slug: "voluntary-health-organizations"
  },
  "813219": {
    description: "This U.S. industry comprises establishments (except voluntary health organizations) primarily engaged in raising funds for a wide range of social welfare activities, such as educational, scientific, cultural, and health. Illustrative Examples: Community chest fundraising organizations, United fund councils, Federated charities, United funds for colleges, Cross-References.",
    industries: ["philanthropic-fundraising-services"],
    name: "Other Grantmaking and Giving Services",
    slug: "other-grantmaking-and-giving-services"
  },
  "813311": {
    description: "This U.S. industry comprises establishments primarily engaged in promoting causes associated with human rights either for a broad or specific constituency.  Establishments in this industry address issues, such as protecting and promoting the broad constitutional rights and civil liberties of individuals and those suffering from neglect, abuse, or exploitation; promoting the interests of specific groups, such as children, women, senior citizens, or persons with disabilities; improving relations between racial, ethnic, and cultural groups; and promoting voter education and registration.  These organizations may solicit contributions and offer memberships to support these causes. Illustrative Examples: Civil liberties organizations, Senior citizens' advocacy organizations, Human rights advocacy organizations, Veterans' rights organizations.",
    industries: ["consumer-services"],
    name: "Human Rights Organizations",
    slug: "human-rights-organizations"
  },
  "813312": {
    description: "This U.S. industry comprises establishments primarily engaged in promoting the preservation and protection of the environment and wildlife.  Establishments in this industry address issues, such as clean air and water; global warming; conserving and developing natural resources, including land, plant, water, and energy resources; and protecting and preserving wildlife and endangered species.  These organizations may solicit contributions and offer memberships to support these causes. Illustrative Examples: Animal rights organizations, Natural resource preservation organizations, Conservation advocacy organizations, Wildlife preservation organizations, Humane societies without animal shelters, Cross-References.",
    industries: ["consumer-services"],
    name: "Environment, Conservation and Wildlife Organizations",
    slug: "environment-conservation-and-wildlife-organizations"
  },
  "813319": {
    description: "This U.S. industry comprises establishments primarily engaged in social advocacy (except human rights and environmental protection, conservation, and wildlife preservation).  Establishments in this industry address issues, such as peace and international understanding; community action (excluding civic organizations); or advancing social causes, such as firearms safety, drunk driving prevention, or drug abuse awareness.  These organizations may solicit contributions and offer memberships to support these causes. Illustrative Examples: Community action advocacy organizations, Substance abuse prevention advocacy organizations, Firearms advocacy organizations, Taxpayers' advocacy organizations, Peace advocacy organizations.",
    industries: ["consumer-services"],
    name: "Other Social Advocacy Organizations",
    slug: "other-social-advocacy-organizations"
  },
  "813410": {
    description: "This industry comprises establishments primarily engaged in promoting the civic and social interests of their members.  Establishments in this industry may operate bars and restaurants for their members. Illustrative Examples: Alumni associations, Granges, Automobile clubs (except travel), Parent-teacher associations, Booster clubs, Scouting organizations, Ethnic associations, Social clubs, Fraternal lodges, Veterans' membership organizations, Cross-References.",
    industries: ["civic-and-social-organizations"],
    name: "Civic and Social Organizations",
    slug: "civic-and-social-organizations"
  },
  "813910": {
    description: "This industry comprises establishments primarily engaged in promoting the business interests of their members.  These establishments may conduct research on new products and services; develop market statistics; sponsor quality and certification standards; lobby public officials; or publish newsletters, books, or periodicals for distribution to their members. Illustrative Examples: Agricultural organizations (except youth farming organizations, farm granges), Real estate boards, Chambers of commerce, Trade associations, Manufacturers' associations, Cross-References.",
    industries: ["industry-associations"],
    name: "Business Associations",
    slug: "business-associations"
  },
  "813920": {
    description: "This industry comprises establishments primarily engaged in promoting the professional interests of their members and the profession as a whole.  These establishments may conduct research; develop statistics; sponsor quality and certification standards; lobby public officials; or publish newsletters, books, or periodicals for distribution to their members. Illustrative Examples: Bar associations, Learned societies, Dentists' associations, Peer review boards, Engineers' associations, Professional standards review boards, Health professionals' associations, Scientists' associations.",
    industries: ["professional-organizations"],
    name: "Professional Organizations",
    slug: "professional-organizations"
  },
  "813930": {
    description: "This industry comprises establishments primarily engaged in promoting the interests of organized labor and union employees. Cross-References.",
    industries: ["political-organizations"],
    name: "Labor Unions and Similar Labor Organizations",
    slug: "labor-unions-and-similar-labor-organizations"
  },
  "813940": {
    description: "This industry comprises establishments primarily engaged in promoting the interests of national, state, or local political parties or candidates.  Included are political groups organized to raise funds for a political party or individual candidates. Illustrative Examples: Campaign organizations, political, Political organizations or clubs, Political action committees (PACs), Political parties, Political campaign organizations.",
    industries: ["political-organizations"],
    name: "Political Organizations",
    slug: "political-organizations"
  },
  "813990": {
    description: "This industry comprises establishments (except religious organizations, social advocacy organizations, civic and social organizations, business associations, professional organizations, labor unions, and political organizations) primarily engaged in promoting the interests of their members. Illustrative Examples: Athletic associations and leagues, regulatory, Property owners' associations, Condominium and homeowners' associations, Tenants' associations (except advocacy), Cooperative owners' associations.",
    industries: ["political-organizations"],
    name: "Other Similar Organizations (except Business, Professional, Labor, and Political Organizations)",
    slug: "other-similar-organizations-except-business-professional-labor-and-political-organizations"
  },
  "814110": {
    description: "This industry comprises private households primarily engaged in employing workers on or about the premises in activities primarily concerned with the operation of the household.  These private households may employ individuals, such as cooks, maids, nannies, butlers, non-medical personal care aides, and outside workers, such as gardeners, caretakers, and other maintenance workers.",
    industries: ["household-services"],
    name: "Private Households",
    slug: "private-households"
  },
  "921110": {
    description: "This industry comprises government establishments serving as offices of chief executives and their advisory committees and commissions.  This industry includes offices of the president, governors, and mayors, in addition to executive advisory commissions.",
    industries: ["executive-offices"],
    name: "Executive Offices",
    slug: "executive-offices"
  },
  "921120": {
    description: "This industry comprises government establishments serving as legislative bodies and their advisory committees and commissions.  Included in this industry are legislative bodies, such as Congress, state legislatures, and advisory and study legislative commissions.",
    industries: ["legislative-offices"],
    name: "Legislative Bodies",
    slug: "legislative-bodies"
  },
  "921130": {
    description: "This industry comprises government establishments primarily engaged in public finance, taxation, and monetary policy.  Included are financial administration activities, such as monetary policy; tax administration and collection; custody and disbursement of funds; debt and investment administration; auditing activities; and government employee retirement trust fund administration.",
    industries: ["public-policy-offices"],
    name: "Public Finance Activities",
    slug: "public-finance-activities"
  },
  "921140": {
    description: "This industry comprises government establishments serving as councils and boards of commissioners or supervisors and such bodies where the chief executive (e.g., county executive or city mayor) is a member of the legislative body (e.g., county or city council) itself. Cross-References. Government establishments primarily engaged in--",
    industries: ["public-policy-offices"],
    name: "Executive and Legislative Offices, Combined",
    slug: "executive-and-legislative-offices-combined"
  },
  "921150": {
    description: "This industry comprises American Indian and Alaska Native governing bodies.  Establishments in this industry perform legislative, judicial, and administrative functions for their American Indian and Alaska Native lands.  Included in this industry are American Indian and Alaska Native councils, courts, and law enforcement bodies. Cross-References.",
    industries: ["public-policy-offices"],
    name: "American Indian and Alaska Native Tribal Governments",
    slug: "american-indian-and-alaska-native-tribal-governments"
  },
  "921190": {
    description: "This industry comprises government establishments primarily engaged in providing general support for government.  Such support services include personnel services, election boards, and other general government support establishments that are not classified elsewhere in public administration. Illustrative Examples: Civil rights commissions, Personnel offices, government, Civil service commissions, Supply agencies, government, General services departments, government, Cross-References.",
    industries: ["public-policy-offices"],
    name: "Other General Government Support",
    slug: "other-general-government-support"
  },
  "922110": {
    description: "This industry comprises civilian courts of law (except American Indian and Alaska Native tribal courts).  Included in this industry are civilian courts, courts of law, and sheriffs' offices conducting court functions only. Cross-References.",
    industries: ["courts-of-law"],
    name: "Courts",
    slug: "courts"
  },
  "922120": {
    description: "This industry comprises government establishments primarily engaged in criminal and civil law enforcement, police, traffic safety, and other activities related to the enforcement of the law and preservation of order.  Combined police and fire departments are included in this industry. Cross-References.",
    industries: ["law-enforcement"],
    name: "Police Protection",
    slug: "police-protection"
  },
  "922130": {
    description: "This industry comprises government establishments primarily engaged in providing legal counsel or prosecution services for the government. Illustrative Examples: Attorney generals' offices, Public defenders' offices, District attorneys' offices, Public prosecutors' offices, Cross-References.",
    industries: ["government-administration"],
    name: "Legal Counsel and Prosecution",
    slug: "legal-counsel-and-prosecution"
  },
  "922140": {
    description: "This industry comprises government establishments primarily engaged in managing and operating correctional institutions.  The facility is generally designed for the confinement, correction, and rehabilitation of adult and/or juvenile offenders sentenced by a court. Illustrative Examples: Correctional institutions, public administration, Penitentiaries, public administration, Detention centers, public administration, Prisons, public administration, Jails, public administration , Cross-References.",
    industries: ["correctional-institutions"],
    name: "Correctional Institutions",
    slug: "correctional-institutions"
  },
  "922150": {
    description: "This industry comprises government establishments primarily engaged in judicially administering probation offices, parole offices and boards, and pardon boards. Cross-References.",
    industries: ["government-administration"],
    name: "Parole Offices and Probation Offices",
    slug: "parole-offices-and-probation-offices"
  },
  "922160": {
    description: "This industry comprises government establishments primarily engaged in firefighting and other related fire protection activities.  Government establishments providing combined fire protection and ambulance or rescue services are classified in this industry.",
    industries: ["fire-protection"],
    name: "Fire Protection",
    slug: "fire-protection"
  },
  "922190": {
    description: "This industry comprises government establishments primarily engaged in public order and safety (except courts, police protection, legal counsel and prosecution, correctional institutions, parole offices, probation offices, pardon boards, and fire protection).  These establishments include the general administration of public order and safety programs.  Government establishments responsible for the collection of statistics on public safety are included in this industry. Illustrative Examples: Consumer product safety commissions, public administration, Emergency planning and management offices, government, Disaster preparedness and management offices, government, Public safety bureaus and statistics centers, government, Cross-References.",
    industries: ["public-safety"],
    name: "Other Justice, Public Order, and Safety Activities",
    slug: "other-justice-public-order-and-safety-activities"
  },
  "923110": {
    description: "This industry comprises government establishments primarily engaged in the central coordination, planning, supervision, and administration of funds, policies, intergovernmental activities, statistical reports and data collection, and centralized programs for educational administration.  Government scholarship programs are included in this industry. Illustrative Examples: Education offices, nonoperating, public administration, State education departments, Education statistics centers, government, University regents or boards, government, Cross-References.",
    industries: ["education-administration-programs"],
    name: "Administration of Education Programs",
    slug: "administration-of-education-programs"
  },
  "923120": {
    description: "This industry comprises government establishments primarily engaged in the planning, administration, and coordination of public health programs and services, including environmental health activities, mental health, categorical health programs, health statistics, and immunization services.  Government establishments primarily engaged in conducting public health-related inspections are included in this industry. Illustrative Examples: Communicable disease program administration, public administration, Mental health program administration, public administration, Coroners' offices, public administration, Public health program administration, nonoperating, public administration, Health program administration, public administration, Cross-References. Government establishments primarily engaged in--",
    industries: ["public-health"],
    name: "Administration of Public Health Programs",
    slug: "administration-of-public-health-programs"
  },
  "923130": {
    description: "This industry comprises government establishments primarily engaged in the planning, administration, and coordination of programs for public assistance, social work, and welfare activities.  The administration of Social Security, disability insurance, Medicare, unemployment insurance, and workers' compensation programs are included in this industry. Cross-References. Government establishments primarily engaged in--",
    industries: ["public-assistance-programs"],
    name: "Administration of Human Resource Programs (except Education, Public Health, and Veterans' Affairs Programs)",
    slug: "administration-of-human-resource-programs-except-education-public-health-and-veterans-affairs-programs"
  },
  "923140": {
    description: "This industry comprises government establishments primarily engaged in the administration of programs of assistance, training, counseling, and other services to veterans and their dependents, heirs, or survivors.  Included in this industry are Veterans' Affairs offices that maintain liaison and coordinate activities with other service organizations and governmental agencies. Cross-References.",
    industries: ["health-and-human-services"],
    name: "Administration of Veterans' Affairs",
    slug: "administration-of-veterans-affairs"
  },
  "924110": {
    description: "This industry comprises government establishments primarily engaged in one or more of the following: (1) the administration, regulation, and enforcement of air and water resource programs; (2) the administration and regulation of solid waste management programs; (3) the administration and regulation of water and air pollution control and prevention programs; (4) the administration and regulation of flood control programs; (5) the administration and regulation of drainage development and water resource consumption programs; (6) the administration and regulation of toxic waste removal and cleanup programs; and (7) coordination of these activities at intergovernmental levels. Illustrative Examples: Environmental protection program administration, public administration, Waste management program (except sanitation districts) administration, public administration, Pollution control program administration, public administration, Water control and quality program administration, public administration, Cross-References. Government establishments primarily engaged in--",
    industries: ["air-water-and-waste-program-management"],
    name: "Administration of Air and Water Resource and Solid Waste Management Programs",
    slug: "administration-of-air-and-water-resource-and-solid-waste-management-programs"
  },
  "924120": {
    description: "This industry comprises government establishments primarily engaged in the administration, regulation, supervision, and control of land use, including recreational areas; conservation and preservation of natural resources; erosion control; geological survey program administration; weather forecasting program administration; and the administration and protection of publicly and privately owned forest lands.  Government establishments responsible for planning, management, regulation, and conservation of game, fish, and wildlife populations, including wildlife management areas and field stations; and other administrative matters relating to the protection of fish, game, and wildlife are included in this industry. Cross-References. Government establishments primarily engaged in--",
    industries: ["conservation-programs"],
    name: "Administration of Conservation Programs",
    slug: "administration-of-conservation-programs"
  },
  "925110": {
    description: "This industry comprises government establishments primarily engaged in the administration and planning of housing programs. Cross-References. Government establishments primarily engaged in--",
    industries: ["housing-programs"],
    name: "Administration of Housing Programs",
    slug: "administration-of-housing-programs"
  },
  "925120": {
    description: "This industry comprises government establishments primarily engaged in the administration and planning of the development of urban and rural areas.  Included in this industry are government zoning boards and commissions. Illustrative Examples: Land redevelopment agencies, government, Regional planning and development program administration, public administration, Urban planning commissions, government, Zoning boards and commissions, public administration",
    industries: ["community-development-and-urban-planning"],
    name: "Administration of Urban Planning and Community and Rural Development",
    slug: "administration-of-urban-planning-and-community-and-rural-development"
  },
  "926110": {
    description: "This industry comprises government establishments primarily engaged in the administration, promotion, and development of economic resources, including business, industry, and tourism.  Included in this industry are government establishments responsible for the development of general statistical data and analyses and promotion of the general economic well-being of the governed area. Illustrative Examples: Consumer protection offices, public administration, Small business development agencies, public administration, Economic development agencies, government, Trade commissions, government, General economics statistical agencies, public administration, Cross-References. Government establishments primarily engaged in--",
    industries: ["economic-programs"],
    name: "Administration of General Economic Programs",
    slug: "administration-of-general-economic-programs"
  },
  "926120": {
    description: "This industry comprises government establishments primarily engaged in the administration, regulation, licensing, planning, inspection, and investigation of transportation services and facilities.  Included in this industry are government establishments responsible for motor vehicle and operator licensing, the Coast Guard (except the Coast Guard Academy), and parking authorities. Cross-References.",
    industries: ["transportation-programs"],
    name: "Regulation and Administration of Transportation Programs",
    slug: "regulation-and-administration-of-transportation-programs"
  },
  "926130": {
    description: "This industry comprises government establishments primarily engaged in the administration, regulation, licensing, and inspection of utilities, such as communications, electric power (including fossil, nuclear, solar, water, and wind), gas and water supply, and sewerage. Cross-References. Government establishments primarily engaged in--",
    industries: ["utilities-administration"],
    name: "Regulation and Administration of Communications, Electric, Gas, and Other Utilities",
    slug: "regulation-and-administration-of-communications-electric-gas-and-other-utilities"
  },
  "926140": {
    description: "This industry comprises government establishments primarily engaged in the planning, administration, and coordination of agricultural programs for production, marketing, and utilization, including educational and promotional activities.  Included in this industry are government establishments responsible for regulating and controlling the grading and inspection of food, plants, animals, and other agricultural products. Cross-References. Government establishments primarily engaged in--",
    industries: ["economic-programs"],
    name: "Regulation of Agricultural Marketing and Commodities",
    slug: "regulation-of-agricultural-marketing-and-commodities"
  },
  "926150": {
    description: "This industry comprises government establishments primarily engaged in the regulation, licensing, and inspection of commercial sectors, such as retail trade, professional occupations, manufacturing, mining, construction, and services.  Included in this industry are government establishments maintaining physical standards, regulating hazardous conditions not elsewhere classified, and enforcing alcoholic beverage control regulations. Illustrative Examples: Alcoholic beverage control boards, public administration, Labor management negotiations boards, government, Banking regulatory agencies, public administration, Licensing and permit issuance for business operations, government, Building inspections, government, Licensing and permit issuance for professional occupations, government, Insurance commissions, government, Securities regulation commissions, public administration, Cross-References.",
    industries: ["economic-programs"],
    name: "Regulation, Licensing, and Inspection of Miscellaneous Commercial Sectors",
    slug: "regulation-licensing-and-inspection-of-miscellaneous-commercial-sectors"
  },
  "927110": {
    description: "This industry comprises government establishments primarily engaged in the administration and operations of space flights, space research, and space exploration.  Included in this industry are government establishments operating space flight centers. Cross-References.",
    industries: ["space-research-and-technology"],
    name: "Space Research and Technology",
    slug: "space-research-and-technology"
  },
  "928110": {
    description: "This industry comprises government establishments of the Armed Forces, including the National Guard, primarily engaged in national security and related activities. Illustrative Examples: Air Force, Military police, Army, Military training schools (except military service academies), Marine Corps, National Guard, Military courts, Navy, Cross-References. Government establishments primarily engaged in--",
    industries: ["armed-forces"],
    name: "National Security",
    slug: "national-security"
  },
  "928120": {
    description: "This industry comprises establishments of U.S. and foreign governments primarily engaged in international affairs and programs relating to other nations and peoples. Cross-References.",
    industries: ["international-affairs"],
    name: "International Affairs",
    slug: "international-affairs"
  },
  "44-45": {
    description: 'The Sector as a Whole The Retail Trade sector comprises establishments primarily engaged in retailing merchandise, generally without transformation, and rendering services incidental to the sale of merchandise. The retailing process is the final step in the distribution of merchandise; retailers are, therefore, organized to sell merchandise in small quantities to the general public.  Retail stores are fixed point-of-sale locations, located and designed to attract a high volume of walk-in customers.  In general, retail stores have extensive displays of merchandise and use mass-media advertising to attract customers.  Retailers often reach customers and market merchandise with methods other than, or in addition to, physical stores, such as Internet websites, the broadcasting of "infomercials," the broadcasting and publishing of direct-response advertising, the publishing of paper and electronic catalogs, door-to-door solicitation, in-home demonstration, selling from portable stalls (street vendors, except food), and distribution through vending machines.  Establishments engaged in the direct sale and home delivery of products, such as home heating oil dealers and home delivery newspaper routes, are included here. Retail establishments typically sell merchandise to the general public for personal or household consumption, but some also serve business and institutional clients.  These include office supply retailers, computer and software retailers, building materials dealers, plumbing supply retailers, and electrical supply retailers. In addition to retailing merchandise, some retailers are also engaged in the provision of after-sales services, such as repair and installation.  For example, new automobile dealers, electronics and appliance retailers, and musical instrument and supplies retailers often provide repair services.  As a general rule, establishments engaged in retailing merchandise and providing after-sales services are classified in this sector. Retail trade establishments are grouped into industries and industry groups typically based on one or more of the following criteria: (a) The merchandise line or lines carried; for example, specialty retailers are distinguished from general-line retailers. (b) The usual trade designation of the establishments.  This criterion applies in cases where a retailer is well recognized by the industry and the public, but difficult to define strictly in terms of merchandise lines carried; for example, pharmacies and department stores. (c) Human resource requirements in terms of expertise; for example, the staff of an automobile dealer requires knowledge in financing, registering, and licensing issues that are not necessary in other retail industries. The buying of goods for resale is a characteristic of retail trade establishments that particularly distinguishes them from establishments in the agriculture, manufacturing, and construction industries.  For example, farms that sell their products at or from the point of production are not classified in retail, but rather in agriculture.  Similarly, establishments that both manufacture and sell their products to the general public are not classified in retail, but rather in manufacturing.  However, establishments that engage in processing activities incidental to retailing are classified in retail.  This includes optical goods retailers that grind lenses, and meat and seafood retailers that process carcasses into cuts. Wholesalers also engage in the buying of goods for resale, but they are not usually organized to serve the general public.  They typically operate from a warehouse or office, and neither the design nor the location of these premises is intended to solicit a high volume of walk-in traffic.  Wholesalers supply institutional, industrial, wholesale, and retail clients; their operations are, therefore, generally organized to purchase, sell, and deliver merchandise in larger quantities.  However, dealers of durable nonconsumer goods, such as farm machinery and heavy-duty trucks, are included in wholesale trade even if they often sell these products in single units.',
    industries: ["retail"],
    name: "Retail Trade",
    slug: "retail-trade"
  },
  "48-49": {
    description: "The Sector as a Whole The Transportation and Warehousing sector includes industries providing transportation of passengers and cargo, warehousing and storage for goods, scenic and sightseeing transportation, and support activities related to modes of transportation.  Establishments in these industries use transportation equipment or transportation-related facilities as a productive asset.  The type of equipment depends on the mode of transportation.  The modes of transportation are air, rail, water, road, and pipeline. The Transportation and Warehousing sector distinguishes three basic types of activities: subsectors for each mode of transportation, a subsector for warehousing and storage, and a subsector for establishments providing support activities for transportation.  In addition, there are subsectors for establishments that provide passenger transportation for scenic and sightseeing purposes, postal services, and courier services. A separate subsector for support activities is established in the sector because, first, support activities for transportation are inherently multimodal, such as freight transportation arrangement, or have multimodal aspects.  Secondly, there are production process similarities among the support activity industries. One of the support activities identified in the Support Activities for Transportation subsector is the routine repair and maintenance of transportation equipment (e.g., aircraft at an airport, railroad rolling stock at a railroad terminal, or ships at a harbor or port facility).  Such establishments do not perform complete overhauling or rebuilding of transportation equipment (i.e., periodic restoration of transportation equipment to original design specifications) or transportation equipment conversion (i.e., major modification to systems).  An establishment that primarily performs factory (or shipyard) overhauls, rebuilding, or conversions of aircraft, railroad rolling stock, or ships is classified in Subsector 336, Transportation Equipment Manufacturing, according to the type of equipment. Many of the establishments in this sector often operate on networks, with physical facilities, labor forces, and equipment spread over an extensive geographic area. Warehousing establishments in this sector are distinguished from merchant wholesaling in that the warehouse establishments do not sell the goods. Excluded from this sector are establishments primarily engaged in providing travel agent, travel arrangement, and reservation services that support transportation establishments, hotels, other businesses, and government agencies.  These establishments are classified in Sector 56, Administrative and Support and Waste Management and Remediation Services.  Establishments primarily engaged in providing rental and leasing of transportation equipment without operator are classified in Subsector 532, Rental and Leasing Services.  Establishments primarily engaged in providing medical care with transportation are classified in Sector 62, Health Care and Social Assistance.",
    industries: ["transportation-logistics-supply-chain-and-storage"],
    name: "Transportation and Warehousing",
    slug: "transportation-and-warehousing"
  }
};

const industriesSic = {
  100: {
    industry: "farming",
    name: "Agricultural production crops",
    slug: "agricultural-production-crops"
  },
  200: {
    industry: "farming",
    name: "Agricultural prod livestock animal specialties",
    slug: "agricultural-prod-livestock-animal-specialties"
  },
  700: {
    industry: "farming-ranching-forestry",
    name: "Agricultural services",
    slug: "agricultural-services"
  },
  800: {
    industry: "forestry-and-logging",
    name: "Forestry",
    slug: "forestry"
  },
  900: {
    industry: "fisheries",
    name: "Fishing hunting and trapping",
    slug: "fishing-hunting-and-trapping"
  },
  1e3: {
    industry: "metal-ore-mining",
    name: "Metal mining",
    slug: "metal-mining"
  },
  1040: {
    industry: "metal-ore-mining",
    name: "Gold and silver ores",
    slug: "gold-and-silver-ores"
  },
  1090: {
    industry: "metal-ore-mining",
    name: "Miscellaneous metal ores",
    slug: "miscellaneous-metal-ores"
  },
  1220: {
    industry: "coal-mining",
    name: "Bituminous coal lignite mining",
    slug: "bituminous-coal-lignite-mining"
  },
  1221: {
    industry: "coal-mining",
    name: "Bituminous coal lignite surface mining",
    slug: "bituminous-coal-lignite-surface-mining"
  },
  1311: {
    industry: "oil-and-gas",
    name: "Crude petroleum natural gas",
    slug: "crude-petroleum-natural-gas"
  },
  1381: {
    industry: "oil-and-gas",
    name: "Drilling oil gas wells",
    slug: "drilling-oil-gas-wells"
  },
  1382: {
    industry: "oil-and-gas",
    name: "Oil gas field exploration services",
    slug: "oil-gas-field-exploration-services"
  },
  1389: {
    industry: "oil-and-gas",
    name: "Oil gas field services nec",
    slug: "oil-gas-field-services-nec"
  },
  1400: {
    industry: "nonmetallic-mineral-mining",
    name: "Mining quarrying of nonmetallic minerals no fuels",
    slug: "mining-quarrying-of-nonmetallic-minerals-no-fuels"
  },
  1520: {
    industry: "residential-building-construction",
    name: "General bldg contractors residential bldgs",
    slug: "general-bldg-contractors-residential-bldgs"
  },
  1531: {
    industry: "building-construction",
    name: "Operative builders",
    slug: "operative-builders"
  },
  1540: {
    industry: "nonresidential-building-construction",
    name: "General bldg contractors nonresidential bldgs",
    slug: "general-bldg-contractors-nonresidential-bldgs"
  },
  1600: {
    industry: "construction",
    name: "Heavy construction other than bldg const contractors",
    slug: "heavy-construction-other-than-bldg-const-contractors"
  },
  1623: {
    industry: "utility-system-construction",
    name: "Water sewer pipeline comm power line construction",
    slug: "water-sewer-pipeline-comm-power-line-construction"
  },
  1700: {
    industry: "specialty-trade-contractors",
    name: "Construction special trade contractors",
    slug: "construction-special-trade-contractors"
  },
  1731: {
    industry: "building-equipment-contractors",
    name: "Electrical work",
    slug: "electrical-work"
  },
  2e3: {
    industry: "food-and-beverage-manufacturing",
    name: "Food and kindred products",
    slug: "food-and-kindred-products"
  },
  2011: {
    industry: "meat-products-manufacturing",
    name: "Meat packing plants",
    slug: "meat-packing-plants"
  },
  2013: {
    industry: "meat-products-manufacturing",
    name: "Sausages other prepared meat products",
    slug: "sausages-other-prepared-meat-products"
  },
  2015: {
    industry: "meat-products-manufacturing",
    name: "Poultry slaughtering and processing",
    slug: "poultry-slaughtering-and-processing"
  },
  2020: {
    industry: "dairy-product-manufacturing",
    name: "Dairy products",
    slug: "dairy-products"
  },
  2024: {
    industry: "dairy-product-manufacturing",
    name: "Ice cream frozen desserts",
    slug: "ice-cream-frozen-desserts"
  },
  2030: {
    industry: "fruit-and-vegetable-preserves-manufacturing",
    name: "Canned frozen preservd fruit veg food specialties",
    slug: "canned-frozen-preservd-fruit-veg-food-specialties"
  },
  2033: {
    industry: "fruit-and-vegetable-preserves-manufacturing",
    name: "Canned fruits veg preserves jams jellies",
    slug: "canned-fruits-veg-preserves-jams-jellies"
  },
  2040: {
    industry: "wholesale-raw-farm-products",
    name: "Grain mill products",
    slug: "grain-mill-products"
  },
  2050: {
    industry: "baked-goods-manufacturing",
    name: "Bakery products",
    slug: "bakery-products"
  },
  2052: {
    industry: "baked-goods-manufacturing",
    name: "Cookies crackers",
    slug: "cookies-crackers"
  },
  2060: {
    industry: "sugar-and-confectionery-product-manufacturing",
    name: "Sugar confectionery products",
    slug: "sugar-confectionery-products"
  },
  2070: {
    industry: "food-and-beverage-manufacturing",
    name: "Fats oils",
    slug: "fats-oils"
  },
  2080: {
    industry: "beverage-manufacturing",
    name: "Beverages",
    slug: "beverages"
  },
  2082: {
    industry: "breweries",
    name: "Malt beverages",
    slug: "malt-beverages"
  },
  2086: {
    industry: "beverage-manufacturing",
    name: "Bottled canned soft drinks carbonated waters",
    slug: "bottled-canned-soft-drinks-carbonated-waters"
  },
  2090: {
    industry: "food-and-beverage-manufacturing",
    name: "Miscellaneous food preparations kindred products",
    slug: "miscellaneous-food-preparations-kindred-products"
  },
  2092: {
    industry: "seafood-product-manufacturing",
    name: "Prepared fresh or frozen fish seafoods",
    slug: "prepared-fresh-or-frozen-fish-seafoods"
  },
  2100: {
    industry: "tobacco-manufacturing",
    name: "Tobacco products",
    slug: "tobacco-products"
  },
  2111: {
    industry: "tobacco-manufacturing",
    name: "Cigarettes",
    slug: "cigarettes"
  },
  2200: {
    industry: "textile-manufacturing",
    name: "Textile mill products",
    slug: "textile-mill-products"
  },
  2211: {
    industry: "textile-manufacturing",
    name: "Broadwoven fabric mills cotton",
    slug: "broadwoven-fabric-mills-cotton"
  },
  2221: {
    industry: "textile-manufacturing",
    name: "Broadwoven fabric mills man made fiber silk",
    slug: "broadwoven-fabric-mills-man-made-fiber-silk"
  },
  2250: {
    industry: "textile-manufacturing",
    name: "Knitting mills",
    slug: "knitting-mills"
  },
  2253: {
    industry: "apparel-manufacturing",
    name: "Knit outerwear mills",
    slug: "knit-outerwear-mills"
  },
  2273: {
    industry: "textile-manufacturing",
    name: "Carpets rugs",
    slug: "carpets-rugs"
  },
  2300: {
    industry: "apparel-manufacturing",
    name: "Apparel other finishd prods of fabrics similar matl",
    slug: "apparel-other-finishd-prods-of-fabrics-similar-matl"
  },
  2320: {
    industry: "apparel-manufacturing",
    name: "Men s boys furnishgs work clothing allied garments",
    slug: "men-s-boys-furnishgs-work-clothing-allied-garments"
  },
  2330: {
    industry: "apparel-manufacturing",
    name: "Women s misses and juniors outerwear",
    slug: "women-s-misses-and-juniors-outerwear"
  },
  2340: {
    industry: "apparel-manufacturing",
    name: "Women s misses children s infants undergarments",
    slug: "women-s-misses-children-s-infants-undergarments"
  },
  2390: {
    industry: "textile-manufacturing",
    name: "Miscellaneous fabricated textile products",
    slug: "miscellaneous-fabricated-textile-products"
  },
  2400: {
    industry: "wood-product-manufacturing",
    name: "Lumber wood products no furniture",
    slug: "lumber-wood-products-no-furniture"
  },
  2421: {
    industry: "wood-product-manufacturing",
    name: "Sawmills planting mills general",
    slug: "sawmills-planting-mills-general"
  },
  2430: {
    industry: "wood-product-manufacturing",
    name: "Millwood veneer plywood structural wood members",
    slug: "millwood-veneer-plywood-structural-wood-members"
  },
  2451: {
    industry: "wood-product-manufacturing",
    name: "Mobile homes",
    slug: "mobile-homes"
  },
  2452: {
    industry: "wood-product-manufacturing",
    name: "Prefabricated wood bldgs components",
    slug: "prefabricated-wood-bldgs-components"
  },
  2510: {
    industry: "household-and-institutional-furniture-manufacturing",
    name: "Household furniture",
    slug: "household-furniture"
  },
  2511: {
    industry: "household-and-institutional-furniture-manufacturing",
    name: "Wood household furniture no upholstered",
    slug: "wood-household-furniture-no-upholstered"
  },
  2520: {
    industry: "office-furniture-and-fixtures-manufacturing",
    name: "Office furniture",
    slug: "office-furniture"
  },
  2522: {
    industry: "office-furniture-and-fixtures-manufacturing",
    name: "Office furniture no wood",
    slug: "office-furniture-no-wood"
  },
  2531: {
    industry: "household-and-institutional-furniture-manufacturing",
    name: "Public bldg related furniture",
    slug: "public-bldg-related-furniture"
  },
  2540: {
    industry: "office-furniture-and-fixtures-manufacturing",
    name: "Partitions shelvg lockers office store fixtures",
    slug: "partitions-shelvg-lockers-office-store-fixtures"
  },
  2590: {
    industry: "household-and-institutional-furniture-manufacturing",
    name: "Miscellaneous furniture fixtures",
    slug: "miscellaneous-furniture-fixtures"
  },
  2600: {
    industry: "paper-and-forest-product-manufacturing",
    name: "Papers allied products",
    slug: "papers-allied-products"
  },
  2611: {
    industry: "paper-and-forest-product-manufacturing",
    name: "Pulp mills",
    slug: "pulp-mills"
  },
  2621: {
    industry: "paper-and-forest-product-manufacturing",
    name: "Paper mills",
    slug: "paper-mills"
  },
  2631: {
    industry: "paper-and-forest-product-manufacturing",
    name: "Paperboard mills",
    slug: "paperboard-mills"
  },
  2650: {
    industry: "packaging-and-containers-manufacturing",
    name: "Paperboard containers boxes",
    slug: "paperboard-containers-boxes"
  },
  2670: {
    industry: "packaging-and-containers-manufacturing",
    name: "Converted paper paperboard prods no containers boxes",
    slug: "converted-paper-paperboard-prods-no-containers-boxes"
  },
  2673: {
    industry: "packaging-and-containers-manufacturing",
    name: "Plastics foil coated paper bags",
    slug: "plastics-foil-coated-paper-bags"
  },
  2711: {
    industry: "newspaper-publishing",
    name: "Newspapers publishing or publishing printing",
    slug: "newspapers-publishing-or-publishing-printing"
  },
  2721: {
    industry: "periodical-publishing",
    name: "Periodicals publishing or publishing printing",
    slug: "periodicals-publishing-or-publishing-printing"
  },
  2731: {
    industry: "book-publishing",
    name: "Books publishing or publishing printing",
    slug: "books-publishing-or-publishing-printing"
  },
  2732: {
    industry: "book-and-periodical-publishing",
    name: "Book printing",
    slug: "book-printing"
  },
  2741: {
    industry: "book-and-periodical-publishing",
    name: "Miscellaneous publishing",
    slug: "miscellaneous-publishing"
  },
  2750: {
    industry: "printing-services",
    name: "Commercial printing",
    slug: "commercial-printing"
  },
  2761: {
    industry: "printing-services",
    name: "Manifold business forms",
    slug: "manifold-business-forms"
  },
  2771: {
    industry: "printing-services",
    name: "Greeting cards",
    slug: "greeting-cards"
  },
  2780: {
    industry: "printing-services",
    name: "Blankbooks looseleaf binders bookbinding related work",
    slug: "blankbooks-looseleaf-binders-bookbinding-related-work"
  },
  2790: {
    industry: "printing-services",
    name: "Service industries for the printing trade",
    slug: "service-industries-for-the-printing-trade"
  },
  2800: {
    industry: "chemical-manufacturing",
    name: "Chemicals allied products",
    slug: "chemicals-allied-products"
  },
  2810: {
    industry: "chemical-manufacturing",
    name: "Industrial inorganic chemicals",
    slug: "industrial-inorganic-chemicals"
  },
  2820: {
    industry: "plastics-and-rubber-product-manufacturing",
    name: "Plastic material synth resin rubber cellulos no glass",
    slug: "plastic-material-synth-resin-rubber-cellulos-no-glass"
  },
  2821: {
    industry: "plastics-and-rubber-product-manufacturing",
    name: "Plastic materials synth resins nonvulcan elastomers",
    slug: "plastic-materials-synth-resins-nonvulcan-elastomers"
  },
  2833: {
    industry: "pharmaceutical-manufacturing",
    name: "Medicinal chemicals botanical products",
    slug: "medicinal-chemicals-botanical-products"
  },
  2834: {
    industry: "pharmaceutical-manufacturing",
    name: "Pharmaceutical preparations",
    slug: "pharmaceutical-preparations"
  },
  2835: {
    industry: "pharmaceutical-manufacturing",
    name: "In vitro in vivo diagnostic substances",
    slug: "in-vitro-in-vivo-diagnostic-substances"
  },
  2836: {
    industry: "pharmaceutical-manufacturing",
    name: "Biological products no diagnostic substances",
    slug: "biological-products-no-diagnostic-substances"
  },
  2840: {
    industry: "soap-and-cleaning-product-manufacturing",
    name: "Soap detergents cleang preparations perfumes cosmetics",
    slug: "soap-detergents-cleang-preparations-perfumes-cosmetics"
  },
  2842: {
    industry: "soap-and-cleaning-product-manufacturing",
    name: "Specialty cleaning polishing and sanitation preparations",
    slug: "specialty-cleaning-polishing-and-sanitation-preparations"
  },
  2844: {
    industry: "soap-and-cleaning-product-manufacturing",
    name: "Perfumes cosmetics other toilet preparations",
    slug: "perfumes-cosmetics-other-toilet-preparations"
  },
  2851: {
    industry: "wholesale-chemical-and-allied-products",
    name: "Paints varnishes lacquers enamels allied products",
    slug: "paints-varnishes-lacquers-enamels-allied-products"
  },
  2860: {
    industry: "chemical-manufacturing",
    name: "Industrial organic chemicals",
    slug: "industrial-organic-chemicals"
  },
  2870: {
    industry: "agricultural-chemical-manufacturing",
    name: "Agricultural chemicals",
    slug: "agricultural-chemicals"
  },
  2890: {
    industry: "chemical-manufacturing",
    name: "Miscellaneous chemical products",
    slug: "miscellaneous-chemical-products"
  },
  2891: {
    industry: "paint-coating-and-adhesive-manufacturing",
    name: "Adhesives sealants",
    slug: "adhesives-sealants"
  },
  2911: {
    industry: "oil-and-coal-product-manufacturing",
    name: "Petroleum refining",
    slug: "petroleum-refining"
  },
  2950: {
    industry: "oil-and-coal-product-manufacturing",
    name: "Asphalt paving roofing materials",
    slug: "asphalt-paving-roofing-materials"
  },
  2990: {
    industry: "oil-and-coal-product-manufacturing",
    name: "Miscellaneous products of petroleum coal",
    slug: "miscellaneous-products-of-petroleum-coal"
  },
  3011: {
    industry: "rubber-products-manufacturing",
    name: "Tires inner tubes",
    slug: "tires-inner-tubes"
  },
  3021: {
    industry: "footwear-manufacturing",
    name: "Rubber plastics footwear",
    slug: "rubber-plastics-footwear"
  },
  3050: {
    industry: "rubber-products-manufacturing",
    name: "Gaskets packg sealg devices rubber plastics hose",
    slug: "gaskets-packg-sealg-devices-rubber-plastics-hose"
  },
  3060: {
    industry: "rubber-products-manufacturing",
    name: "Fabricated rubber products nec",
    slug: "fabricated-rubber-products-nec"
  },
  3080: {
    industry: "plastics-manufacturing",
    name: "Miscellaneous plastics products",
    slug: "miscellaneous-plastics-products"
  },
  3081: {
    industry: "plastics-manufacturing",
    name: "Unsupported plastics film sheet",
    slug: "unsupported-plastics-film-sheet"
  },
  3086: {
    industry: "plastics-manufacturing",
    name: "Plastics foam products",
    slug: "plastics-foam-products"
  },
  3089: {
    industry: "plastics-manufacturing",
    name: "Plastics products nec",
    slug: "plastics-products-nec"
  },
  3100: {
    industry: "leather-product-manufacturing",
    name: "Leather leather products",
    slug: "leather-leather-products"
  },
  3140: {
    industry: "footwear-manufacturing",
    name: "Footwear no rubber",
    slug: "footwear-no-rubber"
  },
  3211: {
    industry: "glass-product-manufacturing",
    name: "Flat glass",
    slug: "flat-glass"
  },
  3220: {
    industry: "glass-product-manufacturing",
    name: "Glass glassware pressed or blown",
    slug: "glass-glassware-pressed-or-blown"
  },
  3221: {
    industry: "glass-product-manufacturing",
    name: "Glass containers",
    slug: "glass-containers"
  },
  3231: {
    industry: "glass-product-manufacturing",
    name: "Glass products made of purchased glass",
    slug: "glass-products-made-of-purchased-glass"
  },
  3241: {
    industry: "glass-ceramics-and-concrete-manufacturing",
    name: "Cement hydraulic",
    slug: "cement-hydraulic"
  },
  3250: {
    industry: "clay-and-refractory-products-manufacturing",
    name: "Structural clay products",
    slug: "structural-clay-products"
  },
  3260: {
    industry: "clay-and-refractory-products-manufacturing",
    name: "Pottery related products",
    slug: "pottery-related-products"
  },
  3270: {
    industry: "glass-ceramics-and-concrete-manufacturing",
    name: "Concrete gypsum plaster products",
    slug: "concrete-gypsum-plaster-products"
  },
  3272: {
    industry: "glass-ceramics-and-concrete-manufacturing",
    name: "Concrete products except block brick",
    slug: "concrete-products-except-block-brick"
  },
  3281: {
    industry: "glass-ceramics-and-concrete-manufacturing",
    name: "Cut stone stone products",
    slug: "cut-stone-stone-products"
  },
  3290: {
    industry: "abrasives-and-nonmetallic-minerals-manufacturing",
    name: "Abrasive asbestos misc nonmetallic mineral prods",
    slug: "abrasive-asbestos-misc-nonmetallic-mineral-prods"
  },
  3310: {
    industry: "primary-metal-manufacturing",
    name: "Steel works blast furnaces rolling finishing mills",
    slug: "steel-works-blast-furnaces-rolling-finishing-mills"
  },
  3312: {
    industry: "primary-metal-manufacturing",
    name: "Steel works blast furnaces rolling mills coke ovens",
    slug: "steel-works-blast-furnaces-rolling-mills-coke-ovens"
  },
  3317: {
    industry: "primary-metal-manufacturing",
    name: "Steel pipe tubes",
    slug: "steel-pipe-tubes"
  },
  3320: {
    industry: "primary-metal-manufacturing",
    name: "Iron steel foundries",
    slug: "iron-steel-foundries"
  },
  3330: {
    industry: "primary-metal-manufacturing",
    name: "Primary smelting refining of nonferrous metals",
    slug: "primary-smelting-refining-of-nonferrous-metals"
  },
  3334: {
    industry: "primary-metal-manufacturing",
    name: "Primary production of aluminum",
    slug: "primary-production-of-aluminum"
  },
  3341: {
    industry: "primary-metal-manufacturing",
    name: "Secondary smelting refining of nonferrous metals",
    slug: "secondary-smelting-refining-of-nonferrous-metals"
  },
  3350: {
    industry: "primary-metal-manufacturing",
    name: "Rolling drawing extruding of nonferrous metals",
    slug: "rolling-drawing-extruding-of-nonferrous-metals"
  },
  3357: {
    industry: "primary-metal-manufacturing",
    name: "Drawing insulating of nonferrous wire",
    slug: "drawing-insulating-of-nonferrous-wire"
  },
  3360: {
    industry: "primary-metal-manufacturing",
    name: "Nonferrous foundries castings",
    slug: "nonferrous-foundries-castings"
  },
  3390: {
    industry: "primary-metal-manufacturing",
    name: "Miscellaneous primary metal products",
    slug: "miscellaneous-primary-metal-products"
  },
  3411: {
    industry: "fabricated-metal-products",
    name: "Metal cans",
    slug: "metal-cans"
  },
  3412: {
    industry: "fabricated-metal-products",
    name: "Metal shipping barrels drums kegs pails",
    slug: "metal-shipping-barrels-drums-kegs-pails"
  },
  3420: {
    industry: "cutlery-and-handtool-manufacturing",
    name: "Cutlery handtools general hardware",
    slug: "cutlery-handtools-general-hardware"
  },
  3430: {
    industry: "fabricated-metal-products",
    name: "Hearing equip except elec warm air plumbing fixtures",
    slug: "hearing-equip-except-elec-warm-air-plumbing-fixtures"
  },
  3433: {
    industry: "fabricated-metal-products",
    name: "Heating equipment except electric warm air furnaces",
    slug: "heating-equipment-except-electric-warm-air-furnaces"
  },
  3440: {
    industry: "fabricated-metal-products",
    name: "Fabricated structural metal products",
    slug: "fabricated-structural-metal-products"
  },
  3442: {
    industry: "fabricated-metal-products",
    name: "Metal doors sash frames moldings trim",
    slug: "metal-doors-sash-frames-moldings-trim"
  },
  3443: {
    industry: "fabricated-metal-products",
    name: "Fabricated plate work boiler shops",
    slug: "fabricated-plate-work-boiler-shops"
  },
  3444: {
    industry: "fabricated-metal-products",
    name: "Sheet metal work",
    slug: "sheet-metal-work"
  },
  3448: {
    industry: "fabricated-metal-products",
    name: "Prefabricated metal buildings components",
    slug: "prefabricated-metal-buildings-components"
  },
  3451: {
    industry: "turned-products-and-fastener-manufacturing",
    name: "Screw machine products",
    slug: "screw-machine-products"
  },
  3452: {
    industry: "turned-products-and-fastener-manufacturing",
    name: "Bolts nuts screws rivets washers",
    slug: "bolts-nuts-screws-rivets-washers"
  },
  3460: {
    industry: "metalworking-machinery-manufacturing",
    name: "Metal forgings stampings",
    slug: "metal-forgings-stampings"
  },
  3470: {
    industry: "metal-treatments",
    name: "Coating engraving allied services",
    slug: "coating-engraving-allied-services"
  },
  3480: {
    industry: "defense-and-space-manufacturing",
    name: "Ordnance accessories no vehicles guided missiles",
    slug: "ordnance-accessories-no-vehicles-guided-missiles"
  },
  3490: {
    industry: "fabricated-metal-products",
    name: "Miscellaneous fabricated metal products",
    slug: "miscellaneous-fabricated-metal-products"
  },
  3510: {
    industry: "engines-and-power-transmission-equipment-manufacturing",
    name: "Engines turbines",
    slug: "engines-turbines"
  },
  3523: {
    industry: "agriculture-construction-mining-machinery-manufacturing",
    name: "Farm machinery equipment",
    slug: "farm-machinery-equipment"
  },
  3524: {
    industry: "agriculture-construction-mining-machinery-manufacturing",
    name: "Lawn garden tractors home lawn gardens equip",
    slug: "lawn-garden-tractors-home-lawn-gardens-equip"
  },
  3530: {
    industry: "agriculture-construction-mining-machinery-manufacturing",
    name: "Construction mining materials handling machinery equip",
    slug: "construction-mining-materials-handling-machinery-equip"
  },
  3531: {
    industry: "agriculture-construction-mining-machinery-manufacturing",
    name: "Construction machinery equip",
    slug: "construction-machinery-equip"
  },
  3532: {
    industry: "agriculture-construction-mining-machinery-manufacturing",
    name: "Mining machinery equip no oil gas field mach equip",
    slug: "mining-machinery-equip-no-oil-gas-field-mach-equip"
  },
  3533: {
    industry: "agriculture-construction-mining-machinery-manufacturing",
    name: "Oil gas field machinery equipment",
    slug: "oil-gas-field-machinery-equipment"
  },
  3537: {
    industry: "industrial-machinery-manufacturing",
    name: "Industrial trucks tractors trailers stackers",
    slug: "industrial-trucks-tractors-trailors-stackers"
  },
  3540: {
    industry: "metalworking-machinery-manufacturing",
    name: "Metalworking machinery equipment",
    slug: "metalworking-machinery-equipment"
  },
  3541: {
    industry: "metalworking-machinery-manufacturing",
    name: "Machine tools metal cutting types",
    slug: "machine-tools-metal-cutting-types"
  },
  3550: {
    industry: "specialty-trade-contractors",
    name: "Special industry machinery no metalworking machinery",
    slug: "special-industry-machinery-no-metalworking-machinery"
  },
  3555: {
    industry: "printing-services",
    name: "Printing trades machinery equipment",
    slug: "printing-trades-machinery-equipment"
  },
  3559: {
    industry: "specialty-trade-contractors",
    name: "Special industry machinery nec",
    slug: "special-industry-machinery-nec"
  },
  3560: {
    industry: "industrial-machinery-manufacturing",
    name: "General industrial machinery equipment",
    slug: "general-industrial-machinery-equipment"
  },
  3561: {
    industry: "industrial-machinery-manufacturing",
    name: "Pumps pumping equipment",
    slug: "pumps-pumping-equipment"
  },
  3562: {
    industry: "metal-valve-ball-and-roller-manufacturing",
    name: "Ball roller bearings",
    slug: "ball-roller-bearings"
  },
  3564: {
    industry: "industrial-machinery-manufacturing",
    name: "Industrial commercial fans blowers air purifying equip",
    slug: "industrial-commercial-fans-blowers-air-purifying-equip"
  },
  3567: {
    industry: "industrial-machinery-manufacturing",
    name: "Industrial process furnaces ovens",
    slug: "industrial-process-furnaces-ovens"
  },
  3569: {
    industry: "industrial-machinery-manufacturing",
    name: "General industrial machinery equipment nec",
    slug: "general-industrial-machinery-equipment-nec"
  },
  3570: {
    industry: "computers-and-electronics-manufacturing",
    name: "Computer office equipment",
    slug: "computer-office-equipment"
  },
  3571: {
    industry: "computers-and-electronics-manufacturing",
    name: "Electronic computers",
    slug: "electronic-computers"
  },
  3572: {
    industry: "computers-and-electronics-manufacturing",
    name: "Computer storage devices",
    slug: "computer-storage-devices"
  },
  3575: {
    industry: "computers-and-electronics-manufacturing",
    name: "Computer terminals",
    slug: "computer-terminals"
  },
  3576: {
    industry: "computers-and-electronics-manufacturing",
    name: "Computer communications equipment",
    slug: "computer-communications-equipment"
  },
  3577: {
    industry: "computers-and-electronics-manufacturing",
    name: "Computer peripheral equipment nec",
    slug: "computer-peripheral-equipment-nec"
  },
  3578: {
    industry: "computers-and-electronics-manufacturing",
    name: "Calculating accounting machines no electronic computers",
    slug: "calculating-accounting-machines-no-electronic-computers"
  },
  3579: {
    industry: "computers-and-electronics-manufacturing",
    name: "Office machines nec",
    slug: "office-machines-nec"
  },
  3580: {
    industry: "industrial-machinery-manufacturing",
    name: "Refrigeration service industry machinery",
    slug: "refrigeration-service-industry-machinery"
  },
  3585: {
    industry: "industrial-machinery-manufacturing",
    name: "Air cond warm air heating equipment comm indl refrigeration",
    slug: "air-cond-warm-air-heatg-equip-comm-indl-refrig-equip"
  },
  3590: {
    industry: "industrial-machinery-manufacturing",
    name: "Misc industrial commercial machinery equipment",
    slug: "misc-industrial-commercial-machinery-equipment"
  },
  3600: {
    industry: "electrical-equipment-manufacturing",
    name: "Electronic other electrical equipment no computer equip",
    slug: "electronic-other-electrical-equipment-no-computer-equip"
  },
  3612: {
    industry: "electrical-equipment-manufacturing",
    name: "Power distribution specialty transformers",
    slug: "power-distribution-specialty-transformers"
  },
  3613: {
    industry: "electrical-equipment-manufacturing",
    name: "Switchgear switchboard apparatus",
    slug: "switchgear-switchboard-apparatus"
  },
  3620: {
    industry: "electrical-equipment-manufacturing",
    name: "Electrical industrial apparatus",
    slug: "electrical-industrial-apparatus"
  },
  3621: {
    industry: "electrical-equipment-manufacturing",
    name: "Motors generators",
    slug: "motors-generators"
  },
  3630: {
    industry: "household-appliance-manufacturing",
    name: "Household appliances",
    slug: "household-appliances"
  },
  3634: {
    industry: "household-appliance-manufacturing",
    name: "Electric housewares fans",
    slug: "electric-housewares-fans"
  },
  3640: {
    industry: "electric-lighting-equipment-manufacturing",
    name: "Electric lighting wiring equipment",
    slug: "electric-lighting-wiring-equipment"
  },
  3651: {
    industry: "audio-and-video-equipment-manufacturing",
    name: "Household audio video equipment",
    slug: "household-audio-video-equipment"
  },
  3652: {
    industry: "audio-and-video-equipment-manufacturing",
    name: "Phonograph records prerecorded audio tapes disks",
    slug: "phonograph-records-prerecorded-audio-tapes-disks"
  },
  3661: {
    industry: "communications-equipment-manufacturing",
    name: "Telephone telegraph apparatus",
    slug: "telephone-telegraph-apparatus"
  },
  3663: {
    industry: "communications-equipment-manufacturing",
    name: "Radio tv broadcasting communications equipment",
    slug: "radio-tv-broadcasting-communications-equipment"
  },
  3669: {
    industry: "communications-equipment-manufacturing",
    name: "Communications equipment nec",
    slug: "communications-equipment-nec"
  },
  3670: {
    industry: "retail-appliances-electrical-and-electronic-equipment",
    name: "Electronic components accessories",
    slug: "electronic-components-accessories"
  },
  3672: {
    industry: "retail-appliances-electrical-and-electronic-equipment",
    name: "Printed circuit boards",
    slug: "printed-circuit-boards"
  },
  3674: {
    industry: "semiconductor-manufacturing",
    name: "Semiconductors related devices",
    slug: "semiconductors-related-devices"
  },
  3677: {
    industry: "retail-appliances-electrical-and-electronic-equipment",
    name: "Electronic coils transformers other inductors",
    slug: "electronic-coils-transformers-other-inductors"
  },
  3678: {
    industry: "retail-appliances-electrical-and-electronic-equipment",
    name: "Electronic connectors",
    slug: "electronic-connectors"
  },
  3679: {
    industry: "retail-appliances-electrical-and-electronic-equipment",
    name: "Electronic components nec",
    slug: "electronic-components-nec"
  },
  3690: {
    industry: "electrical-equipment-manufacturing",
    name: "Miscellaneous electrical machinery equipment supplies",
    slug: "miscellaneous-electrical-machinery-equipment-supplies"
  },
  3695: {
    industry: "magnetic-and-optical-media-manufacturing",
    name: "Magnetic optical recording media",
    slug: "magnetic-optical-recording-media"
  },
  3711: {
    industry: "motor-vehicle-manufacturing",
    name: "Motor vehicles passenger car bodies",
    slug: "motor-vehicles-passenger-car-bodies"
  },
  3713: {
    industry: "motor-vehicle-manufacturing",
    name: "Truck bus bodies",
    slug: "truck-bus-bodies"
  },
  3714: {
    industry: "motor-vehicle-parts-manufacturing",
    name: "Motor vehicle parts accessories",
    slug: "motor-vehicle-parts-accessories"
  },
  3715: {
    industry: "motor-vehicle-parts-manufacturing",
    name: "Truck trailers",
    slug: "truck-trailers"
  },
  3716: {
    industry: "motor-vehicle-manufacturing",
    name: "Motor homes",
    slug: "motor-homes"
  },
  3720: {
    industry: "aviation-and-aerospace-component-manufacturing",
    name: "Aircraft parts",
    slug: "aircraft-parts"
  },
  3721: {
    industry: "aviation-and-aerospace-component-manufacturing",
    name: "Aircraft",
    slug: "aircraft"
  },
  3724: {
    industry: "aviation-and-aerospace-component-manufacturing",
    name: "Aircraft engines engine parts",
    slug: "aircraft-engines-engine-parts"
  },
  3728: {
    industry: "aviation-and-aerospace-component-manufacturing",
    name: "Aircraft parts auxiliary equipment nec",
    slug: "aircraft-parts-auxiliary-equipment-nec"
  },
  3730: {
    industry: "shipbuilding",
    name: "Ship boat building repairing",
    slug: "ship-boat-building-repairing"
  },
  3743: {
    industry: "railroad-equipment-manufacturing",
    name: "Railroad equipment",
    slug: "railroad-equipment"
  },
  3751: {
    industry: "motor-vehicle-manufacturing",
    name: "Motorcycles bicycles parts",
    slug: "motorcycles-bicycles-parts"
  },
  3760: {
    industry: "defense-and-space-manufacturing",
    name: "Guided missiles space vehicles parts",
    slug: "guided-missiles-space-vehicles-parts"
  },
  3790: {
    industry: "transportation-equipment-manufacturing",
    name: "Miscellaneous transportation equipment",
    slug: "miscellaneous-transportation-equipment"
  },
  3812: {
    industry: "defense-and-space-manufacturing",
    name: "Search detection navigation guidance aeronautical sys",
    slug: "search-detection-navagation-guidance-aeronautical-sys"
  },
  3821: {
    industry: "measuring-and-control-instrument-manufacturing",
    name: "Laboratory apparatus furniture",
    slug: "laboratory-apparatus-furniture"
  },
  3822: {
    industry: "industrial-machinery-manufacturing",
    name: "Auto controls for regulating residential comml environments",
    slug: "auto-controls-for-regulating-residential-comml-environments"
  },
  3823: {
    industry: "measuring-and-control-instrument-manufacturing",
    name: "Industrial instruments for measurement display and control",
    slug: "industrial-instruments-for-measurement-display-and-control"
  },
  3824: {
    industry: "measuring-and-control-instrument-manufacturing",
    name: "Totalizing fluid meters counting devices",
    slug: "totalizing-fluid-meters-counting-devices"
  },
  3825: {
    industry: "measuring-and-control-instrument-manufacturing",
    name: "Instruments for meas testing of electricity elec signals",
    slug: "instruments-for-meas-testing-of-electricity-elec-signals"
  },
  3826: {
    industry: "measuring-and-control-instrument-manufacturing",
    name: "Laboratory analytical instruments",
    slug: "laboratory-analytical-instruments"
  },
  3827: {
    industry: "magnetic-and-optical-media-manufacturing",
    name: "Optical instruments lenses",
    slug: "optical-instruments-lenses"
  },
  3829: {
    industry: "measuring-and-control-instrument-manufacturing",
    name: "Measuring controlling devices nec",
    slug: "measuring-controlling-devices-nec"
  },
  3841: {
    industry: "medical-equipment-manufacturing",
    name: "Surgical medical instruments apparatus",
    slug: "surgical-medical-instruments-apparatus"
  },
  3842: {
    industry: "medical-equipment-manufacturing",
    name: "Orthopedic prosthetic surgical appliances supplies",
    slug: "orthopedic-prosthetic-surgical-appliances-supplies"
  },
  3843: {
    industry: "medical-equipment-manufacturing",
    name: "Dental equipment supplies",
    slug: "dental-equipment-supplies"
  },
  3844: {
    industry: "medical-equipment-manufacturing",
    name: "X ray apparatus tubes related irradiation apparatus",
    slug: "x-ray-apparatus-tubes-related-irradiation-apparatus"
  },
  3845: {
    industry: "medical-equipment-manufacturing",
    name: "Electromedical electrotherapeutic apparatus",
    slug: "electromedical-electrotherapeutic-apparatus"
  },
  3851: {
    industry: "medical-equipment-manufacturing",
    name: "Ophthalmic goods",
    slug: "ophthalmic-goods"
  },
  3861: {
    industry: "photography",
    name: "Photographic equipment supplies",
    slug: "photographic-equipment-supplies"
  },
  3873: {
    industry: "retail-luxury-goods-and-jewelry",
    name: "Watches clocks clockwork operated devices parts",
    slug: "watches-clocks-clockwork-operated-devices-parts"
  },
  3910: {
    industry: "retail-luxury-goods-and-jewelry",
    name: "Jewelry silverware plated ware",
    slug: "jewelry-silverware-plated-ware"
  },
  3911: {
    industry: "retail-luxury-goods-and-jewelry",
    name: "Jewelry precious metal",
    slug: "jewelry-precious-metal"
  },
  3931: {
    industry: "retail-musical-instruments",
    name: "Musical instruments",
    slug: "musical-instruments"
  },
  3942: {
    industry: "manufacturing",
    name: "Dolls stuffed toys",
    slug: "dolls-stuffed-toys"
  },
  3944: {
    industry: "manufacturing",
    name: "Games toys children s vehicles no dolls bicycles",
    slug: "games-toys-children-s-vehicles-no-dolls-bicycles"
  },
  3949: {
    industry: "sporting-goods-manufacturing",
    name: "Sporting athletic goods nec",
    slug: "sporting-athletic-goods-nec"
  },
  3950: {
    industry: "writing-and-editing",
    name: "Pens pencils other artists materials",
    slug: "pens-pencils-other-artists-materials"
  },
  3960: {
    industry: "retail-luxury-goods-and-jewelry",
    name: "Costume jewelry novelties",
    slug: "costume-jewelry-novelties"
  },
  3990: {
    industry: "manufacturing",
    name: "Miscellaneous manufacturing industries",
    slug: "miscellaneous-manufacturing-industries"
  },
  4011: {
    industry: "rail-transportation",
    name: "Railroads line haul operating",
    slug: "railroads-line-haul-operating"
  },
  4013: {
    industry: "rail-transportation",
    name: "Railroad switching terminal establishments",
    slug: "railroad-switching-terminal-establishments"
  },
  4100: {
    industry: "urban-transit-services",
    name: "Local suburban transit interurban hwy passenger trans",
    slug: "local-suburban-transit-interurban-hwy-passenger-trans"
  },
  4210: {
    industry: "truck-transportation",
    name: "Trucking courier services no air",
    slug: "trucking-courier-services-no-air"
  },
  4213: {
    industry: "truck-transportation",
    name: "Trucking no local",
    slug: "trucking-no-local"
  },
  4220: {
    industry: "warehousing-and-storage",
    name: "Public warehousing storage",
    slug: "public-warehousing-storage"
  },
  4231: {
    industry: "truck-transportation",
    name: "Terminal maintenance facilities for motor freight transport",
    slug: "terminal-maintenance-facilities-for-motor-freight-transport"
  },
  4400: {
    industry: "maritime-transportation",
    name: "Water transportation",
    slug: "water-transportation"
  },
  4412: {
    industry: "maritime-transportation",
    name: "Deep sea foreign transportation of freight",
    slug: "deep-sea-foreign-transportation-of-freight"
  },
  4512: {
    industry: "airlines-and-aviation",
    name: "Air transportation scheduled",
    slug: "air-transportation-scheduled"
  },
  4513: {
    industry: "freight-and-package-transportation",
    name: "Air courier services",
    slug: "air-courier-services"
  },
  4522: {
    industry: "airlines-and-aviation",
    name: "Air transportation nonscheduled",
    slug: "air-transportation-nonscheduled"
  },
  4581: {
    industry: "airlines-and-aviation",
    name: "Airports flying fields airport terminal services",
    slug: "airports-flying-fields-airport-terminal-services"
  },
  4610: {
    industry: "pipeline-transportation",
    name: "Pipe lines no natural gas",
    slug: "pipe-lines-no-natural-gas"
  },
  4700: {
    industry: "transportation-logistics-supply-chain-and-storage",
    name: "Transportation services",
    slug: "transportation-services"
  },
  4731: {
    industry: "transportation-logistics-supply-chain-and-storage",
    name: "Arrangement of transportation of freight cargo",
    slug: "arrangement-of-transportation-of-freight-cargo"
  },
  4812: {
    industry: "telecommunications",
    name: "Radiotelephone communications",
    slug: "radiotelephone-communications"
  },
  4813: {
    industry: "telecommunications",
    name: "Telephone communications no radiotelephone",
    slug: "telephone-communications-no-radiotelephone"
  },
  4822: {
    industry: "telecommunications",
    name: "Telegraph other message communications",
    slug: "telegraph-other-message-communications"
  },
  4832: {
    industry: "radio-and-television-broadcasting",
    name: "Radio broadcasting stations",
    slug: "radio-broadcasting-stations"
  },
  4833: {
    industry: "radio-and-television-broadcasting",
    name: "Television broadcasting stations",
    slug: "television-broadcasting-stations"
  },
  4841: {
    industry: "cable-and-satellite-programming",
    name: "Cable other pay television services",
    slug: "cable-other-pay-television-services"
  },
  4899: {
    industry: "telecommunications",
    name: "Communications services nec",
    slug: "communications-services-nec"
  },
  4900: {
    industry: "utilities",
    name: "Electric gas sanitary services",
    slug: "electric-gas-sanitary-services"
  },
  4911: {
    industry: "utilities",
    name: "Electric services",
    slug: "electric-services"
  },
  4922: {
    industry: "natural-gas-distribution",
    name: "Natural gas transmission",
    slug: "natural-gas-transmission"
  },
  4923: {
    industry: "natural-gas-distribution",
    name: "Natural gas transmission distribution",
    slug: "natural-gas-transmission-distribution"
  },
  4924: {
    industry: "natural-gas-distribution",
    name: "Natural gas distribution",
    slug: "natural-gas-distribution"
  },
  4931: {
    industry: "utilities",
    name: "Electric other services combined",
    slug: "electric-other-services-combined"
  },
  4932: {
    industry: "utilities",
    name: "Gas other services combined",
    slug: "gas-other-services-combined"
  },
  4941: {
    industry: "water-supply-and-irrigation-systems",
    name: "Water supply",
    slug: "water-supply"
  },
  4950: {
    industry: "waste-treatment-and-disposal",
    name: "Sanitary services",
    slug: "sanitary-services"
  },
  4953: {
    industry: "waste-collection",
    name: "Refuse systems",
    slug: "refuse-systems"
  },
  4955: {
    industry: "waste-treatment-and-disposal",
    name: "Hazardous waste management",
    slug: "hazardous-waste-management"
  },
  4961: {
    industry: "steam-and-air-conditioning-supply",
    name: "Steam air conditioning supply",
    slug: "steam-air-conditioning-supply"
  },
  4991: {
    industry: "electric-power-generation",
    name: "Cogeneration services small power producers",
    slug: "cogeneration-services-small-power-producers"
  },
  5e3: {
    industry: "wholesale",
    name: "Wholesale durable goods",
    slug: "wholesale-durable-goods"
  },
  5010: {
    industry: "wholesale-motor-vehicles-and-parts",
    name: "Wholesale motor vehicles motor vehicle parts supplies",
    slug: "wholesale-motor-vehicles-motor-vehicle-parts-supplies"
  },
  5013: {
    industry: "wholesale-motor-vehicles-and-parts",
    name: "Wholesale motor vehicle supplies new parts",
    slug: "wholesale-motor-vehicle-supplies-new-parts"
  },
  5020: {
    industry: "wholesale-furniture-and-home-furnishings",
    name: "Wholesale furniture home furnishings",
    slug: "wholesale-furniture-home-furnishings"
  },
  5030: {
    industry: "wholesale-building-materials",
    name: "Wholesale lumber other construction materials",
    slug: "wholesale-lumber-other-construction-materials"
  },
  5031: {
    industry: "wholesale-building-materials",
    name: "Wholesale lumber plywood millwork wood panels",
    slug: "wholesale-lumber-plywood-millwork-wood-panels"
  },
  5040: {
    industry: "wholesale",
    name: "Wholesale professional commercial equipment supplies",
    slug: "wholesale-professional-commercial-equipment-supplies"
  },
  5045: {
    industry: "wholesale-computer-equipment",
    name: "Wholesale computers peripheral equipment software",
    slug: "wholesale-computers-peripheral-equipment-software"
  },
  5047: {
    industry: "medical-equipment-manufacturing",
    name: "Wholesale medical dental hospital equipment supplies",
    slug: "wholesale-medical-dental-hospital-equipment-supplies"
  },
  5050: {
    industry: "wholesale-metals-and-minerals",
    name: "Wholesale metals minerals no petroleum",
    slug: "wholesale-metals-minerals-no-petroleum"
  },
  5051: {
    industry: "wholesale-metals-and-minerals",
    name: "Wholesale metals service centers offices",
    slug: "wholesale-metals-service-centers-offices"
  },
  5063: {
    industry: "wholesale-appliances-electrical-and-electronics",
    name: "Wholesale electrical apparatus equipment wiring supplies",
    slug: "wholesale-electrical-apparatus-equipment-wiring-supplies"
  },
  5064: {
    industry: "wholesale-appliances-electrical-and-electronics",
    name: "Wholesale electrical appliances tv radio sets",
    slug: "wholesale-electrical-appliances-tv-radio-sets"
  },
  5065: {
    industry: "wholesale-appliances-electrical-and-electronics",
    name: "Wholesale electronic parts equipment nec",
    slug: "wholesale-electronic-parts-equipment-nec"
  },
  5070: {
    industry: "wholesale-hardware-plumbing-heating-equipment",
    name: "Wholesale hardware plumbing heating equipment supplies",
    slug: "wholesale-hardware-plumbing-heating-equipment-supplies"
  },
  5072: {
    industry: "wholesale-hardware-plumbing-heating-equipment",
    name: "Wholesale hardware",
    slug: "wholesale-hardware"
  },
  5080: {
    industry: "wholesale-machinery",
    name: "Wholesale machinery equipment supplies",
    slug: "wholesale-machinery-equipment-supplies"
  },
  5082: {
    industry: "wholesale-machinery",
    name: "Wholesale construction mining no petro machinery equip",
    slug: "wholesale-construction-mining-no-petro-machinery-equip"
  },
  5084: {
    industry: "wholesale-machinery",
    name: "Wholesale industrial machinery equipment",
    slug: "wholesale-industrial-machinery-equipment"
  },
  5090: {
    industry: "wholesale",
    name: "Wholesale misc durable goods",
    slug: "wholesale-misc-durable-goods"
  },
  5094: {
    industry: "wholesale-luxury-goods-and-jewelry",
    name: "Wholesale jewelry watches precious stones metals",
    slug: "wholesale-jewelry-watches-precious-stones-metals"
  },
  5099: {
    industry: "wholesale",
    name: "Wholesale durable goods nec",
    slug: "wholesale-durable-goods-nec"
  },
  5110: {
    industry: "wholesale-paper-products",
    name: "Wholesale paper paper products",
    slug: "wholesale-paper-paper-products"
  },
  5122: {
    industry: "wholesale-drugs-and-sundries",
    name: "Wholesale drugs proprietaries druggists sundries",
    slug: "wholesale-drugs-proprietaries-druggists-sundries"
  },
  5130: {
    industry: "wholesale-apparel-and-sewing-supplies",
    name: "Wholesale apparel piece goods notions",
    slug: "wholesale-apparel-piece-goods-notions"
  },
  5140: {
    industry: "wholesale-food-and-beverage",
    name: "Wholesale groceries related products",
    slug: "wholesale-groceries-related-products"
  },
  5141: {
    industry: "wholesale-food-and-beverage",
    name: "Wholesale groceries general line",
    slug: "wholesale-groceries-general-line"
  },
  5150: {
    industry: "wholesale-raw-farm-products",
    name: "Wholesale farm product raw materials",
    slug: "wholesale-farm-product-raw-materials"
  },
  5160: {
    industry: "wholesale-chemical-and-allied-products",
    name: "Wholesale chemicals allied products",
    slug: "wholesale-chemicals-allied-products"
  },
  5171: {
    industry: "wholesale-petroleum-and-petroleum-products",
    name: "Wholesale petroleum bulk stations terminals",
    slug: "wholesale-petroleum-bulk-stations-terminals"
  },
  5172: {
    industry: "wholesale-petroleum-and-petroleum-products",
    name: "Wholesale petroleum petroleum products no bulk stations",
    slug: "wholesale-petroleum-petroleum-products-no-bulk-stations"
  },
  5180: {
    industry: "wholesale-alcoholic-beverages",
    name: "Wholesale beer wine distilled alcoholic beverages",
    slug: "wholesale-beer-wine-distilled-alcoholic-beverages"
  },
  5190: {
    industry: "wholesale",
    name: "Wholesale miscellaneous nondurable goods",
    slug: "wholesale-miscellaneous-nondurable-goods"
  },
  5200: {
    industry: "retail-building-materials-and-garden-equipment",
    name: "Retail building materials hardware garden supply",
    slug: "retail-building-materials-hardware-garden-supply"
  },
  5211: {
    industry: "retail-building-materials-and-garden-equipment",
    name: "Retail lumber other building materials dealers",
    slug: "retail-lumber-other-building-materials-dealers"
  },
  5271: {
    industry: "retail-motor-vehicles",
    name: "Retail mobile home dealers",
    slug: "retail-mobile-home-dealers"
  },
  5311: {
    industry: "retail",
    name: "Retail department stores",
    slug: "retail-department-stores"
  },
  5331: {
    industry: "retail",
    name: "Retail variety stores",
    slug: "retail-variety-stores"
  },
  5399: {
    industry: "retail",
    name: "Retail misc general merchandise stores",
    slug: "retail-misc-general-merchandise-stores"
  },
  5400: {
    industry: "retail-groceries",
    name: "Retail food stores",
    slug: "retail-food-stores"
  },
  5411: {
    industry: "retail-groceries",
    name: "Retail grocery stores",
    slug: "retail-grocery-stores"
  },
  5412: {
    industry: "retail-groceries",
    name: "Retail convenience stores",
    slug: "retail-convenience-stores"
  },
  5500: {
    industry: "retail-motor-vehicles",
    name: "Retail auto dealers gasoline stations",
    slug: "retail-auto-dealers-gasoline-stations"
  },
  5531: {
    industry: "retail-motor-vehicles",
    name: "Retail auto home supply stores",
    slug: "retail-auto-home-supply-stores"
  },
  5600: {
    industry: "retail-apparel-and-fashion",
    name: "Retail apparel accessory stores",
    slug: "retail-apparel-accessory-stores"
  },
  5621: {
    industry: "retail-apparel-and-fashion",
    name: "Retail women s clothing stores",
    slug: "retail-women-s-clothing-stores"
  },
  5651: {
    industry: "retail-apparel-and-fashion",
    name: "Retail family clothing stores",
    slug: "retail-family-clothing-stores"
  },
  5661: {
    industry: "retail-apparel-and-fashion",
    name: "Retail shoe stores",
    slug: "retail-shoe-stores"
  },
  5700: {
    industry: "retail-furniture-and-home-furnishings",
    name: "Retail home furniture furnishings equipment stores",
    slug: "retail-home-furniture-furnishings-equipment-stores"
  },
  5712: {
    industry: "retail-furniture-and-home-furnishings",
    name: "Retail furniture stores",
    slug: "retail-furniture-stores"
  },
  5731: {
    industry: "retail-appliances-electrical-and-electronic-equipment",
    name: "Retail radio tv consumer electronics stores",
    slug: "retail-radio-tv-consumer-electronics-stores"
  },
  5734: {
    industry: "retail-appliances-electrical-and-electronic-equipment",
    name: "Retail computer computer software stores",
    slug: "retail-computer-computer-software-stores"
  },
  5735: {
    industry: "retail-musical-instruments",
    name: "Retail record prerecorded tape stores",
    slug: "retail-record-prerecorded-tape-stores"
  },
  5810: {
    industry: "food-and-beverage-services",
    name: "Retail eating drinking places",
    slug: "retail-eating-drinking-places"
  },
  5812: {
    industry: "food-and-beverage-services",
    name: "Retail eating places",
    slug: "retail-eating-places"
  },
  5900: {
    industry: "retail",
    name: "Retail miscellaneous retail",
    slug: "retail-miscellaneous-retail"
  },
  5912: {
    industry: "retail-pharmacies",
    name: "Retail drug stores and proprietary stores",
    slug: "retail-drug-stores-and-proprietary-stores"
  },
  5940: {
    industry: "retail",
    name: "Retail miscellaneous shopping goods stores",
    slug: "retail-miscellaneous-shopping-goods-stores"
  },
  5944: {
    industry: "retail-luxury-goods-and-jewelry",
    name: "Retail jewelry stores",
    slug: "retail-jewelry-stores"
  },
  5945: {
    industry: "retail",
    name: "Retail hobby toy game shops",
    slug: "retail-hobby-toy-game-shops"
  },
  5960: {
    industry: "online-and-mail-order-retail",
    name: "Retail nonstore retailers",
    slug: "retail-nonstore-retailers"
  },
  5961: {
    industry: "online-and-mail-order-retail",
    name: "Retail catalog mail order houses",
    slug: "retail-catalog-mail-order-houses"
  },
  5990: {
    industry: "retail",
    name: "Retail retail stores nec",
    slug: "retail-retail-stores-nec"
  },
  6021: {
    industry: "banking",
    name: "National commercial banks",
    slug: "national-commercial-banks"
  },
  6022: {
    industry: "banking",
    name: "State commercial banks",
    slug: "state-commercial-banks"
  },
  6029: {
    industry: "banking",
    name: "Commercial banks nec",
    slug: "commercial-banks-nec"
  },
  6035: {
    industry: "savings-institutions",
    name: "Savings institution federally chartered",
    slug: "savings-institution-federally-chartered"
  },
  6036: {
    industry: "savings-institutions",
    name: "Savings institutions not federally chartered",
    slug: "savings-institutions-not-federally-chartered"
  },
  6099: {
    industry: "banking",
    name: "Functions related to depository banking nec",
    slug: "functions-related-to-depository-banking-nec"
  },
  6111: {
    industry: "credit-intermediation",
    name: "Federal federally sponsored credit agencies",
    slug: "federal-federally-sponsored-credit-agencies"
  },
  6141: {
    industry: "credit-intermediation",
    name: "Personal credit institutions",
    slug: "personal-credit-institutions"
  },
  6153: {
    industry: "credit-intermediation",
    name: "Short term business credit institutions",
    slug: "short-term-business-credit-institutions"
  },
  6159: {
    industry: "credit-intermediation",
    name: "Miscellaneous business credit institution",
    slug: "miscellaneous-business-credit-institution"
  },
  6162: {
    industry: "credit-intermediation",
    name: "Mortgage bankers loan correspondents",
    slug: "mortgage-bankers-loan-correspondents"
  },
  6163: {
    industry: "loan-brokers",
    name: "Loan brokers",
    slug: "loan-brokers"
  },
  6172: {
    industry: "credit-intermediation",
    name: "Finance lessors",
    slug: "finance-lessors"
  },
  6189: {
    industry: "capital-markets",
    name: "Asset backed securities",
    slug: "asset-backed-securities"
  },
  6199: {
    industry: "financial-services",
    name: "Finance services",
    slug: "finance-services"
  },
  6200: {
    industry: "capital-markets",
    name: "Security commodity brokers dealers exchanges services",
    slug: "security-commodity-brokers-dealers-exchanges-services"
  },
  6211: {
    industry: "capital-markets",
    name: "Security brokers dealers flotation companies",
    slug: "security-brokers-dealers-flotation-companies"
  },
  6221: {
    industry: "capital-markets",
    name: "Commodity contracts brokers dealers",
    slug: "commodity-contracts-brokers-dealers"
  },
  6282: {
    industry: "investment-advice",
    name: "Investment advice",
    slug: "investment-advice"
  },
  6311: {
    industry: "insurance",
    name: "Life insurance",
    slug: "life-insurance"
  },
  6321: {
    industry: "insurance",
    name: "Accident health insurance",
    slug: "accident-health-insurance"
  },
  6324: {
    industry: "insurance",
    name: "Hospital medical service plans",
    slug: "hospital-medical-service-plans"
  },
  6331: {
    industry: "insurance",
    name: "Fire marine casualty insurance",
    slug: "fire-marine-casualty-insurance"
  },
  6351: {
    industry: "insurance",
    name: "Surety insurance",
    slug: "surety-insurance"
  },
  6361: {
    industry: "insurance",
    name: "Title insurance",
    slug: "title-insurance"
  },
  6399: {
    industry: "insurance",
    name: "Insurance carriers nec",
    slug: "insurance-carriers-nec"
  },
  6411: {
    industry: "insurance-agencies-and-brokerages",
    name: "Insurance agents brokers service",
    slug: "insurance-agents-brokers-service"
  },
  6500: {
    industry: "real-estate",
    name: "Real estate",
    slug: "real-estate"
  },
  6510: {
    industry: "leasing-non-residential-real-estate",
    name: "Real estate operators no developers lessors",
    slug: "real-estate-operators-no-developers-lessors"
  },
  6512: {
    industry: "leasing-non-residential-real-estate",
    name: "Operators of nonresidential buildings",
    slug: "operators-of-nonresidential-buildings"
  },
  6513: {
    industry: "leasing-residential-real-estate",
    name: "Operators of apartment buildings",
    slug: "operators-of-apartment-buildings"
  },
  6519: {
    industry: "leasing-non-residential-real-estate",
    name: "Lessors of real property nec",
    slug: "lessors-of-real-property-nec"
  },
  6531: {
    industry: "real-estate-agents-and-brokers",
    name: "Real estate agents managers for others",
    slug: "real-estate-agents-managers-for-others"
  },
  6532: {
    industry: "real-estate",
    name: "Real estate dealers for their own account",
    slug: "real-estate-dealers-for-their-own-account"
  },
  6552: {
    industry: "subdivision-of-land",
    name: "Land subdividers developers no cemeteries",
    slug: "land-subdividers-developers-no-cemeteries"
  },
  6770: {
    industry: "capital-markets",
    name: "Blank checks",
    slug: "blank-checks"
  },
  6792: {
    industry: "oil-and-gas",
    name: "Oil royalty traders",
    slug: "oil-royalty-traders"
  },
  6794: {
    industry: "real-estate",
    name: "Patent owners lessors",
    slug: "patent-owners-lessors"
  },
  6795: {
    industry: "mining",
    name: "Mineral royalty traders",
    slug: "mineral-royalty-traders"
  },
  6798: {
    industry: "real-estate",
    name: "Real estate investment trusts",
    slug: "real-estate-investment-trusts"
  },
  6799: {
    industry: "capital-markets",
    name: "Investors nec",
    slug: "investors-nec"
  },
  7e3: {
    industry: "accommodation-services",
    name: "Hotels rooming houses camps other lodging places",
    slug: "hotels-rooming-houses-camps-other-lodging-places"
  },
  7011: {
    industry: "accommodation-services",
    name: "Hotels motels",
    slug: "hotels-motels"
  },
  7200: {
    industry: "personal-care-services",
    name: "Services personal services",
    slug: "services-personal-services"
  },
  7310: {
    industry: "advertising-services",
    name: "Services advertising",
    slug: "services-advertising"
  },
  7311: {
    industry: "advertising-services",
    name: "Services advertising agencies",
    slug: "services-advertising-agencies"
  },
  7320: {
    industry: "collection-agencies",
    name: "Services consumer credit reporting collection agencies",
    slug: "services-consumer-credit-reporting-collection-agencies"
  },
  7330: {
    industry: "photography",
    name: "Services mailing reproduction commercial art photography",
    slug: "services-mailing-reproduction-commercial-art-photography"
  },
  7331: {
    industry: "advertising-services",
    name: "Services direct mail advertising services",
    slug: "services-direct-mail-advertising-services"
  },
  7340: {
    industry: "facilities-services",
    name: "Services to dwellings other buildings",
    slug: "services-to-dwellings-other-buildings"
  },
  7350: {
    industry: "equipment-rental-services",
    name: "Services miscellaneous equipment rental leasing",
    slug: "services-miscellaneous-equipment-rental-leasing"
  },
  7359: {
    industry: "equipment-rental-services",
    name: "Services equipment rental leasing nec",
    slug: "services-equipment-rental-leasing-nec"
  },
  7361: {
    industry: "staffing-and-recruiting",
    name: "Services employment agencies",
    slug: "services-employment-agencies"
  },
  7363: {
    industry: "staffing-and-recruiting",
    name: "Services help supply services",
    slug: "services-help-supply-services"
  },
  7370: {
    industry: "it-services-and-it-consulting",
    name: "Services computer programming data processing etc",
    slug: "services-computer-programming-data-processing-etc"
  },
  7371: {
    industry: "it-services-and-it-consulting",
    name: "Services computer programming services",
    slug: "services-computer-programming-services"
  },
  7372: {
    industry: "software-development",
    name: "Services prepackaged software",
    slug: "services-prepackaged-software"
  },
  7373: {
    industry: "it-system-design-services",
    name: "Services computer integrated systems design",
    slug: "services-computer-integrated-systems-design"
  },
  7374: {
    industry: "it-system-data-services",
    name: "Services computer processing data preparation",
    slug: "services-computer-processing-data-preparation"
  },
  7377: {
    industry: "it-system-installation-and-disposal",
    name: "Services computer rental leasing",
    slug: "services-computer-rental-leasing"
  },
  7380: {
    industry: "business-consulting-and-services",
    name: "Services miscellaneous business services",
    slug: "services-miscellaneous-business-services"
  },
  7381: {
    industry: "security-guards-and-patrol-services",
    name: "Services detective guard armored car services",
    slug: "services-detective-guard-armored-car-services"
  },
  7384: {
    industry: "photography",
    name: "Services photofinishing laboratories",
    slug: "services-photofinishing-laboratories"
  },
  7385: {
    industry: "telecommunications",
    name: "Services telephone interconnect systems",
    slug: "services-telephone-interconnect-systems"
  },
  7389: {
    industry: "business-consulting-and-services",
    name: "Services business services nec",
    slug: "services-business-services-nec"
  },
  7500: {
    industry: "vehicle-repair-and-maintenance",
    name: "Services automotive repair services parking",
    slug: "services-automotive-repair-services-parking"
  },
  7510: {
    industry: "vehicle-repair-and-maintenance",
    name: "Services auto rental leasing no drivers",
    slug: "services-auto-rental-leasing-no-drivers"
  },
  7600: {
    industry: "repair-and-maintenance",
    name: "Services miscellaneous repair services",
    slug: "services-miscellaneous-repair-services"
  },
  7812: {
    industry: "media-production",
    name: "Services motion picture video tape production",
    slug: "services-motion-picture-video-tape-production"
  },
  7819: {
    industry: "media-production",
    name: "Services allied to motion picture production",
    slug: "services-allied-to-motion-picture-production"
  },
  7822: {
    industry: "media-production",
    name: "Services motion picture video tape distribution",
    slug: "services-motion-picture-video-tape-distribution"
  },
  7829: {
    industry: "media-production",
    name: "Services allied to motion picture distribution",
    slug: "services-allied-to-motion-picture-distribution"
  },
  7830: {
    industry: "media-production",
    name: "Services motion picture theaters",
    slug: "services-motion-picture-theaters"
  },
  7841: {
    industry: "media-production",
    name: "Services video tape rental",
    slug: "services-video-tape-rental"
  },
  7900: {
    industry: "recreational-facilities",
    name: "Services amusement recreation services",
    slug: "services-amusement-recreation-services"
  },
  7948: {
    industry: "racetracks",
    name: "Services racing including track operation",
    slug: "services-racing-including-track-operation"
  },
  7990: {
    industry: "recreational-facilities",
    name: "Services miscellaneous amusement recreation",
    slug: "services-miscellaneous-amusement-recreation"
  },
  7997: {
    industry: "sports-teams-and-clubs",
    name: "Services membership sports recreation clubs",
    slug: "services-membership-sports-recreation-clubs"
  },
  8e3: {
    industry: "health-and-human-services",
    name: "Services health services",
    slug: "services-health-services"
  },
  8011: {
    industry: "medical-practices",
    name: "Services offices clinics of doctors of medicine",
    slug: "services-offices-clinics-of-doctors-of-medicine"
  },
  8050: {
    industry: "nursing-homes-and-residential-care-facilities",
    name: "Services nursing personal care facilities",
    slug: "services-nursing-personal-care-facilities"
  },
  8051: {
    industry: "nursing-homes-and-residential-care-facilities",
    name: "Services skilled nursing care facilities",
    slug: "services-skilled-nursing-care-facilities"
  },
  8060: {
    industry: "hospitals",
    name: "Services hospitals",
    slug: "services-hospitals"
  },
  8062: {
    industry: "hospitals",
    name: "Services general medical surgical hospitals nec",
    slug: "services-general-medical-surgical-hospitals-nec"
  },
  8071: {
    industry: "medical-and-diagnostic-laboratories",
    name: "Services medical laboratories",
    slug: "services-medical-laboratories"
  },
  8082: {
    industry: "home-health-care-services",
    name: "Services home health care services",
    slug: "services-home-health-care-services"
  },
  8090: {
    industry: "health-and-human-services",
    name: "Services misc health allied services nec",
    slug: "services-misc-health-allied-services-nec"
  },
  8093: {
    industry: "outpatient-care-centers",
    name: "Services specialty outpatient facilities nec",
    slug: "services-specialty-outpatient-facilities-nec"
  },
  8111: {
    industry: "legal-services",
    name: "Services legal services",
    slug: "services-legal-services"
  },
  8200: {
    industry: "education",
    name: "Services educational services",
    slug: "services-educational-services"
  },
  8300: {
    industry: "civic-and-social-organizations",
    name: "Services social services",
    slug: "services-social-services"
  },
  8351: {
    industry: "child-day-care-services",
    name: "Services child day care services",
    slug: "services-child-day-care-services"
  },
  8600: {
    industry: "professional-organizations",
    name: "Services membership organizations",
    slug: "services-membership-organizations"
  },
  8700: {
    industry: "engineering-services",
    name: "Services engineering accounting research management",
    slug: "services-engineering-accounting-research-management"
  },
  8711: {
    industry: "engineering-services",
    name: "Services engineering services",
    slug: "services-engineering-services"
  },
  8731: {
    industry: "biotechnology-research",
    name: "Services commercial physical biological research",
    slug: "services-commercial-physical-biological-research"
  },
  8734: {
    industry: "research-services",
    name: "Services testing laboratories",
    slug: "services-testing-laboratories"
  },
  8741: {
    industry: "strategic-management-services",
    name: "Services management services",
    slug: "services-management-services"
  },
  8742: {
    industry: "strategic-management-services",
    name: "Services management consulting services",
    slug: "services-management-consulting-services"
  },
  8744: {
    industry: "facilities-services",
    name: "Services facilities support management services",
    slug: "services-facilities-support-management-services"
  },
  8880: {
    industry: "capital-markets",
    name: "American depositary receipts",
    slug: "american-depositary-receipts"
  },
  8888: {
    industry: "international-affairs",
    name: "Foreign governments",
    slug: "foreign-governments"
  },
  8900: {
    industry: "professional-services",
    name: "Services services nec",
    slug: "services-services-nec"
  },
  9721: {
    industry: "international-affairs",
    name: "International affairs",
    slug: "international-affairs"
  },
  9995: {
    industry: "",
    name: "Non operating establishments",
    slug: "non-operating-establishments"
  }
};

const monthlyVisitors = [
  {
    name: {
      en: "Under 10K",
      es: "Menos de 10K",
      fr: "En dessous de 10K"
    },
    slug: "under-10k"
  },
  {
    name: {
      en: "10K - 50K",
      es: "10K - 50K",
      fr: "10K - 50K"
    },
    slug: "10k-50k"
  },
  {
    name: {
      en: "50K - 100K",
      es: "50K - 100K",
      fr: "50K - 100K"
    },
    slug: "50k-100k"
  },
  {
    name: {
      en: "100K - 500K",
      es: "100K - 500K",
      fr: "100K - 500K"
    },
    slug: "100k-500k"
  },
  {
    name: {
      en: "500K - 1M",
      es: "500K - 1M",
      fr: "500K - 1M"
    },
    slug: "500k-1m"
  },
  {
    name: {
      en: "1M - 10M",
      es: "1M - 10M",
      fr: "1M - 10M"
    },
    slug: "1m-10m"
  },
  {
    name: {
      en: "10M - 50M",
      es: "10M - 50M",
      fr: "10M - 50M"
    },
    slug: "10m-50m"
  },
  {
    name: {
      en: "50M - 100M",
      es: "50M - 100M",
      fr: "50M - 100M"
    },
    slug: "50m-100m"
  },
  {
    name: {
      en: "100M - 500M",
      es: "100M - 500M",
      fr: "100M - 500M"
    },
    slug: "100m-500m"
  },
  {
    name: {
      en: "500M - 1B",
      es: "500M - 1B",
      fr: "500M - 1B"
    },
    slug: "500m-1b"
  },
  {
    name: {
      en: "Over 1B",
      es: "M\xE1s de 1B",
      fr: "Au dessus de 1B"
    },
    slug: "over-1b"
  }
];

const revenues = [
  {
    name: {
      en: "Under $1 Million",
      es: "Menos de $1 mill\xF3n",
      fr: "En dessous de $1 Million"
    },
    slug: "under-1m"
  },
  {
    name: {
      en: "$1 million - $10 million",
      es: "$1 mill\xF3n - $10 million",
      fr: "$1 million - $10 million"
    },
    slug: "1m-10m"
  },
  {
    name: {
      en: "$10 million - $50 million",
      es: "$10 mill\xF3n - $50 mill\xF3n",
      fr: "$10 million - $50 million"
    },
    slug: "10m-50m"
  },
  {
    name: {
      en: "$50 million - $100 million",
      es: "$50 mill\xF3n - $100 mill\xF3n",
      fr: "$50 million - $100 million"
    },
    slug: "50m-100m"
  },
  {
    name: {
      en: "$100 million - $200 million",
      es: "$100 mill\xF3n - $200 mill\xF3n",
      fr: "$100 million - $200 million"
    },
    slug: "100m-200m"
  },
  {
    name: {
      en: "$200 million - $1 billion",
      es: "$200 mill\xF3n - $1 mil millones",
      fr: "$200 million - $1 milliard"
    },
    slug: "200m-1b"
  },
  {
    name: {
      en: "Over $1 billion",
      es: "M\xE1s de $1 mil millones",
      fr: "Au dessus de $1 milliard"
    },
    slug: "over-1b"
  }
];

const socialNetworks = [
  {
    name: "Dribbble",
    slug: "dribbble"
  },
  {
    name: "Facebook",
    slug: "facebook"
  },
  {
    name: "GitHub",
    slug: "github"
  },
  {
    name: "Instagram",
    slug: "instagram"
  },
  {
    name: "LinkedIn",
    slug: "linkedin"
  },
  {
    name: "Pinterest",
    slug: "pinterest"
  },
  {
    name: "Snapchat",
    slug: "snapchat"
  },
  {
    name: "TikTok",
    slug: "tiktok"
  },
  {
    name: "Twitter",
    slug: "twitter"
  },
  {
    name: "Wellfound",
    slug: "wellfound"
  },
  {
    name: "YouTube",
    slug: "youtube"
  }
];

const stockExchanges = [
  "ARCX",
  "BATS",
  "BMEX",
  "LSE",
  "MISX",
  "NASDAQ",
  "NYSE",
  "OTCM",
  "SSE",
  "TYO",
  "XAMS",
  "XASX",
  "XATH",
  "XBKK",
  "XBOM",
  "XBRU",
  "XBSE",
  "XCAI",
  "XCNQ",
  "XCSE",
  "XCYS",
  "XETR",
  "XFRA",
  "XHEL",
  "XHKG",
  "XIDX",
  "XJSE",
  "XKLS",
  "XKRX",
  "XLIM",
  "XLIT",
  "XMEX",
  "XMIL",
  "XNSE",
  "XNZE",
  "XOSL",
  "XPAR",
  "XPHS",
  "XSES",
  "XSGO",
  "XSHE",
  "XSTC",
  "XSTO",
  "XSWX",
  "XTAE",
  "XTAI",
  "XTSE",
  "XTSX",
  "XWAR",
  "XWBO"
];

const technologies = {
  "abtasty": {
    categories: [
      "a-b-testing",
      "application-development",
      "commerce",
      "conversion-rate-optimization",
      "development",
      "e-commerce",
      "e-commerce-personalization",
      "feature-management",
      "marketing",
      "mobile-app-optimization",
      "mobile-development",
      "personalization",
      "personalization-engines"
    ],
    free: false,
    g2Slug: "ab-tasty",
    name: "AB Tasty",
    paid: true,
    scripts: ["//try\\.abtasty\\.com/"],
    variables: { _abtasty: "" },
    websiteUrl: "https://www.abtasty.com/"
  },
  "acquia-marketing-cloud": {
    categories: [
      "content-management",
      "customer-data-platforms",
      "customer-journey-analytics",
      "customer-journey-mapping",
      "digital-asset-management",
      "digital-experience-platforms",
      "marketing",
      "marketing-automation",
      "personalization-engines"
    ],
    dependencies: [
      "amazon-ec2",
      "apache",
      "drupal"
    ],
    free: false,
    g2Slug: "acquia-marketing-cloud",
    headers: { "X-AH-Environment": "^\\w+$" },
    name: "Acquia Marketing Cloud",
    paid: true,
    websiteUrl: "https://www.acquia.com/marketingcloud"
  },
  "act-on": {
    categories: [
      "account-based-data",
      "account-based-marketing",
      "brand-advocacy",
      "content-management",
      "conversion-rate-optimization",
      "customer-journey-analytics",
      "customer-journey-mapping",
      "demand-generation",
      "email-template-builder",
      "employee-advocacy",
      "landing-page-builders",
      "lead-generation",
      "lead-scoring",
      "marketing",
      "marketing-account-intelligence",
      "marketing-analytics",
      "marketing-automation",
      "marketing-platforms",
      "online-form-builder",
      "personalization",
      "seo",
      "social-media-analytics",
      "social-media-management",
      "social-media-marketing",
      "transactional-email",
      "visitor-identification"
    ],
    free: false,
    g2Slug: "act-on",
    name: "Act-On",
    paid: true,
    variables: { ActOn: "" },
    websiteUrl: "https://act-on.com/"
  },
  "adcash": {
    categories: [
      "advertiser-campaign-management",
      "demand-side-platform",
      "digital-advertising",
      "display-advertising",
      "native-advertising",
      "publisher-ad-management",
      "search-monetization-platforms"
    ],
    free: true,
    g2Slug: "adcash-advertiser-platform",
    name: "Adcash",
    paid: false,
    scripts: ["^[^\\/]*//(?:[^\\/]+\\.)?adcash\\.com/(?:script|ad)/"],
    variables: {
      ac_bgclick_URL: "",
      ct_nOpp: "",
      ct_nSuUrl: "",
      ct_siteunder: "",
      ct_tag: "",
      SuLoaded: "",
      SuUrl: ""
    },
    websiteUrl: "https://adcash.com/"
  },
  "addshoppers": {
    categories: [
      "commerce",
      "demand-generation",
      "digital-advertising",
      "e-commerce",
      "e-commerce-personalization",
      "email-marketing",
      "identity-resolution",
      "lead-generation",
      "marketing",
      "personalization",
      "personalization-engines",
      "visitor-identification"
    ],
    free: true,
    g2Slug: "addshoppers",
    name: "AddShoppers",
    paid: true,
    scripts: ["cdn\\.shop\\.pe/widget/"],
    websiteUrl: "https://www.addshoppers.com/"
  },
  "addthis": {
    categories: [
      "content-distribution",
      "content-marketing",
      "marketing"
    ],
    free: true,
    g2Slug: "addthis",
    name: "AddThis",
    paid: false,
    scripts: ["addthis\\.com/js/"],
    variables: { addthis: "" },
    websiteUrl: "https://www.addthis.com/"
  },
  "addtoany": {
    categories: ["marketing"],
    free: true,
    g2Slug: "add-to-any",
    name: "AddToAny",
    paid: false,
    scripts: ["addtoany\\.com/menu/page\\.js"],
    variables: { a2apage_init: "" },
    websiteUrl: "https://www.addtoany.com/"
  },
  "adobe-commerce": {
    categories: [
      "aws-marketplace",
      "commerce",
      "e-commerce",
      "e-commerce-personalization",
      "e-commerce-platforms",
      "e-merchandising",
      "marketplace-apps",
      "office",
      "shopping-cart"
    ],
    cookies: { frontend: "" },
    dependencies: ["mysql", "php"],
    free: true,
    g2Slug: "adobe-commerce-formerly-magento-commerce",
    name: "Adobe Commerce",
    nameSynonyms: ["Magento"],
    paid: true,
    scripts: [
      "js/mage",
      "skin/frontend/(?:default|(enterprise))?Enterprise:Community",
      "static/_requirejs"
    ],
    tags: [
      '<script [^>]+data-requiremodule="Magento_',
      '<script [^>]+data-requiremodule="mage/',
      '<script type="text/x-magento-init">'
    ],
    variables: {
      Mage: "",
      VarienForm: ""
    },
    websiteUrl: "https://business.adobe.com/products/magento/magento-commerce.html"
  },
  "adobe-experience-manager": {
    categories: [
      "content-experience-platforms",
      "content-management",
      "content-marketing",
      "customer-communications-management",
      "customer-service",
      "digital-experience-platforms",
      "digital-governance",
      "it-management",
      "marketing",
      "user-generated-content",
      "web-content-management"
    ],
    dependencies: ["java"],
    free: false,
    g2Slug: "adobe-experience-manager",
    name: "Adobe Experience Manager",
    paid: true,
    scripts: [
      "/etc.clientlibs/",
      "/etc/clientlibs/",
      "/etc/designs/"
    ],
    tags: [
      '<div class="[^"]*aem-Grid',
      '<div class="[^"]*parbase',
      '<div[^>]+data-component-path="[^"+]jcr:'
    ],
    websiteUrl: "https://business.adobe.com/products/experience-manager/adobe-experience-manager.html"
  },
  "adobe-marketo-engage": {
    categories: [
      "account-based-analytics",
      "account-based-data",
      "account-based-marketing",
      "account-data-management",
      "marketing",
      "marketing-account-intelligence",
      "marketing-automation",
      "mobile-marketing"
    ],
    free: false,
    g2Slug: "adobe-marketo-engage",
    name: "Adobe Marketo Engage",
    paid: true,
    scripts: ["munchkin\\.marketo\\.net/munchkin\\.js"],
    variables: { Munchkin: "" },
    websiteUrl: "https://business.adobe.com/products/marketo.html"
  },
  "adocean": {
    categories: [
      "advertiser-campaign-management",
      "digital-advertising",
      "video-advertising"
    ],
    free: true,
    g2Slug: "adocean",
    name: "AdOcean",
    paid: false,
    scripts: ["adocean\\.pl", "adocean\\.pl/files/js/ado\\.js"],
    variables: {
      "ado.master": "",
      "ado.placement": "",
      "ado.slave": ""
    },
    websiteUrl: "https://adocean-global.com/"
  },
  "adroll": {
    categories: [
      "advertiser-campaign-management",
      "attribution",
      "cross-channel-advertising",
      "digital-advertising",
      "display-advertising",
      "email-marketing",
      "marketing",
      "mobile-advertising",
      "retargeting",
      "social-media-advertising"
    ],
    free: true,
    g2Slug: "adroll",
    name: "AdRoll",
    paid: true,
    scripts: ["(?:a|s)\\.adroll\\.com"],
    variables: {
      adroll_adv_id: "",
      adroll_pix_id: ""
    },
    websiteUrl: "https://www.adroll.com/"
  },
  "adyen": {
    categories: [
      "commerce",
      "marketplace-apps",
      "office",
      "payment",
      "payment-gateways",
      "payment-processing",
      "sap-store"
    ],
    free: false,
    g2Slug: "adyen",
    name: "Adyen",
    paid: true,
    variables: { "adyen.encrypt.version": "^(.+)$" },
    websiteUrl: "https://www.adyen.com/"
  },
  "aircall": {
    categories: [
      "call-and-contact-center",
      "call-center-infrastructure",
      "contact-center",
      "customer-service",
      "outbound-call-tracking",
      "sales",
      "sales-acceleration",
      "voip-providers"
    ],
    free: false,
    g2Slug: "aircall",
    name: "Aircall",
    paid: true,
    scripts: ["^https?://cdn\\.aircall\\.io/"],
    websiteUrl: "https://aircall.io/"
  },
  "akamai": {
    categories: ["content-delivery-network", "hosting"],
    free: false,
    g2Slug: "akamai",
    headers: { "X-Akamai-Transformed": "" },
    name: "Akamai",
    paid: true,
    websiteUrl: "https://www.akamai.com/"
  },
  "algolia": {
    categories: [
      "a-b-testing",
      "analytics",
      "aws-marketplace",
      "commerce",
      "conversion-rate-optimization",
      "e-commerce",
      "e-commerce-search",
      "e-merchandising",
      "enterprise-search",
      "marketing",
      "marketplace-apps",
      "office"
    ],
    free: true,
    g2Slug: "algolia",
    name: "Algolia",
    paid: true,
    variables: {
      "algoliaAppId": "",
      "AlgoliaSearch": "",
      "algoliasearch.version": "^(.+)$",
      "algoliaSearchApiKey": ""
    },
    websiteUrl: "https://www.algolia.com/"
  },
  "amazon-cloudfront": {
    categories: [
      "content-delivery-network",
      "enterprise-content-delivery-network",
      "hosting"
    ],
    free: false,
    g2Slug: "amazon-cloudfront",
    headers: {
      "Via": "\\(CloudFront\\)$",
      "X-Amz-Cf-Id": ""
    },
    name: "Amazon Cloudfront",
    paid: true,
    websiteUrl: "https://aws.amazon.com/cloudfront/"
  },
  "amazon-ec2": {
    categories: [
      "aws-marketplace",
      "infrastructure-as-a-serviceproviders",
      "it-infrastructure",
      "marketplace-apps",
      "office"
    ],
    free: false,
    g2Slug: "amazon-ec2",
    headers: { Server: "\\(Amazon\\)" },
    name: "Amazon EC2",
    nameSynonyms: ["Amazon Elastic Compute Cloud"],
    paid: true,
    websiteUrl: "https://aws.amazon.com/ec2/"
  },
  "amazon-ecs": {
    categories: [
      "container-management",
      "container-orchestration",
      "containerization",
      "development",
      "devops"
    ],
    free: false,
    g2Slug: "amazon-elastic-container-service-amazon-ecs",
    headers: { Server: "^ECS" },
    name: "Amazon ECS",
    nameSynonyms: ["Amazon Elastic Container Service"],
    paid: true,
    websiteUrl: "https://aws.amazon.com/ecs/"
  },
  "amazon-s3": {
    categories: [
      "archive-storage",
      "it-infrastructure",
      "object-storage",
      "storage-management"
    ],
    free: false,
    g2Slug: "amazon-s3-glacier",
    headers: { Server: "^AmazonS3$" },
    name: "Amazon S3",
    nameSynonyms: ["Amazon S3 Glacier"],
    paid: true,
    websiteUrl: "https://aws.amazon.com/s3/"
  },
  "amplitude": {
    categories: [
      "application-development",
      "conversion-rate-optimization",
      "development",
      "digital-analytics",
      "marketing",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-development",
      "product-analytics"
    ],
    free: true,
    g2Slug: "amplitude-analytics",
    name: "Amplitude",
    paid: true,
    scripts: ["cdn\\.amplitude\\.com"],
    websiteUrl: "https://amplitude.com/"
  },
  "angular": {
    categories: [
      "development",
      "integrated-development-environments",
      "javascript-web-frameworks",
      "php-integrated-development-environments",
      "php-web-frameworks",
      "web-frameworks"
    ],
    free: true,
    g2Slug: "angular",
    name: "Angular",
    paid: false,
    tags: ['<[^>]+ ng-version="([\\d.]+)"'],
    variables: {
      "ng.coreTokens": "",
      "ng.probe": ""
    },
    websiteUrl: "https://angular.dev/"
  },
  "angularjs": {
    categories: [
      "development",
      "javascript-web-frameworks",
      "web-frameworks"
    ],
    free: true,
    g2Slug: "angularjs",
    name: "AngularJS",
    paid: false,
    scripts: [
      "/([\\d.]+(?:-?rc[.\\d]*)*)/angular(?:\\.min)?\\.js",
      "angular.*\\.js",
      "angular[.-]([\\d.]*\\d)[^/]*\\.js"
    ],
    tags: ["<(?:div|html)[^>]+ng-app=", "<ng-app"],
    variables: {
      "angular": "",
      "angular.version.full": "^(.+)$"
    },
    websiteUrl: "https://angularjs.org/"
  },
  "apache": {
    categories: ["application-server", "it-infrastructure"],
    free: true,
    g2Slug: "apache-server",
    headers: { Server: "(?:Apache(?:$|/([\\d.]+)|[^/-])|(?:^|\\b)HTTPD)" },
    name: "Apache",
    nameSynonyms: ["Apache HTTP Server"],
    paid: false,
    websiteUrl: "https://httpd.apache.org/"
  },
  "apache-tomcat": {
    categories: ["application-server", "it-infrastructure"],
    dependencies: ["java"],
    free: true,
    g2Slug: "apache-tomcat",
    headers: {
      "Server": "^Apache-Coyote(?:/([\\d.]+))?",
      "X-Powered-By": "\\bTomcat\\b(?:-([\\d.]+))?"
    },
    name: "Apache Tomcat",
    paid: false,
    websiteUrl: "https://tomcat.apache.org/"
  },
  "apache-traffic-server": {
    categories: ["application-server", "it-infrastructure"],
    free: true,
    g2Slug: "apache-traffic-server",
    headers: { Server: "ATS/?([\\d.]+)?" },
    name: "Apache Traffic Server",
    paid: false,
    websiteUrl: "https://trafficserver.apache.org/"
  },
  "apache-wicket": {
    categories: [
      "development",
      "java-web-frameworks",
      "web-frameworks"
    ],
    dependencies: ["java"],
    free: true,
    g2Slug: "apache-wicket",
    name: "Apache Wicket",
    paid: false,
    variables: { Wicket: "" },
    websiteUrl: "https://wicket.apache.org/"
  },
  "apollo-graphql": {
    categories: [
      "database",
      "graph-databases",
      "it-infrastructure",
      "nosql-databases"
    ],
    free: true,
    g2Slug: "apollographql-com",
    name: "Apollo GraphQL",
    paid: true,
    variables: {
      "__APOLLO_CLIENT__": "",
      "__APOLLO_CLIENT__.version": "^(.+)$"
    },
    websiteUrl: "https://www.apollographql.com/"
  },
  "appcues": {
    categories: [
      "digital-adoption-platforms",
      "mobile-app-optimization",
      "mobile-marketing",
      "personalization"
    ],
    dns: { cname: ["custom-domains.appcues.com"] },
    free: false,
    g2Slug: "appcues",
    name: "Appcues",
    paid: true,
    scripts: ["fast\\.appcues\\.com"],
    tags: ["<!-- Appcues analytics -->"],
    urls: ["fast\\.appcues\\.com"],
    variables: { AppcuesSettings: "" },
    websiteUrl: "https://www.appcues.com/"
  },
  "apple-business-manager": {
    categories: [
      "cloud-file-storage",
      "office-suites",
      "team-collaboration"
    ],
    dns: { txt: ["apple-domain-verification="] },
    free: false,
    name: "Apple Business Manager",
    paid: true,
    websiteUrl: "https://www.apple.com/business/"
  },
  "appnexus": {
    categories: [
      "digital-advertising",
      "publisher-ad-management",
      "supply-side-platforms"
    ],
    free: true,
    g2Slug: "appnexus-marketplace",
    name: "AppNexus",
    paid: true,
    scripts: ["adnxs\\.(?:net|com)"],
    tags: ["<(?:iframe|img)[^>]+adnxs\\.(?:net|com)"],
    websiteUrl: "https://www.appnexus.com/"
  },
  "arcgis-online": {
    categories: [
      "analytics",
      "business-intelligence",
      "gis",
      "location-intelligence",
      "marketplace-apps",
      "office",
      "sap-store"
    ],
    free: true,
    g2Slug: "arcgis-online",
    name: "ArcGIS Online",
    paid: false,
    scripts: ["basemaps\\.arcgis\\.com", "js\\.arcgis\\.com"],
    websiteUrl: "https://www.arcgis.com/home/index.html"
  },
  "arvancloud": {
    categories: ["content-delivery-network", "hosting"],
    free: true,
    g2Slug: "arvancloud",
    headers: { "AR-PoweredBy": "Arvan Cloud \\(arvancloud\\.com\\)" },
    name: "ArvanCloud",
    paid: true,
    variables: { ArvanCloud: "" },
    websiteUrl: "https://www.arvancloud.ir/"
  },
  "astro": {
    categories: [
      "application-development",
      "development",
      "e-commerce",
      "e-commerce-personalization",
      "feature-management",
      "marketing",
      "mobile-app-optimization",
      "mobile-development",
      "personalization",
      "personalization-engines"
    ],
    free: true,
    g2Slug: "astro",
    name: "Astro",
    paid: false,
    tags: ["<[^>]+data-astro-"],
    variables: { Astro: "" },
    websiteUrl: "https://astro.build"
  },
  "at-internet": {
    categories: ["digital-analytics", "marketing"],
    free: false,
    g2Slug: "at-internet-s-analytics-suite",
    name: "AT Internet Analyzer",
    paid: true,
    variables: {
      ATInternet: "",
      xtsite: ""
    },
    websiteUrl: "https://www.atinternet.com/"
  },
  "at-internet-xiti": {
    categories: ["digital-analytics", "marketing"],
    free: true,
    g2Slug: "xiti",
    name: "AT Internet XiTi",
    paid: true,
    scripts: ["xiti\\.com/hit\\.xiti"],
    variables: { xt_click: "" },
    websiteUrl: "https://www.atinternet.com/"
  },
  "aurelia": {
    categories: [
      "development",
      "java-web-frameworks",
      "web-frameworks"
    ],
    free: true,
    g2Slug: "aurelia",
    name: "Aurelia",
    paid: false,
    scripts: ["aurelia(?:\\.min)?\\.js"],
    tags: [
      "<[^>]+au-target-id=[^>]\\d",
      "<[^>]+aurelia-app=[^>]",
      "<[^>]+data-main=[^>]aurelia-bootstrapper"
    ],
    websiteUrl: "https://aurelia.io/"
  },
  "awstats": {
    categories: [
      "it-infrastructure",
      "log-monitoring",
      "monitoring"
    ],
    dependencies: ["perl"],
    free: true,
    g2Slug: "awstats",
    metas: { generator: "AWStats ([\\d.]+(?: \\(build [\\d.]+\\))?)" },
    name: "AWStats",
    paid: false,
    websiteUrl: "https://awstats.sourceforge.io/"
  },
  "backbone-js": {
    categories: [
      "development",
      "javascript-web-frameworks",
      "web-frameworks"
    ],
    free: true,
    g2Slug: "backbone-js",
    name: "Backbone",
    paid: false,
    scripts: ["backbone.*\\.js"],
    variables: {
      "Backbone": "",
      "Backbone.VERSION": "^(.+)$"
    },
    websiteUrl: "https://backbonejs.org/"
  },
  "backdrop-cms": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["php"],
    free: true,
    g2Slug: "backdrop-cms",
    headers: {
      "X-Backdrop-Cache": "",
      "X-Generator": "^Backdrop CMS(?:\\s([\\d.]+))?"
    },
    metas: { generator: "^Backdrop CMS(?:\\s([\\d.]+))?" },
    name: "Backdrop",
    paid: false,
    variables: { Backdrop: "" },
    websiteUrl: "https://backdropcms.org/"
  },
  "betterdocs": {
    categories: ["help-authoring-tools"],
    free: false,
    g2Slug: "betterdocs",
    name: "BetterDocs",
    paid: true,
    scripts: ["/wp-content/plugins/betterdocs/"],
    variables: {
      betterdocs_pro: "",
      betterdocspublic: ""
    },
    websiteUrl: "https://betterdocs.co/"
  },
  "bigcommerce": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms",
      "omnichannel-commerce"
    ],
    free: false,
    g2Slug: "bigcommerce",
    name: "Bigcommerce",
    paid: true,
    scripts: ["cdn\\d+\\.bigcommerce\\.com/", "checkout-sdk\\.bigcommerce\\.com/"],
    tags: ["<link href=[^>]+cdn\\d+\\.bigcommerce\\.com/"],
    urls: ["^https?://[^/]+\\.mybigcommerce\\.com"],
    websiteUrl: "https://www.bigcommerce.com/"
  },
  "blogger": {
    categories: [
      "design",
      "software-design",
      "web-design"
    ],
    dependencies: ["python"],
    free: true,
    g2Slug: "blogger",
    metas: { generator: "^Blogger$" },
    name: "Blogger",
    paid: false,
    urls: ["^https?://[^/]+\\.blogspot\\.com"],
    websiteUrl: "https://www.blogger.com/"
  },
  "boldgrid": {
    categories: [
      "content-management",
      "website-builder",
      "wordpress-site-management"
    ],
    dependencies: ["wordpress"],
    free: false,
    g2Slug: "boldgrid",
    name: "BoldGrid",
    paid: true,
    scripts: ["/wp-content/plugins/post-and-page-builder"],
    tags: [
      `<link rel=["']stylesheet["'] [^>]+boldgrid`,
      `<link rel=["']stylesheet["'] [^>]+post-and-page-builder`,
      "<link[^>]+s\\d+\\.boldgrid\\.com"
    ],
    websiteUrl: "https://www.boldgrid.com/"
  },
  "braintree": {
    categories: [
      "commerce",
      "e-commerce",
      "payment",
      "payment-processing",
      "subscription-billing"
    ],
    free: false,
    g2Slug: "braintree-direct",
    name: "Braintree",
    paid: true,
    variables: {
      "Braintree": "",
      "Braintree.version": "^(.+)$"
    },
    websiteUrl: "https://www.braintreepayments.com/"
  },
  "brevo": {
    categories: [
      "conversion-rate-optimization",
      "email-deliverability",
      "email-template-builder",
      "email-testing",
      "landing-page-builders",
      "marketing",
      "marketing-automation",
      "sms-marketing",
      "transactional-email"
    ],
    dns: {
      txt: [
        "brevo-code:",
        "Sendinblue-code:",
        "include:spf.sendinblue.com"
      ]
    },
    free: true,
    g2Slug: "brevo-marketing-platform",
    name: "Brevo",
    nameSynonyms: ["Sendinblue"],
    paid: true,
    websiteUrl: "https://www.brevo.com/"
  },
  "bubble": {
    categories: [
      "application-development",
      "content-management",
      "design",
      "development",
      "drag-and-drop-app-builder",
      "hosting",
      "mobile-development",
      "no-code-development-platforms",
      "rapid-application-development",
      "software-design",
      "web-design",
      "web-hosting-providers",
      "webops-platforms",
      "website-builder"
    ],
    dependencies: ["node-js"],
    free: true,
    g2Slug: "bubble",
    headers: {
      "x-bubble-capacity-limit": "",
      "x-bubble-capacity-used": "",
      "x-bubble-perf": ""
    },
    name: "Bubble",
    paid: true,
    variables: {
      _bubble_page_load_data: "",
      bubble_environment: "",
      bubble_hostname_modifier: "",
      bubble_version: ""
    },
    websiteUrl: "https://bubble.io/"
  },
  "bugsnag": {
    categories: [
      "bug-tracking",
      "development",
      "devops"
    ],
    free: false,
    g2Slug: "bugsnag",
    name: "BugSnag",
    paid: true,
    scripts: ["/bugsnag.*\\.js"],
    urls: ["^https?://notify.bugsnag.com/"],
    variables: {
      Bugsnag: "",
      bugsnag: "",
      bugsnagClient: ""
    },
    websiteUrl: "https://www.bugsnag.com/"
  },
  "buysellads": {
    categories: [
      "advertiser-campaign-management",
      "cross-channel-advertising",
      "digital-advertising",
      "display-advertising",
      "mobile-advertising",
      "native-advertising",
      "publisher-ad-management",
      "publisher-ad-server",
      "video-advertising"
    ],
    free: true,
    g2Slug: "buysellads",
    name: "BuySellAds",
    paid: false,
    scripts: ["^https?://s\\d\\.buysellads\\.com/"],
    variables: {
      _bsa: "",
      _bsap: "",
      _bsap_serving_callback: "",
      _bsaPRO: ""
    },
    websiteUrl: "https://www.buysellads.com/"
  },
  "caddy": {
    categories: ["servers"],
    dependencies: ["go"],
    free: true,
    headers: { Server: "^Caddy$" },
    name: "Caddy",
    paid: false,
    websiteUrl: "https://caddyserver.com/"
  },
  "cakephp": {
    categories: [
      "development",
      "php-web-frameworks",
      "web-frameworks"
    ],
    cookies: { cakephp: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "cakephp",
    metas: { "application-name": "CakePHP" },
    name: "CakePHP",
    paid: false,
    websiteUrl: "https://cakephp.org/"
  },
  "carbon-ads": {
    categories: [
      "advertiser-campaign-management",
      "digital-advertising",
      "display-advertising"
    ],
    free: true,
    g2Slug: "carbon-ads",
    name: "Carbon Ads",
    paid: false,
    scripts: ["^(?:https?:)?\\/\\/(?:[^\\/]+\\.)*carbonads\\.com\\/"],
    tags: ['<[a-z]+ [^>]*id="carbonads-container"'],
    variables: { _carbonads: "" },
    websiteUrl: "https://www.carbonads.net/"
  },
  "cargo": {
    categories: ["content-management"],
    dependencies: ["php"],
    free: true,
    metas: { cargo_title: "" },
    name: "Cargo",
    paid: true,
    scripts: ["/cargo\\."],
    tags: ["<link [^>]+Cargo feed"],
    websiteUrl: "https://cargo.site/"
  },
  "chameleon": {
    categories: ["digital-adoption-platforms", "customer-self-service"],
    free: true,
    g2Slug: "chameleon",
    name: "Chameleon",
    paid: true,
    scripts: ["fast\\.chameleon\\.io/messo/"],
    variables: { chmln: "" },
    websiteUrl: "https://www.chameleon.io"
  },
  "chartbeat": {
    categories: [
      "a-b-testing",
      "application-development",
      "content-analytics",
      "content-marketing",
      "conversion-rate-optimization",
      "development",
      "digital-analytics",
      "marketing",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-development"
    ],
    free: true,
    g2Slug: "chartbeat",
    name: "Chartbeat",
    paid: true,
    scripts: ["chartbeat\\.js"],
    variables: {
      _sf_async_config: "",
      _sf_endpt: ""
    },
    websiteUrl: "https://chartbeat.com/"
  },
  "clerk-dev": {
    categories: ["customer-identity-and-access-management", "multi-factor-authentication"],
    free: true,
    g2Slug: "clerk-dev",
    name: "Clerk",
    nameSynonyms: ["Clerk.dev"],
    paid: true,
    scripts: ["@clerk/clerk-js", "clerk.browser.js"],
    tags: ['<div id="clerk-components"></div>'],
    urls: ["img.clerk.com/"],
    variables: {
      __clerk_publishable_key: "",
      Clerk: ""
    },
    websiteUrl: "https://clerk.com/"
  },
  "clickfunnels": {
    categories: [
      "a-b-testing",
      "content-management",
      "conversion-rate-optimization",
      "landing-page-builders",
      "marketing",
      "website-builder"
    ],
    free: false,
    g2Slug: "clickfunnels",
    name: "ClickFunnels",
    paid: true,
    tags: ['<meta property="cf:app_domain" content="app.clickfunnels.com"'],
    websiteUrl: "https://www.clickfunnels.com/"
  },
  "clicky": {
    categories: [
      "content-analytics",
      "content-marketing",
      "digital-analytics",
      "marketing"
    ],
    free: true,
    g2Slug: "clicky",
    name: "Clicky",
    paid: true,
    scripts: ["static\\.getclicky\\.com"],
    variables: { clicky: "" },
    websiteUrl: "https://clicky.com/"
  },
  "cloudcart": {
    categories: [
      "commerce",
      "e-commerce",
      "shopping-cart"
    ],
    free: true,
    g2Slug: "cloudcart",
    metas: { author: "^CloudCart LLC$" },
    name: "CloudCart",
    paid: true,
    scripts: ["/cloudcart-(?:assets|storage)/"],
    websiteUrl: "https://cloudcart.com/"
  },
  "cloudflare-cdn": {
    categories: [
      "content-delivery-network",
      "enterprise-content-delivery-network",
      "hosting"
    ],
    cookies: { __cfduid: "" },
    free: true,
    g2Slug: "cloudflare-inc-cloudflare-cdn",
    headers: {
      "cf-cache-status": "",
      "cf-ray": "",
      "Server": "^cloudflare$"
    },
    name: "CloudFlare",
    paid: true,
    variables: { CloudFlare: "" },
    websiteUrl: "https://www.cloudflare.com/application-services/products/cdn/"
  },
  "cms-made-simple": {
    categories: ["content-management", "web-content-management"],
    cookies: { CMSSESSID: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "cms-made-simple",
    metas: { generator: "CMS Made Simple" },
    name: "CMS Made Simple",
    paid: false,
    websiteUrl: "https://www.cmsmadesimple.org/"
  },
  "codeigniter": {
    categories: [
      "development",
      "php-web-frameworks",
      "web-frameworks"
    ],
    cookies: {
      ci_csrf_token: "^(.+)$?2+:",
      ci_session: "",
      exp_last_activity: "",
      exp_tracker: ""
    },
    dependencies: ["php"],
    free: true,
    g2Slug: "codeigniter",
    name: "CodeIgniter",
    paid: false,
    tags: ['<input[^>]+name="ci_csrf_token"'],
    websiteUrl: "https://codeigniter.com/"
  },
  "command-ai": {
    categories: ["digital-adoption-platforms", "chatbots"],
    free: false,
    g2Slug: "command-ai",
    name: "Command AI",
    nameSynonyms: ["CommandBar"],
    paid: true,
    scripts: ["(api|cdn)\\.commandbar\\.com/"],
    tags: ["<div[^>]+data-commandbar-toplevel-styles"],
    variables: { CommandBar: "" },
    websiteUrl: "https://www.command.ai/"
  },
  "comscore": {
    categories: ["digital-analytics", "marketing"],
    free: true,
    g2Slug: "comscore",
    name: "comScore",
    paid: false,
    scripts: ["\\.scorecardresearch\\.com/beacon\\.js|COMSCORE\\.beacon"],
    tags: ['<iframe[^>]* (?:id="comscore"|scr=[^>]+comscore)|\\.scorecardresearch\\.com/beacon\\.js|COMSCORE\\.beacon'],
    variables: {
      _COMSCORE: "",
      COMSCORE: ""
    },
    websiteUrl: "https://www.comscore.com/"
  },
  "concrete-cms": {
    categories: ["content-management", "web-content-management"],
    cookies: { CONCRETE5: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "concrete-cms",
    metas: { generator: "^concrete5 - ([\\d.]+)$" },
    name: "Concrete CMS",
    paid: false,
    scripts: ["/concrete/js/"],
    variables: { CCM_IMAGE_PATH: "" },
    websiteUrl: "https://www.concretecms.com/"
  },
  "contao": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["php"],
    free: true,
    g2Slug: "contao",
    metas: { generator: "^Contao Open Source CMS$" },
    name: "Contao",
    paid: false,
    tags: ["<!--[^>]+powered by (?:TYPOlight|Contao)[^>]*-->", "<link[^>]+(?:typolight|contao)\\.css"],
    websiteUrl: "https://contao.org/"
  },
  "contentful": {
    categories: [
      "ar-vr",
      "content-management",
      "development",
      "headless-cms",
      "marketplace-apps",
      "office",
      "sap-store",
      "virtual-reality",
      "vr-content-management-systems",
      "web-content-management",
      "webops-platforms"
    ],
    free: false,
    g2Slug: "contentful",
    name: "Contentful",
    paid: true,
    tags: ["<[^>]+(?:https?:)?//(?:assets|downloads|images|videos)\\.(?:ct?fassets\\.net|contentful\\.com)"],
    websiteUrl: "https://www.contentful.com/"
  },
  "countly": {
    categories: [
      "application-development",
      "commerce",
      "conversion-rate-optimization",
      "development",
      "digital-analytics",
      "e-commerce",
      "e-commerce-analytics",
      "marketing",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-app-optimization",
      "mobile-crash-reporting",
      "mobile-development",
      "product-analytics",
      "push-notification"
    ],
    free: true,
    g2Slug: "countly",
    name: "Countly",
    paid: true,
    variables: { Countly: "" },
    websiteUrl: "https://countly.com/"
  },
  "cowboy": {
    categories: ["application-server", "it-infrastructure"],
    dependencies: ["erlang"],
    free: true,
    g2Slug: "cowboy",
    headers: { Server: "^Cowboy$" },
    name: "Cowboy",
    paid: false,
    websiteUrl: "https://ninenines.eu/"
  },
  "cpanel": {
    categories: ["hosting", "virtual-private-serversproviders"],
    cookies: {
      cprelogin: "",
      cpsession: ""
    },
    free: false,
    g2Slug: "cpanel",
    headers: { Server: "cpsrvd/([\\d.]+)" },
    name: "cPanel",
    paid: true,
    tags: ["<!-- cPanel"],
    websiteUrl: "https://cpanel.net/"
  },
  "craft-cms": {
    categories: [
      "content-management",
      "headless-cms",
      "web-content-management"
    ],
    cookies: { CraftSessionId: "" },
    free: true,
    g2Slug: "craft-cms",
    headers: { "X-Powered-By": "\\bCraft CMS\\b" },
    name: "Craft CMS",
    paid: true,
    websiteUrl: "https://craftcms.com/"
  },
  "craft-cms-commerce": {
    categories: ["e-commerce-platforms"],
    dependencies: ["craft-cms"],
    free: true,
    headers: { "X-Powered-By": "\\bCraft Commerce\\b" },
    name: "Craft Commerce",
    paid: true,
    websiteUrl: "https://craftcms.com/commerce"
  },
  "crazy-egg": {
    categories: [
      "a-b-testing",
      "conversion-rate-optimization",
      "heatmap",
      "marketing",
      "session-replay"
    ],
    free: true,
    g2Slug: "crazy-egg",
    name: "Crazy Egg",
    paid: true,
    scripts: ["script\\.crazyegg\\.com/pages/scripts/\\d+/\\d+\\.js"],
    variables: { CE2: "" },
    websiteUrl: "https://www.crazyegg.com/"
  },
  "crisp": {
    categories: [
      "conversational-support",
      "customer-self-service",
      "customer-service",
      "digital-customer-service-platforms",
      "help-desk",
      "live-chat",
      "social-customer-service"
    ],
    free: true,
    g2Slug: "crisp",
    name: "Crisp",
    paid: true,
    scripts: ["//client\\.crisp\\.chat/l\\.js"],
    variables: {
      $crisp: "",
      CRISP_WEBSITE_ID: ""
    },
    websiteUrl: "https://crisp.chat/"
  },
  "criteo": {
    categories: [
      "advertiser-campaign-management",
      "cross-channel-advertising",
      "demand-side-platform",
      "digital-advertising",
      "display-advertising",
      "mobile-advertising",
      "other-digital-advertising",
      "retargeting",
      "social-media-advertising"
    ],
    free: true,
    g2Slug: "criteo-marketing-solutions",
    name: "Criteo",
    paid: false,
    scripts: ["//(?:cas\\.criteo\\.com|(?:[^/]\\.)?criteo\\.net)/", "//static.criteo.net/js/ld/ld.js"],
    variables: {
      Criteo: "",
      criteo_pubtag: "",
      criteo_q: ""
    },
    websiteUrl: "https://www.criteo.com/"
  },
  "cs-cart": {
    categories: [
      "commerce",
      "e-commerce",
      "shopping-cart"
    ],
    dependencies: ["php"],
    free: false,
    g2Slug: "cs-cart",
    name: "CS Cart",
    paid: true,
    tags: ["&nbsp;Powered by (?:<a href=[^>]+cs-cart\\.com|CS-Cart)", "\\.cm-noscript[^>]+</style>"],
    variables: { fn_compare_strings: "" },
    websiteUrl: "https://www.cs-cart.com/"
  },
  "dart": {
    categories: ["development", "other-development"],
    free: true,
    g2Slug: "dart-dart",
    name: "Dart",
    paid: false,
    scripts: ["/(?:\\.)?(?:dart)(?:\\.js)?/", "packages/browser/dart\\.js"],
    tags: ['/(?:<script)[^>]+(?:type="application/dart")/'],
    variables: {
      ___dart__$dart_dartObject_ZxYxX_0_: "",
      ___dart_dispatch_record_ZxYxX_0_: ""
    },
    websiteUrl: "https://dart.dev/"
  },
  "datadog": {
    categories: [
      "aiops-platforms",
      "api-marketplaces",
      "application-performance-monitoring",
      "cloud-infrastructure-monitoring",
      "container-monitoring",
      "containerization",
      "database-monitoring",
      "development",
      "devops",
      "devsecops",
      "enterprise-monitoring",
      "incident-response",
      "iot-analytics",
      "iot-device-management",
      "iot-management",
      "it-alerting",
      "it-infrastructure",
      "it-management",
      "log-analysis",
      "log-monitoring",
      "monitoring",
      "network-monitoring",
      "network-security",
      "network-traffic-analysis",
      "observability-solution-suites",
      "security",
      "security-information-and-event-management",
      "server-monitoring",
      "system-security",
      "website-monitoring"
    ],
    free: true,
    g2Slug: "datadog",
    name: "Datadog",
    paid: true,
    scripts: ["datadoghq\\.com", "www\\.datadoghq-browser-agent\\.com"],
    variables: {
      DD_LOGS: "",
      DD_RUM: ""
    },
    websiteUrl: "https://www.datadoghq.com/"
  },
  "datadome": {
    categories: [
      "bot-detection-and-mitigation",
      "cloud-ddos-mitigation",
      "cloud-security",
      "ddos-protection",
      "fraud-detection",
      "security",
      "web-security"
    ],
    cookies: { datadome: "" },
    free: false,
    g2Slug: "datadome",
    headers: {
      "Server": "^DataDome$",
      "X-DataDome": "",
      "X-DataDome-CID": ""
    },
    name: "Datadome",
    paid: true,
    scripts: ["^https://ct\\.datadome\\.co/[a-z]\\.js$", "js\\.datadome\\.co/"],
    websiteUrl: "https://datadome.co/"
  },
  "datalife-engine": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["apache", "php"],
    free: false,
    g2Slug: "datalife-engine-dle",
    metas: { generator: "DataLife Engine" },
    name: "DataLife Engine",
    nameSynonyms: ["DLE"],
    paid: true,
    variables: { dle_root: "" },
    websiteUrl: "https://dle-news.com/"
  },
  "datocms": {
    categories: ["content-management", "headless-cms"],
    free: true,
    g2Slug: "datocms",
    name: "DatoCMS",
    paid: true,
    tags: ["<[^>]+https://www\\.datocms-assets\\.com"],
    websiteUrl: "https://www.datocms.com/"
  },
  "didomi": {
    categories: [
      "consent-management-platforms",
      "data-privacy",
      "marketing",
      "personalization"
    ],
    free: false,
    g2Slug: "didomi",
    name: "Didomi",
    paid: true,
    scripts: ["sdk\\.privacy-center\\.org"],
    variables: {
      Didomi: "",
      didomiConfig: ""
    },
    websiteUrl: "https://www.didomi.io/"
  },
  "discourse": {
    categories: ["marketing", "online-community-management"],
    dependencies: ["ruby-on-rails"],
    free: true,
    g2Slug: "discourse",
    metas: { generator: "Discourse(?: ?/?([\\d.]+\\d))?" },
    name: "Discourse",
    paid: false,
    variables: { Discourse: "" },
    websiteUrl: "https://www.discourse.org/"
  },
  "django": {
    categories: [
      "development",
      "python-web-frameworks",
      "web-frameworks"
    ],
    dependencies: ["python"],
    free: true,
    g2Slug: "django",
    name: "Django",
    paid: false,
    tags: [`(?:powered by <a[^>]+>Django ?([\\d.]+)?<\\/a>|<input[^>]*name=["']csrfmiddlewaretoken["'][^>]*>)`],
    variables: {
      __admin_media_prefix__: "",
      django: ""
    },
    websiteUrl: "https://www.djangoproject.com/"
  },
  "dnn-platform": {
    categories: ["content-management", "web-content-management"],
    cookies: { DotNetNukeAnonymous: "" },
    dependencies: ["microsoft-asp-net"],
    free: false,
    g2Slug: "dnn-foundation-dnn-platform",
    headers: {
      "Cookie": "dnn_IsMobile=",
      "DNNOutputCache": "",
      "X-Compressed-By": "DotNetNuke"
    },
    metas: { generator: "DotNetNuke" },
    name: "DNN Platform",
    nameSynonyms: ["DotNetNuke"],
    paid: true,
    scripts: ["/js/dnn\\.js", "/js/dnncore\\.js"],
    tags: ["<!-- DNN Platform", "<!-- by DotNetNuke Corporation"],
    variables: {
      "dnn.apiversion": "^(.+)$",
      "DotNetNuke": ""
    },
    websiteUrl: "https://www.dnnsoftware.com/"
  },
  "docusaurus": {
    categories: ["knowledge-base"],
    dependencies: ["react"],
    free: true,
    metas: { generator: "^Docusaurus$" },
    name: "Docusaurus",
    paid: false,
    variables: { "search.indexName": "" },
    websiteUrl: "https://docusaurus.io/"
  },
  "dokuwiki": {
    categories: [
      "content-management",
      "knowledge-base",
      "knowledge-management"
    ],
    cookies: { DokuWiki: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "dokuwiki",
    metas: { generator: "^DokuWiki( Release [\\d-]+)?" },
    name: "DokuWiki",
    paid: false,
    tags: ['<a[^>]+href="#dokuwiki__', '<div[^>]+id="dokuwiki__>'],
    websiteUrl: "https://www.dokuwiki.org/"
  },
  "drupal": {
    categories: [
      "aws-marketplace",
      "content-management",
      "marketplace-apps",
      "office",
      "web-content-management"
    ],
    dependencies: ["php"],
    free: true,
    g2Slug: "drupal",
    headers: {
      "Expires": "19 Nov 1978",
      "X-Drupal-Cache": "",
      "X-Generator": "^Drupal(?:\\s([\\d.]+))?"
    },
    metas: { generator: "^Drupal(?:\\s([\\d.]+))?" },
    name: "Drupal",
    paid: false,
    scripts: ["drupal\\.js"],
    tags: ['<(?:link|style)[^>]+"/sites/(?:default|all)/(?:themes|modules)/'],
    variables: { Drupal: "" },
    websiteUrl: "https://www.drupal.org/"
  },
  "drupal-commerce": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    dependencies: ["drupal"],
    free: true,
    g2Slug: "drupal-commerce",
    name: "Drupal Commerce",
    paid: false,
    tags: ['<[^>]+(?:id="block[_-]commerce[_-]cart[_-]cart|class="commerce[_-]product[_-]field)'],
    websiteUrl: "https://drupalcommerce.org/"
  },
  "duda": {
    categories: [
      "content-management",
      "conversion-rate-optimization",
      "landing-page-builders",
      "marketing",
      "personalization",
      "web-content-management",
      "website-builder"
    ],
    free: false,
    g2Slug: "duda",
    name: "Duda",
    paid: true,
    variables: {
      SystemID: "^.*DIRECT.*$",
      version: "^(.*)$"
    },
    websiteUrl: "https://www.duda.co/"
  },
  "dynamicweb": {
    categories: ["content-management"],
    cookies: { Dynamicweb: "" },
    dependencies: ["microsoft-asp-net"],
    free: false,
    metas: { generator: "Dynamicweb ([\\d.]+)" },
    name: "Dynamicweb",
    paid: true,
    websiteUrl: "https://www.dynamicweb.com/"
  },
  "dynatrace": {
    categories: [
      "aiops-platforms",
      "application-performance-monitoring",
      "application-security",
      "cloud-infrastructure-monitoring",
      "container-monitoring",
      "containerization",
      "conversion-rate-optimization",
      "database-monitoring",
      "development",
      "devops",
      "devsecops",
      "digital-experience-monitoring",
      "enterprise-monitoring",
      "incident-response",
      "it-alerting",
      "it-infrastructure",
      "it-management",
      "log-analysis",
      "log-monitoring",
      "marketing",
      "marketplace-apps",
      "monitoring",
      "network-monitoring",
      "observability-solution-suites",
      "office",
      "runtime-application-self-protection",
      "sap-store",
      "security",
      "servicenow-marketplace-apps",
      "session-replay",
      "system-security",
      "website-monitoring"
    ],
    free: true,
    g2Slug: "dynatrace",
    headers: { "X-dynaTrace-JS-Agent": "" },
    name: "Dynatrace",
    paid: true,
    scripts: ["dtagent.*\\.js"],
    websiteUrl: "https://www.dynatrace.com/"
  },
  "elementor": {
    categories: [
      "content-management",
      "marketing",
      "pop-up-builder",
      "website-builder"
    ],
    dependencies: ["wordpress"],
    free: true,
    g2Slug: "elementor",
    name: "Elementor",
    paid: true,
    scripts: ["elementor/assets/js/[^/]+\\.js\\?ver=([\\d.]+)$"],
    tags: [
      `<div class=(?:"|')[^"']*elementor`,
      `<link [^>]*href=(?:"|')[^"']*elementor/assets`,
      `<link [^>]*href=(?:"|')[^"']*uploads/elementor/css`,
      `<section class=(?:"|')[^"']*elementor`
    ],
    variables: { "elementorFrontend.getElements": "" },
    websiteUrl: "https://elementor.com/"
  },
  "ember-js": {
    categories: [
      "development",
      "javascript-web-frameworks",
      "web-frameworks"
    ],
    free: true,
    g2Slug: "ember-js",
    name: "Ember",
    paid: false,
    variables: {
      "Ember": "",
      "Ember.VERSION": "^(.+)$"
    },
    websiteUrl: "https://emberjs.com/"
  },
  "ensighten": {
    categories: ["marketing", "tag-management"],
    free: true,
    g2Slug: "ensighten-manage",
    name: "Ensighten",
    paid: true,
    scripts: ["//nexus\\.ensighten\\.com/"],
    websiteUrl: "https://www.ensighten.com/"
  },
  "envoy-proxy": {
    categories: [
      "cloud-security",
      "security",
      "service-mesh"
    ],
    free: true,
    g2Slug: "the-linux-foundation-envoy",
    headers: {
      "Server": "^envoy$",
      "x-envoy-upstream-service-time": ""
    },
    name: "Envoy Proxy",
    paid: false,
    websiteUrl: "https://www.envoyproxy.io/"
  },
  "epages": {
    categories: [
      "commerce",
      "e-commerce",
      "shopping-cart"
    ],
    free: false,
    g2Slug: "epages",
    name: "EPages",
    paid: true,
    variables: { epages: "" },
    websiteUrl: "https://epages.com/"
  },
  "erlang": {
    categories: ["programming-languages"],
    free: true,
    headers: { Server: "Erlang( OTP/(?:[\\d.ABR-]+))?" },
    name: "Erlang",
    paid: false,
    websiteUrl: "https://www.erlang.org/"
  },
  "express-js": {
    categories: [
      "development",
      "javascript-web-frameworks",
      "web-frameworks"
    ],
    dependencies: ["node-js"],
    free: true,
    g2Slug: "express-js",
    headers: { "X-Powered-By": "^Express$" },
    name: "Express",
    paid: false,
    websiteUrl: "https://expressjs.com/"
  },
  "expressionengine": {
    categories: ["content-management", "web-content-management"],
    cookies: {
      exp_csrf_token: "",
      exp_last_activity: "",
      exp_tracker: ""
    },
    dependencies: ["php"],
    free: true,
    g2Slug: "expressionengine",
    name: "ExpressionEngine",
    paid: true,
    websiteUrl: "https://expressionengine.com/"
  },
  "facebook-social-plugins": {
    categories: ["social-media-marketing"],
    free: true,
    name: "Facebook Social Plugins",
    paid: false,
    scripts: ["//connect\\.facebook\\.net/[^/]*/[a-z]*\\.js"],
    websiteUrl: "https://developers.facebook.com/docs/plugins/"
  },
  "fastly": {
    categories: [
      "content-delivery-network",
      "enterprise-content-delivery-network",
      "hosting",
      "it-management",
      "virtual-waiting-room"
    ],
    free: true,
    g2Slug: "fastly",
    headers: {
      "Fastly-Debug-Digest": "",
      "Vary": "Fastly-SSL",
      "X-Fastly-Request-ID": "",
      "x-via-fastly:": ""
    },
    name: "Fastly",
    paid: false,
    websiteUrl: "https://www.fastly.com/"
  },
  "firebase": {
    categories: [
      "application-development",
      "application-development-platforms",
      "development",
      "mobile-development",
      "mobile-development-platforms"
    ],
    free: true,
    g2Slug: "firebase",
    name: "Firebase",
    paid: true,
    scripts: ["/(?:([\\d.]+)/)?firebase(?:\\.min)?\\.js"],
    variables: { "firebase.SDK_VERSION": "([\\d.]+)$" },
    websiteUrl: "https://firebase.google.com/"
  },
  "flask": {
    categories: [
      "development",
      "python-web-frameworks",
      "web-frameworks"
    ],
    dependencies: ["python"],
    free: true,
    g2Slug: "flask",
    headers: { Server: "Werkzeug/?([\\d\\.]+)?" },
    name: "Flask",
    paid: false,
    websiteUrl: "https://flask.palletsprojects.com/"
  },
  "flywheel": {
    categories: ["development", "webops-platforms"],
    dependencies: ["wordpress"],
    free: false,
    g2Slug: "flywheel",
    headers: {
      "x-fw-hash": "",
      "x-fw-serve": "",
      "x-fw-server": "^Flywheel(?:/([\\d.]+))?",
      "x-fw-static": "",
      "x-fw-type": ""
    },
    name: "Flywheel",
    paid: true,
    websiteUrl: "https://getflywheel.com/"
  },
  "framer": {
    categories: [
      "design",
      "development",
      "web-design",
      "website-builder"
    ],
    dependencies: ["react"],
    free: true,
    name: "Framer",
    paid: false,
    tags: ["<!-- \u2728 Built with Framer \u2022 https://www.framer.com/ -->"],
    variables: { __framer_importFromPackage: "" },
    websiteUrl: "https://www.framer.com/"
  },
  "freespee": {
    categories: [
      "outbound-call-tracking",
      "sales",
      "sales-acceleration"
    ],
    free: false,
    g2Slug: "freespee",
    name: "Freespee",
    paid: true,
    scripts: ["analytics\\.freespee\\.com/js/external/fs\\.(?:min\\.)?js"],
    websiteUrl: "https://www.freespee.com/"
  },
  "freshchat": {
    categories: [
      "artificial-intelligence",
      "chatbots",
      "conversational-intelligence",
      "conversational-marketing",
      "conversational-support",
      "customer-self-service",
      "customer-service",
      "live-chat",
      "marketing",
      "social-customer-service"
    ],
    free: true,
    g2Slug: "freshworks-freshchat",
    name: "Freshchat",
    paid: true,
    scripts: ["wchat\\.freshchat\\.com/js/widget\\.js"],
    websiteUrl: "https://www.freshworks.com/live-chat-software/"
  },
  "freshdesk": {
    categories: [
      "live-chat",
      "customer-self-service",
      "help-desk",
      "digital-customer-service-platforms",
      "social-customer-service",
      "conversational-support",
      "customer-communications-management",
      "shared-inbox",
      "complaint-management"
    ],
    free: true,
    g2Slug: "freshdesk",
    name: "Freshdesk",
    paid: true,
    scripts: ["\\.freshdesk\\.com"],
    tags: ['<a[^>]*href="[^"]*freshdesk[^"]*"[^>]*>Freshdesk</a>'],
    urls: ["\\.freshdesk\\.com"],
    websiteUrl: "https://www.freshworks.com/freshdesk/"
  },
  "freshmarketer": {
    categories: [
      "conversational-marketing",
      "marketing",
      "marketing-automation"
    ],
    free: true,
    g2Slug: "freshworks-freshmarketer",
    name: "Freshmarketer",
    paid: true,
    scripts: ["cdn\\.freshmarketer\\.com"],
    websiteUrl: "https://www.freshworks.com/marketing-automation/"
  },
  "front": {
    categories: [
      "business-instant-messaging",
      "live-chat",
      "email-client",
      "customer-self-service",
      "help-desk",
      "digital-customer-service-platforms",
      "social-customer-service",
      "conversational-support",
      "customer-communications-management"
    ],
    dns: { cname: [".frontkb.com"] },
    free: false,
    g2Slug: "front",
    name: "Front",
    paid: true,
    scripts: ["chat-assets\\.frontapp\\.com/v1/chat\\.bundle\\.js"],
    tags: ['<iframe\\s+id="front-chat-iframe"[^>]*>'],
    variables: { FrontChat: "" },
    websiteUrl: "https://front.com/"
  },
  "fullstory": {
    categories: [
      "application-development",
      "conversion-rate-optimization",
      "development",
      "digital-analytics",
      "heatmap",
      "marketing",
      "marketplace-apps",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-app-optimization",
      "mobile-development",
      "office",
      "product-analytics",
      "sap-store",
      "session-replay"
    ],
    free: false,
    g2Slug: "fullstory",
    name: "FullStory",
    paid: true,
    scripts: ["\\.fullstory\\.com/s/fs\\.js"],
    variables: { _fs_org: "" },
    websiteUrl: "https://www.fullstory.com/"
  },
  "gambio": {
    categories: [
      "commerce",
      "e-commerce",
      "shopping-cart"
    ],
    dependencies: ["php"],
    free: false,
    g2Slug: "gambio",
    name: "Gambio",
    paid: true,
    scripts: ["gm_javascript\\.js\\.php"],
    tags: ['(?:<link[^>]* href="templates/gambio/|<a[^>]content\\.php\\?coID=\\d|<!-- gambio eof -->|<!--[\\s=]+Shopsoftware by Gambio GmbH \\(c\\))'],
    variables: { gambio: "" },
    websiteUrl: "https://www.gambio.com/"
  },
  "gatsby": {
    categories: [
      "ci-cd",
      "content-management",
      "continuous-delivery",
      "development",
      "devops",
      "hosting",
      "web-hosting-providers",
      "webops-platforms",
      "website-builder"
    ],
    dependencies: ["react"],
    free: true,
    g2Slug: "gatsby-cloud",
    metas: { generator: "^Gatsby(?: ([0-9.]+))?$" },
    name: "Gatsby",
    paid: false,
    tags: ['<div id="___gatsby">', '<style id="gatsby-inlined-css">'],
    websiteUrl: "https://www.gatsbyjs.com/"
  },
  "genexus": {
    categories: [
      "application-development",
      "application-development-platforms",
      "business-process-management",
      "development",
      "it-management",
      "low-code-development-platforms",
      "marketplace-apps",
      "mobile-development",
      "mobile-development-platforms",
      "office",
      "process-automation",
      "rapid-application-development",
      "sap-store"
    ],
    free: false,
    g2Slug: "genexus",
    name: "GeneXus",
    paid: true,
    scripts: [".*/static/gxgral.js", ".*/static/gxtimezone.js"],
    tags: ['<link[^>]+?id="gxtheme_css_reference"'],
    variables: { "gx.gxVersion": "^(.+)-.*$" },
    websiteUrl: "https://www.genexus.com/"
  },
  "getfeedback": {
    categories: [
      "customer-service",
      "employee-engagement",
      "experience-management",
      "feedback-analytics",
      "hr",
      "talent-management"
    ],
    free: true,
    g2Slug: "getfeedback",
    name: "GetFeedback",
    nameSynonyms: ["Usabilla"],
    paid: true,
    variables: { usabilla_live: "" },
    websiteUrl: "https://www.getfeedback.com/"
  },
  "ghost": {
    categories: [
      "content-management",
      "headless-cms",
      "web-content-management"
    ],
    dependencies: ["node-js"],
    free: true,
    g2Slug: "ghost",
    headers: { "X-Ghost-Cache-Status": "" },
    metas: { generator: "Ghost(?:\\s([\\d.]+))?" },
    name: "Ghost",
    paid: true,
    websiteUrl: "https://ghost.org/"
  },
  "gitbook": {
    categories: [
      "content-management",
      "knowledge-base",
      "knowledge-management"
    ],
    dns: { cname: [".gitbook.com", ".gitbook.io"] },
    free: true,
    g2Slug: "gitbook",
    metas: { generator: "GitBook(?:.([\\d.]+))?" },
    name: "GitBook",
    paid: true,
    tags: ["<a[^>]*>.*?Powered by GitBook</a>", '<link[^>]*rel="preconnect"[^>]*href="https://api\\.gitbook\\.com"[^>]*>'],
    urls: ["^https?://[^/]+\\.gitbook\\.(com|io)/"],
    variables: { GitBook: "" },
    websiteUrl: "https://www.gitbook.com/"
  },
  "go": {
    categories: ["programming-languages"],
    free: true,
    name: "Go",
    paid: false,
    websiteUrl: "https://golang.org/"
  },
  "godaddy-website-builder": {
    categories: ["content-management", "website-builder"],
    cookies: { dps_site_id: "" },
    free: false,
    g2Slug: "godaddy-website-builder",
    metas: { generator: "Go Daddy Website Builder (.+)" },
    name: "GoDaddy Website Builder",
    paid: true,
    websiteUrl: "https://www.godaddy.com/websites/website-builder"
  },
  "google-adsense": {
    categories: [
      "digital-advertising",
      "publisher-ad-management",
      "publisher-ad-server"
    ],
    free: true,
    g2Slug: "adsense",
    name: "Google AdSense",
    nameSynonyms: ["Adsense"],
    paid: false,
    scripts: [
      "2mdn\\.net",
      "ad\\.ca\\.doubleclick\\.net",
      "ad\\.ca\\.doubleclick\\.net",
      "googlesyndication\\.com/"
    ],
    variables: {
      __google_ad_urls: "",
      Goog_AdSense_: "",
      google_ad_: ""
    },
    websiteUrl: "https://www.google.com/adsense/"
  },
  "google-analytics": {
    categories: [
      "application-development",
      "content-analytics",
      "content-marketing",
      "development",
      "digital-analytics",
      "marketing",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-development"
    ],
    cookies: {
      __utma: "",
      _ga: "",
      _gat: ""
    },
    free: true,
    g2Slug: "google-analytics",
    name: "Google Analytics",
    paid: true,
    scripts: ["google-analytics\\.com\\/(?:ga|urchin|analytics)\\.js"],
    tags: [`<amp-analytics [^>]*type=["']googleanalytics["']`],
    variables: {
      gaGlobal: "",
      GoogleAnalyticsObject: ""
    },
    websiteUrl: "https://analytics.google.com/"
  },
  "google-cloud": {
    categories: [
      "archive-storage",
      "block-storage",
      "cloud-file-storage",
      "cloud-platform-as-a-service",
      "container-management",
      "container-orchestration",
      "container-registry",
      "containerization",
      "development",
      "devops",
      "hybrid-cloud-storage",
      "it-infrastructure",
      "load-balancing",
      "object-storage",
      "storage-management",
      "virtual-private-cloud"
    ],
    free: true,
    g2Slug: "google-cloud",
    headers: { Via: "^1\\.1 google$" },
    name: "Google Cloud",
    paid: true,
    websiteUrl: "https://cloud.google.com/"
  },
  "google-maps": {
    categories: ["gis"],
    free: true,
    g2Slug: "google-maps-api",
    name: "Google Maps",
    paid: true,
    scripts: ["(?:maps\\.google\\.com/maps\\?file=api(?:&v=([\\d.]+))?|maps\\.google\\.com/maps/api/staticmap)", "//maps\\.googleapis\\.com/maps/api/js"],
    websiteUrl: "https://developers.google.com/maps"
  },
  "google-marketing-platform": {
    categories: [
      "advertiser-campaign-management",
      "creative-management-platforms",
      "cross-channel-advertising",
      "demand-side-platform",
      "digital-advertising",
      "display-advertising",
      "mobile-advertising",
      "search-advertising",
      "video-advertising"
    ],
    free: true,
    g2Slug: "google-marketing-platform",
    name: "Google Marketing Platform",
    nameSynonyms: ["DoubleClick"],
    paid: false,
    scripts: [
      "googlesyndication\\.com.*abg\\.js",
      "googlesyndication\\.com/pagead/show_ads\\.js",
      "googletagservices\\.com/tag/js/gpt(?:_mobile)?\\.js",
      "tpc\\.googlesyndication\\.com/safeframe"
    ],
    websiteUrl: "https://marketingplatform.google.com/"
  },
  "google-pagespeed": {
    categories: ["it-infrastructure", "other-it-infrastructure"],
    free: true,
    g2Slug: "pagespeed-tools",
    headers: {
      "X-Mod-Pagespeed": "([\\d.]+)",
      "X-Page-Speed": "(.+)"
    },
    name: "Google PageSpeed",
    paid: false,
    websiteUrl: "https://developers.google.com/speed/pagespeed/"
  },
  "google-tag-manager": {
    categories: [
      "digital-analytics",
      "marketing",
      "tag-management"
    ],
    free: true,
    g2Slug: "google-tag-manager",
    name: "Google Tag Manager",
    paid: false,
    scripts: ["googletagmanager\\.com/gtm\\.js"],
    tags: ["<!-- (?:End )?Google Tag Manager -->", "googletagmanager\\.com/ns\\.html[^>]+></iframe>"],
    variables: {
      google_tag_manager: "",
      googletag: ""
    },
    websiteUrl: "https://marketingplatform.google.com/about/tag-manager/"
  },
  "google-web-toolkit": {
    categories: [
      "development",
      "other-web-frameworks",
      "web-frameworks"
    ],
    dependencies: ["java"],
    free: true,
    g2Slug: "gwt-google-web-toolkit",
    metas: { "gwt:property": "" },
    name: "Google Web Toolkit",
    nameSynonyms: ["GWT"],
    paid: false,
    variables: {
      __gwt_: "",
      __gwt_activeModules: "",
      __gwt_getMetaProperty: "",
      __gwt_isKnownPropertyValue: "",
      __gwt_stylesLoaded: "",
      __gwtlistener: ""
    },
    websiteUrl: "http://www.gwtproject.org/"
  },
  "google-workspace": {
    categories: [
      "business-instant-messaging",
      "calendar",
      "cloud-content-collaboration",
      "content-management",
      "document-creation",
      "email",
      "idea-management",
      "internal-communications",
      "note-taking-management",
      "office",
      "office-suites",
      "presentation",
      "screen-sharing",
      "spreadsheets",
      "survey",
      "team-collaboration",
      "video-conferencing",
      "website-builder"
    ],
    dns: { mx: ["aspmx.l.google.com", ".googlemail.com"] },
    free: false,
    g2Slug: "google-workspace",
    name: "Google Workspace",
    nameSynonyms: ["Google Suite", "G Suite"],
    paid: true,
    websiteUrl: "https://workspace.google.com/"
  },
  "grav": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["php"],
    free: true,
    g2Slug: "grav",
    metas: { generator: "GravCMS(?:\\s([\\d.]+))?" },
    name: "Grav",
    paid: false,
    websiteUrl: "https://getgrav.org/"
  },
  "gridsome": {
    categories: ["website-builder"],
    dependencies: ["vue-js"],
    free: true,
    metas: { generator: "^Gridsome v([\\d.]+)$" },
    name: "Gridsome",
    paid: false,
    websiteUrl: "https://gridsome.org/"
  },
  "groovehq": {
    categories: [
      "customer-self-service",
      "help-desk",
      "shared-inbox"
    ],
    free: false,
    g2Slug: "groovehq",
    name: "GrooveHQ",
    paid: true,
    scripts: ["widget\\.cluster\\.groovehq\\.com"],
    urls: ["\\.groovehq\\.com/"],
    variables: { groove: "" },
    websiteUrl: "https://www.groovehq.com/"
  },
  "gunicorn": {
    categories: ["servers"],
    dependencies: ["python"],
    free: true,
    headers: { Server: "gunicorn(?:/([\\d.]+))?" },
    name: "gunicorn",
    paid: false,
    websiteUrl: "https://gunicorn.org/"
  },
  "hcl-commerce": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    dependencies: ["java"],
    free: false,
    g2Slug: "hcl-commerce",
    name: "IBM WebSphere Commerce",
    nameSynonyms: ["IBM WebSphere Commerce"],
    paid: true,
    tags: ['href="(?:\\/|[^>]+)webapp\\/wcs\\/'],
    urls: ["/wcs/"],
    websiteUrl: "https://www.hcltechsw.com/commerce"
  },
  "heap": {
    categories: [
      "application-development",
      "commerce",
      "conversion-rate-optimization",
      "development",
      "digital-analytics",
      "e-commerce",
      "e-commerce-analytics",
      "marketing",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-app-optimization",
      "mobile-development",
      "product-analytics",
      "session-replay"
    ],
    free: true,
    g2Slug: "heap",
    name: "Heap",
    paid: true,
    scripts: ["heap-\\d+\\.js"],
    variables: { heap: "" },
    websiteUrl: "https://heap.io/"
  },
  "help-scout": {
    categories: [
      "live-chat",
      "customer-self-service",
      "help-desk",
      "digital-customer-service-platforms",
      "conversational-support",
      "customer-communications-management",
      "shared-inbox",
      "email-management"
    ],
    free: false,
    g2Slug: "help-scout",
    name: "Help Scout",
    paid: true,
    scripts: ["beacon-v2\\.helpscout\\.net"],
    variables: { Beacon: "" },
    websiteUrl: "https://www.helpscout.com/"
  },
  "helpcrunch": {
    categories: [
      "live-chat",
      "email-marketing",
      "customer-self-service",
      "help-desk",
      "chatbots",
      "conversational-support",
      "customer-communications-management",
      "shared-inbox",
      "transactional-email"
    ],
    free: false,
    g2Slug: "helpcrunch",
    name: "HelpCrunch",
    paid: true,
    scripts: ["widget\\.helpcrunch\\.com", "embed\\.helpcrunch\\.com/sdk-code\\.js"],
    tags: ["<div>helpcrunch-widget</div>", `<iframe[^>]*name=["']helpcrunch-iframe["']`],
    urls: ["helpcrunch\\.com/fonts/"],
    variables: {
      HelpCrunch: "",
      helpcrunchSettings: ""
    },
    websiteUrl: "https://helpcrunch.com/"
  },
  "helpdocs": {
    categories: ["customer-self-service"],
    dns: { cname: [".helpdocs.io"] },
    free: false,
    g2Slug: "helpdocs",
    metas: { description: "HelpDocs for " },
    name: "HelpDocs",
    paid: true,
    tags: ["<!-- \\[Begin\\] HelpDocs Required Additions -->"],
    urls: ["cdn\\.helpdocs\\.io", "files\\.helpdocs\\.io"],
    websiteUrl: "https://www.helpdocs.io/"
  },
  "helpjuice": {
    categories: [
      "customer-self-service",
      "knowledge-base",
      "contact-center-knowledge-base"
    ],
    dns: { cname: [".helpjuice.com"] },
    free: false,
    g2Slug: "helpjuice",
    name: "Helpjuice",
    paid: true,
    scripts: ["/helpjuice-static/helpjuice_production/swifty/"],
    tags: ["Powered by Helpjuice"],
    urls: ["\\.helpjuice\\.com/"],
    variables: {
      helpjuice_account_url: "",
      helpjuice_preset_hash: ""
    },
    websiteUrl: "https://helpjuice.com/"
  },
  "helpshift": {
    categories: [
      "live-chat",
      "customer-self-service",
      "help-desk",
      "digital-customer-service-platforms",
      "chatbots",
      "conversational-support"
    ],
    free: false,
    g2Slug: "helpshift",
    name: "Helpshift",
    paid: true,
    urls: ["\\.helpshift\\.com"],
    websiteUrl: "https://www.helpshift.com/"
  },
  "hexo": {
    categories: ["website-builder"],
    dependencies: ["node-js"],
    free: true,
    metas: { generator: "Hexo(?: v?([\\d.]+))?" },
    name: "Hexo",
    paid: false,
    tags: ['Powered by <a href="https?://hexo\\.io/?"[^>]*>Hexo</'],
    websiteUrl: "https://hexo.io/"
  },
  "hotjar": {
    categories: [
      "conversion-rate-optimization",
      "heatmap",
      "marketing",
      "session-replay",
      "user-research"
    ],
    free: true,
    g2Slug: "hotjar",
    name: "Hotjar",
    paid: true,
    scripts: ["^//static\\.hotjar\\.com/c/hotjar-"],
    variables: { _hjSettings: "" },
    websiteUrl: "https://www.hotjar.com/"
  },
  "hubspot": {
    categories: [
      "ai-sales-assistant",
      "business-card-scanning",
      "business-scheduling",
      "conversation-intelligence",
      "cpq",
      "crm",
      "demand-generation",
      "email-tracking",
      "lead-generation",
      "marketing",
      "office",
      "online-appointment-scheduling",
      "outbound-call-tracking",
      "quote-management",
      "quote-to-cash",
      "sales",
      "sales-acceleration",
      "sales-acceleration-platforms",
      "sales-analytics",
      "sales-enablement",
      "sales-engagement",
      "sales-intelligence",
      "sales-performance-management",
      "sales-platforms",
      "visitor-identification"
    ],
    dns: {
      cname: [".hubspot.net"],
      txt: ["hubspot-developer-verification"]
    },
    free: false,
    g2Slug: "hubspot-sales-hub",
    name: "HubSpot",
    paid: true,
    scripts: ["js\\.hubspot\\.com/", "static.hsappstatic.net"],
    tags: [
      "<!-- Start of Async HubSpot",
      "<!-- Start of HubSpot Embed",
      "<!-- Start of Meetings Embed Script -->"
    ],
    urls: ["meetings-[^.]+\\.hubspot\\.com/"],
    variables: {
      _hsq: "",
      hubspot: ""
    },
    websiteUrl: "https://www.hubspot.com/"
  },
  "hugo": {
    categories: ["content-management", "website-builder"],
    dependencies: ["go"],
    free: true,
    g2Slug: "hugo",
    metas: { generator: "Hugo ([\\d.]+)?" },
    name: "Hugo",
    paid: false,
    tags: ['powered by <a [^>]*href="http://hugo.spf13.com'],
    websiteUrl: "https://gohugo.io/"
  },
  "imperva-app-protect": {
    categories: [
      "cloud-ddos-mitigation",
      "cloud-security",
      "content-delivery-network",
      "devsecops",
      "hosting",
      "security",
      "web-application-firewalls",
      "web-security",
      "website-security"
    ],
    free: true,
    g2Slug: "imperva-app-protect",
    headers: { "X-CDN": "Incapsula" },
    name: "Imperva App Protect",
    nameSynonyms: ["Incapsula"],
    paid: true,
    websiteUrl: "https://www.imperva.com/products/application-security/"
  },
  "inferno-js": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "Inferno",
    paid: false,
    variables: {
      "Inferno": "",
      "Inferno.version": "^(.+)$"
    },
    websiteUrl: "https://infernojs.org/"
  },
  "inspectlet": {
    categories: [
      "conversion-rate-optimization",
      "digital-analytics",
      "heatmap",
      "marketing",
      "session-replay"
    ],
    free: true,
    g2Slug: "inspectlet",
    name: "Inspectlet",
    paid: true,
    scripts: ["cdn\\.inspectlet\\.com"],
    tags: ["<!-- (?:Begin|End) Inspectlet Embed Code -->"],
    variables: {
      __insp: "",
      __inspld: ""
    },
    websiteUrl: "https://www.inspectlet.com/"
  },
  "instabot": {
    categories: [
      "conversational-marketing",
      "marketing",
      "productivity-bots"
    ],
    free: false,
    g2Slug: "instabot",
    name: "Instabot",
    paid: true,
    scripts: ["/rokoInstabot\\.js"],
    variables: { Instabot: "" },
    websiteUrl: "https://instabot.io/"
  },
  "intercom": {
    categories: [
      "artificial-intelligence",
      "chatbots",
      "conversational-intelligence",
      "conversational-marketing",
      "conversational-support",
      "customer-self-service",
      "customer-service",
      "digital-adoption-platforms",
      "digital-customer-service-platforms",
      "help-desk",
      "live-chat",
      "marketing"
    ],
    dns: { cname: [".intercom.help"] },
    free: false,
    g2Slug: "intercom",
    name: "Intercom",
    paid: true,
    scripts: ["(?:api\\.intercom\\.io/api|static\\.intercomcdn\\.com/intercom\\.v1)", "//widget\\.intercom\\.io/"],
    urls: [
      "intercom\\.help/",
      "\\.intercom\\.io",
      "\\.intercomcdn\\.com/"
    ],
    variables: {
      Intercom: "",
      intercomSettings: ""
    },
    websiteUrl: "https://www.intercom.com/"
  },
  "java": {
    categories: ["programming-languages"],
    cookies: { JSESSIONID: "" },
    free: true,
    name: "Java",
    paid: false,
    websiteUrl: "https://www.java.com/"
  },
  "java-servlet": {
    categories: ["java-development-tools"],
    dependencies: ["java"],
    free: true,
    headers: { "X-Powered-By": "Servlet(?:.([\\d.]+))?" },
    name: "Java Servlet",
    paid: false,
    websiteUrl: "https://www.oracle.com/java/technologies/java-servlet-tec.html"
  },
  "jekyll": {
    categories: ["website-builder"],
    free: true,
    metas: { generator: "Jekyll (v[\\d.]+)?" },
    name: "Jekyll",
    paid: false,
    tags: [
      "<!-- Begin Jekyll SEO tag",
      "<!-- Created with Jekyll Now -",
      'Powered by <a href="https?://jekyllrb\\.com"[^>]*>Jekyll</'
    ],
    websiteUrl: "https://jekyllrb.com/"
  },
  "jenkins": {
    categories: [
      "aws-marketplace",
      "build-automation",
      "ci-cd",
      "continuous-delivery",
      "continuous-integration",
      "development",
      "devops",
      "devops-platforms",
      "marketplace-apps",
      "office"
    ],
    dependencies: ["java"],
    free: true,
    g2Slug: "jenkins",
    headers: { "X-Jenkins": "([\\d.]+)" },
    name: "Jenkins",
    paid: false,
    tags: ['<span class="jenkins_ver"><a href="https://jenkins\\.io/">Jenkins ver\\. ([\\d.]+)'],
    variables: {
      jenkinsCIGlobal: "",
      jenkinsRules: ""
    },
    websiteUrl: "https://www.jenkins.io/"
  },
  "jetty": {
    categories: ["servers"],
    dependencies: ["java"],
    free: true,
    headers: { Server: "Jetty(?:\\(([\\d\\.]*\\d+))?" },
    name: "Jetty",
    paid: false,
    websiteUrl: "https://www.eclipse.org/jetty/"
  },
  "jimdo": {
    categories: ["content-management", "website-builder"],
    free: true,
    g2Slug: "jimdo",
    headers: {
      "X-Jimdo-Instance": "",
      "X-Jimdo-Wid": ""
    },
    name: "Jimdo",
    paid: true,
    urls: ["\\.jimdo\\.com/"],
    websiteUrl: "https://www.jimdo.com/"
  },
  "jimo": {
    categories: ["digital-adoption-platforms"],
    free: false,
    g2Slug: "jimo",
    name: "Jimo",
    paid: true,
    scripts: ["undercity\\.usejimo\\.com/jimo-invader\\.js"],
    variables: {
      JIMO_PROJECT_ID: "",
      JIMO_SESSION_TOKEN: ""
    },
    websiteUrl: "https://www.usejimo.com/"
  },
  "joomla": {
    categories: [
      "aws-marketplace",
      "content-management",
      "marketplace-apps",
      "office",
      "web-content-management"
    ],
    dependencies: ["php"],
    free: true,
    g2Slug: "joomla",
    headers: { "X-Content-Encoded-By": "Joomla! ([\\d.]+)" },
    metas: { generator: "Joomla!(?: ([\\d.]+))?" },
    name: "Joomla",
    paid: false,
    tags: ['(?:<div[^>]+id="wrapper_r"|<(?:link|script)[^>]+(?:feed|components)/com_|<table[^>]+class="pill)'],
    variables: {
      jcomments: "",
      Joomla: ""
    },
    websiteUrl: "https://www.joomla.org/"
  },
  "kajabi": {
    categories: [
      "education",
      "online-learning-platforms",
      "vertical-industry"
    ],
    cookies: { _kjb_session: "" },
    free: false,
    g2Slug: "kajabi-kajabi",
    name: "Kajabi",
    paid: true,
    variables: { Kajabi: "" },
    websiteUrl: "https://kajabi.com/"
  },
  "keap": {
    categories: [
      "accounting-and-finance",
      "conversion-rate-optimization",
      "crm",
      "demand-generation",
      "email-template-builder",
      "email-tracking",
      "erp",
      "invoice-management",
      "landing-page-builders",
      "lead-capture",
      "lead-generation",
      "lead-scoring",
      "marketing",
      "marketing-automation",
      "quote-management",
      "quote-to-cash",
      "sales",
      "sales-acceleration",
      "sales-analytics",
      "sms-marketing"
    ],
    free: true,
    g2Slug: "keap-keap",
    name: "Infusionsoft",
    paid: true,
    tags: ['<input [^>]*name="infusionsoft_version" [^>]*value="([^>]*)" [^>]*\\/>', '<input [^>]*value="([^>]*)" [^>]*name="infusionsoft_version" [^>]*\\/>'],
    websiteUrl: "https://keap.com/"
  },
  "kentico": {
    categories: [
      "content-management",
      "development",
      "digital-experience-platforms",
      "web-content-management",
      "webops-platforms"
    ],
    cookies: { CMSPreferredCulture: "" },
    free: true,
    g2Slug: "kentico",
    metas: { generator: "Kentico CMS ([\\d.R]+ \\(build [\\d.]+\\))" },
    name: "Kentico CMS",
    paid: true,
    websiteUrl: "https://www.kentico.com/"
  },
  "kestrel": {
    categories: ["servers"],
    dependencies: ["microsoft-asp-net"],
    free: true,
    headers: { Server: "^Kestrel" },
    name: "Kestrel",
    paid: false,
    websiteUrl: "https://docs.microsoft.com/en-us/aspnet/core/fundamentals/servers/kestrel?view=aspnetcore-3.1"
  },
  "kevel": {
    categories: [
      "digital-advertising",
      "publisher-ad-management",
      "publisher-ad-server",
      "retail-media-advertising-platforms"
    ],
    free: false,
    g2Slug: "kevel",
    name: "Kevel",
    nameSynonyms: ["Adzerk"],
    paid: true,
    scripts: ["adzerk\\.net/ados\\.js"],
    tags: ['<iframe [^>]*src="[^"]+adzerk\\.net'],
    variables: {
      ados: "",
      adosResults: ""
    },
    websiteUrl: "https://www.kevel.com/"
  },
  "keycdn": {
    categories: [
      "content-delivery-network",
      "development",
      "hosting",
      "ip-address-intelligence"
    ],
    free: false,
    g2Slug: "keycdn",
    headers: { Server: "^keycdn-engine$" },
    name: "KeyCDN",
    paid: true,
    websiteUrl: "https://www.keycdn.com/"
  },
  "kinsta": {
    categories: [
      "hosting",
      "managed-hosting-providers",
      "web-hosting-providers"
    ],
    dependencies: ["wordpress"],
    free: false,
    g2Slug: "kinsta",
    headers: { "x-kinsta-cache": "" },
    name: "Kinsta",
    paid: true,
    websiteUrl: "https://kinsta.com/"
  },
  "kissmetrics": {
    categories: [
      "application-development",
      "commerce",
      "conversion-rate-optimization",
      "development",
      "digital-analytics",
      "e-commerce",
      "e-commerce-analytics",
      "marketing",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-app-optimization",
      "mobile-development",
      "product-analytics"
    ],
    free: true,
    g2Slug: "kissmetrics",
    name: "KISSmetrics",
    paid: true,
    variables: { KM_COOKIE_DOMAIN: "" },
    websiteUrl: "https://www.kissmetricshq.com/"
  },
  "klarna": {
    categories: [
      "commerce",
      "installment-payment-and-buy-now-pay-later",
      "payment"
    ],
    free: false,
    g2Slug: "klarna",
    name: "Klarna Checkout",
    paid: true,
    variables: { _klarnaCheckout: "" },
    websiteUrl: "https://www.klarna.com/"
  },
  "klaviyo": {
    categories: [
      "email-marketing",
      "sms-marketing",
      "marketing-automation",
      "e-commerce",
      "personalization",
      "data-integration"
    ],
    free: false,
    g2Slug: "klaviyo",
    name: "Klaviyo",
    paid: true,
    scripts: [
      "static-tracking\\.klaviyo\\.com",
      "static\\.klaviyo\\.com",
      "cdn\\.klaviyo\\.com",
      "js\\.klaviyo\\.com"
    ],
    variables: {
      klaviyo: "",
      klaviyoJS: ""
    },
    websiteUrl: "https://www.klaviyo.com/"
  },
  "knockout-js": {
    categories: [
      "development",
      "javascript-web-frameworks",
      "web-frameworks"
    ],
    free: true,
    g2Slug: "knockout-js",
    name: "Knockout",
    paid: false,
    variables: { "ko.version": "^(.+)$" },
    websiteUrl: "https://knockoutjs.com/"
  },
  "laravel": {
    categories: [
      "development",
      "php-web-frameworks",
      "web-frameworks"
    ],
    cookies: { laravel_session: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "laravel",
    name: "Laravel",
    paid: false,
    variables: { Laravel: "" },
    websiteUrl: "https://laravel.com/"
  },
  "leaflet-js": {
    categories: ["component-libraries", "development"],
    free: true,
    g2Slug: "leaflet-js",
    name: "Leaflet",
    paid: false,
    scripts: ["leaflet.*\\.js"],
    variables: {
      "L.DistanceGrid": "",
      "L.PosAnimation": "",
      "L.version": "^(.+)$"
    },
    websiteUrl: "https://leafletjs.com/"
  },
  "liferay-commerce": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    free: true,
    g2Slug: "liferay-commerce",
    headers: { "Liferay-Portal": "[a-z\\s]+([\\d.]+)" },
    name: "Liferay Commerce",
    paid: false,
    variables: { Liferay: "" },
    websiteUrl: "https://www.liferay.com/products/commerce"
  },
  "lighttpd": {
    categories: [
      "application-server",
      "it-infrastructure",
      "web-accelerator",
      "web-server-accelerator"
    ],
    free: true,
    g2Slug: "lighttpd",
    headers: { Server: "lighttpd(?:/([\\d.]+))?" },
    name: "lighttpd",
    paid: false,
    websiteUrl: "https://www.lighttpd.net/"
  },
  "liquid-web": {
    categories: [
      "hosting",
      "managed-hosting-providers",
      "web-hosting-providers"
    ],
    free: false,
    g2Slug: "liquid-web-managed-hosting",
    headers: { "x-lw-cache": "" },
    name: "Liquid Web",
    paid: true,
    websiteUrl: "https://www.liquidweb.com/"
  },
  "litespeed-web-server": {
    categories: ["application-server", "it-infrastructure"],
    free: true,
    g2Slug: "litespeed-web-server",
    headers: { Server: "^LiteSpeed$" },
    name: "LiteSpeed Web Server",
    paid: true,
    websiteUrl: "https://www.litespeedtech.com/products/litespeed-web-server"
  },
  "liveagent": {
    categories: [
      "customer-self-service",
      "customer-service",
      "digital-customer-service-platforms",
      "help-desk",
      "live-chat",
      "social-customer-service"
    ],
    free: false,
    g2Slug: "liveagent",
    name: "LiveAgent",
    paid: true,
    scripts: ["ladesk\\.com/scripts/"],
    urls: ["ladesk\\.com/scripts/"],
    variables: {
      LiveAgent: "",
      LiveAgentTracker: ""
    },
    websiteUrl: "https://www.liveagent.com/"
  },
  "livechat": {
    categories: [
      "customer-service",
      "help-desk",
      "live-chat"
    ],
    free: false,
    g2Slug: "livechat",
    name: "LiveChat",
    paid: true,
    scripts: ["cdn\\.livechatinc\\.com/.*tracking\\.js"],
    tags: ["<!-- (?:Start|End) of LiveChat (www.livechatinc.com) code -->"],
    websiteUrl: "https://www.livechat.com/"
  },
  "liveperson": {
    categories: [
      "artificial-intelligence",
      "bot-platforms",
      "chatbots",
      "conversational-intelligence",
      "conversational-marketing",
      "conversational-support",
      "customer-service",
      "live-chat",
      "marketing",
      "marketplace-apps",
      "office",
      "sap-store",
      "social-customer-service"
    ],
    free: true,
    g2Slug: "conversational-cloud",
    name: "LivePerson",
    paid: true,
    scripts: ["^https?://lptag\\.liveperson\\.net/tag/tag\\.js"],
    websiteUrl: "https://www.liveperson.com/"
  },
  "locomotive-cms": {
    categories: ["content-management"],
    dependencies: ["mongodb", "ruby-on-rails"],
    free: true,
    name: "Locomotive CMS",
    paid: false,
    tags: ["<link[^>]*/sites/[a-z\\d]{24}/theme/stylesheets"],
    websiteUrl: "https://www.locomotivecms.com/"
  },
  "lua": {
    categories: ["programming-languages"],
    free: true,
    headers: { "X-Powered-By": "\\bLua(?: ([\\d.]+))?" },
    name: "Lua",
    paid: false,
    websiteUrl: "https://www.lua.org/"
  },
  "lucky-orange": {
    categories: [
      "conversion-rate-optimization",
      "digital-analytics",
      "heatmap",
      "marketing",
      "product-analytics",
      "session-replay"
    ],
    free: false,
    g2Slug: "lucky-orange",
    name: "Lucky Orange",
    paid: true,
    scripts: ["d10lpsik1i8c69\\.cloudfront\\.net/w\\.js"],
    variables: { "__lo_site_id ": "" },
    websiteUrl: "https://www.luckyorange.com/"
  },
  "magnite": {
    categories: [
      "digital-advertising",
      "publisher-ad-management",
      "publisher-ad-server",
      "supply-side-platforms"
    ],
    free: true,
    g2Slug: "magnite-for-sellers",
    name: "Magnite",
    nameSynonyms: ["Rubicon Project"],
    paid: false,
    scripts: ["https?://[^/]*\\.rubiconproject\\.com"],
    websiteUrl: "https://www.magnite.com/"
  },
  "mailchimp": {
    categories: [
      "email-deliverability",
      "email-marketing",
      "marketing"
    ],
    free: true,
    g2Slug: "intuit-mailchimp",
    name: "MailChimp",
    paid: true,
    scripts: ["cdn-images\\.mailchimp\\.com/[^>]*\\.css", "s3\\.amazonaws\\.com/downloads\\.mailchimp\\.com/js/mc-validate\\.js"],
    tags: [
      "<!-- Begin MailChimp Signup Form -->",
      "<form [^>]*data-mailchimp-url",
      '<form [^>]*id="mc-embedded-subscribe-form"',
      '<form [^>]*name="mc-embedded-subscribe-form"',
      '<input [^>]*id="mc-email"'
    ],
    websiteUrl: "https://mailchimp.com/"
  },
  "mariadb": {
    categories: [
      "aws-marketplace",
      "columnar-databases",
      "database",
      "it-infrastructure",
      "it-management",
      "marketplace-apps",
      "office",
      "relational-databases"
    ],
    free: true,
    g2Slug: "mariadb",
    name: "MariaDB",
    paid: false,
    websiteUrl: "https://mariadb.org/"
  },
  "maropost-commerce-cloud": {
    categories: [
      "commerce",
      "e-commerce",
      "multichannel-retail",
      "pos",
      "retail",
      "retail-analytics",
      "retail-management-systems",
      "retail-pos-systems"
    ],
    free: false,
    g2Slug: "maropost-commerce-cloud",
    name: "Maropost Commerce Cloud",
    nameSynonyms: ["Neto"],
    paid: true,
    scripts: ["jquery\\.neto.*\\.js"],
    variables: { NETO: "" },
    websiteUrl: "https://www.maropost.com/commerce-cloud/"
  },
  "matomo": {
    categories: [
      "content-analytics",
      "content-marketing",
      "digital-analytics",
      "marketing"
    ],
    cookies: { PIWIK_SESSID: "" },
    free: false,
    g2Slug: "matomo-formerly-piwik",
    metas: { generator: "(?:Matomo|Piwik) - Open Source Web Analytics" },
    name: "Matomo",
    nameSynonyms: ["Piwik"],
    paid: true,
    scripts: ["piwik\\.js|piwik\\.php", "//cdn.matomo.cloud/"],
    urls: ["//cdn.matomo.cloud/"],
    variables: {
      _paq: "",
      Matomo: "",
      Piwik: ""
    },
    websiteUrl: "https://matomo.org/"
  },
  "mautic": {
    categories: ["marketing", "technology-research-services"],
    free: true,
    g2Slug: "mautic-mautic",
    name: "Mautic",
    paid: false,
    scripts: ["[^a-z]mtc.*\\.js"],
    variables: { MauticTrackingObject: "" },
    websiteUrl: "https://www.mautic.org/"
  },
  "memberstack": {
    categories: [
      "cms",
      "commerce",
      "content-management",
      "customer-identity-and-access-management",
      "e-commerce",
      "identity-management",
      "security"
    ],
    cookies: { memberstack: "" },
    free: false,
    g2Slug: "memberstack",
    name: "MemberStack",
    paid: true,
    scripts: ["memberstack\\.js"],
    urls: ["^https?://.+\\.memberstack\\.io"],
    variables: { MemberStack: "" },
    websiteUrl: "https://www.memberstack.com/"
  },
  "meteor-js": {
    categories: [
      "development",
      "java-web-frameworks",
      "web-frameworks"
    ],
    dependencies: ["mongodb", "node-js"],
    free: true,
    g2Slug: "meteor",
    name: "Meteor JS",
    paid: false,
    tags: ["<link[^>]+__meteor-css__"],
    variables: {
      "Meteor": "",
      "Meteor.release": "^METEOR@([\\d.]+)"
    },
    websiteUrl: "https://www.meteor.com/"
  },
  "microsoft-365": {
    categories: ["office", "office-suites"],
    dns: {
      mx: ["mail.protection.outlook.com"],
      txt: ["spf.protection.outlook.com"]
    },
    free: false,
    g2Slug: "microsoft365",
    name: "Microsoft 365",
    nameSynonyms: ["Office 365"],
    paid: true,
    websiteUrl: "https://www.microsoft.com/microsoft-365"
  },
  "microsoft-asp-net": {
    categories: [
      "development",
      "other-web-frameworks",
      "web-frameworks"
    ],
    cookies: {
      "ASP.NET_SessionId": "",
      "ASPSESSION": ""
    },
    dependencies: ["microsoft-iis"],
    free: true,
    g2Slug: "asp-net",
    headers: {
      "X-AspNet-Version": "(.+)",
      "X-Powered-By": "^ASP\\.NET"
    },
    name: "Microsoft ASP.NET",
    paid: false,
    tags: ['<input[^>]+name="__VIEWSTATE'],
    urls: ["\\.aspx?(?:$|\\?)"],
    websiteUrl: "https://dotnet.microsoft.com/apps/aspnet"
  },
  "microsoft-azure": {
    categories: [
      "archive-storage",
      "block-storage",
      "cloud-file-storage",
      "cloud-platform-as-a-service",
      "container-management",
      "container-orchestration",
      "container-registry",
      "containerization",
      "development",
      "devops",
      "hybrid-cloud-storage",
      "it-infrastructure",
      "load-balancing",
      "object-storage",
      "storage-management",
      "virtual-private-cloud"
    ],
    cookies: {
      ARRAffinity: "",
      TiPMix: ""
    },
    free: true,
    g2Slug: "microsoft-microsoft-azure",
    headers: {
      "azure-regionname": "",
      "azure-sitename": "",
      "azure-slotname": "",
      "azure-version": ""
    },
    name: "Azure",
    paid: true,
    websiteUrl: "https://azure.microsoft.com/"
  },
  "microsoft-azure-cdn": {
    categories: [
      "content-delivery-network",
      "enterprise-content-delivery-network",
      "hosting"
    ],
    free: true,
    g2Slug: "microsoft-azure-cdn",
    headers: {
      "server": "^(?:ECAcc|ECS|ECD)",
      "X-EC-Debug": ""
    },
    name: "Azure CDN",
    paid: true,
    websiteUrl: "https://azure.microsoft.com/services/cdn/"
  },
  "microsoft-iis": {
    categories: ["application-server", "it-infrastructure"],
    free: true,
    g2Slug: "iis-7-5",
    headers: { Server: "^(?:Microsoft-)?IIS(?:/([\\d.]+))?" },
    name: "Microsoft IIS",
    paid: true,
    websiteUrl: "https://www.iis.net/"
  },
  "mixpanel": {
    categories: [
      "application-development",
      "commerce",
      "conversion-rate-optimization",
      "development",
      "digital-analytics",
      "e-commerce",
      "e-commerce-analytics",
      "marketing",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-development",
      "product-analytics"
    ],
    free: true,
    g2Slug: "mixpanel",
    name: "Mixpanel",
    paid: true,
    scripts: ["api\\.mixpanel\\.com/track"],
    variables: { mixpanel: "" },
    websiteUrl: "https://mixpanel.com/"
  },
  "mobirise": {
    categories: [
      "application-development",
      "development",
      "drag-and-drop-app-builder",
      "mobile-development"
    ],
    free: true,
    g2Slug: "mobirise",
    metas: { generator: "^Mobirise v([\\d.]+)" },
    name: "Mobirise",
    paid: false,
    tags: ["<!-- Site made with Mobirise Website Builder v([\\d.]+)"],
    websiteUrl: "https://mobirise.com/"
  },
  "modx": {
    categories: [
      "development",
      "php-web-frameworks",
      "web-frameworks"
    ],
    dependencies: ["php"],
    free: true,
    g2Slug: "modx",
    headers: { "X-Powered-By": "^MODX" },
    metas: { generator: "MODX[^\\d.]*([\\d.]+)?" },
    name: "MODX",
    paid: true,
    tags: [
      "<(?:link|script)[^>]+assets/snippets/",
      "<a[^>]+>Powered by MODX</a>",
      '<form[^>]+id="ajaxSearch_form',
      '<input[^>]+id="ajaxSearch_input'
    ],
    variables: {
      MODX: "",
      MODX_MEDIA_PATH: ""
    },
    websiteUrl: "https://modx.com/"
  },
  "mongodb": {
    categories: [
      "aws-marketplace",
      "database",
      "document-databases",
      "it-infrastructure",
      "marketplace-apps",
      "nosql-databases",
      "office"
    ],
    free: true,
    g2Slug: "mongodb",
    name: "MongoDB",
    paid: true,
    websiteUrl: "https://www.mongodb.com/"
  },
  "moodle": {
    categories: [
      "education",
      "learning-management-systems",
      "vertical-industry"
    ],
    cookies: {
      MOODLEID_: "",
      MoodleSession: ""
    },
    dependencies: ["php"],
    free: true,
    g2Slug: "moodle",
    metas: { keywords: "^moodle" },
    name: "Moodle",
    paid: false,
    tags: ["<img[^>]+moodlelogo"],
    variables: {
      "M.core": "",
      "Y.Moodle": ""
    },
    websiteUrl: "https://moodle.org/"
  },
  "motocms": {
    categories: ["content-management", "website-builder"],
    dependencies: ["php"],
    free: true,
    name: "MotoCMS",
    paid: true,
    scripts: ["/mt-dependencies/js/website(?:assets)?\\.(?:min)?\\.js"],
    tags: ['<link [^>]*href="[^>]*\\/mt-content\\/[^>]*\\.css'],
    websiteUrl: "https://www.motocms.com/"
  },
  "mouseflow": {
    categories: [
      "commerce",
      "conversion-rate-optimization",
      "customer-service",
      "digital-analytics",
      "e-commerce",
      "e-commerce-analytics",
      "enterprise-feedback-management",
      "heatmap",
      "marketing",
      "marketing-analytics",
      "product-analytics",
      "session-replay"
    ],
    free: true,
    g2Slug: "mouseflow",
    name: "Mouse Flow",
    paid: true,
    scripts: ["cdn\\.mouseflow\\.com"],
    variables: { _mfq: "" },
    websiteUrl: "https://mouseflow.com/"
  },
  "mura-cms": {
    categories: [
      "content-management",
      "digital-experience-platforms",
      "headless-cms",
      "web-content-management"
    ],
    free: false,
    g2Slug: "mura-cms",
    metas: { generator: "Mura CMS ([\\d]+)" },
    name: "Mura CMS",
    paid: true,
    websiteUrl: "https://www.murasoftware.com/"
  },
  "mybb": {
    categories: ["content-management", "website-builder"],
    dependencies: ["mysql", "php"],
    free: true,
    g2Slug: "mybb",
    name: "MyBB",
    paid: false,
    tags: ['(?:<script [^>]+\\s+<!--\\s+lang\\.no_new_posts|<a[^>]* title="Powered By MyBB)'],
    variables: { MyBB: "" },
    websiteUrl: "https://mybb.com/"
  },
  "mysql": {
    categories: [
      "database",
      "it-infrastructure",
      "relational-databases"
    ],
    free: true,
    g2Slug: "mysql",
    name: "MySQL",
    paid: false,
    websiteUrl: "https://www.mysql.com/"
  },
  "netlify": {
    categories: [
      "ci-cd",
      "content-delivery-network",
      "continuous-delivery",
      "development",
      "devops",
      "hosting",
      "webops-platforms"
    ],
    free: true,
    g2Slug: "netlify",
    headers: {
      "Server": "^Netlify",
      "X-NF-Request-ID": ""
    },
    name: "Netlify",
    paid: true,
    urls: ["^https?://[^/]+\\.netlify\\.com/"],
    websiteUrl: "https://www.netlify.com/"
  },
  "nette-framework": {
    categories: [
      "development",
      "php-web-frameworks",
      "web-frameworks"
    ],
    cookies: { "nette-browser": "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "nette",
    headers: { "X-Powered-By": "^Nette Framework" },
    name: "Nette Framework",
    paid: false,
    tags: [
      '<div[^>]+id="snippet-',
      "<input[^>]+data-nette-rules",
      '<input[^>]+id="frm-'
    ],
    variables: {
      "Nette": "",
      "Nette.version": "^(.+)$"
    },
    websiteUrl: "https://nette.org/"
  },
  "new-relic": {
    categories: [
      "aiops-platforms",
      "application-performance-monitoring",
      "cloud-infrastructure-monitoring",
      "container-monitoring",
      "containerization",
      "development",
      "devops",
      "devsecops",
      "digital-experience-monitoring",
      "incident-management",
      "it-alerting",
      "it-infrastructure",
      "it-management",
      "log-analysis",
      "log-monitoring",
      "monitoring",
      "observability-solution-suites",
      "security",
      "server-monitoring"
    ],
    free: true,
    g2Slug: "new-relic-one",
    name: "New Relic",
    paid: true,
    variables: {
      newrelic: "",
      NREUM: ""
    },
    websiteUrl: "https://newrelic.com/"
  },
  "next-js": {
    categories: ["javascript-web-frameworks", "website-builder"],
    dependencies: ["node-js", "react"],
    free: true,
    headers: { "x-powered-by": "^Next\\.js ?([0-9.]+)?" },
    name: "Next",
    paid: false,
    variables: { __NEXT_DATA__: "" },
    websiteUrl: "https://nextjs.org/",
    evaluateVersion: async (page) => {
      const version = await page.evaluate(() => {
        try {
          return window?.next?.version;
        } catch {
        }
      });
      return version;
    }
  },
  "next-js-app-router": {
    categories: ["javascript-web-frameworks", "website-builder"],
    dependencies: ["node-js", "react"],
    free: true,
    name: "Next.js (App Router)",
    paid: false,
    websiteUrl: "https://nextjs.org/",
    evaluateVersion: async (page) => {
      const version = await page.evaluate(() => {
        try {
          return window?.next?.appDir;
        } catch {
        }
      });
      return version;
    }
  },
  "nginx": {
    categories: [
      "application-server",
      "container-networking",
      "containerization",
      "ddos-protection",
      "development",
      "devops",
      "devsecops",
      "it-infrastructure",
      "load-balancing",
      "security",
      "web-accelerator",
      "web-application-firewalls",
      "web-security",
      "web-server-accelerator"
    ],
    free: true,
    g2Slug: "f5-nginx",
    headers: {
      "Server": "nginx(?:/([\\d.]+))?",
      "X-Fastcgi-Cache": ""
    },
    name: "Nginx",
    paid: false,
    websiteUrl: "https://www.nginx.com/"
  },
  "node-js": {
    categories: [
      "development",
      "javascript-web-frameworks",
      "web-frameworks"
    ],
    free: true,
    g2Slug: "node-js",
    name: "Node",
    paid: false,
    websiteUrl: "https://nodejs.org/"
  },
  "nopcommerce": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    cookies: { "Nop.customer": "" },
    dependencies: ["microsoft-asp-net"],
    free: true,
    g2Slug: "nopcommerce",
    metas: { generator: "^nopCommerce$" },
    name: "nopCommerce",
    paid: false,
    tags: ["(?:<!--Powered by nopCommerce|Powered by: <a[^>]+nopcommerce)"],
    websiteUrl: "https://www.nopcommerce.com/"
  },
  "nuxt-content": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["nuxt-js"],
    free: true,
    name: "Nuxt Content",
    paid: false,
    websiteUrl: "https://content.nuxtjs.org/",
    evaluate: async (page) => {
      return await page.evaluate(() => {
        return (
          // Detect if navigation is present
          window?.useNuxtApp()?.static?.data?.navigation || Object.values(window?.useNuxtApp()?.static?.data).find((data) => {
            return data?.body && data?._dir && data?._path && data?._file;
          })
        );
      });
    }
  },
  "nuxt-hub": {
    categories: [
      "cloud-file-storage",
      "artificial-intelligence",
      "managed-hosting-providers",
      "build-automation",
      "javascript-web-frameworks"
    ],
    dependencies: ["nuxt-js"],
    free: true,
    name: "Nuxt Hub",
    paid: true,
    websiteUrl: "https://hub.nuxt.com/",
    evaluate: async (page) => {
      return await page.evaluate(() => !!window?.useNuxtApp()?.$config?.public?.hub);
    }
  },
  "nuxt-js": {
    categories: ["javascript-web-frameworks", "website-builder"],
    dependencies: ["node-js", "vue-js"],
    free: true,
    name: "Nuxt",
    paid: false,
    scripts: ["/_nuxt/"],
    tags: ['<div [^>]*id="__nuxt"', "<script [^>]*>window\\.__NUXT__"],
    variables: { $nuxt: "" },
    websiteUrl: "https://nuxtjs.org/",
    evaluateVersion: async (page) => {
      let version;
      version = await page.evaluate(() => {
        try {
          return window.__unctx__?.get("nuxt-app")?.use()?.versions?.nuxt;
        } catch {
        }
      });
      if (!version) {
        try {
          version = await page.evaluate(() => {
            try {
              return window.useNuxtApp().versions?.nuxt;
            } catch {
            }
          });
        } catch {
        }
      }
      if (!version) {
        version = await page.evaluate(() => {
          try {
            return window.__NUXT__?.version;
          } catch {
          }
        });
      }
      if (!version) {
        version = await page.evaluate(() => {
          try {
            return window?.$nuxt ? "2" : void 0;
          } catch {
          }
        });
      }
      return version;
    }
  },
  "nuxt-studio": {
    categories: [
      "content-management",
      "web-content-management",
      "javascript-web-frameworks"
    ],
    dependencies: ["nuxt-js", "nuxt-cotnent"],
    free: true,
    name: "Nuxt Studio",
    paid: true,
    websiteUrl: "https://nuxt.studio/",
    evaluate: async (page) => {
      return await page.evaluate(() => !!window?.useNuxtApp()?.$config?.public?.studio);
    }
  },
  "nuxt-ui": {
    categories: [
      "web-design",
      "web-content-management",
      "web-frameworks",
      "website-builder",
      "javascript-web-frameworks"
    ],
    dependencies: ["nuxt-js"],
    free: true,
    name: "Nuxt UI",
    paid: true,
    websiteUrl: "https://ui.nuxt.com/",
    evaluate: async (page) => {
      return await page.evaluate(() => window?.useNuxtApp()?.$ui);
    }
  },
  "october-cms": {
    categories: ["content-management", "web-content-management"],
    cookies: { october_session: "" },
    dependencies: ["laravel"],
    free: true,
    g2Slug: "october-cms",
    name: "October CMS",
    paid: false,
    websiteUrl: "https://octobercms.com/"
  },
  "odoo": {
    categories: [
      "accounting",
      "accounting-and-finance",
      "business-instant-messaging",
      "discrete-erp",
      "erp",
      "erp-systems",
      "hr",
      "hrms-and-hcm",
      "internal-communications"
    ],
    dependencies: [
      "node-js",
      "postgresql",
      "python"
    ],
    free: true,
    g2Slug: "odoo-odoo-erp",
    metas: { generator: "Odoo" },
    name: "Odoo",
    paid: true,
    scripts: ["/web/js/(?:web\\.assets_common/|website\\.assets_frontend/)"],
    tags: ["<link[^>]* href=[^>]+/web/css/(?:web\\.assets_common/|website\\.assets_frontend/)"],
    websiteUrl: "https://www.odoo.com/"
  },
  "olark": {
    categories: ["customer-service", "live-chat"],
    free: true,
    g2Slug: "olark",
    name: "Olark",
    paid: true,
    scripts: ["^https?:\\/\\/static\\.olark\\.com\\/jsclient\\/loader1\\.js"],
    websiteUrl: "https://www.olark.com/"
  },
  "onesignal": {
    categories: [
      "customer-service",
      "marketing",
      "marketing-automation",
      "mobile-marketing",
      "proactive-notification",
      "push-notification",
      "sms-marketing"
    ],
    free: true,
    g2Slug: "onesignal",
    name: "OneSignal",
    paid: true,
    scripts: ["cdn.onesignal.com/sdks/", "onesignal.com/webPushAnalytics"],
    variables: {
      __oneSignalSdkLoadCount: "",
      OneSignal: ""
    },
    websiteUrl: "https://onesignal.com/"
  },
  "onetrust": {
    categories: [
      "business-continuity-management",
      "cookie-tracking",
      "grc-platforms",
      "operational-risk-management",
      "policy-management",
      "privacy-impact-assessment",
      "privacy-policy-generator",
      "risk-assessment",
      "sensitive-data-discovery",
      "sustainability-management",
      "third-party-and-supplier-risk-management",
      "vendor-security-and-privacy-assessment",
      "consent-management-platforms",
      "data-privacy",
      "data-privacy-management",
      "data-subject-access-request",
      "marketplace-apps",
      "office",
      "servicenow-marketplace-apps",
      "security",
      "vertical-industry"
    ],
    free: false,
    g2Slug: "onetrust",
    name: "OneTrust",
    paid: true,
    scripts: ["\\.onetrust\\.com/"],
    tags: ['<div[^>]+id="onetrust-consent-sdk"'],
    urls: ["\\.onetrust\\.com/"],
    variables: { OneTrust: "" },
    websiteUrl: "https://www.onetrust.com/"
  },
  "open-web-analytics": {
    categories: [
      "content-analytics",
      "content-marketing",
      "marketing"
    ],
    free: true,
    g2Slug: "open-web-analytics",
    name: "Open Web Analytics",
    paid: false,
    tags: ["<!-- (?:Start|End) Open Web Analytics Tracker -->"],
    variables: {
      "OWA.config.baseUrl": "",
      "owa_baseUrl": "",
      "owa_cmds": ""
    },
    websiteUrl: "http://www.openwebanalytics.com/"
  },
  "opencart": {
    categories: [
      "commerce",
      "e-commerce",
      "shopping-cart"
    ],
    cookies: { OCSESSID: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "opencart",
    name: "OpenCart",
    paid: false,
    websiteUrl: "https://www.opencart.com/"
  },
  "openlayers": {
    categories: [
      "development-services-providers",
      "other-development-services-providers",
      "professional-services"
    ],
    free: true,
    g2Slug: "openlayers",
    name: "OpenLayers",
    paid: false,
    scripts: ["openlayers"],
    variables: {
      "ol.CanvasMap": "",
      "OpenLayers.VERSION_NUMBER": "([\\d.]+)"
    },
    websiteUrl: "https://openlayers.org/"
  },
  "opennemas": {
    categories: ["content-management", "web-content-management"],
    free: true,
    g2Slug: "opennemas",
    headers: { "X-Powered-By": "OpenNemas" },
    metas: { generator: "OpenNemas" },
    name: "OpenNemas",
    paid: true,
    websiteUrl: "https://www.opennemas.com/"
  },
  "openresty": {
    categories: [
      "application-performance-monitoring",
      "application-server",
      "cloud-infrastructure-monitoring",
      "devsecops",
      "it-infrastructure",
      "log-analysis",
      "monitoring",
      "observability-solution-suites",
      "security",
      "server-monitoring"
    ],
    dependencies: ["lua", "nginx"],
    free: true,
    g2Slug: "openresty",
    headers: { Server: "openresty(?:/([\\d.]+))?" },
    name: "OpenResty",
    paid: false,
    websiteUrl: "https://openresty.org/"
  },
  "openssl": {
    categories: [
      "confidentiality",
      "security",
      "ssl-and-tls-certificates"
    ],
    free: true,
    g2Slug: "openssl",
    headers: { Server: "OpenSSL(?:/([\\d.]+[a-z]?))?" },
    name: "OpenSSL",
    paid: false,
    websiteUrl: "https://www.openssl.org/"
  },
  "openx": {
    categories: [
      "app-monetization",
      "digital-advertising",
      "marketing",
      "publisher-ad-management",
      "publisher-ad-server",
      "supply-side-platforms",
      "tag-management"
    ],
    free: false,
    g2Slug: "openx",
    name: "OpenX",
    paid: true,
    scripts: ["https?://[^/]*\\.openx\\.net", "https?://[^/]*\\.servedbyopenx\\.com"],
    websiteUrl: "https://www.openx.com/"
  },
  "optimizely": {
    categories: [
      "a-b-testing",
      "alm-software-suites",
      "application-development",
      "ci-cd",
      "commerce",
      "configuration-management",
      "conversion-rate-optimization",
      "development",
      "devops",
      "e-commerce",
      "e-commerce-personalization",
      "feature-management",
      "marketing",
      "personalization-engines"
    ],
    free: true,
    g2Slug: "optimizely-web-experimentation",
    name: "Optimizely",
    paid: true,
    scripts: ["optimizely\\.com.*\\.js"],
    variables: { optimizely: "" },
    websiteUrl: "https://www.optimizely.com/"
  },
  "oracle-application-server": {
    categories: ["application-server", "it-infrastructure"],
    free: false,
    g2Slug: "oracle-internet-application-server",
    headers: { Server: "Oracle[- ]Application[- ]Server(?: Containers for J2EE)?(?:[- ](\\d[\\da-z./]+))?" },
    name: "Oracle Application Server",
    nameSynonyms: ["Oracle Internet Application Server"],
    paid: true,
    websiteUrl: "https://www.oracle.com/middleware/technologies/application-server.html"
  },
  "oracle-commerce": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    free: false,
    g2Slug: "oracle-commerce",
    headers: {
      "OracleCommerceCloud-Version": "^(.+)$",
      "X-ATG-Version": "(?:ATGPlatform/([\\d.]+))?"
    },
    name: "Oracle Commerce",
    paid: true,
    tags: ["<[^>]+_dyncharset", '<[^>]+id="oracle-cc"'],
    websiteUrl: "https://www.oracle.com/cx/ecommerce/"
  },
  "oracle-http-server": {
    categories: ["servers"],
    free: false,
    headers: { Server: "Oracle-HTTP-Server(?:/([\\d.]+))?" },
    name: "Oracle HTTP Server",
    paid: true,
    websiteUrl: "https://docs.oracle.com/cd/E28280_01/web.1111/e10144/intro_ohs.htm"
  },
  "orchard-cms": {
    categories: ["component-content-management-systems", "content-management"],
    dependencies: ["microsoft-asp-net"],
    free: true,
    g2Slug: "orchard-core",
    metas: { generator: "Orchard" },
    name: "Orchard CMS",
    paid: false,
    websiteUrl: "https://orchardcore.net/"
  },
  "oro-commerce": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    dependencies: ["mysql", "php"],
    free: false,
    g2Slug: "orocommerce",
    name: "Oro Commerce",
    paid: true,
    scripts: ["oro\\.min\\.js\\?version=([\\d.]+)"],
    tags: ['<script [^>]+data-requiremodule="oro/', '<script [^>]+data-requiremodule="oroui/'],
    websiteUrl: "https://oroinc.com/b2b-ecommerce/"
  },
  "oscommerce": {
    categories: [
      "commerce",
      "e-commerce",
      "shopping-cart"
    ],
    cookies: { osCsid: "" },
    dependencies: ["mysql", "php"],
    free: true,
    g2Slug: "oscommerce",
    name: "osCommerce",
    paid: false,
    tags: [
      '<(?:input|a)[^>]+name="osCsid"',
      '<(?:tr|td|table)class="[^"]*infoBoxHeading',
      '<br />Powered by <a href="https?://www\\.oscommerce\\.com'
    ],
    websiteUrl: "https://www.oscommerce.com/"
  },
  "osticket": {
    categories: [
      "aws-marketplace",
      "customer-service",
      "help-desk",
      "marketplace-apps",
      "office"
    ],
    cookies: { OSTSESSID: "" },
    dependencies: ["mysql", "php"],
    free: true,
    g2Slug: "osticket",
    name: "osTicket",
    paid: true,
    websiteUrl: "https://osticket.com/"
  },
  "otrs": {
    categories: [
      "customer-service",
      "help-desk",
      "it-management",
      "it-service-management"
    ],
    dependencies: ["perl"],
    free: false,
    g2Slug: "otrs",
    name: "OTRS",
    paid: true,
    scripts: ["^/otrs-web/js/"],
    tags: ["<!--\\s+OTRS: Copyright \\d+-\\d+, OTRS AG"],
    websiteUrl: "https://otrs.com/"
  },
  "outbrain": {
    categories: [
      "content-distribution",
      "content-marketing",
      "digital-advertising",
      "marketing",
      "retargeting"
    ],
    free: true,
    g2Slug: "outbrain-amplify",
    name: "Outbrain",
    paid: true,
    scripts: ["widgets\\.outbrain\\.com/outbrain\\.js"],
    variables: {
      OB_releaseVer: "^(.+)$",
      OutbrainPermaLink: ""
    },
    websiteUrl: "https://www.outbrain.com/"
  },
  "owncloud": {
    categories: [
      "aws-marketplace",
      "cloud-content-collaboration",
      "content-management",
      "marketplace-apps",
      "office"
    ],
    dependencies: ["php"],
    free: true,
    g2Slug: "owncloud",
    name: "ownCloud",
    paid: true,
    tags: ['<a href="https://owncloud\\.com" target="_blank">ownCloud Inc\\.</a><br/>Your Cloud, Your Data, Your Way!'],
    websiteUrl: "https://owncloud.com/"
  },
  "pagefly": {
    categories: [
      "conversion-rate-optimization",
      "landing-page-builders",
      "marketing"
    ],
    free: true,
    g2Slug: "pagefly-page-builder",
    name: "PageFly",
    nameSynonyms: ["PageFly Page Builder"],
    paid: true,
    scripts: ["pagefly\\.io"],
    websiteUrl: "https://pagefly.io/"
  },
  "pagekit": {
    categories: ["content-management", "web-content-management"],
    free: true,
    g2Slug: "pagekit",
    metas: { generator: "Pagekit" },
    name: "Pagekit",
    paid: false,
    websiteUrl: "https://pagekit.com/"
  },
  "pagevamp": {
    categories: ["content-management", "website-builder"],
    free: false,
    headers: { "X-ServedBy": "pagevamp" },
    name: "Pagevamp",
    paid: true,
    variables: { Pagevamp: "" },
    websiteUrl: "https://www.pagevamp.com/"
  },
  "pantheon": {
    categories: [
      "ci-cd",
      "content-management",
      "continuous-integration",
      "development",
      "devops",
      "hosting",
      "managed-hosting-providers",
      "web-content-management",
      "web-hosting-providers",
      "webops-platforms"
    ],
    dependencies: [
      "mariadb",
      "nginx",
      "php"
    ],
    free: false,
    g2Slug: "pantheon",
    headers: {
      "Server": "^Pantheon",
      "x-pantheon-styx-hostname": ""
    },
    name: "Pantheon",
    paid: true,
    websiteUrl: "https://pantheon.io/"
  },
  "parse-ly": {
    categories: [
      "content-analytics",
      "content-marketing",
      "digital-analytics",
      "marketing"
    ],
    free: false,
    g2Slug: "parse-ly",
    name: "Parse.ly",
    paid: true,
    variables: { PARSELY: "" },
    websiteUrl: "https://www.parse.ly/"
  },
  "partnerstack": {
    categories: [
      "affiliate-marketing",
      "partner-ecosystem-platforms",
      "partner-management",
      "sales",
      "marketing"
    ],
    free: false,
    g2Slug: "partnerstack",
    name: "PartnerStack",
    nameSynonyms: ["GrowSumo"],
    paid: true,
    scripts: ["js\\.partnerstack\\.com", "snippet\\.growsumo\\.com/growsumo\\.min\\.js"],
    websiteUrl: "https://www.partnerstack.com/"
  },
  "paypal": {
    categories: [
      "commerce",
      "installment-payment-and-buy-now-pay-later",
      "marketplace-apps",
      "office",
      "payment",
      "payment-gateways",
      "sap-store"
    ],
    free: true,
    g2Slug: "paypal",
    name: "PayPal",
    paid: false,
    scripts: [
      "www\\.paypalobjects\\.com/",
      "www\\.paypal\\.com/tagmanager/pptm\\.js",
      "www\\.paypal\\.com/sdk/js"
    ],
    tags: ["<input[^>]+_s-xclick"],
    variables: {
      PAYPAL: "",
      PayPalCommerceGateway: ""
    },
    websiteUrl: "https://www.paypal.com/"
  },
  "perl": {
    categories: ["programming-languages"],
    free: true,
    headers: { Server: "\\bPerl\\b(?: ?/?v?([\\d.]+))?" },
    name: "Perl",
    paid: false,
    websiteUrl: "https://www.perl.org/"
  },
  "phabricator": {
    categories: [
      "bug-tracking",
      "ci-cd",
      "continuous-integration",
      "development",
      "devops",
      "peer-code-review",
      "source-code-management",
      "version-control-hosting"
    ],
    cookies: { phsid: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "phabricator",
    name: "Phabricator",
    paid: true,
    scripts: ["/phabricator/[a-f0-9]{8}/rsrc/js/phui/[a-z-]+\\.js$"],
    tags: ['<[^>]+(?:class|id)="phabricator-'],
    websiteUrl: "https://www.phacility.com/phabricator/"
  },
  "php": {
    categories: ["programming-languages"],
    cookies: { PHPSESSID: "" },
    free: true,
    headers: {
      "Server": "php/?([\\d.]+)?",
      "X-Powered-By": "^php/?([\\d.]+)?"
    },
    name: "PHP",
    paid: false,
    urls: ["\\.php(?:$|\\?)"],
    websiteUrl: "https://www.php.net/"
  },
  "php-fusion": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["mysql", "php"],
    free: true,
    g2Slug: "php-fusion-cms",
    headers: { "X-Powered-By": "PHP-Fusion (.+)$" },
    name: "PHP-Fusion",
    paid: false,
    tags: ['Powered by <a href="[^>]+php-fusion'],
    websiteUrl: "https://www.php-fusion.co.uk/"
  },
  "phpbb": {
    categories: ["marketing", "online-community-management"],
    cookies: { phpbb: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "phpbb",
    metas: { copyright: "phpBB Group" },
    name: "phpBB",
    paid: false,
    tags: [
      "<[^>]+styles/(?:sub|pro)silver/theme",
      "<div class=phpbb_copyright>",
      "<img[^>]+i_icon_mini",
      '<table class="[^"]*forumline',
      "Powered by <a[^>]+phpBB"
    ],
    variables: {
      phpbb: "",
      style_cookie_settings: ""
    },
    websiteUrl: "https://www.phpbb.com/"
  },
  "phusion-passenger": {
    categories: ["application-server", "it-infrastructure"],
    free: true,
    g2Slug: "phusion-passenger",
    headers: {
      "Server": "Phusion Passenger ([\\d.]+)",
      "X-Powered-By": "Phusion Passenger ?([\\d.]+)?"
    },
    name: "Phusion Passenger",
    paid: true,
    websiteUrl: "https://www.phusionpassenger.com/"
  },
  "pimcore": {
    categories: [
      "commerce",
      "content-management",
      "data-mapping",
      "digital-asset-management",
      "digital-experience-platforms",
      "e-commerce",
      "e-commerce-platforms",
      "headless-cms",
      "it-infrastructure",
      "master-data-management",
      "product-information-management",
      "web-content-management"
    ],
    dependencies: ["php"],
    free: true,
    g2Slug: "pimcore",
    headers: { "X-Powered-By": "^pimcore$" },
    name: "Pimcore",
    paid: true,
    websiteUrl: "https://pimcore.com/"
  },
  "pinterest-widgets": {
    categories: ["social-media-marketing"],
    free: true,
    name: "Pinterest Widgets",
    paid: false,
    scripts: ["//assets\\.pinterest\\.com/js/pinit\\.js"],
    websiteUrl: "https://business.pinterest.com/en/pinterest-widget-builder"
  },
  "platform-sh": {
    categories: [
      "build-automation",
      "ci-cd",
      "cloud-platform-as-a-service",
      "continuous-delivery",
      "development",
      "devops",
      "hosting",
      "managed-hosting-providers",
      "web-hosting-providers",
      "webops-platforms"
    ],
    free: false,
    g2Slug: "platform-sh",
    headers: {
      "x-platform-cluster": "",
      "x-platform-processor": "",
      "x-platform-router": "",
      "x-platform-server": ""
    },
    name: "Platform.sh",
    paid: true,
    websiteUrl: "https://platform.sh/"
  },
  "plausible": {
    categories: ["digital-analytics", "marketing"],
    free: false,
    g2Slug: "plausible-analytics",
    name: "Plausible",
    paid: true,
    scripts: ["plausible\\.io/js/"],
    variables: { plausible: "" },
    websiteUrl: "https://plausible.io/"
  },
  "plesk": {
    categories: [
      "application-server",
      "ci-cd",
      "cloud-platform-as-a-service",
      "content-management",
      "continuous-delivery",
      "development",
      "devops",
      "hosting",
      "it-infrastructure",
      "virtual-private-serversproviders",
      "webops-platforms"
    ],
    free: false,
    g2Slug: "plesk",
    headers: {
      "X-Powered-By": "^Plesk(?:L|W)in",
      "X-Powered-By-Plesk": "^Plesk"
    },
    name: "Plesk",
    paid: true,
    scripts: ["common\\.js\\?plesk"],
    websiteUrl: "https://www.plesk.com/"
  },
  "plone-cms": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["python"],
    free: true,
    g2Slug: "plone-cms",
    metas: { generator: "Plone" },
    name: "Plone CMS",
    paid: false,
    websiteUrl: "https://plone.org/"
  },
  "postgresql": {
    categories: [
      "database",
      "it-infrastructure",
      "relational-databases"
    ],
    free: true,
    g2Slug: "postgresql",
    name: "PostgreSQL",
    paid: false,
    websiteUrl: "https://www.postgresql.org/"
  },
  "prestashop": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms",
      "shopping-cart"
    ],
    cookies: { PrestaShop: "" },
    dependencies: ["mysql", "php"],
    free: true,
    g2Slug: "prestashop",
    headers: { "Powered-By": "^Prestashop$" },
    metas: { generator: "PrestaShop" },
    name: "PrestaShop",
    paid: true,
    tags: [
      "<!-- /Block [a-z ]+ module (?:HEADER|TOP)?\\s?-->",
      "<!-- /Module Block [a-z ]+ -->",
      "Powered by <a\\s+[^>]+>PrestaShop"
    ],
    variables: {
      freeProductTranslation: "",
      priceDisplayMethod: "",
      priceDisplayPrecision: ""
    },
    websiteUrl: "https://www.prestashop.com/"
  },
  "product-fruits": {
    categories: ["digital-adoption-platforms", "customer-self-service"],
    free: false,
    g2Slug: "product-fruits",
    name: "Product Fruits",
    paid: true,
    scripts: ["app\\.productfruits\\.com/static/"],
    variables: { $productFruits: "" },
    websiteUrl: "https://productfruits.com/"
  },
  "progress-sitefinity": {
    categories: [
      "content-management",
      "development",
      "digital-experience-platforms",
      "web-content-management",
      "webops-platforms"
    ],
    dependencies: ["microsoft-asp-net"],
    free: true,
    g2Slug: "progress-sitefinity",
    metas: { generator: "^Sitefinity (.+)$" },
    name: "Progress Sitefinity",
    paid: true,
    websiteUrl: "https://www.progress.com/sitefinity-cms"
  },
  "pubmatic": {
    categories: [
      "app-monetization",
      "digital-advertising",
      "publisher-ad-management",
      "publisher-ad-server",
      "supply-side-platforms"
    ],
    free: true,
    g2Slug: "pubmatic",
    name: "PubMatic",
    paid: false,
    scripts: ["https?://[^/]*\\.pubmatic\\.com"],
    websiteUrl: "https://pubmatic.com/"
  },
  "python": {
    categories: ["programming-languages"],
    free: true,
    headers: { Server: "(?:^|\\s)Python(?:/([\\d.]+))?" },
    name: "Python",
    paid: false,
    websiteUrl: "https://www.python.org/"
  },
  "quantcast": {
    categories: ["digital-analytics", "marketing"],
    free: true,
    g2Slug: "quantcast-measure",
    name: "Quantcast",
    paid: false,
    scripts: ["\\.quantserve\\.com/quant\\.js"],
    variables: { quantserve: "" },
    websiteUrl: "https://www.quantcast.com/"
  },
  "react": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "React",
    paid: false,
    scripts: [
      "/([\\d.]+)/react(?:\\.min)?\\.js",
      "react(?:-with-addons)?[.-]([\\d.]*\\d)[^/]*\\.js",
      "react.*\\.js"
    ],
    tags: ["<[^>]+data-react"],
    variables: {
      "React.version": "^(.+)$",
      "react.version": "^(.+)$"
    },
    websiteUrl: "https://reactjs.org/"
  },
  "react-router": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "React Router",
    paid: false,
    variables: { __reactRouterVersion: "" },
    websiteUrl: "https://reactrouter.com"
  },
  "remix": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "Remix",
    paid: false,
    variables: {
      __remixContext: "",
      __remixManifest: "",
      __remixRouteModules: "",
      __remixRouter: ""
    },
    websiteUrl: "https://remix.run"
  },
  "riot-js": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "Riot",
    paid: false,
    scripts: ["riot(?:\\+compiler)?(?:\\.min)?\\.js"],
    variables: { riot: "" },
    websiteUrl: "https://riot.js.org/"
  },
  "riskified": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-fraud-protection",
      "fraud-detection",
      "security",
      "web-security"
    ],
    free: false,
    g2Slug: "riskified",
    headers: { server: "Riskified Server" },
    name: "Riskified",
    paid: true,
    tags: ["<[^>]*beacon\\.riskified\\.com", "<[^>]*c\\.riskified\\.com"],
    variables: {
      riskifiedBeaconLoad: "",
      RISKX: ""
    },
    websiteUrl: "https://www.riskified.com/"
  },
  "rock-rms": {
    categories: ["church-management", "vertical-industry"],
    dependencies: ["microsoft-iis", "microsoft-asp-net"],
    free: false,
    g2Slug: "rock-rms",
    metas: { generator: "^Rock v([0-9.]+)" },
    name: "Rock RMS",
    paid: true,
    websiteUrl: "https://www.rockrms.com/"
  },
  "ruby": {
    categories: ["programming-languages"],
    free: true,
    headers: { Server: "(?:Mongrel|WEBrick|Ruby)" },
    name: "Ruby",
    paid: false,
    websiteUrl: "https://www.ruby-lang.org/"
  },
  "ruby-on-rails": {
    categories: [
      "development",
      "other-web-frameworks",
      "web-frameworks"
    ],
    cookies: { _session_id: "" },
    dependencies: ["ruby"],
    free: true,
    g2Slug: "ruby-on-rails",
    headers: {
      "Server": "mod_(?:rails|rack)",
      "X-Powered-By": "mod_(?:rails|rack)"
    },
    metas: { "csrf-param": "^authenticity_token$" },
    name: "Ruby on Rails",
    paid: false,
    scripts: ["/assets/application-[a-z\\d]{32}/\\.js"],
    websiteUrl: "https://rubyonrails.org/"
  },
  "sails-js": {
    categories: [
      "application-development",
      "development",
      "javascript-web-frameworks",
      "mobile-development",
      "mobile-development-frameworks",
      "web-frameworks"
    ],
    cookies: { "sails.sid": "" },
    free: true,
    g2Slug: "sails-js",
    headers: { "X-Powered-By": "^Sails(?:$|[^a-z0-9])" },
    name: "Sails",
    paid: false,
    websiteUrl: "https://sailsjs.com/"
  },
  "salesforce": {
    categories: [
      "crm",
      "sales",
      "sales-analytics",
      "sales-platforms"
    ],
    cookies: { "com.salesforce": "" },
    free: false,
    g2Slug: "salesforce-salesforce-sales-cloud",
    name: "Salesforce",
    paid: true,
    tags: ['<[^>]+="brandQuaternaryFgrs"'],
    variables: {
      SFDCApp: "",
      SFDCCmp: "",
      SFDCPage: "",
      SFDCSessionVars: ""
    },
    websiteUrl: "https://www.salesforce.com/"
  },
  "salesforce-account-engagement": {
    categories: [
      "attribution",
      "conversion-rate-optimization",
      "landing-page-builders",
      "marketing",
      "marketing-analytics",
      "marketing-automation"
    ],
    free: true,
    g2Slug: "salesforce-account-engagement-formerly-pardot",
    headers: {
      "X-Pardot-LB": "",
      "X-Pardot-Route": "",
      "X-Pardot-Rsp": ""
    },
    name: "Salesforce Account Engagement",
    nameSynonyms: ["Pardot"],
    paid: true,
    variables: {
      piAId: "",
      piCId: "",
      piHostname: "",
      piProtocol: "",
      piTracker: ""
    },
    websiteUrl: "https://www.salesforce.com/products/marketing-cloud/marketing-automation/"
  },
  "salesforce-commerce-cloud": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms",
      "e-merchandising",
      "mobile-e-commerce",
      "omnichannel-commerce",
      "shopping-cart"
    ],
    free: false,
    g2Slug: "salesforce-commerce-cloud-suite",
    headers: { Server: "Demandware eCommerce Server" },
    name: "Salesforce Commerce Cloud",
    paid: true,
    scripts: ["/demandware\\.static/"],
    tags: ["<[^>]+demandware\\.edgesuite"],
    variables: { dwAnalytics: "" },
    websiteUrl: "https://www.salesforce.com/eu/products/commerce-cloud/overview/"
  },
  "sap": {
    categories: ["crm", "sales"],
    free: false,
    g2Slug: "sap-crm",
    headers: { Server: "SAP NetWeaver Application Server" },
    name: "SAP",
    paid: true,
    websiteUrl: "https://www.sap.com/"
  },
  "sap-commerce-cloud": {
    categories: [
      "accounting-and-finance",
      "commerce",
      "e-commerce",
      "e-commerce-platforms",
      "erp",
      "omnichannel-commerce",
      "order-management"
    ],
    cookies: { _hybris: "" },
    dependencies: ["java"],
    free: false,
    g2Slug: "sap-commerce-cloud",
    name: "SAP Commerce Cloud",
    paid: true,
    tags: ["<[^>]+/(?:sys_master|hybr|_ui/(?:responsive/)?(?:desktop|common(?:/images|/img)?))/"],
    websiteUrl: "https://www.sap.com/products/crm/e-commerce-platforms.html"
  },
  "section-io": {
    categories: [
      "content-delivery-network",
      "hosting",
      "it-management",
      "virtual-waiting-room"
    ],
    free: false,
    g2Slug: "section",
    headers: {
      "section-io-id": "",
      "section-io-origin-status": "",
      "section-io-origin-time-seconds": ""
    },
    name: "Section.io",
    paid: true,
    websiteUrl: "https://www.section.io/"
  },
  "segment": {
    categories: [
      "consent-management-platforms",
      "customer-data-platforms",
      "data-governance",
      "data-integration",
      "data-privacy",
      "data-privacy-management",
      "data-subject-access-request",
      "e-commerce-data-integration",
      "it-infrastructure",
      "it-management",
      "marketing"
    ],
    free: true,
    g2Slug: "segment",
    name: "Segment",
    paid: true,
    scripts: ["cdn\\.segment\\.com/analytics\\.js"],
    variables: { analytics: "" },
    websiteUrl: "https://segment.com/"
  },
  "sentry": {
    categories: [
      "application-development",
      "application-performance-monitoring",
      "bug-tracking",
      "development",
      "devops",
      "game-development",
      "gaming",
      "iot-development",
      "iot-management",
      "it-alerting",
      "it-infrastructure",
      "it-management",
      "log-monitoring",
      "mobile-analytics",
      "mobile-crash-reporting",
      "mobile-development",
      "monitoring",
      "website-monitoring"
    ],
    free: true,
    g2Slug: "sentry",
    name: "Sentry",
    paid: true,
    tags: ["\\.sentry-cdn\\.com/"],
    variables: {
      "__SENTRY__": "",
      "Raven.config": "",
      "ravenOptions.whitelistUrls": "",
      "Sentry": "",
      "Sentry.SDK_VERSION": "(.+)"
    },
    websiteUrl: "https://sentry.io/"
  },
  "sharethis": {
    categories: [
      "content-distribution",
      "content-marketing",
      "marketing"
    ],
    free: true,
    g2Slug: "sharethis-share-buttons",
    name: "ShareThis",
    paid: false,
    scripts: ["w\\.sharethis\\.com/"],
    variables: { SHARETHIS: "" },
    websiteUrl: "https://sharethis.com/"
  },
  "shift4shop": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms",
      "shopping-cart"
    ],
    cookies: { "3dvisit": "" },
    free: true,
    g2Slug: "shift4shop",
    headers: { "X-Powered-By": "3DCART" },
    name: "Shift4Shop",
    nameSynonyms: ["3DCart"],
    paid: true,
    scripts: ["(?:twlh(?:track)?\\.asp|3d_upsell\\.js)"],
    websiteUrl: "https://www.shift4shop.com/"
  },
  "shinystat": {
    categories: ["digital-analytics", "marketing"],
    free: true,
    g2Slug: "shinystat",
    name: "ShinyStat",
    paid: true,
    scripts: ["^https?://codice(?:business|ssl|pro|isp)?\\.shinystat\\.com/cgi-bin/getcod\\.cgi"],
    tags: [`<img[^>]*\\s+src=['"]?https?://www\\.shinystat\\.com/cgi-bin/shinystat\\.cgi\\?[^'"\\s>]*['"\\s/>]`],
    variables: { SSsdk: "" },
    websiteUrl: "https://www.shinystat.com/"
  },
  "shopify": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    free: false,
    g2Slug: "shopify",
    headers: {
      "x-shopid": "",
      "x-shopify-stage": ""
    },
    name: "Shopify",
    nameSynonyms: ["Snowdevil"],
    paid: true,
    tags: [`<link[^>]+=['"]//cdn\\.shopify\\.com`],
    urls: ["^https?://.+\\.myshopify\\.com"],
    variables: { Shopify: "" },
    websiteUrl: "https://www.shopify.com/"
  },
  "shopline": {
    categories: [
      "commerce",
      "e-commerce",
      "shopping-cart"
    ],
    free: false,
    g2Slug: "shopline",
    metas: { "og:image": "https\\:\\/\\/img\\.shoplineapp\\.com" },
    name: "Shopline",
    paid: true,
    websiteUrl: "https://shopline.com/"
  },
  "shopware": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms",
      "multichannel-retail"
    ],
    dependencies: [
      "mysql",
      "php",
      "symfony"
    ],
    free: true,
    g2Slug: "shopware",
    headers: {
      "sw-context-token": "^[\\w]{32}$",
      "sw-invalidation-states": "",
      "sw-language-id": "^[a-fA-F0-9]{32}$",
      "sw-version-id": ""
    },
    metas: { "application-name": "Shopware" },
    name: "Shopware",
    paid: true,
    scripts: [
      "(?:(shopware)|/web/cache/[0-9]{10}_.+)\\.js?4:5",
      "/engine/Shopware/",
      "/jquery\\.shopware\\.min\\.js"
    ],
    tags: ["<title>Shopware ([\\d\\.]+) [^<]+"],
    websiteUrl: "https://www.shopware.com/"
  },
  "signal": {
    categories: [
      "data-management-platforms",
      "digital-advertising",
      "identity-resolution",
      "marketing",
      "tag-management"
    ],
    free: true,
    g2Slug: "transunion-signal",
    name: "Signal",
    paid: true,
    scripts: ["//s\\.btstatic\\.com/tag\\.js", "//s\\.thebrighttag\\.com/iframe\\?"],
    variables: { signalData: "" },
    websiteUrl: "https://www.signal.co/"
  },
  "silverstripe-cms": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["php"],
    free: true,
    g2Slug: "silverstripe-cms",
    metas: { generator: "^SilverStripe" },
    name: "Silverstripe CMS",
    paid: true,
    tags: ['Powered by <a href="[^>]+SilverStripe'],
    websiteUrl: "https://www.silverstripe.org/"
  },
  "simple-analytics": {
    categories: ["digital-analytics", "marketing"],
    free: false,
    g2Slug: "simple-analytics",
    name: "Simple Analytics",
    paid: true,
    scripts: ["^https:\\/\\/cdn\\.simpleanalytics\\.io\\/hello\\.js"],
    websiteUrl: "https://simpleanalytics.com/"
  },
  "sitecore": {
    categories: [
      "content-management",
      "development",
      "digital-experience-platforms",
      "web-content-management",
      "webops-platforms"
    ],
    cookies: { SC_ANALYTICS_GLOBAL_COOKIE: "" },
    free: false,
    g2Slug: "sitecore-sitecore-experience-platform",
    name: "Sitecore",
    paid: true,
    tags: ['<img[^>]+src="[^>]*/~/media/[^>]+\\.ashx'],
    websiteUrl: "https://www.sitecore.com/"
  },
  "siteglide": {
    categories: [
      "commerce",
      "content-management",
      "development",
      "digital-experience-platforms",
      "e-commerce",
      "e-commerce-platforms",
      "web-content-management",
      "webops-platforms"
    ],
    free: true,
    g2Slug: "siteglide",
    name: "Siteglide",
    paid: true,
    scripts: ["siteglide\\.js"],
    websiteUrl: "https://www.siteglide.com/"
  },
  "siteground": {
    categories: [
      "hosting",
      "managed-hosting-providers",
      "virtual-private-serversproviders",
      "web-hosting-providers"
    ],
    free: false,
    g2Slug: "siteground",
    headers: { "host-header": "192fc2e7e50945beb8231a492d6a8024|b7440e60b07ee7b8044761568fab26e8|624d5be7be38418a3e2a818cc8b7029b|6b7412fb82ca5edfd0917e3957f05d89" },
    name: "SiteGround",
    paid: true,
    websiteUrl: "https://www.siteground.com/"
  },
  "smart-ad-server": {
    categories: [
      "app-monetization",
      "digital-advertising",
      "publisher-ad-management",
      "publisher-ad-server",
      "supply-side-platforms"
    ],
    free: true,
    g2Slug: "smart-adserver",
    name: "Smart Ad Server",
    paid: true,
    tags: ["<img[^>]+smartadserver\\.com\\/call"],
    variables: { SmartAdServer: "" },
    websiteUrl: "https://smartadserver.com/"
  },
  "smartlook": {
    categories: [
      "application-development",
      "conversion-rate-optimization",
      "development",
      "digital-analytics",
      "heatmap",
      "marketing",
      "mobile-analytics",
      "mobile-app-analytics",
      "mobile-app-optimization",
      "mobile-development",
      "product-analytics",
      "session-replay"
    ],
    free: true,
    g2Slug: "smartlook",
    name: "Smartlook",
    paid: true,
    scripts: ["rec\\.smartlook\\.com/recorder\\.js"],
    variables: { smartlook: "" },
    websiteUrl: "https://www.smartlook.com/"
  },
  "solid": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "Solid",
    paid: false,
    websiteUrl: "https://www.solidjs.com/"
  },
  "sonarqube": {
    categories: [
      "development",
      "devsecops",
      "secure-code-review",
      "security",
      "software-development-analytics",
      "static-application-security-testing",
      "static-code-analysis"
    ],
    dependencies: ["java"],
    free: true,
    g2Slug: "sonarqube",
    metas: { "application-name": "^SonarQube$" },
    name: "SonarQube",
    paid: true,
    scripts: ["^/js/bundles/sonar\\.js?v=([\\d.]+)$"],
    tags: ['<link href="/css/sonar\\.css\\?v=([\\d.]+)', "<title>SonarQube</title>"],
    variables: {
      SonarMeasures: "",
      SonarRequest: ""
    },
    websiteUrl: "https://www.sonarqube.org/"
  },
  "sqreen": {
    categories: ["application-performance-monitoring", "security"],
    free: true,
    headers: { "X-Protected-By": "^Sqreen$" },
    name: "Sqreen",
    paid: true,
    websiteUrl: "https://www.sqreen.com/"
  },
  "square-e-commerce": {
    categories: [
      "commerce",
      "content-management",
      "e-commerce",
      "e-commerce-platforms",
      "google-workspace-creative",
      "google-workspace-marketplace",
      "google-workspace-productivity",
      "shopping-cart",
      "website-builder"
    ],
    dependencies: ["mysql", "php"],
    free: true,
    g2Slug: "square-e-commerce-formerly-weebly",
    name: "Square E-Commerce",
    nameSynonyms: ["Weebly"],
    paid: true,
    scripts: ["cdn\\d+\\.editmysite\\.com"],
    variables: { "_W.configDomain": "" },
    websiteUrl: "https://squareup.com/us/en/online-store"
  },
  "squarespace": {
    categories: ["content-management", "website-builder"],
    free: true,
    g2Slug: "squarespace",
    headers: { "X-ServedBy": "squarespace" },
    name: "Squarespace",
    paid: true,
    tags: ["<!-- This is Squarespace\\. -->"],
    urls: [
      "assets\\.squarespace\\.com",
      "images\\.squarespace-cdn\\.com",
      "static1\\.squarespace\\.com"
    ],
    variables: { Squarespace: "" },
    websiteUrl: "https://www.squarespace.com/"
  },
  "squiz-matrix": {
    categories: [
      "content-management",
      "digital-experience-platforms",
      "headless-cms",
      "web-content-management",
      "website-builder"
    ],
    dependencies: ["php"],
    free: false,
    g2Slug: "squiz-matrix",
    headers: { "X-Powered-By": "Squiz Matrix" },
    metas: { generator: "Squiz Matrix" },
    name: "Squiz Matrix",
    paid: true,
    tags: ["<!--\\s+Running (?:MySource|Squiz) Matrix"],
    websiteUrl: "https://www.squiz.net/platform/products/matrix"
  },
  "stackla": {
    categories: [
      "commerce",
      "content-management",
      "e-commerce",
      "social-commerce-platforms",
      "user-generated-content"
    ],
    free: true,
    g2Slug: "stackla",
    name: "Stackla",
    paid: false,
    scripts: ["assetscdn\\.stackla\\.com\\/media\\/js\\/widget\\/(?:[a-zA-Z0-9.]+)?\\.js"],
    variables: { Stackla: "" },
    websiteUrl: "https://stackla.com/"
  },
  "statcounter": {
    categories: ["digital-analytics", "marketing"],
    free: true,
    g2Slug: "statcounter",
    name: "Statcounter",
    paid: true,
    scripts: ["statcounter\\.com/counter/counter"],
    variables: {
      _statcounter: "",
      sc_project: "",
      sc_security: ""
    },
    websiteUrl: "https://statcounter.com/"
  },
  "strapi": {
    categories: [
      "aws-marketplace",
      "content-management",
      "headless-cms",
      "marketplace-apps",
      "office",
      "web-content-management"
    ],
    free: true,
    g2Slug: "strapi",
    headers: { "X-Powered-By": "^Strapi" },
    name: "Strapi",
    paid: true,
    websiteUrl: "https://strapi.io/"
  },
  "strikingly": {
    categories: ["content-management", "website-builder"],
    free: true,
    g2Slug: "strikingly",
    name: "Strikingly",
    paid: true,
    tags: ["<!-- Powered by Strikingly.com"],
    websiteUrl: "https://www.strikingly.com/"
  },
  "stripe": {
    categories: [
      "commerce",
      "financial-data-apis",
      "financial-services",
      "payment",
      "payment-gateways",
      "vertical-industry"
    ],
    dns: { txt: ["stripe-verification="] },
    free: true,
    g2Slug: "stripe-connect",
    name: "Stripe",
    paid: false,
    scripts: ["js\\.stripe\\.com"],
    tags: ["<input[^>]+data-stripe"],
    variables: { "Stripe.version": "^(.+)$" },
    websiteUrl: "https://stripe.com/"
  },
  "sucuri": {
    categories: [
      "content-delivery-network",
      "devsecops",
      "hosting",
      "security",
      "web-application-firewalls",
      "web-security",
      "website-security"
    ],
    free: false,
    g2Slug: "sucuri",
    headers: {
      "x-sucuri-cache:": "",
      "x-sucuri-id": ""
    },
    name: "Sucuri",
    paid: true,
    websiteUrl: "https://sucuri.net/"
  },
  "sumome": {
    categories: ["email-popups"],
    free: true,
    name: "SumoMe",
    paid: true,
    scripts: ["load\\.sumome\\.com"],
    websiteUrl: "https://sumo.com/"
  },
  "svelte": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "Svelte",
    paid: false,
    tags: ['<[^>]+class=\\"[^\\"]+\\ssvelte-[\\w]*\\"'],
    websiteUrl: "https://svelte.dev/",
    evaluateVersion: async (page) => {
      const version = await page.evaluate(() => {
        try {
          return window?.__svelte?.v ? Array.from(window?.__svelte?.v)?.[0] : void 0;
        } catch {
        }
      });
      return version;
    }
  },
  "sveltekit": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "SvelteKit",
    paid: false,
    tags: ["<[^>]+data-sveltekit-"],
    variables: { __sveltekit: "" },
    websiteUrl: "https://kit.svelte.dev/"
  },
  "swiftype": {
    categories: ["enterprise-search"],
    free: true,
    name: "Swiftype",
    paid: true,
    scripts: ["swiftype\\.com/embed\\.js$"],
    variables: { Swiftype: "" },
    websiteUrl: "https://swiftype.com/"
  },
  "symfony": {
    categories: [
      "development",
      "php-web-frameworks",
      "web-frameworks"
    ],
    cookies: { sf_redirect: "" },
    dependencies: ["php"],
    free: true,
    g2Slug: "symfony",
    name: "Symfony",
    paid: false,
    tags: ['(?:<div class="sf-toolbar[^>]+?>[^]+<span class="sf-toolbar-value">([\\d.])+|<div id="sfwdt[^"]+" class="[^"]*sf-toolbar)'],
    variables: { Sfjs: "" },
    websiteUrl: "https://symfony.com/"
  },
  "synology-diskstation": {
    categories: [
      "cloud-content-collaboration",
      "cloud-data-security",
      "cloud-file-security",
      "cloud-security",
      "content-management",
      "security"
    ],
    free: false,
    g2Slug: "diskstation",
    metas: {
      "application-name": "Synology DiskStation",
      "description": "^DiskStation provides a full-featured network attached storage"
    },
    name: "Synology DiskStation",
    paid: true,
    scripts: ["webapi/entry\\.cgi\\?api=SYNO\\.(?:Core|Filestation)\\.Desktop\\."],
    tags: ["<noscript><div class='syno-no-script'"],
    websiteUrl: "https://www.synology.com/en-us/dsm"
  },
  "tawk-to": {
    categories: ["customer-service", "live-chat"],
    free: true,
    g2Slug: "tawk-to",
    name: "Tawk.to",
    paid: true,
    scripts: ["//embed\\.tawk\\.to"],
    websiteUrl: "https://www.tawk.to/"
  },
  "tealium": {
    categories: ["marketing", "tag-management"],
    free: true,
    g2Slug: "tealium-iq-tag-management",
    name: "Tealium",
    paid: true,
    scripts: ["/tealium/utag\\.js$", "^(?:https?:)?//tags\\.tiqcdn\\.com/"],
    variables: { TEALIUMENABLED: "" },
    websiteUrl: "https://tealium.com/"
  },
  "thelia": {
    categories: ["e-commerce-platforms"],
    dependencies: ["php", "symfony"],
    free: true,
    name: "Thelia",
    paid: false,
    tags: ["<(?:link|style|script)[^>]+/assets/frontOffice/"],
    websiteUrl: "https://thelia.net/"
  },
  "tilda": {
    categories: ["content-management", "website-builder"],
    free: true,
    g2Slug: "tilda-publishing",
    name: "Tilda",
    paid: true,
    scripts: ["tilda(?:cdn|\\.ws|-blocks)"],
    tags: ["<link[^>]* href=[^>]+tilda(?:cdn|\\.ws|-blocks)"],
    websiteUrl: "https://tilda.cc/"
  },
  "tornado-server": {
    categories: [
      "application-server",
      "development",
      "it-infrastructure",
      "python-web-frameworks",
      "web-frameworks"
    ],
    free: true,
    g2Slug: "tornado",
    headers: { Server: "TornadoServer(?:/([\\d.]+))?" },
    name: "Tornado Server",
    paid: false,
    websiteUrl: "https://www.tornadoweb.org/"
  },
  "trackjs": {
    categories: [
      "application-development",
      "application-performance-monitoring",
      "bug-tracking",
      "development",
      "devops",
      "it-infrastructure",
      "mobile-analytics",
      "mobile-crash-reporting",
      "mobile-development",
      "monitoring"
    ],
    free: true,
    g2Slug: "trackjs",
    name: "TrackJs",
    paid: true,
    scripts: ["tracker\\.js"],
    variables: { TrackJs: "" },
    websiteUrl: "https://trackjs.com/"
  },
  "transifex": {
    categories: [
      "computer-assisted-translation",
      "content-management",
      "development",
      "localization",
      "software-localization",
      "translation-management",
      "website-translation"
    ],
    free: true,
    g2Slug: "transifex",
    name: "Transifex",
    paid: true,
    variables: { "Transifex.live.lib_version": "(.+)" },
    websiteUrl: "https://www.transifex.com/"
  },
  "twitter-widgets": {
    categories: ["social-media-marketing"],
    free: true,
    name: "Twitter Widgets",
    paid: false,
    scripts: ["//platform\\.twitter\\.com/widgets\\.js"],
    websiteUrl: "https://twitter.com/"
  },
  "typo3-cms": {
    categories: ["content-management", "web-content-management"],
    dependencies: ["php"],
    free: true,
    g2Slug: "typo3",
    metas: { generator: "TYPO3\\s+(?:CMS\\s+)?(?:[\\d.]+)?(?:\\s+CMS)?" },
    name: "TYPO3 CMS",
    paid: false,
    scripts: ["^/?typo3(?:conf|temp)/"],
    tags: [
      "<!--\n	This website is powered by TYPO3",
      '<img[^>]+ src="/?typo3(?:conf|temp)/',
      '<link[^>]+ href="/?typo3(?:conf|temp)/'
    ],
    urls: ["/typo3/"],
    websiteUrl: "https://typo3.org/"
  },
  "umbraco": {
    categories: [
      "content-management",
      "development",
      "headless-cms",
      "web-content-management",
      "webops-platforms"
    ],
    dependencies: ["microsoft-asp-net"],
    free: true,
    g2Slug: "umbraco",
    headers: { "X-Umbraco-Version": "^(.+)$" },
    metas: { generator: "umbraco" },
    name: "Umbraco",
    paid: false,
    tags: ["powered by <a href=[^>]+umbraco"],
    urls: ["/umbraco/login\\.aspx(?:$|\\?)"],
    variables: {
      "UC_IMAGE_SERVICE|ITEM_INFO_SERVICE": "",
      "UC_ITEM_INFO_SERVICE": "",
      "UC_SETTINGS": "",
      "Umbraco": ""
    },
    websiteUrl: "https://umbraco.com/"
  },
  "unbounce": {
    categories: [
      "a-b-testing",
      "conversion-rate-optimization",
      "landing-page-builders",
      "marketing",
      "pop-up-builder"
    ],
    free: true,
    g2Slug: "unbounce",
    headers: { "X-Unbounce-PageId": "" },
    name: "Unbounce",
    paid: true,
    scripts: ["ubembed\\.com"],
    websiteUrl: "https://unbounce.com/"
  },
  "user-com": {
    categories: [
      "crm",
      "customer-service",
      "demand-generation",
      "lead-generation",
      "live-chat",
      "marketing",
      "marketing-automation",
      "push-notification",
      "sales",
      "visitor-identification"
    ],
    free: true,
    g2Slug: "user-com",
    name: "user.com",
    paid: true,
    tags: ['<div[^>]+/id="ue_widget"'],
    variables: { UserEngage: "" },
    websiteUrl: "https://www.user.com/"
  },
  "userflow": {
    categories: ["digital-adoption-platforms"],
    cookies: {
      userflow_visitor: "",
      userflow_visitor_id: ""
    },
    free: false,
    g2Slug: "userflow",
    name: "Userflow",
    paid: true,
    scripts: ["js\\.userflow\\.com"],
    variables: { userflow: "" },
    websiteUrl: "https://www.userflow.com/"
  },
  "userguiding": {
    categories: ["digital-adoption-platforms", "customer-self-service"],
    dns: { cname: ["userguiding.site"] },
    free: false,
    g2Slug: "userguiding",
    name: "UserGuiding",
    paid: true,
    scripts: ["static\\.userguiding\\.com"],
    tags: ['<div[^>]+id="__userGuiding__preview_Root"[^>]*>', "<!-- UserGuiding.com -->"],
    variables: {
      userGuiding: "",
      userGuidingLayer: "",
      userGuidingUserId: ""
    },
    websiteUrl: "https://userguiding.com/"
  },
  "userlike": {
    categories: [
      "artificial-intelligence",
      "chatbots",
      "conversational-intelligence",
      "conversational-marketing",
      "conversational-support",
      "customer-service",
      "live-chat",
      "marketing",
      "social-customer-service"
    ],
    free: true,
    g2Slug: "userlike",
    name: "UserLike",
    paid: true,
    scripts: ["userlike\\.min\\.js", "userlikelib\\.min\\.js"],
    websiteUrl: "https://www.userlike.com/"
  },
  "userpilot": {
    categories: [
      "digital-adoption-platforms",
      "product-analytics",
      "survey"
    ],
    dns: { cname: ["js.userpilot.io"] },
    free: false,
    g2Slug: "userpilot",
    name: "Userpilot",
    paid: true,
    scripts: ["js\\.userpilot\\.io"],
    tags: ['<div[^>]+id="userpilotContent"[^>]*>'],
    variables: { userpilotSettings: "" },
    websiteUrl: "https://userpilot.com/"
  },
  "uservoice": {
    categories: [
      "customer-service",
      "development",
      "enterprise-feedback-management",
      "product-management"
    ],
    free: true,
    g2Slug: "uservoice",
    name: "UserVoice",
    paid: true,
    variables: { UserVoice: "" },
    websiteUrl: "https://www.uservoice.com/"
  },
  "usetiful": {
    categories: ["digital-adoption-platforms", "customer-self-service"],
    free: true,
    g2Slug: "usetiful",
    name: "Usetiful",
    paid: true,
    scripts: ["www.usetiful.com/dist/usetiful.js"],
    tags: ["<!-- Usetiful script start -->"],
    variables: {
      USETIFUL: "",
      usetifulTags: ""
    },
    websiteUrl: "https://www.usetiful.com/"
  },
  "vaadin": {
    categories: [
      "development",
      "java-web-frameworks",
      "web-frameworks"
    ],
    dependencies: ["java"],
    free: true,
    g2Slug: "vaadin",
    name: "Vaadin",
    paid: true,
    scripts: ["vaadinBootstrap\\.js(?:\\?v=([\\d.]+))?"],
    variables: { vaadin: "" },
    websiteUrl: "https://vaadin.com/"
  },
  "vanilla-forums": {
    categories: [
      "customer-self-service",
      "customer-service",
      "marketing",
      "online-community-management"
    ],
    dependencies: ["php"],
    free: false,
    g2Slug: "higher-logic-vanilla",
    headers: { "X-Powered-By": "Vanilla" },
    name: "Vanilla Forums",
    nameSynonyms: ["Higher Logic Vanilla"],
    paid: true,
    tags: ['<body id="(?:DiscussionsPage|vanilla)'],
    websiteUrl: "https://vanilla.higherlogic.com/"
  },
  "vbulletin": {
    categories: ["marketing", "online-community-management"],
    cookies: {
      bblastactivity: "",
      bblastvisit: "",
      bbsessionhash: ""
    },
    dependencies: ["php"],
    free: false,
    g2Slug: "vbulletin-cloud",
    metas: { generator: "vBulletin ?([\\d.]+)?" },
    name: "vBulletin",
    paid: true,
    tags: ['<div id="copyright">Powered by vBulletin'],
    variables: { vBulletin: "" },
    websiteUrl: "https://www.vbulletin.com/"
  },
  "vercel": {
    categories: [
      "ci-cd",
      "content-management",
      "continuous-delivery",
      "development",
      "devops",
      "hosting",
      "webops-platforms",
      "website-builder"
    ],
    free: true,
    g2Slug: "vercel",
    headers: {
      "Server": "Vercel",
      "X-Vercel-Cache": "",
      "X-Vercel-Id": ""
    },
    name: "Vercel",
    paid: true,
    websiteUrl: "https://vercel.com/"
  },
  "vidyard": {
    categories: [
      "content-management",
      "design",
      "office",
      "screen-and-video-capture",
      "video",
      "video-cms",
      "video-communications",
      "video-email",
      "video-hosting"
    ],
    free: true,
    g2Slug: "vidyard",
    name: "Vidyard",
    paid: true,
    scripts: ["//play\\.vidyard\\.com/embed/"],
    variables: { Vidyard: "" },
    websiteUrl: "https://www.vidyard.com/"
  },
  "viglink": {
    categories: ["commerce", "e-commerce"],
    free: true,
    g2Slug: "viglink",
    name: "VigLink",
    paid: false,
    scripts: ["(?:^[^/]*//[^/]*viglink\\.com/api/|vglnk\\.js)"],
    variables: {
      vglnk: "",
      vl_cB: "",
      vl_disable: ""
    },
    websiteUrl: "https://www.viglink.com/"
  },
  "virtuemart": {
    categories: ["commerce", "e-commerce"],
    dependencies: ["joomla"],
    free: true,
    g2Slug: "virtuemart",
    name: "VirtueMart",
    paid: false,
    tags: ['<div id="vmMainPage'],
    websiteUrl: "https://virtuemart.net/"
  },
  "vite": {
    categories: [
      "application-development",
      "development",
      "web-frameworks"
    ],
    free: true,
    name: "Vite",
    paid: false,
    websiteUrl: "https://vite.dev/",
    evaluateBundle: async (bundle) => {
      return !!(bundle.content.match(/vite:preloadError/) || bundle.content.match(/__vite__mapDeps/));
    }
  },
  "volusion": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms",
      "google-workspace-business",
      "google-workspace-for-sales",
      "google-workspace-marketplace"
    ],
    free: true,
    g2Slug: "volusion",
    name: "Volusion",
    paid: true,
    scripts: ["/volusion\\.js(?:\\?([\\d.]*))?"],
    tags: ["<body [^>]*data-vn-page-name", '<link [^>]*href="[^"]*/vspfiles/'],
    variables: { volusion: "" },
    websiteUrl: "https://www.volusion.com/"
  },
  "vtex-commerce-platform": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    cookies: {
      vtex_session: "",
      VtexFingerPrint: "",
      VtexWorkspace: ""
    },
    free: false,
    g2Slug: "vtex-commerce-platform",
    headers: {
      powered: "vtex",
      Server: "^VTEX IO$"
    },
    name: "VTEX Commerce Platform",
    paid: true,
    websiteUrl: "https://vtex.com/"
  },
  "vue-js": {
    categories: ["javascript-web-frameworks"],
    free: true,
    name: "Vue",
    paid: false,
    scripts: ["(?:/([\\d.]+))?/vue(?:\\.min)?\\.js", "vue[.-]([\\d.]*\\d)[^/]*\\.js"],
    tags: ["<[^>]+\\sdata-v(?:ue)?-"],
    variables: { "Vue.version": "^(.+)$" },
    websiteUrl: "https://vuejs.org/",
    evaluateVersion: async (page) => {
      let version;
      try {
        version = await page.evaluate(() => {
          try {
            return window?.__unctx__?.get("nuxt-app")?.use()?.versions?.vue;
          } catch {
          }
        });
      } catch {
      }
      if (!version) {
        try {
          version = await page.evaluate(() => {
            try {
              return window?.useNuxtApp?.().versions?.vue;
            } catch {
            }
          });
        } catch {
        }
      }
      if (!version) {
        version = await page.evaluate(() => {
          try {
            return window?.__VUE__?.version;
          } catch {
          }
        });
      }
      if (!version) {
        version = await page.evaluate(() => {
          try {
            return window?.Vue?.version;
          } catch {
          }
        });
      }
      if (!version) {
        version = await page.evaluate(() => {
          try {
            return window?.$nuxt ? "2" : void 0;
          } catch {
          }
        });
      }
      return version;
    }
  },
  "vwo": {
    categories: [
      "a-b-testing",
      "application-development",
      "conversion-rate-optimization",
      "development",
      "feature-management",
      "marketing",
      "mobile-app-optimization",
      "mobile-development",
      "personalization",
      "personalization-engines"
    ],
    free: true,
    g2Slug: "wingify-vwo-testing",
    name: "VWO",
    paid: true,
    scripts: ["dev\\.visualwebsiteoptimizer\\.com"],
    tags: ["<!-- (?:Start|End) Visual Website Optimizer A?Synchronous Code -->"],
    variables: {
      __vwo: "",
      VWO: ""
    },
    websiteUrl: "https://vwo.com/"
  },
  "webflow": {
    categories: [
      "content-management",
      "design",
      "development",
      "hosting",
      "software-design",
      "web-content-management",
      "web-design",
      "web-hosting-providers",
      "webops-platforms",
      "website-builder"
    ],
    free: true,
    g2Slug: "webflow",
    metas: { generator: "Webflow" },
    name: "Webflow",
    paid: true,
    tags: ["<html[^>]+data-wf-site"],
    variables: { Webflow: "" },
    websiteUrl: "https://webflow.com/"
  },
  "weglot": {
    categories: [
      "computer-assisted-translation",
      "content-management",
      "localization",
      "translation-management",
      "website-translation"
    ],
    free: false,
    g2Slug: "weglot",
    headers: { "Weglot-Translated": "" },
    name: "Weglot",
    paid: true,
    scripts: ["cdn\\.weglot\\.com", "wp-content/plugins/weglot"],
    websiteUrl: "https://weglot.com/"
  },
  "whmcs": {
    categories: [
      "accounting-and-finance",
      "billing",
      "erp"
    ],
    cookies: { WHMCS: "" },
    free: true,
    g2Slug: "whmcs",
    name: "WHMCS",
    paid: true,
    websiteUrl: "https://www.whmcs.com/"
  },
  "whooshkaa": {
    categories: ["design", "podcast-hosting-platforms"],
    free: false,
    g2Slug: "whooshkaa",
    name: "Whooshkaa",
    paid: true,
    tags: ['<iframe src="[^>]+whooshkaa\\.com'],
    websiteUrl: "https://www.whooshkaa.com/"
  },
  "wildfly": {
    categories: ["application-server", "it-infrastructure"],
    free: true,
    g2Slug: "wildfly",
    headers: { "X-Powered-By": "JBossWeb(?:-([\\d.]+))?" },
    name: "Wildfly",
    nameSynonyms: ["JBoss"],
    paid: false,
    websiteUrl: "https://jbossweb.jboss.org/"
  },
  "wistia": {
    categories: [
      "content-management",
      "design",
      "development",
      "video",
      "video-cms",
      "video-editing",
      "video-hosting",
      "video-platform-as-a-service"
    ],
    free: true,
    g2Slug: "wistia",
    name: "Wistia",
    paid: true,
    scripts: ["fast\\.wistia\\.com/assets/external"],
    websiteUrl: "https://wistia.com/"
  },
  "wix": {
    categories: [
      "content-management",
      "conversion-rate-optimization",
      "landing-page-builders",
      "marketing",
      "website-builder"
    ],
    cookies: { Domain: "\\.wix\\.com" },
    dependencies: ["react"],
    free: true,
    g2Slug: "wix",
    headers: {
      "X-Wix-Renderer-Server": "",
      "X-Wix-Request-Id": "",
      "X-Wix-Server-Artifact-Id": ""
    },
    metas: { generator: "Wix\\.com Website Builder" },
    name: "Wix",
    paid: true,
    scripts: ["static\\.parastorage\\.com"],
    urls: ["//static\\.wixstatic\\.com//media/"],
    variables: { wixBiSession: "" },
    websiteUrl: "https://www.wix.com/"
  },
  "woocommerce": {
    categories: [
      "commerce",
      "e-commerce",
      "e-commerce-platforms"
    ],
    dependencies: ["wordpress"],
    free: true,
    g2Slug: "woocommerce",
    metas: { generator: "WooCommerce ([\\d.]+)" },
    name: "WooCommerce",
    paid: false,
    scripts: ["/woocommerce(?:\\.min)?\\.js(?:\\?ver=([0-9.]+))?", "/wp-content/plugins/woocommerce/"],
    tags: ["<!-- WooCommerce", "<link rel='[^']+' id='woocommerce-(?:layout|smallscreen|general)-css'  href='https?://[^/]+/wp-content/plugins/woocommerce/assets/css/woocommerce(?:-layout|-smallscreen)?\\.css?ver=([\\d.]+)'"],
    variables: { woocommerce_params: "" },
    websiteUrl: "https://woocommerce.com/"
  },
  "woopra": {
    categories: [
      "commerce",
      "conversion-rate-optimization",
      "customer-journey-analytics",
      "digital-analytics",
      "e-commerce",
      "e-commerce-analytics",
      "marketing",
      "product-analytics",
      "subscription-analytics"
    ],
    free: true,
    g2Slug: "woopra",
    name: "Woopra",
    paid: true,
    scripts: ["static\\.woopra\\.com"],
    websiteUrl: "https://www.woopra.com/"
  },
  "wordpress": {
    categories: [
      "aws-marketplace",
      "content-management",
      "marketplace-apps",
      "office",
      "web-content-management"
    ],
    dependencies: ["mysql", "php"],
    free: true,
    g2Slug: "wordpress-org",
    headers: { link: 'rel="https://api\\.w\\.org/"' },
    metas: { generator: "^WordPress ?([\\d.]+)?" },
    name: "WordPress",
    paid: false,
    scripts: ["/wp-(?:content|dependencies|includes)/"],
    tags: [`<link rel=["']stylesheet["'] [^>]+/wp-(?:content|dependencies|includes)/`, "<link[^>]+s\\d+\\.wp\\.com"],
    variables: { wp_username: "" },
    websiteUrl: "https://wordpress.org/"
  },
  "wordpress-vip": {
    categories: [
      "content-management",
      "digital-experience-platforms",
      "hosting",
      "managed-hosting-providers",
      "web-content-management",
      "web-hosting-providers"
    ],
    dependencies: ["wordpress"],
    free: false,
    g2Slug: "wordpress-vip",
    headers: { "x-powered-by": "^WordPress.com VIP" },
    name: "WordPress VIP",
    paid: true,
    websiteUrl: "https://wpvip.com/"
  },
  "wp-engine": {
    categories: [
      "hosting",
      "managed-hosting-providers",
      "web-hosting-providers"
    ],
    dependencies: ["wordpress"],
    free: false,
    g2Slug: "wp-engine",
    headers: {
      "wpe-backend": "",
      "X-Pass-Why": "",
      "X-Powered-By": "WP Engine",
      "X-WPE-Loopback-Upstream-Addr": ""
    },
    name: "WP Engine",
    paid: true,
    websiteUrl: "https://wpengine.com/"
  },
  "wp-statistics": {
    categories: ["analytics"],
    dependencies: ["wordpress"],
    free: true,
    name: "WP-Statistics",
    paid: false,
    tags: ["<!-- Analytics by WP-Statistics v([\\d.]+) -"],
    websiteUrl: "https://wordpress.org/plugins/wp-statistics/"
  },
  "x-cart": {
    categories: ["e-commerce-platforms"],
    cookies: { xid: "[a-z\\d]{32}(?:;|$)" },
    dependencies: ["php"],
    free: true,
    metas: { generator: "X-Cart(?: (\\d+))?" },
    name: "X-Cart",
    paid: true,
    scripts: ["/skin/common_files/modules/Product_Options/func\\.js"],
    tags: ['<a[^>]+href="[^"]*(?:\\?|&)xcart_form_id=[a-z\\d]{32}(?:&|$)', 'Powered by X-Cart(?: (\\d+))? <a[^>]+href="http://www\\.x-cart\\.com/"[^>]*>'],
    variables: {
      xcart_web_dir: "",
      xliteConfig: ""
    },
    websiteUrl: "https://www.x-cart.com/"
  },
  "xenforo": {
    categories: ["marketing", "online-community-management"],
    cookies: {
      xf_csrf: "",
      xf_session: ""
    },
    dependencies: ["mysql", "php"],
    free: false,
    g2Slug: "xenforo",
    name: "XenForo",
    paid: true,
    tags: ['(?:jQuery\\.extend\\(true, XenForo|Forum software by XenForo\u2122|<!--XF:branding|<html[^>]+id="XenForo")', '<html id="XF" '],
    variables: { "XF.GuestUsername": "" },
    websiteUrl: "https://xenforo.com/"
  },
  "yandex-metrika": {
    categories: [
      "conversion-rate-optimization",
      "digital-analytics",
      "heatmap",
      "marketing",
      "session-replay"
    ],
    free: true,
    g2Slug: "yandex-metrica",
    name: "Yandex.Metrika",
    paid: false,
    scripts: ["//cdn\\.jsdelivr\\.net/npm/yandex-metrica-watch/", "mc\\.yandex\\.ru\\/metrika/watch\\.js"],
    variables: { yandex_metrika: "" },
    websiteUrl: "https://metrica.yandex.com/"
  },
  "yoast-seo": {
    categories: ["marketing", "seo"],
    dependencies: ["wordpress"],
    free: true,
    g2Slug: "yoast-yoast",
    name: "Yoast SEO",
    paid: true,
    tags: ["<!-- This site is optimized with the Yoast (?:WordPress )?SEO"],
    websiteUrl: "https://yoast.com/"
  },
  "youtrack": {
    categories: [
      "bug-tracking",
      "development",
      "devops"
    ],
    free: false,
    g2Slug: "youtrack",
    name: "YouTrack",
    paid: true,
    tags: [
      'data-reactid="[^"]+">youTrack ([0-9.]+)<',
      'no-title="YouTrack">',
      'type="application/opensearchdescription\\+xml" title="YouTrack"/>'
    ],
    websiteUrl: "https://www.jetbrains.com/youtrack/"
  },
  "zeit-now": {
    categories: ["hosting", "web-hosting-providers"],
    free: false,
    headers: {
      "server": "^now$",
      "x-now-cache": "",
      "x-now-id": "",
      "x-now-trace": ""
    },
    name: "Zeit Now",
    paid: true,
    websiteUrl: "https://zeit.co/"
  },
  "zend": {
    categories: ["php-web-frameworks"],
    cookies: { ZENDSERVERSESSID: "" },
    free: true,
    headers: { "X-Powered-By": "Zend(?:Server)?(?:[\\s/]?([0-9.]+))?" },
    name: "Zend",
    paid: true,
    websiteUrl: "https://www.zend.com/"
  },
  "zendesk": {
    categories: [
      "artificial-intelligence",
      "call-and-contact-center",
      "chatbots",
      "complaint-management",
      "contact-center-knowledge-base",
      "conversational-intelligence",
      "conversational-support",
      "customer-self-service",
      "customer-service",
      "digital-customer-service-platforms",
      "genesys-appfoundry-marketplace",
      "google-workspace-business",
      "google-workspace-communication",
      "google-workspace-for-sales",
      "google-workspace-marketplace",
      "help-desk",
      "it-management",
      "it-service-management",
      "live-chat",
      "marketplace-apps",
      "office",
      "proactive-notification",
      "shared-inbox",
      "social-customer-service"
    ],
    cookies: {
      _help_center_session: "",
      _zendesk_cookie: "",
      _zendesk_shared_session: ""
    },
    dns: { cname: [".zendesk.com"] },
    free: false,
    g2Slug: "zendesk-support-suite",
    headers: { "x-zendesk-user-id": "" },
    name: "Zendesk",
    nameSynonyms: ["Zopim"],
    paid: true,
    scripts: ["static\\.zdassets\\.com", "v2\\.zopim\\.com"],
    tags: ["Copyright Zendesk, Inc."],
    urls: ["\\.zendesk\\.com/"],
    variables: { Zendesk: "" },
    websiteUrl: "https://www.zendesk.com/"
  },
  "zenfolio": {
    categories: [
      "design",
      "photo-management",
      "photography"
    ],
    free: true,
    g2Slug: "zenfolio",
    name: "Zenfolio",
    paid: true,
    variables: { Zenfolio: "" },
    websiteUrl: "https://zenfolio.com/"
  },
  "zeta-programmatic": {
    categories: [
      "advertiser-campaign-management",
      "creative-management-platforms",
      "cross-channel-advertising",
      "data-management-platforms",
      "demand-side-platform",
      "digital-advertising",
      "display-advertising",
      "mobile-advertising",
      "video-advertising"
    ],
    free: false,
    g2Slug: "zeta-programmatic-formerly-sizmek",
    name: "Zeta Programmatic",
    nameSynonyms: ["Sizmek"],
    paid: true,
    scripts: ["serving-sys\\.com/"],
    tags: ['(?:<a [^>]*href="[^/]*//[^/]*serving-sys\\.com/|<img [^>]*src="[^/]*//[^/]*serving-sys\\.com/)'],
    websiteUrl: "https://www.zeta.com/zeta-programmatic/"
  },
  "zk-framework": {
    categories: ["java-web-frameworks"],
    dependencies: ["java"],
    free: true,
    name: "ZK Framework",
    paid: true,
    scripts: ["zkau/"],
    tags: ["<!-- ZK [.\\d\\s]+-->"],
    websiteUrl: "https://www.zkoss.org/"
  },
  "zoho-mail": {
    categories: [
      "bookmark-manager",
      "calendar",
      "domain-registration-providers",
      "email",
      "erp",
      "hosting",
      "note-taking-management",
      "office",
      "project-portfolio-and-program-management",
      "task-management"
    ],
    dns: { mx: ["mx\\d?.zoho.eu"] },
    free: false,
    g2Slug: "zoho-mail",
    name: "Zoho Mail",
    paid: true,
    websiteUrl: "https://www.zoho.com/mail/"
  }
};

const tlds = {
  "aaa": { slds: {} },
  "aarp": { slds: {} },
  "abarth": { slds: {} },
  "abb": { slds: {} },
  "abbott": { slds: {} },
  "abbvie": { slds: {} },
  "abc": { slds: {} },
  "able": { slds: {} },
  "abogado": { slds: {} },
  "abudhabi": { slds: {} },
  "ac": { slds: {} },
  "academy": { slds: {} },
  "accenture": { slds: {} },
  "accountant": { slds: {} },
  "accountants": { slds: {} },
  "aco": { slds: {} },
  "active": { slds: {} },
  "actor": { slds: {} },
  "ad": { slds: {} },
  "ads": { slds: {} },
  "adult": { slds: {} },
  "ae": {
    countryWeCanTrust: "ae",
    slds: {
      "ac.ae": {},
      "co.ae": {},
      "gov.ae": {},
      "mil.ae": {},
      "name.ae": {},
      "net.ae": {},
      "org.ae": {},
      "pro.ae": {},
      "sch.ae": {}
    }
  },
  "aeg": { slds: {} },
  "aero": {
    slds: {
      "cargo.aero": {},
      "charter.aero": {}
    }
  },
  "aetna": { slds: {} },
  "af": {
    countryWeCanTrust: "af",
    slds: {
      "com.af": {},
      "edu.af": {},
      "gov.af": {},
      "net.af": {},
      "org.af": {}
    }
  },
  "afl": { slds: {} },
  "africa": { slds: {} },
  "ag": {
    slds: {
      "co.ag": {},
      "com.ag": {},
      "net.ag": {},
      "nom.ag": {},
      "org.ag": {}
    }
  },
  "agakhan": { slds: {} },
  "agency": { slds: {} },
  "ai": {
    slds: {
      "com.ai": {},
      "net.ai": {},
      "off.ai": {},
      "org.ai": {}
    }
  },
  "aig": { slds: {} },
  "aigo": { slds: {} },
  "airbus": { slds: {} },
  "airforce": { slds: {} },
  "airtel": { slds: {} },
  "akdn": { slds: {} },
  "al": {
    slds: {
      "com.al": {},
      "edu.al": {},
      "net.al": {},
      "org.al": {}
    }
  },
  "alfaromeo": { slds: {} },
  "alibaba": { slds: {} },
  "alipay": { slds: {} },
  "allfinanz": { slds: {} },
  "allstate": { slds: {} },
  "ally": { slds: {} },
  "alsace": { slds: {} },
  "alstom": { slds: {} },
  "am": {
    slds: {
      "co.am": {},
      "com.am": {},
      "net.am": {},
      "north.am": {},
      "org.am": {},
      "radio.am": {},
      "south.am": {}
    }
  },
  "amazon": { slds: {} },
  "americanexpress": { slds: {} },
  "americanfamily": { slds: {} },
  "amex": { slds: {} },
  "amfam": { slds: {} },
  "amica": { slds: {} },
  "amsterdam": { slds: {} },
  "analytics": { slds: {} },
  "android": { slds: {} },
  "anquan": { slds: {} },
  "anz": { slds: {} },
  "ao": {
    countryWeCanTrust: "ao",
    slds: {
      "co.ao": {},
      "ed.ao": {},
      "gv.ao": {},
      "it.ao": {},
      "og.ao": {},
      "pb.ao": {}
    }
  },
  "aol": { slds: {} },
  "apartments": { slds: {} },
  "app": { slds: {} },
  "apple": { slds: {} },
  "aq": { slds: {} },
  "aquarelle": { slds: {} },
  "ar": {
    countryWeCanTrust: "ar",
    slds: {
      "com.ar": {},
      "edu.ar": {},
      "gov.ar": {},
      "int.ar": {},
      "mil.ar": {},
      "net.ar": {},
      "org.ar": {}
    }
  },
  "arab": { slds: {} },
  "aramco": { slds: {} },
  "archi": { slds: {} },
  "army": { slds: {} },
  "arpa": { slds: {} },
  "art": { slds: {} },
  "arte": { slds: {} },
  "as": { slds: {} },
  "asda": { slds: {} },
  "asia": { slds: {} },
  "associates": { slds: {} },
  "at": {
    countryWeCanTrust: "at",
    slds: {
      "ac.at": {},
      "co.at": {},
      "gv.at": {},
      "or.at": {},
      "priv.at": {}
    }
  },
  "athleta": { slds: {} },
  "attorney": { slds: {} },
  "au": {
    countryWeCanTrust: "au",
    slds: {
      "act.au": {},
      "asn.au": {},
      "com.au": {},
      "conf.au": {},
      "csiro.au": {},
      "edu.au": {},
      "gov.au": {},
      "id.au": {},
      "info.au": {},
      "net.au": {},
      "nsw.au": {},
      "nt.au": {},
      "org.au": {},
      "oz.au": {},
      "qld.au": {},
      "sa.au": {},
      "tas.au": {},
      "vic.au": {},
      "wa.au": {}
    }
  },
  "auction": { slds: {} },
  "audi": { slds: {} },
  "audible": { slds: {} },
  "audio": { slds: {} },
  "auspost": { slds: {} },
  "author": { slds: {} },
  "auto": { slds: {} },
  "autos": { slds: {} },
  "aw": {
    countryWeCanTrust: "aw",
    slds: { "com.aw": {} }
  },
  "aws": { slds: {} },
  "ax": {
    countryWeCanTrust: "ax",
    slds: {}
  },
  "axa": { slds: {} },
  "az": {
    countryWeCanTrust: "az",
    slds: {
      "biz.az": {},
      "co.az": {},
      "com.az": {},
      "edu.az": {},
      "gov.az": {},
      "info.az": {},
      "int.az": {},
      "mil.az": {},
      "name.az": {},
      "net.az": {},
      "org.az": {},
      "pp.az": {},
      "pro.az": {}
    }
  },
  "azure": { slds: {} },
  "ba": {
    countryWeCanTrust: "ba",
    slds: {
      "co.ba": {},
      "com.ba": {}
    }
  },
  "baby": { slds: {} },
  "baidu": { slds: {} },
  "banamex": { slds: {} },
  "bananarepublic": { slds: {} },
  "band": { slds: {} },
  "bank": { slds: {} },
  "bar": { slds: {} },
  "barcelona": { slds: {} },
  "barclaycard": { slds: {} },
  "barclays": { slds: {} },
  "barefoot": { slds: {} },
  "bargains": { slds: {} },
  "baseball": { slds: {} },
  "basketball": { slds: {} },
  "bauhaus": { slds: {} },
  "bayern": { slds: {} },
  "bb": {
    countryWeCanTrust: "bb",
    slds: {
      "co.bb": {},
      "com.bb": {},
      "edu.bb": {},
      "gov.bb": {},
      "net.bb": {},
      "org.bb": {}
    }
  },
  "bbc": { slds: {} },
  "bbs": { slds: {} },
  "bbt": { slds: {} },
  "bbva": { slds: {} },
  "bcg": { slds: {} },
  "bcn": { slds: {} },
  "bd": {
    countryWeCanTrust: "bd",
    slds: {
      "ac.bd": {},
      "com.bd": {},
      "edu.bd": {},
      "gov.bd": {},
      "mil.bd": {},
      "net.bd": {},
      "org.bd": {}
    }
  },
  "be": {
    countryWeCanTrust: "be",
    slds: { "ac.be": {} }
  },
  "beats": { slds: {} },
  "beauty": { slds: {} },
  "beer": { slds: {} },
  "bentley": { slds: {} },
  "berlin": { slds: {} },
  "best": { slds: {} },
  "bestbuy": { slds: {} },
  "bet": { slds: {} },
  "bf": {
    countryWeCanTrust: "bf",
    slds: { "gov.bf": {} }
  },
  "bg": {
    countryWeCanTrust: "bg",
    slds: {}
  },
  "bh": {
    countryWeCanTrust: "bh",
    slds: {
      "biz.bh": {},
      "cc.bh": {},
      "com.bh": {},
      "edu.bh": {},
      "me.bh": {},
      "name.bh": {},
      "net.bh": {},
      "org.bh": {}
    }
  },
  "bharti": { slds: {} },
  "bi": {
    countryWeCanTrust: "bi",
    slds: {
      "co.bi": {},
      "com.bi": {},
      "edu.bi": {},
      "info.bi": {},
      "mo.bi": {},
      "net.bi": {},
      "or.bi": {},
      "org.bi": {}
    }
  },
  "bible": { slds: {} },
  "bid": { slds: {} },
  "bike": { slds: {} },
  "bing": { slds: {} },
  "bingo": { slds: {} },
  "bio": { slds: {} },
  "bit": { slds: {} },
  "biz": { slds: { "auz.biz": {} } },
  "bj": {
    countryWeCanTrust: "bj",
    slds: { "com.bj": {} }
  },
  "black": { slds: {} },
  "blackfriday": { slds: {} },
  "blanco": { slds: {} },
  "blockbuster": { slds: {} },
  "blog": { slds: {} },
  "bloomberg": { slds: {} },
  "blue": { slds: {} },
  "bm": {
    countryWeCanTrust: "bm",
    slds: {
      "com.bm": {},
      "edu.bm": {},
      "gov.bm": {},
      "net.bm": {},
      "org.bm": {}
    }
  },
  "bms": { slds: {} },
  "bmw": { slds: {} },
  "bn": {
    countryWeCanTrust: "bn",
    slds: {
      "com.bn": {},
      "edu.bn": {},
      "net.bn": {},
      "org.bn": {}
    }
  },
  "bnl": { slds: {} },
  "bnpparibas": { slds: {} },
  "bo": {
    countryWeCanTrust: "bo",
    slds: {
      "com.bo": {},
      "edu.bo": {},
      "gob.bo": {},
      "gov.bo": {},
      "int.bo": {},
      "mil.bo": {},
      "net.bo": {},
      "org.bo": {},
      "tv.bo": {}
    }
  },
  "boats": { slds: {} },
  "boehringer": { slds: {} },
  "bofa": { slds: {} },
  "bom": { slds: {} },
  "bond": { slds: {} },
  "boo": { slds: {} },
  "book": { slds: {} },
  "booking": { slds: {} },
  "boots": { slds: {} },
  "bosch": { slds: {} },
  "bostik": { slds: {} },
  "boston": { slds: {} },
  "bot": { slds: {} },
  "boutique": { slds: {} },
  "box": { slds: {} },
  "br": {
    countryWeCanTrust: "br",
    slds: {
      "abc.br": {},
      "adm.br": {},
      "adv.br": {},
      "agr.br": {},
      "am.br": {},
      "aparecida.br": {},
      "app.br": {},
      "arq.br": {},
      "art.br": {},
      "ato.br": {},
      "belem.br": {},
      "bhz.br": {},
      "bib.br": {},
      "bio.br": {},
      "blog.br": {},
      "bmd.br": {},
      "boavista.br": {},
      "br": {},
      "bsb.br": {},
      "campinas.br": {},
      "caxias.br": {},
      "cim.br": {},
      "cng.br": {},
      "cnt.br": {},
      "com.br": {},
      "coop.br": {},
      "curitiba.br": {},
      "des.br": {},
      "det.br": {},
      "dev.br": {},
      "dpn.br": {},
      "ecn.br": {},
      "eco.br": {},
      "edu.br": {},
      "emp.br": {},
      "enf.br": {},
      "eng.br": {},
      "esp.br": {},
      "etc.br": {},
      "eti.br": {},
      "far.br": {},
      "flog.br": {},
      "floripa.br": {},
      "fm.br": {},
      "fnd.br": {},
      "fortal.br": {},
      "fot.br": {},
      "foz.br": {},
      "fst.br": {},
      "g12.br": {},
      "geo.br": {},
      "ggf.br": {},
      "gov.br": {},
      "gru.br": {},
      "imb.br": {},
      "ind.br": {},
      "inf.br": {},
      "jampa.br": {},
      "jor.br": {},
      "lel.br": {},
      "log.br": {},
      "macapa.br": {},
      "maceio.br": {},
      "manaus.br": {},
      "mat.br": {},
      "med.br": {},
      "mil.br": {},
      "mus.br": {},
      "natal.br": {},
      "net.br": {},
      "nom.br": {},
      "not.br": {},
      "ntr.br": {},
      "odo.br": {},
      "org.br": {},
      "palmas.br": {},
      "poa.br": {},
      "ppg.br": {},
      "pro.br": {},
      "psc.br": {},
      "psi.br": {},
      "qsl.br": {},
      "radio.br": {},
      "rec.br": {},
      "recife.br": {},
      "rep.br": {},
      "rio.br": {},
      "salvador.br": {},
      "seg.br": {},
      "sjc.br": {},
      "slg.br": {},
      "srv.br": {},
      "taxi.br": {},
      "tec.br": {},
      "teo.br": {},
      "tmp.br": {},
      "trd.br": {},
      "tur.br": {},
      "tv.br": {},
      "vet.br": {},
      "vix.br": {},
      "vlog.br": {},
      "wiki.br": {},
      "zlg.br": {}
    }
  },
  "bradesco": { slds: {} },
  "bridgestone": { slds: {} },
  "broadway": { slds: {} },
  "broker": { slds: {} },
  "brother": { slds: {} },
  "brussels": { slds: {} },
  "bs": {
    countryWeCanTrust: "bs",
    slds: {
      "com.bs": {},
      "net.bs": {},
      "org.bs": {}
    }
  },
  "bt": {
    countryWeCanTrust: "bt",
    slds: {
      "com.bt": {},
      "edu.bt": {},
      "gov.bt": {},
      "net.bt": {},
      "org.bt": {}
    }
  },
  "bugatti": { slds: {} },
  "build": { slds: {} },
  "builders": { slds: {} },
  "business": { slds: {} },
  "buy": { slds: {} },
  "buzz": { slds: {} },
  "bv": { slds: {} },
  "bw": {
    countryWeCanTrust: "bw",
    slds: {
      "ac.bw": {},
      "co.bw": {},
      "net.bw": {},
      "org.bw": {}
    }
  },
  "by": {
    countryWeCanTrust: "by",
    slds: {
      "com.by": {},
      "gov.by": {},
      "mil.by": {},
      "minsk.by": {},
      "net.by": {}
    }
  },
  "bz": {
    countryWeCanTrust: "bz",
    slds: {
      "co.bz": {},
      "com.bz": {},
      "net.bz": {},
      "org.bz": {},
      "za.bz": {}
    }
  },
  "bzh": { slds: {} },
  "ca": {
    countryWeCanTrust: "ca",
    slds: {
      "ab.ca": {},
      "bc.ca": {},
      "ca": {},
      "mb.ca": {},
      "nb.ca": {},
      "nf.ca": {},
      "nl.ca": {},
      "ns.ca": {},
      "nt.ca": {},
      "nu.ca": {},
      "on.ca": {},
      "pe.ca": {},
      "qc.ca": {},
      "sk.ca": {},
      "yk.ca": {}
    }
  },
  "cab": { slds: {} },
  "cafe": { slds: {} },
  "cal": { slds: {} },
  "call": { slds: {} },
  "calvinklein": { slds: {} },
  "cam": { slds: {} },
  "camera": { slds: {} },
  "camp": { slds: {} },
  "cancerresearch": { slds: {} },
  "canon": { slds: {} },
  "capetown": { slds: {} },
  "capital": { slds: {} },
  "capitalone": { slds: {} },
  "car": { slds: {} },
  "caravan": { slds: {} },
  "cards": { slds: {} },
  "care": { slds: {} },
  "career": { slds: {} },
  "careers": { slds: {} },
  "cars": { slds: {} },
  "cartier": { slds: {} },
  "casa": { slds: {} },
  "case": { slds: {} },
  "cash": { slds: {} },
  "casino": { slds: {} },
  "cat": { slds: {} },
  "catering": { slds: {} },
  "catholic": { slds: {} },
  "cba": { slds: {} },
  "cbn": { slds: {} },
  "cbre": { slds: {} },
  "cbs": { slds: {} },
  "cc": {
    countryWeCanTrust: "cc",
    slds: { "co.cc": {} }
  },
  "cd": {
    countryWeCanTrust: "cd",
    slds: {
      "com.cd": {},
      "net.cd": {},
      "org.cd": {}
    }
  },
  "center": { slds: {} },
  "ceo": { slds: {} },
  "cern": { slds: {} },
  "cf": {
    countryWeCanTrust: "cf",
    slds: {}
  },
  "cfa": { slds: {} },
  "cfd": { slds: {} },
  "cg": {
    countryWeCanTrust: "cg",
    slds: {}
  },
  "ch": {
    countryWeCanTrust: "ch",
    slds: {
      "com.ch": {},
      "gov.ch": {},
      "net.ch": {},
      "org.ch": {}
    }
  },
  "chanel": { slds: {} },
  "channel": { slds: {} },
  "charity": { slds: {} },
  "chase": { slds: {} },
  "chat": { slds: {} },
  "cheap": { slds: {} },
  "chintai": { slds: {} },
  "christmas": { slds: {} },
  "chrome": { slds: {} },
  "chrysler": { slds: {} },
  "church": { slds: {} },
  "ci": {
    countryWeCanTrust: "ci",
    slds: {
      "ac.ci": {},
      "co.ci": {},
      "com.ci": {},
      "ed.ci": {},
      "edu.ci": {},
      "go.ci": {},
      "in.ci": {},
      "int.ci": {},
      "net.ci": {},
      "nom.ci": {},
      "or.ci": {},
      "org.ci": {}
    }
  },
  "cipriani": { slds: {} },
  "circle": { slds: {} },
  "cisco": { slds: {} },
  "citadel": { slds: {} },
  "citi": { slds: {} },
  "citic": { slds: {} },
  "city": { slds: {} },
  "ck": {
    countryWeCanTrust: "ck",
    slds: {
      "biz.ck": {},
      "co.ck": {},
      "edu.ck": {},
      "gen.ck": {},
      "gov.ck": {},
      "info.ck": {},
      "net.ck": {},
      "org.ck": {}
    }
  },
  "cl": {
    countryWeCanTrust: "cl",
    slds: {}
  },
  "claims": { slds: {} },
  "cleaning": { slds: {} },
  "click": { slds: {} },
  "clinic": { slds: {} },
  "clinique": { slds: {} },
  "clothing": { slds: {} },
  "cloud": { slds: {} },
  "club": { slds: {} },
  "clubmed": { slds: {} },
  "cm": {
    countryWeCanTrust: "cm",
    slds: {
      "co.cm": {},
      "com.cm": {},
      "net.cm": {}
    }
  },
  "cn": {
    countryWeCanTrust: "cn",
    slds: {
      "ac.cn": {},
      "ah.cn": {},
      "bj.cn": {},
      "cn": {},
      "com.cn": {},
      "cq.cn": {},
      "edu.cn": {},
      "fj.cn": {},
      "gd.cn": {},
      "gov.cn": {},
      "gs.cn": {},
      "gx.cn": {},
      "gz.cn": {},
      "ha.cn": {},
      "hb.cn": {},
      "he.cn": {},
      "hi.cn": {},
      "hk.cn": {},
      "hl.cn": {},
      "hn.cn": {},
      "jl.cn": {},
      "js.cn": {},
      "jx.cn": {},
      "ln.cn": {},
      "mo.cn": {},
      "net.cn": {},
      "nm.cn": {},
      "nx.cn": {},
      "org.cn": {},
      "qh.cn": {},
      "sc.cn": {},
      "sd.cn": {},
      "sh.cn": {},
      "sn.cn": {},
      "sx.cn": {},
      "tj.cn": {},
      "tw.cn": {},
      "xj.cn": {},
      "xz.cn": {},
      "yn.cn": {},
      "zj.cn": {}
    }
  },
  "co": {
    slds: {
      "com.co": {},
      "edu.co": {},
      "gov.co": {},
      "mil.co": {},
      "net.co": {},
      "org.co": {}
    }
  },
  "coach": { slds: {} },
  "codes": { slds: {} },
  "coffee": { slds: {} },
  "college": { slds: {} },
  "cologne": { slds: {} },
  "com": {
    slds: {
      "ae.com": {},
      "africa.com": {},
      "br.com": {},
      "cn.com": {},
      "co.com": {},
      "de.com": {},
      "eu.com": {},
      "gr.com": {},
      "hk.com": {},
      "hu.com": {},
      "it.com": {},
      "jpn.com": {},
      "kr.com": {},
      "mex.com": {},
      "no.com": {},
      "nv.com": {},
      "pty-ltd.com": {},
      "qc.com": {},
      "ru.com": {},
      "sa.com": {},
      "se.com": {},
      "uk.com": {},
      "us.com": {},
      "za.com": {}
    }
  },
  "comcast": { slds: {} },
  "commbank": { slds: {} },
  "community": { slds: {} },
  "company": { slds: {} },
  "compare": { slds: {} },
  "computer": { slds: {} },
  "comsec": { slds: {} },
  "condos": { slds: {} },
  "construction": { slds: {} },
  "consulting": { slds: {} },
  "contact": { slds: {} },
  "contractors": { slds: {} },
  "cooking": { slds: {} },
  "cool": { slds: {} },
  "coop": { slds: {} },
  "corp": { slds: {} },
  "corsica": { slds: {} },
  "country": { slds: {} },
  "coupon": { slds: {} },
  "coupons": { slds: {} },
  "courses": { slds: {} },
  "cpa": { slds: {} },
  "cr": {
    countryWeCanTrust: "cr",
    slds: {
      "ac.cr": {},
      "co.cr": {},
      "ed.cr": {},
      "fi.cr": {},
      "go.cr": {},
      "or.cr": {},
      "sa.cr": {}
    }
  },
  "credit": { slds: {} },
  "creditcard": { slds: {} },
  "creditunion": { slds: {} },
  "cricket": { slds: {} },
  "crown": { slds: {} },
  "crs": { slds: {} },
  "cruise": { slds: {} },
  "cruises": { slds: {} },
  "crypto": { slds: {} },
  "csc": { slds: {} },
  "cu": {
    countryWeCanTrust: "cu",
    slds: {
      "com.cu": {},
      "edu.cu": {},
      "gov.cu": {},
      "inf.cu": {},
      "net.cu": {},
      "org.cu": {}
    }
  },
  "cuisinella": { slds: {} },
  "cv": {
    slds: {
      "com.cv": {},
      "edu.cv": {},
      "int.cv": {},
      "net.cv": {},
      "nome.cv": {},
      "org.cv": {},
      "publ.cv": {}
    }
  },
  "cw": {
    countryWeCanTrust: "cw",
    slds: {
      "com.cw": {},
      "net.cw": {}
    }
  },
  "cx": { slds: {} },
  "cy": {
    countryWeCanTrust: "cy",
    slds: {
      "ac.cy": {},
      "biz.cy": {},
      "com.cy": {},
      "ekloges.cy": {},
      "info.cy": {},
      "ltd.cy": {},
      "name.cy": {},
      "net.cy": {},
      "org.cy": {},
      "parliament.cy": {},
      "press.cy": {},
      "pro.cy": {},
      "tm.cy": {}
    }
  },
  "cymru": { slds: {} },
  "cyou": { slds: {} },
  "cz": {
    countryWeCanTrust: "cz",
    slds: { "co.cz": {} }
  },
  "dabur": { slds: {} },
  "dad": { slds: {} },
  "dance": { slds: {} },
  "data": { slds: {} },
  "date": { slds: {} },
  "dating": { slds: {} },
  "datsun": { slds: {} },
  "day": { slds: {} },
  "dclk": { slds: {} },
  "dds": { slds: {} },
  "de": {
    countryWeCanTrust: "de",
    slds: {
      "co.de": {},
      "com.de": {}
    }
  },
  "deal": { slds: {} },
  "dealer": { slds: {} },
  "deals": { slds: {} },
  "degree": { slds: {} },
  "delivery": { slds: {} },
  "dell": { slds: {} },
  "deloitte": { slds: {} },
  "delta": { slds: {} },
  "democrat": { slds: {} },
  "dental": { slds: {} },
  "dentist": { slds: {} },
  "desi": { slds: {} },
  "design": { slds: {} },
  "dev": { slds: {} },
  "dhl": { slds: {} },
  "diamonds": { slds: {} },
  "diet": { slds: {} },
  "digital": { slds: {} },
  "direct": { slds: {} },
  "directory": { slds: {} },
  "discount": { slds: {} },
  "discover": { slds: {} },
  "dish": { slds: {} },
  "diy": { slds: {} },
  "dj": { slds: {} },
  "dk": {
    countryWeCanTrust: "dk",
    slds: {
      "biz.dk": {},
      "co.dk": {}
    }
  },
  "dm": {
    countryWeCanTrust: "dm",
    slds: {
      "co.dm": {},
      "com.dm": {},
      "edu.dm": {},
      "gov.dm": {},
      "net.dm": {},
      "org.dm": {}
    }
  },
  "dnp": { slds: {} },
  "do": {
    countryWeCanTrust: "do",
    slds: {
      "art.do": {},
      "com.do": {},
      "edu.do": {},
      "gob.do": {},
      "gov.do": {},
      "mil.do": {},
      "net.do": {},
      "org.do": {},
      "sld.do": {},
      "web.do": {}
    }
  },
  "docs": { slds: {} },
  "doctor": { slds: {} },
  "dodge": { slds: {} },
  "dog": { slds: {} },
  "doha": { slds: {} },
  "domains": { slds: {} },
  "dot": { slds: {} },
  "download": { slds: {} },
  "drive": { slds: {} },
  "dtv": { slds: {} },
  "dubai": { slds: {} },
  "dunlop": { slds: {} },
  "dupont": { slds: {} },
  "durban": { slds: {} },
  "dvag": { slds: {} },
  "dvr": { slds: {} },
  "dz": {
    countryWeCanTrust: "dz",
    slds: {
      "art.dz": {},
      "asso.dz": {},
      "com.dz": {},
      "edu.dz": {},
      "gov.dz": {},
      "net.dz": {},
      "org.dz": {},
      "pol.dz": {}
    }
  },
  "earth": { slds: {} },
  "eat": { slds: {} },
  "ec": {
    countryWeCanTrust: "ec",
    slds: {
      "com.ec": {},
      "edu.ec": {},
      "fin.ec": {},
      "gov.ec": {},
      "info.ec": {},
      "med.ec": {},
      "mil.ec": {},
      "net.ec": {},
      "org.ec": {},
      "pro.ec": {}
    }
  },
  "eco": { slds: {} },
  "edeka": { slds: {} },
  "edu": { slds: {} },
  "education": { slds: {} },
  "ee": {
    countryWeCanTrust: "ee",
    slds: {
      "co.ee": {},
      "com.ee": {},
      "fie.ee": {},
      "med.ee": {},
      "org.ee": {},
      "pri.ee": {}
    }
  },
  "eg": {
    countryWeCanTrust: "eg",
    slds: {
      "com.eg": {},
      "edu.eg": {},
      "eun.eg": {},
      "gov.eg": {},
      "info.eg": {},
      "mil.eg": {},
      "name.eg": {},
      "net.eg": {},
      "org.eg": {},
      "sci.eg": {},
      "tv.eg": {}
    }
  },
  "eh": {
    countryWeCanTrust: "eh",
    slds: {}
  },
  "email": { slds: {} },
  "emerck": { slds: {} },
  "energy": { slds: {} },
  "engineer": { slds: {} },
  "engineering": { slds: {} },
  "enterprises": { slds: {} },
  "entertainment": { slds: {} },
  "epson": { slds: {} },
  "equipment": { slds: {} },
  "er": {
    countryWeCanTrust: "er",
    slds: {}
  },
  "ericsson": { slds: {} },
  "erni": { slds: {} },
  "es": {
    countryWeCanTrust: "es",
    slds: {
      "com.es": {},
      "edu.es": {},
      "gob.es": {},
      "nom.es": {},
      "org.es": {}
    }
  },
  "esq": { slds: {} },
  "estate": { slds: {} },
  "esurance": { slds: {} },
  "et": {
    countryWeCanTrust: "et",
    slds: {
      "biz.et": {},
      "com.et": {},
      "edu.et": {},
      "gov.et": {},
      "info.et": {},
      "name.et": {},
      "net.et": {},
      "org.et": {}
    }
  },
  "eth": { slds: {} },
  "etisalat": { slds: {} },
  "eu": { slds: {} },
  "eurovision": { slds: {} },
  "eus": { slds: {} },
  "events": { slds: {} },
  "everbank": { slds: {} },
  "example": { slds: {} },
  "exchange": { slds: {} },
  "expert": { slds: {} },
  "exposed": { slds: {} },
  "express": { slds: {} },
  "extraspace": { slds: {} },
  "fage": { slds: {} },
  "fail": { slds: {} },
  "fairwinds": { slds: {} },
  "faith": { slds: {} },
  "family": { slds: {} },
  "fan": { slds: {} },
  "fans": { slds: {} },
  "farm": { slds: {} },
  "farmers": { slds: {} },
  "fashion": { slds: {} },
  "fast": { slds: {} },
  "fedex": { slds: {} },
  "feedback": { slds: {} },
  "ferrari": { slds: {} },
  "ferrero": { slds: {} },
  "fi": {
    countryWeCanTrust: "fi",
    slds: { "aland.fi": {} }
  },
  "fiat": { slds: {} },
  "fidelity": { slds: {} },
  "fido": { slds: {} },
  "film": { slds: {} },
  "final": { slds: {} },
  "finance": { slds: {} },
  "financial": { slds: {} },
  "fire": { slds: {} },
  "firestone": { slds: {} },
  "firmdale": { slds: {} },
  "fish": { slds: {} },
  "fishing": { slds: {} },
  "fit": { slds: {} },
  "fitness": { slds: {} },
  "fj": {
    countryWeCanTrust: "fj",
    slds: {
      "ac.fj": {},
      "biz.fj": {},
      "com.fj": {},
      "gov.fj": {},
      "info.fj": {},
      "mil.fj": {},
      "name.fj": {},
      "net.fj": {},
      "org.fj": {},
      "pro.fj": {},
      "school.fj": {}
    }
  },
  "fk": {
    countryWeCanTrust: "fk",
    slds: {
      "ac.fk": {},
      "co.fk": {},
      "gov.fk": {},
      "net.fk": {},
      "nom.fk": {},
      "org.fk": {}
    }
  },
  "flickr": { slds: {} },
  "flights": { slds: {} },
  "flir": { slds: {} },
  "florist": { slds: {} },
  "flowers": { slds: {} },
  "flsmidth": { slds: {} },
  "fly": { slds: {} },
  "fm": { slds: { "radio.fm": {} } },
  "fo": {
    countryWeCanTrust: "fo",
    slds: {}
  },
  "foo": { slds: {} },
  "food": { slds: {} },
  "foodnetwork": { slds: {} },
  "football": { slds: {} },
  "ford": { slds: {} },
  "forex": { slds: {} },
  "forsale": { slds: {} },
  "forum": { slds: {} },
  "foundation": { slds: {} },
  "fox": { slds: {} },
  "fr": {
    countryWeCanTrust: "fr",
    slds: {
      "aeroport.fr": {},
      "asso.fr": {},
      "avocat.fr": {},
      "chambagri.fr": {},
      "chirurgiens-dentistes.fr": {},
      "com.fr": {},
      "experts-comptables.fr": {},
      "geometre-expert.fr": {},
      "gouv.fr": {},
      "medecin.fr": {},
      "nom.fr": {},
      "notaires.fr": {},
      "pharmacien.fr": {},
      "port.fr": {},
      "prd.fr": {},
      "presse.fr": {},
      "tm.fr": {},
      "veterinaire.fr": {}
    }
  },
  "free": { slds: {} },
  "fresenius": { slds: {} },
  "frl": { slds: {} },
  "frogans": { slds: {} },
  "frontdoor": { slds: {} },
  "frontier": { slds: {} },
  "ftr": { slds: {} },
  "fujitsu": { slds: {} },
  "fujixerox": { slds: {} },
  "fun": { slds: {} },
  "fund": { slds: {} },
  "fur": { slds: {} },
  "furniture": { slds: {} },
  "futbol": { slds: {} },
  "fyi": { slds: {} },
  "ga": {
    countryWeCanTrust: "ga",
    slds: {}
  },
  "gal": { slds: {} },
  "gallery": { slds: {} },
  "gallo": { slds: {} },
  "gallup": { slds: {} },
  "game": { slds: {} },
  "games": { slds: {} },
  "gap": { slds: {} },
  "garden": { slds: {} },
  "gay": { slds: {} },
  "gb": { slds: {} },
  "gbiz": { slds: {} },
  "gd": {
    countryWeCanTrust: "gd",
    slds: {}
  },
  "gdn": { slds: {} },
  "ge": {
    countryWeCanTrust: "ge",
    slds: {
      "com.ge": {},
      "edu.ge": {},
      "gov.ge": {},
      "mil.ge": {},
      "net.ge": {},
      "org.ge": {},
      "pvt.ge": {}
    }
  },
  "gea": { slds: {} },
  "gent": { slds: {} },
  "genting": { slds: {} },
  "george": { slds: {} },
  "gf": { slds: {} },
  "gg": {
    countryWeCanTrust: "gg",
    slds: {
      "co.gg": {},
      "net.gg": {},
      "org.gg": {}
    }
  },
  "ggee": { slds: {} },
  "gh": {
    countryWeCanTrust: "gh",
    slds: {
      "com.gh": {},
      "edu.gh": {},
      "gov.gh": {},
      "mil.gh": {},
      "org.gh": {}
    }
  },
  "gi": {
    countryWeCanTrust: "gi",
    slds: {
      "com.gi": {},
      "edu.gi": {},
      "gov.gi": {},
      "ltd.gi": {},
      "mod.gi": {},
      "org.gi": {}
    }
  },
  "gift": { slds: {} },
  "gifts": { slds: {} },
  "gives": { slds: {} },
  "giving": { slds: {} },
  "gl": {
    countryWeCanTrust: "gl",
    slds: {
      "co.gl": {},
      "com.gl": {},
      "edu.gl": {},
      "net.gl": {},
      "org.gl": {}
    }
  },
  "glass": { slds: {} },
  "gle": { slds: {} },
  "global": { slds: {} },
  "globo": { slds: {} },
  "gm": {
    countryWeCanTrust: "gm",
    slds: {}
  },
  "gmail": { slds: {} },
  "gmbh": { slds: {} },
  "gmo": { slds: {} },
  "gmx": { slds: {} },
  "gn": {
    countryWeCanTrust: "gn",
    slds: {
      "ac.gn": {},
      "com.gn": {},
      "gov.gn": {},
      "net.gn": {},
      "org.gn": {}
    }
  },
  "godaddy": { slds: {} },
  "gold": { slds: {} },
  "goldpoint": { slds: {} },
  "golf": { slds: {} },
  "goo": { slds: {} },
  "goodyear": { slds: {} },
  "goog": { slds: {} },
  "google": { slds: {} },
  "gop": { slds: {} },
  "got": { slds: {} },
  "gov": { slds: {} },
  "gov.to": {
    countryWeCanTrust: "to",
    slds: {}
  },
  "gp": {
    countryWeCanTrust: "gp",
    slds: {
      "asso.gp": {},
      "com.gp": {},
      "edu.gp": {},
      "mobi.gp": {},
      "net.gp": {},
      "org.gp": {}
    }
  },
  "gq": {
    countryWeCanTrust: "gq",
    slds: {}
  },
  "gr": {
    countryWeCanTrust: "gr",
    slds: {
      "com.gr": {},
      "edu.gr": {},
      "gov.gr": {},
      "net.gr": {},
      "org.gr": {}
    }
  },
  "grainger": { slds: {} },
  "graphics": { slds: {} },
  "gratis": { slds: {} },
  "green": { slds: {} },
  "gripe": { slds: {} },
  "grocery": { slds: {} },
  "group": { slds: {} },
  "gs": {
    countryWeCanTrust: "gs",
    slds: {}
  },
  "gt": {
    countryWeCanTrust: "gt",
    slds: {
      "com.gt": {},
      "ind.gt": {},
      "net.gt": {},
      "org.gt": {}
    }
  },
  "gu": { slds: { "com.gu": {} } },
  "guardian": { slds: {} },
  "gucci": { slds: {} },
  "guge": { slds: {} },
  "guide": { slds: {} },
  "guitars": { slds: {} },
  "guru": { slds: {} },
  "gw": {
    countryWeCanTrust: "gw",
    slds: {}
  },
  "gy": {
    countryWeCanTrust: "gy",
    slds: {
      "co.gy": {},
      "com.gy": {},
      "net.gy": {}
    }
  },
  "hair": { slds: {} },
  "hamburg": { slds: {} },
  "hangout": { slds: {} },
  "haus": { slds: {} },
  "hbo": { slds: {} },
  "hdfc": { slds: {} },
  "hdfcbank": { slds: {} },
  "health": { slds: {} },
  "healthcare": { slds: {} },
  "help": { slds: {} },
  "helsinki": { slds: {} },
  "here": { slds: {} },
  "hermes": { slds: {} },
  "hiphop": { slds: {} },
  "hisamitsu": { slds: {} },
  "hitachi": { slds: {} },
  "hiv": { slds: {} },
  "hk": {
    countryWeCanTrust: "hk",
    slds: {
      "com.hk": {},
      "edu.hk": {},
      "gov.hk": {},
      "idv.hk": {},
      "inc.hk": {},
      "ltd.hk": {},
      "net.hk": {},
      "org.hk": {},
      "\u516C\u53F8.hk": {}
    }
  },
  "hkt": { slds: {} },
  "hm": { slds: {} },
  "hn": {
    countryWeCanTrust: "hn",
    slds: {
      "com.hn": {},
      "edu.hn": {},
      "gob.hn": {},
      "mil.hn": {},
      "net.hn": {},
      "org.hn": {}
    }
  },
  "hockey": { slds: {} },
  "holdings": { slds: {} },
  "holiday": { slds: {} },
  "hom": { slds: {} },
  "home": { slds: {} },
  "homedepot": { slds: {} },
  "homegoods": { slds: {} },
  "homes": { slds: {} },
  "homesense": { slds: {} },
  "honda": { slds: {} },
  "honeywell": { slds: {} },
  "horse": { slds: {} },
  "hospital": { slds: {} },
  "host": { slds: {} },
  "hosting": { slds: {} },
  "hot": { slds: {} },
  "hotels": { slds: {} },
  "hotmail": { slds: {} },
  "house": { slds: {} },
  "how": { slds: {} },
  "hr": {
    countryWeCanTrust: "hr",
    slds: {
      "com.hr": {},
      "from.hr": {},
      "iz.hr": {},
      "name.hr": {}
    }
  },
  "hsbc": { slds: {} },
  "ht": {
    slds: {
      "adult.ht": {},
      "art.ht": {},
      "asso.ht": {},
      "com.ht": {},
      "edu.ht": {},
      "firm.ht": {},
      "info.ht": {},
      "net.ht": {},
      "org.ht": {},
      "perso.ht": {},
      "pol.ht": {},
      "pro.ht": {},
      "rel.ht": {},
      "shop.ht": {}
    }
  },
  "hu": {
    countryWeCanTrust: "hu",
    slds: {
      "2000.hu": {},
      "agrar.hu": {},
      "bolt.hu": {},
      "casino.hu": {},
      "city.hu": {},
      "co.hu": {},
      "erotica.hu": {},
      "erotika.hu": {},
      "film.hu": {},
      "forum.hu": {},
      "games.hu": {},
      "hotel.hu": {},
      "info.hu": {},
      "ingatlan.hu": {},
      "jogasz.hu": {},
      "konyvelo.hu": {},
      "lakas.hu": {},
      "media.hu": {},
      "news.hu": {},
      "org.hu": {},
      "priv.hu": {},
      "reklam.hu": {},
      "sex.hu": {},
      "shop.hu": {},
      "sport.hu": {},
      "suli.hu": {},
      "szex.hu": {},
      "tm.hu": {},
      "tozsde.hu": {},
      "utazas.hu": {},
      "video.hu": {}
    }
  },
  "hughes": { slds: {} },
  "hyatt": { slds: {} },
  "hyundai": { slds: {} },
  "ibm": { slds: {} },
  "icbc": { slds: {} },
  "ice": { slds: {} },
  "icu": { slds: {} },
  "id": {
    countryWeCanTrust: "id",
    slds: {
      "ac.id": {},
      "biz.id": {},
      "co.id": {},
      "go.id": {},
      "my.id": {},
      "or.id": {},
      "web.id": {}
    }
  },
  "ie": {
    countryWeCanTrust: "ie",
    slds: { "gov.ie": {} }
  },
  "ieee": { slds: {} },
  "ifm": { slds: {} },
  "ikano": { slds: {} },
  "il": {
    countryWeCanTrust: "il",
    slds: {
      "ac.il": {},
      "co.il": {},
      "gov.il": {},
      "idf.il": {},
      "k12.il": {},
      "muni.il": {},
      "net.il": {},
      "org.il": {}
    }
  },
  "im": {
    slds: {
      "ac.im": {},
      "co.im": {},
      "com.im": {},
      "gov.im": {},
      "ltd.co.im": {},
      "net.im": {},
      "nic.im": {},
      "org.im": {},
      "plc.co.im": {}
    }
  },
  "imamat": { slds: {} },
  "imdb": { slds: {} },
  "immo": { slds: {} },
  "immobilien": { slds: {} },
  "in": {
    countryWeCanTrust: "in",
    slds: {
      "5g.in": {},
      "6g.in": {},
      "ac.in": {},
      "ahmdabad.in": {},
      "ai.in": {},
      "am.in": {},
      "bihar.in": {},
      "biz.in": {},
      "business.in": {},
      "ca.in": {},
      "cn.in": {},
      "co.in": {},
      "com.in": {},
      "coop.in": {},
      "cs.in": {},
      "delhi.in": {},
      "dr.in": {},
      "edu.in": {},
      "er.in": {},
      "firm.in": {},
      "gen.in": {},
      "gov.in": {},
      "gujarat.in": {},
      "ind.in": {},
      "info.in": {},
      "int.in": {},
      "internet.in": {},
      "io.in": {},
      "me.in": {},
      "mil.in": {},
      "net.in": {},
      "nic.in": {},
      "org.in": {},
      "pg.in": {},
      "post.in": {},
      "pro.in": {},
      "res.in": {},
      "travel.in": {},
      "tv.in": {},
      "uk.in": {},
      "up.in": {},
      "us.in": {}
    }
  },
  "inc": { slds: {} },
  "industries": { slds: {} },
  "infiniti": { slds: {} },
  "info": { slds: { "auz.info": {} } },
  "ing": { slds: {} },
  "ink": { slds: {} },
  "institute": { slds: {} },
  "insurance": { slds: {} },
  "insure": { slds: {} },
  "int": { slds: {} },
  "intel": { slds: {} },
  "internal": { slds: {} },
  "international": { slds: {} },
  "intranet": { slds: {} },
  "intuit": { slds: {} },
  "invalid": { slds: {} },
  "investments": { slds: {} },
  "io": { slds: {} },
  "ipiranga": { slds: {} },
  "iq": {
    countryWeCanTrust: "iq",
    slds: { "com.iq": {} }
  },
  "ir": {
    countryWeCanTrust: "ir",
    slds: {
      "ac.ir": {},
      "co.ir": {},
      "gov.ir": {},
      "net.ir": {},
      "org.ir": {},
      "sch.ir": {}
    }
  },
  "irish": { slds: {} },
  "is": {
    countryWeCanTrust: "is",
    slds: {}
  },
  "iselect": { slds: {} },
  "ismaili": { slds: {} },
  "ist": { slds: {} },
  "istanbul": { slds: {} },
  "it": {
    countryWeCanTrust: "it",
    slds: {
      "abr.it": {},
      "abruzzo.it": {},
      "ag.it": {},
      "agrigento.it": {},
      "al.it": {},
      "alessandria.it": {},
      "alto-adige.it": {},
      "altoadige.it": {},
      "an.it": {},
      "ancona.it": {},
      "andria-barletta-trani.it": {},
      "andria-trani-barletta.it": {},
      "andriabarlettatrani.it": {},
      "andriatranibarletta.it": {},
      "ao.it": {},
      "aosta.it": {},
      "aoste.it": {},
      "ap.it": {},
      "aq.it": {},
      "aquila.it": {},
      "ar.it": {},
      "arezzo.it": {},
      "ascoli-piceno.it": {},
      "ascolipiceno.it": {},
      "asti.it": {},
      "at.it": {},
      "av.it": {},
      "avellino.it": {},
      "ba.it": {},
      "balsan.it": {},
      "bari.it": {},
      "barletta-trani-andria.it": {},
      "barlettatraniandria.it": {},
      "bas.it": {},
      "basilicata.it": {},
      "belluno.it": {},
      "benevento.it": {},
      "bergamo.it": {},
      "bg.it": {},
      "bi.it": {},
      "biella.it": {},
      "bl.it": {},
      "bn.it": {},
      "bo.it": {},
      "bologna.it": {},
      "bolzano.it": {},
      "bozen.it": {},
      "br.it": {},
      "brescia.it": {},
      "brindisi.it": {},
      "bs.it": {},
      "bt.it": {},
      "bz.it": {},
      "ca.it": {},
      "cagliari.it": {},
      "cal.it": {},
      "calabria.it": {},
      "caltanissetta.it": {},
      "cam.it": {},
      "campania.it": {},
      "campidano-medio.it": {},
      "campidanomedio.it": {},
      "campobasso.it": {},
      "carbonia-iglesias.it": {},
      "carboniaiglesias.it": {},
      "carrara-massa.it": {},
      "carraramassa.it": {},
      "caserta.it": {},
      "catania.it": {},
      "catanzaro.it": {},
      "cb.it": {},
      "ce.it": {},
      "cesena-forli.it": {},
      "cesenaforli.it": {},
      "ch.it": {},
      "chieti.it": {},
      "ci.it": {},
      "cl.it": {},
      "cn.it": {},
      "co.it": {},
      "como.it": {},
      "cosenza.it": {},
      "cr.it": {},
      "cremona.it": {},
      "crotone.it": {},
      "cs.it": {},
      "ct.it": {},
      "cuneo.it": {},
      "cz.it": {},
      "dell-ogliastra.it": {},
      "dellogliastra.it": {},
      "emilia-romagna.it": {},
      "emiliaromagna.it": {},
      "emr.it": {},
      "en.it": {},
      "enna.it": {},
      "fc.it": {},
      "fe.it": {},
      "fermo.it": {},
      "ferrara.it": {},
      "fg.it": {},
      "fi.it": {},
      "firenze.it": {},
      "florence.it": {},
      "fm.it": {},
      "foggia.it": {},
      "forli-cesena.it": {},
      "forlicesena.it": {},
      "fr.it": {},
      "friuli-v-giulia.it": {},
      "friuli-ve-giulia.it": {},
      "friuli-vegiulia.it": {},
      "friuli-venezia-giulia.it": {},
      "friuli-veneziagiulia.it": {},
      "friuli-vgiulia.it": {},
      "friuliv-giulia.it": {},
      "friulive-giulia.it": {},
      "friulivegiulia.it": {},
      "friulivenezia-giulia.it": {},
      "friuliveneziagiulia.it": {},
      "friulivgiulia.it": {},
      "frosinone.it": {},
      "fvg.it": {},
      "ge.it": {},
      "genoa.it": {},
      "genova.it": {},
      "go.it": {},
      "gorizia.it": {},
      "gov.it": {},
      "gr.it": {},
      "grosseto.it": {},
      "iglesias-carbonia.it": {},
      "iglesiascarbonia.it": {},
      "im.it": {},
      "imperia.it": {},
      "is.it": {},
      "isernia.it": {},
      "kr.it": {},
      "la-spezia.it": {},
      "laquila.it": {},
      "laspezia.it": {},
      "latina.it": {},
      "laz.it": {},
      "lazio.it": {},
      "lc.it": {},
      "le.it": {},
      "lecce.it": {},
      "lecco.it": {},
      "li.it": {},
      "lig.it": {},
      "liguria.it": {},
      "livorno.it": {},
      "lo.it": {},
      "lodi.it": {},
      "lom.it": {},
      "lombardia.it": {},
      "lombardy.it": {},
      "lt.it": {},
      "lu.it": {},
      "lucania.it": {},
      "lucca.it": {},
      "macerata.it": {},
      "mantova.it": {},
      "mar.it": {},
      "marche.it": {},
      "massa-carrara.it": {},
      "massacarrara.it": {},
      "matera.it": {},
      "mb.it": {},
      "mc.it": {},
      "me.it": {},
      "medio-campidano.it": {},
      "mediocampidano.it": {},
      "messina.it": {},
      "mi.it": {},
      "milan.it": {},
      "milano.it": {},
      "mn.it": {},
      "mo.it": {},
      "modena.it": {},
      "mol.it": {},
      "molise.it": {},
      "monza.it": {},
      "monza-brianza.it": {},
      "monza-e-della-brianza.it": {},
      "monzabrianza.it": {},
      "monzaebrianza.it": {},
      "monzaedellabrianza.it": {},
      "ms.it": {},
      "mt.it": {},
      "na.it": {},
      "naples.it": {},
      "napoli.it": {},
      "no.it": {},
      "novara.it": {},
      "nu.it": {},
      "nuoro.it": {},
      "og.it": {},
      "ogliastra.it": {},
      "olbia-tempio.it": {},
      "olbiatempio.it": {},
      "or.it": {},
      "oristano.it": {},
      "ot.it": {},
      "pa.it": {},
      "padova.it": {},
      "padua.it": {},
      "palermo.it": {},
      "parma.it": {},
      "pavia.it": {},
      "pc.it": {},
      "pd.it": {},
      "pe.it": {},
      "perugia.it": {},
      "pesaro-urbino.it": {},
      "pesarourbino.it": {},
      "pescara.it": {},
      "pg.it": {},
      "pi.it": {},
      "piacenza.it": {},
      "piedmont.it": {},
      "piemonte.it": {},
      "pisa.it": {},
      "pistoia.it": {},
      "pmn.it": {},
      "pn.it": {},
      "po.it": {},
      "pordenone.it": {},
      "potenza.it": {},
      "pr.it": {},
      "prato.it": {},
      "pt.it": {},
      "pu.it": {},
      "pug.it": {},
      "puglia.it": {},
      "pv.it": {},
      "pz.it": {},
      "ra.it": {},
      "ragusa.it": {},
      "ravenna.it": {},
      "rc.it": {},
      "re.it": {},
      "reggio-calabria.it": {},
      "reggio-emilia.it": {},
      "reggiocalabria.it": {},
      "reggioemilia.it": {},
      "rg.it": {},
      "ri.it": {},
      "rieti.it": {},
      "rimini.it": {},
      "rm.it": {},
      "rn.it": {},
      "ro.it": {},
      "roma.it": {},
      "rome.it": {},
      "rovigo.it": {},
      "sa.it": {},
      "salerno.it": {},
      "sar.it": {},
      "sardegna.it": {},
      "sardinia.it": {},
      "sassari.it": {},
      "savona.it": {},
      "si.it": {},
      "sic.it": {},
      "sicilia.it": {},
      "sicily.it": {},
      "siena.it": {},
      "siracusa.it": {},
      "so.it": {},
      "sondrio.it": {},
      "sp.it": {},
      "sr.it": {},
      "ss.it": {},
      "suedtirol.it": {},
      "sv.it": {},
      "ta.it": {},
      "taa.it": {},
      "taranto.it": {},
      "te.it": {},
      "tempio-olbia.it": {},
      "tempioolbia.it": {},
      "teramo.it": {},
      "terni.it": {},
      "tn.it": {},
      "to.it": {},
      "torino.it": {},
      "tos.it": {},
      "toscana.it": {},
      "tp.it": {},
      "tr.it": {},
      "trani-andria-barletta.it": {},
      "trani-barletta-andria.it": {},
      "traniandriabarletta.it": {},
      "tranibarlettaandria.it": {},
      "trapani.it": {},
      "trentino.it": {},
      "trentino-a-adige.it": {},
      "trentino-aadige.it": {},
      "trentino-alto-adige.it": {},
      "trentino-altoadige.it": {},
      "trentino-s-tirol.it": {},
      "trentino-stirol.it": {},
      "trentino-sud-tirol.it": {},
      "trentino-sudtirol.it": {},
      "trentino-sued-tirol.it": {},
      "trentino-suedtirol.it": {},
      "trentinoa-adige.it": {},
      "trentinoaadige.it": {},
      "trentinoalto-adige.it": {},
      "trentinoaltoadige.it": {},
      "trentinos-tirol.it": {},
      "trentinosud-tirol.it": {},
      "trentinosudtirol.it": {},
      "trentinosued-tirol.it": {},
      "trentinosuedtirol.it": {},
      "trento.it": {},
      "treviso.it": {},
      "trieste.it": {},
      "ts.it": {},
      "turin.it": {},
      "tuscany.it": {},
      "tv.it": {},
      "ud.it": {},
      "udine.it": {},
      "umb.it": {},
      "umbria.it": {},
      "urbino-pesaro.it": {},
      "urbinopesaro.it": {},
      "va.it": {},
      "val-d-aosta.it": {},
      "val-daosta.it": {},
      "vald-aosta.it": {},
      "valdaosta.it": {},
      "valle-d-aosta.it": {},
      "valle-daosta.it": {},
      "valled-aosta.it": {},
      "valledaosta.it": {},
      "vao.it": {},
      "varese.it": {},
      "vb.it": {},
      "vc.it": {},
      "vda.it": {},
      "ve.it": {},
      "ven.it": {},
      "veneto.it": {},
      "venezia.it": {},
      "venice.it": {},
      "verbania.it": {},
      "vercelli.it": {},
      "verona.it": {},
      "vi.it": {},
      "vibo-valentia.it": {},
      "vibovalentia.it": {},
      "vicenza.it": {},
      "viterbo.it": {},
      "vr.it": {},
      "vs.it": {},
      "vt.it": {},
      "vv.it": {}
    }
  },
  "itau": { slds: {} },
  "itv": { slds: {} },
  "iveco": { slds: {} },
  "jaguar": { slds: {} },
  "java": { slds: {} },
  "jcb": { slds: {} },
  "jcp": { slds: {} },
  "je": {
    countryWeCanTrust: "je",
    slds: {
      "co.je": {},
      "net.je": {},
      "org.je": {}
    }
  },
  "jeep": { slds: {} },
  "jetzt": { slds: {} },
  "jewelry": { slds: {} },
  "jio": { slds: {} },
  "jll": { slds: {} },
  "jm": {
    countryWeCanTrust: "jm",
    slds: {
      "com.jm": {},
      "edu.jm": {},
      "gov.jm": {},
      "net.jm": {},
      "org.jm": {}
    }
  },
  "jmp": { slds: {} },
  "jnj": { slds: {} },
  "jo": {
    countryWeCanTrust: "jo",
    slds: {
      "com.jo": {},
      "edu.jo": {},
      "gov.jo": {},
      "mil.jo": {},
      "name.jo": {},
      "net.jo": {},
      "org.jo": {},
      "sch.jo": {}
    }
  },
  "jobs": { slds: {} },
  "joburg": { slds: {} },
  "jot": { slds: {} },
  "joy": { slds: {} },
  "jp": {
    countryWeCanTrust: "jp",
    slds: {
      "ac.jp": {},
      "ad.jp": {},
      "aichi.jp": {},
      "akita.jp": {},
      "aomori.jp": {},
      "chiba.jp": {},
      "co.jp": {},
      "ed.jp": {},
      "ehime.jp": {},
      "fukui.jp": {},
      "fukuoka.jp": {},
      "fukushima.jp": {},
      "gifu.jp": {},
      "go.jp": {},
      "gr.jp": {},
      "gunma.jp": {},
      "hiroshima.jp": {},
      "hokkaido.jp": {},
      "hyogo.jp": {},
      "ibaraki.jp": {},
      "ishikawa.jp": {},
      "iwate.jp": {},
      "kagawa.jp": {},
      "kagoshima.jp": {},
      "kanagawa.jp": {},
      "kawasaki.jp": {},
      "kitakyushu.jp": {},
      "kobe.jp": {},
      "kochi.jp": {},
      "kumamoto.jp": {},
      "kyoto.jp": {},
      "lg.jp": {},
      "mie.jp": {},
      "miyagi.jp": {},
      "miyazaki.jp": {},
      "nagano.jp": {},
      "nagasaki.jp": {},
      "nagoya.jp": {},
      "nara.jp": {},
      "ne.jp": {},
      "niigata.jp": {},
      "oita.jp": {},
      "okayama.jp": {},
      "okinawa.jp": {},
      "or.jp": {},
      "osaka.jp": {},
      "saga.jp": {},
      "saitama.jp": {},
      "sapporo.jp": {},
      "sendai.jp": {},
      "shiga.jp": {},
      "shimane.jp": {},
      "shizuoka.jp": {},
      "tochigi.jp": {},
      "tokushima.jp": {},
      "tokyo.jp": {},
      "tottori.jp": {},
      "toyama.jp": {},
      "wakayama.jp": {},
      "yamagata.jp": {},
      "yamaguchi.jp": {},
      "yamanashi.jp": {},
      "yokohama.jp": {}
    }
  },
  "jpmorgan": { slds: {} },
  "jprs": { slds: {} },
  "juegos": { slds: {} },
  "juniper": { slds: {} },
  "kaufen": { slds: {} },
  "kddi": { slds: {} },
  "ke": {
    countryWeCanTrust: "ke",
    slds: {
      "ac.ke": {},
      "co.ke": {},
      "go.ke": {},
      "info.ke": {},
      "me.ke": {},
      "mobi.ke": {},
      "ne.ke": {},
      "or.ke": {},
      "sc.ke": {}
    }
  },
  "kerryhotels": { slds: {} },
  "kerrylogistics": { slds: {} },
  "kerryproperties": { slds: {} },
  "kfh": { slds: {} },
  "kg": {
    countryWeCanTrust: "kg",
    slds: {
      "com.kg": {},
      "net.kg": {},
      "org.kg": {}
    }
  },
  "kh": {
    countryWeCanTrust: "kh",
    slds: {
      "com.kh": {},
      "edu.kh": {},
      "gov.kh": {},
      "mil.kh": {},
      "net.kh": {},
      "org.kh": {},
      "per.kh": {}
    }
  },
  "ki": {
    countryWeCanTrust: "ki",
    slds: {
      "biz.ki": {},
      "com.ki": {},
      "edu.ki": {},
      "gov.ki": {},
      "info.ki": {},
      "mobi.ki": {},
      "net.ki": {},
      "org.ki": {},
      "phone.ki": {},
      "tel.ki": {}
    }
  },
  "kia": { slds: {} },
  "kids": { slds: {} },
  "kim": { slds: {} },
  "kinder": { slds: {} },
  "kindle": { slds: {} },
  "kitchen": { slds: {} },
  "kiwi": { slds: {} },
  "km": {
    countryWeCanTrust: "km",
    slds: {
      "com.km": {},
      "nom.km": {},
      "org.km": {},
      "tm.km": {}
    }
  },
  "kn": {
    countryWeCanTrust: "kn",
    slds: {
      "com.kn": {},
      "edu.kn": {},
      "gov.kn": {}
    }
  },
  "koeln": { slds: {} },
  "komatsu": { slds: {} },
  "kosher": { slds: {} },
  "kp": {
    countryWeCanTrust: "kp",
    slds: {}
  },
  "kpmg": { slds: {} },
  "kpn": { slds: {} },
  "kr": {
    countryWeCanTrust: "kr",
    slds: {
      "co.kr": {},
      "go.kr": {},
      "ms.kr": {},
      "ne.kr": {},
      "or.kr": {},
      "pe.kr": {},
      "re.kr": {},
      "seoul.kr": {}
    }
  },
  "krd": { slds: {} },
  "kred": { slds: {} },
  "ku": { slds: {} },
  "kuokgroup": { slds: {} },
  "kw": {
    countryWeCanTrust: "kw",
    slds: {
      "com.kw": {},
      "edu.kw": {},
      "gov.kw": {},
      "mil.kw": {},
      "net.kw": {},
      "org.kw": {}
    }
  },
  "ky": {
    countryWeCanTrust: "ky",
    slds: {
      "com.ky": {},
      "edu.ky": {},
      "gov.ky": {},
      "net.ky": {},
      "org.ky": {}
    }
  },
  "kyoto": { slds: {} },
  "kz": {
    countryWeCanTrust: "kz",
    slds: {
      "com.kz": {},
      "edu.kz": {},
      "gov.kz": {},
      "mil.kz": {},
      "net.kz": {},
      "org.kz": {}
    }
  },
  "la": { slds: {} },
  "lacaixa": { slds: {} },
  "ladbrokes": { slds: {} },
  "lamborghini": { slds: {} },
  "lamer": { slds: {} },
  "lan": { slds: {} },
  "lancaster": { slds: {} },
  "lancia": { slds: {} },
  "lancome": { slds: {} },
  "land": { slds: {} },
  "landrover": { slds: {} },
  "lanxess": { slds: {} },
  "lasalle": { slds: {} },
  "lat": { slds: {} },
  "latino": { slds: {} },
  "latrobe": { slds: {} },
  "law": { slds: {} },
  "lawyer": { slds: {} },
  "lb": {
    countryWeCanTrust: "lb",
    slds: {
      "com.lb": {},
      "edu.lb": {},
      "gov.lb": {},
      "net.lb": {},
      "org.lb": {}
    }
  },
  "lc": {
    countryWeCanTrust: "lc",
    slds: {
      "co.lc": {},
      "com.lc": {},
      "edu.lc": {},
      "gov.lc": {},
      "l.lc": {},
      "net.lc": {},
      "org.lc": {},
      "p.lc": {}
    }
  },
  "lds": { slds: {} },
  "lease": { slds: {} },
  "leclerc": { slds: {} },
  "lefrak": { slds: {} },
  "legal": { slds: {} },
  "lego": { slds: {} },
  "lexus": { slds: {} },
  "lgbt": { slds: {} },
  "li": {
    countryWeCanTrust: "li",
    slds: {
      "com.li": {},
      "gov.li": {},
      "net.li": {},
      "org.li": {}
    }
  },
  "liaison": { slds: {} },
  "lib": { slds: {} },
  "lidl": { slds: {} },
  "life": { slds: {} },
  "lifeinsurance": { slds: {} },
  "lifestyle": { slds: {} },
  "lighting": { slds: {} },
  "like": { slds: {} },
  "lilly": { slds: {} },
  "limited": { slds: {} },
  "limo": { slds: {} },
  "lincoln": { slds: {} },
  "linde": { slds: {} },
  "link": { slds: {} },
  "lipsy": { slds: {} },
  "live": { slds: {} },
  "living": { slds: {} },
  "lixil": { slds: {} },
  "lk": {
    countryWeCanTrust: "lk",
    slds: {
      "assn.lk": {},
      "com.lk": {},
      "edu.lk": {},
      "gov.lk": {},
      "grp.lk": {},
      "hotel.lk": {},
      "int.lk": {},
      "ltd.lk": {},
      "net.lk": {},
      "ngo.lk": {},
      "org.lk": {},
      "sch.lk": {},
      "soc.lk": {},
      "web.lk": {}
    }
  },
  "llc": { slds: {} },
  "llp": { slds: {} },
  "loan": { slds: {} },
  "loans": { slds: {} },
  "local": { slds: {} },
  "localhost": { slds: {} },
  "locker": { slds: {} },
  "locus": { slds: {} },
  "lol": { slds: {} },
  "london": { slds: {} },
  "lotte": { slds: {} },
  "lotto": { slds: {} },
  "love": { slds: {} },
  "lpl": { slds: {} },
  "lplfinancial": { slds: {} },
  "lr": {
    countryWeCanTrust: "lr",
    slds: {
      "com.lr": {},
      "edu.lr": {},
      "gov.lr": {},
      "net.lr": {},
      "org.lr": {}
    }
  },
  "ls": {
    countryWeCanTrust: "ls",
    slds: {
      "co.ls": {},
      "net.ls": {},
      "org.ls": {}
    }
  },
  "lt": {
    countryWeCanTrust: "lt",
    slds: {
      "gov.lt": {},
      "mil.lt": {}
    }
  },
  "ltd": { slds: {} },
  "ltda": { slds: {} },
  "lu": {
    countryWeCanTrust: "lu",
    slds: {
      "gov.lu": {},
      "mil.lu": {},
      "net.lu": {},
      "org.lu": {}
    }
  },
  "lundbeck": { slds: {} },
  "lupin": { slds: {} },
  "luxe": { slds: {} },
  "luxury": { slds: {} },
  "lv": {
    countryWeCanTrust: "lv",
    slds: {
      "asn.lv": {},
      "com.lv": {},
      "conf.lv": {},
      "edu.lv": {},
      "gov.lv": {},
      "id.lv": {},
      "mil.lv": {},
      "net.lv": {},
      "org.lv": {}
    }
  },
  "ly": {
    slds: {
      "com.ly": {},
      "edu.ly": {},
      "gov.ly": {},
      "id.ly": {},
      "med.ly": {},
      "net.ly": {},
      "org.ly": {},
      "plc.ly": {},
      "sch.ly": {}
    }
  },
  "ma": {
    countryWeCanTrust: "ma",
    slds: {
      "ac.ma": {},
      "co.ma": {},
      "gov.ma": {},
      "net.ma": {},
      "org.ma": {},
      "press.ma": {}
    }
  },
  "macys": { slds: {} },
  "madrid": { slds: {} },
  "maif": { slds: {} },
  "maison": { slds: {} },
  "makeup": { slds: {} },
  "man": { slds: {} },
  "management": { slds: {} },
  "mango": { slds: {} },
  "map": { slds: {} },
  "market": { slds: {} },
  "marketing": { slds: {} },
  "markets": { slds: {} },
  "marriott": { slds: {} },
  "marshalls": { slds: {} },
  "maserati": { slds: {} },
  "mattel": { slds: {} },
  "mba": { slds: {} },
  "mc": {
    countryWeCanTrust: "mc",
    slds: {
      "asso.mc": {},
      "tm.mc": {}
    }
  },
  "mckinsey": { slds: {} },
  "md": {
    countryWeCanTrust: "md",
    slds: {}
  },
  "me": { slds: {} },
  "med": { slds: {} },
  "media": { slds: {} },
  "meet": { slds: {} },
  "melbourne": { slds: {} },
  "meme": { slds: {} },
  "memorial": { slds: {} },
  "men": { slds: {} },
  "menu": { slds: {} },
  "merckmsd": { slds: {} },
  "metlife": { slds: {} },
  "mg": {
    countryWeCanTrust: "mg",
    slds: {
      "co.mg": {},
      "com.mg": {},
      "edu.mg": {},
      "gov.mg": {},
      "mil.mg": {},
      "net.mg": {},
      "nom.mg": {},
      "org.mg": {},
      "prd.mg": {},
      "tm.mg": {}
    }
  },
  "mh": {
    countryWeCanTrust: "mh",
    slds: {}
  },
  "miami": { slds: {} },
  "microsoft": { slds: {} },
  "mil": { slds: {} },
  "mini": { slds: {} },
  "mint": { slds: {} },
  "mit": { slds: {} },
  "mitsubishi": { slds: {} },
  "mk": {
    countryWeCanTrust: "mk",
    slds: {
      "com.mk": {},
      "edu.mk": {},
      "inf.mk": {},
      "net.mk": {},
      "org.mk": {}
    }
  },
  "ml": {
    countryWeCanTrust: "ml",
    slds: {
      "com.ml": {},
      "info.ml": {},
      "net.ml": {},
      "org.ml": {}
    }
  },
  "mlb": { slds: {} },
  "mls": { slds: {} },
  "mm": {
    countryWeCanTrust: "mm",
    slds: {
      "biz.mm": {},
      "com.mm": {},
      "net.mm": {},
      "org.mm": {},
      "per.mm": {}
    }
  },
  "mma": { slds: {} },
  "mn": {
    countryWeCanTrust: "mn",
    slds: { "gov.mn": {} }
  },
  "mo": {
    countryWeCanTrust: "mo",
    slds: {
      "com.mo": {},
      "edu.mo": {},
      "gov.mo": {},
      "net.mo": {},
      "org.mo": {}
    }
  },
  "mobi": { slds: {} },
  "mobile": { slds: {} },
  "mobily": { slds: {} },
  "moda": { slds: {} },
  "moe": { slds: {} },
  "moi": { slds: {} },
  "mom": { slds: {} },
  "monash": { slds: {} },
  "money": { slds: {} },
  "monster": { slds: {} },
  "mormon": { slds: {} },
  "mortgage": { slds: {} },
  "moscow": { slds: {} },
  "moto": { slds: {} },
  "motorcycles": { slds: {} },
  "mov": { slds: {} },
  "movie": { slds: {} },
  "movistar": { slds: {} },
  "mp": {
    countryWeCanTrust: "mp",
    slds: {}
  },
  "mq": {
    countryWeCanTrust: "mq",
    slds: {}
  },
  "mr": {
    slds: {
      "edu.mr": {},
      "org.mr": {},
      "perso.mr": {}
    }
  },
  "ms": {
    slds: {
      "co.ms": {},
      "com.ms": {},
      "org.ms": {}
    }
  },
  "msd": { slds: {} },
  "mt": {
    countryWeCanTrust: "mt",
    slds: {
      "com.mt": {},
      "edu.mt": {},
      "gov.mt": {},
      "net.mt": {},
      "org.mt": {}
    }
  },
  "mtn": { slds: {} },
  "mtr": { slds: {} },
  "mu": {
    countryWeCanTrust: "mu",
    slds: {
      "ac.mu": {},
      "co.mu": {},
      "com.mu": {},
      "net.mu": {},
      "nom.mu": {},
      "or.mu": {},
      "org.mu": {}
    }
  },
  "museum": { slds: {} },
  "music": { slds: {} },
  "mutual": { slds: {} },
  "mv": {
    countryWeCanTrust: "mv",
    slds: {
      "aero.mv": {},
      "biz.mv": {},
      "com.mv": {},
      "coop.mv": {},
      "edu.mv": {},
      "gov.mv": {},
      "info.mv": {},
      "int.mv": {},
      "mil.mv": {},
      "museum.mv": {},
      "name.mv": {},
      "net.mv": {},
      "org.mv": {},
      "pro.mv": {}
    }
  },
  "mw": {
    countryWeCanTrust: "mw",
    slds: {
      "ac.mw": {},
      "co.mw": {},
      "com.mw": {},
      "coop.mw": {},
      "edu.mw": {},
      "gov.mw": {},
      "int.mw": {},
      "museum.mw": {},
      "net.mw": {},
      "org.mw": {}
    }
  },
  "mx": {
    countryWeCanTrust: "mx",
    slds: {
      "com.mx": {},
      "edu.mx": {},
      "gob.mx": {},
      "net.mx": {},
      "org.mx": {}
    }
  },
  "my": {
    countryWeCanTrust: "my",
    slds: {
      "com.my": { countryWeCanTrust: "my" },
      "edu.my": { countryWeCanTrust: "my" },
      "gov.my": { countryWeCanTrust: "my" },
      "mil.my": { countryWeCanTrust: "my" },
      "name.my": { countryWeCanTrust: "my" },
      "net.my": { countryWeCanTrust: "my" },
      "org.my": { countryWeCanTrust: "my" }
    }
  },
  "mz": {
    countryWeCanTrust: "mz",
    slds: {
      "co.mz": {},
      "edu.mz": {},
      "net.mz": {},
      "org.mz": {}
    }
  },
  "na": {
    countryWeCanTrust: "na",
    slds: {
      "alt.na": {},
      "cc.na": {},
      "co.na": {},
      "com.na": {},
      "edu.na": {},
      "info.na": {},
      "net.na": {},
      "org.na": {},
      "school.na": {}
    }
  },
  "nab": { slds: {} },
  "nadex": { slds: {} },
  "nagoya": { slds: {} },
  "name": { slds: {} },
  "nationwide": { slds: {} },
  "natura": { slds: {} },
  "navy": { slds: {} },
  "nba": { slds: {} },
  "nc": {
    countryWeCanTrust: "nc",
    slds: {}
  },
  "ne": {
    countryWeCanTrust: "ne",
    slds: {
      "com.ne": {},
      "info.ne": {},
      "int.ne": {},
      "org.ne": {},
      "perso.ne": {}
    }
  },
  "nec": { slds: {} },
  "net": {
    slds: {
      "auz.net": {},
      "gb.net": {},
      "hu.net": {},
      "in.net": {},
      "jp.net": {},
      "ru.net": {},
      "se.net": {},
      "uk.net": {}
    }
  },
  "netbank": { slds: {} },
  "netflix": { slds: {} },
  "network": { slds: {} },
  "neustar": { slds: {} },
  "new": { slds: {} },
  "newholland": { slds: {} },
  "news": { slds: {} },
  "next": { slds: {} },
  "nextdirect": { slds: {} },
  "nexus": { slds: {} },
  "nf": {
    countryWeCanTrust: "nf",
    slds: {
      "arts.nf": {},
      "com.nf": {},
      "firm.nf": {},
      "info.nf": {},
      "net.nf": {},
      "org.nf": {},
      "other.nf": {},
      "per.nf": {},
      "rec.nf": {},
      "store.nf": {},
      "web.nf": {}
    }
  },
  "nfl": { slds: {} },
  "ng": {
    countryWeCanTrust: "ng",
    slds: {
      "com.ng": {},
      "edu.ng": {},
      "gov.ng": {},
      "i.ng": {},
      "lg.gov.ng": {},
      "mobi.ng": {},
      "name.ng": {},
      "net.ng": {},
      "org.ng": {},
      "sch.ng": {}
    }
  },
  "ngo": { slds: {} },
  "nhk": { slds: {} },
  "ni": {
    countryWeCanTrust: "ni",
    slds: {
      "ac.ni": {},
      "biz.ni": {},
      "co.ni": {},
      "com.ni": {},
      "edu.ni": {},
      "gob.ni": {},
      "in.ni": {},
      "info.ni": {},
      "int.ni": {},
      "mil.ni": {},
      "net.ni": {},
      "nom.ni": {},
      "org.ni": {},
      "web.ni": {}
    }
  },
  "nico": { slds: {} },
  "nike": { slds: {} },
  "nikon": { slds: {} },
  "ninja": { slds: {} },
  "nissan": { slds: {} },
  "nissay": { slds: {} },
  "nl": {
    countryWeCanTrust: "nl",
    slds: {
      "co.nl": {},
      "com.nl": {},
      "net.nl": {}
    }
  },
  "no": {
    countryWeCanTrust: "no",
    slds: {
      "co.no": {},
      "fhs.no": {},
      "folkebibl.no": {},
      "fylkesbibl.no": {},
      "gs.no": {},
      "herad.no": {},
      "idrett.no": {},
      "kommune.no": {},
      "mil.no": {},
      "museum.no": {},
      "priv.no": {},
      "stat.no": {},
      "uenorge.no": {},
      "vgs.no": {}
    }
  },
  "nokia": { slds: {} },
  "northwesternmutual": { slds: {} },
  "norton": { slds: {} },
  "now": { slds: {} },
  "nowruz": { slds: {} },
  "nowtv": { slds: {} },
  "np": {
    countryWeCanTrust: "np",
    slds: {
      "aero.np": {},
      "asia.np": {},
      "biz.np": {},
      "com.np": {},
      "coop.np": {},
      "edu.np": {},
      "gov.np": {},
      "info.np": {},
      "mil.np": {},
      "mobi.np": {},
      "name.np": {},
      "net.np": {},
      "org.np": {},
      "pro.np": {},
      "travel.np": {}
    }
  },
  "nr": {
    countryWeCanTrust: "nr",
    slds: {
      "biz.nr": {},
      "com.nr": {},
      "edu.nr": {},
      "gov.nr": {},
      "info.nr": {},
      "net.nr": {},
      "org.nr": {}
    }
  },
  "nra": { slds: {} },
  "nrw": { slds: {} },
  "ntt": { slds: {} },
  "nu": {
    countryWeCanTrust: "nu",
    slds: { "co.nu": {} }
  },
  "nyc": { slds: {} },
  "nz": {
    countryWeCanTrust: "nz",
    slds: {
      "ac.nz": {},
      "co.net.nz": {},
      "co.nz": {},
      "cri.nz": {},
      "geek.nz": {},
      "gen.nz": {},
      "govt.nz": {},
      "health.nz": {},
      "iwi.nz": {},
      "kiwi.nz": {},
      "maori.nz": {},
      "mil.nz": {},
      "net.nz": {},
      "org.nz": {},
      "school.nz": {}
    }
  },
  "obi": { slds: {} },
  "observer": { slds: {} },
  "office": { slds: {} },
  "okinawa": { slds: {} },
  "olayan": { slds: {} },
  "olayangroup": { slds: {} },
  "ollo": { slds: {} },
  "om": {
    countryWeCanTrust: "om",
    slds: {
      "ac.com": {},
      "biz.om": {},
      "co.om": {},
      "com.om": {},
      "edu.om": {},
      "gov.om": {},
      "med.om": {},
      "mil.om": {},
      "museum.om": {},
      "net.om": {},
      "org.om": {},
      "pro.om": {},
      "sch.om": {}
    }
  },
  "omega": { slds: {} },
  "one": { slds: {} },
  "ong": { slds: {} },
  "onion": { slds: {} },
  "onl": { slds: {} },
  "online": { slds: {} },
  "ooo": { slds: {} },
  "open": { slds: {} },
  "oracle": { slds: {} },
  "orange": { slds: {} },
  "org": {
    slds: {
      "ae.org": {},
      "hk.org": {},
      "us.org": {}
    }
  },
  "organic": { slds: {} },
  "origins": { slds: {} },
  "osaka": { slds: {} },
  "otsuka": { slds: {} },
  "ott": { slds: {} },
  "ovh": { slds: {} },
  "pa": {
    countryWeCanTrust: "pa",
    slds: {
      "abo.pa": {},
      "ac.pa": {},
      "com.pa": {},
      "edu.pa": {},
      "gob.pa": {},
      "ing.pa": {},
      "med.pa": {},
      "net.pa": {},
      "nom.pa": {},
      "org.pa": {},
      "sld.pa": {}
    }
  },
  "page": { slds: {} },
  "panasonic": { slds: {} },
  "paris": { slds: {} },
  "pars": { slds: {} },
  "partners": { slds: {} },
  "parts": { slds: {} },
  "party": { slds: {} },
  "pay": { slds: {} },
  "pccw": { slds: {} },
  "pe": {
    countryWeCanTrust: "pe",
    slds: {
      "com.pe": {},
      "edu.pe": {},
      "gob.pe": {},
      "mil.pe": {},
      "net.pe": {},
      "nom.pe": {},
      "org.pe": {}
    }
  },
  "pet": { slds: {} },
  "pf": {
    countryWeCanTrust: "pf",
    slds: {
      "asso.pf": {},
      "com.pf": {},
      "edu.pf": {},
      "gov.pf": {},
      "org.pf": {}
    }
  },
  "pfizer": { slds: {} },
  "pg": {
    countryWeCanTrust: "pg",
    slds: {
      "com.pg": {},
      "net.pg": {},
      "org.pg": {}
    }
  },
  "ph": {
    countryWeCanTrust: "ph",
    slds: {
      "com.ph": {},
      "gov.ph": {},
      "net.ph": {},
      "org.ph": {}
    }
  },
  "pharmacy": { slds: {} },
  "phd": { slds: {} },
  "philips": { slds: {} },
  "phone": { slds: {} },
  "photo": { slds: {} },
  "photography": { slds: {} },
  "photos": { slds: {} },
  "physio": { slds: {} },
  "piaget": { slds: {} },
  "pics": { slds: {} },
  "pictet": { slds: {} },
  "pictures": { slds: {} },
  "pid": { slds: {} },
  "pin": { slds: {} },
  "ping": { slds: {} },
  "pink": { slds: {} },
  "pioneer": { slds: {} },
  "pizza": { slds: {} },
  "pk": {
    countryWeCanTrust: "pk",
    slds: {
      "biz.pk": {},
      "com.pk": {},
      "edu.pk": {},
      "fam.pk": {},
      "gob.pk": {},
      "gok.pk": {},
      "gon.pk": {},
      "gop.pk": {},
      "gos.pk": {},
      "gov.pk": {},
      "net.pk": {},
      "org.pk": {},
      "web.pk": {}
    }
  },
  "pl": {
    countryWeCanTrust: "pl",
    slds: {
      "agro.pl": {},
      "aid.pl": {},
      "art.pl": {},
      "atm.pl": {},
      "augustow.pl": {},
      "auto.pl": {},
      "babia-gora.pl": {},
      "bedzin.pl": {},
      "beskidy.pl": {},
      "bialowieza.pl": {},
      "bialystok.pl": {},
      "bielawa.pl": {},
      "bieszczady.pl": {},
      "biz.pl": {},
      "boleslawiec.pl": {},
      "bydgoszcz.pl": {},
      "bytom.pl": {},
      "cieszyn.pl": {},
      "com.pl": {},
      "czeladz.pl": {},
      "czest.pl": {},
      "dlugoleka.pl": {},
      "edu.pl": {},
      "elblag.pl": {},
      "elk.pl": {},
      "gda.pl": {},
      "gdansk.pl": {},
      "glogow.pl": {},
      "gmina.pl": {},
      "gniezno.pl": {},
      "gorlice.pl": {},
      "gov.pl": {},
      "grajewo.pl": {},
      "gsm.pl": {},
      "ilawa.pl": {},
      "info.pl": {},
      "jaworzno.pl": {},
      "jelenia-gora.pl": {},
      "jgora.pl": {},
      "kalisz.pl": {},
      "karpacz.pl": {},
      "kartuzy.pl": {},
      "kaszuby.pl": {},
      "katowice.pl": {},
      "kazimierz-dolny.pl": {},
      "kepno.pl": {},
      "ketrzyn.pl": {},
      "klodzko.pl": {},
      "kobierzyce.pl": {},
      "kolobrzeg.pl": {},
      "konin.pl": {},
      "konskowola.pl": {},
      "krakow.pl": {},
      "kutno.pl": {},
      "lapy.pl": {},
      "lebork.pl": {},
      "legnica.pl": {},
      "lezajsk.pl": {},
      "limanowa.pl": {},
      "lodz.pl": {},
      "lomza.pl": {},
      "lowicz.pl": {},
      "lubin.pl": {},
      "lublin.pl": {},
      "lukow.pl": {},
      "mail.pl": {},
      "malbork.pl": {},
      "malopolska.pl": {},
      "mazowsze.pl": {},
      "mazury.pl": {},
      "media.pl": {},
      "miasta.pl": {},
      "mielec.pl": {},
      "mielno.pl": {},
      "mil.pl": {},
      "mragowo.pl": {},
      "naklo.pl": {},
      "net.pl": {},
      "ngo.pl": {},
      "nieruchomosci.pl": {},
      "nom.pl": {},
      "nowaruda.pl": {},
      "nysa.pl": {},
      "olawa.pl": {},
      "olecko.pl": {},
      "olkusz.pl": {},
      "olsztyn.pl": {},
      "opoczno.pl": {},
      "opole.pl": {},
      "org.pl": {},
      "ostroda.pl": {},
      "ostroleka.pl": {},
      "ostrowiec.pl": {},
      "ostrowwlkp.pl": {},
      "pc.pl": {},
      "pila.pl": {},
      "pisz.pl": {},
      "podhale.pl": {},
      "podlasie.pl": {},
      "polkowice.pl": {},
      "pomorskie.pl": {},
      "pomorze.pl": {},
      "powiat.pl": {},
      "poznan.pl": {},
      "priv.pl": {},
      "prochowice.pl": {},
      "pruszkow.pl": {},
      "przeworsk.pl": {},
      "pulawy.pl": {},
      "radom.pl": {},
      "rawa-maz.pl": {},
      "realestate.pl": {},
      "rel.pl": {},
      "rybnik.pl": {},
      "rzeszow.pl": {},
      "sanok.pl": {},
      "sejny.pl": {},
      "sex.pl": {},
      "shop.pl": {},
      "sklep.pl": {},
      "skoczow.pl": {},
      "slask.pl": {},
      "slupsk.pl": {},
      "sos.pl": {},
      "sosnowiec.pl": {},
      "stalowa-wola.pl": {},
      "starachowice.pl": {},
      "stargard.pl": {},
      "suwalki.pl": {},
      "swidnica.pl": {},
      "swiebodzin.pl": {},
      "swinoujscie.pl": {},
      "szczecin.pl": {},
      "szczytno.pl": {},
      "szkola.pl": {},
      "targi.pl": {},
      "tarnobrzeg.pl": {},
      "tgory.pl": {},
      "tm.pl": {},
      "torun.pl": {},
      "tourism.pl": {},
      "travel.pl": {},
      "turek.pl": {},
      "turystyka.pl": {},
      "tychy.pl": {},
      "ustka.pl": {},
      "walbrzych.pl": {},
      "warmia.pl": {},
      "warszawa.pl": {},
      "waw.pl": {},
      "wegrow.pl": {},
      "wielun.pl": {},
      "wlocl.pl": {},
      "wloclawek.pl": {},
      "wodzislaw.pl": {},
      "wolomin.pl": {},
      "wroc.pl": {},
      "wroclaw.pl": {},
      "zachpomor.pl": {},
      "zagan.pl": {},
      "zarow.pl": {},
      "zgora.pl": {},
      "zgorzelec.pl": {}
    }
  },
  "place": { slds: {} },
  "play": { slds: {} },
  "playstation": { slds: {} },
  "plumbing": { slds: {} },
  "plus": { slds: {} },
  "pm": {
    countryWeCanTrust: "pm",
    slds: {}
  },
  "pn": {
    slds: {
      "co.pn": {},
      "net.pn": {},
      "org.pn": {}
    }
  },
  "pnc": { slds: {} },
  "pohl": { slds: {} },
  "poker": { slds: {} },
  "politie": { slds: {} },
  "porn": { slds: {} },
  "post": {
    slds: {
      "com.post": {},
      "edu.post": {},
      "org.post": {}
    }
  },
  "pr": {
    countryWeCanTrust: "pr",
    slds: {
      "at.pr": {},
      "biz.pr": {},
      "ch.pr": {},
      "com.pr": {},
      "de.pr": {},
      "edu.pr": {},
      "eu.pr": {},
      "fr.pr": {},
      "gov.pr": {},
      "info.pr": {},
      "isla.pr": {},
      "it.pr": {},
      "name.pr": {},
      "net.pr": {},
      "nl.pr": {},
      "org.pr": {},
      "pro.pr": {},
      "uk.pr": {}
    }
  },
  "pramerica": { slds: {} },
  "praxi": { slds: {} },
  "press": { slds: {} },
  "prime": { slds: {} },
  "privat": { slds: {} },
  "pro": {
    slds: {
      "aaa.pro": {},
      "aca.pro": {},
      "acct.pro": {},
      "arc.pro": {},
      "avocat.pro": {},
      "bar.pro": {},
      "bus.pro": {},
      "chi.pro": {},
      "chiro.pro": {},
      "cpa.pro": {},
      "den.pro": {},
      "dent.pro": {},
      "eng.pro": {},
      "jur.pro": {},
      "law.pro": {},
      "med.pro": {},
      "min.pro": {},
      "nur.pro": {},
      "nurse.pro": {},
      "pharma.pro": {},
      "prof.pro": {},
      "prx.pro": {},
      "recht.pro": {},
      "rel.pro": {},
      "teach.pro": {},
      "vet.pro": {}
    }
  },
  "prod": { slds: {} },
  "productions": { slds: {} },
  "prof": { slds: {} },
  "progressive": { slds: {} },
  "promo": { slds: {} },
  "properties": { slds: {} },
  "property": { slds: {} },
  "protection": { slds: {} },
  "pru": { slds: {} },
  "prudential": { slds: {} },
  "ps": {
    countryWeCanTrust: "ps",
    slds: {
      "com.ps": {},
      "edu.ps": {},
      "gov.ps": {},
      "net.ps": {},
      "org.ps": {},
      "plo.ps": {},
      "sec.ps": {}
    }
  },
  "pt": {
    countryWeCanTrust: "pt",
    slds: {
      "co.pt": {},
      "com.pt": {},
      "edu.pt": {},
      "gov.pt": {},
      "int.pt": {},
      "net.pt": {},
      "nome.pt": {},
      "org.pt": {},
      "publ.pt": {}
    }
  },
  "pub": { slds: {} },
  "pw": {
    countryWeCanTrust: "pw",
    slds: {}
  },
  "pwc": { slds: {} },
  "py": {
    countryWeCanTrust: "py",
    slds: {
      "com.py": {},
      "coop.py": {},
      "edu.py": {},
      "gov.py": {},
      "net.py": {},
      "org.py": {}
    }
  },
  "qa": {
    countryWeCanTrust: "qa",
    slds: {
      "com.qa": {},
      "edu.qa": {},
      "mil.qa": {},
      "name.qa": {},
      "net.qa": {},
      "org.qa": {},
      "sch.qa": {}
    }
  },
  "qpon": { slds: {} },
  "quebec": { slds: {} },
  "quest": { slds: {} },
  "qvc": { slds: {} },
  "racing": { slds: {} },
  "radio": { slds: {} },
  "re": {
    countryWeCanTrust: "re",
    slds: {}
  },
  "read": { slds: {} },
  "realestate": { slds: {} },
  "realtor": { slds: {} },
  "realty": { slds: {} },
  "recipes": { slds: {} },
  "red": { slds: {} },
  "redstone": { slds: {} },
  "redumbrella": { slds: {} },
  "rehab": { slds: {} },
  "reise": { slds: {} },
  "reisen": { slds: {} },
  "reit": { slds: {} },
  "reliance": { slds: {} },
  "ren": { slds: {} },
  "rent": { slds: {} },
  "rentals": { slds: {} },
  "repair": { slds: {} },
  "report": { slds: {} },
  "republican": { slds: {} },
  "rest": { slds: {} },
  "restaurant": { slds: {} },
  "review": { slds: {} },
  "reviews": { slds: {} },
  "rexroth": { slds: {} },
  "rich": { slds: {} },
  "richardli": { slds: {} },
  "ricoh": { slds: {} },
  "ril": { slds: {} },
  "rio": { slds: {} },
  "rip": { slds: {} },
  "rmit": { slds: {} },
  "ro": {
    countryWeCanTrust: "ro",
    slds: {
      "arts.ro": {},
      "co.ro": {},
      "com.ro": {},
      "firm.ro": {},
      "info.ro": {},
      "ne.ro": {},
      "nom.ro": {},
      "nt.ro": {},
      "or.ro": {},
      "org.ro": {},
      "rec.ro": {},
      "sa.ro": {},
      "srl.ro": {},
      "store.ro": {},
      "tm.ro": {},
      "www.ro": {}
    }
  },
  "rocher": { slds: {} },
  "rocks": { slds: {} },
  "rodeo": { slds: {} },
  "rogers": { slds: {} },
  "room": { slds: {} },
  "rs": {
    countryWeCanTrust: "rs",
    slds: {
      "co.rs": {},
      "edu.rs": {},
      "in.rs": {},
      "org.rs": {}
    }
  },
  "rsvp": { slds: {} },
  "ru": {
    countryWeCanTrust: "ru",
    slds: {
      "ac.ru": {},
      "adygeya.ru": {},
      "bashkiria.ru": {},
      "bir.ru": {},
      "cbg.ru": {},
      "com.ru": {},
      "dagestan.ru": {},
      "grozny.ru": {},
      "int.ru": {},
      "kalmykia.ru": {},
      "kustanai.ru": {},
      "marine.ru": {},
      "mordovia.ru": {},
      "msk.ru": {},
      "mytis.ru": {},
      "nalchik.ru": {},
      "net.ru": {},
      "nov.ru": {},
      "org.ru": {},
      "pp.ru": {},
      "pyatigorsk.ru": {},
      "spb.ru": {},
      "vladikavkaz.ru": {},
      "vladimir.ru": {}
    }
  },
  "rugby": { slds: {} },
  "ruhr": { slds: {} },
  "run": { slds: {} },
  "rw": {
    countryWeCanTrust: "rw",
    slds: {
      "ac.rw": {},
      "co.rw": {},
      "com.rw": {},
      "edu.rw": {},
      "gouv.rw": {},
      "gov.rw": {},
      "int.rw": {},
      "mil.rw": {},
      "net.rw": {},
      "org.rw": {}
    }
  },
  "rwe": { slds: {} },
  "ryukyu": { slds: {} },
  "sa": {
    countryWeCanTrust: "sa",
    slds: {
      "com.sa": {},
      "edu.sa": {},
      "gov.sa": {},
      "med.sa": {},
      "net.sa": {},
      "org.sa": {},
      "pub.sa": {},
      "sch.sa": {}
    }
  },
  "saarland": { slds: {} },
  "safe": { slds: {} },
  "safety": { slds: {} },
  "sakura": { slds: {} },
  "sale": { slds: {} },
  "salon": { slds: {} },
  "samsclub": { slds: {} },
  "samsung": { slds: {} },
  "sandvik": { slds: {} },
  "sandvikcoromant": { slds: {} },
  "sanofi": { slds: {} },
  "sap": { slds: {} },
  "sarl": { slds: {} },
  "sas": { slds: {} },
  "save": { slds: {} },
  "saxo": { slds: {} },
  "sb": {
    countryWeCanTrust: "sb",
    slds: {
      "com.sb": {},
      "edu.sb": {},
      "gov.sb": {},
      "net.sb": {},
      "org.sb": {}
    }
  },
  "sbi": { slds: {} },
  "sbs": { slds: {} },
  "sc": {
    countryWeCanTrust: "sc",
    slds: {
      "com.sc": {},
      "edu.sc": {},
      "gov.sc": {},
      "net.sc": {},
      "org.sc": {}
    }
  },
  "sca": { slds: {} },
  "scb": { slds: {} },
  "schaeffler": { slds: {} },
  "schmidt": { slds: {} },
  "scholarships": { slds: {} },
  "school": { slds: {} },
  "schule": { slds: {} },
  "schwarz": { slds: {} },
  "science": { slds: {} },
  "scjohnson": { slds: {} },
  "scor": { slds: {} },
  "scot": { slds: {} },
  "sd": {
    countryWeCanTrust: "sd",
    slds: {
      "com.sd": {},
      "edu.sd": {},
      "gov.sd": {},
      "info.sd": {},
      "med.sd": {},
      "net.sd": {},
      "org.sd": {},
      "tv.sd": {}
    }
  },
  "se": {
    countryWeCanTrust: "se",
    slds: {
      "ab.se": {},
      "ac.se": {},
      "bd.se": {},
      "brand.se": {},
      "c.se": {},
      "com.se": {},
      "d.se": {},
      "e.se": {},
      "f.se": {},
      "fh.se": {},
      "fhsk.se": {},
      "fhv.se": {},
      "g.se": {},
      "h.se": {},
      "i.se": {},
      "k.se": {},
      "komforb.se": {},
      "kommunalforbund.se": {},
      "komvux.se": {},
      "lanarb.se": {},
      "lanbib.se": {},
      "m.se": {},
      "mil.se": {},
      "n.se": {},
      "naturbruksgymn.se": {},
      "o.se": {},
      "org.se": {},
      "parti.se": {},
      "pp.se": {},
      "press.se": {},
      "s.se": {},
      "sshn.se": {},
      "t.se": {},
      "tm.se": {},
      "u.se": {},
      "w.se": {},
      "x.se": {},
      "y.se": {},
      "z.se": {}
    }
  },
  "search": { slds: {} },
  "seat": { slds: {} },
  "secure": { slds: {} },
  "security": { slds: {} },
  "seek": { slds: {} },
  "select": { slds: {} },
  "sener": { slds: {} },
  "services": { slds: {} },
  "ses": { slds: {} },
  "seven": { slds: {} },
  "sew": { slds: {} },
  "sex": { slds: {} },
  "sexy": { slds: {} },
  "sfr": { slds: {} },
  "sg": {
    countryWeCanTrust: "sg",
    slds: {
      "com.sg": {},
      "edu.sg": {},
      "gov.sg": {},
      "idn.sg": {},
      "net.sg": {},
      "org.sg": {},
      "per.sg": {}
    }
  },
  "sh": {
    countryWeCanTrust: "sh",
    slds: {}
  },
  "shangrila": { slds: {} },
  "sharp": { slds: {} },
  "shaw": { slds: {} },
  "shell": { slds: {} },
  "shia": { slds: {} },
  "shiksha": { slds: {} },
  "shoes": { slds: {} },
  "shop": { slds: {} },
  "shopping": { slds: {} },
  "shouji": { slds: {} },
  "show": { slds: {} },
  "showtime": { slds: {} },
  "shriram": { slds: {} },
  "si": {
    slds: {
      "ae.si": {},
      "at.si": {},
      "cn.si": {},
      "co.si": {},
      "de.si": {},
      "uk.si": {},
      "us.si": {}
    }
  },
  "silk": { slds: {} },
  "sina": { slds: {} },
  "singles": { slds: {} },
  "site": { slds: {} },
  "sj": { slds: {} },
  "sk": {
    countryWeCanTrust: "sk",
    slds: { "org.sk": {} }
  },
  "ski": { slds: {} },
  "skin": { slds: {} },
  "sky": { slds: {} },
  "skype": { slds: {} },
  "sl": {
    countryWeCanTrust: "sl",
    slds: {
      "com.sl": {},
      "edu.sl": {},
      "net.sl": {},
      "org.sl": {}
    }
  },
  "sling": { slds: {} },
  "sm": {
    countryWeCanTrust: "sm",
    slds: {}
  },
  "smart": { slds: {} },
  "smile": { slds: {} },
  "sn": {
    countryWeCanTrust: "sn",
    slds: {
      "art.sn": {},
      "com.sn": {},
      "edu.sn": {},
      "org.sn": {},
      "perso.sn": {},
      "univ.sn": {}
    }
  },
  "sncf": { slds: {} },
  "so": {
    slds: {
      "com.so": {},
      "net.so": {},
      "org.so": {}
    }
  },
  "soccer": { slds: {} },
  "social": { slds: {} },
  "softbank": { slds: {} },
  "software": { slds: {} },
  "sohu": { slds: {} },
  "solar": { slds: {} },
  "solutions": { slds: {} },
  "song": { slds: {} },
  "sony": { slds: {} },
  "soy": { slds: {} },
  "spa": { slds: {} },
  "space": { slds: {} },
  "spiegel": { slds: {} },
  "sport": { slds: {} },
  "spot": { slds: {} },
  "spreadbetting": { slds: {} },
  "sr": {
    countryWeCanTrust: "sr",
    slds: {}
  },
  "srl": { slds: {} },
  "ss": {
    countryWeCanTrust: "ss",
    slds: {
      "biz.ss": {},
      "co.ss": {},
      "com.ss": {},
      "me.ss": {},
      "net.ss": {}
    }
  },
  "st": {
    countryWeCanTrust: "st",
    slds: {}
  },
  "stada": { slds: {} },
  "staples": { slds: {} },
  "star": { slds: {} },
  "starhub": { slds: {} },
  "statebank": { slds: {} },
  "statefarm": { slds: {} },
  "statoil": { slds: {} },
  "stc": { slds: {} },
  "stcgroup": { slds: {} },
  "stockholm": { slds: {} },
  "storage": { slds: {} },
  "store": { slds: {} },
  "stream": { slds: {} },
  "studio": { slds: {} },
  "study": { slds: {} },
  "style": { slds: {} },
  "su": {
    slds: {
      "abkhazia.su": {},
      "adygeya.su": {},
      "aktyubinsk.su": {},
      "arkhangelsk.su": {},
      "armenia.su": {},
      "ashgabad.su": {},
      "azerbaijan.su": {},
      "balashov.su": {},
      "bashkiria.su": {},
      "bryansk.su": {},
      "bukhara.su": {},
      "chimkent.su": {},
      "dagestan.su": {},
      "east-kazakhstan.su": {},
      "exnet.su": {},
      "georgia.su": {},
      "grozny.su": {},
      "ivanovo.su": {},
      "jambyl.su": {},
      "kalmykia.su": {},
      "kaluga.su": {},
      "karacol.su": {},
      "karaganda.su": {},
      "karelia.su": {},
      "khakassia.su": {},
      "krasnodar.su": {},
      "kurgan.su": {},
      "kustanai.su": {},
      "lenug.su": {},
      "mangyshlak.su": {},
      "mordovia.su": {},
      "msk.su": {},
      "murmansk.su": {},
      "nalchik.su": {},
      "navoi.su": {},
      "north-kazakhstan.su": {},
      "nov.su": {},
      "obninsk.su": {},
      "penza.su": {},
      "pokrovsk.su": {},
      "sochi.su": {},
      "spb.su": {},
      "tashkent.su": {},
      "termez.su": {},
      "togliatti.su": {},
      "troitsk.su": {},
      "tselinograd.su": {},
      "tula.su": {},
      "tuva.su": {},
      "vladikavkaz.su": {},
      "vladimir.su": {},
      "vologda.su": {}
    }
  },
  "sucks": { slds: {} },
  "supplies": { slds: {} },
  "supply": { slds: {} },
  "support": { slds: {} },
  "surf": { slds: {} },
  "surgery": { slds: {} },
  "suzuki": { slds: {} },
  "sv": {
    countryWeCanTrust: "sv",
    slds: {
      "com.sv": {},
      "edu.sv": {},
      "gob.sv": {},
      "org.sv": {},
      "red.sv": {}
    }
  },
  "swatch": { slds: {} },
  "swiftcover": { slds: {} },
  "swiss": { slds: {} },
  "sx": {
    countryWeCanTrust: "sx",
    slds: {}
  },
  "sy": {
    countryWeCanTrust: "sy",
    slds: {
      "com.sy": {},
      "gov.sy": {},
      "net.sy": {}
    }
  },
  "sydney": { slds: {} },
  "symantec": { slds: {} },
  "systems": { slds: {} },
  "sz": {
    countryWeCanTrust: "sz",
    slds: {
      "co.sz": {},
      "org.sz": {}
    }
  },
  "tab": { slds: {} },
  "taipei": { slds: {} },
  "talk": { slds: {} },
  "taobao": { slds: {} },
  "target": { slds: {} },
  "tatamotors": { slds: {} },
  "tatar": { slds: {} },
  "tattoo": { slds: {} },
  "tax": { slds: {} },
  "taxi": { slds: {} },
  "tc": {
    slds: {
      "com.tc": {},
      "net.tc": {},
      "org.tc": {},
      "pro.tc": {}
    }
  },
  "tci": { slds: {} },
  "td": {
    countryWeCanTrust: "td",
    slds: {
      "com.td": {},
      "net.td": {},
      "org.td": {},
      "tourism.td": {}
    }
  },
  "tdk": { slds: {} },
  "team": { slds: {} },
  "tech": { slds: {} },
  "technology": { slds: {} },
  "tel": { slds: {} },
  "telecity": { slds: {} },
  "telefonica": { slds: {} },
  "temasek": { slds: {} },
  "tennis": { slds: {} },
  "test": { slds: {} },
  "teva": { slds: {} },
  "tf": {
    countryWeCanTrust: "tf",
    slds: {}
  },
  "tg": {
    countryWeCanTrust: "tg",
    slds: {}
  },
  "th": {
    countryWeCanTrust: "th",
    slds: {
      "ac.th": {},
      "co.th": {},
      "go.th": {},
      "in.th": {},
      "mi.th": {},
      "net.th": {},
      "or.th": {}
    }
  },
  "thd": { slds: {} },
  "theater": { slds: {} },
  "theatre": { slds: {} },
  "tiaa": { slds: {} },
  "tickets": { slds: {} },
  "tienda": { slds: {} },
  "tiffany": { slds: {} },
  "tips": { slds: {} },
  "tires": { slds: {} },
  "tirol": { slds: {} },
  "tj": {
    countryWeCanTrust: "tj",
    slds: {
      "ac.tj": {},
      "aero.tj": {},
      "biz.tj": {},
      "co.tj": {},
      "com.tj": {},
      "coop.tj": {},
      "dyn.tj": {},
      "edu.tj": {},
      "go.tj": {},
      "gov.tj": {},
      "info.tj": {},
      "int.tj": {},
      "mil.tj": {},
      "museum.tj": {},
      "my.tj": {},
      "name.tj": {},
      "net.tj": {},
      "org.tj": {},
      "per.tj": {},
      "pro.tj": {},
      "web.tj": {}
    }
  },
  "tjmaxx": { slds: {} },
  "tjx": { slds: {} },
  "tk": {
    countryWeCanTrust: "tk",
    slds: {}
  },
  "tkmaxx": { slds: {} },
  "tl": {
    countryWeCanTrust: "tl",
    slds: {
      "com.tl": {},
      "net.tl": {},
      "org.tl": {}
    }
  },
  "tm": {
    countryWeCanTrust: "tm",
    slds: {}
  },
  "tmall": { slds: {} },
  "tn": {
    countryWeCanTrust: "tn",
    slds: {
      "agrinet.tn": {},
      "com.tn": {},
      "defense.tn": {},
      "edunet.tn": {},
      "ens.tn": {},
      "fin.tn": {},
      "gov.tn": {},
      "ind.tn": {},
      "info.tn": {},
      "intl.tn": {},
      "nat.tn": {},
      "net.tn": {},
      "org.tn": {},
      "perso.tn": {},
      "rnrt.tn": {},
      "rns.tn": {},
      "rnu.tn": {},
      "tourism.tn": {}
    }
  },
  "to": { slds: {} },
  "today": { slds: {} },
  "tokyo": { slds: {} },
  "tools": { slds: {} },
  "top": { slds: {} },
  "toray": { slds: {} },
  "toshiba": { slds: {} },
  "total": { slds: {} },
  "tours": { slds: {} },
  "town": { slds: {} },
  "toyota": { slds: {} },
  "toys": { slds: {} },
  "tr": {
    countryWeCanTrust: "tr",
    slds: {
      "av.tr": {},
      "bbs.tr": {},
      "bel.tr": {},
      "biz.tr": {},
      "com.tr": {},
      "dr.tr": {},
      "edu.tr": {},
      "gen.tr": {},
      "gov.tr": {},
      "info.tr": {},
      "k12.tr": {},
      "mil.tr": {},
      "name.tr": {},
      "net.tr": {},
      "org.tr": {},
      "pol.tr": {},
      "tel.tr": {},
      "tv.tr": {},
      "web.tr": {}
    }
  },
  "trade": { slds: {} },
  "trading": { slds: {} },
  "training": { slds: {} },
  "travel": { slds: {} },
  "travelchannel": { slds: {} },
  "travelers": { slds: {} },
  "travelersinsurance": { slds: {} },
  "trust": { slds: {} },
  "trv": { slds: {} },
  "tt": {
    countryWeCanTrust: "tt",
    slds: {
      "biz.tt": {},
      "co.tt": {},
      "com.tt": {},
      "edu.tt": {},
      "gov.tt": {},
      "info.tt": {},
      "jobs.tt": {},
      "mobi.tt": {},
      "name.tt": {},
      "net.tt": {},
      "org.tt": {},
      "pro.tt": {}
    }
  },
  "tube": { slds: {} },
  "tui": { slds: {} },
  "tunes": { slds: {} },
  "tushu": { slds: {} },
  "tv": { slds: { "gov.tv": { countryWeCanTrust: "tv" } } },
  "tvs": { slds: {} },
  "tw": {
    countryWeCanTrust: "tw",
    slds: {
      "club.tw": {},
      "com.tw": {},
      "ebiz.tw": {},
      "edu.tw": {},
      "game.tw": {},
      "gov.tw": {},
      "idv.tw": {},
      "mil.tw": {},
      "net.tw": {},
      "org.tw": {}
    }
  },
  "tz": {
    countryWeCanTrust: "tz",
    slds: {
      "ac.tz": {},
      "co.tz": {},
      "go.tz": {},
      "hotel.tz": {},
      "info.tz": {},
      "me.tz": {},
      "mil.tz": {},
      "mobi.tz": {},
      "ne.tz": {},
      "or.tz": {},
      "sc.tz": {},
      "tv.tz": {}
    }
  },
  "ua": {
    countryWeCanTrust: "ua",
    slds: {
      "biz.ua": {},
      "cherkassy.ua": {},
      "cherkasy.ua": {},
      "chernigov.ua": {},
      "chernivtsi.ua": {},
      "chernovtsy.ua": {},
      "ck.ua": {},
      "cn.ua": {},
      "co.ua": {},
      "com.ua": {},
      "crimea.ua": {},
      "cv.ua": {},
      "dn.ua": {},
      "dnepropetrovsk.ua": {},
      "dnipropetrovsk.ua": {},
      "donetsk.ua": {},
      "dp.ua": {},
      "edu.ua": {},
      "gov.ua": {},
      "if.ua": {},
      "in.ua": {},
      "ivano-frankivsk.ua": {},
      "kh.ua": {},
      "kharkiv.ua": {},
      "kharkov.ua": {},
      "kherson.ua": {},
      "khmelnitskiy.ua": {},
      "kiev.ua": {},
      "kirovograd.ua": {},
      "km.ua": {},
      "kr.ua": {},
      "ks.ua": {},
      "kv.ua": {},
      "kyiv.ua": {},
      "lg.ua": {},
      "lt.ua": {},
      "lugansk.ua": {},
      "lutsk.ua": {},
      "lviv.ua": {},
      "mk.ua": {},
      "net.ua": {},
      "nikolaev.ua": {},
      "od.ua": {},
      "odesa.ua": {},
      "odessa.ua": {},
      "org.ua": {},
      "pl.ua": {},
      "poltava.ua": {},
      "pp.ua": {},
      "rivne.ua": {},
      "rovno.ua": {},
      "rv.ua": {},
      "sebastopol.ua": {},
      "sm.ua": {},
      "sumy.ua": {},
      "te.ua": {},
      "ternopil.ua": {},
      "uz.ua": {},
      "uzhgorod.ua": {},
      "vinnica.ua": {},
      "vn.ua": {},
      "volyn.ua": {},
      "yalta.ua": {},
      "zaporizhzhe.ua": {},
      "zhitomir.ua": {},
      "zp.ua": {},
      "zt.ua": {}
    }
  },
  "ubank": { slds: {} },
  "ubs": { slds: {} },
  "uconnect": { slds: {} },
  "ug": {
    countryWeCanTrust: "ug",
    slds: {
      "ac.ug": {},
      "co.ug": {},
      "com.ug": {},
      "go.ug": {},
      "ne.ug": {},
      "or.ug": {},
      "org.ug": {},
      "sc.ug": {}
    }
  },
  "uk": {
    countryWeCanTrust: "gb",
    slds: {
      "ac.uk": {},
      "barking-dagenham.sch.uk": {},
      "barnet.sch.uk": {},
      "barnsley.sch.uk": {},
      "bathnes.sch.uk": {},
      "beds.sch.uk": {},
      "bexley.sch.uk": {},
      "bham.sch.uk": {},
      "bl.uk": {},
      "blackburn.sch.uk": {},
      "blackpool.sch.uk": {},
      "bolton.sch.uk": {},
      "bournemouth.sch.uk": {},
      "bracknell-forest.sch.uk": {},
      "bradford.sch.uk": {},
      "brent.sch.uk": {},
      "co.uk": {},
      "doncaster.sch.uk": {},
      "gov.uk": {},
      "icnet.uk": {},
      "jet.uk": {},
      "ltd.uk": {},
      "me.uk": {},
      "mil.uk": {},
      "mod.uk": {},
      "nel.uk": {},
      "net.uk": {},
      "nhs.uk": {},
      "nic.uk": {},
      "nls.uk": {},
      "org.uk": {},
      "parliament.uk": {},
      "plc.uk": {},
      "police.uk": {},
      "sch.uk": {}
    }
  },
  "unicom": { slds: {} },
  "university": { slds: {} },
  "uno": { slds: {} },
  "uol": { slds: {} },
  "ups": { slds: {} },
  "us": {
    countryWeCanTrust: "us",
    slds: {
      "ak.us": {},
      "al.us": {},
      "ar.us": {},
      "az.us": {},
      "ca.us": {},
      "co.us": {},
      "ct.us": {},
      "dc.us": {},
      "de.us": {},
      "dni.us": {},
      "fed.us": {},
      "fl.us": {},
      "ga.us": {},
      "hi.us": {},
      "ia.us": {},
      "id.us": {},
      "il.us": {},
      "in.us": {},
      "isa.us": {},
      "kids.us": {},
      "ks.us": {},
      "ky.us": {},
      "la.us": {},
      "ma.us": {},
      "md.us": {},
      "me.us": {},
      "mi.us": {},
      "mn.us": {},
      "mo.us": {},
      "ms.us": {},
      "mt.us": {},
      "nc.us": {},
      "nd.us": {},
      "ne.us": {},
      "nh.us": {},
      "nj.us": {},
      "nm.us": {},
      "nsn.us": {},
      "nv.us": {},
      "ny.us": {},
      "oh.us": {},
      "ok.us": {},
      "or.us": {},
      "pa.us": {},
      "ri.us": {},
      "sc.us": {},
      "sd.us": {},
      "tn.us": {},
      "tx.us": {},
      "ut.us": {},
      "va.us": {},
      "vt.us": {},
      "wa.us": {},
      "wi.us": {},
      "wv.us": {},
      "wy.us": {}
    }
  },
  "uy": {
    countryWeCanTrust: "uy",
    slds: {
      "com.uy": {},
      "edu.uy": {},
      "gub.uy": {},
      "mil.uy": {},
      "net.uy": {},
      "org.uy": {}
    }
  },
  "uz": {
    countryWeCanTrust: "uz",
    slds: {
      "biz.uz": {},
      "co.uz": {},
      "com.uz": {},
      "net.uz": {},
      "org.uz": {}
    }
  },
  "va": {
    countryWeCanTrust: "va",
    slds: {}
  },
  "vacations": { slds: {} },
  "vana": { slds: {} },
  "vanguard": { slds: {} },
  "vc": {
    slds: {
      "com.vc": {},
      "net.vc": {},
      "org.vc": {}
    }
  },
  "ve": {
    countryWeCanTrust: "ve",
    slds: {
      "co.ve": {},
      "com.ve": {},
      "info.ve": {},
      "net.ve": {},
      "org.ve": {},
      "web.ve": {}
    }
  },
  "vegas": { slds: {} },
  "ventures": { slds: {} },
  "verisign": { slds: {} },
  "verm\xF6gensberater": { slds: {} },
  "verm\xF6gensberatung": { slds: {} },
  "versicherung": { slds: {} },
  "vet": { slds: {} },
  "vg": {
    countryWeCanTrust: "vg",
    slds: {}
  },
  "vi": {
    countryWeCanTrust: "vi",
    slds: {
      "co.vi": {},
      "com.vi": {},
      "edu.vi": {},
      "gov.vi": {},
      "net.vi": {},
      "org.vi": {}
    }
  },
  "viajes": { slds: {} },
  "video": { slds: {} },
  "vig": { slds: {} },
  "viking": { slds: {} },
  "villas": { slds: {} },
  "vin": { slds: {} },
  "vip": { slds: {} },
  "virgin": { slds: {} },
  "visa": { slds: {} },
  "vision": { slds: {} },
  "vista": { slds: {} },
  "vistaprint": { slds: {} },
  "viva": { slds: {} },
  "vivo": { slds: {} },
  "vlaanderen": { slds: {} },
  "vn": {
    countryWeCanTrust: "vn",
    slds: {
      "ac.vn": {},
      "biz.vn": {},
      "com.vn": {},
      "edu.vn": {},
      "gov.vn": {},
      "health.vn": {},
      "info.vn": {},
      "int.vn": {},
      "name.vn": {},
      "net.vn": {},
      "org.vn": {},
      "pro.vn": {}
    }
  },
  "vodka": { slds: {} },
  "volkswagen": { slds: {} },
  "volvo": { slds: {} },
  "vote": { slds: {} },
  "voting": { slds: {} },
  "voto": { slds: {} },
  "voyage": { slds: {} },
  "vu": {
    countryWeCanTrust: "vu",
    slds: {
      "com.vu": {},
      "net.vu": {},
      "org.vu": {}
    }
  },
  "wales": { slds: {} },
  "walmart": { slds: {} },
  "walter": { slds: {} },
  "wang": { slds: {} },
  "wanggou": { slds: {} },
  "watch": { slds: {} },
  "watches": { slds: {} },
  "weather": { slds: {} },
  "weatherchannel": { slds: {} },
  "webcam": { slds: {} },
  "weber": { slds: {} },
  "website": { slds: {} },
  "wed": { slds: {} },
  "wedding": { slds: {} },
  "weibo": { slds: {} },
  "weir": { slds: {} },
  "wf": {
    countryWeCanTrust: "wf",
    slds: {}
  },
  "whoswho": { slds: {} },
  "wien": { slds: {} },
  "wiki": { slds: {} },
  "williamhill": { slds: {} },
  "win": { slds: {} },
  "windows": { slds: {} },
  "wine": { slds: {} },
  "winners": { slds: {} },
  "wl": { slds: {} },
  "wme": { slds: {} },
  "wolterskluwer": { slds: {} },
  "woodside": { slds: {} },
  "work": { slds: {} },
  "works": { slds: {} },
  "world": { slds: {} },
  "wow": { slds: {} },
  "ws": {
    slds: {
      "com.ws": {},
      "net.ws": {},
      "org.ws": {}
    }
  },
  "wtc": { slds: {} },
  "wtf": { slds: {} },
  "xbox": { slds: {} },
  "xerox": { slds: {} },
  "xfinity": { slds: {} },
  "xihuan": { slds: {} },
  "xin": { slds: {} },
  "xlm": { slds: {} },
  "xxx": { slds: {} },
  "xyz": { slds: {} },
  "yachts": { slds: {} },
  "yahoo": { slds: {} },
  "yamaxun": { slds: {} },
  "yandex": { slds: {} },
  "ye": {
    countryWeCanTrust: "ye",
    slds: {
      "com.ye": {},
      "net.ye": {},
      "org.ye": {}
    }
  },
  "yodobashi": { slds: {} },
  "yoga": { slds: {} },
  "yokohama": { slds: {} },
  "you": { slds: {} },
  "youtube": { slds: {} },
  "yt": { slds: {} },
  "yun": { slds: {} },
  "za": {
    countryWeCanTrust: "za",
    slds: {
      "ac.za": {},
      "alt.za": {},
      "city.za": {},
      "co.za": {},
      "edu.za": {},
      "gov.za": {},
      "law.za": {},
      "mil.za": {},
      "net.za": {},
      "ngo.za": {},
      "nom.za": {},
      "org.za": {},
      "school.za": {},
      "tm.za": {},
      "web.za": {}
    }
  },
  "zappos": { slds: {} },
  "zara": { slds: {} },
  "zero": { slds: {} },
  "zip": { slds: {} },
  "zippo": { slds: {} },
  "zm": {
    countryWeCanTrust: "zm",
    slds: {
      "ac.zm": {},
      "co.zm": {},
      "com.zm": {},
      "gov.zm": {},
      "org.zm": {},
      "sch.zm": {}
    }
  },
  "zone": { slds: {} },
  "zuerich": { slds: {} },
  "zw": {
    countryWeCanTrust: "zw",
    slds: {
      "ac.zw": {},
      "co.zw": {},
      "gov.zw": {},
      "org.zw": {}
    }
  },
  "\u03B5\u03BB": { slds: {} },
  "\u03B5\u03C5": { slds: {} },
  "\u0431\u0433": { slds: {} },
  "\u0431\u0435\u043B": { slds: {} },
  "\u0434\u0435\u0442\u0438": { slds: {} },
  "\u0435\u044E": { slds: {} },
  "\u043A\u0430\u0442\u043E\u043B\u0438\u043A": { slds: {} },
  "\u043A\u043E\u043C": { slds: {} },
  "\u043C\u043A\u0434": { slds: {} },
  "\u043C\u043E\u043D": { slds: {} },
  "\u043C\u043E\u0441\u043A\u0432\u0430": { slds: {} },
  "\u043E\u043D\u043B\u0430\u0439\u043D": { slds: {} },
  "\u043E\u0440\u0433": { slds: {} },
  "\u0440\u0443\u0441": { slds: {} },
  "\u0440\u0444": {
    slds: {
      "\u043A\u043E\u043C.\u0440\u0444": {},
      "\u043D\u0435\u0442.\u0440\u0444": {},
      "\u043E\u0440\u0433.\u0440\u0444": {}
    }
  },
  "\u0441\u0430\u0439\u0442": { slds: {} },
  "\u0441\u0440\u0431": {
    slds: {
      "\u0430\u043A.\u0441\u0440\u0431": {},
      "\u043F\u0440.\u0441\u0440\u0431": {},
      "\u0443\u043F\u0440.\u0441\u0440\u0431": {}
    }
  },
  "\u0443\u043A\u0440": { slds: {} },
  "\u049B\u0430\u0437": { slds: {} },
  "\u0570\u0561\u0575": { slds: {} },
  "\u05D9\u05E9\u05E8\u05D0\u05DC": { slds: {} },
  "\u05E7\u05D5\u05DD": { slds: {} },
  "\u0627\u0628\u0648\u0638\u0628\u064A": { slds: {} },
  "\u0627\u0631\u0627\u0645\u0643\u0648": { slds: {} },
  "\u0627\u0644\u0627\u0631\u062F\u0646": { slds: {} },
  "\u0627\u0644\u0628\u062D\u0631\u064A\u0646": { slds: {} },
  "\u0627\u0644\u062C\u0632\u0627\u0626\u0631": { slds: {} },
  "\u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629": { slds: {} },
  "\u0627\u0644\u0639\u0644\u064A\u0627\u0646": { slds: {} },
  "\u0627\u0644\u0645\u063A\u0631\u0628": { slds: {} },
  "\u0627\u0645\u0627\u0631\u0627\u062A": { slds: {} },
  "\u0627\u06CC\u0631\u0627\u0646": { slds: {} },
  "\u0628\u0627\u0631\u062A": { slds: {} },
  "\u0628\u0627\u0632\u0627\u0631": { slds: {} },
  "\u0628\u064A\u062A\u0643": { slds: {} },
  "\u0628\u06BE\u0627\u0631\u062A": { slds: {} },
  "\u062A\u0648\u0646\u0633": { slds: {} },
  "\u0633\u0648\u062F\u0627\u0646": { slds: {} },
  "\u0633\u0648\u0631\u064A\u0629": { slds: {} },
  "\u0634\u0628\u0643\u0629": { slds: {} },
  "\u0639\u0631\u0627\u0642": { slds: {} },
  "\u0639\u0631\u0628": { slds: {} },
  "\u0639\u0645\u0627\u0646": { slds: {} },
  "\u0641\u0644\u0633\u0637\u064A\u0646": { slds: {} },
  "\u0642\u0637\u0631": { slds: {} },
  "\u0643\u0627\u062B\u0648\u0644\u064A\u0643": { slds: {} },
  "\u0643\u0648\u0645": { slds: {} },
  "\u0645\u0635\u0631": { slds: {} },
  "\u0645\u0644\u064A\u0633\u064A\u0627": { slds: {} },
  "\u0645\u0648\u0631\u064A\u062A\u0627\u0646\u064A\u0627": { slds: {} },
  "\u0645\u0648\u0642\u0639": { slds: {} },
  "\u0647\u0645\u0631\u0627\u0647": { slds: {} },
  "\u067E\u0627\u0643\u0633\u062A\u0627\u0646": { slds: {} },
  "\u067E\u0627\u06A9\u0633\u062A\u0627\u0646": { slds: {} },
  "\u0680\u0627\u0631\u062A": { slds: {} },
  "\u0915\u0949\u092E": { slds: {} },
  "\u0928\u0947\u091F": { slds: {} },
  "\u092D\u093E\u0930\u0924": { slds: { "\u0915\u0902\u092A\u0928\u0940.\u092D\u093E\u0930\u0924": {} } },
  "\u092D\u093E\u0930\u0924\u092E\u094D": { slds: {} },
  "\u092D\u093E\u0930\u094B\u0924": { slds: {} },
  "\u0938\u0902\u0917\u0920\u0928": { slds: {} },
  "\u09AC\u09BE\u0982\u09B2\u09BE": { slds: {} },
  "\u09AD\u09BE\u09B0\u09A4": { slds: { "\u0995\u09CB\u09AE\u09CD\u09AA\u09BE\u09A8\u09BF.\u09AD\u09BE\u09B0\u09A4": {} } },
  "\u09AD\u09BE\u09F0\u09A4": { slds: {} },
  "\u0A2D\u0A3E\u0A30\u0A24": { slds: { "\u0A15\u0A70\u0A2A\u0A28\u0A40.\u0A2D\u0A3E\u0A30\u0A24": {} } },
  "\u0AAD\u0ABE\u0AB0\u0AA4": { slds: { "\u0A95\u0A82\u0AAA\u0AA8\u0AC0.\u0AAD\u0ABE\u0AB0\u0AA4": {} } },
  "\u0B2D\u0B3E\u0B30\u0B24": { slds: {} },
  "\u0B87\u0BA8\u0BCD\u0BA4\u0BBF\u0BAF\u0BBE": { slds: { "\u0BA8\u0BBF\u0BB1\u0BC1\u0BB5\u0BA9\u0BAE\u0BCD.\u0B87\u0BA8\u0BCD\u0BA4\u0BBF\u0BAF\u0BBE": {} } },
  "\u0B87\u0BB2\u0B99\u0BCD\u0B95\u0BC8": { slds: {} },
  "\u0B9A\u0BBF\u0B99\u0BCD\u0B95\u0BAA\u0BCD\u0BAA\u0BC2\u0BB0\u0BCD": { slds: {} },
  "\u0C2D\u0C3E\u0C30\u0C24\u0C4D": { slds: { "\u0C15\u0C02\u0C2A\u0C46\u0C28\u0C40.\u0C2D\u0C3E\u0C30\u0C24\u0C4D": {} } },
  "\u0CAD\u0CBE\u0CB0\u0CA4": { slds: {} },
  "\u0D2D\u0D3E\u0D30\u0D24\u0D02": { slds: {} },
  "\u0DBD\u0D82\u0D9A\u0DCF": { slds: {} },
  "\u0E04\u0E2D\u0E21": { slds: {} },
  "\u0E44\u0E17\u0E22": { slds: { "\u0E18\u0E38\u0E23\u0E01\u0E34\u0E08.\u0E44\u0E17\u0E22": {} } },
  "\u0EA5\u0EB2\u0EA7": { slds: {} },
  "\u10D2\u10D4": { slds: {} },
  "\u307F\u3093\u306A": { slds: {} },
  "\u30A2\u30DE\u30BE\u30F3": { slds: {} },
  "\u30AF\u30E9\u30A6\u30C9": { slds: {} },
  "\u30B0\u30FC\u30B0\u30EB": { slds: {} },
  "\u30B3\u30E0": { slds: {} },
  "\u30B9\u30C8\u30A2": { slds: {} },
  "\u30BB\u30FC\u30EB": { slds: {} },
  "\u30D5\u30A1\u30C3\u30B7\u30E7\u30F3": { slds: {} },
  "\u30DD\u30A4\u30F3\u30C8": { slds: {} },
  "\u4E16\u754C": { slds: {} },
  "\u4E2D\u4FE1": { slds: {} },
  "\u4E2D\u56FD": { slds: {} },
  "\u4E2D\u570B": { slds: {} },
  "\u4E2D\u6587\u7F51": { slds: {} },
  "\u4E9A\u9A6C\u900A": { slds: {} },
  "\u4F01\u4E1A": { slds: {} },
  "\u4F5B\u5C71": { slds: {} },
  "\u4FE1\u606F": { slds: {} },
  "\u5065\u5EB7": { slds: {} },
  "\u516B\u5366": { slds: {} },
  "\u516C\u53F8": { slds: {} },
  "\u516C\u76CA": { slds: {} },
  "\u53F0\u6E7E": { slds: {} },
  "\u53F0\u7063": { slds: {} },
  "\u5546\u57CE": { slds: {} },
  "\u5546\u5E97": { slds: {} },
  "\u5546\u6807": { slds: {} },
  "\u5609\u91CC": { slds: {} },
  "\u5609\u91CC\u5927\u9152\u5E97": { slds: {} },
  "\u5728\u7EBF": { slds: {} },
  "\u5927\u62FF": { slds: {} },
  "\u5929\u4E3B\u6559": { slds: {} },
  "\u5A31\u4E50": { slds: {} },
  "\u5BB6\u96FB": { slds: {} },
  "\u5E7F\u4E1C": { slds: {} },
  "\u5FAE\u535A": { slds: {} },
  "\u6148\u5584": { slds: {} },
  "\u6211\u7231\u4F60": { slds: {} },
  "\u624B\u673A": { slds: {} },
  "\u62DB\u8058": { slds: {} },
  "\u653F\u52A1": { slds: {} },
  "\u653F\u5E9C": { slds: {} },
  "\u65B0\u52A0\u5761": { slds: {} },
  "\u65B0\u95FB": { slds: {} },
  "\u65F6\u5C1A": { slds: {} },
  "\u66F8\u7C4D": { slds: {} },
  "\u673A\u6784": { slds: {} },
  "\u6DE1\u9A6C\u9521": { slds: {} },
  "\u6E38\u620F": { slds: {} },
  "\u6FB3\u9580": { slds: {} },
  "\u70B9\u770B": { slds: {} },
  "\u79FB\u52A8": { slds: {} },
  "\u7EC4\u7EC7\u673A\u6784": { slds: {} },
  "\u7F51\u5740": { slds: {} },
  "\u7F51\u5E97": { slds: {} },
  "\u7F51\u7AD9": { slds: {} },
  "\u7F51\u7EDC": { slds: {} },
  "\u8054\u901A": { slds: {} },
  "\u8C37\u6B4C": { slds: {} },
  "\u8D2D\u7269": { slds: {} },
  "\u901A\u8CA9": { slds: {} },
  "\u96C6\u56E2": { slds: {} },
  "\u96FB\u8A0A\u76C8\u79D1": { slds: {} },
  "\u98DE\u5229\u6D66": { slds: {} },
  "\u98DF\u54C1": { slds: {} },
  "\u9910\u5385": { slds: {} },
  "\u9999\u683C\u91CC\u62C9": { slds: {} },
  "\u9999\u6E2F": {
    slds: {
      "\u500B\u4EBA.\u9999\u6E2F": {},
      "\u516C\u53F8.\u9999\u6E2F": {},
      "\u653F\u5E9C.\u9999\u6E2F": {},
      "\u6559\u80B2.\u9999\u6E2F": {},
      "\u7D44\u7E54.\u9999\u6E2F": {},
      "\u7DB2\u7D61.\u9999\u6E2F": {}
    }
  },
  "\uB2F7\uB137": { slds: {} },
  "\uB2F7\uCEF4": { slds: {} },
  "\uC0BC\uC131": { slds: {} },
  "\uD55C\uAD6D": { slds: {} }
};

const totalEmployees = [
  {
    name: {
      en: "1 - 10",
      es: "1 - 10",
      fr: "1 - 10"
    },
    slug: "1-10"
  },
  {
    name: {
      en: "10 - 50",
      es: "10 - 50",
      fr: "10 - 50"
    },
    slug: "10-50"
  },
  {
    name: {
      en: "50 - 200",
      es: "50 - 200",
      fr: "50 - 200"
    },
    slug: "50-200"
  },
  {
    name: {
      en: "200 - 500",
      es: "200 - 500",
      fr: "200 - 500"
    },
    slug: "200-500"
  },
  {
    name: {
      en: "500 - 1,000",
      es: "500 - 1,000",
      fr: "500 - 1,000"
    },
    slug: "500-1k"
  },
  {
    name: {
      en: "1,000 - 5,000",
      es: "1,000 - 5,000",
      fr: "1,000 - 5,000"
    },
    slug: "1k-5k"
  },
  {
    name: {
      en: "5,000 - 10,000",
      es: "5,000 - 10,000",
      fr: "5,000 - 10,000"
    },
    slug: "5k-10k"
  },
  {
    name: {
      en: "Over 10,000",
      es: "M\xE1s de 10,000",
      fr: "Au dessus de 10,000"
    },
    slug: "over-10k"
  }
];

const segmentationSeparator = "~";
const segmentationSeparatorForLocationCode = "|";
const segmentationSortOrder = [
  "asc",
  "desc"
];
const allSegmentationOperators = [
  "and",
  "or"
];
const allSegmentationSigns = [
  "equals",
  "exactEquals",
  "greater",
  "lower",
  "notEquals"
];
const matchSegmentationSigns = [
  "equals",
  "exactEquals",
  "notEquals"
];
const compareSegmentationSigns = [
  "greater",
  "lower",
  "notEquals",
  "exactEquals"
];
const segmentationOperatorsRuleSchema = z.enum(allSegmentationOperators);
const segmentationAllSignsRuleSchema = z.enum(allSegmentationSigns);
const segmentationMatchSignsRuleSchema = z.enum(matchSegmentationSigns);
const segmentationCompareSignsRuleSchema = z.enum(compareSegmentationSigns);
const segmentationBlockedOperatorRuleSchema = z.boolean();
const segmentationValueSchema = z.union([z.string(), z.number()]);
z.array(segmentationValueSchema);
const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
const yearSchema = z.union([
  z.string().refine(
    (val) => {
      const num = Number.parseInt(val, 10);
      return !Number.isNaN(num) && Number.isInteger(num) && num >= 1e3 && num <= currentYear;
    },
    { message: `Value must be an integer between 1000 and ${currentYear}` }
  ),
  z.number().int("Value must be an integer").min(1e3, "Value must be at least 1000").max(currentYear, `Value must not exceed ${currentYear}`)
]);
const kebabCaseSchema = z.string().regex(
  /^[a-z0-9]+(-[a-z0-9]+)*$/,
  "String must be in kebab-case format (lowercase letters, numbers, separated by single hyphens)"
);
const domainSchema = z.string().refine(
  (val) => isValidFullDomain(val),
  { message: "Invalid domain format, it must be a valid complete domain or subdomain" }
);
const technologySchema = z.string().refine(
  (val) => {
    const validTechnologies = Object.keys(technologies);
    if (validTechnologies.includes(val)) {
      return true;
    }
    const versionMatch = val.match(/^(.+)@(.*)$/);
    if (versionMatch) {
      const [, technology] = versionMatch;
      if (!validTechnologies.includes(technology)) {
        return false;
      }
      return true;
    }
    return false;
  },
  { message: 'Value must be either a valid technology name or in the format "technology@version" where technology is from the valid technologies list' }
).transform((val) => {
  const validTechnologies = Object.keys(technologies);
  if (validTechnologies.includes(val)) {
    return `${val}@`;
  }
  if (val.includes("@")) {
    return val;
  }
  return val;
});
const segmentationAttributeValidationMapSchema = {
  "about.businessType": z.object({
    attribute: z.literal("about.businessType"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationAllSignsRuleSchema,
    values: z.array(z.enum(businessTypes.map((businessType) => businessType.slug)))
  }),
  "about.industries": z.object({
    attribute: z.literal("about.industries"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationAllSignsRuleSchema,
    // Open industries list
    values: z.array(kebabCaseSchema)
  }),
  "about.industry": z.object({
    attribute: z.literal("about.industry"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationAllSignsRuleSchema,
    // Constrained industries list
    values: z.array(z.enum(Object.keys(industries)))
  }),
  "about.name": z.object({
    attribute: z.literal("about.name"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationAllSignsRuleSchema,
    values: z.array(z.string())
  }),
  "about.totalEmployees": z.object({
    attribute: z.literal("about.totalEmployees"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(totalEmployees.map((totalEmployee) => totalEmployee.slug)))
  }),
  "about.yearFounded": z.object({
    attribute: z.literal("about.yearFounded"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationCompareSignsRuleSchema,
    values: z.array(yearSchema)
  }),
  "ai.search": z.object({
    attribute: z.literal("ai.search"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.string().max(2048, "Value must be less than 1024 characters"))
  }),
  "analytics.monthlyVisitors": z.object({
    attribute: z.literal("analytics.monthlyVisitors"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(monthlyVisitors.map((monthlyVisitor) => monthlyVisitor.slug)))
  }),
  "apps": z.object({
    attribute: z.literal("apps"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(apps.map((app) => app.slug)))
  }),
  "codes.naics": z.object({
    attribute: z.literal("codes.naics"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(Object.keys(industriesNaics)))
  }),
  "codes.sic": z.object({
    attribute: z.literal("codes.sic"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(Object.keys(industriesSic)))
  }),
  "contacts": z.object({
    attribute: z.literal("contacts"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(contacts.map((contact) => contact.slug)))
  }),
  "domain.domain": z.object({
    attribute: z.literal("domain.domain"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(domainSchema)
  }),
  "domain.tld": z.object({
    attribute: z.literal("domain.tld"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(Object.keys(tlds)))
  }),
  "finances.revenue": z.object({
    attribute: z.literal("finances.revenue"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(revenues.map((revenue) => revenue.slug)))
  }),
  "finances.stockExchange": z.object({
    attribute: z.literal("finances.stockExchange"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(stockExchanges))
  }),
  "locations.headquarters.city.code": z.object({
    attribute: z.literal("locations.headquarters.city.code"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.string())
  }),
  "locations.headquarters.continent.code": z.object({
    attribute: z.literal("locations.headquarters.continent.code"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(continents.map((continent) => continent.code)))
  }),
  "locations.headquarters.country.code": z.object({
    attribute: z.literal("locations.headquarters.country.code"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.string())
  }),
  "locations.headquarters.county.code": z.object({
    attribute: z.literal("locations.headquarters.county.code"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.string())
  }),
  "locations.headquarters.state.code": z.object({
    attribute: z.literal("locations.headquarters.state.code"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.string())
  }),
  "meta.listIds": z.object({
    attribute: z.literal("meta.listIds"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.union([z.string(), z.number()]))
  }),
  "meta.score": z.object({
    attribute: z.literal("meta.score"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationCompareSignsRuleSchema,
    values: z.array(z.number().min(0).max(200)).max(1)
  }),
  "meta.syncedAt": z.object({
    attribute: z.literal("meta.syncedAt"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationCompareSignsRuleSchema,
    values: z.array(z.string()).max(1)
  }),
  "socials": z.object({
    attribute: z.literal("socials"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(socialNetworks.map((socialNetwork) => socialNetwork.slug)))
  }),
  "socials.linkedin.id": z.object({
    attribute: z.literal("socials.linkedin.id"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.string())
  }),
  "technologies.active": z.object({
    attribute: z.literal("technologies.active"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(technologySchema)
  }),
  "technologies.categories": z.object({
    attribute: z.literal("technologies.categories"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.enum(Array.from(new Set(Object.values(technologies).map((technology) => technology.categories).flat()))))
  }),
  "urls": z.object({
    attribute: z.literal("urls"),
    blockedOperator: z.boolean().optional(),
    operator: segmentationOperatorsRuleSchema,
    sign: segmentationMatchSignsRuleSchema,
    values: z.array(z.string())
  })
};
const SegmentationAttributeSchema = z.enum(Object.keys(segmentationAttributeValidationMapSchema));
const genericSegmentationConditionSchema = z.object({
  attribute: SegmentationAttributeSchema,
  blockedOperator: segmentationBlockedOperatorRuleSchema.optional(),
  operator: segmentationOperatorsRuleSchema,
  sign: segmentationAllSignsRuleSchema,
  values: z.array(z.union([z.string(), z.number()]))
}).openapi("SegmentationCondition", { description: "A condition for our platform segmentation engine." });
const segmentationValidation = {
  allSignsRule: segmentationAllSignsRuleSchema,
  attributeValidationMap: segmentationAttributeValidationMapSchema,
  compareSignsRule: segmentationCompareSignsRuleSchema,
  matchSignsRule: segmentationMatchSignsRuleSchema,
  operatorsRule: segmentationOperatorsRuleSchema
};

export { apps as a, businessTypes as b, segmentationSortOrder as c, allSegmentationSigns as d, segmentationSeparatorForLocationCode as e, segmentationSeparator as f, genericSegmentationConditionSchema as g, allSegmentationOperators as h, contacts as i, continents as j, industries as k, industriesNaics as l, monthlyVisitors as m, industriesSic as n, segmentationValidation as o, stockExchanges as p, technologies as q, revenues as r, socialNetworks as s, totalEmployees as t, tlds as u, zodHelpers as z };
